const { useState, useEffect, useRef, useCallback, useMemo } = React;

// ── Meter-local timezone helpers ─────────────────────────────
// Every report screen in the UI ultimately sends `from_ts` / `to_ts`
// epoch seconds to the backend for profile-row filtering. Meters and
// the CAS store timestamps against the **meter-local** wall clock
// (Asia/Tashkent for this deployment — UTC+5, no DST). If a filter
// is derived from `new Date(ymd + 'T00:00:00')` the browser's own tz
// sneaks into the epoch and operators outside Tashkent silently see
// rows shifted by `browser_offset - meter_offset` hours — e.g. an
// operator in GMT picking "February 2026" queries `Feb 1 00:00 UTC`
// instead of `Feb 1 00:00 Tashkent` and misses the row the meter
// actually stored at `Jan 31 19:00 UTC`. These helpers return the
// epoch for a given wall-clock instant **in the meter's tz**,
// regardless of the browser's local tz.
//
// Uses Intl so the logic still works if we ever switch to a tz with
// DST. No dependency on Luxon / date-fns / etc. — just the JS
// built-in formatter.
const METER_TZ = 'Asia/Tashkent';
function localDateToEpoch(ymd, hour = 0, minute = 0, second = 0) {
  if (!ymd || typeof ymd !== 'string') return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  // Step 1: treat the wall-clock fields as if they were UTC. Call that
  // epoch `asUtcEpoch`.
  const asUtcEpoch = Date.UTC(y, m - 1, d, hour, minute, second) / 1000;
  // Step 2: ask Intl what asUtcEpoch LOOKS LIKE in the meter's tz. For
  // Tashkent (UTC+5) the answer is 5h AHEAD of the wall-clock we want.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: METER_TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(asUtcEpoch * 1000));
  const read = (type) => parseInt(parts.find(p => p.type === type).value, 10);
  const tzEpoch = Date.UTC(
    read('year'), read('month') - 1, read('day'),
    read('hour'), read('minute'), read('second'),
  ) / 1000;
  // Step 3: tzEpoch - asUtcEpoch = the tz offset (in seconds) at that
  // instant. The wall-clock we ACTUALLY want is `ymd hh:mm:ss` in the
  // meter tz, whose epoch is `asUtcEpoch - offset`.
  return 2 * asUtcEpoch - tzEpoch;
}
function dayStart(ymd)  { return localDateToEpoch(ymd, 0, 0, 0); }
function dayEnd(ymd)    { return localDateToEpoch(ymd, 23, 59, 59); }
// YYYY-MM-DD for an epoch in the METER timezone, NOT the browser's and
// NOT UTC. `new Date().toISOString().slice(0,10)` is wrong for every
// operator here: between 00:00 and 05:00 Tashkent it is still the
// PREVIOUS day in UTC, so a date picker defaulted from it shows
// yesterday and the operator refreshes a day they are not looking at.
// Observed 2026-08-07 03:04 TAS — the picker read 2026-08-06.
function tashkentYmd(epochSeconds = Math.floor(Date.now() / 1000)) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: METER_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(epochSeconds * 1000));
  const get = (t) => parts.find(p => p.type === t).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}
// ONE DATE FORMAT FOR EVERY ON-SCREEN TIMESTAMP: dd/mm/yyyy [HH:MM[:SS]] in
// Tashkent time (operator decision 2026-10-06; the XLSX exports use the same,
// see XLSX_DATETIME_FORMAT). Accepts what the API returns: ISO text with or
// without an offset ("2026-10-04T00:00:00+05:00", "2026-10-04 00:00:00" =
// already local, "...Z" = UTC), epoch seconds or ms, or a Date. Anything that
// is not a timestamp is returned unchanged, so a stray value never vanishes.
// Use ONLY for display — API parameters, <input type=date> values and file
// names stay ISO (yyyy-mm-dd).
function _toTashkentParts(v, utc = false) {
  if (v === null || v === undefined || v === '') return null;
  let d = null;
  if (v instanceof Date) d = v;
  else if (typeof v === 'number' && isFinite(v)) {
    if (v < 1e8) return null;
    d = new Date(v > 1e12 ? v : v * 1000);
  } else if (typeof v === 'string') {
    const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/);
    if (!m) return null;
    if (!m[7] && !utc) {   // no offset: meter/report times are already local (Tashkent)
      return { y: m[1], mo: m[2], d: m[3], h: m[4] || '00', mi: m[5] || '00', s: m[6] || '00', hasTime: !!m[4] };
    }
    // Record times (created_at, last_login_at, cached_at, ...) are written
    // as naive UTC by the server — pass {utc: true} for those.
    d = new Date(v.trim().replace(' ', 'T') + (m[7] ? '' : 'Z'));
  }
  if (!d || isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: METER_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(d);
  const g = (t) => (parts.find(p => p.type === t) || {}).value;
  return { y: g('year'), mo: g('month'), d: g('day'), h: g('hour'), mi: g('minute'), s: g('second'), hasTime: true };
}
// dd/mm/yyyy HH:MM (opts: seconds -> :SS, utc -> naive input is UTC; date-only input stays date-only)
function fmtDT(v, opts = {}) {
  const p = _toTashkentParts(v, !!opts.utc);
  if (!p) return v;
  const date = `${p.d}/${p.mo}/${p.y}`;
  if (!p.hasTime) return date;
  return `${date} ${p.h}:${p.mi}${opts.seconds ? ':' + p.s : ''}`;
}
// dd/mm/yyyy
function fmtD(v, opts = {}) {
  const p = _toTashkentParts(v, !!opts.utc);
  return p ? `${p.d}/${p.mo}/${p.y}` : v;
}
// yyyy-mm-dd (ISO, for the "Date"/"from" pickers) -> dd/mm/yyyy for labels
function fmtYmd(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ymd;
}
function monthStart(year, month1Based) {
  const pad = (n) => String(n).padStart(2, '0');
  return localDateToEpoch(`${year}-${pad(month1Based)}-01`, 0, 0, 0);
}
function monthEnd(year, month1Based) {
  const nextY = month1Based === 12 ? year + 1 : year;
  const nextM = month1Based === 12 ? 1 : month1Based + 1;
  // One second before the next month's first instant — inclusive upper
  // bound for `row_ts <= to_ts` comparisons (storage.py uses inclusive).
  return monthStart(nextY, nextM) - 1;
}

// CSS Styles
const styles = `
  /* ── Theme tokens ───────────────────────────────────────────
     Semantic color variables. Dark is the default; light mode
     is activated by setting data-theme="light" on <html>. Any
     CSS rule that uses var(--xxx) automatically switches when
     the theme toggles — no JS re-render needed.
     ───────────────────────────────────────────────────────── */
  :root, [data-theme="dark"] {
    --bg-base:       #0a0e17;
    --bg-card:       #111827;
    --bg-card-alt:   #0f172a;
    --bg-input:      #1a2332;
    --bg-elevated:   #1a1f2e;
    --border:        #1e293b;
    --border-input:  #2d3748;
    --text-primary:  #e2e8f0;
    --text-secondary:#94a3b8;
    --text-label:    #cbd5e1;
    --text-muted:    #64748b;
    --text-dimmed:   #475569;
    --accent:        #00e5ff;
    --accent-hover:  #22d3ee;
    --accent-secondary: #7c3aed;
    --success:       #22c55e;
    --danger:        #ef4444;
    --warning:       #fbbf24;
    --scrollbar-track: #0a0e17;
    --scrollbar-thumb: #2d3748;
  }
  [data-theme="light"] {
    --bg-base:       #f1f5f9;
    --bg-card:       #ffffff;
    --bg-card-alt:   #f8fafc;
    --bg-input:      #f1f5f9;
    --bg-elevated:   #e2e8f0;
    --border:        #cbd5e1;
    --border-input:  #94a3b8;
    --text-primary:  #0f172a;
    --text-secondary:#475569;
    --text-label:    #334155;
    --text-muted:    #64748b;
    --text-dimmed:   #94a3b8;
    --accent:        #0891b2;
    --accent-hover:  #0e7490;
    --accent-secondary: #7c3aed;
    --success:       #16a34a;
    --danger:        #dc2626;
    --warning:       #d97706;
    --scrollbar-track: #e2e8f0;
    --scrollbar-thumb: #94a3b8;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
    background: var(--bg-base);
    color: var(--text-primary);
    line-height: 1.6;
  }

  #root {
    min-height: 100vh;
  }

  .app-container {
    display: flex;
    min-height: 100vh;
    background: var(--bg-base);
  }

  .sidebar {
    width: 240px;
    background: var(--bg-card);
    border-right: 1px solid var(--border);
    padding: 24px 16px;
    display: flex;
    flex-direction: column;
    position: fixed;
    height: 100vh;
    overflow-y: auto;
    z-index: 100;
    transition: transform 0.25s ease, opacity 0.25s ease;
  }
  .sidebar.collapsed {
    transform: translateX(-240px);
    opacity: 0;
    pointer-events: none;
  }

  .logo {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 24px;
    font-size: 18px;
    font-weight: bold;
    line-height: 1.2;
  }

  .logo-text {
    background: linear-gradient(135deg, #00e5ff, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .version-badge {
    display: inline-block;
    background: rgba(124, 58, 237, 0.2);
    color: #7c3aed;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: bold;
    margin-top: 4px;
  }

  .nav-list {
    list-style: none;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .nav-item {
    padding: 12px;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: var(--text-secondary);
    transition: all 0.2s;
    border-left: 3px solid transparent;
    margin-left: -3px;
  }

  .nav-item:hover {
    color: var(--accent);
    background: rgba(0, 229, 255, 0.05);
  }

  .nav-item.active {
    color: var(--accent);
    background: rgba(0, 229, 255, 0.1);
    border-left-color: var(--accent);
  }

  .nav-icon {
    font-size: 16px;
    min-width: 20px;
  }

  .sidebar-bottom {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-top: 1px solid var(--border);
    padding-top: 16px;
    margin-top: 16px;
  }

  .status-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  .status-dot.connected {
    background: var(--success);
  }

  .status-dot.disconnected {
    background: var(--danger);
    animation: none;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .main-content {
    flex: 1;
    /* THE CONTAINMENT FIX. This is a flex item (.app-container is
       display:flex), and flex items default to min-width:auto, which
       resolves to their CONTENT's min-content width — the item refuses to
       shrink below its widest child. Per spec that only bites while
       overflow is 'visible', so the overflow-y:auto below was masking it by
       accident; the moment anything set overflow-y:visible, a wide table or
       chart forced .main-content past the viewport, dragged .app-container
       with it, and the whole page scrolled sideways. overflow-x:hidden on
       body cannot fix that, because the flex box is genuinely wider than
       the screen — which is why every content-level fix just moved the
       symptom somewhere else.
       min-width:0 states the intent directly and holds regardless of
       overflow. Wide children then scroll in their own box, as intended. */
    min-width: 0;
    margin-left: 240px;
    padding: 32px;
    overflow-y: auto;
    max-height: 100vh;
  }

  .tab-content {
    animation: fadeIn 0.3s;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: linear-gradient(135deg, rgba(0, 229, 255, 0.05), rgba(124, 58, 237, 0.05));
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: all 0.3s;
  }

  .stat-card:hover {
    border-color: var(--accent);
    box-shadow: 0 0 20px rgba(0, 229, 255, 0.2);
  }

  .stat-value {
    font-size: 32px;
    font-weight: bold;
    color: var(--accent);
  }

  .stat-label {
    font-size: 13px;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .activity-feed {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 32px;
  }

  .activity-item {
    padding: 12px;
    border-left: 3px solid var(--accent-secondary);
    margin-bottom: 12px;
    font-size: 13px;
  }

  .activity-time {
    color: var(--text-secondary);
    font-size: 11px;
  }

  .button {
    padding: 10px 16px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .button-primary {
    background: var(--accent);
    color: var(--bg-base);
  }

  .button-primary:hover {
    background: var(--accent-hover);
    box-shadow: 0 0 15px rgba(0, 229, 255, 0.3);
  }

  .button-secondary {
    background: var(--accent-secondary);
    color: #fff;
  }

  .button-secondary:hover {
    background: #6d28d9; /* intentionally hardcoded — purple shade darker than --accent-secondary */
  }

  .button-danger {
    background: var(--danger);
    color: #fff;
  }

  .button-danger:hover {
    background: #dc2626;
  }

  /* Global disabled style for every .button variant. Without this the
     button-danger / button-success classes stayed fully coloured even
     when React set disabled=true — admins could click a visually-red
     Disconnect button that silently did nothing because the HTML
     disabled attribute was actually set. */
  .button:disabled,
  .button[disabled] {
    opacity: 0.45;
    cursor: not-allowed;
    filter: saturate(0.5);
  }
  .button:disabled:hover,
  .button[disabled]:hover {
    background: var(--danger);         /* no hover brighten when disabled */
    box-shadow: none;
  }

  .button-sm {
    padding: 6px 12px;
    font-size: 12px;
  }

  .table-container {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    /* overflow-x, NOT overflow:hidden. Clipping hid every table wider than
       its container — the columns were not merely off-screen, they were
       unreachable, because a clipped box has no scrollbar and no scroll
       gesture. On a phone that silently swallows most of a meter table.
       overflow-x:auto keeps the rounded corners doing their job while
       letting a wide table scroll inside its own box, so the page body
       never scrolls sideways. */
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  th {
    background: var(--bg-card-alt);
    padding: 12px;
    text-align: left;
    color: var(--text-secondary);
    font-weight: 600;
    border-bottom: 1px solid var(--border);
  }

  td {
    padding: 12px;
    border-bottom: 1px solid var(--border);
  }

  tr:nth-child(even) {
    background: var(--bg-card-alt);
  }

  tr:hover {
    background: rgba(0, 229, 255, 0.05);
  }

  .input-field {
    background: var(--bg-input);
    border: 1px solid var(--border-input);
    border-radius: 6px;
    padding: 10px 12px;
    color: var(--text-primary);
    font-size: 13px;
    transition: all 0.2s;
    font-family: inherit;
    width: 100%;
    box-sizing: border-box;
  }

  .input-field:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 10px rgba(0, 229, 255, 0.2);
  }

  /* Native <input type="date"> picker indicator. The default calendar
   * glyph Chrome / Edge overlay in the trailing edge of the input is
   * black, which disappears into the dark theme background and makes
   * the field look like a plain text input. Inverting the filter in
   * the default (dark) theme restores the glyph; light theme resets
   * it back so the icon doesn't become invisibly white on white.
   * Firefox renders its own icon and ignores these rules. */
  input[type="date"]::-webkit-calendar-picker-indicator,
  input[type="datetime-local"]::-webkit-calendar-picker-indicator,
  input[type="month"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.9;
    transform: scale(1.15);
    margin-left: 4px;
    filter: invert(1) brightness(1.4);
  }
  [data-theme="light"] input[type="date"]::-webkit-calendar-picker-indicator,
  [data-theme="light"] input[type="datetime-local"]::-webkit-calendar-picker-indicator,
  [data-theme="light"] input[type="month"]::-webkit-calendar-picker-indicator {
    filter: none;
  }

  /* Form labels — works under both flex and block display modes.
   * Most call sites use the default flex layout, but several modals
   * set display:block inline. Either way, the > input/select/textarea
   * rule below gives the input a full-width box with a proper top
   * margin so the label text never visually touches the field. */
  .input-label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 18px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-label);
    line-height: 1.4;
  }
  .input-label > input,
  .input-label > select,
  .input-label > textarea {
    display: block;
    width: 100%;
    margin-top: 8px;
    box-sizing: border-box;
  }

  .form-group {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
    margin-bottom: 20px;
  }

  .spinner {
    /* Two-tone ring driven by currentColor — visible on any parent:
       dark arc on bright primary button, bright on dark page bg.
       Previously the spinner hard-coded the accent cyan (#00e5ff)
       which perfectly matched the primary button background and
       became invisible during loading. */
    border: 3px solid rgba(127, 127, 127, 0.3);
    border-top-color: currentColor;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
    box-sizing: border-box;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
  }

  .badge-success {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  }

  .badge-warning {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
  }

  .badge-danger {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  .badge-info {
    background: rgba(0, 229, 255, 0.2);
    color: #00e5ff;
  }

  .toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 16px;
    max-width: 400px;
    animation: slideIn 0.3s;
    z-index: 1000;
  }

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .toast.success {
    border-left: 4px solid var(--success);
  }

  .toast.error {
    border-left: 4px solid var(--danger);
  }

  .toast.info {
    border-left: 4px solid var(--accent);
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
  }

  .modal {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }

  .modal-header {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 16px;
    color: var(--text-primary);
  }

  .modal-body {
    margin-bottom: 20px;
    color: var(--text-secondary);
    font-size: 13px;
  }

  .modal-footer {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  .empty-state {
    text-align: center;
    padding: 48px 24px;
    color: var(--text-muted);
  }

  .empty-state-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .tabs-pills {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .pill {
    padding: 8px 14px;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
  }

  .pill:hover {
    border-color: var(--accent);
    color: var(--accent);
  }

  .pill.active {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--bg-base);
  }

  .textarea {
    background: var(--bg-input);
    border: 1px solid var(--border-input);
    border-radius: 6px;
    padding: 10px 12px;
    color: var(--text-primary);
    font-size: 13px;
    font-family: 'Courier New', monospace;
    min-height: 100px;
    resize: vertical;
  }

  .textarea:focus {
    outline: none;
    border-color: var(--accent);
  }

  .grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
  }

  .quick-actions {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .action-panel {
    background: linear-gradient(135deg, rgba(0, 229, 255, 0.05), rgba(124, 58, 237, 0.05));
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px;
    margin-top: 16px;
  }

  .mini-table {
    width: 100%;
    font-size: 12px;
  }

  .mini-table th {
    background: transparent;
    padding: 8px;
    font-size: 11px;
  }

  .mini-table td {
    padding: 8px;
  }

  .drag-drop {
    border: 2px dashed var(--border);
    border-radius: 8px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
  }

  .drag-drop:hover {
    border-color: var(--accent);
    background: rgba(0, 229, 255, 0.05);
  }

  .drag-drop.active {
    border-color: var(--accent);
    background: rgba(0, 229, 255, 0.1);
  }

  .svg-chart {
    width: 100%;
    height: 300px;
    margin-top: 20px;
  }

  .section-title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 16px;
    color: var(--text-primary);
  }

  .section-divider {
    height: 1px;
    background: var(--border);
    margin: 24px 0;
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--scrollbar-track);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #3f4654;
  }

  /* ==================================================================
     MOBILE / RESPONSIVE LAYER
     ==================================================================
     This app shipped with a viewport meta tag and ZERO media queries, so
     every phone rendered the 240px fixed sidebar plus a 240px content
     offset inside a ~390px screen — roughly 150px of usable width.

     WHY '!important' APPEARS HERE AND ALMOST NOWHERE ELSE. The app sets
     layout through React inline styles ('style={{marginLeft: ...}}'),
     which outrank any normal stylesheet rule. A stylesheet declaration
     marked !important does outrank a non-important inline style, so this
     is the one mechanism that can restate layout for small screens
     without rewriting thousands of inline styles across 30k lines. It is
     deliberately scoped to inside these media queries: nothing here
     affects the desktop cascade.

     Breakpoints: 900px (tablet / small laptop), 600px (phone),
     420px (small phone). Chosen from the app's own content — the widest
     unavoidable table needs ~900px, the KPI grid stops fitting two
     columns near 600px.
     ================================================================== */

  @media (max-width: 900px) {
    /* The sidebar stops reserving a column and becomes an overlay. The
       inline marginLeft (240px open / 64px collapsed) is what we are
       overriding; without this the content column is unusable. */
    .main-content {
      margin-left: 0 !important;
      /* The extra top padding clears the fixed MENU button, which sits at
         top:10px left:10px whenever the sidebar is closed — the default on
         a phone. Without it the button lands on top of the page heading
         ('MENU' overlapping 'Customers'). */
      padding: 64px 14px 160px;
      /* Desktop makes this its own scroll box (max-height:100vh +
         overflow-y:auto). On a phone that nests a scroller inside the page
         scroller, which iOS in particular handles badly — momentum stops at
         the inner edge and the last card can feel unreachable. Let the body
         do the scrolling instead. */
      max-height: none;
      overflow-y: visible;
    }

    /* A 130px strip plus its labels pushes the day table off a phone
       screen; 96px keeps the chart and the first rows together. */
    .collection-strip { height: 96px !important; }

    .sidebar {
      width: min(84vw, 300px);
      box-shadow: 6px 0 28px rgba(0, 0, 0, 0.45);
      z-index: 1200;
    }
    .sidebar.collapsed {
      /* Wider than the widest allowed sidebar, so it clears the screen
         whatever 'min()' resolved to. */
      transform: translateX(-320px);
    }

    /* Tap-anywhere-to-dismiss backdrop, rendered only on mobile. */
    .sidebar-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      z-index: 1150;
      border: 0;
      padding: 0;
      margin: 0;
      cursor: pointer;
    }

    /* Cards and panels: reclaim the horizontal padding phones cannot spare. */
    .stat-card,
    .card,
    .panel,
    .table-container { border-radius: 10px; }

    /* Any auto-fit grid collapses to one column. The app uses minmax()
       floors from 120px to 420px; the large ones overflow a phone, and
       the small ones produce unreadably narrow columns. One column is
       right for all of them at this width. */
    .stat-grid { grid-template-columns: 1fr 1fr !important; gap: 12px; }

    /* Tables become their own scroll container.
       Only 23 of the 64 tables in this app sit inside a .table-container;
       the other 41 are bare <table> elements whose parent has no overflow
       rule, so on a phone they widened the page itself and every column in
       the layout drifted right. display:block makes the table box scroll
       horizontally on its own, which needs no wrapper markup and therefore
       fixes all 64 uniformly. The trade is that cells size to content
       instead of sharing width equally — correct here, because nowrap plus
       a real scroll beats twelve columns crushed into 350px.
       A table already inside .table-container simply scrolls itself first;
       the outer container never overflows, so the two never fight. */
    table {
      display: block;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      font-size: 12.5px;
      max-width: 100%;
      /* display:block makes the table shrink-to-fit, so a two-column table
         would sit narrow and left-aligned in a wide card. min-width keeps
         those filling their container while wide ones still scroll. */
      min-width: 100%;
    }
    th, td { padding: 10px 9px; white-space: nowrap; }

    /* Modals: nearly full-bleed, and scrollable when the on-screen
       keyboard halves the viewport. */
    .modal {
      width: 94vw !important;
      max-width: 94vw !important;
      max-height: 88vh;
      overflow-y: auto;
      padding: 18px;
    }
    .modal-footer { flex-wrap: wrap; }

    /* 16px is the threshold below which iOS Safari zooms the page on
       focus and never zooms back out — the single most common way a
       mobile form traps a user. */
    .input-field,
    .textarea,
    select.input-field,
    input[type="text"], input[type="password"], input[type="number"],
    input[type="date"], input[type="search"], input[type="email"],
    select, textarea {
      font-size: 16px !important;
    }

    /* Touch targets. 44px is the Apple HIG minimum and close to the
       Material 48dp guidance; below it, mis-taps rise sharply. */
    .nav-item { padding: 14px 12px; min-height: 46px; }
    .button, .pill, button { min-height: 40px; }
    .button-sm { min-height: 34px; }
  }

  @media (max-width: 600px) {
    body { -webkit-text-size-adjust: 100%; }

    .main-content { padding: 60px 11px 160px; }

    /* Deliberately NOT 1fr. Five KPI cards at ~150px each, one per row,
       made the dashboard roughly five screens tall and pushed the fleet
       collection monitor below all of them — which is what an operator
       opens this page to read. Two columns plus the compact type below
       fits the whole KPI block in about one screen. */
    .stat-grid { grid-template-columns: 1fr 1fr !important; gap: 10px; }

    /* Compact KPI cards. 32px numerals were sized for a 3-4 across desktop
       grid; in a ~175px phone column "1,225,254.5 kWh" simply does not fit
       and overflowed its card. */
    .stat-card { padding: 13px 12px !important; gap: 4px; border-radius: 10px; }
    .stat-value {
      font-size: 19px !important;
      line-height: 1.2;
      overflow-wrap: anywhere;
    }
    .stat-label { font-size: 10.5px; letter-spacing: 0.3px; }

    /* Section headers and their action rows stack instead of colliding. */
    .section-header,
    .toolbar,
    .filters-row {
      flex-direction: column;
      align-items: stretch !important;
      gap: 10px;
    }

    .tabs-pills { gap: 6px; }
    .pill { font-size: 12px; padding: 9px 12px; }

    /* Full-width primary actions read as buttons on a phone rather than
       as links floating in whitespace. */
    .modal-footer .button { flex: 1 1 auto; justify-content: center; }

    .empty-state { padding: 32px 16px; }
  }

  @media (max-width: 420px) {
    .main-content { padding: 58px 9px 150px; }
    .stat-value { font-size: 17px !important; }
    .stat-card { padding: 11px 10px !important; }
    .modal { padding: 15px; }
    th, td { padding: 9px 7px; }
  }

  /* ── CONTAINMENT ──────────────────────────────────────────────────
     Horizontal page scroll is a layout failure, not a cosmetic one: it
     drifts every column and hides the right edge of the screen. The rule
     is that wide content scrolls INSIDE its own box; the page never moves.

     min-width:0 is the load-bearing declaration here, not overflow-x.
     Grid and flex items default to min-width:auto = their content's
     min-content width, so ONE wide table anywhere inflates its item, then
     its container, then the page. Clipping with overflow-x:hidden only
     hides the evidence — the box is still oversized and the layout still
     drifts. Every level of the chain therefore gets min-width:0, and
     overflow-x:hidden stays only as a backstop for anything missed.
     ------------------------------------------------------------------ */
  @media (max-width: 900px) {
    /* overflow-x ONLY on the root element.
       Per css-overflow-3: "if one of overflow-x/overflow-y is visible and
       the other is not, visible computes to auto." So overflow-x:hidden on
       body and on .app-container silently made BOTH of them vertical scroll
       containers as well. Nested inside a .app-container of min-height:100vh
       — and mobile 100vh excludes the collapsing URL and nav bars — the
       inner box reached its end while its last stretch sat behind the
       browser chrome, and the page simply stopped scrolling with the chart's
       date labels still hidden.
       The root element is the viewport scroller already, so clipping there
       costs nothing. Everything below it keeps overflow visible and the page
       scrolls as one document. */
    html { overflow-x: hidden; }
    body { max-width: 100%; }

    .app-container {
      max-width: 100vw;
      /* dvh tracks the visible viewport as mobile chrome collapses; vh does
         not, and 100vh here reserved space the user could never scroll to.
         The vh line stays first as the fallback for older browsers. */
      min-height: 100vh;
      min-height: 100dvh;
    }

    .main-content {
      min-width: 0;
      max-width: 100%;
      /* break-word, NOT anywhere. 'anywhere' also shrinks an element's
         min-content size, and it INHERITS — so every descendant became
         breakable and short button labels were split mid-word ("Delete"
         rendered as "Delet / e"). break-word only breaks a word that
         genuinely cannot fit on its own line, which is all a long serial or
         LDN needs now that min-width:0 handles the layout pressure. */
      overflow-wrap: break-word;
    }

    /* Flex rows WRAP rather than overhang.
       flex-shrink:0 above stops a control being squeezed until its label
       spills; the consequence is that a row too wide for the screen now
       overflows instead. For a row of action buttons the right answer is to
       flow onto the next line — measured overhangs of 23px (Customers) and
       52px (Reports) before this rule. Strips that are meant to scroll opt
       out explicitly below.
       This only changes rows that DO overflow; a row that already fits is
       unaffected by flex-wrap.
       ROWS only. Wrapping a flex-direction:column box with a max-height
       (every modal card) does not stack anything — the items that exceed
       the height start a SECOND COLUMN to the right, outside the card.
       The DCU Collection Detail popup rendered empty on a phone this way
       (2026-10-27): its table was laid out off-screen beside the filters. */
    .main-content [style*="display: flex"]:not([style*="flex-direction: column"]) { flex-wrap: wrap; }

    /* ...except these, which scroll on one line by design. */
    .subtab-bar, .collection-strip { flex-wrap: nowrap !important; }

    /* Tab strips scroll sideways instead of wrapping onto four rows.
       flex-wrap and white-space are both overridden because the inline
       style sets wrap and the control rule above sets nowrap. */
    .subtab-bar {
      flex-wrap: nowrap !important;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      padding-bottom: 2px;
    }
    .subtab-bar::-webkit-scrollbar { display: none; }
    .subtab-bar > * { flex: 0 0 auto; }

    /* Controls never break mid-word. A wrapped verb on a button reads as a
       rendering fault, and these labels are short enough that the honest
       answer to "it does not fit" is a wider button, not a broken word. */
    .button, .button-sm, .pill, button, .nav-item, th {
      overflow-wrap: normal;
      word-break: keep-all;
      white-space: nowrap;
      /* flex-shrink:0 is REQUIRED alongside nowrap, not optional.
         Flex items shrink by default, so a nowrap label in a squeezed row
         does not wrap and does not shrink the box either — the text simply
         spills out of its own button and lands on top of the next one.
         Measured in a real 393px viewport: the Meters tab row rendered as
         "Inbound MetersDCUs" and "Archive ContDelete Ma". Keeping the box
         at its label width makes the ROW overflow instead, which the
         scrollable strips below turn into a swipe. */
      flex-shrink: 0;
    }

    /* Direct children of the content column, and the items of any grid or
       flex row inside it. Without this a single wide child (a 15-bar chart,
       a 12-column table) re-inflates the column the rule above just fixed. */
    .main-content > *,
    .stat-grid > *,
    .table-container,
    [style*="display: grid"] > *,
    [style*="display: flex"] > * {
      min-width: 0;
      max-width: 100%;
    }
  }

  /* Inline auto-fit grids.
     The app builds most of its card grids as React inline styles, e.g.
     style={{gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))'}}.
     auto-fit does NOT rescue a track whose floor exceeds the container:
     minmax(420px, 1fr) inside a 350px phone column produces one 420px
     track and 70px of horizontal overflow. There is no class to hook, so
     these match on the inline style text itself — the floors below are the
     ones this file actually contains, largest first.

     Verified against the source: floors in use are 120,130,140,160,170,
     180,200,220,230,240,250,260,280,300,320,420. */
  @media (max-width: 900px) {
    [style*="minmax(420px"], [style*="minmax(320px"],
    [style*="minmax(300px"], [style*="minmax(280px"],
    [style*="minmax(260px"], [style*="minmax(250px"],
    [style*="minmax(240px"], [style*="minmax(230px"] {
      grid-template-columns: 1fr !important;
    }
  }
  @media (max-width: 600px) {
    /* Below 600px even a 200px floor leaves two cramped columns; one
       readable column beats two truncated ones. */
    [style*="minmax(220px"], [style*="minmax(200px"],
    [style*="minmax(180px"], [style*="minmax(170px"],
    [style*="minmax(160px"] {
      grid-template-columns: 1fr !important;
    }
    /* The smallest grids (120-140px floors) are chip/among rows — two
       columns still reads well and one would waste the width. */
    [style*="minmax(140px"], [style*="minmax(130px"],
    [style*="minmax(120px"] {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)) !important;
    }
  }

  /* Respect the user's motion preference — the sidebar slide and the
     tab fade are decorative. */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

`;

// API Helper
// Tolerate paths with or without a leading /api prefix; both forms exist in
// older parts of this file. We always send to /api/<rest>.
//
// Auth: cookie-based. The login endpoint sets two cookies — emeter_auth
// (HttpOnly, carries the JWT, JS can't read it) and emeter_csrf (NOT
// HttpOnly, JS reads via document.cookie). credentials:'include' on every
// fetch makes the browser auto-attach both. On state-changing requests
// (POST/PUT/DELETE/PATCH) we read the csrf cookie and echo it as
// X-CSRF-Token — the backend's double-submit check rejects the request
// when the header doesn't match the cookie, defeating CSRF.

const CSRF_COOKIE = 'emeter_csrf';

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) { return null; }
}

function getCsrfToken() {
  return readCookie(CSRF_COOKIE);
}

const _SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const api = async (path, options = {}) => {
  let p = path.startsWith('/api/') ? path.slice(4) : path;
  if (!p.startsWith('/')) p = '/' + p;
  const method = (options.method || 'GET').toUpperCase();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (!_SAFE_METHODS.has(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch(`/api${p}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  if (res.status === 401) {
    // Cookie expired or rejected — let AuthProvider show the login screen.
    try { window.dispatchEvent(new Event('auth-expired')); } catch (_) {}
  }
  if (!res.ok) {
    let detail = res.statusText;
    try { detail = (await res.json()).detail || res.statusText; } catch (_) {}
    // Structured error details ({error, hint, ...}) must not collapse to
    // "[object Object]" in toasts — flatten to something an operator can read.
    if (detail && typeof detail !== 'string') {
      detail = (detail.error || detail.hint)
        ? [detail.error, detail.hint].filter(Boolean).join(' — ')
        : JSON.stringify(detail);
    }
    throw new Error(detail);
  }
  return res.json();
};

// ---- Background-jobs tracker -------------------------------------------------
// Client-side registry of DCU background jobs (param-rebuild / repair) the
// operator fires this session. There is no server-side "list all jobs" endpoint,
// so we track the job_ids we launched and poll each via /api/jobs/{id}. Shared
// module-level store with pub-sub so the corner widget and the action buttons
// stay in sync across tab switches.
const _bgJobs = { list: [], subs: new Set() };
const bgJobs = {
  // { id, dcu, op, onDone? }. onDone(state, job) fires EXACTLY once when the
  // job reaches a terminal state — the hook a view uses to refresh itself
  // instead of asking the operator to remember to hit Refresh.
  add(job) {
    _bgJobs.list = [{ ...job, state: 'pending', ts: Date.now() }, ..._bgJobs.list].slice(0, 40);
    bgJobs._emit();
  },
  update(id, patch) {
    _bgJobs.list = _bgJobs.list.map(j => (j.id === id ? { ...j, ...patch } : j));
    bgJobs._emit();
  },
  remove(id) { _bgJobs.list = _bgJobs.list.filter(j => j.id !== id); bgJobs._emit(); },
  get() { return _bgJobs.list; },
  subscribe(fn) { _bgJobs.subs.add(fn); return () => _bgJobs.subs.delete(fn); },
  _emit() { _bgJobs.subs.forEach(fn => { try { fn(_bgJobs.list); } catch (_) {} }); },
};

// Fixed corner widget showing live/queued background jobs. Renders nothing until
// a job is fired. One 4s poll loop reads the current active list each tick, so
// it survives navigation and never churns intervals.
function BgJobsWidget() {
  const [jobs, setJobs] = React.useState(bgJobs.get());
  const [open, setOpen] = React.useState(true);
  React.useEffect(() => bgJobs.subscribe(setJobs), []);
  React.useEffect(() => {
    const t = setInterval(async () => {
      const active = bgJobs.get().filter(j => j.state === 'pending' || j.state === 'running');
      for (const j of active) {
        try {
          const s = await api(`/jobs/${j.id}`);
          const state = s?.state || j.state;
          const terminal = state === 'done' || state === 'failed' || state === 'cancelled';
          // Re-read the LIVE entry rather than trusting the snapshot taken
          // before the await: when /jobs is slow two 4s ticks overlap and both
          // see the same job as active, which would fire onDone twice.
          const live = bgJobs.get().find(x => x.id === j.id);
          if (!live) continue;                       // dismissed while in flight
          const fire = terminal && !live._notified;
          bgJobs.update(j.id, {
            state, result: s?.result, error: s?.error,
            ...(fire ? { _notified: true } : {}),
          });
          if (fire) {
            try { live.onDone && live.onDone(state, s); } catch (_) { /* view's problem */ }
            setTimeout(() => bgJobs.remove(j.id), 30000); // auto-clear after 30s
          }
        } catch (_) { /* transient — retry next tick */ }
      }
    }, 4000);
    return () => clearInterval(t);
  }, []);
  if (jobs.length === 0) return null;
  const col = { pending: '#a16207', running: '#2563eb', done: '#16a34a', failed: '#dc2626', cancelled: '#6b7280' };
  const activeN = jobs.filter(j => j.state === 'pending' || j.state === 'running').length;
  const line = (j) => {
    if (j.state === 'done' && j.result) {
      const r = j.result;
      const miss = (r.missing || []).length;
      return `${r.ok ? '✓' : '⚠'} ${r.n_after ?? '?'} rows${(r.corrected_saps || []).length ? `, ${r.corrected_saps.length} fixed` : ''}${miss ? `, ${miss} missing` : ''}`;
    }
    if (j.state === 'failed') return j.error || 'failed';
    // A job waiting to retry carries its reason — show it, never a bare
    // "pending" (an upload sat 10 min refused with nothing to act on).
    if (j.state === 'pending' && j.error) return j.error;
    return j.state;
  };
  return (
    <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9800, width: open ? 320 : 'auto',
      background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8,
      boxShadow: '0 6px 20px rgba(0,0,0,0.45)', fontSize: 12, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', padding: '7px 11px', background: '#1e293b', cursor: 'pointer', fontWeight: 600 }}>
        <span>{activeN > 0 ? '⏳' : '✓'} Background jobs{activeN ? ` (${activeN} active)` : ''}</span>
        <span style={{ color: '#94a3b8' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {jobs.map(j => (
            <div key={j.id} style={{ padding: '7px 11px', borderTop: '1px solid #1e293b',
              display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{j.op} · {j.dcu}</div>
                <div style={{ color: '#94a3b8', fontSize: 11, wordBreak: 'break-word' }}>{line(j)}</div>
              </div>
              <span style={{ color: col[j.state] || '#94a3b8', whiteSpace: 'nowrap', fontWeight: 600,
                textTransform: 'uppercase', fontSize: 10, marginTop: 2 }}>{j.state}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Fetch a profile's "latest row per serial" for an arbitrary number of
// serials, transparently chunking under the backend's 500-serial-per-request
// cap and merging the results. The All-Customers scope (670+ meters) exceeds
// the cap in a single request, which 400s and leaves the table empty; every
// caller that batches serials must go through this.
async function latestBulkChunked(name, serials, dateParams) {
  const CHUNK = 400;                     // < the backend's 500 cap, with margin
  const list = (serials || []).filter(Boolean);
  let captures = [];
  const meters = {};
  for (let i = 0; i < list.length; i += CHUNK) {
    const params = new URLSearchParams();
    params.set('serials', list.slice(i, i + CHUNK).join(','));
    for (const [k, v] of Object.entries(dateParams || {})) {
      if (v !== undefined && v !== null) params.set(k, String(v));
    }
    const resp = await api(`/profile/${name}/latest-bulk?${params}`);
    if (!captures.length && resp?.captures?.length) captures = resp.captures;
    Object.assign(meters, resp?.meters || {});
  }
  return { captures, meters };
}

// Shared CSV download helper. Prepends a UTF-8 BOM so Excel on Windows
// auto-detects the encoding and renders Cyrillic customer names / OBIS
// labels correctly instead of mojibake. All nine client-built CSV
// exports funnel through this so future format tweaks (separator,
// charset, line-ending) are a one-line change.
const downloadCsv = (csvString, filename) => {
  const blob = new Blob(['\ufeff' + csvString], {
    type: 'text/csv;charset=utf-8'
  });
  // Same two mobile requirements as downloadXlsx below: the anchor has to be
  // in the document for the synthetic click to register on Android/Firefox,
  // and the object URL must outlive the asynchronous download handoff.
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch (_) {}
    URL.revokeObjectURL(url);
  }, 60_000);
};

// ─────────────────────────────────────────────────────────────
// Shared XLSX download helper with progress feedback.
//
// Before this, every xlsx export site did its own fetch + blob +
// anchor click with a `// toast not available in this scope — silent
// fail` fallback. At 500m+ scale a report can be 30+ MB over GPRS-
// speed uplinks — a 10-second "frozen button" felt broken. Now:
//   - Toast fires on start
//   - Progress is reported from Content-Length when present
//   - Final toast shows file size on success; error message on fail
//   - Accepts optional onProgress(received, total) callback for
//     sites that want to wire in a dedicated progress bar
// All eight existing blob-download sites can migrate to this; for
// now we touch the three big report exports (daily-billing, monthly-
// billing, period-consumption) which have the worst UX at scale.
// ─────────────────────────────────────────────────────────────
async function downloadXlsx(
  url,
  filename,
  { toast = null, onProgress = null, headers = {} } = {},
) {
  // Cookie-based auth: credentials:'include' attaches the emeter_auth
  // cookie automatically. GET so no CSRF token needed.
  const allHeaders = { ...headers };
  const startAt = Date.now();
  toast && toast.push(`⬇ Generating ${filename}…`, 'info');
  let res;
  try {
    res = await fetch(url, { headers: allHeaders, credentials: 'include' });
  } catch (err) {
    toast && toast.push(`Download failed: ${err.message}`, 'error');
    throw err;
  }
  if (!res.ok) {
    let detail = res.statusText;
    try { detail = (await res.json()).detail || detail; } catch (_) {}
    toast && toast.push(`Download failed: ${detail}`, 'error');
    throw new Error(detail);
  }
  const totalStr = res.headers.get('content-length');
  const total = totalStr ? parseInt(totalStr, 10) : 0;

  // Stream the response so we can report progress for big files.
  // Falls back to plain .blob() if streaming isn't available.
  let blob;
  if (res.body && typeof res.body.getReader === 'function') {
    const reader = res.body.getReader();
    const chunks = [];
    let received = 0;
    let lastToastPct = -1;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      if (onProgress) onProgress(received, total);
      if (toast && total > 0) {
        const pct = Math.floor(received / total * 100);
        // Update toast every 25% to avoid spam on big files.
        if (pct >= lastToastPct + 25 && pct < 100) {
          toast.push(`⬇ ${pct}% (${(received / 1024).toFixed(0)} KB of ${(total / 1024).toFixed(0)} KB)`, 'info');
          lastToastPct = pct;
        }
      }
    }
    blob = new Blob(chunks, { type: res.headers.get('content-type') || 'application/octet-stream' });
  } else {
    blob = await res.blob();
  }

  // Two things here are mobile-critical and were both wrong.
  //
  // 1. THE ANCHOR MUST BE IN THE DOCUMENT. A detached <a> is click()-able on
  //    desktop Chrome, but Firefox and the Android WebView/Chrome stack
  //    ignore a synthetic click on an element that was never inserted — the
  //    call returns cleanly and nothing downloads. No error is raised, which
  //    is why this looked like "the button does nothing" on a phone while
  //    working on every desktop it was tested on.
  //
  // 2. revokeObjectURL MUST NOT RUN SYNCHRONOUSLY. Desktop starts the
  //    download during click(), so revoking on the next line is (barely)
  //    safe. On Android the fetch of the blob: URL is asynchronous, so
  //    revoking immediately destroys the object before the download reads
  //    it — again silently. Defer the revoke; a minute is far longer than
  //    any handoff needs and the blob is freed either way.
  const dl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = dl;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { document.body.removeChild(a); } catch (_) {}
    URL.revokeObjectURL(dl);
  }, 60_000);
  const secs = ((Date.now() - startAt) / 1000).toFixed(1);
  const kb = (blob.size / 1024).toFixed(0);
  toast && toast.push(`✓ ${filename} — ${kb} KB in ${secs}s`, 'success');
}


// Auth context — { user, login, logout, refresh }. Components can call
// useAuth() to get the current user (or null) and trigger a logout.
const AuthContext = React.createContext({
  user: null,
  login: async () => {},
  logout: () => {},
  refresh: async () => {},
});
function useAuth() {
  return React.useContext(AuthContext);
}

// Per-user permissions (emeter/permissions.py). The server sends the user's
// EFFECTIVE list on /auth/me; every screen gates on it, never on the role —
// a role is only the default set now, and an admin can customise any user.
function userCan(user, perm) {
  return !!(user && Array.isArray(user.permissions) && user.permissions.includes(perm));
}
function useCan() {
  const { user } = useAuth();
  return React.useCallback((perm) => userCan(user, perm), [user]);
}

// ─────────────────────────────────────────────────────────────
// i18n — English / Russian. Flat key dictionary, persisted in
// localStorage so the choice survives reloads. Lookups fall back
// to the EN string when a key is missing in RU, then to the key
// itself, so untranslated UI never breaks — it just shows English.
//
// Coverage: login page, sidebar tabs, dashboard headlines,
// common buttons (Read / Refresh / Search / Save / Delete /
// Export), MeterDetailView sub-tab labels, customer / hierarchy
// section titles. Deeper text is being translated incrementally
// — anywhere a string still shows in English in the RU mode is
// fair game for a follow-up pass.
// ─────────────────────────────────────────────────────────────
const LANG_STORAGE_KEY = 'apm_lang';

const LANG_DICT = {
  en: {
    // login
    'login.title':         'eMeter',
    'login.subtitle':      'Automated Metering Intelligence — sign in to continue',
    'login.username':      'Username',
    'login.password':      'Password',
    'login.submit':        'Sign in',
    'login.failed':        'Login failed',
    // sidebar tabs (the leading emoji stays, only the word changes)
    'tab.dashboard':       'Dashboard',
    'tab.customers':       'Customers',
    'tab.meters':          'Meters',
    'tab.device_mgmt':     'Device Management',
    'tab.relay_control':   'Relay Control',
    'tab.reports':         'Reports',
    'tab.live_billing':    'Live Billing',
    'tab.admin_tools':     'Admin Tools',
    'tab.system':          'System',
    'tab.api_management':  'API Management',
    'tab.windows_app_mgmt': 'Windows App Management',
    // API Management — sub-tabs and self-service key panel
    'apim.subtab_keys':         '🔑 My API Keys',
    'apim.subtab_docs':         '📖 API Reference',
    'apim.intro':               'Issue API keys for your account. Each key inherits your customer and meter scope — the key can read what your dashboard account can read, nothing more. Use the API Reference tab for endpoint documentation.',
    'apim.btn_issue':           '➕ Issue new key',
    'apim.btn_refresh':         '🔄 Refresh',
    'apim.flash_issued':        '✓ New key issued — copy it NOW. It will never be shown again.',
    'apim.btn_copy':            '📋 Copy',
    'apim.btn_dismiss':         'Dismiss',
    'apim.toast_copied':        'Copied to clipboard',
    'apim.col_label':           'Label',
    'apim.col_prefix':          'Prefix',
    'apim.col_created':         'Created',
    'apim.col_last_used':       'Last used',
    'apim.col_state':           'State',
    'apim.cell_never':          'never',
    'apim.cell_active':         '● Active',
    'apim.cell_revoked':        '● Revoked',
    'apim.btn_revoke':          '🗑 Revoke',
    'apim.btn_delete':          '✕ Delete',
    'apim.confirm_delete_q':    'Permanently delete this key?',
    'apim.confirm_delete_b':    'This removes the api_keys row entirely. Cannot be undone. Use Revoke instead if you might need to inspect this key later — Revoke keeps the row + history; Delete only keeps audit trail (without the bound-key link).',
    'apim.toast_deleted':       'Key permanently deleted',
    'apim.empty_keys':          'No API keys issued yet.',
    'apim.confirm_revoke_q':    'Revoke this key?',
    'apim.confirm_revoke_b':    'Subsequent requests with this key will return 401. Cannot be undone.',
    'apim.toast_revoked':       'Key revoked',
    'apim.modal_title':         'Issue new API key',
    'apim.modal_label':         'Label (free text — shows in the key list)',
    'apim.modal_label_ph':      'e.g. BI dashboard integration',
    'apim.modal_hint':          'The key inherits your customer / meter scope automatically.',
    'apim.modal_cancel':        'Cancel',
    'apim.modal_submit':        'Issue key',
    // External Servers — vendor-side admin tab listing customer installs
    'tab.ext_servers':           'External Servers',
    'extsrv.title':              '📡 External Servers',
    'extsrv.intro':              'Customer eMeter installs that check in to this central. Each row is one on-prem install reporting meter counts on its heartbeat cadence. Use Revoke to disable a customer’s service; Restore to bring it back.',
    'extsrv.btn_register':       '➕ Register install',
    'extsrv.btn_refresh':        '🔄 Refresh',
    'extsrv.empty':              'No customer installs registered yet. Register one to mint a check-in secret for the build pipeline.',
    'extsrv.col_customer':       'Customer',
    'extsrv.col_install':        'Install ID',
    'extsrv.col_fqdn':           'FQDN / IP',
    'extsrv.col_last_checkin':   'Last check-in',
    'extsrv.col_meters':         'Meters',
    'extsrv.col_status':         'Status',
    'extsrv.col_actions':        'Actions',
    'extsrv.cell_never':         'never',
    'extsrv.status_active':      '● Active',
    'extsrv.status_stale':       '⚠ Stale',
    'extsrv.status_revoked':     '⛔ Revoked',
    'extsrv.btn_revoke':         '🔴 Revoke',
    'extsrv.btn_restore':        '🟢 Restore',
    'extsrv.btn_delete':         '✕ Delete',
    'extsrv.btn_view_summary':   'View last summary',
    'extsrv.btn_hide_summary':   'Hide summary',
    'extsrv.confirm_revoke_q':   'Revoke this customer install?',
    'extsrv.confirm_revoke_b':   'On the next heartbeat the customer’s service returns 503 on every /api/* path except login. Vendor admin (this account) bypasses the gate, so we can still log in to diagnose. Cannot be reversed without a Restore.',
    'extsrv.confirm_restore_q':  'Restore this customer install?',
    'extsrv.confirm_restore_b':  'On the next heartbeat the customer’s service is re-enabled. Audit logged.',
    'extsrv.confirm_delete_q':   'Permanently delete this install record?',
    'extsrv.confirm_delete_b':   'Removes the row entirely. The install’s heartbeat will start failing (no matching install_id on central). Use Revoke instead if you only want to stop the service — Delete is for installs that are decommissioned.',
    'extsrv.toast_revoked':      'Install revoked',
    'extsrv.toast_restored':     'Install restored',
    'extsrv.toast_deleted':      'Install record deleted',
    'extsrv.toast_registered':   'Install registered — copy the secret NOW',
    'extsrv.modal_title':        'Register customer install',
    'extsrv.modal_customer':     'Customer name',
    'extsrv.modal_customer_ph':  'e.g. BigBag Energy',
    'extsrv.modal_fqdn':         'FQDN or IP (optional)',
    'extsrv.modal_fqdn_ph':      'e.g. meters.customer.com',
    'extsrv.modal_hint':         'A check-in secret will be generated server-side and shown ONCE. The build pipeline bakes it into the customer’s install-manifest.json.',
    'extsrv.modal_submit':       'Register',
    'extsrv.flash_secret':       '✓ Install registered. Copy the secret + install_id NOW — the secret cannot be retrieved later.',
    'extsrv.label_install_id':   'Install ID:',
    'extsrv.label_secret':       'Check-in secret (base64):',
    'extsrv.btn_copy':           '📋 Copy',
    'extsrv.btn_dismiss':        'Dismiss',
    'extsrv.reason_label':       'Reason (shown in audit log + sent to the customer):',
    'extsrv.reason_ph':          'e.g. Subscription expired 2026-04-30',
    'extsrv.summary_title':      'Last heartbeat summary',
    // common buttons
    'btn.read':            'Read',
    'btn.refresh':         'Refresh',
    'btn.back_to_list':    '← Back to list',
    'btn.search':          'Search',
    'btn.save':            'Save',
    'btn.delete':          'Delete',
    'btn.edit':            'Edit',
    'btn.rename':          'Rename',
    'modal.rename_meter':  'Rename meter',
    'modal.edit_meter_fields': 'Edit meter details',
    'form.ct_ratio_warning': 'Reports calculate with the current CT ratio, including past periods. Changing it changes the kWh shown for this meter\'s earlier dates too.',
    'modal.rename_transformer': 'Rename transformer',
    'form.new_name':       'New name',
    'toast.renamed':       'Renamed',
    'btn.add':             'Add',
    'btn.cancel':          'Cancel',
    'btn.close':           'Close',
    'btn.export_csv':      'CSV',
    'btn.export_xlsx':     'XLSX',
    'btn.read_status':     'Read Status',
    'btn.read_calendar':   'Read Calendar',
    'btn.read_selected':   'Read Selected',
    'btn.disconnect':      'Disconnect',
    'btn.reconnect':       'Reconnect',
    'btn.reset':           'Reset',
    'btn.logout':          'Log out',
    'btn.change_pw':       'Change password',
    'btn.all':             'All',
    'btn.none':            'None',
    // KPIs
    'kpi.total_meters':         'Total meters',
    'kpi.online':               'Online',
    'kpi.offline':              'Offline',
    'kpi.energy_today':         'Energy today',
    'kpi.energy_month':         'Energy this month',
    'kpi.assigned_meters':      'Assigned meters',
    'kpi.unassigned_meters':    'Unassigned meters',
    'kpi.abnormal_plc':            'Abnormal PLC Connections',
    'plc.abnormal_none':           'none detected',
    'plc.abnormal_unknown':        'unknown meters on PLC bus · click to inspect',
    'plc.abnormal_unknown_cross':  'unknown · +{n} on a nearby DCU · click to inspect',
    'plc.abnormal_cross':          '{n} meter(s) heard by a nearby DCU (ok) · click to view',
    'kpi.session_open':         'Session open',
    'kpi.relay_off':            'Relay off',
    'keygap.title':             'Commissioning blockers',
    'keygap.tile_hint':         'Show which meters have not commissioned, and whether keys are the reason',
    'keygap.installed':         'installed',
    'keygap.commissioned':      'commissioned',
    'keygap.blocked':           'blocked on keys',
    'keygap.copy':              'Copy serials',
    'keygap.copied':            'serial(s) copied',
    'keygap.was_commissioned':  'commissioned earlier — keys since lost',
    'keygap.g_none':            'No key record',
    'keygap.g_none_hint':       'Nothing in the key store for these. They cannot open an HLS5 session, so they can never commission — import their keys.',
    'keygap.g_partial':         'Incomplete GMAC keys',
    'keygap.g_partial_hint':    'A record exists but the auth / block-cipher pair is not complete. HLS5 needs both — re-import from the source file.',
    'keygap.g_keyed':           'Keyed, but never commissioned',
    'keygap.g_keyed_hint':      'Keys are present and usable, so this is NOT a key problem — check the link, DCU registration and whether the meter has ever dialled in.',
    'keygap.all_clear':         'Every installed meter is commissioned and holds its keys.',
    'kpi.meters_installed':     'Meters installed',
    // Meter detail sub-tabs
    'mdt.home':            'Home',
    'mdt.event':           'Event',
    'mdt.readings':        'Meter Readings',
    'mdt.relay':           'Relay Control',
    'mdt.collection':      'Collection Status',
    'mdt.installation':    'Installation Status',
    'mdt.tariff':          'Tariff',
    'mdt.network':         'Network Config',
    // Network config tab
    'net.admin_only':       'Remote IP / APN changes are admin-only, high-risk actions.',
    'net.kill_switch':      'Remote writes are kill-switched at the server (EMETER_REMOTE_WRITES=disabled).',
    'net.kill_switch_dry':  'Dry-run mode — PDU built and logged but NOT transmitted.',
    'net.target_dcu':       'Target: DCU (Holley concentrator)',
    'net.target_meter':     'Target: meter (via DCU SAP passthrough)',
    'net.target_direct':    'Target: meter (direct GPRS session)',
    'net.current':          'Current configuration',
    'net.server_ip':        'Server IP',
    'net.server_port':      'Server port',
    'net.apn':              'APN',
    'net.heartbeat_s':      'Heartbeat (seconds)',
    'net.new_server_ip':    'New server IP',
    'net.new_port':         'New port',
    'net.new_apn':          'New APN',
    'net.new_hb':           'New heartbeat (seconds)',
    'net.probe_tcp':        'Test TCP reachability',
    'net.probe_ok':         '✓ Reachable',
    'net.probe_fail':       '✗ Unreachable — DO NOT APPLY',
    'net.probe_hint':       'Before applying, confirm the new IP:port is reachable from the eMeter server.',
    'net.type_serial':      'Type the full meter/DCU serial to unlock Apply:',
    'net.apply':            'Apply change',
    'net.read':             'Read current',
    'net.apn_over_plc':     'APN change via PLC is blocked — the meter would drop GPRS and we\'d lose the PLC lifeline.',
    'net.blast_warn':       '⚠ Changing IP/port/APN can take the DCU or meter offline until recovered via optical port — double-check the target before applying.',
    'net.dry_suffix':       '(DRY-RUN)',
    // Meter readings sub-tabs
    'mr.daily':            'Daily',
    'mr.monthly':          'Monthly',
    'mr.load_profile':     'Load Profile',
    'mr.on_demand':        'On Demand',
    'mr.power_quality':    'Power Quality',
    'mr.period':           'Period Consumption',
    'period.calculate':    'Calculate',
    'period.start':        'Start Reading',
    'period.end':          'End Reading',
    'period.consumption':  'Consumption',
    'period.no_data':      'No daily billing data found for this date range.',
    'toast.map_load_failed':       'Leaflet failed to load — check the network',
    'toast.map_tiles_fallback':    'Primary map tiles failed; using OpenStreetMap fallback',
    'toast.refuse_delete_self':    'Refusing to delete yourself',
    'toast.profile_meta_missing':  'Profile metadata not loaded yet',
    'toast.pick_dates':            'Pick a from and to date',
    'toast.to_before_from':        'Cannot derive entry indices from dates',
    'toast.billing_loaded':        'Billing data loaded',
    // System sub-tabs
    'sys.scheduler':       'Scheduler',
    'sys.poller':          'Poller',
    'sys.keys':            'Keys',
    'sys.users':           'Users',
    'sys.security':        'Security',
    'sys.health':          'Fleet Health',
    'sys.server_health':   'Server Health',
    'sys.com_log':         'COM Log',
    // Admin Tools sub-tabs
    'adm.load_curve':      'Load Curve',
    'adm.read_meter':      'Read Meter',
    'adm.write_meter':     'Write Meter',
    'adm.activity_log':    'User Activity Log',
    // Misc
    'common.language':     'Language',
    'common.loading':      'Loading…',
    'common.no_data':      'No data',
    'common.from':         'From',
    'common.to':           'To',
    'common.date':         'Date',
    'common.time':         'Time',
    'common.user':         'User',
    'common.action':       'Action',
    'common.customer':     'Customer',
    'common.meter':        'Meter',
    'common.target':       'Target',
    'common.details':      'Details',
    'common.status':       'Status',
    'common.online':       'online',
    'common.offline':      'offline',
    'common.connected':    'connected',
    'common.owner':        'Owner',
    'common.location':     'Location',
    'common.created':      'Created',
    'common.total':        'Total',
    'common.yes':          'yes',
    'common.no':           'no',
    'common.none_assigned': 'None assigned yet.',
    'tf.meters_on':        'Meters on this transformer',
    'th.conn':             'Conn',
    'th.ct_vt':            'CT/VT',
    'role.main':           'main',
    'role.customer':       'customer',
    'btn.assign_dcu':      'Assign DCU',
    'tip.create_customer_first': 'Create a customer first',
    'tip.create_transformer_first': 'Create a transformer first',
    'msg.readonly_hierarchy': "Read-only — your role doesn't allow editing the hierarchy.",
    'msg.loading_hierarchy': 'Loading hierarchy…',
    'msg.no_customers_yet': 'No customers yet.',
    'msg.no_transformers_yet': 'No transformers yet.',
    'msg.no_meters_assigned': 'No meters assigned yet.',
    'msg.no_readings_yet': 'No readings collected yet.',
    'msg.click_refresh_relay': 'click Refresh',
    'msg.relay_unknown': 'Relay ?',
    'label.relay_status':  'Relay Status',
    'label.read_at':       'Read at',
    'ph.search_hierarchy':  'Search customer, account, meter…',
    'tree.all_customers_home': 'All Customers · AMI Home',
    'tree.collapse_all':      'Collapse all',
    'tree.collapse_all_hint': 'Close every open customer, transformer and DCU in the tree',
    'card.transformers_count': 'Transformers',
    'card.contact':           'Contact',
    'card.notes':             'Notes',
    'card.device_status':     'Device Status by Meter Type',
    'section.live_readings':  'Live readings',
    'kpi.installed':          'Installed',
    'kpi.commissioned':       'Commissioned',
    'th.installed':           'Installed',
    'th.commissioned':        'Commissioned',
    'th.on':                  'On',
    'th.off':                 'Off',
    'th.no_relay':            'No relay',
    'th.register':            'Register',
    'group.instantaneous':    'Instantaneous',
    'group.energy':           'Energy',
    'group.other':            'Other',
    'badge.live':             '🟢 Live',
    'badge.offline':          '🔴 Offline',
    'common.address':         'Address',
    'common.phone':           'Phone',
    'common.email':           'Email',
    'common.live':            'live',
    'th.meter_type':          'Meter Type',
    'th.location':            'Location',
    'th.meters_count':        'Meters',
    'th.online_status':       'Online',
    'ph.add_note':            'Add a note (payment reminder, service remark, ops note…)',
    'btn.add_note':           'Add note',
    'common.no_transformers': 'No transformers yet.',
    'common.no_notes':        'No notes yet.',
    'common.no_meters_assigned': 'No meters assigned yet.',
    'card.transformers':      'Transformers',
    'card.notes_count':       'Notes',
    'common.account_number':  'Account',
    'tab.home':               'Home',
    'tab.event':              'Event',
    'tab.readings':           'Meter Readings',
    'tab.relay':              'Relay Control',
    'tab.collection':         'Collection Status',
    'tab.installation':       'Installation Status',
    'tab.tariff':             'Tariff',
    'tcsc.collection_rate':   'Collection Rate',
    'tcsc.daily_billing':     'Daily Billing',
    'tcsc.load_profile':      'Load Profile',
    'tcsc.th_daily':          'Daily',
    'tcsc.th_load':           'Load',
    'tcsc.ok':                'OK',
    'tcsc.fail':              'Fail',
    'common.meters_count':    'meters',
    'common.from':            'From',
    'common.to':              'To',
    // modal titles
    'modal.new_customer':     'New Customer',
    'modal.edit_customer':    'Edit Customer',
    'modal.new_transformer':  'New Transformer',
    'modal.edit_transformer': 'Edit Transformer',
    'modal.assign_meter':     'Assign Meter',
    'modal.edit_assignment':  'Edit Meter Assignment',
    'modal.change_password':  'Change Password',
    // form labels
    'form.name':              'Name',
    'form.name_required':     'Name (required)',
    'form.account_number':    'Account number',
    'form.address':           'Address',
    'form.phone':             'Phone',
    'form.email':             'Email',
    'form.customer':          'Customer',
    'form.customer_required': 'Customer (required)',
    'form.location':          'Location',
    'form.role':              'Role',
    'form.object_name':       'Object name',
    'form.comm_addr':         'Comm Addr (DLMS short)',
    'form.install_desc':      'Install description',
    'form.meter_type':        'Meter type override',
    'form.connection_type':   'Connection type',
    'form.ct_ratio':          'CT Ratio',
    'form.vt_ratio':          'VT Ratio',
    'form.latitude':          'Latitude',
    'form.longitude':         'Longitude',
    'form.current_password':  'Current password',
    'form.new_password':      'New password',
    'form.confirm_password':  'Confirm new password',
    'form.username':          'Username',
    'form.full_name':         'Full name',
    'form.password':          'Password',
    'form.password_hint':     'Password (min 8 chars)',
    'form.new_pw_hint':       'New password (leave blank to keep current)',
    'form.ct_vt_help':        'CT and VT ratios are configured per meter when the meter is assigned to this transformer.',
    // toast messages (static)
    'toast.customer_created':     'Customer created',
    'toast.customer_updated':     'Customer updated',
    'toast.customer_deleted':     'Customer deleted',
    'toast.transformer_created':  'Transformer created',
    'toast.transformer_updated':  'Transformer updated',
    'toast.transformer_deleted':  'Transformer deleted',
    'toast.meter_assigned':       'Meter assigned',
    'toast.assignment_updated':   'Assignment updated',
    'toast.assignment_deleted':   'Meter assignment deleted',
    'toast.user_created':         'User created',
    'toast.user_updated':         'User updated',
    'toast.user_deleted':         'User deleted',
    'toast.password_updated':     'Password updated',
    'toast.note_added':           'Note added',
    'toast.note_deleted':         'Note deleted',
    'toast.read_complete':        'Read complete',
    'toast.xlsx_downloaded':      'XLSX downloaded',
    'toast.relay_refreshed':      'Relay status refreshed',
    'toast.calendar_read':        'Calendar read',
    'toast.device_info_read':     'Device info refreshed',
    'toast.collection_loaded':    'Collection stats loaded',
    // toast (dynamic with {vars})
    'toast.pick_meter_first':     'Pick a meter first',
    'toast.pick_customer_first':  'Pick a customer first',
    'toast.nothing_selected':     'Nothing selected',
    'toast.poller_set':           'Poller interval set to {n}s',
    'toast.session_opened':       'Secure session opened with {serial}',
    'toast.session_closed':       'Secure session closed for {serial}',
    'toast.relay_sent':           'Relay {action} sent',
    'toast.loaded_n':             'Loaded {n} {what}',
    'toast.n_registers_read':     '{panel}: {n} register(s) read',
    'toast.report_downloaded':    '{kind} report downloaded',
    'toast.keys_imported':        'Imported {n} keys (store now contains {total})',
    'toast.keys_cleared':         'All keys cleared',
    'toast.interval_invalid':     'Interval must be between 5 and 3600 seconds',
    'toast.pw_no_match':          'New passwords do not match',
    'toast.pw_too_short':         'Password must be at least 8 characters',
    'toast.no_assignments':       'This customer has no meter assignments yet',
    'toast.scheduler_running':    'Running midnight collection…',
    'toast.scheduler_done':       'Scheduler done: {summary}',
    // section titles
    'section.dashboard':          'Dashboard',
    'section.admin_tools':        'Admin Tools',
    'section.system':             'System',
    'section.connected_meters':   'Connected Meters',
    'section.read_meter':         'Read Meter',
    'section.write_meter':        'Write Meter',
    'section.load_curve':         'Load Curve',
    'section.events':             'Event Logs',
    'section.customers':          'Customers',
    'section.users':              'Users',
    'section.reports':            'Reports',
    'section.billing':            'Billing',
    'section.meter_keys':         'Meter Keys',
    'section.notes':              'Notes',
    'section.polling_interval':   'Polling interval',
    // table headers
    'th.serial':           'Serial',
    'th.type':             'Type',
    'th.assignment':       'Assignment',
    'th.manufacturer':     'Manufacturer',
    'th.firmware':         'Firmware',
    'th.status':           'Status',
    'th.connected':        'Connected',
    'th.last_heartbeat':   'Last HB',
    'th.actions':          'Actions',
    'th.timestamp':        'Timestamp',
    'th.value':            'Value',
    'th.obis':             'OBIS',
    'th.direction':        'Direction',
    'th.length':           'Length',
    'th.hex_data':         'Hex Data',
    'th.username':         'Username',
    'th.full_name':        'Full name',
    'th.role':             'Role',
    'th.active':           'Active',
    'th.customers_access': 'Customers',
    'th.last_login':       'Last login',
    'th.name':             'Name',
    'th.date':             'Date',
    'th.event_name':       'Event',
    'th.event_code':       'Code',
    'th.no':               '#',
    'th.meter_no':         'Meter No',
    'th.ct_vt':            'CT · VT',
    'th.online_status':    'Online',
    'th.latest_row':       'Latest row',
    // empty states / messages
    'msg.nothing_selected':     'Nothing selected',
    'msg.no_data_yet':          'No data yet',
    'msg.admin_required':       'Admin access required.',
    'msg.pick_meter':           'Pick a meter from the tree on the left.',
    'msg.no_meters':            'No meters connected',
    'msg.authenticating':       'Authenticating…',
    'msg.no_notes':             'No notes yet.',
    'msg.meters_connected':     '{n} meter(s) connected',
    'msg.confirm_delete':       'Are you sure you want to delete this?',
    // buttons
    'btn.new_customer':     'New Customer',
    'btn.new_transformer':  'New Transformer',
    'btn.assign_meter':     'Assign Meter',
    'btn.new_user':         'New User',
    'btn.update':           'Update',
    'btn.create':           'Create',
    'btn.confirm':          'Confirm',
    'btn.run_midnight':     'Run now',
    'btn.open_session':     'Open',
    'btn.close_session':    'Close',
    'btn.load_billing':     'Load Billing',
    'btn.load_history':     'Load history',
    'btn.load_events':      'Load Events',
    // assign meter specific
    'assign.role_customer':   'Customer meter',
    'assign.role_main':       'Main meter (transformer-side)',
    'assign.auto_detect':     'Auto-detect',
    'assign.connection_direct':'Direct',
    'assign.connection_ct':   'CT',
    'assign.connection_ct_vt':'CT/VT',
    // idle timer
    'idle.title':           'Session Timeout',
    'idle.warning':         'You will be logged out in {n} seconds due to inactivity.',
    'idle.stay':            'Stay logged in',
    'idle.logout_now':      'Log out now',
    'idle.setting_label':   'Auto-logout timeout',
    'idle.never':           'Never',
    'idle.minutes':         '{n} min',
    'meter.status':                  'Status',
    'meter.meter_collection':        'Meter Collection',
    'meter.relay_control_tasks':     'Relay Control Tasks',
    'meter.online':                  'Online',
    'meter.offline':                 'Offline',
    'meter.power_on':                'Power On',
    'meter.power_off':               'Power Off',
    'meter.daily_profile':           'Daily Profile',
    'meter.monthly_load_profile':    'Monthly / Load Profile',
    'meter.failed':                  'Failed',
    'meter.succeed':                 'Succeed',
    'meter.cancelled':               'Cancelled',
    'meter.meter_no':                'Meter No.',
    'meter.logical_no':              'Logical No',
    'meter.meter_type':              'Meter Type',
    'meter.meter_status':            'Meter Status',
    'meter.commissioned_live':       'Commissioned \u00b7 Live',
    'meter.commissioned_off':        'Commissioned \u00b7 Off',
    'meter.not_commissioned':        'Not commissioned',
    'meter.general':                 'General',
    'meter.archive':                 'Archive',
    'meter.installations':           'Installations',
    'meter.seals_info':              'Seals Info',
    'meter.vector_chart':            'Vector Chart',
    'meter.work_reply':              'Work Reply',
    'meter.operator_tasks':          'Operator tasks',
    'meter.billing_data_snapshot':   'Billing data snapshot',
    'meter.communication':           'Communication',
    'meter.read_device_info':        'Read device info',
    'meter.read_device_info_title':  'Read model, firmware, production date from the meter',
    'meter.open_secure_session_first': 'Open a secure session first',
    'meter.live_reads_require_session': 'Live reads require an open secure session',
    // dashboard
    'dashboard.energy_chart_title':  'Daily energy consumption (last 30 days)',
    'dashboard.hint_pick_meter':     'Pick a meter from the Customers hierarchy to see its full detail view (Home · Events · Readings · On-Demand · Relay · Tariff).',
    'dashboard.chart_no_data':       'No data yet. The midnight scheduler will start populating this tomorrow.',
    // poller
    'poller.running':                '● Running',
    'poller.stopped':                '● Stopped',
    'poller.currently':              'currently {n}s',
    'poller.custom':                 'Custom',
    'poller.seconds':                'seconds',
    // scheduler
    'scheduler.title':               'Midnight scheduler',
    'scheduler.fires_at':            'Fires at',
    'scheduler.last_run':            'Last run',
    'scheduler.never':               'never',
    // meters tab
    'meters.assigned':               'Assigned',
    'meters.unassigned':             'Unassigned',
    'meters.waiting':                'Waiting for meters to connect on port 8002…',
    'meters.no_match_filter':        'No meters match the {filter} filter. ',
    'meters.show_all':               'Show all',
    'meters.release_session':        'Release GMAC secure session',
    // com log
    'comlog.connected':              'Connected',
    'comlog.disconnected':           'Disconnected',
    'comlog.waiting':                'Waiting for communication frames...',
    // relay tab
    'relay.unknown': 'unknown', 'relay.disconnected': 'disconnected', 'relay.connected': 'connected',
    'relay.ready_reconnect': 'ready for reconnection', 'relay.refresh_failed': 'Refresh failed',
    'relay.disconnect_upper': 'DISCONNECT', 'relay.reconnect_upper': 'RECONNECT',
    'relay.confirm_prefix': 'the relay on meter', 'relay.confirm_power': 'This physically switches power',
    'relay.off': 'OFF', 'relay.on': 'ON',
    'relay.confirm_suffix': 'at the meter. Any downstream equipment will be affected.',
    'relay.action_failed': 'failed', 'relay.events': 'relay events',
    'relay.history_load_failed': 'History load failed', 'relay.relay_status': 'Relay status',
    'relay.click_read': 'click Read Status', 'relay.read_at': 'Read at',
    'relay.clear_status_title': 'Clear the displayed status',
    'relay.session_required': 'Relay control requires an open secure session.',
    'relay.history': 'History', 'relay.history_30d': 'Relay history (last 30 days)',
    'relay.click_load_hint': 'Click Load to read the Disconnect Control event log (OBIS 0.0.99.98.2.255).',
    'relay.logical_device_name': 'Logical device name', 'relay.last_heartbeat': 'Last heartbeat',
    'relay.admin_only': 'Relay disconnect/reconnect requires operator or admin access.',
    'relay.kill_switch': 'Remote writes are kill-switched at the server (EMETER_REMOTE_WRITES=disabled).',
    'relay.kill_switch_dry': 'Dry-run mode — actions build the PDU but do NOT send.',
    'relay.type_serial': 'Type the full 12-digit meter serial to enable actions:',
    'relay.control_mode': 'Control mode', 'relay.output_state': 'Output state',
    'relay.ctrl_state': 'Control state',
    'relay.mode_hint_5': 'Mode 5 — remote disconnect + remote reconnect supported',
    'relay.mode_hint_3': 'Mode 3 — disconnect remote, reconnect requires manual action',
    'relay.mode_hint_2': 'Mode 2 — disconnect remote, reconnect requires local button',
    'relay.mode_hint_1': 'Mode 1 — direct remote or local connect/disconnect',
    'relay.mode_hint_0': 'Mode 0 — local (manual) only, no remote control',
    'relay.mode_hint_other': 'Mode {n} — see DLMS §15',
    'relay.bus': 'Bus', 'relay.bus_direct': 'Direct (GPRS)',
    'relay.bus_plc': 'PLC (via DCU)', 'relay.bus_rf': 'RF (via DCU)',
    'relay.bus_rs485': 'RS-485 (via DCU)',
    // Relay Control tab + Relay Operation Log ('rlog.*').
    // reason_* labels are display-only — the CODE in RELAY_REASONS is what
    // goes on the wire, so translating these can never change what is stored.
    'relay.reason_debt': 'Debt', 'relay.reason_debt_paid': 'Debt paid',
    'relay.reason_maintenance': 'Maintenance', 'relay.reason_tamper': 'Tamper',
    'relay.reason_other': 'Other',
    'relay.badge_on': 'Relay On', 'relay.badge_off': 'Relay Off', 'relay.badge_unknown': 'Unknown',
    'rlog.title': 'Relay Operation Log',
    'rlog.cmd_history': 'eMeter command history',
    'rlog.none_for_meter': 'No relay commands issued for this meter yet.',
    // columns
    'rlog.col_status': 'Status', 'rlog.col_action': 'Action', 'rlog.col_mode': 'Mode',
    'rlog.col_reason': 'Reason', 'rlog.col_attempts': 'Attempts', 'rlog.col_by': 'By',
    'rlog.col_requested': 'Requested', 'rlog.col_confirmed': 'Confirmed',
    'rlog.col_meter_no': 'Meter No.', 'rlog.col_dcu': 'DCU No',
    'rlog.col_last_attempt': 'Last attempt', 'rlog.col_operate': 'Operate',
    'rlog.col_account': 'Account ID', 'rlog.col_customer': 'Customer', 'rlog.col_tr': 'TR',
    'rlog.col_meter_type': 'Meter type', 'rlog.col_comm': 'Comm', 'rlog.col_relay': 'Relay',
    'rlog.col_refresh': 'Refreshed',
    // delivery mode
    'rlog.mode_immediate': 'immediate', 'rlog.mode_deferred': 'deferred',
    'rlog.mode_immediate_long': 'Immediate (≤60s, else timeout)',
    'rlog.mode_deferred_long': 'Deferred (runs when meter online)',
    // verdicts — see relayVerdict(). Open states say why we are still waiting;
    // terminal states say what happened and never contradict that.
    'rlog.v_sending': 'Sending…',
    'rlog.v_busy': 'Busy — PLC child unreachable, retrying',
    'rlog.v_transport': 'Link failure — retrying',
    'rlog.v_offline': 'Meter offline — waiting',
    'rlog.v_queue_busy': 'DCU queue busy — waiting',
    'rlog.v_requeued': 'Re-queued after restart',
    'rlog.v_queued': 'Queued',
    'rlog.v_already': 'Already in state',
    'rlog.v_armed': 'Armed — needs manual action at meter',
    'rlog.v_drift': 'Mismatch — relay not in requested state',
    'rlog.v_success': 'Success',
    'rlog.v_cancelled': 'Cancelled',
    'rlog.v_superseded': 'Replaced by a newer command',
    'rlog.v_expired': 'Expired',
    'rlog.v_timeout': 'Timed out',
    'rlog.v_failed': 'Failed',
    'rlog.v_failed_max': 'Failed — retries exhausted',
    // page chrome
    'rlog.embedded_hint_prefix': 'Relay Control — meters on DCU',
    'rlog.embedded_hint_suffix': 'Tick rows (shift-click for a range) and use the bulk bar; or open a meter for single control + history.',
    'rlog.banner_killed': 'Remote writes are kill-switched (EMETER_REMOTE_WRITES=disabled). Commands cannot be issued until an admin enables them on the server.',
    'rlog.banner_dry_run': 'Dry-run mode — commands build the PDU and record history but do NOT change the relay.',
    'rlog.kpi_total': 'Total', 'rlog.kpi_connected': 'Connected',
    'rlog.kpi_disconnected': 'Disconnected', 'rlog.kpi_unknown': 'Unknown',
    'rlog.kpi_open_commands': 'Open commands',
    'rlog.search_placeholder': 'Search serial / account / customer…',
    'rlog.btn_search': 'Search', 'rlog.btn_refresh': 'Refresh',
    'rlog.all_comm_types': 'All comm types', 'rlog.all_relay_states': 'All relay states',
    'rlog.n_selected': '{n} selected', 'rlog.n_meters': '{n} meters',
    'rlog.tip_shift_range': 'Shift-click to select a range',
    'rlog.tip_unsupported': 'Relay control not supported (not DCU-routed)',
    'rlog.no_meters_match': 'No meters match the filter.',
    'rlog.prev': 'Prev', 'rlog.page': 'Page', 'rlog.next': 'Next',
    // bulk bar
    'rlog.lbl_action': 'Action', 'rlog.lbl_delivery': 'Delivery',
    'rlog.act_disconnect': 'Disconnect (OFF)', 'rlog.act_reconnect': 'Reconnect (ON)',
    'rlog.remark_placeholder': 'Remark (optional)',
    'rlog.confirm_warn': 'Disconnect cuts power. Type',
    'rlog.confirm_suffix': 'to confirm:',
    'rlog.btn_disconnect_n': 'Disconnect {n} meter(s)',
    'rlog.btn_reconnect_n': 'Reconnect {n} meter(s)',
    'rlog.btn_clear_sel': 'Clear selection',
    'rlog.btn_read_status_n': 'Read relay status ({n})',
    'rlog.toast_read_queued': 'Relay status read queued for {n} meter(s) — results fill in as each meter answers',
    'rlog.toast_read_already': ', {n} already queued', 'rlog.toast_read_not_routed': ', {n} not behind a DCU',
    'rlog.toast_read_failed': 'Read status failed',
    // log panel
    'rlog.total': 'Total', 'rlog.btn_hide': 'Hide', 'rlog.btn_show': 'Show',
    'rlog.all_tasks': 'All tasks', 'rlog.task_switch_off': 'Switch-Off',
    'rlog.task_switch_on': 'Switch-On', 'rlog.btn_clear_filters': 'Clear filters',
    'rlog.next_retry': 'Next retry', 'rlog.btn_detail': 'Detail', 'rlog.btn_cancel': 'Cancel',
    'rlog.no_ops_match': 'No relay operations match these filters.',
    'rlog.per_page': '{n}/page',
    // detail drawer
    'rlog.detail_title': 'Relay operation', 'rlog.btn_close': 'Close',
    'rlog.detail_raw': 'Engine detail', 'rlog.detail_error_class': 'Error class',
    'rlog.detail_first_sent': 'First sent', 'rlog.detail_batch': 'Batch',
    'rlog.state_before': 'before', 'rlog.state_after': 'after',
    // toasts
    'rlog.toast_select_one': 'Select at least one meter',
    'rlog.toast_type_confirm': 'Type "{phrase}" to confirm',
    'rlog.toast_queued': '{n} command(s) queued', 'rlog.toast_skipped': ', {n} skipped',
    'rlog.dry_run_tag': 'DRY-RUN',
    'rlog.toast_submit_failed': 'Submit failed', 'rlog.toast_cancelled': 'Command cancelled',
    'rlog.toast_cancel_failed': 'Cancel failed', 'rlog.toast_list_failed': 'Relay list failed',
    // tariff tab
    'tariff.session_required': 'Tariff calendar reads require an open secure session.',
    'tariff.activity_calendar': 'Activity calendar', 'tariff.last_read_at': 'Last read at',
    'tariff.edit_admin': 'Edit (admin)', 'tariff.edit_disabled': 'Calendar write is disabled in this build',
    'tariff.current_active': 'Current Active Tariff', 'tariff.from_register': 'from register',
    'tariff.refreshed_at': 'refreshed at', 'tariff.active_calendar': 'Active Calendar',
    'tariff.click_read': 'Click Read Calendar to fetch.',
    'tariff.empty_warning': 'This meter does not expose the activity calendar via a simple attribute-2 read.',
    'tariff.active_cal_label': 'Active calendar', 'tariff.passive_cal_label': 'Passive calendar',
    'tariff.special_days': 'Special days table', 'tariff.editor_planned': 'Editor (planned)',
    'tariff.editor_desc': 'A full Tariff write UI is planned for the next iteration.',
    // installation tab
    'install.identification': 'Identification', 'install.hierarchy': 'Hierarchy',
    'install.connection': 'Connection', 'install.install_metadata': 'Install metadata',
    'install.firmware': 'Firmware', 'install.production_date': 'Production date',
    'install.click_read': 'click Read', 'install.customer_account': 'Customer account #',
    'install.transformer': 'Transformer', 'install.transformer_loc': 'Transformer location', 'install.dcu': 'DCU',
    'install.role_main': 'main (transformer-side)', 'install.role_customer': 'customer',
    'install.seals_vector': 'Seals & vector check',
    'install.seals_not_collected': 'Seal status and vector-check results are not yet collected from the meter.',
    'install.read_dev_title': 'Read device_info from the meter',
    // collection tab
    'collection.window_days': 'Window (days)', 'collection.daily_billing': 'Daily billing',
    'collection.load_profile': 'Load profile', 'collection.day_breakdown': 'Day breakdown',
    'collection.ok': 'ok', 'collection.partial': 'partial', 'collection.missing': 'missing',
    'collection.stats_failed': 'Collection stats failed',
    // on-demand tab
    'ondemand.session_required': 'On-demand reads require an open secure session.',
    'ondemand.session_hint': 'Use the Meters > Open button to establish one.',
    // These five were referenced by t() but never defined in EITHER dictionary,
    // so they rendered as the literal key ("📋 ondemand.status") in English and
    // Russian alike. Found 2026-08-11 by diffing t() call sites against the
    // dicts. Note meter.last_seen's `|| 'Last seen'` fallback never fired —
    // t() returns the key itself when a lookup misses, which is truthy.
    'ondemand.status': 'Status', 'ondemand.energy': 'Energy',
    'ondemand.line': 'Line', 'ondemand.manufacturer': 'Manufacturer',
    'meter.last_seen': 'Last seen',
    'ondemand.panels': 'Panels', 'ondemand.read_at': 'Read at',
    'ondemand.current_status': 'Current Status', 'ondemand.conn_energy': 'Connection Status (Energy)',
    'ondemand.conn_line': 'Connection Line Pool', 'ondemand.meter_diag': 'Meter Errors / Diagnostics',
    'ondemand.serial_number': 'Serial Number', 'ondemand.firmware_ver': 'Firmware Version',
    'ondemand.prod_date': 'Production Date', 'ondemand.logical_dev': 'Logical Device Name',
    'ondemand.active_import': 'Active + (import)', 'ondemand.active_export': 'Active - (export)',
    'ondemand.reactive_import': 'Reactive + (import)', 'ondemand.reactive_export': 'Reactive - (export)',
    'ondemand.apparent_import': 'Apparent + (import)', 'ondemand.apparent_export': 'Apparent - (export)',
    'ondemand.voltage_l1': 'Voltage L1', 'ondemand.voltage_l2': 'Voltage L2', 'ondemand.voltage_l3': 'Voltage L3',
    'ondemand.current_l1': 'Current L1', 'ondemand.current_l2': 'Current L2', 'ondemand.current_l3': 'Current L3',
    'ondemand.power_factor': 'Power Factor', 'ondemand.frequency': 'Frequency',
    'ondemand.status_word': 'Meter Status Word', 'ondemand.battery_voltage': 'Battery Voltage',
    'ondemand.operating_time': 'Operating Time', 'ondemand.ct_ratio': 'CT Ratio', 'ondemand.vt_ratio': 'VT Ratio',
    'ondemand.read_group': 'Read group',
    // events tab
    'events.category': 'Category',
    'events.all_categories': 'All categories', 'events.event_search': 'Event name search',
    'events.search_hint': 'e.g. power, relay, tamper',
    'events.session_required': 'A secure session is required to read event logs from the meter.',
    'events.reading_logs': 'Reading 11 event logs...', 'events.occurred': 'Occurred',
    'events.received': 'Received',
    'events.no_events': 'No events loaded yet. Pick a date range and click Search.',
    'events.of_total': 'of', 'events.events_label': 'events',
    'events.filtered_by': 'filtered by', 'events.search_label': 'search',
    'events.loaded_events': 'Loaded {n} events',
    'events.load_failed': 'Events load failed',
    'events.exported_rows': 'Exported {n} rows',
  },
  ru: {
    // login
    'login.title':         'eMeter',
    'login.subtitle':      'Автоматизированный учёт электроэнергии — войдите, чтобы продолжить',
    'login.username':      'Имя пользователя',
    'login.password':      'Пароль',
    'login.submit':        'Войти',
    'login.failed':        'Ошибка входа',
    // sidebar tabs
    'tab.dashboard':       'Панель',
    'tab.customers':       'Абоненты',
    'tab.meters':          'Счётчики',
    'tab.device_mgmt':     'Управление устройствами',
    'tab.relay_control':   'Управление реле',
    'tab.reports':         'Отчёты',
    'tab.live_billing':    'Биллинг',
    'tab.admin_tools':     'Админ',
    'tab.system':          'Система',
    'tab.api_management':  'Управление API',
    'tab.windows_app_mgmt': 'Управление Windows-приложением',
    'apim.subtab_keys':         '🔑 Мои API-ключи',
    'apim.subtab_docs':         '📖 Справочник API',
    'apim.intro':               'Выпускайте API-ключи для вашей учётной записи. Каждый ключ наследует область видимости вашего аккаунта — ключ может читать только то, что вы видите в дашборде, ничего больше. Описание эндпоинтов — на вкладке «Справочник API».',
    'apim.btn_issue':           '➕ Выпустить ключ',
    'apim.btn_refresh':         '🔄 Обновить',
    'apim.flash_issued':        '✓ Ключ выпущен — скопируйте СЕЙЧАС. Повторно он показан не будет.',
    'apim.btn_copy':            '📋 Скопировать',
    'apim.btn_dismiss':         'Закрыть',
    'apim.toast_copied':        'Скопировано в буфер обмена',
    'apim.col_label':           'Метка',
    'apim.col_prefix':          'Префикс',
    'apim.col_created':         'Создан',
    'apim.col_last_used':       'Последнее использование',
    'apim.col_state':           'Состояние',
    'apim.cell_never':          'никогда',
    'apim.cell_active':         '● Активный',
    'apim.cell_revoked':        '● Отозван',
    'apim.btn_revoke':          '🗑 Отозвать',
    'apim.btn_delete':          '✕ Удалить',
    'apim.confirm_delete_q':    'Удалить ключ безвозвратно?',
    'apim.confirm_delete_b':    'Удаляет строку из таблицы api_keys полностью. Отменить нельзя. Если ключ может потребоваться для расследования — лучше «Отозвать»: эта операция оставляет строку + историю; «Удалить» сохраняет только аудит-лог (без связи с ключом).',
    'apim.toast_deleted':       'Ключ удалён',
    'apim.empty_keys':          'API-ключи ещё не выпускались.',
    'apim.confirm_revoke_q':    'Отозвать этот ключ?',
    'apim.confirm_revoke_b':    'Последующие запросы с этим ключом будут возвращать 401. Отменить нельзя.',
    'apim.toast_revoked':       'Ключ отозван',
    'apim.modal_title':         'Выпустить API-ключ',
    'apim.modal_label':         'Метка (произвольный текст — отображается в списке ключей)',
    'apim.modal_label_ph':      'например, «BI dashboard integration»',
    'apim.modal_hint':          'Ключ автоматически наследует вашу область видимости (абоненты / счётчики).',
    'apim.modal_cancel':        'Отмена',
    'apim.modal_submit':        'Выпустить',
    // Внешние серверы — заказчиков, отчитывающихся в центральный экземпляр
    'tab.ext_servers':           'Внешние серверы',
    'extsrv.title':              '📡 Внешние серверы',
    'extsrv.intro':              'Установки eMeter у заказчиков, отчитывающиеся на этот центральный сервер. Каждая строка — одна локальная установка, отправляющая счёт счётчиков по расписанию heartbeat. «Отозвать» — отключает сервис у заказчика; «Восстановить» — включает обратно.',
    'extsrv.btn_register':       '➕ Зарегистрировать установку',
    'extsrv.btn_refresh':        '🔄 Обновить',
    'extsrv.empty':              'Установок заказчиков пока не зарегистрировано. Зарегистрируйте одну, чтобы получить секрет check-in для билда.',
    'extsrv.col_customer':       'Заказчик',
    'extsrv.col_install':        'Install ID',
    'extsrv.col_fqdn':           'FQDN / IP',
    'extsrv.col_last_checkin':   'Последний check-in',
    'extsrv.col_meters':         'Счётчиков',
    'extsrv.col_status':         'Состояние',
    'extsrv.col_actions':        'Действия',
    'extsrv.cell_never':         'никогда',
    'extsrv.status_active':      '● Активна',
    'extsrv.status_stale':       '⚠ Нет связи',
    'extsrv.status_revoked':     '⛔ Отозвана',
    'extsrv.btn_revoke':         '🔴 Отозвать',
    'extsrv.btn_restore':        '🟢 Восстановить',
    'extsrv.btn_delete':         '✕ Удалить',
    'extsrv.btn_view_summary':   'Показать сводку',
    'extsrv.btn_hide_summary':   'Скрыть сводку',
    'extsrv.confirm_revoke_q':   'Отозвать установку у заказчика?',
    'extsrv.confirm_revoke_b':   'При следующем heartbeat сервис заказчика начнёт возвращать 503 на всех /api/* кроме входа. Вендорский админ (эта учётка) обходит блокировку и сможет войти для диагностики. Откат — только через «Восстановить».',
    'extsrv.confirm_restore_q':  'Восстановить установку у заказчика?',
    'extsrv.confirm_restore_b':  'При следующем heartbeat сервис заказчика снова включится. Записывается в аудит-лог.',
    'extsrv.confirm_delete_q':   'Удалить запись об установке безвозвратно?',
    'extsrv.confirm_delete_b':   'Удаляет строку полностью. Heartbeat от установки начнёт фейлиться (на центральном нет такого install_id). Используйте «Отозвать», если нужно только остановить сервис — «Удалить» подходит для выведенных из эксплуатации установок.',
    'extsrv.toast_revoked':      'Установка отозвана',
    'extsrv.toast_restored':     'Установка восстановлена',
    'extsrv.toast_deleted':      'Запись установки удалена',
    'extsrv.toast_registered':   'Установка зарегистрирована — скопируйте секрет СЕЙЧАС',
    'extsrv.modal_title':        'Зарегистрировать установку',
    'extsrv.modal_customer':     'Название заказчика',
    'extsrv.modal_customer_ph':  'например, BigBag Energy',
    'extsrv.modal_fqdn':         'FQDN или IP (необязательно)',
    'extsrv.modal_fqdn_ph':      'например, meters.customer.com',
    'extsrv.modal_hint':         'Секрет check-in сгенерируется на сервере и будет показан ОДИН РАЗ. Сборщик пакета вшивает его в install-manifest.json заказчика.',
    'extsrv.modal_submit':       'Зарегистрировать',
    'extsrv.flash_secret':       '✓ Установка зарегистрирована. Скопируйте секрет + install_id СЕЙЧАС — секрет нельзя получить позже.',
    'extsrv.label_install_id':   'Install ID:',
    'extsrv.label_secret':       'Секрет check-in (base64):',
    'extsrv.btn_copy':           '📋 Скопировать',
    'extsrv.btn_dismiss':        'Закрыть',
    'extsrv.reason_label':       'Причина (попадает в аудит-лог + отправляется заказчику):',
    'extsrv.reason_ph':          'например, «Подписка истекла 2026-04-30»',
    'extsrv.summary_title':      'Последняя сводка heartbeat',
    // common buttons
    'btn.read':            'Читать',
    'btn.refresh':         'Обновить',
    'btn.back_to_list':    '← Назад к списку',
    'btn.search':          'Поиск',
    'btn.save':            'Сохранить',
    'btn.delete':          'Удалить',
    'btn.edit':            'Редактировать',
    'btn.rename':          'Переименовать',
    'modal.rename_meter':  'Переименовать счётчик',
    'modal.edit_meter_fields': 'Изменение данных счётчика',
    'form.ct_ratio_warning': 'Отчёты считаются по текущему КТТ, включая прошлые периоды. Его изменение изменит кВт·ч и за прошлые даты этого счётчика.',
    'modal.rename_transformer': 'Переименовать ТП',
    'form.new_name':       'Новое название',
    'toast.renamed':       'Переименовано',
    'btn.add':             'Добавить',
    'btn.cancel':          'Отмена',
    'btn.close':           'Закрыть',
    'btn.export_csv':      'CSV',
    'btn.export_xlsx':     'XLSX',
    'btn.read_status':     'Прочитать статус',
    'btn.read_calendar':   'Прочитать календарь',
    'btn.read_selected':   'Прочитать выбранное',
    'btn.disconnect':      'Отключить',
    'btn.reconnect':       'Подключить',
    'btn.reset':           'Сбросить',
    'btn.logout':          'Выйти',
    'btn.change_pw':       'Сменить пароль',
    'btn.all':             'Все',
    'btn.none':            'Ничего',
    // KPIs
    'kpi.total_meters':         'Всего счётчиков',
    'kpi.online':               'Онлайн',
    'kpi.offline':              'Офлайн',
    'kpi.energy_today':         'Энергия за сегодня',
    'kpi.energy_month':         'Энергия за месяц',
    'kpi.assigned_meters':      'Назначенные счётчики',
    'kpi.unassigned_meters':    'Неназначенные счётчики',
    'kpi.abnormal_plc':            'Аномальные PLC-подключения',
    'plc.abnormal_none':           'не обнаружено',
    'plc.abnormal_unknown':        'неизвестные счётчики на PLC-шине · нажмите для просмотра',
    'plc.abnormal_unknown_cross':  'неизвестные · +{n} на соседнем DCU · нажмите',
    'plc.abnormal_cross':          '{n} счётчик(ов) на соседнем DCU (норма) · нажмите',
    'kpi.session_open':         'Сессия активна',
    'kpi.relay_off':            'Реле выключено',
    'keygap.title':             'Препятствия для ввода в эксплуатацию',
    'keygap.tile_hint':         'Показать счётчики, не введённые в эксплуатацию, и связано ли это с ключами',
    'keygap.installed':         'установлено',
    'keygap.commissioned':      'введено в эксплуатацию',
    'keygap.blocked':           'заблокировано из-за ключей',
    'keygap.copy':              'Копировать номера',
    'keygap.copied':            'номер(ов) скопировано',
    'keygap.was_commissioned':  'ранее введён в эксплуатацию — ключи утеряны',
    'keygap.g_none':            'Нет записи о ключах',
    'keygap.g_none_hint':       'В хранилище ключей для них ничего нет. Сессия HLS5 невозможна, поэтому ввод в эксплуатацию не произойдёт — импортируйте ключи.',
    'keygap.g_partial':         'Неполные ключи GMAC',
    'keygap.g_partial_hint':    'Запись есть, но пара ключей (аутентификации / шифрования) неполная. Для HLS5 нужны оба — переимпортируйте из исходного файла.',
    'keygap.g_keyed':           'Ключи есть, но в эксплуатацию не введён',
    'keygap.g_keyed_hint':      'Ключи присутствуют и пригодны, значит дело НЕ в ключах — проверьте связь, регистрацию на УСПД и выходил ли счётчик на связь.',
    'keygap.all_clear':         'Все установленные счётчики введены в эксплуатацию и имеют ключи.',
    'kpi.meters_installed':     'Установлено счётчиков',
    // Meter detail sub-tabs
    'mdt.network':         'Сетевые настройки',
    'net.admin_only':       'Изменение IP / APN — действия с высоким риском, только для администратора.',
    'net.kill_switch':      'Удалённая запись заблокирована на сервере (EMETER_REMOTE_WRITES=disabled).',
    'net.kill_switch_dry':  'Режим dry-run — PDU собран и залогирован, но НЕ отправлен.',
    'net.target_dcu':       'Цель: DCU (концентратор Holley)',
    'net.target_meter':     'Цель: счётчик (через DCU SAP passthrough)',
    'net.target_direct':    'Цель: счётчик (прямая GPRS-сессия)',
    'net.current':          'Текущая конфигурация',
    'net.server_ip':        'IP сервера',
    'net.server_port':      'Порт сервера',
    'net.apn':              'APN',
    'net.heartbeat_s':      'Heartbeat (сек)',
    'net.new_server_ip':    'Новый IP сервера',
    'net.new_port':         'Новый порт',
    'net.new_apn':          'Новый APN',
    'net.new_hb':           'Новый heartbeat (сек)',
    'net.probe_tcp':        'Проверить TCP-доступность',
    'net.probe_ok':         '✓ Доступно',
    'net.probe_fail':       '✗ Недоступно — НЕ ПРИМЕНЯТЬ',
    'net.probe_hint':       'Перед применением подтвердите, что новый IP:port доступен с сервера eMeter.',
    'net.type_serial':      'Введите полный серийный номер счётчика/DCU, чтобы разблокировать Применить:',
    'net.apply':            'Применить',
    'net.read':             'Прочитать',
    'net.apn_over_plc':     'Изменение APN через PLC запрещено — счётчик потеряет GPRS и мы потеряем линию PLC.',
    'net.blast_warn':       '⚠ Изменение IP/порта/APN может вывести DCU или счётчик из сети до восстановления через оптический порт — перепроверьте значения перед применением.',
    'net.dry_suffix':       '(DRY-RUN)',
    'mdt.home':            'Главная',
    'mdt.event':           'События',
    'mdt.readings':        'Показания',
    'mdt.relay':           'Управление реле',
    'mdt.collection':      'Сбор данных',
    'mdt.installation':    'Установка',
    'mdt.tariff':          'Тариф',
    // Meter readings sub-tabs
    'mr.daily':            'Сутки',
    'mr.monthly':          'Месяц',
    'mr.load_profile':     'График нагрузки',
    'mr.on_demand':        'По запросу',
    'mr.power_quality':    'Качество электроэнергии',
    'mr.period':           'Расход за период',
    'period.calculate':    'Рассчитать',
    'period.start':        'Нач. показание',
    'period.end':          'Кон. показание',
    'period.consumption':  'Расход',
    'period.no_data':      'Нет данных суточного биллинга за выбранный период.',
    'toast.map_load_failed':       'Ошибка загрузки карты',
    'toast.map_tiles_fallback':    'Основные тайлы карты недоступны; используется OpenStreetMap',
    'toast.refuse_delete_self':    'Нельзя удалить самого себя',
    'toast.profile_meta_missing':  'Метаданные профиля не загружены',
    'toast.pick_dates':            'Укажите даты начала и конца',
    'toast.to_before_from':        'Невозможно определить индексы из дат',
    'toast.billing_loaded':        'Данные биллинга загружены',
    // System sub-tabs
    'sys.scheduler':       'Планировщик',
    'sys.poller':          'Опрос',
    'sys.keys':            'Ключи',
    'sys.security':        'Безопасность',
    'sys.health':          'Состояние парка',
    'sys.server_health':   'Состояние сервера',
    'sys.users':           'Пользователи',
    'sys.com_log':         'Журнал связи',
    // Admin Tools sub-tabs
    'adm.load_curve':      'График нагрузки',
    'adm.read_meter':      'Чтение счётчика',
    'adm.write_meter':     'Запись счётчика',
    'adm.activity_log':    'Журнал действий',
    // Misc
    'common.language':     'Язык',
    'common.loading':      'Загрузка…',
    'common.no_data':      'Нет данных',
    'common.from':         'С',
    'common.to':           'По',
    'common.date':         'Дата',
    'common.time':         'Время',
    'common.user':         'Пользователь',
    'common.action':       'Действие',
    'common.customer':     'Абонент',
    'common.meter':        'Счётчик',
    'common.target':       'Объект',
    'common.details':      'Детали',
    'common.status':       'Статус',
    'common.online':       'онлайн',
    'common.offline':      'офлайн',
    'common.connected':    'подключён',
    'common.owner':        'Владелец',
    'common.location':     'Местоположение',
    'common.created':      'Создано',
    'common.total':        'Всего',
    'common.yes':          'да',
    'common.no':           'нет',
    'common.none_assigned': 'Пока ничего не назначено.',
    'tf.meters_on':        'Счётчики на трансформаторе',
    'th.conn':             'Подкл.',
    'th.ct_vt':            'CT/VT',
    'role.main':           'основной',
    'role.customer':       'потребитель',
    'btn.assign_dcu':      'Назначить DCU',
    'tip.create_customer_first': 'Сначала создайте абонента',
    'tip.create_transformer_first': 'Сначала создайте трансформатор',
    'msg.readonly_hierarchy': 'Только чтение — ваша роль не позволяет редактировать иерархию.',
    'msg.loading_hierarchy': 'Загрузка иерархии…',
    'msg.no_customers_yet': 'Абонентов пока нет.',
    'msg.no_transformers_yet': 'Трансформаторов пока нет.',
    'msg.no_meters_assigned': 'Счётчики пока не назначены.',
    'msg.no_readings_yet': 'Показания ещё не собраны.',
    'msg.click_refresh_relay': 'нажмите Обновить',
    'msg.relay_unknown': 'Реле ?',
    'label.relay_status':  'Статус реле',
    'label.read_at':       'Считано в',
    'ph.search_hierarchy':  'Поиск: абонент, счёт, счётчик…',
    'tree.all_customers_home': 'Все абоненты · AMI',
    'tree.collapse_all':      'Свернуть все',
    'tree.collapse_all_hint': 'Закрыть все раскрытые узлы: абонентов, трансформаторы и УСПД',
    'card.transformers_count': 'Трансформаторы',
    'card.contact':           'Контакты',
    'card.notes':             'Заметки',
    'card.device_status':     'Статус устройств по типу счётчика',
    'section.live_readings':  'Показания в реальном времени',
    'kpi.installed':          'Установлено',
    'kpi.commissioned':       'Введено в эксплуатацию',
    'th.installed':           'Установлено',
    'th.commissioned':        'Введено в экспл.',
    'th.on':                  'Вкл',
    'th.off':                 'Выкл',
    'th.no_relay':            'Без реле',
    'th.register':            'Регистр',
    'group.instantaneous':    'Мгновенные',
    'group.energy':           'Энергия',
    'group.other':            'Прочее',
    'badge.live':             '🟢 В сети',
    'badge.offline':          '🔴 Нет сети',
    'common.address':         'Адрес',
    'common.phone':           'Телефон',
    'common.email':           'Эл. почта',
    'common.live':            'в сети',
    'th.meter_type':          'Тип счётчика',
    'th.location':            'Местоположение',
    'th.meters_count':        'Счётчики',
    'th.online_status':       'В сети',
    'ph.add_note':            'Добавить заметку (напоминание об оплате, сервисная пометка…)',
    'btn.add_note':           'Добавить заметку',
    'common.no_transformers': 'Трансформаторов пока нет.',
    'common.no_notes':        'Заметок пока нет.',
    'common.no_meters_assigned': 'Счётчики пока не назначены.',
    'card.transformers':      'Трансформаторы',
    'card.notes_count':       'Заметки',
    'common.account_number':  'Счёт',
    'tab.home':               'Главная',
    'tab.event':              'События',
    'tab.readings':           'Показания',
    'tab.relay':              'Управление реле',
    'tab.collection':         'Сбор данных',
    'tab.installation':       'Установка',
    'tab.tariff':             'Тариф',
    'tcsc.collection_rate':   'Сбор данных',
    'tcsc.daily_billing':     'Суточный профиль',
    'tcsc.load_profile':      'Профиль нагрузки',
    'tcsc.th_daily':          'Суточный',
    'tcsc.th_load':           'Нагрузка',
    'tcsc.ok':                'OK',
    'tcsc.fail':              'Ошибка',
    'common.meters_count':    'счётчиков',
    'common.from':            'С',
    'common.to':              'ПО',
    // modal titles
    'modal.new_customer':     'Новый абонент',
    'modal.edit_customer':    'Редактирование абонента',
    'modal.new_transformer':  'Новый трансформатор',
    'modal.edit_transformer': 'Редактирование трансформатора',
    'modal.assign_meter':     'Назначить счётчик',
    'modal.edit_assignment':  'Редактирование назначения',
    'modal.change_password':  'Смена пароля',
    // form labels
    'form.name':              'Имя',
    'form.name_required':     'Имя (обязательно)',
    'form.account_number':    'Номер лицевого счёта',
    'form.address':           'Адрес',
    'form.phone':             'Телефон',
    'form.email':             'Электронная почта',
    'form.customer':          'Абонент',
    'form.customer_required': 'Абонент (обязательно)',
    'form.location':          'Местоположение',
    'form.role':              'Роль',
    'form.object_name':       'Наименование объекта',
    'form.comm_addr':         'Адрес связи (DLMS)',
    'form.install_desc':      'Описание установки',
    'form.meter_type':        'Тип счётчика (переопределение)',
    'form.connection_type':   'Тип подключения',
    'form.ct_ratio':          'КТТ',
    'form.vt_ratio':          'КТН',
    'form.latitude':          'Широта',
    'form.longitude':         'Долгота',
    'form.current_password':  'Текущий пароль',
    'form.new_password':      'Новый пароль',
    'form.confirm_password':  'Подтверждение пароля',
    'form.username':          'Имя пользователя',
    'form.full_name':         'Полное имя',
    'form.password':          'Пароль',
    'form.password_hint':     'Пароль (мин. 8 символов)',
    'form.new_pw_hint':       'Новый пароль (оставьте пустым для сохранения текущего)',
    'form.ct_vt_help':        'КТТ и КТН настраиваются для каждого счётчика при его назначении на трансформатор.',
    // toast messages
    'toast.customer_created':     'Абонент создан',
    'toast.customer_updated':     'Абонент обновлён',
    'toast.customer_deleted':     'Абонент удалён',
    'toast.transformer_created':  'Трансформатор создан',
    'toast.transformer_updated':  'Трансформатор обновлён',
    'toast.transformer_deleted':  'Трансформатор удалён',
    'toast.meter_assigned':       'Счётчик назначен',
    'toast.assignment_updated':   'Назначение обновлено',
    'toast.assignment_deleted':   'Назначение счётчика удалено',
    'toast.user_created':         'Пользователь создан',
    'toast.user_updated':         'Пользователь обновлён',
    'toast.user_deleted':         'Пользователь удалён',
    'toast.password_updated':     'Пароль обновлён',
    'toast.note_added':           'Заметка добавлена',
    'toast.note_deleted':         'Заметка удалена',
    'toast.read_complete':        'Чтение завершено',
    'toast.xlsx_downloaded':      'XLSX скачан',
    'toast.relay_refreshed':      'Статус реле обновлён',
    'toast.calendar_read':        'Календарь прочитан',
    'toast.device_info_read':     'Информация устройства обновлена',
    'toast.collection_loaded':    'Статистика сбора загружена',
    'toast.pick_meter_first':     'Сначала выберите счётчик',
    'toast.pick_customer_first':  'Сначала выберите абонента',
    'toast.nothing_selected':     'Ничего не выбрано',
    'toast.poller_set':           'Интервал опроса: {n} сек.',
    'toast.session_opened':       'Сеанс открыт для {serial}',
    'toast.session_closed':       'Сеанс закрыт для {serial}',
    'toast.relay_sent':           'Команда реле «{action}» отправлена',
    'toast.loaded_n':             'Загружено {n} {what}',
    'toast.n_registers_read':     '{panel}: {n} регистр(ов) прочитано',
    'toast.report_downloaded':    'Отчёт «{kind}» скачан',
    'toast.keys_imported':        'Импортировано {n} ключей (всего в хранилище {total})',
    'toast.keys_cleared':         'Все ключи удалены',
    'toast.interval_invalid':     'Интервал должен быть от 5 до 3600 секунд',
    'toast.pw_no_match':          'Пароли не совпадают',
    'toast.pw_too_short':         'Пароль должен быть не менее 8 символов',
    'toast.no_assignments':       'У этого абонента нет назначенных счётчиков',
    'toast.scheduler_running':    'Запуск ночного сбора данных…',
    'toast.scheduler_done':       'Сбор завершён: {summary}',
    // section titles
    'section.dashboard':          'Панель управления',
    'section.admin_tools':        'Инструменты администратора',
    'section.system':             'Система',
    'section.connected_meters':   'Подключённые счётчики',
    'section.read_meter':         'Чтение счётчика',
    'section.write_meter':        'Запись в счётчик',
    'section.load_curve':         'График нагрузки',
    'section.events':             'Журнал событий',
    'section.customers':          'Абоненты',
    'section.users':              'Пользователи',
    'section.reports':            'Отчёты',
    'section.billing':            'Биллинг',
    'section.meter_keys':         'Ключи счётчиков',
    'section.notes':              'Заметки',
    'section.polling_interval':   'Интервал опроса',
    // table headers
    'th.serial':           'Серийный №',
    'th.type':             'Тип',
    'th.assignment':       'Назначение',
    'th.manufacturer':     'Производитель',
    'th.firmware':         'Прошивка',
    'th.status':           'Статус',
    'th.connected':        'Подключён',
    'th.last_heartbeat':   'Посл. связь',
    'th.actions':          'Действия',
    'th.timestamp':        'Время',
    'th.value':            'Значение',
    'th.obis':             'OBIS',
    'th.direction':        'Направление',
    'th.length':           'Длина',
    'th.hex_data':         'Hex данные',
    'th.username':         'Пользователь',
    'th.full_name':        'Полное имя',
    'th.role':             'Роль',
    'th.active':           'Активен',
    'th.customers_access': 'Абоненты',
    'th.last_login':       'Посл. вход',
    'th.name':             'Имя',
    'th.date':             'Дата',
    'th.event_name':       'Событие',
    'th.event_code':       'Код',
    'th.no':               '№',
    'th.meter_no':         'Номер счётчика',
    'th.ct_vt':            'КТТ · КТН',
    'th.online_status':    'Онлайн',
    'th.latest_row':       'Посл. запись',
    // empty states
    'msg.nothing_selected':     'Ничего не выбрано',
    'msg.no_data_yet':          'Данные пока отсутствуют',
    'msg.admin_required':       'Требуется доступ администратора.',
    'msg.pick_meter':           'Выберите счётчик в дереве слева.',
    'msg.no_meters':            'Счётчики не подключены',
    'msg.authenticating':       'Авторизация…',
    'msg.no_notes':             'Заметок пока нет.',
    'msg.meters_connected':     '{n} счётчик(ов) подключено',
    'msg.confirm_delete':       'Вы уверены, что хотите удалить?',
    // buttons
    'btn.new_customer':     'Новый абонент',
    'btn.new_transformer':  'Новый трансформатор',
    'btn.assign_meter':     'Назначить счётчик',
    'btn.new_user':         'Новый пользователь',
    'btn.update':           'Обновить',
    'btn.create':           'Создать',
    'btn.confirm':          'Подтвердить',
    'btn.run_midnight':     'Запустить сейчас',
    'btn.open_session':     'Открыть',
    'btn.close_session':    'Закрыть',
    'btn.load_billing':     'Загрузить биллинг',
    'btn.load_history':     'Загрузить историю',
    'btn.load_events':      'Загрузить события',
    // assign meter
    'assign.role_customer':   'Клиентский счётчик',
    'assign.role_main':       'Главный счётчик (на трансформаторе)',
    'assign.auto_detect':     'Автоопределение',
    'assign.connection_direct':'Прямое',
    'assign.connection_ct':   'Через ТТ',
    'assign.connection_ct_vt':'Через ТТ/ТН',
    // idle timer
    'idle.title':           'Тайм-аут сессии',
    'idle.warning':         'Вы будете отключены через {n} секунд из-за бездействия.',
    'idle.stay':            'Остаться в системе',
    'idle.logout_now':      'Выйти сейчас',
    'idle.setting_label':   'Автовыход по бездействию',
    'idle.never':           'Никогда',
    'idle.minutes':         '{n} мин',
    'meter.status':                  'Статус',
    'meter.meter_collection':        'Сбор данных',
    'meter.relay_control_tasks':     'Задачи реле',
    'meter.online':                  'В сети',
    'meter.offline':                 'Не в сети',
    'meter.power_on':                'Питание вкл.',
    'meter.power_off':               'Питание откл.',
    'meter.daily_profile':           'Суточный профиль',
    'meter.monthly_load_profile':    'Месячный / Профиль нагрузки',
    'meter.failed':                  'Ошибка',
    'meter.succeed':                 'Успешно',
    'meter.cancelled':               'Отменено',
    'meter.meter_no':                'Номер счётчика',
    'meter.logical_no':              'Логический №',
    'meter.meter_type':              'Тип счётчика',
    'meter.meter_status':            'Статус счётчика',
    'meter.commissioned_live':       'Введён в эксплуатацию · В сети',
    'meter.commissioned_off':        'Введён в эксплуатацию · Не в сети',
    'meter.not_commissioned':        'Не введён в эксплуатацию',
    'meter.general':                 'Общее',
    'meter.archive':                 'Архив',
    'meter.installations':           'Установки',
    'meter.seals_info':              'Пломбы',
    'meter.vector_chart':            'Векторная диаграмма',
    'meter.work_reply':              'Наряд-ответ',
    'meter.operator_tasks':          'Задачи оператора',
    'meter.billing_data_snapshot':   'Данные биллинга',
    'meter.communication':           'Связь',
    'meter.read_device_info':        'Прочитать инфо устройства',
    'meter.read_device_info_title':  'Прочитать модель, прошивку, дату выпуска со счётчика',
    'meter.open_secure_session_first': 'Сначала откройте защищённую сессию',
    'meter.live_reads_require_session': 'Для чтения требуется открытая защищённая сессия',
    // dashboard
    'dashboard.energy_chart_title':  'Суточное потребление энергии (последние 30 дней)',
    'dashboard.hint_pick_meter':     'Выберите счётчик в иерархии абонентов для просмотра детальной информации (Главная · События · Показания · По запросу · Реле · Тариф).',
    'dashboard.chart_no_data':       'Данных пока нет. Ночной планировщик начнёт заполнять завтра.',
    // poller
    'poller.running':                '● Работает',
    'poller.stopped':                '● Остановлен',
    'poller.currently':              'текущий {n} сек.',
    'poller.custom':                 'Другой',
    'poller.seconds':                'секунд',
    // scheduler
    'scheduler.title':               'Ночной планировщик',
    'scheduler.fires_at':            'Запуск в',
    'scheduler.last_run':            'Последний запуск',
    'scheduler.never':               'никогда',
    // meters tab
    'meters.assigned':               'Назначенные',
    'meters.unassigned':             'Неназначенные',
    'meters.waiting':                'Ожидание подключения счётчиков на порту 8002…',
    'meters.no_match_filter':        'Нет счётчиков, соответствующих фильтру «{filter}». ',
    'meters.show_all':               'Показать все',
    'meters.release_session':        'Закрыть защищённую сессию GMAC',
    // com log
    'comlog.connected':              'Подключён',
    'comlog.disconnected':           'Отключён',
    'comlog.waiting':                'Ожидание кадров связи…',
    // relay tab
    'relay.unknown': 'неизвестно', 'relay.disconnected': 'отключено', 'relay.connected': 'подключено',
    'relay.ready_reconnect': 'готово к повторному подключению', 'relay.refresh_failed': 'Ошибка обновления',
    'relay.disconnect_upper': 'ОТКЛЮЧИТЬ', 'relay.reconnect_upper': 'ПОДКЛЮЧИТЬ',
    'relay.confirm_prefix': 'реле на счётчике', 'relay.confirm_power': 'Это физически переключает питание',
    'relay.off': 'ВЫКЛ', 'relay.on': 'ВКЛ',
    'relay.confirm_suffix': 'на счётчике. Подключённое оборудование будет затронуто.',
    'relay.action_failed': 'ошибка', 'relay.events': 'событий реле',
    'relay.history_load_failed': 'Ошибка загрузки истории', 'relay.relay_status': 'Статус реле',
    'relay.click_read': 'нажмите Прочитать статус', 'relay.read_at': 'Прочитано в',
    'relay.clear_status_title': 'Очистить отображаемый статус',
    'relay.session_required': 'Для управления реле нужна открытая защищённая сессия.',
    'relay.history': 'История', 'relay.history_30d': 'История реле (30 дней)',
    'relay.click_load_hint': 'Нажмите Загрузить для чтения журнала отключения (OBIS 0.0.99.98.2.255).',
    'relay.logical_device_name': 'Логическое имя устройства', 'relay.last_heartbeat': 'Последний отклик',
    'relay.admin_only': 'Отключение/подключение реле доступно оператору или администратору.',
    'relay.kill_switch': 'Удалённая запись отключена на сервере (EMETER_REMOTE_WRITES=disabled).',
    'relay.kill_switch_dry': 'Режим dry-run — PDU собирается, но НЕ отправляется.',
    'relay.type_serial': 'Введите полный 12-значный серийный номер счётчика, чтобы включить действия:',
    'relay.control_mode': 'Режим управления', 'relay.output_state': 'Состояние выхода',
    'relay.ctrl_state': 'Состояние управления',
    'relay.mode_hint_5': 'Режим 5 — удалённое отключение + удалённое подключение',
    'relay.mode_hint_3': 'Режим 3 — удалённое отключение, подключение требует ручного действия',
    'relay.mode_hint_2': 'Режим 2 — удалённое отключение, подключение только локальной кнопкой',
    'relay.mode_hint_1': 'Режим 1 — прямое удалённое/локальное подкл./откл.',
    'relay.mode_hint_0': 'Режим 0 — только локально, без удалённого управления',
    'relay.mode_hint_other': 'Режим {n} — см. DLMS §15',
    'relay.bus': 'Шина', 'relay.bus_direct': 'Прямой (GPRS)',
    'relay.bus_plc': 'PLC (через DCU)', 'relay.bus_rf': 'RF (через DCU)',
    'relay.bus_rs485': 'RS-485 (через DCU)',
    // Управление реле + журнал операций с реле ('rlog.*')
    'relay.reason_debt': 'Задолженность', 'relay.reason_debt_paid': 'Задолженность погашена',
    'relay.reason_maintenance': 'Обслуживание', 'relay.reason_tamper': 'Вмешательство',
    'relay.reason_other': 'Другое',
    'relay.badge_on': 'Реле включено', 'relay.badge_off': 'Реле отключено',
    'relay.badge_unknown': 'Неизвестно',
    'rlog.title': 'Журнал операций с реле',
    'rlog.cmd_history': 'История команд eMeter',
    'rlog.none_for_meter': 'По этому счётчику команды реле ещё не выдавались.',
    // столбцы
    'rlog.col_status': 'Статус', 'rlog.col_action': 'Действие', 'rlog.col_mode': 'Режим',
    'rlog.col_reason': 'Причина', 'rlog.col_attempts': 'Попытки', 'rlog.col_by': 'Кем',
    'rlog.col_requested': 'Запрошено', 'rlog.col_confirmed': 'Подтверждено',
    'rlog.col_meter_no': '№ счётчика', 'rlog.col_dcu': '№ DCU',
    'rlog.col_last_attempt': 'Последняя попытка', 'rlog.col_operate': 'Действия',
    'rlog.col_account': 'Лицевой счёт', 'rlog.col_customer': 'Абонент', 'rlog.col_tr': 'ТП',
    'rlog.col_meter_type': 'Тип счётчика', 'rlog.col_comm': 'Связь', 'rlog.col_relay': 'Реле',
    'rlog.col_refresh': 'Обновлено',
    // режим доставки
    'rlog.mode_immediate': 'немедленный', 'rlog.mode_deferred': 'отложенный',
    'rlog.mode_immediate_long': 'Немедленно (≤60 с, иначе тайм-аут)',
    'rlog.mode_deferred_long': 'Отложенно (когда счётчик выйдет на связь)',
    // вердикты — см. relayVerdict()
    'rlog.v_sending': 'Отправка…',
    'rlog.v_busy': 'Занято — PLC-модуль недоступен, повтор',
    'rlog.v_transport': 'Сбой связи — повтор',
    'rlog.v_offline': 'Счётчик не на связи — ожидание',
    'rlog.v_queue_busy': 'Очередь DCU занята — ожидание',
    'rlog.v_requeued': 'Возвращено в очередь после перезапуска',
    'rlog.v_queued': 'В очереди',
    'rlog.v_already': 'Уже в этом состоянии',
    'rlog.v_armed': 'Взведено — требуется действие на счётчике',
    'rlog.v_drift': 'Расхождение — реле не в заданном состоянии',
    'rlog.v_success': 'Выполнено',
    'rlog.v_cancelled': 'Отменено',
    'rlog.v_superseded': 'Заменено более новой командой',
    'rlog.v_expired': 'Срок действия истёк',
    'rlog.v_timeout': 'Истекло время ожидания',
    'rlog.v_failed': 'Ошибка',
    'rlog.v_failed_max': 'Ошибка — попытки исчерпаны',
    // оформление страницы
    'rlog.embedded_hint_prefix': 'Управление реле — счётчики на DCU',
    'rlog.embedded_hint_suffix': 'Отметьте строки (Shift+клик — диапазон) и используйте панель массовых операций; либо откройте счётчик для одиночного управления и истории.',
    'rlog.banner_killed': 'Удалённые записи заблокированы аварийным выключателем (EMETER_REMOTE_WRITES=disabled). Команды нельзя выдать, пока администратор не включит их на сервере.',
    'rlog.banner_dry_run': 'Тестовый режим — команды формируют PDU и пишутся в историю, но НЕ переключают реле.',
    'rlog.kpi_total': 'Всего', 'rlog.kpi_connected': 'Подключено',
    'rlog.kpi_disconnected': 'Отключено', 'rlog.kpi_unknown': 'Неизвестно',
    'rlog.kpi_open_commands': 'Открытых команд',
    'rlog.search_placeholder': 'Поиск: серийный № / лицевой счёт / абонент…',
    'rlog.btn_search': 'Поиск', 'rlog.btn_refresh': 'Обновить',
    'rlog.all_comm_types': 'Все виды связи', 'rlog.all_relay_states': 'Все состояния реле',
    'rlog.n_selected': 'выбрано: {n}', 'rlog.n_meters': 'счётчиков: {n}',
    'rlog.tip_shift_range': 'Shift+клик — выбрать диапазон',
    'rlog.tip_unsupported': 'Управление реле не поддерживается (не через DCU)',
    'rlog.no_meters_match': 'Нет счётчиков по этому фильтру.',
    'rlog.prev': 'Назад', 'rlog.page': 'Страница', 'rlog.next': 'Вперёд',
    // панель массовых операций
    'rlog.lbl_action': 'Действие', 'rlog.lbl_delivery': 'Доставка',
    'rlog.act_disconnect': 'Отключить (ВЫКЛ)', 'rlog.act_reconnect': 'Подключить (ВКЛ)',
    'rlog.remark_placeholder': 'Примечание (необязательно)',
    'rlog.confirm_warn': 'Отключение снимает питание. Введите',
    'rlog.confirm_suffix': 'для подтверждения:',
    'rlog.btn_disconnect_n': 'Отключить счётчиков: {n}',
    'rlog.btn_reconnect_n': 'Подключить счётчиков: {n}',
    'rlog.btn_clear_sel': 'Снять выделение',
    'rlog.btn_read_status_n': 'Прочитать статус реле ({n})',
    'rlog.toast_read_queued': 'Чтение статуса реле поставлено в очередь: {n} — результаты появятся по мере ответа счётчиков',
    'rlog.toast_read_already': ', уже в очереди: {n}', 'rlog.toast_read_not_routed': ', не через DCU: {n}',
    'rlog.toast_read_failed': 'Ошибка чтения статуса',
    // панель журнала
    'rlog.total': 'Всего', 'rlog.btn_hide': 'Скрыть', 'rlog.btn_show': 'Показать',
    'rlog.all_tasks': 'Все задания', 'rlog.task_switch_off': 'Отключение',
    'rlog.task_switch_on': 'Подключение', 'rlog.btn_clear_filters': 'Сбросить фильтры',
    'rlog.next_retry': 'Следующая попытка', 'rlog.btn_detail': 'Подробно', 'rlog.btn_cancel': 'Отмена',
    'rlog.no_ops_match': 'Нет операций с реле по этим фильтрам.',
    'rlog.per_page': '{n} на странице',
    // окно подробностей
    'rlog.detail_title': 'Операция с реле', 'rlog.btn_close': 'Закрыть',
    'rlog.detail_raw': 'Детали системы', 'rlog.detail_error_class': 'Класс ошибки',
    'rlog.detail_first_sent': 'Первая отправка', 'rlog.detail_batch': 'Пакет',
    'rlog.state_before': 'до', 'rlog.state_after': 'после',
    // уведомления
    'rlog.toast_select_one': 'Выберите хотя бы один счётчик',
    'rlog.toast_type_confirm': 'Введите «{phrase}» для подтверждения',
    'rlog.toast_queued': 'Команд поставлено в очередь: {n}', 'rlog.toast_skipped': ', пропущено: {n}',
    'rlog.dry_run_tag': 'ТЕСТОВЫЙ РЕЖИМ',
    'rlog.toast_submit_failed': 'Ошибка отправки', 'rlog.toast_cancelled': 'Команда отменена',
    'rlog.toast_cancel_failed': 'Ошибка отмены', 'rlog.toast_list_failed': 'Ошибка загрузки списка реле',
    // tariff tab
    'tariff.session_required': 'Для чтения тарифного календаря нужна открытая защищённая сессия.',
    'tariff.activity_calendar': 'Календарь активности', 'tariff.last_read_at': 'Последнее чтение в',
    'tariff.edit_admin': 'Редактировать (админ)', 'tariff.edit_disabled': 'Запись календаря отключена в этой сборке',
    'tariff.current_active': 'Текущий активный тариф', 'tariff.from_register': 'из регистра',
    'tariff.refreshed_at': 'обновлено в', 'tariff.active_calendar': 'Активный календарь',
    'tariff.click_read': 'Нажмите Прочитать календарь для загрузки.',
    'tariff.empty_warning': 'Счётчик не возвращает календарь активности через простое чтение атрибута 2.',
    'tariff.active_cal_label': 'Активный календарь', 'tariff.passive_cal_label': 'Пассивный календарь',
    'tariff.special_days': 'Таблица особых дней', 'tariff.editor_planned': 'Редактор (планируется)',
    'tariff.editor_desc': 'Полный интерфейс редактирования тарифов планируется в следующей итерации.',
    // installation tab
    'install.identification': 'Идентификация', 'install.hierarchy': 'Иерархия',
    'install.connection': 'Подключение', 'install.install_metadata': 'Метаданные установки',
    'install.firmware': 'Прошивка', 'install.production_date': 'Дата производства',
    'install.click_read': 'нажмите Прочитать', 'install.customer_account': 'Лицевой счёт абонента',
    'install.transformer': 'Трансформатор', 'install.transformer_loc': 'Расположение трансформатора', 'install.dcu': 'DCU',
    'install.role_main': 'основной (на трансформаторе)', 'install.role_customer': 'абонентский',
    'install.seals_vector': 'Пломбы и векторная проверка',
    'install.seals_not_collected': 'Статус пломб и результаты векторной проверки пока не собираются со счётчика.',
    'install.read_dev_title': 'Прочитать device_info со счётчика',
    // collection tab
    'collection.window_days': 'Окно (дней)', 'collection.daily_billing': 'Суточный биллинг',
    'collection.load_profile': 'Профиль нагрузки', 'collection.day_breakdown': 'Разбивка по дням',
    'collection.ok': 'ок', 'collection.partial': 'частично', 'collection.missing': 'отсутствует',
    'collection.stats_failed': 'Ошибка загрузки статистики сбора',
    // on-demand tab
    'ondemand.session_required': 'Для чтения по запросу нужна открытая защищённая сессия.',
    'ondemand.session_hint': 'Используйте кнопку Счётчики > Открыть для установки соединения.',
    'ondemand.status': 'Статус', 'ondemand.energy': 'Энергия',
    'ondemand.line': 'Линия', 'ondemand.manufacturer': 'Изготовитель',
    'meter.last_seen': 'Последняя связь',
    'ondemand.panels': 'Панели', 'ondemand.read_at': 'Прочитано в',
    'ondemand.current_status': 'Текущий статус', 'ondemand.conn_energy': 'Статус подключения (Энергия)',
    'ondemand.conn_line': 'Параметры линии', 'ondemand.meter_diag': 'Ошибки / Диагностика',
    'ondemand.serial_number': 'Серийный номер', 'ondemand.firmware_ver': 'Версия прошивки',
    'ondemand.prod_date': 'Дата производства', 'ondemand.logical_dev': 'Логическое имя устройства',
    'ondemand.active_import': 'Актив + (импорт)', 'ondemand.active_export': 'Актив - (экспорт)',
    'ondemand.reactive_import': 'Реактив + (импорт)', 'ondemand.reactive_export': 'Реактив - (экспорт)',
    'ondemand.apparent_import': 'Полная + (импорт)', 'ondemand.apparent_export': 'Полная - (экспорт)',
    'ondemand.voltage_l1': 'Напряжение L1', 'ondemand.voltage_l2': 'Напряжение L2', 'ondemand.voltage_l3': 'Напряжение L3',
    'ondemand.current_l1': 'Ток L1', 'ondemand.current_l2': 'Ток L2', 'ondemand.current_l3': 'Ток L3',
    'ondemand.power_factor': 'Коэф. мощности', 'ondemand.frequency': 'Частота',
    'ondemand.status_word': 'Слово состояния', 'ondemand.battery_voltage': 'Напряжение батареи',
    'ondemand.operating_time': 'Время работы', 'ondemand.ct_ratio': 'КТТ', 'ondemand.vt_ratio': 'КТН',
    'ondemand.read_group': 'Группа чтения',
    // events tab
    'events.category': 'Категория',
    'events.all_categories': 'Все категории', 'events.event_search': 'Поиск по событиям',
    'events.search_hint': 'напр. питание, реле, вскрытие',
    'events.session_required': 'Для чтения журналов событий нужна защищённая сессия.',
    'events.reading_logs': 'Чтение 11 журналов событий...', 'events.occurred': 'Произошло',
    'events.received': 'Получено',
    'events.no_events': 'Событий нет. Выберите диапазон дат и нажмите Поиск.',
    'events.of_total': 'из', 'events.events_label': 'событий',
    'events.filtered_by': 'фильтр по', 'events.search_label': 'поиск',
    'events.loaded_events': 'Загружено {n} событий',
    'events.load_failed': 'Ошибка загрузки событий',
    'events.exported_rows': 'Экспортировано {n} строк',
  },
};

const LanguageContext = React.createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
});

function useLang() {
  return React.useContext(LanguageContext);
}

function useT() {
  return React.useContext(LanguageContext).t;
}

function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(LANG_STORAGE_KEY) || 'en'; } catch (_) { return 'en'; }
  });
  const setLang = useCallback((l) => {
    setLangState(l);
    try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch (_) {}
  }, []);
  const t = useCallback((key, vars) => {
    const dict = LANG_DICT[lang] || LANG_DICT.en;
    let s = dict[key] ?? LANG_DICT.en[key] ?? key;
    // Interpolation: t('toast.poller_set', { n: 300 }) produces
    // "Poller interval set to 300s" or "Интервал опроса: 300 сек."
    // Placeholders in translation strings use {name} syntax.
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        s = s.replace(`{${k}}`, v);
      });
    }
    return s;
  }, [lang]);
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Report language — the language of the DOWNLOADED reports (XLSX/CSV), kept
// SEPARATE from the app UI language so an operator can work in English but
// generate Russian reports for Uzbekenergo. Defaults to the UI language until
// the operator picks a report-specific one; persisted independently.
const REPORT_LANG_STORAGE_KEY = 'emeter_report_lang';
const ReportLangContext = React.createContext({ reportLang: 'en', setReportLang: () => {} });

function useReportLang() {
  return React.useContext(ReportLangContext);
}

// Read the operator's chosen report language at download time (or null to fall
// back to the UI language). Report handlers read this directly so we don't have
// to thread a hook through every report component; the toggle keeps it in sync.
function getReportLang() {
  try { return localStorage.getItem(REPORT_LANG_STORAGE_KEY) || null; } catch (_) { return null; }
}

function ReportLangProvider({ children }) {
  const { lang } = useLang();
  const [override, setOverride] = useState(() => {
    try { return localStorage.getItem(REPORT_LANG_STORAGE_KEY) || null; } catch (_) { return null; }
  });
  const setReportLang = useCallback((l) => {
    setOverride(l);
    try { localStorage.setItem(REPORT_LANG_STORAGE_KEY, l); } catch (_) {}
  }, []);
  // No explicit choice yet → follow the UI language.
  const reportLang = override || lang || 'en';
  return (
    <ReportLangContext.Provider value={{ reportLang, setReportLang }}>
      {children}
    </ReportLangContext.Provider>
  );
}

// Small EN/RU control for the report screens. Sets the report language only
// (the app UI language is unaffected).
function ReportLangToggle() {
  const { reportLang, setReportLang } = useReportLang();
  const c = useThemeColors();
  const btn = (active) => ({
    padding: '2px 8px', fontSize: '11px', cursor: 'pointer',
    border: `1px solid ${active ? '#7c3aed' : c.border}`,
    background: active ? '#7c3aed' : 'transparent',
    color: active ? '#fff' : c.textMuted, borderRadius: '4px',
  });
  return (
    <span
      style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}
      title="Report language — language of the downloaded reports, independent of the app UI language"
    >
      <span style={{ fontSize: '11px', color: c.textMuted }}>📄 Report language:</span>
      <button type="button" style={btn(reportLang === 'en')} onClick={() => setReportLang('en')}>EN</button>
      <button type="button" style={btn(reportLang === 'ru')} onClick={() => setReportLang('ru')}>RU</button>
    </span>
  );
}

// Compact language pill switch — shown on the login page (top-right)
// and inside the sidebar's user card. Two states only: EN / RU.
function LanguageSwitch({ compact = false }) {
  const { lang, setLang } = useLang();
  const c = useThemeColors();
  const btn = (active) => ({
    padding: compact ? '2px 8px' : '4px 12px',
    background: active ? c.accent : 'transparent',
    color: active ? c.bgBase : c.textSecondary,
    border: `1px solid ${c.border}`,
    borderRadius: '4px',
    fontSize: compact ? '10px' : '11px',
    fontWeight: 600,
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  });
  return (
    <div style={{ display: 'inline-flex', gap: '4px' }}>
      <button style={btn(lang === 'en')} onClick={() => setLang('en')} aria-label="English">EN</button>
      <button style={btn(lang === 'ru')} onClick={() => setLang('ru')} aria-label="Русский">RU</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Theme — light / dark mode. Mirrors the LanguageContext pattern:
// localStorage persistence, context + hook, compact toggle widget.
//
// CSS variables are defined in the `styles` template above under
// :root (dark default) and [data-theme="light"]. Class-based
// elements theme via CSS automatically. Inline JSX styles need the
// useThemeColors() hook which returns a plain JS object with the
// resolved hex values for the current theme.
// ─────────────────────────────────────────────────────────────
const THEME_STORAGE_KEY = 'apm_theme';

const DARK_COLORS = {
  bgBase: '#0a0e17', bgCard: '#111827', bgCardAlt: '#0f172a',
  bgInput: '#1a2332', bgElevated: '#1a1f2e',
  border: '#1e293b', borderInput: '#2d3748',
  textPrimary: '#e2e8f0', textSecondary: '#94a3b8',
  textLabel: '#cbd5e1', textMuted: '#64748b', textDimmed: '#475569',
  accent: '#00e5ff', accentSecondary: '#7c3aed',
  success: '#22c55e', danger: '#ef4444', warning: '#fbbf24',
};
const LIGHT_COLORS = {
  bgBase: '#f1f5f9', bgCard: '#ffffff', bgCardAlt: '#f8fafc',
  bgInput: '#f1f5f9', bgElevated: '#e2e8f0',
  border: '#cbd5e1', borderInput: '#94a3b8',
  textPrimary: '#0f172a', textSecondary: '#475569',
  textLabel: '#334155', textMuted: '#64748b', textDimmed: '#94a3b8',
  accent: '#0891b2', accentSecondary: '#7c3aed',
  success: '#16a34a', danger: '#dc2626', warning: '#d97706',
};

const ThemeContext = React.createContext({ theme: 'dark', setTheme: () => {} });
function useTheme() { return React.useContext(ThemeContext); }
// Is the viewport phone/tablet sized? Mirrors the 900px breakpoint in the
// stylesheet's mobile layer, so JS-side behaviour (sidebar starts closed,
// tapping a nav item dismisses it) switches at exactly the same width the
// CSS does. Keeping the number in both places in sync matters: if they drift,
// you get a sidebar that overlays the content but never closes itself.
//
// matchMedia rather than a resize listener on innerWidth: it fires only when
// the predicate actually flips, so rotating a phone or opening the on-screen
// keyboard does not spam re-renders. The `addEventListener`/`addListener`
// pair covers Safari < 14, which is still common on older iPhones.
const MOBILE_BREAKPOINT_PX = 900;

function useIsMobile() {
  const q = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;
  const [isMobile, setIsMobile] = React.useState(() => {
    try { return window.matchMedia(q).matches; } catch (_) { return false; }
  });
  React.useEffect(() => {
    let mql;
    try { mql = window.matchMedia(q); } catch (_) { return; }
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, [q]);
  return isMobile;
}

function useThemeColors() {
  const { theme } = useTheme();
  return theme === 'light' ? LIGHT_COLORS : DARK_COLORS;
}

function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(THEME_STORAGE_KEY) || 'dark'; } catch (_) { return 'dark'; }
  });
  const setTheme = useCallback((t) => {
    setThemeState(t);
    try { localStorage.setItem(THEME_STORAGE_KEY, t); } catch (_) {}
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Update the meta theme-color tag so the browser chrome matches.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.content = theme === 'light' ? '#f1f5f9' : '#0a0e17';
  }, [theme]);
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function ThemeSwitch({ compact = false }) {
  const { theme, setTheme } = useTheme();
  const c = useThemeColors();
  const isDark = theme === 'dark';
  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      style={{
        padding: compact ? '2px 8px' : '4px 12px',
        background: 'transparent',
        border: `1px solid ${c.border}`,
        borderRadius: '4px',
        fontSize: compact ? '12px' : '14px',
        cursor: 'pointer',
        color: isDark ? '#fbbf24' : '#0891b2',
      }}
    >
      {isDark ? '☀' : '🌙'}
    </button>
  );
}


// Returns a debounced copy of `value` that only updates after `delay` ms of
// inactivity. Used to avoid filtering large lists on every keystroke.
function useDebounce(value, delay) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// Split the high-churn fleet list from the low-churn selection. Components
// that only read the selected serial no longer re-render every time a websocket
// status batch replaces `meters` (important at 3000-5000 devices).
const MeterSelectionContext = React.createContext({
  selected: null,
  setSelected: () => {},
});
const MeterListContext = React.createContext({ meters: [] });

function useMeterSelection() {
  return React.useContext(MeterSelectionContext);
}
function useMeterList() {
  return React.useContext(MeterListContext);
}

// Assignment data is large and used by multiple tabs. Keep one session cache
// at the app root so navigation does not re-download /api/assignments on every
// mount. Tabs may call refresh({force:true}) after a mutation.
const AssignmentsContext = React.createContext({
  assignments: [],
  assignmentsLoading: false,
  refreshAssignments: async () => [],
});
function useAssignments() {
  return React.useContext(AssignmentsContext);
}

// Navigation context — lets any nested component (e.g. a KPI card on
// the dashboard) switch to a different sidebar tab and optionally
// preset sub-state on the target tab. `meterFilter` is a side-channel
// used by the Meters tab: 'all' | 'assigned' | 'unassigned'. Set it
// before calling setActiveTab('🔌 Device Management') to jump directly into a
// filtered view.
const NavContext = React.createContext({
  setActiveTab: () => {},
  meterFilter: 'all',
  setMeterFilter: () => {},
  dcuFocus: null,
  setDcuFocus: () => {},
  collapseSidebar: () => {},
});

function useNav() {
  return React.useContext(NavContext);
}

// Session-scoped navigation state. Persists to sessionStorage so a page
// RELOAD lands the operator back where they were (same tab, same session),
// but a NEW browser session (or an explicit logout, which clears these keys)
// starts fresh — e.g. Customers opens on "All Customers", not the last meter
// you happened to be looking at yesterday. Used for the customer tree
// selection, the Device-Management sub-tab, and the selected DCU.
const _NAV_SESSION_KEYS = ['apm_last_selection', 'apm_meters_sub', 'apm_dcu_selected',
                           'apm_tree_exp_customers', 'apm_tree_exp_transformers',
                           'apm_tree_exp_dcus'];
function _clearNavSession() {
  try { for (const k of _NAV_SESSION_KEYS) sessionStorage.removeItem(k); } catch (_) {}
}
function useSessionState(key, initial) {
  const [val, setVal] = React.useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw !== null) return JSON.parse(raw);
    } catch (_) {}
    return typeof initial === 'function' ? initial() : initial;
  });
  const set = React.useCallback((v) => {
    setVal(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      try {
        if (next === null || next === undefined) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, [key]);
  return [val, set];
}

// Set-valued sibling of useSessionState. A Set cannot go through
// JSON.stringify — it serialises to "{}" and comes back as a plain object
// with no .has()/.add() — so the customer tree's expansion state could not
// use the helper above and was plain useState(new Set()). That meant every
// remount collapsed the tree: a browser reload, or simply switching to
// another tab and back, and the operator had to re-open the customer they
// were working in. Stored as an array, restored as a Set.
function useSessionSet(key) {
  const [val, setVal] = React.useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw !== null) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch (_) {}
    return new Set();
  });
  const set = React.useCallback((v) => {
    setVal(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      try { sessionStorage.setItem(key, JSON.stringify(Array.from(next))); } catch (_) {}
      return next;
    });
  }, [key]);
  return [val, set];
}

// Toast / notifications context — any component can call useToast().push(msg, type).
const ToastContext = React.createContext({ push: () => {} });
function useToast() {
  return React.useContext(ToastContext);
}

// Confirm dialog context — styled modal replacement for native confirm().
// Usage:
//   const confirm = useConfirm();
//   const ok = await confirm('Delete X?', 'This cannot be undone.');
//   const ok = await confirm('Enable live', 'High blast radius.',
//                            { confirmLabel: 'Enable', confirmColor: '#16a34a' });
// The third opts arg is optional — legacy calls keep the red "Delete"
// button label + colour for backwards compat.
const ConfirmContext = React.createContext(() => Promise.resolve(false));
function useConfirm() {
  return React.useContext(ConfirmContext);
}

function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, resolve, confirmLabel?, confirmColor? }
  const c = useThemeColors();
  const t = useT();

  const showConfirm = useCallback((title, message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        title, message, resolve,
        confirmLabel: opts.confirmLabel,   // string | undefined
        confirmColor: opts.confirmColor,   // CSS colour | undefined
      });
    });
  }, []);

  const handleYes = () => { state?.resolve(true); setState(null); };
  const handleNo = () => { state?.resolve(false); setState(null); };

  return (
    <ConfirmContext.Provider value={showConfirm}>
      {children}
      {state && (
        <div
          onClick={handleNo}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.65)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: '12px',
            padding: '28px 32px',
            width: '420px',
            maxWidth: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: '40px',
              marginBottom: '12px',
            }}>
              &#9888;
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: 600,
              color: c.textPrimary,
              marginBottom: '8px',
            }}>
              {state.title}
            </div>
            {state.message && (
              <div style={{
                fontSize: '13px',
                color: c.textSecondary,
                marginBottom: '24px',
                lineHeight: 1.5,
                whiteSpace: 'pre-line',
              }}>
                {state.message}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={handleNo}
                className="button button-secondary"
                style={{ minWidth: '100px', padding: '8px 20px' }}
              >
                {t('btn.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleYes}
                className="button"
                style={{
                  minWidth: '100px',
                  padding: '8px 20px',
                  background: state.confirmColor || '#dc2626',
                  color: '#fff',
                  border: `1px solid ${state.confirmColor || '#dc2626'}`,
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {state.confirmLabel || t('btn.delete') || 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// MeterSelector — bounded searchable combobox shared by meter-operation tabs.
// A native <select> with 5000 <option> nodes made every tab mount expensive;
// this renders at most RESULT_LIMIT matches and preserves the current choice.
function MeterSelector({ requireSession = false }) {
  const { selected, setSelected } = useMeterSelection();
  const { meters } = useMeterList();
  const c = useThemeColors();
  const listboxId = React.useId();
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const RESULT_LIMIT = 75;
  const eligible = React.useMemo(
    () => requireSession ? meters.filter(m => m.session_open) : meters,
    [meters, requireSession],
  );
  const selectedMeter = React.useMemo(
    () => meters.find(m => (m.serial_number || m.serial) === selected) || null,
    [meters, selected],
  );
  const query = searchText.trim().toLowerCase();
  const results = React.useMemo(() => {
    // Small lists remain one-click browsable. Fleet-sized lists require two
    // characters before scanning/showing matches; the selected meter remains
    // visible even with an empty query.
    if (!query) {
      if (eligible.length <= 100) return eligible.slice(0, RESULT_LIMIT);
      return selectedMeter && eligible.includes(selectedMeter) ? [selectedMeter] : [];
    }
    if (query.length < 2 && eligible.length > 100) return [];
    const out = [];
    for (const m of eligible) {
      const serial = String(m.serial_number || m.serial || '').toLowerCase();
      const deviceId = String(m.device_id_serial || '').toLowerCase();
      const status = String(m.status || '').toLowerCase();
      if (serial.includes(query) || deviceId.includes(query) || status.includes(query)) {
        out.push(m);
        if (out.length >= RESULT_LIMIT) break;
      }
    }
    return out;
  }, [eligible, query, selectedMeter]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);
  useEffect(() => { setActiveIndex(-1); }, [query, results.length]);

  const choose = useCallback((m) => {
    setSelected(m ? (m.serial_number || m.serial) : null);
    setSearchText('');
    setOpen(false);
  }, [setSelected]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (results.length > 0) {
        setActiveIndex(i => Math.min(results.length - 1, i < 0 ? 0 : i + 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (results.length > 0) {
        setActiveIndex(i => i <= 0 ? results.length - 1 : i - 1);
      }
    } else if (e.key === 'Enter' && open && results[activeIndex]) {
      e.preventDefault();
      choose(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (meters.length === 0) {
    return (
      <div style={{
        padding: '12px 16px',
        background: c.bgElevated,
        border: `1px solid ${c.borderInput}`,
        borderRadius: '6px',
        marginBottom: '16px',
        color: c.textSecondary,
        fontSize: '13px',
      }}>
        No meters connected. Wait for a meter to dial in to port 8002.
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      background: c.bgElevated,
      border: `1px solid ${c.borderInput}`,
      borderRadius: '6px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: '13px', color: c.textSecondary }}>Meter:</span>
      {selectedMeter && (
        <button
          type="button"
          className="button button-sm button-secondary"
          onClick={() => choose(null)}
          title="Clear selected meter"
          style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {selectedMeter.device_id_serial || selectedMeter.serial} ×
        </button>
      )}
      <div ref={rootRef} style={{ position: 'relative', minWidth: '280px', flex: '1 1 320px', maxWidth: '520px' }}>
        <input
          className="input-field"
          role="combobox"
          aria-label="Search or select meter"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={open && results[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined}
          aria-autocomplete="list"
          placeholder={eligible.length > 100 ? 'Search serial (type at least 2 characters)…' : 'Search or choose a meter…'}
          value={searchText}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setSearchText(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          style={{ width: '100%', padding: '7px 10px', fontSize: '12px' }}
        />
        {open && (
          <div
            id={listboxId}
            role="listbox"
            style={{
              position: 'absolute', zIndex: 250, top: 'calc(100% + 4px)', left: 0, right: 0,
              maxHeight: '300px', overflowY: 'auto', background: c.bgCard,
              border: `1px solid ${c.borderInput}`, borderRadius: '6px',
              boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
            }}
          >
            {eligible.length > 100 && query.length < 2 && results.length === 0 && (
              <div style={{ padding: '10px 12px', color: c.textMuted, fontSize: '12px' }}>
                Type at least 2 characters to search {eligible.length.toLocaleString()} meters.
              </div>
            )}
            {query.length >= 2 && results.length === 0 && (
              <div style={{ padding: '10px 12px', color: c.textMuted, fontSize: '12px' }}>No matching meters.</div>
            )}
            {results.map((m, i) => {
              const serial = m.serial_number || m.serial;
              return (
                <button
                  id={`${listboxId}-option-${i}`}
                  type="button"
                  role="option"
                  aria-selected={serial === selected}
                  key={serial}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(m)}
                  style={{
                    display: 'flex', width: '100%', justifyContent: 'space-between', gap: '12px',
                    padding: '8px 10px', border: 'none', textAlign: 'left', cursor: 'pointer',
                    background: i === activeIndex ? c.bgCardAlt : 'transparent', color: c.textPrimary,
                  }}
                >
                  <span><code>{m.device_id_serial || serial}</code>{m.device_id_serial && m.device_id_serial !== serial ? ` (${serial})` : ''}</span>
                  <span style={{ color: c.textMuted, fontSize: '11px' }}>{m.status || 'unknown'}</span>
                </button>
              );
            })}
            {results.length === RESULT_LIMIT && (
              <div style={{ padding: '6px 10px', color: c.textMuted, fontSize: '10px', borderTop: `1px solid ${c.border}` }}>
                Showing first {RESULT_LIMIT} matches — type more characters to narrow the list.
              </div>
            )}
          </div>
        )}
      </div>
      <span style={{ color: c.textMuted, fontSize: '11px' }}>{eligible.length.toLocaleString()} eligible</span>
      {requireSession && selected && !meters.find(m => m.serial === selected)?.session_open && (
        <span style={{ color: c.warning, fontSize: '12px' }}>
          Session not open — Open it from the Inbound Meters tab
        </span>
      )}
    </div>
  );
}

// WebSocket Hook
// opts.onMessage: callback-delivery mode — each frame is handed to the
// callback and NOT pushed into the `messages` state. Use this for high-volume
// streams consumed by an always-mounted component (e.g. the App's /ws/meters):
// the state-array mode re-renders the owner on EVERY frame, which at fleet
// scale re-rendered the entire active tab once per second.
function useWebSocket(path, opts) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const onMessageRef = useRef(null);
  onMessageRef.current = (opts && opts.onMessage) || null;

  useEffect(() => {
    let cancelled = false;
    let reconnectTimer = null;
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, capped at 60s.
    // Reset to 1s on a successful open. Previously this was a flat
    // 3 s — fine for a single drop, but a server restart that takes
    // ~20 s to come back used to slam the WS endpoint with 7 dial
    // attempts in that window from every connected dashboard.
    let nextDelayMs = 1000;
    const RECONNECT_DELAY_CAP_MS = 60_000;

    const connect = () => {
      if (cancelled) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Cookie-based auth: the browser auto-attaches same-origin cookies
      // (including emeter_auth HttpOnly) on the WS handshake. The
      // backend reads the cookie in _authorise_ws. No query-param
      // token needed. If the user is logged out (no cookie), the
      // backend closes with code 4401 and onclose retries — but the
      // AuthGate keeps this component unmounted until after login.
      const url = `${protocol}//${window.location.host}${path}`;
      wsRef.current = new WebSocket(url);

      wsRef.current.onopen = () => {
        setConnected(true);
        // Successful connect — reset the backoff so the next drop
        // starts a fresh 1 s → 60 s curve.
        nextDelayMs = 1000;
      };
      wsRef.current.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (onMessageRef.current) {
            onMessageRef.current(msg);   // callback mode: no state, no re-render
            return;
          }
          setMessages(prev => [msg, ...prev.slice(0, 499)]);
        } catch (err) {
          console.error('WebSocket parse error:', err);
        }
      };
      wsRef.current.onclose = (ev) => {
        setConnected(false);
        // Don't reconnect on auth failures (4401 / 4403) — the user
        // needs to log in again or the role didn't permit the stream.
        // Reconnect on transport-level disconnects with exponential
        // backoff so a long server outage doesn't get hammered.
        if (!cancelled && ev.code !== 4401 && ev.code !== 4403) {
          const delay = nextDelayMs;
          nextDelayMs = Math.min(nextDelayMs * 2, RECONNECT_DELAY_CAP_MS);
          reconnectTimer = setTimeout(connect, delay);
        }
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [path]);

  const clear = useCallback(() => setMessages([]), []);

  return { messages, connected, clear };
}

// Toast Component
function Toast({ message, type = 'info', onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast ${type}`}>
      <div>{message}</div>
    </div>
  );
}

// Loading Spinner
function Spinner() {
  return <div className="spinner" />;
}

// Type-to-filter picker for lists a <select> cannot serve.
//
// The DCU pickers were plain dropdowns over ~38 twelve-digit serials that
// differ only in the last few characters (129235042049 / 129235042050 are
// adjacent in the list AND a source/target pair). Finding one meant scanning
// visually for a near-identical number, which is precisely the choice a bulk
// move must not get wrong. Typing "2050" narrows it to one line.
//
// options: [{value, label, hint, disabled}]. `hint` renders muted on the right
// (connection state, meter count) and is SEARCHABLE too, so "Atlas" or
// "offline" find their rows.
function SearchableSelect({
  value, onChange, options, placeholder = 'Search…',
  allowClear = true, clearLabel = 'All', width = 240, disabled = false,
}) {
  const c = useThemeColors();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [hi, setHi] = React.useState(0);
  const boxRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  const selected = React.useMemo(
    () => (options || []).find(o => String(o.value) === String(value)) || null,
    [options, value]);

  const matches = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = options || [];
    if (!s) return base;
    // Every token must appear somewhere in value/label/hint, so "atlas 2050"
    // works as well as either alone.
    const toks = s.split(/\s+/);
    return base.filter(o => {
      const hay = `${o.value} ${o.label || ''} ${o.hint || ''}`.toLowerCase();
      return toks.every(t => hay.includes(t));
    });
  }, [options, q]);

  // Close on any click that is not inside this control.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false); setQ('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  React.useEffect(() => { setHi(0); }, [q, open]);

  // Keep the highlighted row in view when arrowing past the fold.
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${hi}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const commit = (opt) => {
    if (opt && opt.disabled) return;
    onChange(opt ? String(opt.value) : '');
    setOpen(false); setQ('');
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && matches[hi]) commit(matches[hi]);
      else setOpen(true);
    } else if (e.key === 'Escape') { setOpen(false); setQ(''); e.currentTarget.blur(); }
  };

  const field = {
    padding: '6px 10px', fontSize: '12px', width: '100%', boxSizing: 'border-box',
    background: disabled ? c.border : c.bgInput, color: c.textPrimary,
    border: `1px solid ${c.border}`, borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'text',
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', width, flexShrink: 0 }}>
      <input
        ref={inputRef}
        disabled={disabled}
        value={open ? q : (selected ? (selected.label || String(selected.value)) : '')}
        placeholder={selected ? '' : placeholder}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { if (!disabled) { setOpen(true); setQ(''); } }}
        onKeyDown={onKey}
        style={field} />
      {/* Clear sits inside the field so the control keeps one grid slot. */}
      {allowClear && selected && !open && !disabled && (
        <button
          onMouseDown={e => { e.preventDefault(); onChange(''); }}
          title={`Clear — show ${clearLabel.toLowerCase()}`}
          style={{
            position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', color: c.textMuted,
            cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 4px',
          }}>×</button>
      )}
      {open && (
        <div ref={listRef} style={{
          position: 'absolute', zIndex: 50, top: 'calc(100% + 3px)', left: 0, right: 0,
          maxHeight: '260px', overflowY: 'auto', background: c.bgCard,
          border: `1px solid ${c.border}`, borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        }}>
          {allowClear && (
            <div data-idx="-1" onMouseDown={e => { e.preventDefault(); commit(null); }}
              style={{ padding: '7px 10px', fontSize: '12px', cursor: 'pointer',
                color: c.textMuted, borderBottom: `1px solid ${c.border}` }}>
              {clearLabel}
            </div>
          )}
          {matches.length === 0 && (
            <div style={{ padding: '9px 10px', fontSize: '12px', color: c.textMuted }}>
              No match for “{q}”
            </div>
          )}
          {matches.map((o, i) => (
            <div key={String(o.value)} data-idx={i}
              onMouseEnter={() => setHi(i)}
              onMouseDown={e => { e.preventDefault(); commit(o); }}
              style={{
                display: 'flex', justifyContent: 'space-between', gap: '10px',
                padding: '7px 10px', fontSize: '12px',
                cursor: o.disabled ? 'not-allowed' : 'pointer',
                opacity: o.disabled ? 0.45 : 1,
                background: i === hi ? c.bgHover || c.border : 'transparent',
                color: String(o.value) === String(value) ? '#7c3aed' : c.textPrimary,
              }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {o.label || String(o.value)}
              </span>
              {o.hint && (
                <span style={{ color: c.textMuted, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {o.hint}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Quick-range preset buttons for from/to date inputs. Hands back
// YYYY-MM-DD strings (the same shape the <input type="date"> expects)
// computed against the meter-local tz so "Last 7d" always means the
// last 7 Tashkent days regardless of browser tz. Presets:
//   24h → yesterday through today
//   7d / 31d / 90d / 180d / 365d → N-day window ending today
//   mtd → first of current month through today
// Accepts (setFromDate, setToDate) callbacks and an optional `compact`
// flag to render tighter buttons inside cramped toolbars.
function QuickRangeButtons({ setFromDate, setToDate, compact = false }) {
  const c = useThemeColors();
  // Use Intl against METER_TZ so the returned YYYY-MM-DD strings are
  // the operator's local (Tashkent) calendar date, not the browser's.
  const tashkentYmd = (epochSeconds) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: METER_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date(epochSeconds * 1000));
    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const d = parts.find(p => p.type === 'day').value;
    return `${y}-${m}-${d}`;
  };
  const apply = (key) => {
    const nowS = Math.floor(Date.now() / 1000);
    const today = tashkentYmd(nowS);
    if (key === 'mtd') {
      setFromDate(today.slice(0, 7) + '-01');
      setToDate(today);
      return;
    }
    const days = { '24h': 1, '7d': 7, '31d': 31, '90d': 90, '180d': 180, '365d': 365 }[key];
    if (days == null) return;
    setFromDate(tashkentYmd(nowS - days * 86400));
    setToDate(today);
  };
  const btn = {
    padding: compact ? '2px 6px' : '4px 10px',
    fontSize: compact ? '10px' : '11px',
    background: c.bgBase,
    border: `1px solid ${c.border}`,
    color: c.textSecondary,
    borderRadius: '4px',
    cursor: 'pointer',
  };
  const presets = [
    ['24h', '24h'], ['7d', '7d'], ['31d', '31d'],
    ['90d', '90d'], ['180d', '180d'], ['365d', '1y'], ['mtd', 'MTD'],
  ];
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {presets.map(([key, label]) => (
        <button key={key} type="button" style={btn} onClick={() => apply(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

// Dismissible inline error banner for when a non-fatal backend call
// fails and the card would otherwise fall back to stale/empty state
// silently. Pair with a retry handler so the operator can recover
// without navigating away.
// Fleet alert banner (Phase 2). Polls /api/alerts and shows active conditions
// the operator must see — crash-looping DCUs and mass-drops — that the ordinary
// tab views do not surface. Dismissible per-render; reappears if still active
// on the next poll, because an active fault should not stay hidden.
function AlertBanner() {
  const c = useThemeColors();
  const { user } = useAuth();
  const [alerts, setAlerts] = React.useState([]);
  const [dismissed, setDismissed] = React.useState({});   // key -> true
  const [open_, setOpen] = React.useState(false);         // show the extras
  React.useEffect(() => {
    if (!userCan(user, 'dcu_diagnostics')) return;
    let alive = true;
    const load = async () => {
      try {
        const d = await api('/alerts');
        if (alive) setAlerts((d && d.alerts) || []);
      } catch (e) { /* a failed poll must never break the app */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [user && user.permissions]);

  const keyOf = (a) => a.kind === 'dcu_crash_loop' ? `cl:${a.dcu}`
    : a.kind === 'mass_drop' ? `md:${a.at}` : JSON.stringify(a);
  const shown = alerts.filter(a => !dismissed[keyOf(a)]);
  if (!shown.length) return null;

  // QUIET BY DEFAULT. This used to render every alert as its own full-bleed
  // solid-crimson block; two concurrent mass-drop alerts stacked into a slab
  // that dominated the page (reported 2026-09-11). An alert is information,
  // not an interruption — the operator is usually mid-task on something else.
  //
  // So: ONE compact strip, opaque card background (it is sticky, and a
  // translucent bar over scrolling content is unreadable), severity carried by
  // a 3px left border and the icon rather than by flooding the row with
  // colour. Extra alerts collapse behind a "+N" the operator can expand.
  const rows = open_ ? shown : shown.slice(0, 1);
  const hidden = shown.length - rows.length;
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 40, marginBottom: '10px' }}>
      {rows.map((a) => {
        const err = a.severity === 'error';
        const accent = err ? c.danger : c.warning;
        return (
          <div key={keyOf(a)} style={{
            background: c.bgCard, color: c.textSecondary,
            borderLeft: `3px solid ${accent}`,
            border: `1px solid ${c.border}`, borderLeftWidth: '3px',
            borderLeftColor: accent, borderRadius: '4px',
            padding: '5px 10px', fontSize: '12px', marginBottom: '4px',
            display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: accent }}>{err ? '⟳' : '⚠'}</span>
            <span style={{
              flex: 1, minWidth: 0,
              // Collapsed: one line, ellipsised. Expanded: full text, so a
              // long message is still readable rather than silently cut.
              ...(open_ ? {} : { whiteSpace: 'nowrap', overflow: 'hidden',
                                 textOverflow: 'ellipsis' }),
            }} title={a.message}>{a.message}</span>
            {hidden > 0 && (
              <button onClick={() => setOpen(true)}
                title={`${hidden} more alert${hidden === 1 ? '' : 's'}`}
                style={{ background: 'transparent', border: `1px solid ${c.border}`,
                  color: c.textMuted, cursor: 'pointer', fontSize: '10px',
                  borderRadius: '8px', padding: '0 6px', lineHeight: '16px' }}>
                +{hidden}
              </button>
            )}
            {open_ && shown.length > 1 && a === rows[0] && (
              <button onClick={() => setOpen(false)} title="Collapse"
                style={{ background: 'transparent', border: 'none',
                  color: c.textMuted, cursor: 'pointer', fontSize: '11px' }}>▴</button>
            )}
            <button onClick={() => setDismissed(d => ({ ...d, [keyOf(a)]: true }))}
              title="Hide until the next check"
              style={{ background: 'transparent', border: 'none', color: c.textMuted,
                cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}>×</button>
          </div>
        );
      })}
    </div>
  );
}


function ErrorBanner({ message, onRetry, onDismiss, style }) {
  if (!message) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 12px', marginBottom: '8px',
      background: 'rgba(239, 68, 68, 0.08)',
      border: '1px solid rgba(239, 68, 68, 0.35)',
      borderRadius: '6px', fontSize: '12px',
      color: 'var(--danger)',
      ...style,
    }}>
      <span style={{ flex: 1 }}>⚠ {message}</span>
      {onRetry && (
        <button className="button button-sm button-secondary"
                onClick={onRetry}
                style={{ padding: '2px 8px', fontSize: '11px' }}>
          Retry
        </button>
      )}
      {onDismiss && (
        <button onClick={onDismiss}
                aria-label="dismiss"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--danger)', fontSize: '14px', padding: '0 4px',
                }}>
          ✕
        </button>
      )}
    </div>
  );
}

// Status Badge
function StatusBadge({ status }) {
  const colors = {
    'identified': 'badge-info',
    'session open': 'badge-success',
    'connected': 'badge-success',
    'disconnected': 'badge-danger',
    'identifying': 'badge-warning',
    // Distinct from "offline" on purpose — see the call site.
    'never seen': 'badge-warning'
  };
  const dots = {
    'identified': '●',
    'session open': '●',
    'connected': '●',
    'disconnected': '●',
    'identifying': '◐',
    'never seen': '◌'
  };
  const titles = {
    'never seen': 'Assigned but has NEVER dialled in — nothing server-side '
      + 'can fix this; it needs a field check (power, SIM, APN, server address).',
    'offline': 'Has connected before, but is not connected right now.',
  };
  return (
    <span className={`badge ${colors[status] || 'badge-info'}`}
          title={titles[status] || undefined}>
      {dots[status] || '○'} {status}
    </span>
  );
}

// Confirm Dialog
function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">{title}</div>
        <div className="modal-body">{message}</div>
        <div className="modal-footer">
          <button className="button button-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="button button-danger" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// Dashboard Tab — high-level KPIs + daily energy chart, scoped to what
// the logged-in user can see via their customer access list.
//
// Data sources:
//   GET /api/kpis                — snapshot of energy + counts
//   GET /api/kpis/energy-history — 30-day daily kWh series for the chart
//   GET /api/scheduler/status    — last midnight-run summary (admin footer)
//   WebSocket /ws/events         — live activity feed
// Admin-only dashboard KPI: count of meters seen on PLC DCUs' blacklists that
// aren't in our system (from the daily scan). Click → that DCU's Black List.
function AbnormalPlcKpi() {
  const { user } = useAuth();
  const nav = useNav();
  const c = useThemeColors();
  const t = useT();
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!userCan(user, 'dcu_diagnostics')) return;
    let alive = true;
    const load = () => api('/abnormal-plc-connections').then(d => { if (alive) setSummary(d); }).catch(() => {});
    load();
    const id = setInterval(load, 10 * 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.role]);
  if (!userCan(user, 'dcu_diagnostics')) return null;
  const count = summary?.total_unknown ?? 0;     // unknown meters = the anomaly
  const cross = summary?.total_cross ?? 0;        // registered to a nearby DCU (ok, informational)
  const topDcu = (summary?.dcus || []).find(d => d.count > 0)?.dcu_serial
    || (summary?.dcus || []).find(d => d.cross_count > 0)?.dcu_serial;
  const anyIssue = count > 0 || cross > 0;
  const sub = !summary ? ''
    : count > 0 ? (cross > 0 ? t('plc.abnormal_unknown_cross', { n: cross }) : t('plc.abnormal_unknown'))
    : cross > 0 ? t('plc.abnormal_cross', { n: cross })
    : t('plc.abnormal_none');
  return (
    <KpiCard
      label={t('kpi.abnormal_plc')}
      value={summary ? count : '—'}
      sub={sub}
      accent={count > 0 ? c.danger : (cross > 0 ? c.warning : c.success)}
      onClick={anyIssue && topDcu ? () => { nav.setDcuFocus({ dcu: topDcu, section: 'blacklist' }); nav.setActiveTab('🔌 Device Management'); } : null}
    />
  );
}

function DashboardTab() {
  const { user } = useAuth();
  const toast = useToast();
  const nav = useNav();
  const t = useT();
  const c = useThemeColors();
  const [kpis, setKpis] = useState(null);
  const [history, setHistory] = useState([]);

  // Cross-tab navigation helper — presets the Meters-tab filter and
  // then switches to that tab. Used by the clickable KPI cards below.
  const jumpToMeters = (filter) => {
    nav.setMeterFilter(filter);
    nav.setActiveTab('🔌 Device Management');
  };

  useEffect(() => {
    let mounted = true;
    const fetchKpis = async () => {
      try {
        const [k, h] = await Promise.all([
          api('/kpis'),
          api('/kpis/energy-history?days=30'),
        ]);
        if (!mounted) return;
        setKpis(k);
        setHistory(h.series || []);
      } catch (err) {
        if (mounted) toast.push(`KPI fetch failed: ${err.message}`, 'error');
      }
    };
    fetchKpis();
    // 30 s (was 15 s): the backend caches the energy aggregates for 60 s
    // anyway, so a faster poll only re-downloads identical numbers.
    const t = setInterval(fetchKpis, 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <div className="tab-content">
      <div className="section-title">📊 {t('tab.dashboard')}</div>

      {/* ── KPI cards ── */}
      <div className="stat-grid">
        <KpiCard
          label={t('kpi.energy_today')}
          value={kpis ? `${kpis.energy_today_kwh.toLocaleString()} kWh` : '—'}
          accent={c.accent}
        />
        <KpiCard
          label={t('kpi.energy_month')}
          value={kpis ? `${kpis.energy_month_kwh.toLocaleString()} kWh` : '—'}
          accent={c.accentSecondary}
        />
        <KpiCard
          label={t('kpi.assigned_meters')}
          value={kpis ? `${kpis.online_count} / ${kpis.meter_count}` : '—'}
          sub={kpis ? `${kpis.session_count} ${t('kpi.session_open')}` : ''}
          accent={c.success}
          onClick={() => jumpToMeters('assigned')}
        />
        <KpiCard
          label={t('kpi.unassigned_meters')}
          value={kpis ? kpis.unassigned_count : '—'}
          sub={
            kpis && kpis.unassigned_count > 0
              ? (userCan(user, 'dcu_diagnostics') ? 'click to claim' : 'click to view')
              : 'none to claim'
          }
          accent={c.warning}
          onClick={
            kpis && kpis.unassigned_count > 0
              ? () => jumpToMeters('unassigned')
              : null
          }
        />
        <AbnormalPlcKpi />
      </div>

      {/* ── Fleet daily-collection monitor ──
          Above the energy chart deliberately: "how many meters reported
          today" is the question an operator opens this page to answer, and
          on a phone anything below the KPI grid plus a chart is two screens
          down. Energy totals follow it. */}
      <CollectionStatusCard />

      {/* ── Energy-history chart ── */}
      <div style={{
        marginTop: '24px',
        background: c.bgCard,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '20px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: c.textLabel }}>
            📈 {t('dashboard.energy_chart_title')}
          </div>
          <div style={{ fontSize: '12px', color: c.textMuted }}>
            {history.length > 0 && `Σ ${history.reduce((s, x) => s + x.kwh, 0).toLocaleString()} kWh`}
          </div>
        </div>
        <EnergyBarChart data={history} />
      </div>

      <div style={{ marginTop: '24px', fontSize: '12px', color: c.textMuted, textAlign: 'center' }}>
        {t('dashboard.hint_pick_meter')}
      </div>
    </div>
  );
}

// Fleet-wide daily-collection monitor — per-day count of meters with a
// daily-billing row over the last 15 days, auto-refreshing every 60 s.
// Surfaces collection dips (e.g. an offline DCU drops its children) at a
// glance. Reads GET /api/reports/daily-collection-status (overall, not
// per-client).
function CollectionStatusCard() {
  const c = useThemeColors();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  // Clickable "Missing" breakdown modal: which customers own the meters that
  // didn't collect on a given day.
  const [missDate, setMissDate] = useState(null);
  const [miss, setMiss] = useState(null);
  const [missLoading, setMissLoading] = useState(false);
  const [missErr, setMissErr] = useState(null);
  // The bar strip runs oldest -> newest, so on a narrow screen the newest
  // days — the ones an operator actually opens this for — start scrolled
  // off the right edge. 15 bars need ~450px; a phone gives ~330px, so four
  // days were hidden with nothing on screen to suggest they existed.
  // Park the strip at the right end once data lands; history is a swipe
  // left from there.
  const stripRef = React.useRef(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('/reports/daily-collection-status?days=15');
      setData(d); setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  const openMissing = useCallback(async (date) => {
    setMissDate(date); setMiss(null); setMissErr(null); setMissLoading(true);
    try {
      const d = await api(`/reports/daily-collection-missing?date=${encodeURIComponent(date)}`);
      setMiss(d);
    } catch (e) {
      setMissErr(e.message);
    } finally {
      setMissLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    // Daily collection changes once per day per meter (the midnight snapshot +
    // gap-fill via the 01:00 Tashkent scheduler). Poll every 30 min so the new
    // day's data appears on its own shortly after it lands; the manual ↻
    // Refresh button covers the case where a day didn't sync properly.
    const id = setInterval(load, 30 * 60_000);
    return () => clearInterval(id);
  }, [load]);
  const byDay = (data && data.by_day) || [];

  // Jump the strip to its right edge whenever the data changes. Runs after
  // paint, so scrollWidth is already the real width. Harmless on a desktop,
  // where the strip does not overflow and scrollLeft stays 0.
  React.useEffect(() => {
    const el = stripRef.current;
    if (!el || !byDay.length) return;
    el.scrollLeft = el.scrollWidth;
  }, [byDay.length, data]);
  const fleet = (data && data.fleet) || 0;
  const colorFor = (pct) => (pct >= 95 ? '#22c55e' : pct >= 80 ? '#f59e0b' : '#ef4444');
  return (
    <div style={{ marginTop: '24px', background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
        <div className="section-title" style={{ margin: 0 }}>📅 Daily Collection — last 15 days ({data?.scoped ? 'your meters' : 'all meters'})</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '11px', color: c.textMuted }}>{fleet} meters · auto-updates every 30 min</span>
          <button
            onClick={load}
            disabled={loading}
            title="Refresh now"
            style={{ fontSize: '11px', padding: '3px 9px', cursor: loading ? 'default' : 'pointer', background: c.bgCard, color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: '4px' }}
          >
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>
      {err && <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '8px' }}>{err}</div>}

      {/* Headline figures for the most recent day. The bar strip and the
          table below both answer 'how many collected' only by reading a
          chart; on a phone that chart is 15 bars in ~330px. These three
          numbers answer it directly and are the first thing on screen. */}
      {byDay.length > 0 && (() => {
        const last = byDay[byDay.length - 1];
        const tile = (label, value, color) => (
          <div style={{
            flex: '1 1 90px', background: c.bgCardAlt, border: `1px solid ${c.border}`,
            borderRadius: '8px', padding: '10px 12px',
          }}>
            <div style={{ fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase',
              color: c.textMuted, marginBottom: '4px' }}>{label}</div>
            <div style={{ fontSize: '21px', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </div>
          </div>
        );
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
            {tile('Collected', `${last.collected}`, '#22c55e')}
            {tile('Not collected', `${last.missing}`, last.missing > 0 ? '#f59e0b' : c.textMuted)}
            {tile(`Coverage · ${String(last.date || '').slice(5)}`, `${last.pct}%`, colorFor(last.pct))}
          </div>
        );
      })()}

      {/* minWidth per bar + horizontal scroll: 15 bars sharing a 330px phone
          leaves ~20px each, too narrow for the % label above them. Scrolling
          keeps every day readable at its natural width. */}
      <div ref={stripRef}
        className="collection-strip"
        style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '130px',
        marginBottom: '14px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {byDay.map((d) => (
          <div key={d.date}
               title={`${d.date}: ${d.collected}/${fleet} (${d.pct}%) · missing ${d.missing}`}
               style={{ flex: '1 0 auto', minWidth: '26px', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
            <div style={{ fontSize: '9px', color: c.textMuted, flex: '0 0 auto' }}>{d.pct}%</div>
            {/* The bar sizes itself against the space LEFT OVER after the two
                labels, not against a hardcoded pixel height. The old
                `(pct/100) * 95px` assumed a 130px strip: 9px label + 95px bar
                + 3px gap + 9px date = 116px of content, so the moment the
                strip was shortened for phones (96px) the date row was sliced
                off the bottom with no scrollbar to reveal it. A flexible
                middle track adapts to any strip height. */}
            <div style={{ flex: '1 1 auto', minHeight: 0, width: '70%',
              display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ width: '100%', height: `${Math.max(2, d.pct)}%`,
                background: colorFor(d.pct), borderRadius: '2px 2px 0 0' }} />
            </div>
            <div style={{ fontSize: '9px', color: c.textMuted, marginTop: '3px', flex: '0 0 auto' }}>{String(d.date || '').slice(5)}</div>
          </div>
        ))}
      </div>
      <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: c.textMuted, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
            <th style={{ padding: '4px 6px' }}>Date</th>
            <th style={{ padding: '4px 6px', textAlign: 'right' }}>Collected</th>
            <th style={{ padding: '4px 6px', textAlign: 'right' }}>Missing</th>
            <th style={{ padding: '4px 6px', textAlign: 'right' }}>%</th>
          </tr>
        </thead>
        <tbody>
          {[...byDay].reverse().map((d) => (
            <tr key={d.date} style={{ borderBottom: `1px solid ${c.border}` }}>
              <td style={{ padding: '4px 6px' }}>{d.date}</td>
              <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{d.collected}/{fleet}</td>
              <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace' }}>
                {d.missing > 0 ? (
                  <button
                    onClick={() => openMissing(d.date)}
                    title="Show which customers are missing meters this day"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#f59e0b', fontFamily: 'monospace', fontSize: '12px', textDecoration: 'underline' }}
                  >
                    {d.missing}
                  </button>
                ) : (
                  <span style={{ color: c.textMuted }}>{d.missing}</span>
                )}
              </td>
              <td style={{ padding: '4px 6px', textAlign: 'right', color: colorFor(d.pct), fontWeight: 600 }}>{d.pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      {missDate && (
        <ModalShell title={`Missing meters — ${missDate}`} onClose={() => setMissDate(null)}>
          {missLoading && <div style={{ color: c.textMuted, fontSize: '13px' }}>Loading…</div>}
          {missErr && <div style={{ color: '#ef4444', fontSize: '13px' }}>{missErr}</div>}
          {miss && !missLoading && (
            miss.total_missing === 0 ? (
              <div style={{ color: '#22c55e', fontSize: '13px' }}>✓ No missing meters — full collection this day.</div>
            ) : (
              <div>
                <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '10px' }}>
                  {miss.total_missing} meter{miss.total_missing === 1 ? '' : 's'} across {miss.by_customer.length} customer{miss.by_customer.length === 1 ? '' : 's'}
                </div>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: c.textMuted, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
                      <th style={{ padding: '4px 6px' }}>Customer</th>
                      <th style={{ padding: '4px 6px', textAlign: 'right' }}>Missing</th>
                      <th style={{ padding: '4px 6px' }}>DCU(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {miss.by_customer.map((row, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${c.border}` }}>
                        <td style={{ padding: '4px 6px' }}>{row.customer_name}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', color: '#f59e0b', fontWeight: 600, fontFamily: 'monospace' }}>{row.missing}</td>
                        <td style={{ padding: '4px 6px', fontFamily: 'monospace', color: c.textMuted, wordBreak: 'break-all' }}>
                          {row.crash_looping_dcus && row.crash_looping_dcus.length > 0 && (
                            <div style={{ color: '#a855f7', marginBottom: '2px' }}
                                 title="These DCUs are crash-looping — restarting repeatedly, so they connect but never finish a session. That is why their meters are missing. A site reset will not help.">
                              ⟳ crash-looping: {row.crash_looping_dcus.join(', ')}
                            </div>
                          )}
                          {row.dcus && row.dcus.length ? row.dcus.join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </ModalShell>
      )}
    </div>
  );
}

// Direct (GPRS) meter collection queue — emeter/direct_collector.py.
// One continuous queue, most-behind meters first, small index reads. This
// panel mirrors GET /api/direct-collection/status and switches the rollout
// (off / cohort / all) without a restart via POST /api/direct-collection/mode.
function DirectCollectionPanel() {
  const toast = useToast();
  const c = useThemeColors();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(() => {
    api('/direct-collection/status').then(setSt).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);
  const setMode = async (mode, percent) => {
    const what = mode === 'off' ? 'hand every direct meter back to the scheduler bursts'
      : mode === 'all' ? 'collect EVERY direct meter through the queue'
      : `collect ${percent}% of direct meters through the queue`;
    if (!window.confirm(`Direct collection: ${what}? Takes effect within 15 s, no restart.`)) return;
    setBusy(true);
    try {
      await api('/direct-collection/mode', { method: 'POST', body: JSON.stringify({ mode, percent }) });
      toast.push(`Direct collection mode: ${mode}${mode === 'cohort' ? ` (${percent}%)` : ''}`, 'success');
      refresh();
    } catch (e) { toast.push(e.message || String(e), 'error'); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  const lh = st.last_hour || {};
  const lag = st.lag_h || {};
  const pol = st.poller || {};
  const tile = (label, value, sub, warn) => (
    <div style={{ flex: 1, minWidth: '130px', background: c.bgBase, border: `1px solid ${warn ? '#f59e0b' : c.border}`, borderRadius: '8px', padding: '10px 12px' }}>
      <div style={{ fontSize: '11px', color: c.textMuted }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: 700, color: warn ? '#f59e0b' : c.textPrimary }}>{value ?? '—'}</div>
      {sub ? <div style={{ fontSize: '11px', color: c.textSecondary }}>{sub}</div> : null}
    </div>
  );
  const btn = (label, mode, percent, active) => (
    <button key={label} disabled={busy || active} onClick={() => setMode(mode, percent)}
      style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', cursor: active ? 'default' : 'pointer',
               background: active ? c.bgElevated : c.bgBase, color: active ? c.accent : c.textPrimary,
               border: active ? `2px solid ${c.accent}` : `1px solid ${c.border}` }}>{label}</button>
  );
  const pct = st.percent;
  return (
    <div style={{ marginTop: '16px', background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: c.textPrimary }}>Direct meter collection</div>
        <span style={{ fontSize: '12px', color: st.running ? '#22c55e' : '#ef4444' }}>
          {st.running ? 'running' : 'not running'}
        </span>
        <span style={{ flex: 1 }} />
        {btn('Off', 'off', null, st.mode === 'off')}
        {btn('Trial 7%', 'cohort', 7, st.mode === 'cohort' && pct === 7)}
        {btn('All', 'all', null, st.mode === 'all')}
      </div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '12px', lineHeight: 1.5 }}>
        GPRS meters are read continuously, most-behind first, only what is new — every {st.interval_h ?? 3} h
        per meter. Meters in the queue are skipped by the 00/06/12/18 bursts. Mode: <strong>{st.mode}</strong>
        {st.mode === 'cohort' ? ` (${pct}%)` : ''}.
      </div>
      {(st.alerts || []).map((a, i) => (
        <div key={i} style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '6px' }}>⚠ {a}</div>
      ))}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {tile('Meters in queue (online)', st.owned_connected, `${(st.in_flight || []).length}/${st.workers} workers reading`)}
        {tile('Load profile behind', lag.lp_p50 != null ? `${lag.lp_p50} h` : null,
              `95% within ${lag.lp_p95 ?? '—'} h · ${lag.lp_over_6h ?? 0} over 6 h`, (lag.lp_p95 || 0) > 6)}
        {tile('Power quality behind', lag.pq_p50 != null ? `${lag.pq_p50} h` : null, `95% within ${lag.pq_p95 ?? '—'} h`, (lag.pq_p95 || 0) > 6)}
        {tile('Last hour', `${lh.jobs ?? 0} meters`, `${lh.answered ?? 0} answered · ${lh.rows ?? 0} rows`)}
        {tile('Per meter', lh.job_s_p50 != null ? `${lh.job_s_p50} s` : null, `95% under ${lh.job_s_p95 ?? '—'} s`)}
        {tile('Workers busy', lh.busy_pct != null ? `${lh.busy_pct}%` : null, 'add workers above 50%', (lh.busy_pct || 0) > 50)}
        {tile('Live poll age', pol.age_s_p50 != null ? `${Math.round(pol.age_s_p50 / 60)} min` : null,
              pol.age_s_p95 != null ? `95% within ${Math.round(pol.age_s_p95 / 60)} min · ${pol.workers ?? '—'} workers · ${pol.busy_pct ?? '—'}% busy` : '')}
      </div>
      {st.dcu && (() => {
        const u = st.dcu.unread_hours_24h || {};
        const n = st.dcu.nightly || {};
        return (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {tile('DCU hourly LP/PQ unread (24 h)', u.lp_total != null ? `${u.lp_total} / ${u.pq_total} h` : null,
                  (u.worst || []).slice(0, 3).map(w => `${w.dcu} ${w.lp}/${w.pq}`).join(' · ') || `${u.dcus ?? 0} DCUs, none behind`,
                  (u.worst || []).some(w => Math.max(w.lp, w.pq) > 6))}
            {st.dcu.vendor_led && st.dcu.vendor_led.running && st.dcu.vendor_led.mode !== 'off' && tile('DCU vendor-led collection',
                  `${st.dcu.vendor_led.this_hour?.done ?? 0} DCUs done this hour`,
                  `${st.dcu.vendor_led.last_24h?.ok ?? 0}/${st.dcu.vendor_led.last_24h?.slots ?? 0} slots ok in 24 h · ${st.dcu.vendor_led.last_24h?.rows ?? 0} rows`,
                  (st.dcu.vendor_led.last_24h?.slots || 0) >= 20 && (st.dcu.vendor_led.last_24h?.ok || 0) < 0.8 * st.dcu.vendor_led.last_24h.slots)}
            {st.dcu.quiet_trial && st.dcu.quiet_trial.mode !== 'off' && tile('DCU quiet-night trial',
                  `${st.dcu.quiet_trial.trial_dcus_connected} trial / ${st.dcu.quiet_trial.control_dcus_connected} control`,
                  `${st.dcu.quiet_trial.window} · ${st.dcu.quiet_trial.reads_skipped} reads skipped`)}
            {tile('Nightly DCU sweep', n.dcus != null ? `${n.dcus} DCUs` : null,
                  n.not_finished && n.not_finished.length ? `not finished: ${n.not_finished.join(', ')}` : `${n.sweep_workers ?? '—'} sweep workers · all finished`,
                  !!(n.not_finished && n.not_finished.length))}
          </div>
        );
      })()}
      {(st.recent || []).length > 0 && (
        <div style={{ maxHeight: '220px', overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: '6px' }}>
          {[...st.recent].reverse().map((j, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '11px', padding: '4px 8px', borderBottom: `1px solid ${c.border}22` }}>
              <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{j.serial}</span>
              <span style={{ color: c.textMuted }}>{new Date(j.started * 1000).toLocaleTimeString('en-GB', { timeZone: 'Asia/Tashkent' })}</span>
              <span style={{ color: c.textMuted }}>{j.dur_s}s</span>
              <span style={{ color: j.skipped ? c.textMuted : j.answered ? '#22c55e' : '#ef4444' }}>
                {j.skipped || (j.answered ? `+${j.rows} rows` : 'no answer')}
              </span>
              <span style={{ color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {Object.entries(j.steps || {}).map(([k, v]) => `${k} ${v.error || v.skipped || `+${v.rows ?? 0}`} ${v.s != null ? v.s + 's' : ''}`).join(' · ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Small settings widget for the live-poll interval. Reads + writes
// /api/poller/config. Lives at the bottom of the dashboard so the
// admin sees it without leaving the main view.
function PollerIntervalSettings() {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api('/poller/config').then(c => {
      setConfig(c);
      setDraft(String(c.interval_seconds ?? ''));
    }).catch(err => toast.push(err.message, 'error'));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async (seconds) => {
    const n = typeof seconds === 'number' ? seconds : parseInt(draft, 10);
    if (Number.isNaN(n) || n < 5 || n > 3600) {
      toast.push(t('toast.interval_invalid'), 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await api('/poller/config', {
        method: 'PATCH',
        body: JSON.stringify({ interval_seconds: n }),
      });
      toast.push(`Poller interval set to ${r.interval_seconds}s`, 'success');
      refresh();
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!config) return null;
  const current = config.interval_seconds;
  const dirty = String(current) !== draft;

  // Preset options with estimated daily SIM traffic per meter.
  // Formula: (86400/interval) cycles × 25 registers × ~120 bytes/register
  // × 1.12 GPRS overhead + ~215 KB fixed (heartbeat + scheduler + reconnect).
  const presets = [
    { sec: 60,  label: '1 min',  desc: 'Real-time feel',          mb: '~4.9 MB/day' },
    { sec: 120, label: '2 min',  desc: 'Near-real-time',           mb: '~2.6 MB/day' },
    { sec: 300, label: '5 min',  desc: 'Standard AMI (recommended)', mb: '~1.2 MB/day' },
    { sec: 600, label: '10 min', desc: 'Low-traffic',              mb: '~690 KB/day' },
    { sec: 900, label: '15 min', desc: 'Load-profile cadence',     mb: '~530 KB/day' },
  ];

  const presetBtn = (p) => {
    const active = current === p.sec;
    return (
      <button
        key={p.sec}
        onClick={() => { setDraft(String(p.sec)); save(p.sec); }}
        disabled={busy || active}
        style={{
          padding: '10px 14px',
          background: active ? c.bgElevated : c.bgBase,
          border: active ? `2px solid ${c.accent}` : `1px solid ${c.border}`,
          borderRadius: '8px',
          color: active ? c.accent : c.textPrimary,
          cursor: active ? 'default' : 'pointer',
          textAlign: 'left',
          flex: 1,
          minWidth: '140px',
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: 700 }}>{p.label}</div>
        <div style={{ fontSize: '11px', color: c.textSecondary, marginTop: '2px' }}>{p.desc}</div>
        <div style={{ fontSize: '10px', color: c.textMuted, marginTop: '4px', fontFamily: 'monospace' }}>{p.mb}</div>
      </button>
    );
  };

  return (
    <div style={{
      marginTop: '16px',
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: c.textLabel }}>
          ⚙ {t('section.polling_interval')}
        </div>
        <div style={{ fontSize: '12px', color: config.running ? c.success : c.danger }}>
          {config.running ? t('poller.running') : t('poller.stopped')} · {t('poller.currently', { n: current })}
        </div>
      </div>

      <div style={{ fontSize: '12px', color: c.textSecondary, marginBottom: '14px', lineHeight: 1.5 }}>
        Controls how often the poller reads 25 registers (V/I/P/E) from each meter via GPRS.
        Lower intervals = fresher dashboard data but higher SIM data consumption.
        The estimated traffic below is <strong>per meter per day</strong> and includes
        TCP/IP overhead + heartbeats + midnight scheduler pulls.
        Changes take effect immediately and persist across restarts.
      </div>

      {/* ── Preset buttons ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {presets.map(presetBtn)}
      </div>

      {/* ── Custom input for non-standard values ── */}
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'center',
        padding: '10px 12px',
        background: c.bgBase,
        border: `1px solid ${c.border}`,
        borderRadius: '6px',
      }}>
        <span style={{ fontSize: '11px', color: c.textMuted, whiteSpace: 'nowrap' }}>{t('poller.custom')}:</span>
        <input
          type="number"
          className="input-field"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          min="5"
          max="3600"
          style={{ width: '100px', margin: 0, padding: '4px 8px', fontSize: '13px' }}
        />
        <span style={{ fontSize: '11px', color: c.textMuted }}>{t('poller.seconds')}</span>
        <button
          className="button button-primary button-sm"
          onClick={() => save()}
          disabled={busy || !dirty}
          style={{ marginLeft: 'auto' }}
        >
          {busy ? <Spinner /> : t('btn.save')}
        </button>
      </div>
    </div>
  );
}

// Midnight-scheduler status card lifted out of DashboardTab during
// the v3 UI refresh (Phase 1.1 stripped it from the Dashboard; this
// is its new home under ⚙ System → Scheduler). Polls the admin-only
// /api/scheduler/status endpoint every 60 s and exposes the
// /api/scheduler/run-now button for operator-triggered backfills.
function IdleTimeoutSettings() {
  const t = useT();
  const c = useThemeColors();
  const [value, setValue] = useState(() => {
    try {
      const v = localStorage.getItem(IDLE_TIMEOUT_KEY);
      return v != null ? parseInt(v, 10) : IDLE_DEFAULT_MINUTES;
    } catch (_) { return IDLE_DEFAULT_MINUTES; }
  });

  const handleChange = (e) => {
    const v = parseInt(e.target.value, 10);
    setValue(v);
    try { localStorage.setItem(IDLE_TIMEOUT_KEY, String(v)); } catch (_) {}
  };

  const options = [
    { val: 5,  label: t('idle.minutes', { n: 5 }) },
    { val: 10, label: t('idle.minutes', { n: 10 }) },
    { val: 15, label: t('idle.minutes', { n: 15 }) },
    { val: 30, label: t('idle.minutes', { n: 30 }) },
    { val: 60, label: t('idle.minutes', { n: 60 }) },
    { val: 0,  label: t('idle.never') },
  ];

  return (
    <div style={{
      marginTop: '16px',
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '20px',
    }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: c.textLabel, marginBottom: '8px' }}>
        🔒 {t('idle.setting_label')}
      </div>
      <div style={{ fontSize: '12px', color: c.textSecondary, marginBottom: '12px' }}>
        {value === 0
          ? 'Auto-logout is disabled. The session will stay open until the JWT expires (12 hours).'
          : `The session will auto-logout after ${value} minute(s) of inactivity.`}
      </div>
      <select
        className="input-field"
        value={value}
        onChange={handleChange}
        style={{ width: '200px' }}
      >
        {options.map(o => (
          <option key={o.val} value={o.val}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SchedulerSettings() {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [schedStatus, setSchedStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchSched = () => {
      api('/scheduler/status').then(d => mounted && setSchedStatus(d)).catch(() => {});
    };
    fetchSched();
    const t = setInterval(fetchSched, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // While a manual run is in flight, tick an elapsed-seconds counter
  // so the button label stays alive ("Running… 42s"). Long midnight
  // pulls can take 1-2 minutes and a static spinner feels frozen.
  useEffect(() => {
    if (!running) { setElapsed(0); return; }
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      toast.push(t('toast.scheduler_running'), 'info');
      const res = await api('/scheduler/run-now', { method: 'POST' });
      const parts = [
        `${res.meters_succeeded ?? res.meters_attempted} meter(s) ok`,
        `${res.meters_skipped ?? 0} skipped`,
      ];
      if ((res.dcus_attempted ?? 0) > 0) {
        parts.push(
          `${res.dcus_attempted} DCU → ${res.dcu_meter_rows_stored ?? 0} row(s)`
        );
      }
      parts.push(`${res.duration_seconds}s`);
      // Surface the background retry queue so the operator knows
      // whether any meters are still being chased down (missed their
      // yesterday row — 8 attempts × 20 min kick in automatically).
      const retryDirect = res.retry_direct_queued ?? 0;
      const retryDcu = res.retry_dcu_queued ?? 0;
      const retryTotal = retryDirect + retryDcu;
      if (retryTotal > 0) {
        const bits = [];
        if (retryDirect) bits.push(`${retryDirect} direct`);
        if (retryDcu) bits.push(`${retryDcu} DCU`);
        parts.push(`${retryTotal} queued for retry (${bits.join(' + ')})`);
      }
      toast.push(`Scheduler done: ${parts.join(' · ')}`, 'success');
      setSchedStatus(prev => ({ ...(prev || {}), last_summary: res }));
    } catch (err) {
      toast.push(err.message || 'Scheduler run failed', 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '20px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: c.textLabel }}>
          🌙 {t('scheduler.title')}
        </div>
        <button
          className="button button-secondary"
          onClick={runNow}
          disabled={running}
          style={{ padding: '6px 12px', fontSize: '12px' }}
        >
          {running ? <><Spinner /> Running… {elapsed}s</> : <>▶ {t('btn.run_midnight')}</>}
        </button>
      </div>
      <div style={{ fontSize: '12px', color: c.textSecondary, lineHeight: 1.6 }}>
        Pulls yesterday's daily-billing row, 96 load-profile rows, and
        96 power-quality rows from every keyed meter at midnight local
        time. The <strong>Run now</strong> button fires the collection
        immediately against meters that are currently online — useful
        as a backfill trigger after a service restart.
        <br />
        {schedStatus && (
          <>
            <strong>{t('scheduler.fires_at')}:</strong> {String(schedStatus.hour_of_day ?? 0).padStart(2, '0')}:00 {schedStatus.tz || 'local'}
            {' · '}
            <strong>{t('scheduler.last_run')}:</strong> {schedStatus.last_run_date || t('scheduler.never')}
            {schedStatus.last_summary && (
              <>
                {' · '}
                {schedStatus.last_summary.meters_succeeded ?? schedStatus.last_summary.meters_attempted} meter(s)
                in {schedStatus.last_summary.duration_seconds}s
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// System Settings container — a single admin-only top-level tab that
// gathers all the pages which used to live as their own sidebar tabs:
// Midnight scheduler, Live-poll interval, Keys, Users, COM Log.
// Internal sub-tabs match the MetersTab pattern (underlined accent).
// ─────────────────────────────────────────────────────────────
// 🛠 Admin Tools — high-level admin reporting & user activity.
// Houses the legacy Load Curve / Read Meter / Write Meter utilities
// plus a User Activity Log backed by /api/audit-log. Admin-only.
// ─────────────────────────────────────────────────────────────

const _ADMIN_SUB_TAB_STYLE = (active, c) => ({
  padding: '8px 16px',
  background: active ? c.bgElevated : 'transparent',
  color: active ? c.accent : c.textSecondary,
  border: 'none',
  borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
  fontSize: '14px',
  fontWeight: active ? 600 : 400,
  cursor: 'pointer',
  marginBottom: '-1px',
});

// ─────────────────────────────────────────────────────────────
// 🔍 Diagnostics — the DCU investigations as one-click playbooks.
//
// Backed by /api/admin/diagnose/*, which reads ONLY local sources (no
// device I/O), so this is safe to run mid-collection. Every verdict
// renders the evidence that produced it: over 2026-07-25/26 three
// confident diagnoses turned out to be wrong, and a conclusion without
// its numbers is what let that happen. Do not "tidy" the evidence table
// away — it is the point of the screen.
// ─────────────────────────────────────────────────────────────
const VERDICT_STYLE = {
  healthy:                    { icon: '✅', tone: 'ok'   },
  consistent:                 { icon: '✅', tone: 'ok'   },
  collection_overdue:         { icon: '⏰', tone: 'warn' },
  stale_pending_flags:        { icon: '🏷', tone: 'warn' },
  online_but_deaf:            { icon: '🔇', tone: 'warn' },
  dcu_offline:                { icon: '📴', tone: 'bad'  },
  // Replaces field_population_change, retracted 2026-07-27: that verdict read
  // the PLC blacklist (foreign meters overheard) as an audibility signal, so
  // zero overlap — the normal healthy case — looked like a fault. 'warn' not
  // 'bad' because the honest position is "cause not determined locally".
  registered_but_no_data:     { icon: '📭', tone: 'warn' },
  field_population_change:    { icon: '🔀', tone: 'bad'  },
  device_empty_but_map_populated: { icon: '⚠️', tone: 'bad' },
  no_meters:                  { icon: '␀',  tone: 'warn' },
  unknown_never_probed:       { icon: '❔', tone: 'muted' },
};

function DiagnosticsTab() {
  const c = useThemeColors();
  const [scope, setScope] = useState('dcu');
  const [id, setId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [diag, setDiag] = useState(null);
  const [cons, setCons] = useState(null);
  const [coll, setColl] = useState(null);

  const run = async () => {
    const target = id.trim();
    if (!target) { setErr('Enter a DCU serial, meter serial, or numeric id.'); return; }
    setBusy(true); setErr(null); setDiag(null); setCons(null); setColl(null);
    try {
      // Collection status applies to every scope; the DCU playbooks only to DCUs.
      const jobs = [api(`/admin/diagnose/collection?scope=${scope}&id=${encodeURIComponent(target)}`)
                      .then(setColl)];
      if (scope === 'dcu') {
        jobs.push(api(`/admin/diagnose/dcu/${encodeURIComponent(target)}`).then(setDiag));
        jobs.push(api(`/admin/diagnose/consistency/${encodeURIComponent(target)}`).then(setCons));
      }
      await Promise.all(jobs);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const toneColor = (tone) => tone === 'ok' ? c.success
    : tone === 'warn' ? '#f59e0b' : tone === 'bad' ? '#ef4444' : c.textLabel;

  const Verdict = ({ r }) => {
    if (!r) return null;
    const st = VERDICT_STYLE[r.verdict] || { icon: '•', tone: 'muted' };
    return (
      <div style={{
        background: c.bgInput, border: `1px solid ${c.border}`,
        borderLeft: `4px solid ${toneColor(st.tone)}`,
        borderRadius: 6, padding: '12px 16px', marginBottom: 12,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: toneColor(st.tone) }}>
          {st.icon} {String(r.verdict || '').replace(/_/g, ' ')}
          {r.confidence && (
            <span style={{ fontSize: 12, fontWeight: 400, color: c.textLabel, marginLeft: 8 }}>
              confidence: {r.confidence}
            </span>
          )}
        </div>
        {!!(r.evidence || []).length && (
          <table style={{ width: '100%', marginTop: 10, fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              {r.evidence.map((e, i) => (
                <tr key={`${e.label || i}-${i}`} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 8px 4px 0', color: c.textLabel }}>{e.label}</td>
                  <td style={{ padding: '4px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {typeof e.value === 'object' ? JSON.stringify(e.value) : String(e.value)}
                  </td>
                  <td style={{ padding: '4px 0', color: c.textLabel, fontSize: 11 }}>{e.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!!(r.recommended_actions || []).length && (
          <ul style={{ margin: '10px 0 0', paddingLeft: 20, fontSize: 13, color: c.textLabel }}>
            {r.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        )}
      </div>
    );
  };

  const flagEntries = Object.entries(coll?.flags || {});

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={scope} onChange={(e) => setScope(e.target.value)}
                style={{ padding: '8px 10px', background: c.bgInput, color: c.textPrimary,
                         border: `1px solid ${c.border}`, borderRadius: 4 }}>
          <option value="dcu">DCU</option>
          <option value="meter">Meter</option>
          <option value="customer">Customer (numeric id)</option>
          <option value="transformer">Transformer (numeric id)</option>
        </select>
        <input value={id} onChange={(e) => setId(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && run()}
               placeholder={scope === 'dcu' ? '129205032819' : scope === 'meter' ? 'meter serial' : 'numeric id'}
               style={{ padding: '8px 10px', minWidth: 220, background: c.bgInput,
                        color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: 4 }} />
        <button onClick={run} disabled={busy}
                style={{ padding: '8px 18px', background: c.accent, color: '#fff', border: 'none',
                         borderRadius: 4, cursor: busy ? 'wait' : 'pointer', fontWeight: 600 }}>
          {busy ? 'Running…' : 'Diagnose'}
        </button>
        <span style={{ fontSize: 12, color: c.textLabel }}>
          Read-only — no device I/O, safe during collection.
        </span>
      </div>

      {err && <div style={{ color: '#ef4444', marginBottom: 12 }}>⚠ {err}</div>}

      <Verdict r={diag} />
      <Verdict r={cons} />

      {coll && (
        <div style={{ background: c.bgInput, border: `1px solid ${c.border}`,
                      borderRadius: 6, padding: '12px 16px' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            📊 Collection status — {coll.label || 'scope'} ({coll.meters} meters)
          </div>
          {flagEntries.length === 0
            ? <div style={{ color: c.success }}>No flags raised.</div>
            : (
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                {flagEntries.map(([k, v]) => (
                  <li key={k} style={{ color: '#f59e0b' }}>
                    {k.replace(/_/g, ' ')}: <strong>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</strong>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}
    </div>
  );
}

// Sub-tab -> permission. A sub-tab shows only to users holding it; the tab
// itself shows when at least one does (ADMIN_TOOLS_PERMS in AuthenticatedApp).
const ADMIN_TOOLS_SUBTABS = [
  ['load_curve', 'dcu_diagnostics'],
  ['read',       'dcu_diagnostics'],
  ['write',      'write_meter_settings'],
  ['activity',   'activity_log'],
  ['apiusage',   'manage_users'],
  ['bulk',       'manage_records'],
  ['diag',       'dcu_diagnostics'],
];
const ADMIN_TOOLS_PERMS = [...new Set(ADMIN_TOOLS_SUBTABS.map(([, p]) => p))];

function AdminToolsTab() {
  const { user } = useAuth();
  const t = useT();
  const c = useThemeColors();
  const allowed = ADMIN_TOOLS_SUBTABS.filter(([, p]) => userCan(user, p)).map(([k]) => k);
  const [subState, setSub] = useState('load_curve');
  const sub = allowed.includes(subState) ? subState : allowed[0];
  const show = (k) => allowed.includes(k);

  if (!allowed.length) {
    return (
      <div className="tab-content">
        <div className="section-title">🛠 {t('section.admin_tools')}</div>
        <div style={{ color: c.textMuted }}>{t('msg.admin_required')}</div>
      </div>
    );
  }

  const admTabStyle = (active) => ({
    padding: '8px 16px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    fontSize: '14px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });

  return (
    <div className="tab-content">
      <div className="section-title">🛠 {t('section.admin_tools')}</div>

      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: `1px solid ${c.border}`,
        flexWrap: 'wrap',
      }}>
        {show('load_curve') && <button style={admTabStyle(sub === 'load_curve')} onClick={() => setSub('load_curve')}>📈 {t('adm.load_curve')}</button>}
        {show('read') && <button style={admTabStyle(sub === 'read')}       onClick={() => setSub('read')}      >📖 {t('adm.read_meter')}</button>}
        {show('write') && <button style={admTabStyle(sub === 'write')}      onClick={() => setSub('write')}     >✏️ {t('adm.write_meter')}</button>}
        {show('activity') && <button style={admTabStyle(sub === 'activity')}   onClick={() => setSub('activity')}  >📋 {t('adm.activity_log')}</button>}
        {show('apiusage') && <button style={admTabStyle(sub === 'apiusage')}   onClick={() => setSub('apiusage')}  >🔌 API Usage</button>}
        {show('bulk') && <button style={admTabStyle(sub === 'bulk')}       onClick={() => setSub('bulk')}      >📦 Bulk import/export</button>}
        {show('diag') && <button style={admTabStyle(sub === 'diag')}       onClick={() => setSub('diag')}      >🔍 Diagnostics</button>}
      </div>

      {sub === 'load_curve' && <LoadCurveTab />}
      {sub === 'read'       && <ReadMeterTab />}
      {sub === 'write'      && <WriteMeterTab />}
      {sub === 'activity'   && <UserActivityLog />}
      {sub === 'apiusage'   && <ApiUsageTab />}
      {sub === 'bulk'       && <BulkImportExportPanel />}
      {sub === 'diag'       && <DiagnosticsTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// API Usage — Admin Tools sub-tab. Per-API-key call counts +
// drill-down on top endpoints + top meters. Backed by a single
// /api/api-keys/usage call which aggregates audit_log rows
// grouped by api_key_id.
// ─────────────────────────────────────────────────────────────
function ApiUsageTab() {
  const c = useThemeColors();
  const toast = useToast();
  const [data, setData] = React.useState(null);
  const [windowDays, setWindowDays] = React.useState(7);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(null); // key id

  const load = React.useCallback(() => {
    setLoading(true);
    api(`/api-keys/usage?days=${windowDays}`)
      .then(setData)
      .catch(err => toast.push(err.message, 'error'))
      .finally(() => setLoading(false));
  }, [windowDays]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Spinner />;
  if (!data) return <div style={{ color: c.textMuted, padding: '20px' }}>No data.</div>;

  const Sparkline = ({ days }) => {
    if (!days || days.length === 0) return <span style={{ color: c.textDimmed }}>—</span>;
    const max = Math.max(...days.map(d => d.count), 1);
    const W = 120, H = 28;
    const dx = days.length > 1 ? W / (days.length - 1) : 0;
    const points = days.map((d, i) => {
      const x = i * dx;
      const y = H - (d.count / max) * (H - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return (
      <svg width={W} height={H} style={{ verticalAlign: 'middle' }}>
        <polyline points={points} fill="none" stroke={c.accent} strokeWidth="1.5" />
        {days.map((d, i) => (
          <circle key={i} cx={i * dx} cy={H - (d.count / max) * (H - 2) - 1} r="1.6" fill={c.accent}>
            <title>{d.day}: {d.count}</title>
          </circle>
        ))}
      </svg>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', color: c.textMuted }}>
          Window:
        </div>
        {[1, 7, 30, 90].map(d => (
          <button key={d}
            onClick={() => setWindowDays(d)}
            style={{
              padding: '5px 12px', fontSize: '12px',
              border: `1px solid ${windowDays === d ? c.accent : c.border}`,
              background: windowDays === d ? `${c.accent}22` : 'transparent',
              color: windowDays === d ? c.accent : c.textMuted,
              borderRadius: '4px', cursor: 'pointer',
              fontWeight: windowDays === d ? 600 : 400,
            }}>
            Last {d}d
          </button>
        ))}
        <button className="button button-sm button-secondary" onClick={load} style={{ marginLeft: 'auto' }}>🔄 Refresh</button>
      </div>

      {/* Fleet summary */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '10px', marginBottom: '14px',
      }}>
        <KpiTile label={`API calls — last ${windowDays}d`} value={(data.fleet_window_calls || 0).toLocaleString()} color="#00e5ff" />
        <KpiTile label="API calls — lifetime" value={(data.fleet_lifetime_calls || 0).toLocaleString()} color="#22c55e" />
        <KpiTile label="Active keys" value={(data.keys || []).filter(k => !k.revoked_at).length} color={c.accent} />
        <KpiTile label="Total keys" value={(data.keys || []).length} color={c.textMuted} />
      </div>

      <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: '12px' }}>
          <thead>
            <tr style={{ color: c.textSecondary, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
              <th style={{ padding: '10px 12px' }}>Key</th>
              <th style={{ padding: '10px 12px' }}>Bound user</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>24h</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>7d</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>{windowDays}d</th>
              <th style={{ padding: '10px 12px', textAlign: 'right' }}>Lifetime</th>
              <th style={{ padding: '10px 12px' }}>Trend ({windowDays}d)</th>
              <th style={{ padding: '10px 12px' }}>Last call</th>
              <th style={{ padding: '10px 12px' }}>State</th>
            </tr>
          </thead>
          <tbody>
            {(data.keys || []).length === 0 && (
              <tr><td colSpan="9" style={{ padding: '24px', textAlign: 'center', color: c.textMuted, fontStyle: 'italic' }}>
                No API keys issued yet.
              </td></tr>
            )}
            {(data.keys || []).map(k => {
              const isOpen = expanded === k.id;
              return (
                <React.Fragment key={k.id}>
                  <tr style={{ borderBottom: `1px solid ${c.border}`, opacity: k.revoked_at ? 0.55 : 1, cursor: 'pointer' }}
                      onClick={() => setExpanded(isOpen ? null : k.id)}>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ marginRight: '6px', color: c.textDimmed }}>{isOpen ? '▼' : '▶'}</span>
                      <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{k.prefix}…</span>
                      {k.label && <span style={{ color: c.textMuted, marginLeft: '8px' }}>{k.label}</span>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {k.username ? <>
                        <span style={{ fontFamily: 'monospace' }}>{k.username}</span>
                        <span style={{ color: c.textMuted, marginLeft: '6px', fontSize: '11px' }}>({k.user_role})</span>
                      </> : <span style={{ color: c.danger }}>missing</span>}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{k.calls_24h.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{k.calls_7d.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{k.calls_window.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', color: c.textMuted }}>{k.lifetime_calls.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px' }}><Sparkline days={k.per_day} /></td>
                    <td style={{ padding: '8px 12px', color: c.textMuted, fontSize: '11px' }}>
                      {fmtDT(k.last_call_at, { utc: true }) || <span style={{ color: c.textDimmed }}>never</span>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {k.revoked_at
                        ? <span style={{ color: c.danger, fontSize: '11px' }}>● Revoked</span>
                        : <span style={{ color: c.success, fontSize: '11px' }}>● Active</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr><td colSpan="9" style={{ padding: '0' }}>
                      <div style={{
                        background: c.bgCardAlt, padding: '14px 20px',
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px',
                      }}>
                        <div>
                          <div style={{ fontSize: '11px', color: c.textSecondary, marginBottom: '6px', fontWeight: 600 }}>
                            Top endpoints (last {windowDays}d)
                          </div>
                          {(k.top_endpoints || []).length === 0
                            ? <div style={{ color: c.textDimmed, fontSize: '11px' }}>No calls in window</div>
                            : <table style={{ width: '100%', fontSize: '11px' }}>
                                <tbody>
                                  {k.top_endpoints.map((e, i) => (
                                    <tr key={i}>
                                      <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: c.textPrimary }}>{e.path}</td>
                                      <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', color: c.textMuted }}>{e.count.toLocaleString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                          }
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: c.textSecondary, marginBottom: '6px', fontWeight: 600 }}>
                            Top meters (last {windowDays}d)
                          </div>
                          {(k.top_meters || []).length === 0
                            ? <div style={{ color: c.textDimmed, fontSize: '11px' }}>No meter-scoped calls</div>
                            : <table style={{ width: '100%', fontSize: '11px' }}>
                                <tbody>
                                  {k.top_meters.map((m, i) => (
                                    <tr key={i}>
                                      <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: c.textPrimary }}>{m.serial}</td>
                                      <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', color: c.textMuted }}>{m.count.toLocaleString()}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                          }
                        </div>
                        <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: c.textMuted }}>
                          First call: <code style={{ color: c.textPrimary }}>{fmtDT(k.first_call_at, { utc: true, seconds: true }) || 'never'}</code>
                          <span style={{ margin: '0 12px' }}>·</span>
                          Created: <code style={{ color: c.textPrimary }}>{fmtDT(k.created_at, { utc: true }) || '—'}</code>
                          {k.revoked_at && <>
                            <span style={{ margin: '0 12px' }}>·</span>
                            Revoked: <code style={{ color: c.danger }}>{fmtDT(k.revoked_at, { utc: true })}</code>
                          </>}
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Bulk assignment import/export — admin-only utility for setting
// object_name (and other per-meter curated fields) on 300+ meters
// at once from a spreadsheet, keyed by meter_serial. Round-trip:
// export → edit in Excel → import.
function BulkImportExportPanel() {
  const toast = useToast();
  const c = useThemeColors();
  const { assignments, refreshAssignments } = useAssignments();
  const [busy, setBusy] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [xlsxB64, setXlsxB64] = useState(null);   // base64 of an uploaded .xlsx
  const [fileLabel, setFileLabel] = useState(''); // chosen file name (display)
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);

  // V0.5.x scope picker. Mode = 'all' | 'customer' | 'transformer'
  // | 'dcu'. The corresponding id/serial is sent as a query param so
  // the server filters via `_build_bulk_export_query`. Default 'all'
  // preserves the legacy "download everything" behavior — operators
  // who don't pick a scope get the same file they used to.
  const [scopeMode, setScopeMode] = useState('all');
  const [scopeCustomerId, setScopeCustomerId] = useState('');
  const [scopeTransformerId, setScopeTransformerId] = useState('');
  const [scopeDcuSerial, setScopeDcuSerial] = useState('');
  const [customers, setCustomers] = useState([]);
  const [transformers, setTransformers] = useState([]);
  const dcuSerials = React.useMemo(() => Array.from(new Set(
    assignments.map(a => a.dcu_serial).filter(s => s && s.length)
  )).sort(), [assignments]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api('/customers').then(d => d.customers || []).catch(() => []),
      api('/transformers').then(d => d.transformers || []).catch(() => []),
      refreshAssignments().catch(() => assignments),
    ]).then(([cs, ts, as_]) => {
      if (!mounted) return;
      setCustomers(cs);
      setTransformers(ts);
    });
    return () => { mounted = false; };
  }, [refreshAssignments]);

  // Estimated row count for the operator's current scope. Pure
  // client-side count using the cached assignments list — no extra
  // round trip. Server is the source of truth on download.
  const previewCount = (() => {
    if (scopeMode === 'all') return assignments.length;
    if (scopeMode === 'customer' && scopeCustomerId) {
      const txIds = new Set(
        transformers.filter(t => t.customer_id === Number(scopeCustomerId)).map(t => t.id),
      );
      return assignments.filter(a => txIds.has(a.transformer_id)).length;
    }
    if (scopeMode === 'transformer' && scopeTransformerId) {
      return assignments.filter(a => a.transformer_id === Number(scopeTransformerId)).length;
    }
    if (scopeMode === 'dcu' && scopeDcuSerial) {
      return assignments.filter(a => a.dcu_serial === scopeDcuSerial).length;
    }
    return 0;
  })();

  // Build the export query string from the scope state. Empty
  // string = no filter, server returns everything visible.
  const buildScopeQuery = () => {
    if (scopeMode === 'customer' && scopeCustomerId) {
      return `?customer_id=${encodeURIComponent(scopeCustomerId)}`;
    }
    if (scopeMode === 'transformer' && scopeTransformerId) {
      return `?transformer_id=${encodeURIComponent(scopeTransformerId)}`;
    }
    if (scopeMode === 'dcu' && scopeDcuSerial) {
      return `?dcu_serial=${encodeURIComponent(scopeDcuSerial)}`;
    }
    return '';
  };

  // Build a scope-aware filename so downloads don't collide when the
  // operator round-trips multiple scopes from the same session.
  const buildScopedFilename = (ext) => {
    const date = new Date().toISOString().slice(0, 10);
    let suffix = '';
    if (scopeMode === 'customer' && scopeCustomerId) {
      const cu = customers.find(c => c.id === Number(scopeCustomerId));
      suffix = `_${(cu?.name || `c${scopeCustomerId}`).replace(/\s+/g, '-')}`;
    } else if (scopeMode === 'transformer' && scopeTransformerId) {
      const tx = transformers.find(t => t.id === Number(scopeTransformerId));
      suffix = `_${(tx?.name || `t${scopeTransformerId}`).replace(/\s+/g, '-')}`;
    } else if (scopeMode === 'dcu' && scopeDcuSerial) {
      suffix = `_dcu${scopeDcuSerial}`;
    }
    return `emeter_assignments${suffix}_${date}.${ext}`;
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      await downloadXlsx(
        `/api/assignments/bulk-export.csv${buildScopeQuery()}`,
        buildScopedFilename('csv'),
        { toast },
      );
    } catch (_) { /* toast already shown */
    } finally { setBusy(false); }
  };

  const exportXlsx = async () => {
    setBusy(true);
    try {
      await downloadXlsx(
        `/api/assignments/bulk-export.xlsx${buildScopeQuery()}`,
        buildScopedFilename('xlsx'),
        { toast },
      );
    } catch (_) { /* toast already shown */
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!xlsxB64 && !csvText.trim()) {
      toast.push('Paste CSV, or choose a CSV/XLSX file below', 'error');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      // xlsx_b64 wins when an .xlsx was uploaded; otherwise send CSV text.
      const body = xlsxB64
        ? { xlsx_b64: xlsxB64, dry_run: dryRun }
        : { csv_text: csvText, dry_run: dryRun };
      const r = await api('/assignments/bulk-import', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setResult(r);
      if (!r.dry_run) refreshAssignments({ force: true }).catch(() => {});
      const s = r.summary || {};
      const tone = s.errors ? 'warning' : 'success';
      const prefix = r.dry_run ? 'Dry-run' : 'Import';
      toast.push(
        `${prefix}: ${s.updated} updated · ${s.not_found} not found · ${s.errors} errors · ${s.skipped_empty} skipped`,
        tone,
      );
    } catch (err) {
      toast.push(`Bulk import failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isXlsx = /\.xlsx$/i.test(f.name) ||
      (f.type || '').includes('spreadsheetml');
    const r = new FileReader();
    if (isXlsx) {
      // Binary workbook — send as base64, don't dump bytes into the
      // textarea (that produced the "PK..." garbage). The backend parses
      // it with openpyxl.
      r.onload = () => {
        const res = String(r.result || '');
        const b64 = res.includes(',') ? res.split(',')[1] : res;
        setXlsxB64(b64);
        setCsvText('');
        setFileLabel(f.name);
      };
      r.readAsDataURL(f);
    } else {
      r.onload = () => {
        setCsvText(String(r.result || ''));
        setXlsxB64(null);
        setFileLabel(f.name);
      };
      r.readAsText(f);
    }
  };

  return (
    <div>
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '14px', marginBottom: '12px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '6px' }}>
          📥 Export current assignments
        </div>
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '10px' }}>
          CSV / XLSX of meter assignments (meter_serial, object_name, CT/VT, transformer, customer name, etc.).
          Edit in Excel and re-upload below. The <code>meter_serial</code> column is the lookup key.
          Pick a scope below to download just a subset — keeps round-trips small when you only need to update one
          customer or DCU.
        </div>

        {/* V0.5.x scope picker. Defaults to "All" so operators who
            don't engage with the picker get the same file they got
            before. Each radio toggles which dropdown is active. */}
        <div style={{
          background: c.bgCardAlt, border: `1px solid ${c.border}`,
          borderRadius: '6px', padding: '10px 12px', marginBottom: '10px',
          fontSize: '12px',
        }}>
          <div style={{ fontWeight: 600, color: c.textLabel, marginBottom: '6px' }}>
            Scope
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
            {[
              ['all', 'All assignments'],
              ['customer', 'By customer'],
              ['transformer', 'By transformer'],
              ['dcu', 'By DCU'],
            ].map(([val, label]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={scopeMode === val}
                  onChange={() => setScopeMode(val)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          {scopeMode === 'customer' && (
            <select
              value={scopeCustomerId}
              onChange={(e) => setScopeCustomerId(e.target.value)}
              className="input-field"
              style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
            >
              <option value="">— select a customer —</option>
              {customers.map(cu => (
                <option key={cu.id} value={cu.id}>
                  {cu.name}{cu.customer_no ? ` (${cu.customer_no})` : ''}
                </option>
              ))}
            </select>
          )}
          {scopeMode === 'transformer' && (
            <select
              value={scopeTransformerId}
              onChange={(e) => setScopeTransformerId(e.target.value)}
              className="input-field"
              style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
            >
              <option value="">— select a transformer —</option>
              {transformers.map(t => {
                const cu = customers.find(c => c.id === t.customer_id);
                return (
                  <option key={t.id} value={t.id}>
                    {cu?.name ? `${cu.name} · ` : ''}{t.name}
                  </option>
                );
              })}
            </select>
          )}
          {scopeMode === 'dcu' && (
            <select
              value={scopeDcuSerial}
              onChange={(e) => setScopeDcuSerial(e.target.value)}
              className="input-field"
              style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
            >
              <option value="">— select a DCU —</option>
              {dcuSerials.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

          <div style={{ marginTop: '8px', color: c.textMuted, fontSize: '11px' }}>
            Will export <strong>{previewCount}</strong> assignment
            {previewCount === 1 ? '' : 's'} (estimated client-side; server is the source of truth).
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="button button-secondary" onClick={exportCsv} disabled={busy}>
            {busy ? <Spinner /> : '⬇'} Download CSV
          </button>
          <button className="button button-secondary" onClick={exportXlsx} disabled={busy}>
            {busy ? <Spinner /> : '⬇'} Download XLSX
          </button>
        </div>
      </div>

      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '14px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '6px' }}>
          📤 Import object names (and other curated fields)
        </div>
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '10px' }}>
          Upload an edited <strong>CSV or XLSX</strong> (or paste CSV below). Only meters that are
          <strong> already assigned</strong> will be updated (direct meters + DCU-registered meters).
          Empty cells are left untouched — partial files don't overwrite existing data.
          <br />
          Writable columns: <code>object_name, account_number, role, connection_type, ct_ratio,
          vt_ratio, meter_type_override, install_description</code>.
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
          <input type="file"
                 accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                 onChange={onFileChosen}
                 style={{ fontSize: '12px', color: c.textSecondary }} />
          {xlsxB64 && (
            <span style={{ fontSize: '11px', color: c.success, fontWeight: 600 }}>
              📊 {fileLabel} (XLSX) loaded
            </span>
          )}
          <label style={{ fontSize: '12px', color: c.textLabel, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run (validate only, no writes)
          </label>
        </div>

        <textarea
          value={csvText}
          onChange={(e) => { setCsvText(e.target.value); setXlsxB64(null); }}
          placeholder="meter_serial,object_name,ct_ratio,vt_ratio&#10;121207186453,Main entrance,100/5,&#10;..."
          style={{
            width: '100%', minHeight: '140px', fontFamily: 'monospace', fontSize: '11px',
            padding: '8px', background: c.bgInput, color: c.textPrimary,
            border: `1px solid ${c.borderInput}`, borderRadius: '4px',
            marginBottom: '10px',
          }}
        />

        <button className="button button-primary" onClick={runImport}
                disabled={busy || (!csvText.trim() && !xlsxB64)}>
          {busy ? <><Spinner /> {dryRun ? 'Validating…' : 'Importing…'}</>
                : (dryRun ? '🧪 Dry run' : '✓ Apply changes')}
        </button>

        {result && (
          <div style={{
            marginTop: '14px', padding: '10px 12px',
            background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: '6px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
              {result.dry_run ? 'Dry-run results' : 'Import complete'}
            </div>
            <div style={{ fontSize: '12px', color: c.textSecondary, marginBottom: '8px' }}>
              {result.summary.updated} updated · {result.summary.not_found} not found ·
              {' '}{result.summary.errors} errors · {result.summary.skipped_empty} empty rows skipped
            </div>
            {result.not_found?.length > 0 && (
              <details style={{ fontSize: '11px', marginTop: '4px' }}>
                <summary style={{ cursor: 'pointer', color: c.warning }}>
                  {result.not_found.length} serial(s) not in assignment table — click to view
                </summary>
                <ul style={{ margin: '4px 0 0 16px', fontFamily: 'monospace', fontSize: '11px' }}>
                  {result.not_found.slice(0, 50).map((r, i) => (
                    <li key={i}>line {r.line}: {r.meter_serial}</li>
                  ))}
                  {result.not_found.length > 50 && <li>…{result.not_found.length - 50} more</li>}
                </ul>
              </details>
            )}
            {result.errors?.length > 0 && (
              <details style={{ fontSize: '11px', marginTop: '4px' }} open>
                <summary style={{ cursor: 'pointer', color: c.danger }}>
                  {result.errors.length} error(s) — click to view
                </summary>
                <ul style={{ margin: '4px 0 0 16px', fontFamily: 'monospace', fontSize: '11px', color: c.danger }}>
                  {result.errors.slice(0, 50).map((r, i) => (
                    <li key={i}>line {r.line}: {r.meter_serial} — {r.error}</li>
                  ))}
                  {result.errors.length > 50 && <li>…{result.errors.length - 50} more</li>}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// User Activity Log panel — read-only view over /api/audit-log.
// Filters can be stacked: username, customer, action, date range.
// The backend returns the distinct action/user lists so the dropdowns
// populate themselves on first load.
function UserActivityLog() {
  const [items, setItems] = useState([]);
  const c = useThemeColors();
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [filterUser, setFilterUser] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterMeter, setFilterMeter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(200);

  // Customers list is needed so an admin can filter by customer name
  // instead of memorising IDs. Loaded once on mount.
  useEffect(() => {
    (async () => {
      try {
        const data = await api('/customers');
        setCustomers(Array.isArray(data) ? data : []);
      } catch (_) {}
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams();
      if (filterUser)     params.set('user_id', filterUser);
      if (filterCustomer) params.set('customer_id', filterCustomer);
      if (filterAction)   params.set('action', filterAction);
      if (filterMeter)    params.set('meter_serial', filterMeter);
      if (fromDate) {
        params.set('from_ts', String(dayStart(fromDate)));
      }
      if (toDate) {
        params.set('to_ts', String(dayEnd(toDate)));
      }
      params.set('limit', String(limit));
      const data = await api('/audit-log?' + params.toString());
      setItems(data.items || []);
      setActions(data.actions || []);
      setUsers(data.users || []);
    } catch (e) {
      setErr(e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterCustomer, filterAction, filterMeter, fromDate, toDate, limit]);

  useEffect(() => { load(); }, []);

  const exportCsv = () => {
    if (!items.length) return;
    const header = ['Time', 'User', 'Role', 'Action', 'Customer', 'Meter', 'Target', 'Details'];
    const rows = items.map(it => [
      it.ts || '',
      it.username || '',
      '',
      it.action || '',
      it.customer_name || (it.customer_id != null ? `#${it.customer_id}` : ''),
      it.meter_serial || '',
      it.target || '',
      it.details || '',
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','))
      .join('\n');
    downloadCsv(csv, `user_activity_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const inputStyle = {
    padding: '6px 10px',
    background: c.bgBase,
    border: `1px solid ${c.border}`,
    borderRadius: '6px',
    color: c.textPrimary,
    fontSize: '13px',
  };

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '8px',
        marginBottom: '12px',
        padding: '12px',
        background: c.bgCardAlt,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
      }}>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>USER</div>
          <select style={inputStyle} value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
            <option value=''>All users</option>
            {users.map(u => (
              <option key={u.user_id ?? 'x'} value={u.user_id ?? ''}>{u.username || '—'}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>CUSTOMER</div>
          <select style={inputStyle} value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)}>
            <option value=''>All customers</option>
            {customers.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>ACTION</div>
          <select style={inputStyle} value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
            <option value=''>All actions</option>
            {actions.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>METER SERIAL</div>
          <input style={inputStyle} value={filterMeter} onChange={(e) => setFilterMeter(e.target.value)} placeholder="serial..." />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>FROM</div>
          <input type='date' style={inputStyle} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>TO</div>
          <input type='date' style={inputStyle} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>QUICK RANGE</div>
          <QuickRangeButtons setFromDate={setFromDate} setToDate={setToDate} compact />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>LIMIT</div>
          <select style={inputStyle} value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}>
            {[100, 200, 500, 1000, 2000].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
          <button
            className="btn btn-primary"
            style={{ fontSize: '13px', padding: '6px 14px' }}
            onClick={load}
            disabled={loading}
          >
            {loading ? 'Loading…' : '🔍 Search'}
          </button>
          <button
            className="btn btn-secondary"
            style={{ fontSize: '13px', padding: '6px 14px' }}
            onClick={exportCsv}
            disabled={!items.length}
          >
            ⬇ CSV
          </button>
        </div>
      </div>

      {err && (
        <div style={{ color: '#fca5a5', marginBottom: '8px', fontSize: '13px' }}>{err}</div>
      )}

      <div style={{
        background: c.bgCardAlt,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        overflow: 'auto',
        maxHeight: '65vh',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead style={{
            position: 'sticky',
            top: 0,
            background: '#1e293b',
            color: c.textPrimary,
          }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Time</th>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>User</th>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Action</th>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Meter</th>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Target</th>
              <th style={{ padding: '8px 10px', textAlign: 'left' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: c.textMuted }}>
                  No activity matches the current filters.
                </td>
              </tr>
            )}
            {items.map((it) => {
              const when = it.ts ? fmtDT(it.ts, { seconds: true }) : '—';
              return (
                <tr key={it.id} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '6px 10px', color: c.textSecondary, whiteSpace: 'nowrap' }}>{when}</td>
                  <td style={{ padding: '6px 10px', color: c.textPrimary }}>{it.username || '—'}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: '#1e293b',
                      color: c.accent,
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>{it.action}</span>
                  </td>
                  <td style={{ padding: '6px 10px', color: c.textPrimary }}>
                    {it.customer_name || (it.customer_id != null ? `#${it.customer_id}` : '—')}
                  </td>
                  <td style={{ padding: '6px 10px', color: c.textPrimary, fontFamily: 'monospace' }}>
                    {it.meter_serial || '—'}
                  </td>
                  <td style={{ padding: '6px 10px', color: c.textSecondary }}>{it.target || '—'}</td>
                  <td style={{ padding: '6px 10px', color: c.textMuted }}>{it.details || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '8px', fontSize: '12px', color: c.textMuted }}>
        Showing {items.length} entries{items.length >= limit ? ' (limit reached — narrow the filter or raise the limit)' : ''}.
      </div>
    </div>
  );
}


// Users + API keys sit under "Manage users"; everything else here is
// "System settings".
const SYSTEM_SUBTABS = [
  ['scheduler', 'system_settings'], ['poller', 'system_settings'],
  ['keys', 'system_settings'], ['users', 'manage_users'],
  ['apikeys', 'manage_users'], ['security', 'system_settings'],
  ['health', 'system_settings'], ['serverhealth', 'system_settings'],
  ['comlog', 'system_settings'], ['server', 'system_settings'],
];

function SystemSettingsTab() {
  const { user } = useAuth();
  const t = useT();
  const c = useThemeColors();
  const allowed = SYSTEM_SUBTABS.filter(([, p]) => userCan(user, p)).map(([k]) => k);
  const [subState, setSub] = useState('scheduler');
  const sub = allowed.includes(subState) ? subState : allowed[0];
  const show = (k) => allowed.includes(k);

  if (!allowed.length) {
    return (
      <div className="tab-content">
        <div className="section-title">⚙ {t('tab.system')}</div>
        <div style={{ color: c.textMuted }}>{t('msg.admin_required')}</div>
      </div>
    );
  }

  const tabBtnStyle = (active) => ({
    padding: '8px 16px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    fontSize: '14px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });

  return (
    <div className="tab-content">
      <div className="section-title">⚙ {t('tab.system')}</div>

      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: `1px solid ${c.border}`,
        flexWrap: 'wrap',
      }}>
        {show('scheduler') && <button style={tabBtnStyle(sub === 'scheduler')} onClick={() => setSub('scheduler')}>🌙 {t('sys.scheduler')}</button>}
        {show('poller') && <button style={tabBtnStyle(sub === 'poller')}    onClick={() => setSub('poller')}   >⚙ {t('sys.poller')}</button>}
        {show('keys') && <button style={tabBtnStyle(sub === 'keys')}      onClick={() => setSub('keys')}     >🔑 {t('sys.keys')}</button>}
        {show('users') && <button style={tabBtnStyle(sub === 'users')}     onClick={() => setSub('users')}    >👥 {t('sys.users')}</button>}
        {show('apikeys') && <button style={tabBtnStyle(sub === 'apikeys')}   onClick={() => setSub('apikeys')}  >🔌 API Keys</button>}
        {show('security') && <button style={tabBtnStyle(sub === 'security')}  onClick={() => setSub('security')} >🔐 {t('sys.security')}</button>}
        {show('health') && <button style={tabBtnStyle(sub === 'health')}    onClick={() => setSub('health')}   >📶 {t('sys.health')}</button>}
        {show('serverhealth') && <button style={tabBtnStyle(sub === 'serverhealth')} onClick={() => setSub('serverhealth')}>📊 {t('sys.server_health')}</button>}
        {show('comlog') && <button style={tabBtnStyle(sub === 'comlog')}    onClick={() => setSub('comlog')}   >📡 {t('sys.com_log')}</button>}
        {show('server') && <button style={tabBtnStyle(sub === 'server')}    onClick={() => setSub('server')}   >🔄 Server Control</button>}
      </div>

      {sub === 'scheduler' && <><SchedulerSettings /><DirectCollectionPanel /></>}
      {sub === 'poller'    && <>
        <PollerIntervalSettings />
        <IdleTimeoutSettings />
      </>}
      {sub === 'keys'      && <KeysTab />}
      {sub === 'users'     && <UsersTab />}
      {sub === 'apikeys'   && <ApiKeysAdminTab />}
      {sub === 'security'  && <SecuritySettings />}
      {sub === 'health'    && <FleetHealthSettings />}
      {sub === 'serverhealth' && <ServerHealthCard />}
      {sub === 'comlog'    && <COMLogTab />}
      {sub === 'server'    && <ServerControlSection />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ServerControlSection — System → Server Control sub-tab (admin).
// Full-service restart with a typed confirmation + a cooldown guard
// (rapid restarts can lock up Holley DCU firmware). The service runs
// under systemd Restart=always, so the backend restarts by exiting;
// this UI just triggers it and waits for /healthz to come back.
// ─────────────────────────────────────────────────────────────
function ServerControlSection() {
  const c = useThemeColors();
  const toast = useToast();
  const [info, setInfo] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const load = useCallback(() => {
    api('/system/restart-info').then(setInfo).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const fmt = (s) => {
    if (s == null) return '—';
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    return d ? `${d}d ${h}h ${m}m` : h ? `${h}h ${m}m` : `${m}m ${Math.floor(s % 60)}s`;
  };

  const pollBack = useCallback(() => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries++;
      try {
        const r = await fetch('/healthz', { cache: 'no-store' });
        if (r.ok) { clearInterval(iv); setRestarting(false); toast.push('Server is back online', 'success'); load(); return; }
      } catch (_) { /* still down */ }
      if (tries > 45) { clearInterval(iv); setRestarting(false); toast.push('Still waiting on the server — check the service', 'error'); }
    }, 2000);
  }, [toast, load]);

  const doRestart = async () => {
    if (typed.trim().toUpperCase() !== 'RESTART') return;
    setBusy(true);
    try {
      await api('/system/restart', { method: 'POST', body: JSON.stringify({ confirm: 'RESTART', reason: 'admin UI' }) });
      setConfirming(false); setTyped(''); setRestarting(true);
      toast.push('Restart triggered — reconnecting…', 'info');
      setTimeout(pollBack, 4000);   // give it a moment to go down first
    } catch (e) {
      toast.push(`Restart failed: ${e.message || e}`, 'error');
    } finally { setBusy(false); }
  };

  const allowed = info?.restart_allowed;
  const cd = info?.cooldown_remaining_seconds || 0;
  const box = { background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '18px 20px', maxWidth: '620px' };

  return (
    <div style={box}>
      <div className="section-title" style={{ marginTop: 0 }}>🔄 Full server restart</div>
      <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '14px', lineHeight: 1.5 }}>
        Restarts the entire eMeter service. Every live meter/DCU TCP session is dropped and re-established
        (meters redial within ~1–2 min). Midnight history collection and any in-flight reads for that window are lost.
        <strong> Use sparingly</strong> — repeated restarts can lock up Holley DCU firmware.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '13px', marginBottom: '16px' }}>
        <span style={{ color: c.textMuted }}>Uptime:</span>
        <span style={{ fontFamily: 'monospace' }}>{fmt(info?.uptime_seconds)}</span>
        <span style={{ color: c.textMuted }}>Started:</span>
        <span style={{ fontFamily: 'monospace' }}>{info?.started_at ? fmtDT(info.started_at, { utc: true, seconds: true }) : '—'}</span>
      </div>

      {restarting ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: c.accent, fontSize: '14px' }}>
          <span className="spinner" /> Server is restarting — waiting for it to come back…
        </div>
      ) : !confirming ? (
        <div>
          <button
            disabled={!allowed}
            onClick={() => setConfirming(true)}
            title={allowed ? 'Restart the whole service' : `On cooldown — available in ${fmt(cd)}`}
            style={{ padding: '9px 18px', fontSize: '14px', fontWeight: 600, borderRadius: '6px', border: 'none',
              background: allowed ? '#dc2626' : c.border, color: '#fff', cursor: allowed ? 'pointer' : 'not-allowed' }}>
            🔄 Restart Server
          </button>
          {!allowed && info && (
            <span style={{ marginLeft: '12px', fontSize: '12px', color: '#f59e0b' }}>
              Cooldown: available in {fmt(cd)} (guards against rapid restarts)
            </span>
          )}
        </div>
      ) : (
        <div style={{ border: `1px solid #dc2626`, borderRadius: '6px', padding: '14px', background: '#dc262610' }}>
          <div style={{ fontSize: '13px', color: c.textPrimary, marginBottom: '10px' }}>
            This will drop every meter/DCU session. Type <strong>RESTART</strong> to confirm:
          </div>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="RESTART" autoFocus
            style={{ padding: '7px 10px', fontSize: '14px', width: '160px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', fontFamily: 'monospace' }} />
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button disabled={busy || typed.trim().toUpperCase() !== 'RESTART'} onClick={doRestart}
              style={{ padding: '8px 16px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: 'none',
                background: (busy || typed.trim().toUpperCase() !== 'RESTART') ? c.border : '#dc2626', color: '#fff',
                cursor: (busy || typed.trim().toUpperCase() !== 'RESTART') ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Restarting…' : 'Confirm restart'}
            </button>
            <button disabled={busy} onClick={() => { setConfirming(false); setTyped(''); }}
              style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '6px', border: `1px solid ${c.border}`, background: 'transparent', color: c.textPrimary, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// SecuritySettings — System → Security sub-tab.
// Admin-only (parent gates). Lives here rather than beside the
// Relay/Network tabs so a high-blast-radius kill-switch flip is
// an intentional destination, not a one-hand-on-keyboard reflex.
// ─────────────────────────────────────────────────────────────
function SecuritySettings() {
  const c = useThemeColors();
  const toast = useToast();
  const confirm = useConfirm();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setState(await api('/admin/remote-writes-mode'));
    } catch (err) {
      toast.push(`Load failed: ${err.message}`, 'error');
    }
  };
  useEffect(() => { load(); }, []);

  const flip = async (target) => {
    if (!state || target === state.mode) return;
    if (target === 'enabled') {
      if (!(await confirm(
        'Enable live remote writes',
        'Switching to LIVE mode allows relay disconnects, remote IP changes, and APN writes to actually reach meters and DCUs.\n\n' +
        'A wrong IP or a misclick can permanently disconnect a meter (requires optical-port truck roll) or cut power to a consumer.\n\n' +
        'Continue?',
        { confirmLabel: 'Enable', confirmColor: '#16a34a' },
      ))) return;
    }
    setBusy(true);
    try {
      const r = await api('/admin/remote-writes-mode', {
        method: 'PUT',
        body: JSON.stringify({ mode: target, confirm: target === 'enabled' }),
      });
      toast.push(`Remote writes: ${r.previous} → ${r.mode}`, 'success');
      await load();
    } catch (err) {
      toast.push(`Flip failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div style={{ color: c.textMuted }}>Loading…</div>;

  const modeMeta = {
    disabled: { color: c.danger,  icon: '🔒', label: 'Disabled',
                desc: 'Block ALL remote writes. /relay and /network-config return 503. This is the safe default.' },
    dry_run:  { color: c.warning, icon: '🧪', label: 'Dry-run',
                desc: 'Build the PDU and log it, but do NOT transmit. Endpoints return success=true rc=0. No meter change.' },
    enabled:  { color: c.success, icon: '⚡', label: 'Enabled',
                desc: 'Full live execution. Writes actually happen on the meter / DCU. High blast radius — wrong values can require optical-port recovery.' },
  };

  return (
    <div>
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '20px', marginBottom: '16px',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px', color: c.textLabel }}>
          🔐 Remote writes kill-switch
        </div>
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '16px' }}>
          Server-side gate for relay disconnect/reconnect and remote IP/APN writes.
          Changes apply <strong>immediately</strong> — no service restart needed. The
          chosen mode persists across restarts.
        </div>

        {/* Current state */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '12px 14px', marginBottom: '16px',
          background: c.bgElevated,
          border: `2px solid ${modeMeta[state.mode].color}`,
          borderRadius: '6px',
        }}>
          <div style={{ fontSize: '28px' }}>{modeMeta[state.mode].icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: modeMeta[state.mode].color }}>
              Current mode: {modeMeta[state.mode].label.toUpperCase()}
            </div>
            <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '2px' }}>
              {modeMeta[state.mode].desc}
            </div>
          </div>
        </div>

        {/* Switcher */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['disabled', 'dry_run', 'enabled'].map(v => {
            const m = modeMeta[v];
            const active = v === state.mode;
            return (
              <button
                key={v}
                className="button"
                disabled={busy || active}
                onClick={() => flip(v)}
                style={{
                  flex: '1 1 180px',
                  padding: '12px 14px',
                  background: active ? m.color : 'transparent',
                  color: active ? '#0b1120' : m.color,
                  border: `2px solid ${m.color}`,
                  fontWeight: active ? 700 : 500,
                  cursor: active ? 'default' : 'pointer',
                }}
              >
                <div style={{ fontSize: '14px' }}>{m.icon} {m.label}</div>
              </button>
            );
          })}
        </div>

        {/* Source attribution — tells the admin where the current
            value came from (sidecar vs env) in case of weirdness. */}
        <div style={{ marginTop: '16px', fontSize: '10px', color: c.textDimmed }}>
          Source priority: runtime override → sidecar file → env var → default
          <br />
          runtime_override={String(state.runtime_override ?? 'none')} ·
          sidecar={String(state.sidecar ?? 'none')} ·
          env_var={String(state.env_var ?? 'none')}
        </div>
      </div>

      {/* Quick reference card */}
      <div style={{
        background: c.bgCardAlt, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '14px',
        fontSize: '11px', color: c.textMuted, lineHeight: 1.6,
      }}>
        <strong style={{ color: c.textLabel }}>Mode choice guide</strong>
        <ul style={{ marginTop: '8px', paddingLeft: '18px' }}>
          <li>
            <strong style={{ color: c.danger }}>🔒 Disabled</strong> — default / safe state.
            Use any time you're not actively in a maintenance window.
          </li>
          <li>
            <strong style={{ color: c.warning }}>🧪 Dry-run</strong> — verifies the new meter
            model / firmware accepts our PDUs without actually transmitting.
            The wire bytes are logged to the service journal so you can
            compare against reference captures.
          </li>
          <li>
            <strong style={{ color: c.success }}>⚡ Enabled</strong> — only while operator is
            actively performing an IP / APN / relay change. Flip back to
            Disabled immediately after.
          </li>
        </ul>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// FleetHealthSettings — System → Fleet Health sub-tab.
// One-screen admin-only overview of every connected meter +
// every DCU's vendor health snapshot. Refreshes every 30 s.
// Tripped circuit breakers surface first, so any flapping peer
// is visually obvious without hunting through the Inbound Meters
// tab. No new backend — reuses /admin/meters-health plus the
// existing DCU vendor-selective endpoints.
// ─────────────────────────────────────────────────────────────
function FleetHealthSettings() {
  const c = useThemeColors();
  const toast = useToast();
  const [meters, setMeters] = React.useState([]);
  const [dcuBundles, setDcuBundles] = React.useState({}); // serial → {cq, os}
  const [queues, setQueues] = React.useState({});         // serial → queue status
  const [presence, setPresence] = React.useState(null);   // fleet-presence payload
  const [loading, setLoading] = React.useState(false);
  const [lastAt, setLastAt] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    // Presence is a cheap CACHED snapshot (in-memory pool + one DB query, no
    // live meter I/O) — fetch and render it immediately, decoupled from the
    // slow per-DCU LIVE link reads below. It used to be awaited inside the
    // same Promise.all as communication-quality/online-status, each of which
    // is a live DLMS vendor read that can block for seconds (up to ~60s on a
    // half-dead DCU socket). That gated the presence card behind the slowest
    // DCU, so it appeared to "hang" even though its own endpoint returns in
    // ~10ms. Fire-and-forget so a slow DCU never delays online/offline state.
    api('/admin/fleet-presence').then(setPresence).catch(() => {});
    try {
      let fetchError = null;
      const r = await api('/admin/meters-health').catch((e) => {
        fetchError = e.message || 'fleet health endpoint unreachable';
        return { meters: [] };
      });
      const list = r.meters || [];
      setMeters(list);
      setLoadError(fetchError);
      // Per-DCU LIVE health + queue (slow — live DLMS vendor reads). These
      // fill in the DCU detail rows when ready; they no longer block the
      // presence card above.
      const dcus = list.filter(m => m.device_type === 'dcu' && m.session_open);
      const [bundleArr, queueArr] = await Promise.all([
        Promise.all(dcus.map(async m => {
          const [cq, os] = await Promise.all([
            api(`/dcus/${m.serial}/communication-quality`).catch(() => null),
            api(`/dcus/${m.serial}/online-status`).catch(() => null),
          ]);
          return [m.serial, { cq, os }];
        })),
        Promise.all(dcus.map(async m => {
          const q = await api(`/dcus/${m.serial}/queue`).catch(() => null);
          return [m.serial, q];
        })),
      ]);
      setDcuBundles(Object.fromEntries(bundleArr));
      setQueues(Object.fromEntries(queueArr));
      setLastAt(new Date().toLocaleTimeString());
    } catch (err) {
      toast.push(`Fleet health load failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    // Auto-refresh every 30 min — the per-DCU live reads are heavy, so a
    // slow cadence keeps background load light; the 🔄 Refresh button is
    // there for an on-demand snapshot.
    const id = setInterval(load, 30 * 60_000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, []);

  const totalMeters = meters.length;
  const dcus = meters.filter(m => m.device_type === 'dcu');
  const direct = meters.filter(m => m.device_type !== 'dcu');
  const tripped = meters.filter(m => m.health?.circuit_tripped);
  const sessionOpen = meters.filter(m => m.session_open).length;

  // Presence: every direct meter + DCU that has connected at least once,
  // live or offline, sorted offline-first by the backend.
  const presDevices = presence?.devices || [];
  const presCounts = presence?.counts || { total: 0, live: 0, offline: 0 };

  // "5m ago" / "2h ago" / "3d ago" — null = never seen.
  const fmtSince = (sec) => {
    if (sec == null) return '—';
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`;
    return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h ago`;
  };
  const fmtSeen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString();
  };

  const kpi = (label, value, color) => (
    <div style={{
      flex: '1 1 160px',
      background: c.bgCard, border: `1px solid ${c.border}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: '6px', padding: '10px 14px',
    }}>
      <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color }}>{value}</div>
    </div>
  );

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px',
      }}>
        <div style={{ fontSize: '13px', color: c.textMuted }}>
          Live fleet snapshot · auto-refresh every 30 min
          {lastAt && <span style={{ marginLeft: '8px' }}>· last {lastAt}</span>}
        </div>
        <button className="button button-sm button-secondary" onClick={load} disabled={loading}>
          {loading ? <Spinner /> : '🔄 Refresh'}
        </button>
      </div>

      <ErrorBanner
        message={loadError ? `Fleet health: ${loadError}` : null}
        onRetry={load}
        onDismiss={() => setLoadError(null)}
      />

      {/* KPI strip */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {kpi('Total in pool', totalMeters, c.accent)}
        {kpi('DCUs', dcus.length, '#a855f7')}
        {kpi('Direct meters', direct.length, '#0ea5e9')}
        {kpi('Live now', presCounts.live, c.success)}
        {kpi('Offline', presCounts.offline, presCounts.offline ? c.danger : c.textMuted)}
        {kpi('Breaker tripped', tripped.length, tripped.length ? c.danger : c.textMuted)}
      </div>

      {/* Presence table — every direct meter + DCU that has connected at
          least once, live or offline, with last-seen + how long since.
          Offline devices surface first (longest-gone at the top). */}
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '12px', marginBottom: '14px',
      }}>
        <div style={{
          fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '8px',
          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px',
        }}>
          <span>📡 Presence — direct meters &amp; DCUs ({presCounts.total})</span>
          <span style={{ fontSize: '11px', fontWeight: 400, color: c.textMuted }}>
            <span style={{ color: c.success }}>● {presCounts.live} live</span>
            {' · '}
            <span style={{ color: presCounts.offline ? c.danger : c.textMuted }}>
              ○ {presCounts.offline} offline
            </span>
          </span>
        </div>
        {presDevices.length === 0 ? (
          <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic' }}>
            No direct meters or DCUs have connected yet.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '11px' }}>
            <thead>
              <tr style={{ color: c.textMuted, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
                <th style={{ padding: '4px 6px' }}>Serial</th>
                <th style={{ padding: '4px 6px' }}>Type</th>
                <th style={{ padding: '4px 6px' }}>Status</th>
                <th style={{ padding: '4px 6px' }}>Last seen</th>
                <th style={{ padding: '4px 6px' }}>Since</th>
              </tr>
            </thead>
            <tbody>
              {presDevices.map(d => (
                <tr key={d.serial} style={{ borderBottom: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>
                    {d.device_id_serial || d.serial}
                    {d.device_id_serial && d.device_id_serial !== d.serial && (
                      <span style={{ color: c.textMuted, marginLeft: '6px', fontSize: '10px' }}>
                        ({d.serial})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {d.kind === 'dcu'
                      ? <span style={{ color: '#a855f7' }}>🛰 DCU</span>
                      : <span style={{ color: '#0ea5e9' }}>🔌 Direct</span>}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {d.live
                      ? <span style={{ color: c.success, fontWeight: 600 }}>● Live</span>
                      : <span style={{ color: c.danger }}>○ Offline</span>}
                  </td>
                  <td style={{ padding: '4px 6px', color: c.textMuted }}>{fmtSeen(d.last_seen)}</td>
                  <td style={{ padding: '4px 6px', color: d.live ? c.textMuted : c.warning }}>
                    {d.live ? 'now' : fmtSince(d.seconds_since_seen)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: '6px', fontSize: '10px', color: c.textMuted }}>
          "Live" = heartbeat within the watchdog window (180s meters / 600s DCUs).
          For offline devices, "Since" is how long since the server last saw it online.
        </div>
      </div>

      {/* DCU health table */}
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '12px', marginBottom: '14px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '8px' }}>
          🛰 DCU health ({dcus.length})
        </div>
        {dcus.length === 0 ? (
          <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic' }}>
            No DCUs in the CAS pool right now.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '11px' }}>
            <thead>
              <tr style={{ color: c.textMuted, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
                <th style={{ padding: '4px 6px' }}>Serial</th>
                <th style={{ padding: '4px 6px' }}>Session</th>
                <th style={{ padding: '4px 6px' }}>RSSI</th>
                <th style={{ padding: '4px 6px' }}>BER</th>
                <th style={{ padding: '4px 6px' }}>PLC</th>
                <th style={{ padding: '4px 6px' }}>RF</th>
                <th style={{ padding: '4px 6px' }}>Online meters</th>
                <th style={{ padding: '4px 6px' }}>Queue</th>
                <th style={{ padding: '4px 6px' }}>Breaker</th>
              </tr>
            </thead>
            <tbody>
              {dcus.map(d => {
                const cq = dcuBundles[d.serial]?.cq;
                const os = dcuBundles[d.serial]?.os;
                const q = queues[d.serial];
                const tripped = d.health?.circuit_tripped;
                return (
                  <tr key={d.serial} style={{ borderBottom: `1px solid ${c.border}` }}>
                    <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{d.serial}</td>
                    <td style={{ padding: '4px 6px' }}>
                      {d.session_open
                        ? <span style={{ color: c.success }}>● open</span>
                        : <span style={{ color: c.danger }}>● closed</span>}
                    </td>
                    <td style={{ padding: '4px 6px' }}>{cq?.rssi ?? '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{cq?.ber ?? '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{cq?.plc_level ?? '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{cq?.rf_level ?? '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{os?.entries?.length ?? '—'}</td>
                    <td style={{ padding: '4px 6px' }}>
                      {q ? `${q.jobs_running}/${q.jobs_pending}/${q.jobs_done}/${q.jobs_failed}` : '—'}
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      {tripped
                        ? <span style={{ color: c.danger }}>⚠ tripped</span>
                        : <span style={{ color: c.success }}>✓ OK</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {dcus.length > 0 && (
          <div style={{ marginTop: '6px', fontSize: '10px', color: c.textMuted }}>
            Queue column = running / pending / done / failed jobs on that DCU's collector queue.
          </div>
        )}
      </div>

      {/* Direct meters table */}
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '12px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '8px' }}>
          🔌 Direct meters ({direct.length})
        </div>
        {direct.length === 0 ? (
          <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic' }}>
            No direct-GPRS meters in the pool.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '11px' }}>
            <thead>
              <tr style={{ color: c.textMuted, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
                <th style={{ padding: '4px 6px' }}>Serial</th>
                <th style={{ padding: '4px 6px' }}>Session</th>
                <th style={{ padding: '4px 6px' }}>Fails</th>
                <th style={{ padding: '4px 6px' }}>Trips</th>
                <th style={{ padding: '4px 6px' }}>Breaker</th>
              </tr>
            </thead>
            <tbody>
              {direct
                .slice()
                .sort((a, b) =>
                  (b.health?.circuit_tripped ? 1 : 0) - (a.health?.circuit_tripped ? 1 : 0))
                .map(m => {
                  const tripped = m.health?.circuit_tripped;
                  return (
                    <tr key={m.serial} style={{ borderBottom: `1px solid ${c.border}` }}>
                      <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{m.serial}</td>
                      <td style={{ padding: '4px 6px' }}>
                        {m.session_open
                          ? <span style={{ color: c.success }}>● open</span>
                          : <span style={{ color: c.textMuted }}>● closed</span>}
                      </td>
                      <td style={{ padding: '4px 6px' }}>{m.health?.consecutive_failures ?? 0}</td>
                      <td style={{ padding: '4px 6px' }}>{m.health?.trip_count ?? 0}</td>
                      <td style={{ padding: '4px 6px' }}>
                        {tripped
                          ? <span style={{ color: c.danger }}>⚠ tripped</span>
                          : <span style={{ color: c.success }}>✓ OK</span>}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ServerHealthCard — System → Server Health sub-tab.
// Phase α (Tier A Item 1) tripwires. Polls /api/admin/system-health
// every 30 s and renders a six-tile dashboard: Disk, RAM, CPU load,
// SQLite write contention proxy, daily-billing coverage, audit
// table size. Each tile is colored by its server-side `*_status`
// field (ok/warn/crit) and shows the action recommendation that
// fires when the threshold is crossed (e.g. "plan KVM 2 upgrade").
// Admin-only (parent gates).
// ─────────────────────────────────────────────────────────────
function ServerHealthCard() {
  const c = useThemeColors();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastAt, setLastAt] = useState(null);
  // V0.5.x: drill-down panel state. `expanded` is null | 'stale' | 'uncommissioned'.
  // Details fetched on demand the first time a tile is clicked.
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api('/admin/system-health');
      setData(r);
      setLastAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetails = React.useCallback(async () => {
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const r = await api('/admin/system-health/details');
      setDetails(r);
    } catch (e) {
      setDetailsError(e?.message || String(e));
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const onTileClick = (kind) => {
    setExpanded((prev) => (prev === kind ? null : kind));
    if (!details && !detailsLoading) {
      void loadDetails();
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, []);

  const statusColor = (s) =>
    s === 'crit' ? c.danger
    : s === 'warn' ? c.warning
    : s === 'ok' ? '#22c55e'
    : c.textMuted;

  const fmtBytes = (n) => {
    if (n == null || !isFinite(n)) return '—';
    if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(0)} MB`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(0)} KB`;
    return `${n} B`;
  };

  const Tile = ({ label, value, status, sub, action, onClick, expanded }) => (
    <div
      onClick={onClick}
      style={{
        flex: '1 1 220px',
        background: c.bgCard,
        border: `1px solid ${expanded ? c.accent : c.border}`,
        borderLeft: `4px solid ${statusColor(status)}`,
        borderRadius: '6px',
        padding: '12px 14px',
        minHeight: '88px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 120ms',
      }}
    >
      <div style={{
        fontSize: '10px', color: c.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.5px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>{label}</span>
        {onClick && (
          <span style={{ fontSize: '10px', color: c.accent }}>
            {expanded ? '▾ click to close' : '▸ click for details'}
          </span>
        )}
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, color: statusColor(status), marginTop: 2 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: c.textMuted, marginTop: 2 }}>
          {sub}
        </div>
      )}
      {status === 'crit' && action && (
        <div style={{ fontSize: '11px', color: c.danger, marginTop: 6, fontWeight: 600 }}>
          ⚠ {action}
        </div>
      )}
      {status === 'warn' && action && (
        <div style={{ fontSize: '11px', color: c.warning, marginTop: 6 }}>
          {action}
        </div>
      )}
    </div>
  );

  // Group stale meters by DCU (or "(direct)") so the operator can
  // see which DCU is dragging the cluster — almost always the right
  // diagnostic frame.
  const groupByDcu = (meters) => {
    const groups = {};
    for (const m of meters || []) {
      const key = m.dcu_serial || '(direct)';
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    }
    return groups;
  };

  const renderDetailsPanel = () => {
    if (expanded == null) return null;
    if (detailsLoading) {
      return (
        <div style={{
          marginTop: 10, padding: 14,
          background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: 6,
          color: c.textMuted, fontSize: 12,
        }}>
          Loading meter list…
        </div>
      );
    }
    if (detailsError) {
      return (
        <div style={{
          marginTop: 10, padding: 12,
          background: '#3b1d1d', border: `1px solid ${c.danger}`, borderRadius: 6,
          color: '#fca5a5', fontSize: 12,
        }}>
          {detailsError}
        </div>
      );
    }
    if (!details) return null;

    const list = expanded === 'stale' ? details.stale : details.uncommissioned;
    const title = expanded === 'stale'
      ? `Stale meters — silent for >7 days but produced data within 30 days (${list.length})`
      : `Uncommissioned meters — assigned but never produced a row (${list.length})`;

    if (!list.length) {
      return (
        <div style={{
          marginTop: 10, padding: 14,
          background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: 6,
          color: c.textMuted, fontSize: 13,
        }}>
          {title} — no meters in this category. ✓
        </div>
      );
    }

    const grouped = groupByDcu(list);
    const dcuKeys = Object.keys(grouped).sort((a, b) => grouped[b].length - grouped[a].length);

    return (
      <div style={{
        marginTop: 10, padding: 14,
        background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: 6,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: c.textPrimary }}>
          {title}
        </div>
        {expanded === 'stale' && (
          <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 10 }}>
            These meters had data once but the scheduler hasn't received any in the last 7 days.
            Most-likely cause: their DCU lost connectivity, or the meter itself dropped its session.
            Sorted oldest-silent first; grouped by DCU so you can spot offline-DCU clusters.
          </div>
        )}
        {expanded === 'uncommissioned' && (
          <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 10 }}>
            These meters are in the assignments table but the central has never received a daily-billing
            row from them. Causes: never physically installed, wrong serial, never dialed in, or
            commissioning still in progress. Grouped by customer.
          </div>
        )}
        {dcuKeys.map((dcu) => (
          <div key={dcu} style={{ marginBottom: 12 }}>
            <div style={{
              fontSize: 11, color: c.textSecondary, fontWeight: 600,
              padding: '4px 0', borderBottom: `1px dashed ${c.border}`, marginBottom: 4,
            }}>
              {expanded === 'stale'
                ? <>DCU <code>{dcu}</code> — {grouped[dcu].length} meter{grouped[dcu].length === 1 ? '' : 's'}</>
                : <>{dcu === '(direct)' ? 'Direct (no DCU)' : <>DCU <code>{dcu}</code></>} — {grouped[dcu].length} meter{grouped[dcu].length === 1 ? '' : 's'}</>
              }
            </div>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: c.textMuted, textAlign: 'left' }}>
                  <th style={{ padding: '4px 6px' }}>Serial</th>
                  <th style={{ padding: '4px 6px' }}>Customer</th>
                  <th style={{ padding: '4px 6px' }}>Transformer</th>
                  {expanded === 'stale'
                    ? <>
                        <th style={{ padding: '4px 6px' }}>Days silent</th>
                        <th style={{ padding: '4px 6px' }}>Last row</th>
                      </>
                    : <th style={{ padding: '4px 6px' }}>Commissioned</th>
                  }
                </tr>
              </thead>
              <tbody>
                {grouped[dcu].map((m) => (
                  <tr key={m.serial}>
                    <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{m.serial}</td>
                    <td style={{ padding: '4px 6px' }}>{m.customer || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{m.transformer || '—'}</td>
                    {expanded === 'stale'
                      ? <>
                          <td style={{ padding: '4px 6px', fontFamily: 'monospace', color: c.warning }}>
                            {m.days_silent}d
                          </td>
                          <td style={{ padding: '4px 6px', color: c.textMuted }}>
                            {m.last_row_iso ? fmtDT(m.last_row_iso, { seconds: true }) : '—'}
                          </td>
                        </>
                      : <td style={{ padding: '4px 6px', color: c.textMuted }}>
                          {m.commissioned_at ? fmtD(m.commissioned_at, { utc: true }) : 'no'}
                        </td>
                    }
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px',
      }}>
        <div style={{ fontSize: '13px', color: c.textMuted }}>
          Server-side health metrics — auto-refresh every 30 s. Tripwires match the readiness audit thresholds.
          {lastAt && <> · Last update: {lastAt}</>}
        </div>
        <button className="button button-sm" onClick={load} disabled={loading}>
          {loading ? '…' : '🔄'} Refresh
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px', background: '#3b1d1d',
          border: `1px solid ${c.danger}`, borderRadius: '6px',
          color: '#fca5a5', marginBottom: '12px',
        }}>
          {error}
        </div>
      )}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
            <Tile
              label="Disk"
              value={`${data.disk_pct}%`}
              status={data.disk_status}
              sub={`${fmtBytes(data.disk_used_bytes)} / ${fmtBytes(data.disk_total_bytes)}`}
              action={data.thresholds?.disk?.action_crit}
            />
            <Tile
              label="RAM"
              value={`${data.ram_pct}%`}
              status={data.ram_status}
              sub={`${fmtBytes((data.ram_used_kb || 0) * 1024)} / ${fmtBytes((data.ram_total_kb || 0) * 1024)}`}
              action={data.thresholds?.ram?.action_crit}
            />
            <Tile
              label={`CPU load (1m)`}
              value={`${data.load_avg_1m}`}
              status={data.load_status}
              sub={`${data.cpu_count} vCPU · saturation at ${data.thresholds?.load_avg_1m?.crit}`}
              action={data.thresholds?.load_avg_1m?.action_crit}
            />
            <Tile
              label="SQLite write lock (24h)"
              value={data.db_locked_24h}
              status={data.db_locked_status}
              sub="`database is locked` events"
              action={data.thresholds?.db_locked_24h?.action_crit}
            />
            <Tile
              label="Daily-billing coverage (7d)"
              value={
                data.daily_billing_coverage_7d != null
                  ? `${data.daily_billing_coverage_7d}%`
                  : '—'
              }
              status={data.coverage_status}
              sub={
                data.fleet_operational_30d != null
                  ? `${data.fleet_collected_7d} / ${data.fleet_operational_30d} operational`
                  : 'meters with ≥1 daily row'
              }
              action={data.thresholds?.daily_billing_coverage_7d?.action_crit}
            />
            <Tile
              label="Audit log rows"
              value={(data.audit_rows_total || 0).toLocaleString()}
              status={data.audit_status}
              sub={`readings.db: ${fmtBytes(data.readings_db_bytes)}`}
              action={data.thresholds?.audit_rows_total?.action_crit}
            />
            {/* Fleet breakdown tiles — separate "scheduler missed"
                from "meters never came online". Both are clickable
                to open a per-meter drill-down list. */}
            <Tile
              label="Stale meters (>7d silent)"
              value={data.fleet_stale_7d ?? '—'}
              status={
                (data.fleet_stale_7d ?? 0) > 25 ? 'crit'
                : (data.fleet_stale_7d ?? 0) > 10 ? 'warn'
                : 'ok'
              }
              sub="had data once, not in last 7d — scheduler-missed signal"
              action={
                (data.fleet_stale_7d ?? 0) > 25
                  ? 'check DCU connectivity / meter sessions'
                  : null
              }
              onClick={() => onTileClick('stale')}
              expanded={expanded === 'stale'}
            />
            <Tile
              label="Uncommissioned"
              value={data.fleet_uncommissioned ?? '—'}
              status="ok"
              sub={`assigned but never produced a row · ${data.fleet_total_assigned ?? 0} total meters`}
              action={null}
              onClick={() => onTileClick('uncommissioned')}
              expanded={expanded === 'uncommissioned'}
            />
          </div>

          {renderDetailsPanel()}
          <div style={{
            padding: '10px 12px', background: c.bgCardAlt,
            border: `1px dashed ${c.border}`, borderRadius: '6px',
            fontSize: '11px', color: c.textMuted,
          }}>
            All tiles green → no action needed. Yellow → plan ahead.
            Red → tripwire fired; the action shown is the recommended response from the readiness audit (docs/readiness-100-2000-meters.md).
          </div>
        </>
      )}
    </div>
  );
}


// KPI card — single colored accent bar on the left, label + value.
// `onClick` (optional) makes the card behave as a button: cursor,
// hover lift, keyboard-accessible. Pass null/undefined for static
// cards (the two energy counters at the top of the dashboard).
function KpiCard({ label, value, sub, accent, onClick }) {
  const c = useThemeColors();
  if (accent === undefined) accent = c.accent;
  const clickable = typeof onClick === 'function';
  const [hover, setHover] = useState(false);
  return (
    <div
      className="stat-card"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      onMouseEnter={() => clickable && setHover(true)}
      onMouseLeave={() => clickable && setHover(false)}
      style={{
        borderLeft: `4px solid ${accent}`,
        cursor: clickable ? 'pointer' : 'default',
        transform: clickable && hover ? 'translateY(-2px)' : 'none',
        boxShadow: clickable && hover ? `0 4px 12px rgba(0,0,0,0.35)` : 'none',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        outline: 'none',
      }}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: accent }}>{value}</div>
      {sub && (
        <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '4px' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// Simple SVG bar chart for the 30-day energy series. Designed to look
// decent at any width; no tooltips yet (Phase D if the user asks).
function EnergyBarChart({ data }) {
  const c = useThemeColors();
  const t = useT();
  if (!data || data.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: c.textDimmed, fontSize: '12px' }}>
        {t('dashboard.chart_no_data')}
      </div>
    );
  }
  const width = 800;
  const height = 200;
  const padding = { top: 10, right: 20, bottom: 30, left: 50 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;
  const maxKwh = Math.max(...data.map(d => d.kwh), 1);
  const barW = plotW / data.length;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {/* grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
        <g key={i}>
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + (1 - f) * plotH}
            y2={padding.top + (1 - f) * plotH}
            stroke={c.border}
            strokeDasharray="4,4"
          />
          <text
            x={padding.left - 8}
            y={padding.top + (1 - f) * plotH + 4}
            textAnchor="end"
            fill={c.textMuted}
            fontSize="10"
          >
            {(maxKwh * f).toFixed(0)}
          </text>
        </g>
      ))}
      {/* bars */}
      {data.map((d, i) => {
        const h = (d.kwh / maxKwh) * plotH;
        return (
          <g key={i}>
            <rect
              x={padding.left + i * barW + 1}
              y={padding.top + plotH - h}
              width={Math.max(1, barW - 2)}
              height={h}
              fill="url(#barGradient)"
            >
              <title>{d.date}: {d.kwh.toLocaleString()} kWh</title>
            </rect>
          </g>
        );
      })}
      <defs>
        <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={c.accent} stopOpacity="0.9" />
          <stop offset="100%" stopColor={c.accent} stopOpacity="0.3" />
        </linearGradient>
      </defs>
      {/* first / last date label */}
      <text
        x={padding.left}
        y={height - 8}
        textAnchor="start"
        fill={c.textMuted}
        fontSize="10"
      >
        {data[0]?.date}
      </text>
      <text
        x={width - padding.right}
        y={height - 8}
        textAnchor="end"
        fill={c.textMuted}
        fontSize="10"
      >
        {data[data.length - 1]?.date}
      </text>
    </svg>
  );
}

// Inbound Meters Tab
function InboundMetersTab() {
  const { meters } = useMeterList();
  const { setSelected } = useMeterSelection();
  const nav = useNav();
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = userCan(user, 'manage_records');
  const [liveMeter, setLiveMeter] = useState(null);
  const [busy, setBusy] = useState(null); // serial currently being acted on
  // 'all' | 'assigned' | 'unassigned'. Seeded from the nav context so
  // a KPI-card click on the Dashboard lands on the right filter.
  const [filter, setFilter] = useState(nav.meterFilter || 'all');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 150);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  // Whenever the preset changes from elsewhere (e.g. the user clicked
  // a different KPI card), sync the local filter.
  useEffect(() => { setFilter(nav.meterFilter || 'all'); }, [nav.meterFilter]);

  // Shared session cache: tab navigation no longer downloads the full
  // assignment payload again. While this tab is open, refresh it at a relaxed
  // cadence; failures keep the last-known-good context value.
  const { assignments, refreshAssignments } = useAssignments();
  useEffect(() => {
    let mounted = true;
    let failStreak = 0;
    let fastTimer = null;
    const FAST_RETRY_BUDGET = 3;
    const FAST_RETRY_DELAY_MS = 2_000;

    const scheduleFastRetry = () => {
      if (!mounted || fastTimer !== null) return;
      if (failStreak <= 0 || failStreak > FAST_RETRY_BUDGET) return;
      fastTimer = setTimeout(() => {
        fastTimer = null;
        if (mounted) fetchAssignments();
      }, FAST_RETRY_DELAY_MS);
    };

    const fetchAssignments = async () => {
      try {
        await refreshAssignments();
        if (mounted) failStreak = 0;
        return;
      } catch (e) {
        console.warn('[/assignments] fetch failed', e?.message);
        failStreak += 1;
      }
      // Outside the try: schedule the next fast retry only if we
      // haven't burned the budget. Once exhausted, the next slow
      // tick is the next attempt — no idle 2 s wakeups in between.
      scheduleFastRetry();
    };

    fetchAssignments();
    // One shared 60 s refresh while the tab is visible. The bounded 2 s retry
    // burst above still reacts quickly to a transient malformed/zombie response.
    const slow = setInterval(fetchAssignments, 60_000);
    return () => {
      mounted = false;
      clearInterval(slow);
      if (fastTimer !== null) clearTimeout(fastTimer);
    };
  }, [refreshAssignments]);
  const assignmentBySerial = React.useMemo(() => {
    const m = {};
    for (const a of assignments) m[a.meter_serial] = a;
    return m;
  }, [assignments]);

  // Exclude DCUs from all counts — they belong in the DCU tab
  const metersOnly = React.useMemo(() => meters.filter(m => m.device_type !== 'dcu'), [meters]);
  const onlineMeters = React.useMemo(() => metersOnly.filter(m => m.online || m.session_open || m._via_dcu), [metersOnly]);
  const offlineMeters = React.useMemo(() => metersOnly.filter(m => !m.online && !m.session_open && !m._via_dcu), [metersOnly]);
  const counts = React.useMemo(() => {
    let assigned = 0;
    let unassigned = 0;
    for (const m of metersOnly) {
      const s = m.serial_number || m.serial;
      if (assignmentBySerial[s]) assigned += 1;
      else unassigned += 1;
    }
    return {
      all: metersOnly.length, assigned, unassigned,
      online: onlineMeters.length, offline: offlineMeters.length,
    };
  }, [metersOnly, onlineMeters, offlineMeters, assignmentBySerial]);

  const filteredMeters = React.useMemo(() => {
    let list = metersOnly;
    if (filter === 'online') list = onlineMeters;
    if (filter === 'offline') list = offlineMeters;
    if (filter === 'assigned') list = list.filter(m => assignmentBySerial[m.serial_number || m.serial]);
    if (filter === 'unassigned') list = list.filter(m => !assignmentBySerial[m.serial_number || m.serial]);
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(m => {
        const serial = (m.serial_number || m.serial || '').toLowerCase();
        const devId = (m.device_id_serial || '').toLowerCase();  // nameplate serial
        const asn = assignmentBySerial[m.serial_number || m.serial];
        const name = (asn?.object_name || '').toLowerCase();
        const mfg = (m.manufacturer || '').toLowerCase();
        const fw = (m.firmware_version || m.firmware || '').toLowerCase();
        return serial.includes(q) || devId.includes(q) || name.includes(q) || mfg.includes(q) || fw.includes(q);
      });
    }
    return list;
  }, [meters, filter, assignmentBySerial, debouncedSearch]);
  const pageCount = Math.max(1, Math.ceil(filteredMeters.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageMeters = React.useMemo(
    () => filteredMeters.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredMeters, currentPage, pageSize],
  );
  useEffect(() => { setPage(1); }, [filter, debouncedSearch, pageSize]);
  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  // ── Bulk selection (Unassigned filter, admin only) ──────────────
  // Scoped to the Unassigned bucket deliberately: claiming meters that
  // have no assignment is additive and safe, whereas bulk-rewriting
  // meters that are ALREADY assigned would silently overwrite CT/VT
  // ratios operators set per meter. That's a separate, riskier feature.
  const selectMode = isAdmin && filter === 'unassigned';
  const [selectedSerials, setSelectedSerials] = useState(() => new Set());
  const [anchorIdx, setAnchorIdx] = useState(null);   // for shift-click ranges
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTargets, setBulkTargets] = useState(null); // {transformers, customers}

  // Drop the selection whenever the underlying list changes out from
  // under it — a stale serial from a previous filter would otherwise
  // ride along into the assign call.
  useEffect(() => {
    setSelectedSerials(new Set());
    setAnchorIdx(null);
  }, [filter, debouncedSearch]);

  const serialOf = (m) => m.serial_number || m.serial;

  const toggleRow = (index, shiftKey) => {
    const serial = serialOf(pageMeters[index]);
    if (!serial) return;
    setSelectedSerials(prev => {
      const next = new Set(prev);
      // Shift extends from the last row the operator touched, matching
      // file-manager convention: the new row's resulting state is
      // applied across the whole range.
      if (shiftKey && anchorIdx !== null && anchorIdx < pageMeters.length) {
        const lo = Math.min(anchorIdx, index);
        const hi = Math.max(anchorIdx, index);
        const turnOn = !next.has(serial);
        for (let i = lo; i <= hi; i++) {
          const s = serialOf(pageMeters[i] || {});
          if (!s) continue;
          if (turnOn) next.add(s); else next.delete(s);
        }
      } else if (next.has(serial)) {
        next.delete(serial);
      } else {
        next.add(serial);
      }
      return next;
    });
    setAnchorIdx(index);
  };

  const pageSerials = React.useMemo(
    () => pageMeters.map(serialOf).filter(Boolean),
    [pageMeters],
  );
  const pageAllSelected = pageSerials.length > 0
    && pageSerials.every(s => selectedSerials.has(s));

  const togglePage = () => {
    setSelectedSerials(prev => {
      const next = new Set(prev);
      if (pageAllSelected) pageSerials.forEach(s => next.delete(s));
      else pageSerials.forEach(s => next.add(s));
      return next;
    });
    setAnchorIdx(null);
  };

  // "Select all" spans every page of the current filter, not just the
  // visible one — with 500 unassigned meters nobody wants to page
  // through five screens ticking boxes.
  const selectAllFiltered = () => {
    setSelectedSerials(new Set(filteredMeters.map(serialOf).filter(Boolean)));
    setAnchorIdx(null);
  };

  const openBulkAssign = async () => {
    setBusy('bulk-assign');
    try {
      const [customers, transformers] = await Promise.all([
        api('/customers').then(d => d.customers || []),
        api('/transformers').then(d => d.transformers || []),
      ]);
      if (!transformers.length) {
        toast.push('No transformers exist yet — create one under Customers first', 'error');
        return;
      }
      setBulkTargets({ customers, transformers });
      setBulkOpen(true);
    } catch (err) {
      toast.push(`Could not load customers/transformers: ${err.message}`, 'error');
    } finally { setBusy(null); }
  };

  const handleOpenSession = async (serial) => {
    setBusy(serial);
    try {
      await api(`/meters/${serial}/open-session`, { method: 'POST' });
      toast.push(`Secure session opened with ${serial}`, 'success');
      // Auto-open the live readings panel for this meter
      setSelected(serial);
      setLiveMeter(serial);
    } catch (err) {
      toast.push(`Open session failed: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleCloseSession = async (serial) => {
    setBusy(serial);
    try {
      await api(`/meters/${serial}/close-session`, { method: 'POST' });
      toast.push(`Secure session closed for ${serial}`, 'info');
    } catch (err) {
      toast.push(`Close session failed: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleRelay = async (serial, action) => {
    if (!(await confirm(`Relay ${action}`, `Are you sure you want to ${action} the relay on meter ${serial}?`))) return;
    try {
      await api(`/meters/${serial}/relay`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      toast.push(`Relay ${action} sent to ${serial}`, 'success');
    } catch (err) {
      toast.push(`Relay ${action} failed: ${err.message}`, 'error');
    }
  };

  // Inline filter pill used by the toolbar below. Active pill has the
  // accent color; counts are always shown so the operator knows at a
  // glance how many meters fall in each bucket.
  const FilterPill = ({ kind, label, count, accent }) => {
    const active = filter === kind;
    return (
      <button
        className="button button-sm"
        onClick={() => { setFilter(kind); nav.setMeterFilter(kind); }}
        style={{
          padding: '6px 14px',
          background: active ? accent : c.bgCardAlt,
          color: active ? c.bgBase : c.textSecondary,
          border: `1px solid ${active ? accent : c.border}`,
          fontWeight: active ? 600 : 400,
        }}
      >
        {label} <span style={{ opacity: 0.8 }}>({count})</span>
      </button>
    );
  };

  const renderPaginationControls = () => {
    if (filteredMeters.length === 0) return null;
    const from = (currentPage - 1) * pageSize + 1;
    const to = Math.min(filteredMeters.length, currentPage * pageSize);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '11px', color: c.textSecondary }}>
        <span>Showing {from.toLocaleString()}–{to.toLocaleString()} of {filteredMeters.length.toLocaleString()}</span>
        <button className="button button-sm button-secondary" disabled={currentPage <= 1} onClick={() => setPage(1)}>«</button>
        <button className="button button-sm button-secondary" disabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
        <span>Page {currentPage.toLocaleString()} / {pageCount.toLocaleString()}</span>
        <button className="button button-sm button-secondary" disabled={currentPage >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}>›</button>
        <button className="button button-sm button-secondary" disabled={currentPage >= pageCount} onClick={() => setPage(pageCount)}>»</button>
        <label style={{ marginLeft: 'auto', display: 'flex', gap: '5px', alignItems: 'center' }}>
          Rows
          <select className="input-field" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} style={{ padding: '4px 6px', fontSize: '11px' }}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </label>
      </div>
    );
  };

  // Detect malformed-Holley pattern serials in the current meter
  // list. Same rule the cas_server identification guard uses: 24 hex
  // chars where decoded bytes contain at least one non-ASCII-digit.
  // Real Holley serials are 12 ASCII digits, so they never appear as
  // 24-char hex. This is purely defensive — if the running guard ever
  // had a gap, we still see + clean the residue.
  const isMalformedSerial = (s) => {
    if (typeof s !== 'string' || s.length !== 24) return false;
    if (!/^[0-9a-fA-F]{24}$/.test(s)) return false;
    let digitCount = 0;
    for (let i = 0; i < 12; i++) {
      const byte = parseInt(s.substring(i * 2, i * 2 + 2), 16);
      if (byte >= 0x30 && byte <= 0x39) digitCount++;
    }
    return digitCount < 12;
  };
  const malformedSerials = React.useMemo(
    () => meters.filter(m => isMalformedSerial(m.serial_number || m.serial))
                .map(m => m.serial_number || m.serial),
    [meters]
  );

  const cleanMalformedPhantoms = async () => {
    if (!malformedSerials.length) return;
    if (!(await confirm(
      `Clean ${malformedSerials.length} phantom serial${malformedSerials.length === 1 ? '' : 's'}?`,
      `Removes the corrupted-pattern entries from the in-memory pool and the known-devices sidecar. Real meters with valid 12-digit serials are never affected.`,
    ))) return;
    setBusy('clean-malformed');
    let ok = 0, fail = 0;
    for (const serial of malformedSerials) {
      try {
        await api(`/admin/inbound-meters/${encodeURIComponent(serial)}/forget`, { method: 'POST' });
        ok++;
      } catch (e) {
        fail++;
      }
    }
    toast.push(
      fail
        ? `Cleaned ${ok} phantom${ok === 1 ? '' : 's'}, ${fail} failed`
        : `Cleaned ${ok} phantom serial${ok === 1 ? '' : 's'}`,
      fail ? 'error' : 'success',
    );
    setBusy(null);
  };

  return (
    <div className="tab-content">
      <div className="section-title">🔌 {t('section.connected_meters')}</div>

      {/* ── Filter toolbar with search ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterPill kind="all" label={t('btn.all')} count={counts.all} accent={c.accent} />
        <FilterPill kind="online" label="Online" count={counts.online} accent="#22c55e" />
        <FilterPill kind="offline" label="Offline" count={counts.offline} accent="#ef4444" />
        <FilterPill kind="assigned" label={t('meters.assigned')} count={counts.assigned} accent={c.success} />
        <FilterPill kind="unassigned" label={t('meters.unassigned')} count={counts.unassigned} accent={c.warning} />
        <input
          className="input-field"
          placeholder="🔍 Search serial, name, manufacturer..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginLeft: 'auto', padding: '6px 10px', fontSize: '12px', width: '260px' }}
        />
        {isAdmin && malformedSerials.length > 0 && (
          <button
            className="button button-sm"
            onClick={cleanMalformedPhantoms}
            disabled={busy === 'clean-malformed'}
            style={{
              background: c.warning,
              color: c.bgBase,
              fontWeight: 600,
              padding: '6px 12px',
            }}
            title="Removes phantom entries (corrupted serials that can never authenticate)"
          >
            {busy === 'clean-malformed'
              ? '⌛ Cleaning…'
              : `🧹 Clean ${malformedSerials.length} phantom${malformedSerials.length === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {/* ── Bulk-selection action bar (Unassigned filter only) ── */}
      {selectMode && filteredMeters.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          marginBottom: '10px', padding: '8px 12px', borderRadius: '6px',
          background: selectedSerials.size ? c.bgCardAlt : 'transparent',
          border: `1px solid ${selectedSerials.size ? c.accent : c.border}`,
        }}>
          <span style={{ fontSize: '12px', color: selectedSerials.size ? c.textPrimary : c.textSecondary }}>
            {selectedSerials.size
              ? <><strong>{selectedSerials.size}</strong> of {filteredMeters.length} selected</>
              : <>Tick meters to assign them in bulk — <kbd>Shift</kbd>+click selects a range</>}
          </span>
          <button className="button button-sm button-secondary" onClick={selectAllFiltered}
                  disabled={selectedSerials.size === filteredMeters.length}>
            Select all {filteredMeters.length}
          </button>
          {selectedSerials.size > 0 && (
            <button className="button button-sm button-secondary"
                    onClick={() => { setSelectedSerials(new Set()); setAnchorIdx(null); }}>
              Clear
            </button>
          )}
          <button
            className="button button-sm"
            onClick={openBulkAssign}
            disabled={!selectedSerials.size || busy === 'bulk-assign'}
            style={{
              marginLeft: 'auto', fontWeight: 600, padding: '6px 14px',
              background: selectedSerials.size ? c.accent : c.bgCardAlt,
              color: selectedSerials.size ? c.bgBase : c.textMuted,
              border: `1px solid ${selectedSerials.size ? c.accent : c.border}`,
            }}
          >
            {busy === 'bulk-assign'
              ? '⌛ Loading…'
              : `🔗 Assign ${selectedSerials.size || ''} selected`}
          </button>
        </div>
      )}

      <div style={{ marginBottom: '8px' }}>{renderPaginationControls()}</div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              {selectMode && (
                <th style={{ width: '32px' }}>
                  <input
                    type="checkbox"
                    checked={pageAllSelected}
                    onChange={togglePage}
                    title={pageAllSelected ? 'Deselect this page' : 'Select this page'}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
              )}
              <th>{t('th.serial')}</th>
              <th>{t('th.type')}</th>
              <th>{t('th.assignment')}</th>
              <th>{t('th.manufacturer')}</th>
              <th>{t('th.firmware')}</th>
              <th>{t('th.status')}</th>
              <th>{t('th.connected')}</th>
              <th>{t('th.last_heartbeat')}</th>
              <th>{t('th.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {meters.length === 0 ? (
              <tr>
                <td colSpan={selectMode ? 10 : 9} style={{ textAlign: 'center', color: c.textMuted, padding: '24px' }}>
                  ⏳ {t('meters.waiting')}
                </td>
              </tr>
            ) : filteredMeters.length === 0 ? (
              <tr>
                <td colSpan={selectMode ? 10 : 9} style={{ textAlign: 'center', color: c.textMuted, padding: '24px' }}>
                  {t('meters.no_match_filter', { filter })}
                  {filter !== 'all' && (
                    <> <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); setFilter('all'); nav.setMeterFilter('all'); }}
                      style={{ color: c.accent }}
                    >{t('meters.show_all')}</a>.</>
                  )}
                </td>
              </tr>
            ) : (
              pageMeters.map((m, rowIdx) => {
                const a = assignmentBySerial[m.serial];
                const rowSerial = serialOf(m);
                const ticked = selectMode && selectedSerials.has(rowSerial);
                return (
                <React.Fragment key={m.serial}>
                <tr
                  onClick={() => {
                    setSelected(m.serial);
                    // Jump into the Customers hierarchy tab, which
                    // auto-selects the matching meter node via a
                    // MeterContext effect and renders MeterDetailView
                    // in the detail column.
                    if (a) nav.setActiveTab('👥 Customers');
                  }}
                  style={{
                    cursor: 'pointer',
                    background: ticked ? c.bgCardAlt : undefined,
                  }}
                >
                  {selectMode && (
                    /* stopPropagation so ticking a box never also fires
                       the row's navigate-to-detail handler */
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={ticked}
                        onChange={() => {}}
                        onClick={(e) => toggleRow(rowIdx, e.shiftKey)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                  )}
                  <td>
                    {/* Nameplate Device-ID is the facing serial when known;
                        the LDN (operational key) shows muted alongside. */}
                    <code>{m.device_id_serial || m.serial}</code>
                    {m.device_id_serial && m.device_id_serial !== m.serial && (
                      <code style={{ color: c.textMuted, marginLeft: '6px', fontSize: '10px' }}>
                        ({m.serial})
                      </code>
                    )}
                    {m.dcu_provisioned === false && (
                      <span
                        title="Assigned under a DCU but not registered on it — won't collect data until you Register it on the DCU."
                        style={{ marginLeft: '8px', fontSize: '10px', color: c.warning, fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        ⚠ not on DCU
                      </span>
                    )}
                  </td>
                  <td><MeterTypeBadge mt={m.meter_type} compact /></td>
                  <td style={{ fontSize: '11px' }}>
                    {a ? (
                      <span style={{ color: c.success }}>
                        ✓ {a.object_name || a.meter_serial}
                      </span>
                    ) : (
                      <span style={{ color: c.warning }}>⚠ {t('meters.unassigned')}</span>
                    )}
                  </td>
                  <td>{m.manufacturer || '-'}</td>
                  <td><code style={{ fontSize: '11px' }}>{(m.firmware || '').slice(0, 24)}</code></td>
                  {/* A meter that has NEVER dialled in reads "never seen", not
                      "offline". Offline means it has been here and will likely
                      be back; never-seen means nothing server-side can help and
                      it needs a field check. Showing both as "offline" hides
                      the only one that needs someone to act. */}
                  <td><StatusBadge status={m.never_connected ? 'never seen' : m.status} /></td>
                  <td style={{ fontSize: '11px' }}>
                    {m.connected_since ? fmtDT(m.connected_since, { utc: true, seconds: true }) : '-'}
                  </td>
                  <td style={{ fontSize: '11px' }}>
                    {m.last_heartbeat ? fmtDT(m.last_heartbeat, { utc: true, seconds: true }) : '-'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* Same rule as the Live TAB in meter detail: the poller
                        cannot reach a meter behind a DCU (no per-meter
                        session — the DCU holds the link), so this panel
                        would refresh an empty table every 5 s. Reading such
                        a meter live is the On Demand tab's SAP path, one
                        register at a time and operator-initiated. */}
                    {!m._via_dcu && (
                      <button
                        className="button button-sm button-primary"
                        onClick={() => { setSelected(m.serial); setLiveMeter(m.serial); }}
                        title="Show live readings"
                      >
                        📡 Live
                      </button>
                    )}
                    <button
                      className="button button-sm button-secondary"
                      onClick={() => handleOpenSession(m.serial)}
                      disabled={busy === m.serial || m.status === 'session open'}
                      style={{ marginLeft: '4px' }}
                      title="Open GMAC secure session"
                    >
                      {busy === m.serial ? '…' : t('btn.open_session')}
                    </button>
                    <button
                      className="button button-sm button-secondary"
                      onClick={() => handleCloseSession(m.serial)}
                      disabled={busy === m.serial || m.status !== 'session open'}
                      style={{ marginLeft: '4px' }}
                      title={t('meters.release_session')}
                    >
                      {t('btn.close_session')}
                    </button>
                    <button
                      className="button button-sm button-danger"
                      onClick={() => handleRelay(m.serial, 'disconnect')}
                      disabled={m.status !== 'session open'}
                      style={{ marginLeft: '4px' }}
                      title="Disconnect the meter relay"
                    >
                      ⚡ Disc
                    </button>
                    <button
                      className="button button-sm button-success"
                      onClick={() => handleRelay(m.serial, 'reconnect')}
                      disabled={m.status !== 'session open'}
                      style={{ marginLeft: '4px' }}
                      title="Reconnect the meter relay"
                    >
                      ⚡ Conn
                    </button>
                    {!a && !m._offline && (
                      <button
                        className="button button-sm button-primary"
                        onClick={(e) => { e.stopPropagation(); nav.setActiveTab('👥 Customers'); }}
                        style={{ marginLeft: '4px' }}
                        title="Claim this unassigned meter via the Customers tab"
                      >
                        🔗 Assign
                      </button>
                    )}
                    {/*
                     * Admin-only Purge button. Shows for:
                     *   - offline direct meters with an assignment
                     *   - ANY via-DCU meter whose session isn't open
                     *     (those won't trip m._offline because the DCU
                     *     keeps them "online" in the API response)
                     *   - any meter that's no longer identifying
                     * Calls the consolidated /purge endpoint which clears
                     * MeterAssignment + meter_dcu_map + live CAS entry +
                     * known-devices file in one round-trip.
                     */}
                    {isAdmin && (
                      (m._offline && a) ||
                      (m._via_dcu && !m.session_open) ||
                      (!m.identified && !m.session_open)
                    ) && (
                      <button
                        className="button button-sm"
                        style={{ marginLeft: '4px', background: '#7f1d1d', color: '#fca5a5', border: 'none' }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const name = a?.object_name || m.serial;
                          if (!(await confirm(
                            'Purge meter',
                            `Permanently remove ${name}?\n\n` +
                            `This clears the assignment, DCU routing, live pool entry and identity cache.\n` +
                            `Stored readings (SQLite profile_rows) are kept.`,
                            { confirmLabel: 'Purge', confirmColor: '#dc2626' },
                          ))) return;
                          try {
                            const r = await api(`/meters/${m.serial}/purge`, { method: 'DELETE' });
                            const bits = [];
                            if (r.assignment_removed) bits.push('assignment');
                            if (r.map_removed) bits.push(`${r.map_keys_removed?.length || 0} map entries`);
                            if (r.live_removed) bits.push('live pool');
                            if (r.known_removed) bits.push('identity cache');
                            toast.push(
                              bits.length ? `Purged ${m.serial}: cleared ${bits.join(' + ')}`
                                          : `Purged ${m.serial} (nothing to clean)`,
                              'success',
                            );
                            if (r.errors?.length) {
                              toast.push(`Warnings: ${r.errors.join('; ')}`, 'warning');
                            }
                          } catch (err) {
                            toast.push(err.message, 'error');
                          }
                        }}
                        title="Permanently purge this meter from the system"
                      >
                        🗑 Purge
                      </button>
                    )}
                  </td>
                </tr>
                {liveMeter === m.serial && (
                  <tr>
                    <td colSpan={selectMode ? 10 : 9} style={{ padding: 0, background: c.bgCardAlt, borderTop: `1px solid ${c.border}` }}>
                      <LiveReadingsPanel
                        serial={m.serial}
                        onClose={() => setLiveMeter(null)}
                        showObis={true}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '8px' }}>{renderPaginationControls()}</div>

      <div style={{ marginTop: '8px', fontSize: '12px', color: c.textSecondary }}>
        <strong>Open</strong> requests an HLS5/GMAC secure session so we can read registers.
        Click <strong>📡 Live</strong> on any meter to see live voltage/current/power/energy
        polled every 60 seconds. Unassigned meters show a <strong>🔗 Assign</strong> shortcut.
        {selectMode && <> On the <strong>Unassigned</strong> filter you can tick several meters
        (Shift+click for a range, or <strong>Select all</strong>) and assign the whole batch
        to one transformer in a single step.</>}
      </div>

      {bulkOpen && bulkTargets && (
        <BulkAssignMetersModal
          serials={Array.from(selectedSerials)}
          transformers={bulkTargets.transformers}
          customers={bulkTargets.customers}
          onClose={() => setBulkOpen(false)}
          onSaved={(r) => {
            setBulkOpen(false);
            // Keep any serial the server skipped still ticked, so the
            // operator can see and retry exactly what didn't land.
            const done = new Set(r.assigned || []);
            setSelectedSerials(prev => new Set(
              Array.from(prev).filter(s => !done.has(s))
            ));
            setAnchorIdx(null);
            refreshAssignments({ force: true }).catch(() => {});
          }}
        />
      )}
    </div>
  );
}

// Live readings panel — polls /api/meters/{serial}/latest every 5s and also
// listens on /ws/readings for instant pushes from the backend poller.
// ────────────────────────────────────────────────────────────────────
// CT/VT ratio helpers — DOCUMENTATION ONLY, no client-side scaling.
//
// IMPORTANT: Holley CAS meters (and most utility-grade three-phase
// meters with CT/VT support) have their CT and VT ratios programmed
// *into the meter itself* during commissioning. The meter internally
// multiplies and reports PRIMARY-SIDE values through the standard
// OBIS registers. Reading 1.0.1.8.0.255 on our test meter returns
// 7816.98 kWh — the same cumulative number printed on the LCD.
//
// An earlier version of this file applied a client-side multiplier
// of CT × VT to V/I/P/E codes, which double-scaled the values. The
// LCD shows 7816.98 kWh, the API returns 7816.98 kWh, and that's
// the truth — don't multiply again.
//
// We keep ``parseRatio`` and ``obisScaleMultiplier`` here as named
// no-ops in case a future non-Holley meter actually reports
// secondary-side values and we need a per-vendor toggle. For now
// the multiplier is hard-wired to 1 and the assignment's CT/VT
// fields are surfaced in the UI as informational metadata only.
// ────────────────────────────────────────────────────────────────────
// A CT/VT ratio is routinely written WITH its unit — "200/5A" is the standard
// nameplate notation — and the column mixes Cyrillic "А" (U+0410) with Latin
// "A" (U+0041), because the sheets are edited in Russian-locale Excel and the
// two glyphs are identical on screen. The old pattern anchored on \d+$ and so
// matched neither, returning 1 and silently discarding the ratio: 220 of
// Промзона's 238 meters looked unscaled and `isPrimary` stayed false, which is
// what made a correct bulk upload look like it had not applied.
// Keep this in step with _parse_ratio in api_server.py.
function parseRatio(str) {
  if (str === null || str === undefined) return 1;
  const txt = String(str).trim();
  if (!txt) return 1;
  const side = (s) => {
    const m = String(s).match(/^\s*(\d+(?:[.,]\d+)?)/);
    return m ? parseFloat(m[1].replace(',', '.')) : null;
  };
  const sep = txt.indexOf('/') >= 0 ? '/' : (txt.indexOf(':') >= 0 ? ':' : null);
  if (sep === null) {
    const only = side(txt);
    return (only !== null && Number.isFinite(only)) ? only : 1;
  }
  const i = txt.indexOf(sep);
  const num = side(txt.slice(0, i));
  const den = side(txt.slice(i + 1));
  if (num === null || den === null || !den || !Number.isFinite(num)) return 1;
  return num / den;
}

// Hard-wired no-op. The meter has already applied the ratios.
function obisScaleMultiplier(_obis, _assignment) {
  return 1;
}

// Compute the CT × VT coefficient from a MeterAssignment row.
// Returns 1 for direct-connected meters (5/5 CT, no VT) or when
// the ratios are missing. Used by the CT/VT toggle in Meter
// Readings to let operators see primary-side vs secondary-side.
//
// Offered CT ratios: 5/5 through 5000/5, plus 400/1 (CT_RATIOS).
// Offered VT ratios: 6000/100, 10000/100, 35000/100, 40000/100 (VT_RATIOS).
// Example: CT 100/5 × VT 10000/100 = 20 × 100 = 2000.
function computeCTVTMultiplier(assignment) {
  if (!assignment) return 1;
  const ct = parseRatio(assignment.ct_ratio);
  const vt = parseRatio(assignment.vt_ratio);
  return ct * vt;
}

function LiveReadingsPanel({ serial, onClose, showObis = false }) {
  const c = useThemeColors();
  const t = useT();
  const [readings, setReadings] = useState([]);
  const [poller, setPoller] = useState(null);
  const [error, setError] = useState(null);
  const { assignments } = useAssignments();
  const assignment = React.useMemo(
    () => assignments.find(x => x.meter_serial === serial) || null,
    [assignments, serial],
  );
  const { messages: wsReadings } = useWebSocket('/ws/readings');

  // Friendly labels for the OBIS codes the poller collects by default.
  const OBIS_LABELS = {
    "1.0.32.7.0.255": ["Voltage L1", "V"],
    "1.0.52.7.0.255": ["Voltage L2", "V"],
    "1.0.72.7.0.255": ["Voltage L3", "V"],
    "1.0.31.7.0.255": ["Current L1", "A"],
    "1.0.51.7.0.255": ["Current L2", "A"],
    "1.0.71.7.0.255": ["Current L3", "A"],
    "1.0.90.7.0.255": ["Current N",  "A"],
    "1.0.1.7.0.255":  ["Active Power +", "W"],
    "1.0.2.7.0.255":  ["Active Power -", "W"],
    "1.0.3.7.0.255":  ["Reactive Power +", "var"],
    "1.0.4.7.0.255":  ["Reactive Power -", "var"],
    "1.0.9.7.0.255":  ["Apparent Power +", "VA"],
    "1.0.10.7.0.255": ["Apparent Power -", "VA"],
    "1.0.13.7.0.255": ["Power Factor", ""],
    "1.0.14.7.0.255": ["Frequency", "Hz"],
    "1.0.1.8.0.255":  ["Active Energy Import", "Wh"],
    "1.0.2.8.0.255":  ["Active Energy Export", "Wh"],
  };

  const refresh = useCallback(async () => {
    try {
      const data = await api(`/meters/${serial}/latest`);
      setReadings(data.readings || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load readings');
    }
  }, [serial]);

  // Periodic refresh + initial load
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  // Re-fetch immediately when a fresh /ws/readings event arrives for this meter
  useEffect(() => {
    if (wsReadings.length === 0) return;
    const latest = wsReadings[0];
    if (latest && latest.serial === serial) {
      refresh();
    }
  }, [wsReadings, serial, refresh]);

  // Get poller status (so we know if it's healthy / when next attempt is)
  useEffect(() => {
    const fetchPoller = async () => {
      try {
        const data = await api('/poller/status');
        setPoller(data);
      } catch (err) {
        // ignore
      }
    };
    fetchPoller();
    const t = setInterval(fetchPoller, 5000);
    return () => clearInterval(t);
  }, []);

  // Group readings by category for display.
  // Values are already scaled by the meter's own scaler-unit (poller
  // applies that before storing). Here we additionally apply the
  // CT/VT primary-side multiplier from the meter's assignment, so
  // operators see real primary values instead of secondary-side
  // numbers. The multiplier is 1 for direct-connected meters.
  // For energy in Wh we render kWh when the value is large enough.
  const grouped = { "Instantaneous": [], "Energy": [], "Other": [] };
  const ctvtMult = (obis) => obisScaleMultiplier(obis, assignment);
  const isPrimary = assignment && (parseRatio(assignment.ct_ratio) !== 1 || parseRatio(assignment.vt_ratio) !== 1);
  for (const r of readings) {
    const meta = OBIS_LABELS[r.obis];
    const label = meta ? meta[0] : r.obis;
    // Prefer the unit from the meter; fall back to the hardcoded label unit
    let unit = r.unit || (meta ? meta[1] : '');
    const num = r.value_num;
    let displayNum = num;
    // Apply primary-side CT/VT scaling
    const mult = ctvtMult(r.obis);
    if (typeof displayNum === 'number' && mult !== 1) {
      displayNum = displayNum * mult;
    }
    // Auto-promote large Wh / VAh / varh totals to kilo-units
    if (typeof displayNum === 'number' && Math.abs(displayNum) >= 1000 &&
        ['Wh', 'VAh', 'varh'].includes(unit)) {
      displayNum = displayNum / 1000;
      unit = 'k' + unit;
    }
    const display = displayNum !== null && displayNum !== undefined
      ? (Number.isInteger(displayNum)
          ? displayNum.toLocaleString()
          : displayNum.toLocaleString(undefined, { maximumFractionDigits: 3 }))
      : r.value;
    const ts = r.ts ? fmtDT(r.ts, { seconds: true }) : '-';
    const item = { obis: r.obis, label, unit, value: display, ts, scaled: mult !== 1 };
    if (meta && (r.obis.startsWith('1.0.1.8') || r.obis.startsWith('1.0.2.8'))) {
      grouped.Energy.push(item);
    } else if (meta) {
      grouped.Instantaneous.push(item);
    } else {
      grouped.Other.push(item);
    }
  }

  const failureCount = poller?.failure_counts?.[serial] || 0;
  const nextAttempt = poller?.next_attempt_at?.[serial];
  const nextAttemptStr = nextAttempt
    ? fmtDT(nextAttempt, { seconds: true })
    : null;

  // Live/offline badge. Meter is "live" when we have readings AND
  // the poller isn't currently stuck in a failure streak for this
  // serial. Poller-status 404 (unknown serial) also counts as offline.
  const hasReadings = readings.length > 0;
  const isLive = hasReadings && failureCount === 0 && !error;
  const badgeBg = isLive ? '#22c55e' : '#ef4444';
  const badgeLabel = isLive ? t('badge.live') : t('badge.offline');

  return (
    <div className="action-panel" style={{ marginTop: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="section-title" style={{ margin: 0 }}>📡 {t('section.live_readings')} — {serial}</div>
          <span style={{
            padding: '3px 10px', borderRadius: '12px',
            background: `${badgeBg}22`, border: `1px solid ${badgeBg}66`,
            color: badgeBg, fontSize: '11px', fontWeight: 700,
            letterSpacing: '0.3px',
          }}>
            {badgeLabel}
          </span>
        </div>
        {typeof onClose === 'function' && (
          <button className="button button-sm button-secondary" onClick={onClose}>
            {t('btn.close')}
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: '8px 12px', background: '#3b1818', color: '#fca5a5',
                      borderRadius: '6px', marginTop: '8px', fontSize: '13px' }}>
          ⚠️ {error}
        </div>
      )}

      {readings.length === 0 && !error && (
        <div style={{ padding: '12px', color: c.textSecondary, fontSize: '13px' }}>
          {t('msg.no_readings_yet')}
          {failureCount > 0 && (
            <> Poller has {failureCount} consecutive failures
              {nextAttemptStr && <> (next attempt at {nextAttemptStr})</>}.
              {failureCount > 0 && (
                <div style={{ marginTop: '6px', color: c.warning }}>
                  Most likely cause: meter keys not loaded. Upload your keyfile
                  on the Keys tab.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {readings.length > 0 && (
        <div style={{ marginTop: '12px' }}>
          {Object.entries(grouped).map(([groupName, items]) => {
            if (items.length === 0) return null;
            // The "Other" bucket collects registers whose OBIS isn't
            // in our friendly-label dict — typically device-info
            // metadata (serial, firmware, LDN, manufacturer etc.).
            // Operators in the customer view don't need that noise;
            // only admins browsing Inbound Meters (showObis=true)
            // see it for diagnostics.
            if (groupName === 'Other' && !showObis) return null;
            return (
              <div key={groupName} style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', textTransform: 'uppercase',
                              color: c.textSecondary, marginBottom: '6px' }}>
                  {t(`group.${groupName.toLowerCase()}`) || groupName}
                </div>
                <div style={{ display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                              gap: '8px' }}>
                  {items.map(item => (
                    <div key={item.obis} style={{
                      padding: '10px 12px',
                      background: c.bgElevated,
                      borderRadius: '6px',
                      border: `1px solid ${c.borderInput}`,
                    }}>
                      <div style={{ fontSize: '11px', color: c.textSecondary }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '18px', color: c.accent, marginTop: '4px' }}>
                        {item.value} <span style={{ fontSize: '12px', color: c.textMuted }}>{item.unit}</span>
                      </div>
                      <div style={{ fontSize: '10px', color: c.textDimmed, marginTop: '2px' }}>
                        {showObis ? `${item.obis} · ${item.ts}` : item.ts}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Connection Tab
function ConnectionTab() {
  const c = useThemeColors();
  const [formData, setFormData] = useState({
    host: 'localhost',
    port: 4059,
    serial: '',
    ldName: '',
    sapChoice: 'Public',
    encryption: true,
    authKey: '',
    cipherKey: ''
  });
  const [connected, setConnected] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="tab-content">
      <div className="form-group">
        <label className="input-label">
          Host
          <input
            type="text"
            className="input-field"
            value={formData.host}
            onChange={(e) => handleChange('host', e.target.value)}
          />
        </label>
        <label className="input-label">
          Port
          <input
            type="number"
            className="input-field"
            value={formData.port}
            onChange={(e) => handleChange('port', e.target.value)}
          />
        </label>
        <label className="input-label">
          Serial Number
          <input
            type="text"
            className="input-field"
            value={formData.serial}
            onChange={(e) => handleChange('serial', e.target.value)}
          />
        </label>
        <label className="input-label">
          Logical Device Name
          <input
            type="text"
            className="input-field"
            value={formData.ldName}
            onChange={(e) => handleChange('ldName', e.target.value)}
          />
        </label>
      </div>

      <div className="form-group">
        <label className="input-label">
          Client SAP
          <select
            className="input-field"
            value={formData.sapChoice}
            onChange={(e) => handleChange('sapChoice', e.target.value)}
          >
            <option>Public</option>
            <option>Read</option>
            <option>Management</option>
          </select>
        </label>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={formData.encryption}
            onChange={(e) => handleChange('encryption', e.target.checked)}
          />
          <span>Use Encryption</span>
        </label>
      </div>

      {formData.encryption && (
        <div className="form-group">
          <label className="input-label">
            Auth Key (Hex)
            <input
              type="text"
              className="input-field"
              placeholder="000102030405060708090a0b0c0d0e0f"
              value={formData.authKey}
              onChange={(e) => handleChange('authKey', e.target.value)}
            />
          </label>
          <label className="input-label">
            Block Cipher Key (Hex)
            <input
              type="text"
              className="input-field"
              placeholder="000102030405060708090a0b0c0d0e0f"
              value={formData.cipherKey}
              onChange={(e) => handleChange('cipherKey', e.target.value)}
            />
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <button className="button button-primary" onClick={() => setConnected(!connected)}>
          {connected ? '🔌 Disconnect' : '🔌 Connect'}
        </button>
      </div>

      <div style={{
        padding: '16px',
        background: c.bgCardAlt,
        borderRadius: '8px',
        border: `1px solid ${connected ? '#22c55e' : '#ef4444'}`,
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: connected ? '#22c55e' : '#ef4444',
          animation: connected ? 'pulse 2s infinite' : 'none'
        }} />
        <div>
          <strong>{connected ? 'Connected' : 'Disconnected'}</strong>
          {connected && <div style={{ fontSize: '12px', color: c.textSecondary }}>Session: active</div>}
        </div>
      </div>
    </div>
  );
}

// Read Meter Tab — runs single OBIS reads or group reads against the selected
// inbound meter via /api/meters/{serial}/read[/{obis} | -group/{group}].
function ReadMeterTab() {
  const { selected } = useMeterSelection();
  const toast = useToast();
  const t = useT();
  const [readType, setReadType] = useState('group');
  const [obisCode, setObisCode] = useState('0.0.96.1.0.255');
  const [groupName, setGroupName] = useState('instantaneous');
  const [attribute, setAttribute] = useState(2);
  const [groups, setGroups] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api('/groups').then(setGroups).catch(() => {});
  }, []);

  const handleRead = async () => {
    if (!selected) {
      toast.push(t('toast.pick_meter_first'), 'error');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      let data;
      if (readType === 'obis') {
        data = await api(`/meters/${selected}/read/${obisCode}?attribute=${attribute}`);
        setResult({ kind: 'obis', data });
      } else {
        data = await api(`/meters/${selected}/read-group/${groupName}`);
        setResult({ kind: 'group', data });
      }
      toast.push(t('toast.read_complete'), 'success');
    } catch (err) {
      toast.push(`Read failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="section-title">📖 {t('section.read_meter')}</div>
      <MeterSelector requireSession={true} />

      <div className="form-group">
        <label className="input-label">
          Read Type
          <select
            className="input-field"
            value={readType}
            onChange={(e) => setReadType(e.target.value)}
          >
            <option value="group">Read group (multiple OBIS at once)</option>
            <option value="obis">Single OBIS code</option>
          </select>
        </label>
      </div>

      {readType === 'group' ? (
        <div className="form-group">
          <label className="input-label">
            Group
            <select
              className="input-field"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            >
              {groups.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="form-group">
          <label className="input-label">
            OBIS Code
            <input
              type="text"
              className="input-field"
              value={obisCode}
              onChange={(e) => setObisCode(e.target.value)}
              placeholder="0.0.96.1.0.255"
            />
          </label>
          <label className="input-label">
            Attribute
            <input
              type="number"
              className="input-field"
              value={attribute}
              onChange={(e) => setAttribute(parseInt(e.target.value) || 2)}
            />
          </label>
        </div>
      )}

      <button
        className="button button-primary"
        onClick={handleRead}
        disabled={busy || !selected}
      >
        {busy ? <Spinner /> : '📖 Read'}
      </button>

      {result && result.kind === 'obis' && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table>
            <thead>
              <tr><th>{t('th.obis')}</th><th>{t('th.value')}</th><th>{t('th.timestamp')}</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><code>{result.data.obis_code}</code></td>
                <td><strong>{String(result.data.value)}</strong></td>
                <td style={{ fontSize: '11px' }}>{fmtDT(result.data.timestamp, { utc: true, seconds: true })}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {result && result.kind === 'group' && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table>
            <thead>
              <tr><th>{t('th.obis')}</th><th>{t('th.value')}</th></tr>
            </thead>
            <tbody>
              {Object.entries(result.data.results || {}).map(([obis, val]) => (
                <tr key={obis}>
                  <td><code>{obis}</code></td>
                  <td>{val === null ? '—' : <strong>{String(val)}</strong>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Write Meter Tab — writes one OBIS value on the selected inbound meter via
// POST /api/meters/{serial}/write.
function WriteMeterTab() {
  const c = useThemeColors();
  const { selected } = useMeterSelection();
  const toast = useToast();
  const t = useT();
  const [formData, setFormData] = useState({
    obis_code: '0.0.1.0.0.255',
    value: '',
    data_type: 'string',
    attribute: 2,
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const presets = {
    'Set Clock to now': { obis_code: '0.0.1.0.0.255', data_type: 'datetime', value: new Date().toISOString() },
    'Set APN': { obis_code: '0.0.25.4.0.255', data_type: 'string', value: '' },
    'Set Server IP': { obis_code: '0.0.96.12.4.255', data_type: 'string', value: '' },
  };

  const handlePreset = (presetName) => {
    setFormData(prev => ({ ...prev, ...presets[presetName] }));
  };

  const handleWrite = async () => {
    if (!selected) {
      toast.push(t('toast.pick_meter_first'), 'error');
      return;
    }
    setBusy(true);
    try {
      await api(`/meters/${selected}/write`, {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      toast.push(`Wrote ${formData.obis_code} on ${selected}`, 'success');
      setShowConfirm(false);
    } catch (err) {
      toast.push(`Write failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tab-content">
      <div className="section-title">✏️ {t('section.write_meter')}</div>
      <MeterSelector requireSession={true} />

      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: c.textSecondary, marginBottom: '8px' }}>Quick Presets:</div>
        <div className="quick-actions">
          {Object.keys(presets).map(key => (
            <button
              key={key}
              className="button button-secondary button-sm"
              onClick={() => handlePreset(key)}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      <div className="section-divider" />

      <div className="form-group">
        <label className="input-label">
          OBIS Code
          <input
            type="text"
            className="input-field"
            value={formData.obis_code}
            onChange={(e) => setFormData(prev => ({ ...prev, obis_code: e.target.value }))}
          />
        </label>
        <label className="input-label">
          Value
          <input
            type="text"
            className="input-field"
            value={formData.value}
            onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
          />
        </label>
        <label className="input-label">
          Data Type
          <select
            className="input-field"
            value={formData.data_type}
            onChange={(e) => setFormData(prev => ({ ...prev, data_type: e.target.value }))}
          >
            <option>string</option>
            <option>int</option>
            <option>uint</option>
            <option>hex</option>
            <option>datetime</option>
            <option>boolean</option>
            <option>octet-string</option>
          </select>
        </label>
        <label className="input-label">
          Attribute
          <input
            type="number"
            className="input-field"
            value={formData.attribute}
            onChange={(e) => setFormData(prev => ({ ...prev, attribute: parseInt(e.target.value) || 2 }))}
          />
        </label>
      </div>

      <button
        className="button button-primary"
        onClick={() => setShowConfirm(true)}
        disabled={busy || !selected || !formData.value}
      >
        ✏️ Write
      </button>

      {showConfirm && (
        <ConfirmDialog
          title="Confirm Write"
          message={`Write '${formData.value}' to OBIS ${formData.obis_code} on ${selected}?`}
          onConfirm={handleWrite}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}

// Load Curve Tab — pulls historical readings from /api/meters/{serial}/history
// for the active power import OBIS, plots them.
function LoadCurveTab() {
  const { selected } = useMeterSelection();
  const toast = useToast();
  const t = useT();
  const [obis, setObis] = useState('1.0.1.7.0.255'); // Active Power +
  const [hours, setHours] = useState(24);
  const [data, setData] = useState([]);
  const [busy, setBusy] = useState(false);

  const handleLoad = async () => {
    if (!selected) {
      toast.push(t('toast.pick_meter_first'), 'error');
      return;
    }
    setBusy(true);
    try {
      const fromTs = Date.now() / 1000 - hours * 3600;
      const result = await api(
        `/meters/${selected}/history?obis=${obis}&from_ts=${fromTs}&limit=2000`
      );
      // Sort oldest → newest for charting
      const rows = (result.readings || []).sort((a, b) => a.ts - b.ts);
      setData(rows);
      toast.push(`Loaded ${rows.length} samples`, 'success');
    } catch (err) {
      toast.push(`Load failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const generateChartSVG = () => {
    if (data.length === 0) return null;
    const values = data.map(d => d.value_num || 0);
    const maxVal = Math.max(...values, 1);
    const minVal = Math.min(...values, 0);
    const range = maxVal - minVal || 1;
    const height = 300;
    const width = 800;
    const padding = 40;
    const plotHeight = height - 2 * padding;
    const plotWidth = width - 2 * padding;

    const points = values.map((v, i) => {
      const x = padding + (i / Math.max(values.length - 1, 1)) * plotWidth;
      const y = height - padding - ((v - minVal) / range) * plotHeight;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg className="svg-chart" viewBox={`0 0 ${width} ${height}`} style={{ marginTop: '20px' }}>
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00e5ff" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#00e5ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => (
          <line
            key={`grid-${i}`}
            x1={padding}
            y1={height - padding - frac * plotHeight}
            x2={width - padding}
            y2={height - padding - frac * plotHeight}
            stroke="#1e293b"
            strokeWidth="1"
            strokeDasharray="5,5"
          />
        ))}
        <polygon
          points={`${padding},${height - padding} ${points} ${width - padding},${height - padding}`}
          fill="url(#grad)"
        />
        <polyline points={points} fill="none" stroke="#00e5ff" strokeWidth="2" />
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#94a3b8" strokeWidth="1" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#94a3b8" strokeWidth="1" />
        <text x={padding + 4} y={padding + 12} fill="#94a3b8" fontSize="10">
          max: {maxVal.toLocaleString()}
        </text>
        <text x={padding + 4} y={height - padding - 4} fill="#94a3b8" fontSize="10">
          min: {minVal.toLocaleString()}
        </text>
      </svg>
    );
  };

  return (
    <div className="tab-content">
      <div className="section-title">📈 {t('section.load_curve')}</div>
      <MeterSelector requireSession={false} />

      <div className="form-group">
        <label className="input-label">
          OBIS code
          <select
            className="input-field"
            value={obis}
            onChange={(e) => setObis(e.target.value)}
          >
            <option value="1.0.1.7.0.255">Active power import (W)</option>
            <option value="1.0.32.7.0.255">Voltage L1 (V)</option>
            <option value="1.0.52.7.0.255">Voltage L2 (V)</option>
            <option value="1.0.72.7.0.255">Voltage L3 (V)</option>
            <option value="1.0.31.7.0.255">Current L1 (A)</option>
            <option value="1.0.14.7.0.255">Frequency (Hz)</option>
            <option value="1.0.1.8.0.255">Active energy import (Wh)</option>
          </select>
        </label>
        <label className="input-label">
          Hours back
          <input
            type="number"
            className="input-field"
            value={hours}
            onChange={(e) => setHours(parseInt(e.target.value) || 24)}
          />
        </label>
      </div>

      <button className="button button-primary" onClick={handleLoad} disabled={busy || !selected}>
        {busy ? <Spinner /> : '📊 Load history'}
      </button>

      {data.length > 0 && (
        <>
          {generateChartSVG()}
          <div className="table-container" style={{ marginTop: '20px' }}>
            <table>
              <thead>
                <tr><th>{t('th.timestamp')}</th><th>{t('th.value')}</th></tr>
              </thead>
              <tbody>
                {data.slice(-50).reverse().map((row, idx) => (
                  <tr key={idx}>
                    <td>{fmtDT(row.ts, { seconds: true })}</td>
                    <td>{row.value_num !== null ? row.value_num.toLocaleString() : row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// (EventsTab removed in Phase 4.1 — replaced by MeterDetailEventsTab.)

// ═══════════════════════════════════════════════════════════════════════
// Map Tab — Leaflet map of every transformer + assigned meter the
// user can see, with online/offline status colour coding.
//
// How it works
// ────────────
//   - Pulls /api/transformers + /api/assignments + /api/meters in
//     parallel. The first two are RBAC-scoped server-side; we cross-
//     reference them with the live meter list to get online status
//     per marker.
//   - Renders any transformer with both lat+lon as a square marker
//     (red if its assigned meter is offline, green if online).
//   - Renders any meter assignment with its own lat+lon as a circle
//     marker (same colouring).
//   - Default centre is Tashkent (41.31, 69.25) since that's where
//     our test meters live; we recenter to fit-bounds once any
//     marker exists.
//   - Clicking a marker pops a Leaflet popup with the customer +
//     transformer + meter details and an online indicator.
//
// Leaflet is loaded from CDN by index.html so we just touch
// `window.L` here. The map needs a sized container, so the wrapper
// div is given an explicit minHeight.
// ═══════════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────────
// MetersTab — container for the Inbound Meters + Map sub-tabs.
//
// ──────────────────────────────────────────────────────────────────
// Relay Control tab (admin-only). Modeled on the Holley AMI vendor
// portal: a paginated/filterable meter list (cached relay state — never
// live reads), multi-select bulk disconnect/reconnect with immediate or
// deferred delivery, and a commands/tasks panel showing attempts + status.
// All control flows through POST /api/relay/commands → per-DCU serialized
// queue. Gated by the EMETER_REMOTE_WRITES kill-switch.
// ──────────────────────────────────────────────────────────────────
// Reason codes are a CLOSED set fixed by the backend (relay_commands.reason_code
// is written verbatim from this list), so the code is the wire value and the
// label is looked up per-language at render time. Never send the label.
// control_mode values in which a remote reconnect only ARMS the relay and a
// manual action at the meter is needed to restore supply. Mirrors
// _RELAY_MODES_REMOTE_RECONNECT_ARMS_ONLY in api_server.py — CAS Meter Spec
// §15 Figure 3 enables the arming transition (a) in modes 2 and 4 only; modes
// 1/3/5/6 get (d), which closes the relay remotely. Kept here so the log can
// re-judge rows whose verdict was stored by the older, wrong rule.
const RELAY_ARMING_MODES = [2, 4];

const RELAY_REASONS = [
  { code: 'debt', key: 'relay.reason_debt' },
  { code: 'debt_paid', key: 'relay.reason_debt_paid' },
  { code: 'maintenance', key: 'relay.reason_maintenance' },
  { code: 'tamper', key: 'relay.reason_tamper' },
  { code: 'other', key: 'relay.reason_other' },
];

// Translate a reason_code that came back from the API. Codes written by older
// builds (or by a future backend) may not be in RELAY_REASONS — show the raw
// code rather than swallowing it, so an operator can still tell rows apart.
function relayReasonLabel(t, code) {
  if (!code) return '—';
  const r = RELAY_REASONS.find(x => x.code === code);
  return r ? t(r.key) : code;
}

function relayStatusBadge(c, t, status) {
  const map = {
    connected: ['🟢', c.success, 'relay.badge_on'],
    disconnected: ['🔴', c.danger, 'relay.badge_off'],
    unknown: ['⚪', c.textMuted, 'relay.badge_unknown'],
  };
  const [icon, color, key] = map[status] || map.unknown;
  return (
    <span style={{ color, fontWeight: 600, whiteSpace: 'nowrap' }}>{icon} {t(key)}</span>
  );
}

function fmtRelayTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString();
}

// Render a NAIVE-UTC ISO timestamp (how last_seen is stored fleet-wide,
// no trailing Z) in the meter timezone. Two traps this avoids:
//   * `new Date('2026-08-23T19:26:32')` parses as BROWSER-LOCAL, so an
//     operator abroad would read a DCU's death several hours out.
//   * `.toLocaleString()` follows the browser too. Every operator-facing
//     time in this app is Tashkent, so it is pinned, not inferred.
function fmtMeterTzUtcIso(iso) {
  if (!iso) return null;
  const d = new Date(/[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d)) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: METER_TZ, hour12: false,
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).format(d).replace(',', '') + ' Tashkent';
}

// How long ago, coarse. "down 14.8 h" is the number an operator acts on.
function fmtAgeSince(iso) {
  if (!iso) return null;
  const d = new Date(/[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d)) return null;
  const h = (Date.now() - d.getTime()) / 3600000;
  if (h < 0) return null;
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} days`;
}

// Shown in the DCU Status sub-tab when the unit is not dialled in.
// Before this existed the pane rendered NOTHING in that case (the panel
// was guarded on a truthy payload and the fetch 404'd), so a dead DCU
// looked identical to a still-loading one.
function DcuOfflineNotice({ status }) {
  const c = useThemeColors();
  const seen = fmtMeterTzUtcIso(status.last_seen);
  const age = fmtAgeSince(status.last_seen);
  return (
    <div style={{
      padding: '14px 16px', borderRadius: '8px',
      background: '#ef444418', border: '1px solid #ef4444',
    }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: '#ef4444', marginBottom: '8px' }}>
        DCU offline — not dialled in
      </div>
      <div style={{ fontSize: '12px', color: c.textPrimary }}>
        {seen ? `Last seen ${seen}${age ? ` (${age} ago)` : ''}`
              : 'No connection has ever been recorded for this device.'}
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '8px', lineHeight: 1.5 }}>
        Signal, storage and status word are read once at connect time and
        cached; they are only available while the unit is connected.
        Nothing here contacts the device.
      </div>
    </div>
  );
}

// Per-meter eMeter relay command history (system-of-record): the
// disconnect/reconnect commands WE issued, with reason + operator +
// attempts. Rendered inside the meter-detail Relay → History sub-tab,
// below the meter's own on-board event log.
function RelayCommandHistory({ serial }) {
  const c = useThemeColors();
  const t = useT();
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api(`/meters/${serial}/relay-history?limit=100`)
      .then(d => { if (alive) setRows(d.commands || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [serial]);
  if (rows === null) return null;
  const th = { textAlign: 'left', padding: '4px 6px', color: c.textSecondary, borderBottom: `1px solid ${c.border}` };
  const td = { padding: '4px 6px', borderBottom: `1px solid ${c.border}` };
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '8px' }}>
        🎛 {t('rlog.cmd_history')}
      </div>
      {rows.length === 0 ? (
        <div style={{ color: c.textDimmed, fontSize: '12px', fontStyle: 'italic' }}>
          {t('rlog.none_for_meter')}
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: '11px' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
              <th style={th}>{t('rlog.col_status')}</th>
              <th style={th}>{t('rlog.col_action')}</th>
              <th style={th}>{t('rlog.col_mode')}</th>
              <th style={th}>{t('rlog.col_reason')}</th>
              <th style={th}>{t('rlog.col_attempts')}</th>
              <th style={th}>{t('rlog.col_by')}</th>
              <th style={th}>{t('rlog.col_requested')}</th>
              <th style={th}>{t('rlog.col_confirmed')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(cmd => {
              const v = relayVerdict(cmd);
              return (
              <tr key={cmd.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                <td style={{ ...td, color: relayVerdictColor(c, v), fontWeight: 600 }}>
                  {relayVerdictText(t, v)}
                </td>
                <td style={td}>{cmd.action === 'disconnect' ? `🔴 ${t('relay.off')}` : `🟢 ${t('relay.on')}`}</td>
                <td style={td}>{t(cmd.mode === 'immediate' ? 'rlog.mode_immediate' : 'rlog.mode_deferred')}</td>
                <td style={td}>{relayReasonLabel(t, cmd.reason_code)}{cmd.remark ? ` (${cmd.remark})` : ''}</td>
                <td style={td}>{relayAttempts(cmd)}</td>
                <td style={td}>{cmd.requested_by || '—'}</td>
                <td style={{ ...td, color: c.textMuted }}>{fmtRelayTs(cmd.requested_at)}</td>
                <td style={{ ...td, color: c.textMuted }}>{fmtRelayTs(cmd.confirmed_at)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ONE verdict per command — what happened, and is it over?
//
// WHY THIS REPLACED THE OLD Result + Process PAIR. They were two independent
// functions reading the same row, and they could disagree. `relayVendorResult`
// consulted `last_error_class` BEFORE `status`; `relayVendorProcess` consulted
// `status` alone. But last_error_class is STICKY — the backend records the last
// error seen on any attempt and never clears it when the command later ends
// (production has 27 rows that are status=confirmed, result_detail=ok,
// last_error_class=plc_unreachable). So a command that hit a busy PLC once and
// was then superseded rendered as "Busy status" — a RETRYABLE word implying we
// are still trying — right next to "Finish", meaning we stopped. 30 of the 143
// rows in the log were mislabelled this way, including every cancelled command
// that had ever seen a busy PLC, which read as "Busy status" rather than
// "Cancelled". Reported by an operator against meter 121257315482, 2026-08-11.
//
// THE RULE: a terminal status IS the outcome, and nothing overrides it.
// last_error_class is consulted ONLY while the command is still open, where it
// genuinely explains why we are still waiting. Merging the two columns into one
// cell is what makes the contradiction unrepresentable rather than merely fixed.
//
// Returns { key, tone, open } — `key` is an i18n key (see 'rlog.v_*'), `open`
// is true while the command is still in flight. `raw` is set only for a status
// we do not recognise, so a future backend status shows verbatim instead of
// being silently forced into a bucket.
function relayVerdict(cmd) {
  const st = String(cmd?.status || '');
  const ec = String(cmd?.last_error_class || '');
  // Dry-run rows carry result_detail as "dry-run success" / "dry-run no_change"
  // (see api_server.py ~13553), so normalise the prefix off before matching.
  const rd = String(cmd?.result_detail || '').replace(/^dry-run\s+/, '');

  // ── Still open. Here last_error_class is the LIVE reason we are waiting,
  //    which is exactly what an operator needs to decide whether to wait.
  if (st === 'sending') return { key: 'rlog.v_sending', tone: 'info', open: true };
  if (st === 'pending') {
    if (ec === 'plc_unreachable')    return { key: 'rlog.v_busy',       tone: 'warn', open: true };
    if (ec === 'transport')          return { key: 'rlog.v_transport',  tone: 'warn', open: true };
    if (ec === 'offline')            return { key: 'rlog.v_offline',    tone: 'warn', open: true };
    if (ec === 'queue_backpressure') return { key: 'rlog.v_queue_busy', tone: 'warn', open: true };
    if (ec === 'restart')            return { key: 'rlog.v_requeued',   tone: 'warn', open: true };
    return { key: 'rlog.v_queued', tone: 'info', open: true };
  }

  // ── Terminal. The status is the outcome; last_error_class is history.
  if (st === 'confirmed' || st === 'dry_run') {
    // "No change" is confirmed WITHOUT anything having been sent: the pre-read
    // found the relay already in the requested state, so the command
    // short-circuited and attempts stayed 0. Collapsing it into "Success" made
    // the log self-contradictory — 0 attempts next to Success, reported by an
    // operator 2026-07-30. It is not a failure and not a dispatch; say so.
    if (rd === 'no_change') return { key: 'rlog.v_already', tone: 'muted', open: false };
    // The meter accepted the method, but its control_mode (2 or 5 per CAS Meter
    // Spec §15) means a remote reconnect only ARMS the relay — supply is not
    // restored until someone acts AT the meter. Showing "Success" here would
    // tell an operator the customer is back on when they are not.
    // "Armed" is a STORED verdict — result_detail was written at dispatch time,
    // so rows decided by the old rule keep saying "needs manual action at the
    // meter" forever. Four such rows exist from before 2026-08-11 16:49
    // Tashkent, all on control_mode 5, all wrong: 9 mode-5 reconnects since
    // then returned plain ok. Left alone they are a standing instruction to
    // send a technician to a meter that reconnected remotely.
    //
    // So re-derive it from the evidence the row itself carries rather than
    // trusting the stored word: arming is only possible where the spec enables
    // transition (a), i.e. control_mode 2 or 4. A row claiming "armed" with a
    // KNOWN non-arming mode is a stale verdict, and the command behind it
    // simply succeeded (rc=0, exactly like every other 'ok' row here).
    // The raw `armed_awaiting_manual` still shows on the detail line beneath,
    // so nothing is hidden — only the instruction is corrected. When the mode
    // is unknown we cannot second-guess it and the warning stands.
    if (rd === 'armed_awaiting_manual') {
      const cm = cmd?.pre_state?.control_mode;
      if (typeof cm === 'number' && !RELAY_ARMING_MODES.includes(cm)) {
        return { key: 'rlog.v_success', tone: 'ok', open: false };
      }
      return { key: 'rlog.v_armed', tone: 'warn', open: false };
    }
    // rc=0 but the post-read disagreed with what we asked for (api_server.py
    // ~12752). The device said yes and the relay did something else — the one
    // outcome an operator must never see filed under "Success".
    if (rd === 'drift_detected') return { key: 'rlog.v_drift', tone: 'bad', open: false };
    return { key: 'rlog.v_success', tone: 'ok', open: false };
  }
  if (st === 'cancelled')  return { key: 'rlog.v_cancelled',  tone: 'muted', open: false };
  if (st === 'superseded') return { key: 'rlog.v_superseded', tone: 'muted', open: false };
  if (st === 'expired')    return { key: 'rlog.v_expired',    tone: 'bad',   open: false };
  if (st === 'timeout')    return { key: 'rlog.v_timeout',    tone: 'bad',   open: false };
  if (st === 'failed') {
    if (ec === 'max_attempts') return { key: 'rlog.v_failed_max', tone: 'bad', open: false };
    return { key: 'rlog.v_failed', tone: 'bad', open: false };
  }
  return { key: '', tone: 'muted', open: false, raw: st || '—' };
}

// Verdict text + colour in one call. Kept separate from relayVerdict so the
// classification stays a pure function of the row and can be reasoned about
// without a React context in scope.
function relayVerdictText(t, v) { return v.raw || t(v.key); }
function relayVerdictColor(c, v) {
  return v.tone === 'ok' ? c.success
       : v.tone === 'warn' ? c.warning
       : v.tone === 'bad' ? c.danger
       : v.tone === 'info' ? c.accent
       : c.textMuted;
}

// "3/20" — the ceiling comes from the API (max_attempts) because it is
// env-tunable server-side (EMETER_RELAY_MAX_ATTEMPTS) and shipped hardcoded
// here as "/30" while the server used 20, so a dead command at 20/20 looked
// like it had ten tries left. 0 attempts renders as "—": nothing was ever
// sent, and "0/20" reads as a failed dispatch.
function relayAttempts(cmd) {
  const n = cmd?.attempts ?? 0;
  if (n === 0) return '—';
  const max = cmd?.max_attempts;
  return max ? `${n}/${max}` : String(n);
}

function RelayControlTab({ dcuSerial = null } = {}) {
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const embedded = !!dcuSerial;       // rendered inside a DCU detail view
  const lastIdx = React.useRef(null); // for shift-click range selection
  const [summary, setSummary] = React.useState(null);
  const [data, setData] = React.useState({ meters: [], total: 0, page: 1, pages: 1, page_size: 20 });
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [commType, setCommType] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [selected, setSelected] = React.useState({});   // serial -> meter row
  const [action, setAction] = React.useState('disconnect');
  const [mode, setMode] = React.useState('immediate');
  const [reason, setReason] = React.useState('debt');
  const [remark, setRemark] = React.useState('');
  const [confirmPhrase, setConfirmPhrase] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [commands, setCommands] = React.useState([]);
  const [showCommands, setShowCommands] = React.useState(true);
  // Relay Operation Log — same controls and vocabulary as the vendor's MDC
  // "Batch Relay Control > Relay Operation Log", so an operator moving between
  // the two tools reads the same columns and the same Result words.
  const [logTotal, setLogTotal] = React.useState(0);
  const [logPage, setLogPage] = React.useState(1);
  const [logPageSize, setLogPageSize] = React.useState(50);
  const [logMeter, setLogMeter] = React.useState('');
  const [logAction, setLogAction] = React.useState('');
  const [logUser, setLogUser] = React.useState('');
  const [logFrom, setLogFrom] = React.useState('');
  const [logTo, setLogTo] = React.useState('');
  const [logDetail, setLogDetail] = React.useState(null);

  const selectedSerials = Object.keys(selected);
  const selCount = selectedSerials.length;
  const killed = summary?.remote_writes_mode === 'disabled';
  const dryRun = summary?.remote_writes_mode === 'dry_run';

  const loadSummary = React.useCallback(() => {
    api('/relay/summary').then(setSummary).catch(() => {});
  }, []);

  const loadMeters = React.useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page: String(page), page_size: '20' });
    if (search.trim()) qs.set('search', search.trim());
    if (commType) qs.set('comm_type', commType);
    if (statusFilter) qs.set('relay_status', statusFilter);
    if (dcuSerial) qs.set('dcu_serial', dcuSerial);
    api(`/relay/meters?${qs.toString()}`)
      .then(d => setData(d))
      .catch(e => toast.push(`${t('rlog.toast_list_failed')}: ${e.message}`, 'error'))
      .finally(() => setLoading(false));
  }, [page, search, commType, statusFilter, dcuSerial]);

  const loadCommands = React.useCallback(() => {
    const cq = new URLSearchParams({
      page: String(logPage), page_size: String(logPageSize),
    });
    if (dcuSerial) cq.set('dcu_serial', dcuSerial);
    if (logMeter.trim()) cq.set('meter_serial', logMeter.trim());
    if (logAction) cq.set('action', logAction);
    if (logUser.trim()) cq.set('requested_by', logUser.trim());
    // Date inputs are local calendar days; send epoch bounds so the server
    // filters on requested_at without either side guessing a timezone.
    if (logFrom) cq.set('from_ts', String(new Date(`${logFrom}T00:00:00`).getTime() / 1000));
    if (logTo) cq.set('to_ts', String(new Date(`${logTo}T00:00:00`).getTime() / 1000 + 86400));
    api(`/relay/commands?${cq.toString()}`)
      .then(d => { setCommands(d.commands || []); setLogTotal(d.total || 0); })
      .catch(() => {});
  }, [dcuSerial, logPage, logPageSize, logMeter, logAction, logUser, logFrom, logTo]);

  React.useEffect(() => { loadSummary(); loadCommands(); }, [dcuSerial]);
  React.useEffect(() => { loadMeters(); }, [page, commType, statusFilter, dcuSerial]);
  // After "Read relay status", reload the list until the queued reads have
  // had time to land (DB-only; the reads themselves run on the DCU queues).
  const [readPollUntil, setReadPollUntil] = useState(0);
  React.useEffect(() => {
    if (!readPollUntil) return;
    const id = setInterval(() => {
      if (Date.now() > readPollUntil) { setReadPollUntil(0); return; }
      loadMeters(); loadSummary();
    }, 15000);
    return () => clearInterval(id);
  }, [readPollUntil, loadMeters]);
  // Poll the commands/tasks panel while open (cheap; DB-only).
  React.useEffect(() => {
    if (!showCommands) return;
    const id = setInterval(loadCommands, 15000);
    return () => clearInterval(id);
  }, [showCommands]);

  const toggleSel = (m) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[m.serial]) delete next[m.serial]; else next[m.serial] = m;
      return next;
    });
  };
  const togglePage = () => {
    const rows = (data.meters || []).filter(m => m.relay_supported);
    const allSel = rows.length > 0 && rows.every(m => selected[m.serial]);
    setSelected(prev => {
      const next = { ...prev };
      rows.forEach(m => { if (allSel) delete next[m.serial]; else next[m.serial] = m; });
      return next;
    });
  };
  // Row click with shift-range support: shift+click selects the contiguous
  // range from the last clicked row to this one (only relay-supported rows).
  const onRowClick = (m, idx, e) => {
    if (!m.relay_supported) return;
    if (e && e.shiftKey && lastIdx.current != null) {
      const all = data.meters || [];
      const [a, b] = [lastIdx.current, idx].sort((x, y) => x - y);
      setSelected(prev => {
        const next = { ...prev };
        for (let i = a; i <= b; i++) {
          const mm = all[i];
          if (mm && mm.relay_supported) next[mm.serial] = mm;
        }
        return next;
      });
    } else {
      toggleSel(m);
    }
    lastIdx.current = idx;
  };
  const clearSel = () => { setSelected({}); lastIdx.current = null; };

  const requiredPhrase = action === 'disconnect'
    ? (selCount === 1 ? selectedSerials[0] : `DISCONNECT ${selCount}`)
    : null;
  const phraseOk = !requiredPhrase || confirmPhrase === requiredPhrase;

  const submit = async () => {
    if (!selCount) { toast.push(t('rlog.toast_select_one'), 'error'); return; }
    if (requiredPhrase && !phraseOk) {
      toast.push(t('rlog.toast_type_confirm', { phrase: requiredPhrase }), 'error'); return;
    }
    setSubmitting(true);
    try {
      const res = await api('/relay/commands', {
        method: 'POST',
        body: JSON.stringify({
          serials: selectedSerials, action, mode, reason_code: reason,
          remark: remark || null, confirm_phrase: confirmPhrase || null,
        }),
      });
      const okN = (res.created || []).length;
      const errN = (res.errors || []).length;
      toast.push(
        t('rlog.toast_queued', { n: okN }) +
        (errN ? t('rlog.toast_skipped', { n: errN }) : '') +
        (res.gated_mode === 'dry_run' ? ` (${t('rlog.dry_run_tag')})` : ''),
        errN ? 'warning' : 'success',
      );
      clearSel(); setConfirmPhrase(''); setRemark('');
      loadCommands(); loadSummary();
      setTimeout(() => { loadCommands(); loadMeters(); }, 3000);
    } catch (e) {
      toast.push(`${t('rlog.toast_submit_failed')}: ${e.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Read-only: queues one relay-state read per selected meter on its DCU's
  // queue. Allowed whatever Action is chosen and needs no confirm phrase —
  // nothing is switched.
  const [readingStatus, setReadingStatus] = useState(false);
  const readStatus = async () => {
    if (!selCount) { toast.push(t('rlog.toast_select_one'), 'error'); return; }
    setReadingStatus(true);
    try {
      const res = await api('/relay/read-status', {
        method: 'POST', body: JSON.stringify({ serials: selectedSerials }),
      });
      const nr = (res.not_routed || []).length;
      toast.push(
        t('rlog.toast_read_queued', { n: res.queued || 0 }) +
        (res.already_queued ? t('rlog.toast_read_already', { n: res.already_queued }) : '') +
        (nr ? t('rlog.toast_read_not_routed', { n: nr }) : ''),
        nr ? 'warning' : 'success',
      );
      clearSel(); setConfirmPhrase('');
      setReadPollUntil(Date.now() + 15 * 60000);
    } catch (e) {
      toast.push(`${t('rlog.toast_read_failed')}: ${e.message}`, 'error');
    } finally {
      setReadingStatus(false);
    }
  };

  const cancelCmd = async (id) => {
    try {
      await api(`/relay/commands/${id}/cancel`, { method: 'POST' });
      toast.push(t('rlog.toast_cancelled'), 'success');
      loadCommands();
    } catch (e) { toast.push(`${t('rlog.toast_cancel_failed')}: ${e.message}`, 'error'); }
  };

  const th = { textAlign: 'left', padding: '6px 8px', fontSize: '11px', color: c.textMuted, textTransform: 'uppercase', borderBottom: `1px solid ${c.border}` };
  const td = { padding: '6px 8px', fontSize: '13px', borderBottom: `1px solid ${c.border}` };
  const kpi = (label, value, color) => (
    <div style={{ flex: '1 1 120px', background: c.bgCard, border: `1px solid ${c.border}`, borderLeft: `4px solid ${color}`, borderRadius: '6px', padding: '8px 12px' }}>
      <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: 700, color }}>{value}</div>
    </div>
  );

  return (
    <div>
      {!embedded && <div className="section-title">🎛 {t('tab.relay_control')}</div>}
      {embedded && (
        <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '10px' }}>
          🎛 {t('rlog.embedded_hint_prefix')} <code>{dcuSerial}</code>. {t('rlog.embedded_hint_suffix')}
        </div>
      )}

      {killed && (
        <div style={{ background: '#7f1d1d', color: '#fff', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', fontSize: '13px' }}>
          ⛔ {t('rlog.banner_killed')}
        </div>
      )}
      {dryRun && (
        <div style={{ background: '#78350f', color: '#fff', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', fontSize: '13px' }}>
          🧪 {t('rlog.banner_dry_run')}
        </div>
      )}

      {/* Summary strip — fleet-wide; hidden in the DCU-scoped (embedded) view. */}
      {!embedded && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {kpi(t('rlog.kpi_total'), summary?.total ?? '—', c.accent)}
          {kpi(t('rlog.kpi_connected'), summary?.connected ?? '—', c.success)}
          {kpi(t('rlog.kpi_disconnected'), summary?.disconnected ?? '—', c.danger)}
          {kpi(t('rlog.kpi_unknown'), summary?.unknown ?? '—', c.textMuted)}
          {kpi(t('rlog.kpi_open_commands'), summary?.open_commands ?? '—', '#f59e0b')}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        <input className="input" placeholder={t('rlog.search_placeholder')} value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setPage(1); loadMeters(); } }}
          style={{ minWidth: '260px' }} />
        <button className="button button-sm button-secondary" onClick={() => { setPage(1); loadMeters(); }}>🔍 {t('rlog.btn_search')}</button>
        <select className="input" value={commType} onChange={e => { setCommType(e.target.value); setPage(1); }}>
          <option value="">{t('rlog.all_comm_types')}</option>
          <option value="6">PLC</option>
          <option value="7">RF</option>
          <option value="1">RS-485</option>
        </select>
        <select className="input" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">{t('rlog.all_relay_states')}</option>
          <option value="connected">{t('rlog.kpi_connected')}</option>
          <option value="disconnected">{t('rlog.kpi_disconnected')}</option>
          <option value="unknown">{t('rlog.kpi_unknown')}</option>
        </select>
        <button className="button button-sm button-secondary" onClick={() => { loadMeters(); loadSummary(); }} disabled={loading}>
          {loading ? <Spinner /> : `🔄 ${t('rlog.btn_refresh')}`}
        </button>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: c.textMuted }}>
          {selCount > 0 ? t('rlog.n_selected', { n: selCount }) : t('rlog.n_meters', { n: data.total })}
        </span>
      </div>

      {/* Meter table */}
      <div style={{ overflowX: 'auto', background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '32px' }}>
                <input type="checkbox" onChange={togglePage}
                  checked={(data.meters || []).filter(m => m.relay_supported).length > 0 &&
                           (data.meters || []).filter(m => m.relay_supported).every(m => selected[m.serial])} />
              </th>
              <th style={th}>{t('rlog.col_account')}</th>
              <th style={th}>{t('rlog.col_meter_no')}</th>
              <th style={th}>{t('rlog.col_customer')}</th>
              <th style={th}>{t('rlog.col_tr')}</th>
              <th style={th}>{t('rlog.col_meter_type')}</th>
              <th style={th}>{t('rlog.col_comm')}</th>
              <th style={th}>{t('rlog.col_relay')}</th>
              <th style={th}>{t('rlog.col_refresh')}</th>
            </tr>
          </thead>
          <tbody>
            {(data.meters || []).map((m, idx) => (
              <tr key={m.serial}
                  onClick={(e) => { if (m.relay_supported && e.target.type !== 'checkbox') onRowClick(m, idx, e); }}
                  style={{ cursor: m.relay_supported ? 'pointer' : 'default', userSelect: 'none',
                           background: selected[m.serial] ? (c.bgHover || 'rgba(99,102,241,0.08)') : 'transparent' }}>
                <td style={td}>
                  <input type="checkbox" checked={!!selected[m.serial]} disabled={!m.relay_supported}
                    title={m.relay_supported ? t('rlog.tip_shift_range') : t('rlog.tip_unsupported')}
                    onChange={() => {}}
                    onClick={(e) => { e.stopPropagation(); onRowClick(m, idx, e); }} />
                </td>
                <td style={td}>{m.account_id || '—'}</td>
                <td style={td}><code>{m.meter_no}</code></td>
                <td style={td}>{m.customer_name || '—'}</td>
                <td style={td}>{m.transformer || '—'}</td>
                <td style={td}>{m.meter_type || '—'}</td>
                <td style={td}>{m.comm_label}{m.dcu_online ? '' : ' ⚠'}</td>
                <td style={td}>{relayStatusBadge(c, t, m.relay_status)}</td>
                <td style={{ ...td, color: c.textMuted, fontSize: '12px' }}>{fmtRelayTs(m.refresh_date)}</td>
              </tr>
            ))}
            {!loading && (data.meters || []).length === 0 && (
              <tr><td style={{ ...td, textAlign: 'center', color: c.textMuted }} colSpan={9}>{t('rlog.no_meters_match')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', marginTop: '8px', fontSize: '13px' }}>
        <button className="button button-sm button-secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ {t('rlog.prev')}</button>
        <span style={{ color: c.textMuted }}>{t('rlog.page')} {data.page} / {data.pages || 1}</span>
        <button className="button button-sm button-secondary" disabled={page >= (data.pages || 1)} onClick={() => setPage(p => p + 1)}>{t('rlog.next')} ›</button>
      </div>

      {/* Bulk action bar */}
      {selCount > 0 && (
        <div style={{ position: 'sticky', bottom: '0', marginTop: '14px', background: c.bgCard, border: `2px solid ${c.accent}`, borderRadius: '8px', padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
            <b style={{ fontSize: '14px' }}>{t('rlog.n_selected', { n: selCount })}</b>
            <label style={{ fontSize: '13px' }}>
              {t('rlog.lbl_action')}:&nbsp;
              <select className="input" value={action} onChange={e => { setAction(e.target.value); setConfirmPhrase(''); }}>
                <option value="disconnect">🔴 {t('rlog.act_disconnect')}</option>
                <option value="reconnect">🟢 {t('rlog.act_reconnect')}</option>
              </select>
            </label>
            <label style={{ fontSize: '13px' }}>
              {t('rlog.lbl_delivery')}:&nbsp;
              <select className="input" value={mode} onChange={e => setMode(e.target.value)}>
                <option value="immediate">{t('rlog.mode_immediate_long')}</option>
                <option value="deferred">{t('rlog.mode_deferred_long')}</option>
              </select>
            </label>
            <label style={{ fontSize: '13px' }}>
              {t('rlog.col_reason')}:&nbsp;
              <select className="input" value={reason} onChange={e => setReason(e.target.value)}>
                {RELAY_REASONS.map(r => <option key={r.code} value={r.code}>{t(r.key)}</option>)}
              </select>
            </label>
            <input className="input" placeholder={t('rlog.remark_placeholder')} value={remark}
              onChange={e => setRemark(e.target.value)} style={{ minWidth: '180px' }} />
          </div>
          {requiredPhrase && (
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', color: c.danger }}>
                ⚠ {t('rlog.confirm_warn')} <code style={{ background: c.bgBase, padding: '1px 6px', borderRadius: '4px' }}>{requiredPhrase}</code> {t('rlog.confirm_suffix')}
              </span>
              <input className="input" value={confirmPhrase} onChange={e => setConfirmPhrase(e.target.value)}
                placeholder={requiredPhrase} style={{ minWidth: '200px', borderColor: phraseOk ? c.success : c.danger }} />
            </div>
          )}
          <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
            <button className="button button-sm"
              style={{ background: action === 'disconnect' ? c.danger : c.success, color: '#fff' }}
              disabled={submitting || killed || (requiredPhrase && !phraseOk)}
              onClick={submit}>
              {submitting ? <Spinner />
                : (action === 'disconnect' ? '🔴 ' : '🟢 ') +
                  t(action === 'disconnect' ? 'rlog.btn_disconnect_n' : 'rlog.btn_reconnect_n', { n: selCount })}
            </button>
            <button className="button button-sm button-secondary" disabled={readingStatus} onClick={readStatus}>
              {readingStatus ? <Spinner /> : '📖 ' + t('rlog.btn_read_status_n', { n: selCount })}
            </button>
            <button className="button button-sm button-secondary" onClick={clearSel}>{t('rlog.btn_clear_sel')}</button>
          </div>
        </div>
      )}

      {/* Commands / tasks panel */}
      <div style={{ marginTop: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div className="section-title" style={{ margin: 0 }}>📋 {t('rlog.title')}</div>
          <span style={{ fontSize: '12px', color: c.textMuted }}>{t('rlog.total')} {logTotal}</span>
          <button className="button button-sm button-secondary" onClick={() => setShowCommands(s => !s)}>
            {showCommands ? t('rlog.btn_hide') : t('rlog.btn_show')}
          </button>
          <button className="button button-sm button-secondary" onClick={loadCommands}>🔄</button>
        </div>
        {showCommands && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <input className="input" style={{ width: '150px' }} placeholder={t('rlog.col_meter_no')}
              value={logMeter} onChange={e => { setLogMeter(e.target.value); setLogPage(1); }} />
            <select className="input" style={{ width: '140px' }} value={logAction}
              onChange={e => { setLogAction(e.target.value); setLogPage(1); }}>
              <option value="">{t('rlog.all_tasks')}</option>
              <option value="disconnect">{t('rlog.task_switch_off')}</option>
              <option value="reconnect">{t('rlog.task_switch_on')}</option>
            </select>
            <input className="input" type="date" style={{ width: '150px' }} value={logFrom}
              onChange={e => { setLogFrom(e.target.value); setLogPage(1); }} />
            <span style={{ color: c.textMuted }}>–</span>
            <input className="input" type="date" style={{ width: '150px' }} value={logTo}
              onChange={e => { setLogTo(e.target.value); setLogPage(1); }} />
            <input className="input" style={{ width: '140px' }} placeholder={t('rlog.col_by')}
              value={logUser} onChange={e => { setLogUser(e.target.value); setLogPage(1); }} />
            <button className="button button-sm" onClick={() => { setLogPage(1); loadCommands(); }}>🔍 {t('rlog.btn_search')}</button>
            {(logMeter || logAction || logUser || logFrom || logTo) && (
              <button className="button button-sm button-secondary" onClick={() => {
                setLogMeter(''); setLogAction(''); setLogUser(''); setLogFrom(''); setLogTo(''); setLogPage(1);
              }}>{t('rlog.btn_clear_filters')}</button>
            )}
          </div>
        )}
        {showCommands && (
          <div style={{ overflowX: 'auto', background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              {/* Columns restored 2026-08-11 to the pre-vendor-parity set.
                  The vendor-worded table (Task Name / Task Type / Start Time /
                  End Time / Process / Result) dropped MODE and REASON, which is
                  what an operator actually needs to answer "why is this meter
                  off?" — the reason code plus the free-text remark carry the
                  human decision, and nothing else in the system records it.
                  Kept from the vendor version: the row number, the DCU column,
                  the filter bar, pagination, and the Detail drawer. */}
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>{t('rlog.col_meter_no')}</th>
                  <th style={th}>{t('rlog.col_dcu')}</th>
                  <th style={th}>{t('rlog.col_action')}</th>
                  <th style={th}>{t('rlog.col_mode')}</th>
                  <th style={th}>{t('rlog.col_status')}</th>
                  <th style={th}>{t('rlog.col_reason')}</th>
                  <th style={th}>{t('rlog.col_attempts')}</th>
                  <th style={th}>{t('rlog.col_last_attempt')}</th>
                  <th style={th}>{t('rlog.col_by')}</th>
                  <th style={th}>{t('rlog.col_requested')}</th>
                  <th style={th}>{t('rlog.col_operate')}</th>
                </tr>
              </thead>
              <tbody>
                {commands.map((cmd, i) => {
                  const v = relayVerdict(cmd);
                  return (
                  <tr key={cmd.id}>
                    <td style={{ ...td, color: c.textMuted }}>{(logPage - 1) * logPageSize + i + 1}</td>
                    <td style={td}><code>{cmd.meter_serial}</code></td>
                    <td style={td}><code style={{ color: c.textMuted }}>{cmd.dcu_serial || '—'}</code></td>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {cmd.action === 'disconnect' ? `🔴 ${t('relay.off')}` : `🟢 ${t('relay.on')}`}
                    </td>
                    <td style={td}>{t(cmd.mode === 'immediate' ? 'rlog.mode_immediate' : 'rlog.mode_deferred')}</td>
                    {/* Status is ONE cell on purpose. It used to be two — Result
                        and Process — computed independently, and they could
                        contradict each other ("Busy status" beside "Finish").
                        The verdict line answers "what happened", the dim line
                        below carries the engine's own words (result_detail),
                        which is the detail the old UI showed and operators
                        asked for back. */}
                    <td style={td}>
                      <div style={{ color: relayVerdictColor(c, v), fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {relayVerdictText(t, v)}
                      </div>
                      {cmd.result_detail && (
                        <div style={{ fontSize: '11px', color: c.textMuted, maxWidth: '260px' }}>
                          {cmd.result_detail}
                        </div>
                      )}
                      {/* Only meaningful while the command is still open: when
                          we will next try. On a finished row it is stale. */}
                      {v.open && cmd.next_attempt_after && (
                        <div style={{ fontSize: '11px', color: c.textMuted }}>
                          ⏱ {t('rlog.next_retry')}: {fmtRelayTs(cmd.next_attempt_after)}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <div>{relayReasonLabel(t, cmd.reason_code)}</div>
                      {cmd.remark && (
                        <div style={{ fontSize: '11px', color: c.textMuted, maxWidth: '240px' }}>{cmd.remark}</div>
                      )}
                    </td>
                    <td style={td}>{relayAttempts(cmd)}</td>
                    <td style={{ ...td, fontSize: '12px', color: c.textMuted }}>{fmtRelayTs(cmd.last_attempt_at)}</td>
                    <td style={td}>{cmd.requested_by || '—'}</td>
                    <td style={{ ...td, fontSize: '12px', color: c.textMuted }}>{fmtRelayTs(cmd.requested_at)}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="button button-sm button-secondary"
                          onClick={() => setLogDetail(cmd)}>{t('rlog.btn_detail')}</button>
                        {cmd.status === 'pending' && (
                          <button className="button button-sm button-secondary" onClick={() => cancelCmd(cmd.id)}>{t('rlog.btn_cancel')}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {commands.length === 0 && (
                  <tr><td style={{ ...td, textAlign: 'center', color: c.textMuted }} colSpan={12}>{t('rlog.no_ops_match')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination — vendor shows "Total N" with a page size selector. */}
        {showCommands && logTotal > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', fontSize: '13px' }}>
            <span style={{ color: c.textMuted }}>{t('rlog.total')} {logTotal}</span>
            <select className="input" style={{ width: '110px' }} value={logPageSize}
              onChange={e => { setLogPageSize(Number(e.target.value)); setLogPage(1); }}>
              <option value={50}>{t('rlog.per_page', { n: 50 })}</option>
              <option value={100}>{t('rlog.per_page', { n: 100 })}</option>
              <option value={200}>{t('rlog.per_page', { n: 200 })}</option>
            </select>
            <button className="button button-sm button-secondary" disabled={logPage <= 1}
              onClick={() => setLogPage(p => Math.max(1, p - 1))}>‹</button>
            <span>{logPage} / {Math.max(1, Math.ceil(logTotal / logPageSize))}</span>
            <button className="button button-sm button-secondary"
              disabled={logPage >= Math.ceil(logTotal / logPageSize)}
              onClick={() => setLogPage(p => p + 1)}>›</button>
          </div>
        )}
      </div>

      {/* Detail drawer — the vendor's per-row "Detail" button. Shows the
          evidence behind the Result: pre/post relay state and every attempt,
          which is what an operator needs to tell "the meter refused" from
          "we never reached it". */}
      {logDetail && (
        <div onClick={() => setLogDetail(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '10px',
            padding: '18px', width: 'min(720px, 92vw)', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div className="section-title" style={{ margin: 0 }}>
                {t('rlog.detail_title')} · {logDetail.meter_serial}
              </div>
              <button className="button button-sm button-secondary" style={{ marginLeft: 'auto' }}
                onClick={() => setLogDetail(null)}>{t('rlog.btn_close')}</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {[
                  [t('rlog.col_meter_no'), logDetail.meter_serial],
                  [t('rlog.col_dcu'), logDetail.dcu_serial || '—'],
                  [t('rlog.col_action'), t(logDetail.action === 'disconnect' ? 'rlog.task_switch_off' : 'rlog.task_switch_on')],
                  [t('relay.bus'), logDetail.comm_label || logDetail.comm_type || '—'],
                  [t('rlog.col_mode'), t(logDetail.mode === 'immediate' ? 'rlog.mode_immediate' : 'rlog.mode_deferred')],
                  // One verdict here too — the drawer used to print Result and
                  // Process as separate rows and inherited the same contradiction.
                  [t('rlog.col_status'), relayVerdictText(t, relayVerdict(logDetail))],
                  [t('rlog.col_attempts'), relayAttempts(logDetail)],
                  [t('rlog.col_reason'), `${relayReasonLabel(t, logDetail.reason_code)}${logDetail.remark ? ' · ' + logDetail.remark : ''}`],
                  // Raw engine fields, deliberately NOT translated: they are the
                  // literal strings written to relay_commands and the thing to
                  // quote verbatim in a bug report.
                  [t('rlog.detail_raw'), logDetail.result_detail || '—'],
                  [t('rlog.detail_error_class'), logDetail.last_error_class || '—'],
                  [t('rlog.col_requested'), `${fmtRelayTs(logDetail.requested_at)} — ${logDetail.requested_by || '—'}`],
                  [t('rlog.detail_first_sent'), fmtRelayTs(logDetail.first_sent_at)],
                  [t('rlog.col_last_attempt'), fmtRelayTs(logDetail.last_attempt_at)],
                  [t('rlog.col_confirmed'), fmtRelayTs(logDetail.confirmed_at)],
                  [t('rlog.next_retry'), fmtRelayTs(logDetail.next_attempt_after)],
                  [t('rlog.detail_batch'), logDetail.batch_id || '—'],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ ...td, color: c.textMuted, width: '160px' }}>{k}</td>
                    <td style={td}>{String(v ?? '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '14px' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>{t('relay.relay_status')}</div>
              <div style={{ display: 'flex', gap: '18px', fontSize: '13px' }}>
                <div>{t('rlog.state_before')}: <code>{JSON.stringify(logDetail.pre_state ?? null)}</code></div>
                <div>{t('rlog.state_after')}: <code>{JSON.stringify(logDetail.post_state ?? null)}</code></div>
              </div>
            </div>
            {Array.isArray(logDetail.attempt_log) && logDetail.attempt_log.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                  {t('rlog.col_attempts')} ({logDetail.attempt_log.length})
                </div>
                <pre style={{
                  background: c.bgInput, border: `1px solid ${c.border}`, borderRadius: '6px',
                  padding: '10px', fontSize: '12px', maxHeight: '220px', overflow: 'auto', margin: 0,
                }}>{JSON.stringify(logDetail.attempt_log, null, 1)}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// The sidebar has a single "🔌 Meters" entry; operators pick between
// the list view (connected meters + session controls) and the
// geographic view via the segmented button bar at the top. No
// section-title wrapper here because each inner tab already renders
// its own (🔌 Connected Meters / 🗺️ Map).
// ──────────────────────────────────────────────────────────────────
function MetersTab() {
  const c = useThemeColors();
  const nav = useNav();
  const [sub, setSub] = useSessionState('apm_meters_sub', 'inbound');
  // Deep-link: the dashboard "Abnormal PLC" box sets nav.dcuFocus → jump to the
  // DCU sub-tab so DcuManagementTab can select that DCU + open its Black List.
  useEffect(() => { if (nav.dcuFocus?.dcu) setSub('dcu'); }, [nav.dcuFocus]);
  const tabBtnStyle = (active) => ({
    padding: '8px 16px',
    background: active ? '#1a1f2e' : 'transparent',
    color: active ? '#00e5ff' : '#94a3b8',
    border: 'none',
    borderBottom: active ? '2px solid #00e5ff' : '2px solid transparent',
    fontSize: '14px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });
  return (
    <div className="tab-content">
      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: `1px solid ${c.border}`,
      }}>
        <button style={tabBtnStyle(sub === 'inbound')} onClick={() => setSub('inbound')}>
          Inbound Meters
        </button>
        <button style={tabBtnStyle(sub === 'dcu')} onClick={() => setSub('dcu')}>
          DCU
        </button>
        <button style={tabBtnStyle(sub === 'map')} onClick={() => setSub('map')}>
          Map
        </button>
        <button style={tabBtnStyle(sub === 'archive')} onClick={() => setSub('archive')}>
          🗂 Archive Control
        </button>
        <button style={tabBtnStyle(sub === 'delete-manager')} onClick={() => setSub('delete-manager')}>
          🗑 Delete Manager
        </button>
      </div>
      {sub === 'inbound' ? <InboundMetersTab />
        : sub === 'dcu' ? <DcuManagementTab />
        : sub === 'archive' ? <ArchiveControlTab />
        : sub === 'delete-manager' ? <DeleteManagerTab />
        : <MapTab />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// ArchiveControlTab — FLEET-WIDE meter management (Device Management →
// Archive Control). One grid over every meter we hold (direct + DCU-routed)
// from /api/admin/fleet-archive, with the collection health an operator needs
// to decide what to prune: DAILY and LOAD-PROFILE ages are shown SEPARATELY
// because they fail independently (a meter can bill fine for days while its LP
// is dead — observed 2026-07-24 on DCU 129205032819).
//
// Actions are bulk and reuse proven backends — nothing new touches a device:
//   * Deregister + Delete → per-DCU /deprovision-meters (async collector job,
//     read-back verified) then /admin/devices/bulk-soft-delete (restorable via
//     Delete Manager).
//   * Move to another DCU → the existing /meters/{s}/move-dcu, which is
//     ordered so the meter is never in two parameter tables at once and
//     auto-rolls-back if the target registration fails.
//
// The three bulk actions are SEPARATE buttons, not one combined step, because
// they answer three different operator questions and only one of them is
// reversible the same way:
//   * Deregister only  — take it off the DCU but keep it listed. This is the
//     "remove + re-add to kick a stalled meter" half-step: upload marks the
//     meter Unregistered server-side (dcu_unregistered), so the DCU pane still
//     offers Register to put it straight back.
//   * Delete only      — hide it from the views without touching any device.
//     A soft-delete carrying a user id is honoured fleet-wide (the 2026-08-14
//     retirement-integrity work), so the row stops being resurrected from the
//     DCU's roster — while every stored reading stays. The meter also keeps
//     COLLECTING, because the DCU aggregate is driven by the device's own
//     table and not by the assignment row: 19 of the 135 meters hidden on
//     2026-08-14 were still producing a week later. What it does lose is the
//     scheduler's daily retry set and short-aggregate fallback, both of which
//     filter deleted_at — so a missed day stops being chased. Said plainly on
//     the button and in its confirm, because "still collecting" and "still
//     self-healing" are not the same promise.
//   * Both             — the original combined path, unchanged.
function ArchiveControlTab() {
  const c = useThemeColors();
  const toast = useToast();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [dcuFilter, setDcuFilter] = React.useState('');
  const [custFilter, setCustFilter] = React.useState('');
  const [trFilter, setTrFilter] = React.useState('');
  const [sort, setSort] = React.useState('worst');
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState(new Set());
  const [busy, setBusy] = React.useState(false);
  const [moveRow, setMoveRow] = React.useState(null);
  // Selected rows, snapshotted when the bulk-move modal opens. A SNAPSHOT and
  // not a live read of `selected`, so clearing the selection after the move
  // cannot pull rows out from under the result list still on screen.
  const [bulkMove, setBulkMove] = React.useState(null);
  // Render a PAGE, not all 1867 rows — a full render is ~22k DOM nodes and was
  // the visible half of the load lag (the other half was the uncached backend
  // scan, now snapshot-cached server-side).
  const PAGE = 250;
  const [shown, setShown] = React.useState(PAGE);

  const load = React.useCallback((opts) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (dcuFilter) p.set('dcu', dcuFilter);
    if (custFilter) p.set('customer', custFilter);
    if (trFilter) p.set('transformer', trFilter);
    if (search.trim()) p.set('search', search.trim());
    if (sort) p.set('sort', sort);
    if (opts?.fresh) p.set('fresh', '1');
    api(`/admin/fleet-archive?${p.toString()}`)
      .then(d => { setData(d); setSelected(new Set()); setShown(PAGE); })
      .catch(e => toast.push(`Load failed: ${e.message}`, 'error'))
      .finally(() => setLoading(false));
  }, [status, dcuFilter, custFilter, trFilter, search, sort, toast]);

  React.useEffect(() => { load(); /* eslint-disable-next-line */ },
    [status, dcuFilter, custFilter, trFilter, sort]);

  const allRows = data?.meters || [];
  const rows = allRows.slice(0, shown);
  const sum = data?.summary || {};
  const facets = data?.facets || {};
  const dcuList = facets.dcus || [];

  // ── Cascading filter options ───────────────────────────────────────
  // Facets are whole-fleet (deliberately — see _fleet_archive_facets), and
  // `by_customer` carries the relationships so the narrowing happens here with
  // no extra request. Every list stays a superset-of-nothing: when no customer
  // is picked, the dependent pickers still offer the full fleet.
  const byCustomer = facets.by_customer || {};
  const customerOptions = React.useMemo(
    () => (facets.customers || []).map(x => {
      const e = byCustomer[x] || {};
      const nt = Object.keys(e.transformers || {}).length;
      return { value: x, label: x, hint: nt ? `${nt} TP` : '' };
    }), [facets.customers, byCustomer]);

  const transformerOptions = React.useMemo(() => {
    // DEGRADE TO THE FULL LIST, never to an empty one. `by_customer` only
    // exists on a backend new enough to send it, so a frontend that lands
    // ahead of the API (or a response served from the 60s snapshot cache from
    // before the deploy) would otherwise show an EMPTY transformer picker and
    // read as "this customer has no transformers".
    const e = byCustomer[custFilter];
    if (custFilter && e && e.transformers) {
      return Object.keys(e.transformers).map(t => {
        const n = (e.transformers[t] || []).length;
        return { value: t, label: t, hint: n ? `${n} DCU` : 'direct' };
      });
    }
    return (facets.transformers || []).map(t => ({ value: t, label: t }));
  }, [custFilter, byCustomer, facets.transformers]);

  const connectionOptions = React.useMemo(() => {
    // Same rule as above: narrow only when we actually have the mapping, and
    // only when the narrowed set is non-empty — a customer whose meters are
    // all direct has no DCUs, and blanking the picker there would hide the
    // "Direct meters only" entry that is the useful choice for them.
    let list = dcuList;
    const e = byCustomer[custFilter];
    if (custFilter && e) {
      const narrowed = (trFilter ? (e.transformers || {})[trFilter] : e.dcus);
      if (narrowed) list = narrowed;
    }
    return [{ value: 'direct', label: 'Direct meters only', hint: 'no DCU' }]
      .concat((list || []).map(d => ({ value: d, label: `DCU ${d}` })));
  }, [dcuList, custFilter, trFilter, byCustomer]);

  // A selection the new parent no longer contains must be dropped, or the grid
  // silently filters on a pair that cannot match and reads as "no results".
  React.useEffect(() => {
    if (!custFilter || !Object.keys(byCustomer).length) return;
    const e = byCustomer[custFilter] || {};
    if (trFilter && !(e.transformers || {})[trFilter]) setTrFilter('');
    if (dcuFilter && dcuFilter !== 'direct' && !(e.dcus || []).includes(dcuFilter)) setDcuFilter('');
    /* eslint-disable-next-line */
  }, [custFilter, byCustomer]);

  React.useEffect(() => {
    if (!custFilter || !trFilter || !Object.keys(byCustomer).length) return;
    const under = ((byCustomer[custFilter] || {}).transformers || {})[trFilter] || [];
    if (dcuFilter && dcuFilter !== 'direct' && !under.includes(dcuFilter)) setDcuFilter('');
    /* eslint-disable-next-line */
  }, [trFilter, byCustomer]);

  const fmtAge = (sec) => {
    if (sec == null) return 'never';
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h`;
    return `${Math.round(sec / 86400)}d`;
  };
  const ageColor = (sec) => sec == null ? '#ef4444'
    : sec <= 86400 ? '#22c55e' : sec <= 7 * 86400 ? '#f59e0b' : '#ef4444';
  const statusBadge = (s) => {
    const map = { alive: ['#22c55e', 'Alive'], silent_24h: ['#f59e0b', 'Silent 24h'],
                  silent_7d: ['#ef4444', 'Silent 7d'], never: ['#6b7280', 'Never'] };
    const [col, label] = map[s] || ['#6b7280', s || '—'];
    return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
      fontWeight: 700, color: '#fff', background: col }}>{label}</span>;
  };

  const toggle = (serial) => setSelected(p => {
    const n = new Set(p); n.has(serial) ? n.delete(serial) : n.add(serial); return n;
  });
  const toggleAll = () => setSelected(p =>
    p.size === rows.length ? new Set() : new Set(rows.map(r => r.meter_serial)));

  // Removal takes the SAME route as the DCU pane's Clear → Upload: stage the
  // removals locally (no device I/O at all), then apply them in ONE verified
  // write per DCU. This replaces a per-64-SAP `deprovision-meters` loop that
  // issued several independent clear+re-add rewrites against the same table.
  //
  // Why it matters: every parameter-table write on permissive firmware is a
  // whole-table rewrite, so N chunked writes meant N chances to lose a table.
  // One staged batch per DCU is one write, and it inherits upload's server-side
  // guards — refuses on an untrusted read, refuses a false-empty pre-read, and
  // refuses any result that would leave the table empty.
  //
  // Staging is also offline-safe: a DCU that is down still records the intent
  // and applies it on the next Upload, where deprovision simply failed.
  //
  // `mode` selects which halves run:
  //   'deregister' — device only. Take the meters off their DCUs and leave the
  //                  rows listed; upload marks them Unregistered server-side so
  //                  the DCU pane can Register them straight back.
  //   'delete'     — database only. NO device is contacted at all: the meters
  //                  stay in their DCU's parameter table and keep collecting;
  //                  the rows just stop being listed. Use when the registration
  //                  is right and only the bookkeeping is wrong.
  //   'both'       — the original combined path.
  const bulkRemove = async (mode) => {
    const dereg = mode !== 'delete';
    const del = mode !== 'deregister';
    const picked = rows.filter(r => selected.has(r.meter_serial));
    if (!picked.length) return;
    const withDcu = dereg ? picked.filter(r => r.dcu_serial) : [];
    const noDcu = picked.filter(r => !r.dcu_serial);
    const nDcus = new Set(withDcu.map(r => r.dcu_serial)).size;
    const head = dereg && del ? `Remove ${picked.length} meter(s) from the fleet?`
      : dereg ? `Deregister ${picked.length} meter(s) from their DCUs?`
      : `Delete ${picked.length} meter(s) from the database?`;
    if (!window.confirm(
      `${head}\n\n`
      + (dereg
        ? `• ${withDcu.length} will be staged for removal across ${nDcus} DCU(s) and applied\n`
          + `  in ONE verified parameter-table write each: read → merge → rewrite\n`
          + `  once → read-back. Refused server-side if a table cannot be read or\n`
          + `  the result would come out empty.\n`
          + (noDcu.length && del ? `• ${noDcu.length} are not routed via a DCU — soft-deleted only\n` : '')
          + (noDcu.length && !del ? `• ${noDcu.length} are not routed via a DCU — nothing to deregister\n` : '')
          + `• Upload applies EVERY change staged on those DCUs, including anything\n`
          + `  staged earlier from the DCU pane.\n`
        : `• NO device is contacted. The meters stay registered on their DCUs and\n`
          + `  keep collecting into history — only the listing changes.\n`
          + `• They DO drop out of the daily retry set and the short-aggregate\n`
          + `  fallback, so a missed day will no longer be chased automatically.\n`)
      + (del
        ? `• All ${picked.length} then move to the Delete Manager (restorable)\n`
          + `• Stored readings are KEPT — nothing in the history is removed.\n`
        : `• The rows stay listed. Each meter is marked Unregistered on its DCU,\n`
          + `  so the DCU pane's Register puts it back with one click.\n`)
      + (dereg ? `\nRuns as one background job per DCU.` : '')
    )) return;
    setBusy(true);
    try {
      // Group by DCU and stage by METER SERIAL — the routing map is keyed by
      // serial, so unlike deprovision (which addresses by SAP) this also covers
      // rows whose SAP we never learned.
      const byDcu = new Map();
      for (const r of withDcu) {
        if (!byDcu.has(r.dcu_serial)) byDcu.set(r.dcu_serial, []);
        byDcu.get(r.dcu_serial).push(String(r.meter_serial));
      }
      // The soft-delete below lands immediately, but each DCU's upload is a
      // background job that can take minutes. Refresh once when the LAST of
      // them reports — per-job reloads would hammer /admin/fleet-archive (a
      // ~1.1 s uncached rebuild) for no extra information. `fresh` bypasses the
      // server-side snapshot cache, which would otherwise hand back the
      // pre-removal picture.
      let outstanding = 0;
      const onJobDone = () => { outstanding -= 1; if (outstanding <= 0) load({ fresh: true }); };
      const queued = [], stagedOnly = [], notStaged = [];
      for (const [dcu, serials] of byDcu) {
        let staged = 0;
        try {
          const st = await api(`/dcus/${dcu}/stage-meters`, {
            method: 'POST',
            body: JSON.stringify({ remove: serials }),
          });
          staged = (st?.staged_remove || []).length;
          // stage_remove only takes on meters the routing map actually holds;
          // anything it declined is not on that DCU as far as we know, so there
          // is nothing to write for it.
          if (staged < serials.length) notStaged.push(`${dcu}: ${serials.length - staged}`);
        } catch (e) {
          toast.push(`DCU ${dcu}: staging failed — ${e.message}`, 'error');
          continue;
        }
        if (staged === 0) continue;   // nothing to apply — do not touch the device
        try {
          const res = await api(`/dcus/${dcu}/upload-param-table`, {
            method: 'POST',
            body: JSON.stringify({ confirm_serial: String(dcu) }),
          });
          if (res?.job_id) {
            outstanding += 1;
            queued.push(dcu);
            bgJobs.add({
              id: res.job_id, dcu: String(dcu), op: `Archive remove ${staged}`,
              onDone: onJobDone,
            });
          }
        } catch (e) {
          // Most often the DCU is offline (upload 404s without a live session).
          // The removal STAYS STAGED and applies on the next Upload, so this is
          // not a failure of the operation — just deferred work.
          stagedOnly.push(dcu);
        }
      }
      if (del) {
        const serials = picked.map(r => r.meter_serial);
        // The reason string is the operator-facing audit trail AND the thing
        // that tells a combined removal apart from a bookkeeping-only one when
        // someone reads the Delete Manager six weeks later.
        const reason = dereg ? 'Archive Control bulk remove'
                             : 'Archive Control bulk delete (kept registered on DCU)';
        for (let i = 0; i < serials.length; i += 500) {
          await api('/admin/devices/bulk-soft-delete', {
            method: 'POST',
            body: JSON.stringify({ serials: serials.slice(i, i + 500), reason }),
          });
        }
      }
      toast.push(
        (del ? `${picked.length} meter(s) → Delete Manager`
             : `${picked.length} meter(s) deregistered — still listed`)
        + (queued.length ? `; ${queued.length} DCU upload(s) queued` : ''),
        'success');
      // Never let deferred or declined work pass silently — an operator who
      // thinks a table was rewritten when it wasn't is exactly how a "repair"
      // gets run twice.
      if (stagedOnly.length) {
        toast.push(
          `${stagedOnly.length} DCU(s) unreachable — removals are STAGED and will apply `
          + `on the next Upload: ${stagedOnly.slice(0, 4).join(', ')}`
          + (stagedOnly.length > 4 ? ` +${stagedOnly.length - 4} more` : ''),
          'info');
      }
      if (notStaged.length) {
        toast.push(
          `Not staged (not in our routing map for that DCU): ${notStaged.slice(0, 4).join(', ')}`
          + (notStaged.length > 4 ? ` +${notStaged.length - 4} more` : ''),
          'info');
      }
      setSelected(new Set());
      // `fresh` because /admin/fleet-archive is snapshot-cached for 60 s and
      // nothing invalidates that cache on a write — a plain reload would hand
      // back the pre-change picture and read as "the action did nothing". The
      // combined path also reloads again when the last upload job reports.
      load({ fresh: true });
    } catch (e) {
      toast.push(`Bulk remove failed: ${e.message}`, 'error');
    } finally { setBusy(false); }
  };

  const th = { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${c.border}`,
    fontSize: '11px', color: c.textMuted, textTransform: 'uppercase', whiteSpace: 'nowrap' };
  const td = { padding: '6px 8px', borderBottom: `1px solid ${c.border}22`, fontSize: '12px' };
  const pill = (active) => ({ padding: '5px 12px', fontSize: '12px', borderRadius: '14px',
    cursor: 'pointer', border: `1px solid ${active ? '#00e5ff' : c.border}`,
    background: active ? '#00e5ff22' : 'transparent', color: active ? '#00e5ff' : c.textSecondary });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: c.textPrimary }}>🗂 Archive Control</div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '2px' }}>
            Fleet-wide meter management — {sum.total ?? 0} meters.
            Daily and load-profile ages are tracked separately; they fail independently.
          </div>
        </div>
        <button style={_dcuBtn('#1e40af', loading)} disabled={loading}
          onClick={() => load({ fresh: true })}
          title="Rebuild the fleet snapshot now (bypasses the 60s server cache)">
          {loading ? 'Loading…' : '↻ Reload'}
        </button>
      </div>

      {/* Health roll-up — click to filter. This IS the "not collecting" report. */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <button style={pill(status === '')} onClick={() => setStatus('')}>All ({sum.total ?? 0})</button>
        <button style={pill(status === 'alive')} onClick={() => setStatus('alive')}>🟢 Alive ({sum.alive ?? 0})</button>
        <button style={pill(status === 'silent_24h')} onClick={() => setStatus('silent_24h')}>🟠 Silent 24h ({sum.silent_24h ?? 0})</button>
        <button style={pill(status === 'silent_7d')} onClick={() => setStatus('silent_7d')}>🔴 Silent 7d ({sum.silent_7d ?? 0})</button>
        <button style={pill(status === 'never')} onClick={() => setStatus('never')}>⚫ Never ({sum.never ?? 0})</button>
        <button style={pill(status === 'stale_daily')} onClick={() => setStatus('stale_daily')}>📅 No daily 48h ({sum.stale_daily ?? 0})</button>
        <button style={pill(status === 'stale_lp')} onClick={() => setStatus('stale_lp')}>📈 No LP 48h ({sum.stale_lp ?? 0})</button>
        {/* Registration state, not data age — a meter cleared an hour ago still
            reads "Alive" on yesterday's data, which is why this is its own
            filter rather than another value of status. */}
        <button style={pill(status === 'unregistered')} onClick={() => setStatus('unregistered')}
          title="Deregistered from their DCU and registered on none — the DCU column shows where they were last. Select them and use Move to DCU to finish the job.">
          🔌 Unregistered ({sum.unregistered ?? 0})
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        <input placeholder="Search meter / object…" value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') load(); }}
          style={{ padding: '6px 10px', fontSize: '12px', background: c.bgInput, color: c.textPrimary,
            border: `1px solid ${c.border}`, borderRadius: '6px', width: '220px' }} />
        {/* Customer → Transformer → DCU cascade. Each picker offers only what
            exists under the selection above it, so choosing Atlas stops the
            transformer list showing every transformer in the fleet. Order is
            deliberate: customer is the coarsest cut an operator actually
            thinks in, and it is what makes the other two lists short. */}
        <SearchableSelect
          value={custFilter} onChange={setCustFilter} options={customerOptions}
          placeholder="Customer…" clearLabel="All customers" width={200} />
        <SearchableSelect
          value={trFilter} onChange={setTrFilter} options={transformerOptions}
          placeholder={custFilter ? 'Transformer…' : 'Transformer (all customers)…'}
          clearLabel="All transformers" width={220} />
        <SearchableSelect
          value={dcuFilter} onChange={setDcuFilter} options={connectionOptions}
          placeholder="DCU / connection…" clearLabel="All connections" width={230} />
        <select value={sort} onChange={e => setSort(e.target.value)}
          title="Default sorts the least-recently-collected first — that is why 'Never' rows lead"
          style={{ padding: '6px 10px', fontSize: '12px', background: c.bgInput, color: c.textPrimary,
            border: `1px solid ${c.border}`, borderRadius: '6px' }}>
          <option value="worst">Sort: worst first</option>
          <option value="recent">Sort: most recent first</option>
          <option value="serial">Sort: meter no</option>
        </select>
        <span style={{ flex: 1 }} />
        {selected.size > 0 && (
          <>
            <span style={{ fontSize: '12px', color: c.textSecondary }}>{selected.size} selected</span>
            <button style={_dcuBtn('#7c3aed', busy)} disabled={busy}
              onClick={() => setBulkMove(rows.filter(r => selected.has(r.meter_serial)))}
              title="Move the selected meters to another DCU. Grouped by their current DCU and moved one source at a time: deregistered from the source FIRST, then registered on the target — never on both at once, and rolled back if the target refuses.">
              🛰 Move to DCU… ({selected.size})
            </button>
            <button style={_dcuBtn('#a16207', busy)} disabled={busy}
              onClick={() => bulkRemove('deregister')}
              title="Take the selected meters off their DCUs' parameter tables and leave them listed. They are marked Unregistered on the DCU, so the DCU pane's Register puts them straight back — the 'remove + re-add to kick a stalled meter' half-step.">
              {busy ? 'Working…' : `📴 Deregister only (${selected.size})`}
            </button>
            <button style={_dcuBtn('#4c1d95', busy)} disabled={busy}
              onClick={() => bulkRemove('delete')}
              title="Soft-delete the selected meters WITHOUT contacting any device. They stay registered on their DCU and keep collecting into history; only the listing changes. They do drop out of the daily retry set, so a missed day is no longer chased automatically. Restorable from the Delete Manager, and no stored reading is removed.">
              {busy ? 'Working…' : `🗑 Delete only (${selected.size})`}
            </button>
            <button style={_dcuBtn('#b91c1c', busy)} disabled={busy}
              onClick={() => bulkRemove('both')}
              title="Deregister from the DCU AND soft-delete: the full removal. Restorable from the Delete Manager; stored readings are kept.">
              {busy ? 'Working…' : `🗑 Deregister + Delete (${selected.size})`}
            </button>
          </>
        )}
      </div>

      {!data ? (
        <div style={{ color: c.textMuted, padding: '20px', fontSize: '12px' }}>
          {loading ? 'Loading fleet archive…' : 'No data.'}
        </div>
      ) : (
        <>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '6px' }}>
            Showing {rows.length} of {data.matched} matching
            {sort === 'worst' && ' — least-recently-collected first, so never/stale meters lead'}
            {sort === 'recent' && ' — most-recently-collected first'}
            {sort === 'serial' && ' — by meter number'}.
            {data.cache_age_seconds > 0 && ` Snapshot ${Math.round(data.cache_age_seconds)}s old.`}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}><input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} /></th>
                  {/* Position in the CURRENT sort+filter, not a stable meter id
                      — it renumbers when either changes. It exists so a row can
                      be referred to out loud ("row 42") and so you can see how
                      far down a 1900-row list you are while paging. */}
                  <th style={th} title="Row number in the current sort and filter">#</th>
                  <th style={th}>Meter No</th>
                  <th style={th}>Comm Addr</th>
                  <th style={th}>Connection</th>
                  <th style={th}>DCU</th>
                  <th style={th}>SAP</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Transformer</th>
                  <th style={th}>Status</th>
                  <th style={th}>Last daily</th>
                  <th style={th}>Last LP</th>
                  <th style={th}>Operate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const nowS = Date.now() / 1000;
                  const dAge = r.last_daily_ts ? nowS - r.last_daily_ts : null;
                  const lAge = r.last_lp_ts ? nowS - r.last_lp_ts : null;
                  return (
                    <tr key={r.meter_serial} style={{ background: selected.has(r.meter_serial) ? '#00e5ff11' : 'transparent' }}>
                      <td style={td}><input type="checkbox" checked={selected.has(r.meter_serial)} onChange={() => toggle(r.meter_serial)} /></td>
                      {/* `rows` is the page slice of `allRows`, and the slice
                          always starts at 0, so i+1 is the true fleet-wide
                          position — no page offset to add. */}
                      <td style={{ ...td, color: c.textMuted, textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>{r.display_serial}</td>
                      <td style={{ ...td, fontFamily: 'monospace', color: c.textMuted }}>{r.comm_addr}</td>
                      <td style={td}>{r.connection === 'dcu' ? 'PLC/RF (DCU)' : r.connection === 'lls_modem' ? 'Direct · LLS modem' : 'Direct'}</td>
                      {/* An unregistered meter's DCU is where it WAS, not where
                          it is. Rendered muted with a "last" marker so the cell
                          stops asserting a registration the device doesn't have. */}
                      <td style={{ ...td, fontFamily: 'monospace',
                        color: r.unregistered ? c.textMuted : undefined }}>
                        {r.dcu_serial || '—'}
                        {r.unregistered && <span style={{ fontSize: '10px', marginLeft: '5px' }}>last</span>}
                      </td>
                      <td style={td}>{r.unregistered ? '—' : (r.sap_id ?? '—')}</td>
                      <td style={td}>{r.customer || '—'}</td>
                      <td style={td}>{r.transformer || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {statusBadge(r.status)}
                        {r.unregistered && (
                          <span title={`Deregistered from ${r.unregistered_dcu || 'its DCU'}; registered on no DCU. Select it and use Move to DCU.`}
                            style={{ marginLeft: '5px', padding: '2px 8px', borderRadius: '10px',
                              fontSize: '11px', fontWeight: 700, color: '#fff', background: '#a16207' }}>
                            Unregistered
                          </span>
                        )}
                      </td>
                      <td style={{ ...td, color: ageColor(dAge), fontWeight: 600 }}>{fmtAge(dAge)}</td>
                      <td style={{ ...td, color: ageColor(lAge), fontWeight: 600 }}>{fmtAge(lAge)}</td>
                      <td style={td}>
                        {r.dcu_serial && (
                          <button style={{ ..._dcuBtn('#7c3aed', false), padding: '3px 10px', fontSize: '11px' }}
                            onClick={() => setMoveRow(r)} title="Move this meter to another DCU that hears it">
                            ⇄ Move
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {allRows.length > rows.length && (
            <div style={{ textAlign: 'center', padding: '12px' }}>
              <button style={_dcuBtn('#334155', false)} onClick={() => setShown(s => s + PAGE)}>
                Show {Math.min(PAGE, allRows.length - rows.length)} more
                ({rows.length} of {allRows.length} rendered)
              </button>
            </div>
          )}
        </>
      )}

      {moveRow && (
        <MoveMeterDialog
          meterSerial={moveRow.comm_addr || moveRow.meter_serial}
          fromDcu={moveRow.dcu_serial}
          defaultTarget=""
          onClose={() => setMoveRow(null)}
          onMoved={() => { setMoveRow(null); load({ fresh: true }); }}
        />
      )}

      {/* onMoved reloads but deliberately does NOT close: the modal is showing
          a per-source-DCU result list, and a bulk move across several sources
          is exactly the case where a partial outcome must stay readable. */}
      {bulkMove && (
        <FleetBulkMoveModal
          rows={bulkMove}
          onClose={() => { setBulkMove(null); setSelected(new Set()); }}
          onMoved={() => load({ fresh: true })}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// DeleteManagerTab — V0.4 Delete Manager. Lists all soft-deleted
// devices (meters + DCUs) from /api/admin/deleted-devices, with
// Restore + Purge actions. Soft-delete is set elsewhere (existing
// DELETE /api/assignments/{id} now soft-deletes; the new
// /api/admin/devices/{serial}/soft-delete endpoint handles DCUs and
// reason-tagged deletes).
// ──────────────────────────────────────────────────────────────────
function DeleteManagerTab() {
  const c = useThemeColors();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all'); // all | meter | dcu
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const reload = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await api(`/admin/deleted-devices${filter === 'all' ? '' : '?type=' + filter}`);
      setRows(data?.devices ?? []);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const restore = async (serial) => {
    try {
      await api(`/admin/devices/${encodeURIComponent(serial)}/restore`, { method: 'POST' });
      await reload();
    } catch (e) {
      alert(`Restore failed: ${e?.message || e}`);
    }
  };
  const purge = async (serial, objectName) => {
    const ok = window.confirm(
      `Permanently purge ${serial}${objectName ? ' (' + objectName + ')' : ''}?\n\n` +
      `This is irreversible. The row will be removed from the database, the routing map, ` +
      `the live CAS pool, the known-devices sidecar, and the parent DCU's parameter table. ` +
      `History remains only in the Audit Log.`
    );
    if (!ok) return;
    try {
      const r = await api(`/admin/devices/${encodeURIComponent(serial)}/purge`, { method: 'DELETE' });
      const stages = r?.stages || {};
      alert(
        `Purged ${serial}.\n\n` +
        `Stages: assignment=${stages.assignment_removed} map=${stages.map_removed} ` +
        `pool=${stages.pool_removed} known=${stages.known_removed}`
      );
      await reload();
    } catch (e) {
      alert(`Purge failed: ${e?.message || e}`);
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.meter_serial || '').toLowerCase().includes(q) ||
      (r.object_name || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.transformer_name || '').toLowerCase().includes(q) ||
      (r.deleted_by || '').toLowerCase().includes(q) ||
      (r.deleted_reason || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: c.textPrimary }}>🗑 Deleted devices ({filteredRows.length})</h3>
        <div style={{ flex: 1 }} />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            padding: '6px 10px', background: c.bgInput, color: c.textPrimary,
            border: `1px solid ${c.border}`, borderRadius: '4px', fontSize: '13px',
          }}
        >
          <option value="all">All devices</option>
          <option value="meter">Meters only</option>
          <option value="dcu">DCUs only</option>
        </select>
        <input
          type="search"
          placeholder="Filter by serial / object / customer / reason…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '6px 10px', background: c.bgInput, color: c.textPrimary,
            border: `1px solid ${c.border}`, borderRadius: '4px', fontSize: '13px',
            minWidth: '300px',
          }}
        />
        <button
          onClick={reload}
          disabled={loading}
          className="button button-secondary"
        >
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {err && (
        <div style={{
          padding: '10px 12px', background: '#3a1a1a', border: `1px solid ${c.danger}`,
          color: c.danger, borderRadius: '6px', marginBottom: '12px', fontSize: '13px',
        }}>
          {err}
        </div>
      )}

      {!loading && filteredRows.length === 0 && (
        <div style={{
          padding: '32px', textAlign: 'center', color: c.textMuted, fontSize: '14px',
          border: `1px dashed ${c.border}`, borderRadius: '6px',
        }}>
          {rows.length === 0
            ? 'No deleted devices. Devices appear here when an admin clicks Delete on a meter or DCU.'
            : 'No matches for your filter.'}
        </div>
      )}

      {filteredRows.length > 0 && (
        <div style={{ overflowX: 'auto', border: `1px solid ${c.border}`, borderRadius: '6px' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '12px',
          }}>
            <thead>
              <tr style={{ background: c.bgHeader }}>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Serial</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Object</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Type</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Customer</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Transformer</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>DCU</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Deleted at</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>By</th>
                <th style={{ padding: '8px', textAlign: 'left', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Reason</th>
                <th style={{ padding: '8px', textAlign: 'right', color: c.textLabel, borderBottom: `1px solid ${c.border}` }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.assignment_id} style={{ borderBottom: `1px solid ${c.borderLight}` }}>
                  <td style={{ padding: '6px 8px', color: c.textPrimary, fontFamily: 'monospace' }}>{r.meter_serial}</td>
                  <td style={{ padding: '6px 8px', color: c.textPrimary }}>{r.object_name || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                      background: r.device_type === 'dcu' ? '#1e3a5f' : '#1e3a3a',
                      color: r.device_type === 'dcu' ? '#60a5fa' : '#34d399',
                    }}>{r.device_type === 'dcu' ? 'DCU' : 'Meter'}</span>
                  </td>
                  <td style={{ padding: '6px 8px', color: c.textPrimary }}>{r.customer_name || '—'}</td>
                  <td style={{ padding: '6px 8px', color: c.textPrimary }}>{r.transformer_name || '—'}</td>
                  <td style={{ padding: '6px 8px', color: c.textMuted, fontFamily: 'monospace' }}>{r.dcu_serial || '—'}</td>
                  <td style={{ padding: '6px 8px', color: c.textMuted, fontSize: '11px' }}>
                    {r.deleted_at ? fmtDT(r.deleted_at, { utc: true, seconds: true }) : '—'}
                  </td>
                  <td style={{ padding: '6px 8px', color: c.textPrimary }}>{r.deleted_by || '—'}</td>
                  <td style={{ padding: '6px 8px', color: c.textMuted, fontStyle: 'italic' }}>
                    {r.deleted_reason || '—'}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <button
                      onClick={() => restore(r.meter_serial)}
                      style={{
                        padding: '4px 10px', marginRight: '6px',
                        background: '#1e3a3a', color: '#34d399',
                        border: '1px solid #34d399', borderRadius: '4px',
                        cursor: 'pointer', fontSize: '11px',
                      }}
                    >🟢 Restore</button>
                    <button
                      onClick={() => purge(r.meter_serial, r.object_name)}
                      style={{
                        padding: '4px 10px',
                        background: '#3a1a1a', color: c.danger,
                        border: `1px solid ${c.danger}`, borderRadius: '4px',
                        cursor: 'pointer', fontSize: '11px',
                      }}
                    >🔴 Purge</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Remembered per-register picks for the DCU-routed On-Demand panel. Same
// shape and failure handling as the report-column prefs: a plain array of
// OBIS codes in localStorage, and a private-mode/quota failure just means the
// selection does not persist. Empty default is deliberate — the whole point of
// the panel is to NOT pull all 37 registers unless asked.
const DCU_ONDEMAND_KEY = 'emeter.dcuOnDemandRegisters.v1';

function loadDcuOnDemandPrefs() {
  try {
    const v = JSON.parse(localStorage.getItem(DCU_ONDEMAND_KEY));
    return Array.isArray(v) ? v.filter(x => typeof x === 'string') : [];
  } catch (_) { return []; }
}

function saveDcuOnDemandPrefs(list) {
  try { localStorage.setItem(DCU_ONDEMAND_KEY, JSON.stringify(list)); }
  catch (_) { /* private mode / quota — picks just won't persist */ }
}

// Wall-clock estimate for a DCU-routed sweep. Measured 2.4-3.9 s per
// register on 129255053336 (2026-08-26), one frame at a time because the DCU
// serves a single task. The first read of a register on a given meter also
// fetches its scaler-unit, so early sweeps run at the top of that range;
// 3 s/register is the honest middle.
function _estimate(n) {
  const secs = Math.round(n * 3);
  if (secs < 90) return `${secs}s`;
  return `${Math.round(secs / 60)} min`;
}

function DcuMeterOnDemandPanel({ dcuSerial, meterSerial }) {
  const c = useThemeColors();

  // ONE action, not two. This pane used to also carry a "Refresh from DCU"
  // button that read /dcus/{d}/profiles/daily — a LIVE over-the-air read of
  // the DCU's aggregate profile (0.0.99.2.18.255), whose result it then
  // recovered by regex-matching a Python `bytearray(b'...')` repr. Three
  // things were wrong with it:
  //
  //   * that profile is EMPTY FLEET-WIDE, so the usual answer was "No data
  //     collected yet for this meter" no matter how healthy the meter was;
  //   * it fired automatically on mount, and a DCU serves ONE task at a
  //     time — so it raced the SAP lookup below for the same slot. On
  //     2026-08-25 05:58 both requests hung 33 s and were aborted, which
  //     left "Read now" disabled on "Resolving…" indefinitely;
  //   * parsing a language-specific repr out of a string is not a decode.
  //
  // The vendor-parity single-register read below replaces it entirely.

  // ── Live single-register read, vendor parity ──────────────────────
  // The block above shows the DCU's STORED daily profile. This reads the
  // meter itself, right now, the only way the vendor ever does: one
  // GET-request-Normal addressed to the meter's SAP on the DCU's existing
  // Management association (see /api/dcus/{d}/sap-read and the decode in its
  // docstring — 4 such frames in 1919, five months apart, always this shape).
  //
  // NOT a profile read. Every profile-over-SAP attempt on this fleet returns
  // 0x06/0x08 or times out, for healthy meters as well as sick ones —
  // measured 2026-08-24 on 129235042049 across SAPs 2, 4, 8 and 9.
  // Same three panels the direct-meter On Demand tab offers, so an operator
  // sees the SAME metrics whether a meter is wired directly or sits behind a
  // DCU. Membership comes from the SERVER (/read-groups -> CAS_READ_GROUPS),
  // not a copy here: the direct tab reads a whole group in one session, this
  // path can only address one register per frame, and the two must not drift.
  const PANELS = [
    { key: 'status', group: 'device_info',   label: '📋 Status', hint: 'serial, firmware, LDN, SIM' },
    { key: 'energy', group: 'energy_all',    label: '⚡ Energy', hint: 'import/export totals + T1–T4' },
    { key: 'line',   group: 'instantaneous', label: '📈 Line',   hint: 'V, A, power, PF, frequency' },
  ];

  const [groups, setGroups] = React.useState(null);
  // Selection is PER REGISTER, not per group.
  //
  // It used to be three group toggles, which meant the smallest thing an
  // operator could ask for was a whole group — 18 registers for Line. Over a
  // DCU each register is its own ~3 s DLMS frame (one task at a time, no
  // batching possible), so "I want to check phase voltages" cost a 55 s sweep
  // of everything including power factor and frequency. The direct-meter tab
  // gets away with group granularity because it reads a whole group inside
  // ONE session; this path cannot. Picking three registers here now costs
  // ~9 s instead of ~2 minutes for all 37.
  const [selectedObis, setSelectedObis] = React.useState(
    () => new Set(loadDcuOnDemandPrefs()));
  const [progress, setProgress] = React.useState(null);
  const stopRef = React.useRef(false);

  React.useEffect(() => {
    let dead = false;
    api('/read-groups')
      .then(g => { if (!dead) setGroups(g || {}); })
      .catch(() => { if (!dead) setGroups({}); });
    return () => { dead = true; };
  }, []);

  const regsFor = (key) => {
    const p = PANELS.find(x => x.key === key);
    return (groups && p && groups[p.group]) || [];
  };
  // Persist the operator's picks. Live reads over a DCU are slow enough that
  // people settle on a handful of registers and come back to the same ones on
  // the next meter; re-ticking them every visit is pure friction.
  const setPicks = (next) => {
    setSelectedObis(next);
    saveDcuOnDemandPrefs(Array.from(next));
  };
  const toggleObis = (obis) => {
    const n = new Set(selectedObis);
    if (n.has(obis)) n.delete(obis); else n.add(obis);
    setPicks(n);
  };
  const setGroupAll = (key, on) => {
    const n = new Set(selectedObis);
    for (const r of regsFor(key)) { if (on) n.add(r.obis); else n.delete(r.obis); }
    setPicks(n);
  };
  const setEveryone = (on) => {
    if (!on) { setPicks(new Set()); return; }
    const n = new Set();
    for (const p of PANELS) for (const r of regsFor(p.key)) n.add(r.obis);
    setPicks(n);
  };
  // Read order follows the panel order so results appear top-to-bottom.
  const chosen = PANELS
    .flatMap(p => regsFor(p.key)
      .filter(r => selectedObis.has(r.obis))
      .map(r => ({ ...r, panel: p.key })));

  const [sap, setSap] = React.useState(null);
  const [sapErr, setSapErr] = React.useState(null);
  const [snapAt, setSnapAt] = React.useState(null);
  const [live, setLive] = React.useState({});     // obis -> {value|error|pending}
  const [liveBusy, setLiveBusy] = React.useState(false);

  React.useEffect(() => {
    let dead = false;
    setSap(null); setSapErr(null); setSnapAt(null); setLive({}); setProgress(null);
    (async () => {
      try {
        // The STORED parameter table, NOT a live read.
        //
        // This used to call /dcus/{d}/meters, whose default source=parameter
        // re-reads 0.0.21.0.1.255 over the air. That read is documented
        // flaky, and on 2026-08-25 05:58 it hung 33 s and was aborted —
        // leaving this button disabled on "Resolving…" with no way forward.
        // param-snapshot is the same table as last confirmed BY the device,
        // served with zero device I/O, so resolving a SAP costs nothing and
        // never competes with the read the operator actually asked for.
        const snap = await api(
          `/dcus/${encodeURIComponent(dcuSerial)}/param-snapshot`);
        if (dead) return;
        const rows = snap?.fields || [];
        const want = String(meterSerial || '');
        const suffix = want.slice(-6);
        // PLC/RF meters carry an LDN like "HLY0040300356914" that embeds the
        // serial's last 6 digits; RS-485 meters may report the bare serial.
        // Match both. `logical_dev_name` is accepted too so this keeps
        // working if it is ever pointed back at a /meters-shaped payload.
        const hit = rows.find(r => {
          const ldn = String(r.ldn ?? r.logical_dev_name ?? '');
          return ldn === want || (suffix && ldn.endsWith(suffix));
        });
        if (hit) { setSap(hit.sap_id); setSnapAt(snap?.read_at || null); }
        else setSapErr(rows.length ? 'not-in-table' : 'no-snapshot');
      } catch (e) {
        if (!dead) setSapErr('failed');
      }
    })();
    return () => { dead = true; };
  }, [dcuSerial, meterSerial]);

  const readLive = async () => {
    if (!sap || liveBusy || !chosen.length) return;
    stopRef.current = false;
    setLiveBusy(true);
    setLive(Object.fromEntries(chosen.map(r => [r.obis, { pending: true }])));
    setProgress({ done: 0, total: chosen.length });
    // SEQUENTIALLY: a DCU serves one task at a time, and each read is a
    // separate DLMS frame. Firing them together would queue on the same
    // connection and desynchronise it. Measured 2.4-3.9 s per register on
    // 129255053336, so a full 37-register sweep is ~2 minutes — which is
    // why this is interruptible and shows a count.
    for (const r of chosen) {
      if (stopRef.current) {
        // Drop the ones we never got to rather than leaving them
        // spinning forever.
        setLive(p => {
          const n = { ...p };
          for (const k of Object.keys(n)) if (n[k].pending) delete n[k];
          return n;
        });
        break;
      }
      try {
        const res = await api(
          `/dcus/${encodeURIComponent(dcuSerial)}/sap-read/${sap}/${r.obis}`
          + `?class_id=${r.class_id}&attribute=2`);
        // Prefer the unit the METER reported over our static OBIS catalogue:
        // the server now reads attr 3 and returns the scaled value alongside
        // the DLMS unit code it came with. `scaled: false` means the meter
        // would not give up its scaler, and the raw integer is flagged rather
        // than dressed up in a unit we only assumed.
        setLive(p => ({ ...p, [r.obis]: {
          value: res.value, took: res.took_s,
          unit: res.unit != null ? res.unit : r.unit,
          scaled: res.scaled !== false,
        } }));
      } catch (e) {
        setLive(p => ({ ...p, [r.obis]: { error: e.message || String(e) } }));
      }
      setProgress(p => (p ? { ...p, done: p.done + 1 } : p));
    }
    setLiveBusy(false);
  };

  const pill = (on) => ({
    padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
    border: `1px solid ${on ? c.accent : c.border}`, borderRadius: '14px',
    background: on ? c.accent + '22' : 'transparent',
    color: on ? c.accent : c.textSecondary, fontWeight: on ? 600 : 400,
  });

  return (
    <div style={{ padding: '10px' }}>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600 }}>On-Demand via DCU {dcuSerial}</div>
        <div style={{ fontSize: '11px', color: c.textMuted }}>
          Reads the meter itself, right now, over the DCU's existing
          management session. Nothing here is served from cache.
        </div>
      </div>

      {/* ── Live read ── */}
      <div style={{ border: `1px solid ${c.border}`, borderRadius: '8px',
        padding: '10px', marginBottom: '14px', background: c.bgCard }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600 }}>Read the meter now</div>
            <div style={{ fontSize: '11px', color: c.textMuted, lineHeight: 1.5 }}>
              {sap ? (
                <>Single-register reads addressed to <strong>SAP {sap}</strong> — the only
                  per-meter operation the vendor performs. Expect 2–16 s each, run one at a
                  time. A miss usually means the DCU cannot reach the meter over PLC/RF.
                  {snapAt && <> · SAP from the parameter-table snapshot taken{' '}
                    {fmtAgeSince(new Date(snapAt * 1000).toISOString())} ago.</>}</>
              ) : sapErr === 'not-in-table' ? (
                <>This meter is not in DCU {dcuSerial}'s stored parameter table, so it has
                  no SAP to address. Register it on the DCU first, or re-read the table
                  from Device Management if you believe the snapshot is stale.</>
              ) : sapErr === 'no-snapshot' ? (
                <>No stored parameter table for DCU {dcuSerial} yet. Open its parameter
                  table once from Device Management and this will resolve.</>
              ) : sapErr ? (
                <>Could not load DCU {dcuSerial}'s parameter table snapshot.</>
              ) : 'Resolving this meter’s SAP from the DCU’s stored parameter table…'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            {liveBusy && (
              <button className="button button-sm button-secondary"
                onClick={() => { stopRef.current = true; }}>Stop</button>
            )}
            <button className="button button-sm" onClick={readLive}
              disabled={!sap || liveBusy || !chosen.length}>
              {liveBusy
                ? `Reading… ${progress ? `${progress.done}/${progress.total}` : ''}`
                : '📡 Read now'}
            </button>
          </div>
        </div>

        {/* Metric selection — per register, grouped. Each tick is one ~3 s
            DLMS frame over the DCU, so the count and estimate below are what
            the operator is actually committing to. */}
        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center',
            flexWrap: 'wrap', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              color: c.textMuted }}>Metrics to read</span>
            <button type="button" className="button button-sm button-secondary"
              disabled={liveBusy || groups === null}
              onClick={() => setEveryone(true)}>Select all</button>
            <button type="button" className="button button-sm button-secondary"
              disabled={liveBusy || !selectedObis.size}
              onClick={() => setEveryone(false)}>Clear</button>
            <span style={{ fontSize: '11px', color: c.textMuted }}>
              {groups === null
                ? 'loading metrics…'
                : chosen.length
                  ? `${chosen.length} selected · about ${_estimate(chosen.length)}, one at a time`
                  : 'nothing selected — tick the metrics you want'}
            </span>
          </div>

          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px' }}>
            {PANELS.map(p => {
              const regs = regsFor(p.key);
              const on = regs.filter(r => selectedObis.has(r.obis)).length;
              const allOn = regs.length > 0 && on === regs.length;
              return (
                <div key={p.key} style={{ border: `1px solid ${c.border}`,
                  borderRadius: '6px', padding: '8px 10px', background: c.bgInput }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px',
                      cursor: liveBusy ? 'default' : 'pointer', fontSize: '12px',
                      fontWeight: 600, color: c.textLabel }} title={p.hint}>
                      <input type="checkbox" disabled={liveBusy || !regs.length}
                        checked={allOn}
                        // Half-ticked when only some of the group is chosen, so
                        // a partial selection is not indistinguishable from none.
                        ref={el => { if (el) el.indeterminate = on > 0 && !allOn; }}
                        onChange={e => setGroupAll(p.key, e.target.checked)} />
                      {p.label}
                    </label>
                    <span style={{ fontSize: '10px', color: c.textMuted }}>
                      {on}/{regs.length}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px',
                    maxHeight: '190px', overflowY: 'auto' }}>
                    {regs.map(r => (
                      <label key={r.obis} title={r.obis}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px',
                          fontSize: '11px', color: c.textSecondary,
                          cursor: liveBusy ? 'default' : 'pointer' }}>
                        <input type="checkbox" disabled={liveBusy}
                          checked={selectedObis.has(r.obis)}
                          onChange={() => toggleObis(r.obis)} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap' }}>{r.name}</span>
                      </label>
                    ))}
                    {!regs.length && (
                      <span style={{ fontSize: '11px', color: c.textMuted }}>—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {Object.keys(live).length > 0 && PANELS.map(p => {
          // Drive off what was actually READ, not off the current ticks —
          // otherwise un-ticking a register after a read would make its
          // result vanish from the screen.
          const regs = regsFor(p.key).filter(r => live[r.obis]);
          if (!regs.length) return null;
          return (
            <div key={p.key} style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                textTransform: 'uppercase', color: c.textMuted, marginBottom: '6px' }}>
                {p.label}
              </div>
              <div style={{ display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '8px' }}>
                {regs.map(r => {
                  const st = live[r.obis];
                  return (
                    <div key={r.obis} style={{ padding: '8px 10px', borderRadius: '6px',
                      background: c.bgInput, border: `1px solid ${c.border}` }}>
                      <div style={{ fontSize: '10px', color: c.textMuted }} title={r.obis}>{r.name}</div>
                      {st.pending && <div style={{ fontSize: '13px', color: '#00e5ff' }}>reading…</div>}
                      {st.error && <div style={{ fontSize: '11px', color: '#f43f5e' }}>{st.error}</div>}
                      {st.value !== undefined && (() => {
                        // Same formatter the direct-meter tab uses, so a
                        // register reads identically on both paths.
                        const f = typeof st.value === 'number'
                          ? formatMetric(st.value, st.unit || '')
                          : { text: String(st.value), unit: st.unit || '' };
                        return (
                          <div style={{ fontSize: '15px', fontWeight: 600, color: '#22c55e',
                            wordBreak: 'break-word' }}>
                            {f.text}{f.unit ? ` ${f.unit}` : ''}
                            {st.scaled === false && (
                              <span title="The meter did not return a scaler-unit for this register, so this is the raw value."
                                style={{ fontSize: '10px', color: '#f59e0b', fontWeight: 400 }}>
                                {' '}⚠ unscaled
                              </span>
                            )}
                            <span style={{ fontSize: '10px', color: c.textMuted, fontWeight: 400 }}>
                              {' '}({st.took}s)
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Collected Data — per-meter daily-collection coverage from the SERVER's
// stored profile_rows (via /meters/{serial}/collection-stats), NOT the DCU's
// on-device aggregate profile (0.0.99.2.18.255). On this fleet the scheduler
// pulls each meter directly, so the DCU aggregate is empty even though we hold
// the data. This shows what we've actually collected per meter on the DCU.
// Coverage colour: full = green, none = red, partial = amber. Used by the
// separate Daily / LP columns so a half-dead stream is visible at a glance.
function _covColor(n, total, c) {
  if (!total) return c.textMuted;
  if (n >= total) return '#22c55e';
  if (n === 0) return '#ef4444';
  return '#f59e0b';
}

function DcuCollectedDataSection({ meters, selectedDcu }) {
  const c = useThemeColors();
  const [rows, setRows] = React.useState(null);   // null = not loaded yet
  const [busy, setBusy] = React.useState(false);
  const [ts, setTs] = React.useState(null);
  const [search, setSearch] = React.useState('');

  // Stale-DCU guard. Reset during RENDER (an effect runs after paint, which
  // flashed the PREVIOUS DCU's rows under the new DCU's header), and gate the
  // async setter so a slow load for the DCU you just left can't land here.
  const curRef = React.useRef(selectedDcu);
  curRef.current = selectedDcu;
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) {
    setPrevDcu(selectedDcu);
    setRows(null); setTs(null); setSearch(''); setBusy(false);
  }

  const load = async () => {
    const forDcu = selectedDcu;
    setBusy(true);
    try {
      const queue = meters.slice();
      const out = [];
      const worker = async () => {
        while (queue.length) {
          const m = queue.shift();
          if (curRef.current !== forDcu) return;   // DCU switched — abandon
          try {
            const r = await api(`/meters/${m.ldn}/collection-stats?days=7`);
            const days = r?.days || [];
            // Daily billing and load profile are SEPARATE streams and fail
            // independently — a meter can bill fine for days while its load
            // profile is dead. The old single "Days OK" counted a day only when
            // BOTH were complete, so a healthy-billing meter with no LP read
            // "0/7" and looked totally dead. Count and show them separately.
            const dailyDays = days.filter(d => (d.daily_billing?.stored || 0) > 0);
            const lpDays = days.filter(d => (d.load_profile?.stored || 0) > 0);
            const lastDaily = dailyDays.length ? dailyDays[dailyDays.length - 1].date : null;
            const lastLp = lpDays.length ? lpDays[lpDays.length - 1].date : null;
            const dailyOk = dailyDays.length;
            const lpOk = days.filter(d => (d.load_profile?.stored || 0) >= 90).length;
            const today = days.length ? days[days.length - 1] : null;
            out.push({
              ldn: m.ldn, sap: m.sap_id, lastDaily, lastLp, dailyOk, lpOk,
              total: days.length,
              lpToday: today ? `${today.load_profile?.stored ?? 0}/${today.load_profile?.expected ?? '?'}` : '—',
              todayStatus: today?.status || '—',
            });
          } catch {
            out.push({ ldn: m.ldn, sap: m.sap_id, error: true });
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, Math.max(1, meters.length)) }, worker));
      if (curRef.current !== forDcu) return;       // switched mid-load — drop it
      out.sort((a, b) => (typeof a.sap === 'number' ? a.sap : 99999) - (typeof b.sap === 'number' ? b.sap : 99999));
      setRows(out); setTs(new Date());
    } finally { if (curRef.current === forDcu) setBusy(false); }
  };

  const filtered = (rows || []).filter(r => !search || String(r.ldn).includes(search.trim()));
  const th = { textAlign: 'left', padding: '5px 6px', borderBottom: `1px solid ${c.border}`, fontSize: '11px', color: c.textMuted, textTransform: 'uppercase' };
  const td = { padding: '6px', borderBottom: `1px solid ${c.border}22`, fontSize: '12px' };
  const statusColor = (s) => s === 'ok' ? '#22c55e' : s === 'partial' ? '#f59e0b' : (s === 'missing' ? '#ef4444' : c.textMuted);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>Collected Data (last 7 days)</div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {rows && <input placeholder="Meter No." value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '5px 10px', fontSize: '12px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', width: '130px' }} />}
          {ts && <span style={{ fontSize: '11px', color: c.textMuted }}>Loaded {ts.toLocaleTimeString()}</span>}
          <button style={_dcuBtn('#1e40af', busy)} disabled={busy || meters.length === 0} onClick={load}>{busy ? 'Loading…' : (rows ? '↻ Reload' : '📊 Load collection status')}</button>
        </div>
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '10px' }}>
        Per-meter coverage stored on the server (pulled directly from each meter). <strong>Daily</strong> and <strong>LP</strong> are counted separately — they fail independently, so a meter can bill fine for days while its load profile is dead. The DCU's on-device aggregate profile is not used here — it's empty on this fleet.
      </div>

      {!rows ? (
        <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic', padding: '10px' }}>
          {busy ? 'Loading collection status…' : (meters.length === 0 ? 'No meters on this DCU.' : 'Click “📊 Load collection status” to see what each meter has collected.')}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Point No</th>
              <th style={th}>Meter No</th>
              <th style={th}>Last daily billing</th>
              <th style={th}>Daily (7d)</th>
              <th style={th}>Last load profile</th>
              <th style={th}>LP (7d)</th>
              <th style={th}>Load profile today</th>
              <th style={th}>Today</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.ldn}>
                <td style={{ ...td, fontWeight: 600 }}>{r.sap}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{r.ldn}</td>
                {r.error ? (
                  <td style={{ ...td, color: c.textMuted }} colSpan={6}>no stored data</td>
                ) : (
                  <>
                    <td style={{ ...td, color: r.lastDaily ? c.textPrimary : c.textMuted }}>{r.lastDaily || '— none —'}</td>
                    <td style={{ ...td, color: _covColor(r.dailyOk, r.total, c), fontWeight: 600 }}>{r.dailyOk}/{r.total}</td>
                    <td style={{ ...td, color: r.lastLp ? c.textPrimary : c.textMuted }}>{r.lastLp || '— none —'}</td>
                    <td style={{ ...td, color: _covColor(r.lpOk, r.total, c), fontWeight: 600 }}>{r.lpOk}/{r.total}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{r.lpToday}</td>
                    <td style={{ ...td, color: statusColor(r.todayStatus), fontWeight: 600 }}>{r.todayStatus}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


// paramTable has TWO shapes and every consumer must tolerate both:
//   * legacy — {value: "<python repr of the raw array>"} straight from
//     /read-raw, which the consumers parse with a matchAll regex;
//   * structured — [{sap_id, ldn}, ...] from the shared server snapshot or an
//     upload's verified read-back, where we already have the fields.
//
// Shipping the structured shape without teaching the consumers about it
// crashed the page: `paramTable.value.matchAll is not a function` on
// Device Management (bundle 906c2ea22e, 2026-07-30). Normalising to
// {sap_id, ldn} pairs in ONE place is what keeps the two shapes from having
// to be remembered at four separate call sites.
const _paramPairs = (pt) => {
  if (!pt) return [];
  if (Array.isArray(pt)) {
    return pt
      .filter(r => r && r.ldn)
      .map(r => ({ sap_id: r.sap_id, ldn: String(r.ldn) }));
  }
  const raw = pt.value;
  // THIRD shape: {value: [{sap_id, ldn}, ...]}. The localStorage cache stores
  // whatever it was given — and the shared snapshot hands it the STRUCTURED
  // array — while the DCU-switch path re-wraps it as {value: …}. Unwrapping
  // only strings meant a cache seeded from the snapshot parsed to ZERO pairs
  // while paramReadState still read 'ok', so every registered meter was
  // reported "Not on DCU". Observed 2026-07-31 on 129205034829: the device
  // held all 43 rows, the snapshot agreed, and the panel claimed 43 of 43 were
  // missing. Same family as the crash this helper was written to fix — the two
  // shapes were never really two.
  if (Array.isArray(raw)) {
    return raw
      .filter(r => r && r.ldn)
      .map(r => ({ sap_id: r.sap_id, ldn: String(r.ldn) }));
  }
  if (typeof raw !== 'string') return [];
  const out = [];
  try {
    for (const m of raw.matchAll(/\[(\d+),\s*bytearray\(b'([^']+)'\)/g)) {
      out.push({ sap_id: parseInt(m[1]), ldn: m[2] });
    }
  } catch (_) { /* unusable read — caller treats as no device truth */ }
  return out;
};

function DcuRegisteredMetersSection({ paramTable, selectedDcu, onDeregistered }) {
  const c = useThemeColors();
  const [deregBusy, setDeregBusy] = React.useState(false);
  const [routedMeters, setRoutedMeters] = React.useState([]);

  // Pull the server-side routing map as the primary source. Some
  // Holley DCU firmwares (e.g. mfr 123224) return the Meter Parameter
  // Table via block-transfer that our raw-read path currently drops,
  // so ``paramTable`` comes back empty even when the DCU has 200+
  // meters configured. The ``routes`` source reflects what the server
  // knows from any of: DCU identification auto-register, event-log
  // rescan, profile-row extraction, or admin bulk-import. If the
  // parameter-table read worked too, we merge both and dedup.
  // Stale-DCU guard: clear during render so the previous DCU's rows never
  // paint under the new header, and ignore a late routes reply for a DCU the
  // operator has already left.
  const curRef = React.useRef(selectedDcu);
  curRef.current = selectedDcu;
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) { setPrevDcu(selectedDcu); setRoutedMeters([]); }
  React.useEffect(() => {
    if (!selectedDcu) { setRoutedMeters([]); return; }
    const forDcu = selectedDcu;
    api(`/dcus/${forDcu}/meters?source=routes`)
      .then(r => { if (curRef.current === forDcu) setRoutedMeters(r?.meters || []); })
      .catch(() => { if (curRef.current === forDcu) setRoutedMeters([]); });
  }, [selectedDcu]);

  // Parse the parameter table value string into meter entries
  const meters = React.useMemo(() => {
    // Collect from both sources so the tab shows whichever is populated.
    const bySerial = new Map();
    for (const r of routedMeters) {
      const ldn = r.ldn || r.meter_serial;
      if (!ldn) continue;
      bySerial.set(ldn, {
        sap_id: r.sap_id ?? '—',
        ldn,
        source: 'routes',
      });
    }
    if (paramTable?.value && paramTable.value !== 'None') {
      try {
        const matches = _paramPairs(paramTable).map(p => [null, String(p.sap_id), p.ldn]);
        for (const m of matches) {
          bySerial.set(m[2], { sap_id: parseInt(m[1]), ldn: m[2], source: 'dcu' });
        }
      } catch { /* ignore — fall back to routes only */ }
    }
    return Array.from(bySerial.values()).sort((a, b) => {
      const sa = typeof a.sap_id === 'number' ? a.sap_id : 99999;
      const sb = typeof b.sap_id === 'number' ? b.sap_id : 99999;
      return sa - sb;
    });
  }, [paramTable, routedMeters]);

  const handleDeregisterAll = async () => {
    if (!window.confirm('Deregister ALL meters from this DCU? The DCU will stop collecting data from them.')) return;
    setDeregBusy(true);
    try {
      await api(`/meters/${selectedDcu}/deregister-meters`, { method: 'DELETE' });
      alert('All meters deregistered');
      if (onDeregistered) onDeregistered();
    } catch (e) {
      alert('Deregister failed: ' + (e.message || e));
    }
    setDeregBusy(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
          Registered Meters ({meters.length})
        </div>
        {meters.length > 0 && (
          <button onClick={handleDeregisterAll} disabled={deregBusy}
            style={{ padding: '4px 12px', fontSize: '11px', background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '4px', cursor: deregBusy ? 'wait' : 'pointer' }}>
            {deregBusy ? 'Deregistering...' : 'Deregister All'}
          </button>
        )}
      </div>
      {meters.length > 0 ? (
        <table style={{ fontSize: '11px', width: '100%' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${c.border}` }}>
              <th style={{ textAlign: 'left', padding: '4px' }}>SAP ID</th>
              <th style={{ textAlign: 'left', padding: '4px' }}>Meter Serial / LDN</th>
              <th style={{ textAlign: 'left', padding: '4px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {meters.map((m, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${c.border}22` }}>
                <td style={{ padding: '6px 4px', fontWeight: 600 }}>{m.sap_id}</td>
                <td style={{ padding: '6px 4px', fontFamily: 'monospace' }}>{m.ldn}</td>
                <td style={{ padding: '6px 4px' }}>
                  <span style={{ color: '#22c55e', fontSize: '10px' }}>Registered</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic', padding: '10px' }}>
          No meters registered. Go to the Blacklist tab to discover and register meters.
        </div>
      )}
    </div>
  );
}

function DcuBlacklistSection({ blacklist, blacklistLoading, blacklistTs, onReadBlacklist, onRefreshCached, paramTable, routedMeters, selectedDcu, onRegistered, onStaged }) {
  const c = useThemeColors();
  const { user } = useAuth();
  const isAdmin = userCan(user, 'manage_dcu_meters');
  const [selected, setSelected] = React.useState(new Set());
  const [moveRow, setMoveRow] = React.useState(null);   // registered meter being moved to another DCU
  const [showHidden, setShowHidden] = React.useState(false);  // reveal hidden (dismissed) rows
  const [hideBusy, setHideBusy] = React.useState(false);

  // Meters already registered on this DCU — this gates whether a blacklist
  // entry can be approved, so it MUST reflect the device, not our cache.
  //
  // It used to union the local routing map with the param table. That made a
  // cleared meter un-re-addable: after a Clear All the device table is empty
  // but the map still lists every meter, so every blacklist row showed
  // "Registered" and Quick Approve skipped them (observed on 129205035235,
  // 2026-07-25 — 83 meters cleared on the device yet impossible to re-add).
  //
  // Now: when we have a TRUSTED param-table read (including one that says the
  // table is empty), the device alone decides. The map is only consulted when
  // the read failed or has not been attempted, where being conservative avoids
  // offering a duplicate registration.
  const registeredLdns = React.useMemo(() => {
    const s = new Set();
    const add = (v) => {
      if (!v) return;
      s.add(String(v));
      s.add(String(v).replace(/\D/g, '').slice(-12));
    };
    const readOk = paramTable && paramTable.value != null && paramTable.value !== 'None';
    if (readOk) {
      for (const p of _paramPairs(paramTable)) add(p.ldn);
      return s;   // device truth — an empty table means nothing is registered
    }
    for (const r of (routedMeters || [])) { add(r.ldn); add(r.meter_serial); }
    return s;
  }, [paramTable, routedMeters]);

  // Did we actually read the device? Drives whether "Registered" is a FACT or
  // just our cache talking. When we could not read (DCU session down, flaky
  // read, or simply not refreshed yet) we must NOT assert "Registered" — that
  // both misleads and blocks re-adding a meter that was genuinely cleared.
  const paramReadOk = !!(paramTable && paramTable.value != null && paramTable.value !== 'None');
  const _isReg = (dm) => {
    const a = String(dm.communication_address || '');
    return registeredLdns.has(a) || registeredLdns.has(a.replace(/\D/g, '').slice(-12));
  };
  const [showBulkConfig, setShowBulkConfig] = React.useState(false);
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const toast = useToast();
  const [bulkConfig, setBulkConfig] = React.useState({
    // Default to G3 PLC — the dominant deployment. Operators on the
    // legacy RS-485 path can still pick it from the dropdown. For
    // PLC/RF the Comm Addr in the DCU param table is the full meter
    // serial (per Holley CAS tool), not the RS-485 last4+1000 convention.
    commType: '6', clientAddr: '17', password: '00000000',
    ctRatio: '5/5', ptRatio: '220/220',
    // '' = auto-detect from serial when the modal opens.
    meterType: '',
  });
  const PT_RATIOS = ['220/220', '380/220', '6000/220', '10000/220', '20000/220', '35000/220', '110000/220', '35000/100', '40000/100'];

  const allItems = blacklist?.blacklist || [];
  // Only a TRUSTED device read may mark a discovered meter as already
  // registered (and thereby hide it from approval). Without one we treat every
  // row as actionable — otherwise a cleared DCU whose session happens to be
  // down leaves the operator unable to re-add anything, which is exactly the
  // dead end hit on 129205035235 (2026-07-25). Re-approving something that IS
  // still registered is harmless: the provision path is read-back verified.
  const unregistered = allItems.filter(dm => !(paramReadOk && _isReg(dm)));
  const registeredFromBlacklist = allItems.filter(dm => paramReadOk && _isReg(dm));
  const hiddenItems = unregistered.filter(dm => dm.hidden);
  const visibleUnregistered = unregistered.filter(dm => !dm.hidden);
  // Rows the operator can act on (select / register / hide). Hidden rows join
  // the acting set only when "show hidden" is toggled on (so they can be
  // restored or registered). `displayItems` is what the table renders:
  // registered rows always, plus the acting set.
  const items = showHidden ? unregistered : visibleUnregistered;
  const displayItems = allItems.filter(dm => _isReg(dm) || showHidden || !dm.hidden);
  const lastClickedRef = React.useRef(null);

  const doHide = async (addresses) => {
    const addrs = Array.from(new Set(addresses)).filter(Boolean);
    if (!addrs.length) return;
    setHideBusy(true);
    try {
      await api(`/meters/${selectedDcu}/dcu-blacklist/hide`,
        { method: 'POST', body: JSON.stringify({ addresses: addrs }) });
      setSelected(new Set());
      if (onRefreshCached) await onRefreshCached();
    } catch (e) { alert(`Hide failed: ${e?.message || e}`); }
    finally { setHideBusy(false); }
  };
  const doRestore = async (addresses, all = false) => {
    setHideBusy(true);
    try {
      await api(`/meters/${selectedDcu}/dcu-blacklist/restore`,
        { method: 'POST', body: JSON.stringify(all ? { all: true } : { addresses: Array.from(addresses) }) });
      if (onRefreshCached) await onRefreshCached();
    } catch (e) { alert(`Restore failed: ${e?.message || e}`); }
    finally { setHideBusy(false); }
  };
  // "Appeared once and never again": preselect visible unregistered rows the
  // DCU intercepted at most once, ready to Hide.
  const selectSeenOnce = () => {
    const once = visibleUnregistered
      .filter(dm => (dm.intercept_count ?? 0) <= 1)
      .map(dm => dm.communication_address);
    setSelected(new Set(once));
    lastClickedRef.current = null;
  };
  const clearList = () => {
    const addrs = visibleUnregistered.map(dm => dm.communication_address);
    if (!addrs.length) return;
    if (!confirm(
      `Hide all ${addrs.length} unregistered discovered meter(s) from this DCU's Black List?\n\n` +
      `• They leave the view AND the abnormal-PLC count\n` +
      `• Nothing on the DCU changes — this is reversible\n` +
      `• Any that transmit again re-appear automatically\n` +
      `• Restore them anytime via “Show hidden”`
    )) return;
    doHide(addrs);
  };
  // DESTRUCTIVE, on-device: executes the vendor blacklist-clear script (244)
  // on the DCU itself — wipes its discovered-meter table on the device.
  // Admin-only, kill-switch-gated server-side, typed-serial confirm here.
  const clearOnDcu = async () => {
    const typed = window.prompt(
      `⚠ Clear the blacklist ON THE DCU ${selectedDcu} itself.\n\n` +
      `This runs the vendor clear script (244) on the device — it wipes the ` +
      `DCU's own discovered-meter table. This is NOT the same as “Clear List”, ` +
      `which only hides rows in this view.\n\n` +
      `• The DCU re-lists meters as they transmit again\n` +
      `• Runs on the DCU's reset script object — admin only\n\n` +
      `Type the DCU serial to confirm:`
    );
    if (typed == null) return;
    if (typed.trim() !== String(selectedDcu)) { alert('Serial did not match — cancelled.'); return; }
    setHideBusy(true);
    try {
      const r = await api(`/dcus/${selectedDcu}/clear-blacklist`,
        { method: 'POST', body: JSON.stringify({ confirm_serial: String(selectedDcu) }) });
      alert(r?.note || 'DCU blacklist cleared on the device.');
      setSelected(new Set());
      if (onRefreshCached) await onRefreshCached();
    } catch (e) { alert(`Clear on DCU failed: ${e?.message || e}`); }
    finally { setHideBusy(false); }
  };

  const toggleOne = (addr, event) => {
    const idx = items.findIndex(dm => dm.communication_address === addr);
    if (event?.shiftKey && lastClickedRef.current !== null && lastClickedRef.current !== idx) {
      // Shift+click: select range between last clicked and current
      const from = Math.min(lastClickedRef.current, idx);
      const to = Math.max(lastClickedRef.current, idx);
      setSelected(prev => {
        const next = new Set(prev);
        for (let i = from; i <= to; i++) {
          const a = items[i]?.communication_address;
          if (a) next.add(a);
        }
        return next;
      });
    } else {
      // Normal click: toggle single
      setSelected(prev => {
        const next = new Set(prev);
        next.has(addr) ? next.delete(addr) : next.add(addr);
        return next;
      });
    }
    lastClickedRef.current = idx;
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(dm => dm.communication_address)));
  };

  const handleBulkRegister = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    let ok = 0, fail = 0;
    let sapId = 2;
    let firstErr = '';
    // Meters registered on ANOTHER DCU are not registered here a second time:
    // stage-meters turns them into a verified move (deregister there, then
    // register here). register-meter refuses them with a 409 as a backstop.
    const elsewhere = new Set(allItems
      .filter(dm => dm.registered_elsewhere && selected.has(dm.communication_address))
      .map(dm => String(dm.communication_address)));
    let moveNote = '';
    if (elsewhere.size) {
      try {
        const st = await api(`/dcus/${selectedDcu}/stage-meters`, {
          method: 'POST',
          body: JSON.stringify({ meters: [...elsewhere].map(a => ({ meter_serial: a })) }),
        });
        moveNote = _stageMovesNote(st);
      } catch (e) { fail += elsewhere.size; firstErr = firstErr || (e.message || String(e)); }
    }
    for (const serial of selected) {
      if (elsewhere.has(String(serial))) continue;
      try {
        // hdlc_addr is only meaningful for RS-485 (comm_type=1). For
        // PLC (6) and RF (7) the backend uses the full serial as BCD
        // bytes and ignores hdlc_addr — so send 0 to keep the param
        // honest rather than surfacing a bogus RS-485 number.
        const hdlcAddr = bulkConfig.commType === '1'
          ? parseInt(serial.slice(-4)) + 1000
          : 0;
        const params = new URLSearchParams({
          meter_serial: serial, sap_id: String(sapId++),
          comm_type: bulkConfig.commType, hdlc_addr: String(hdlcAddr),
          client_addr: bulkConfig.clientAddr, app_context: '1', auth_mech: '1',
        });
        // CT/PT and meter type flow to MeterAssignment + (via meter_type_override)
        // the DCU Meter Parameter Table's dev_type field.
        if (bulkConfig.ctRatio) params.set('ct_ratio', bulkConfig.ctRatio);
        if (bulkConfig.ptRatio) params.set('vt_ratio', bulkConfig.ptRatio);
        // Auto-detect meter type from serial if the bulk dropdown is
        // blank; operator's explicit pick always wins.
        const meterForm = bulkConfig.meterType !== ''
          ? bulkConfig.meterType
          : _deriveMeterTypeFromSerial(serial);
        const casCode = METER_TYPE_FORM_TO_CAS[String(meterForm)];
        if (casCode) params.set('meter_type_override', String(casCode));
        // queue=true: the write runs on the DCU's worker, which waits out a
        // dropped link and retries, instead of failing the rest of the loop.
        params.set('queue', 'true');
        const r = await api(`/meters/${selectedDcu}/register-meter?${params}`, { method: 'POST' });
        if (r && r.job_id) {
          bgJobs.add({ id: r.job_id, dcu: String(selectedDcu), op: `Register ${serial}`,
                       onDone: () => { if (onRegistered) onRegistered(); } });
        }
        ok++;
      } catch (e) { fail++; firstErr = firstErr || (e.message || String(e)); }
    }
    setBulkLoading(false);
    setShowBulkConfig(false);
    setSelected(new Set());
    toast.push(`Queued ${ok} registration(s) on ${selectedDcu}${fail ? `, ${fail} could not be queued` : ''} — `
      + 'they run on the DCU\'s worker (see the jobs tray) and wait for the DCU if it drops.'
      + (moveNote ? ` ${moveNote}` : '')
      + (firstErr ? ` First error: ${firstErr}` : ''),
      fail ? 'warning' : 'success');
  };

  // Quick approve = single backend call that writes ALL selected meters
  // into the DCU Parameter Table in ONE batch and triggers the
  // aggregate-first + gap-fill catchup automatically. Uses Holley
  // defaults (G3 PLC, LLS auth, ASCII-zero password). For large
  // batches this is minutes-faster than the per-meter loop above,
  // because the DCU only does one Parameter Table write.
  const handleQuickApprove = async () => {
    if (selected.size === 0) return;
    if (!confirm(
      `Add ${selected.size} discovered meter(s) to this DCU?\n\n` +
      `• G3 PLC (6), LLS auth, default password; SAPs auto-assigned\n` +
      `• NOTHING is written to the DCU yet — they are added to the system and\n` +
      `  listed in Meters Setting as "Pending upload".\n` +
      `• Go to Meters Setting and click "Upload to DCU" to write them to the\n` +
      `  device in ONE verified operation.\n\n` +
      `For custom CT/PT, password, etc. use "Register Selected" instead.`
    )) return;
    setBulkLoading(true);
    try {
      // Async now: the endpoint enqueues the registration on the per-DCU worker
      // (the write is a slow chunked rebuild + read-back on big permissive DCUs)
      // and returns a job_id immediately, so the UI never hangs. Progress shows
      // in the Background jobs panel and survives navigating away.
      // STAGES only — no device I/O. The meters are created in our system and
      // appear in Meters Setting as "Pending upload"; the DCU is written once,
      // later, via Upload to DCU. This works even while the DCU is offline.
      const res = await api(
        `/dcus/${selectedDcu}/stage-meters`,
        {
          method: 'POST',
          body: JSON.stringify({
            meters: Array.from(selected).map(a => ({ meter_serial: String(a) })),
          }),
        }
      );
      if (res?.staged_add) {
        const cnt = res.staged_add.length;
        const moveNote = _stageMovesNote(res);
        toast.push(
          (cnt ? `${cnt} meter(s) added to the system — open Meters Setting and click "Upload to DCU" to write them to the device.` : 'Nothing staged for upload.')
          + (moveNote ? ` ${moveNote}` : ''),
          (res.skipped || []).length ? 'warning' : 'success',
        );
        setSelected(new Set());
        // IMMEDIATELY, and locally. This used to be
        // `setTimeout(onRegistered, 30000)` — a leftover from when this
        // endpoint wrote to the device and there was something to wait for.
        // stage-meters does "NO device I/O whatsoever" and allocates the SAP
        // synchronously, so the rows are ready the instant it returns; the
        // 30 s wait just meant operators pressed Refresh themselves.
        if (onStaged) onStaged();
      } else {
        toast.push('Approval did not queue — check the journal', 'error');
      }
    } catch (e) {
      toast.push(`Quick approve failed: ${e?.message || e}`, 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const fieldStyle = { padding: '6px 8px', borderRadius: '4px', border: `1px solid ${c.border}`, background: c.bgInput || c.bgCard, color: c.textPrimary, fontSize: '12px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#f59e0b' }}>
          Discovered Meters — {visibleUnregistered.length} unregistered
          {registeredFromBlacklist.length > 0 && (
            <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: 400, marginLeft: '8px' }}>
              {paramReadOk
                ? `(${registeredFromBlacklist.length} already registered)`
                : '(registration state UNVERIFIED — the DCU parameter table could not be read; all rows are actionable)'}
            </span>
          )}
          {hiddenItems.length > 0 && (
            <span style={{ fontSize: '11px', color: c.textMuted, fontWeight: 400, marginLeft: '8px' }}>
              · {hiddenItems.length} hidden
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {blacklistTs && (
            <span style={{ fontSize: '11px', color: c.textMuted }}>
              Last read {blacklistTs.toLocaleString()}{blacklist?.cached ? ' (cached)' : ''}
            </span>
          )}
          {/* Manual blacklist read — never auto-fires (it drives a DLMS
              multi-block read on the DCU). Admin clicks to scan/refresh. */}
          <button onClick={onReadBlacklist} disabled={blacklistLoading}
            title="Read the DCU's discovered-meter (blacklist) table over DLMS"
            style={{ padding: '5px 14px', fontSize: '12px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', cursor: blacklistLoading ? 'wait' : 'pointer', fontWeight: 600, opacity: blacklistLoading ? 0.6 : 1 }}>
            {blacklistLoading ? 'Reading…' : (blacklist === null ? '📡 Read Blacklist' : '↻ Re-read Blacklist')}
          </button>
        {selected.size > 0 && !showBulkConfig && (
          <>
            <button onClick={handleQuickApprove} disabled={bulkLoading}
              title="Single-batch Parameter Table write with Holley defaults + auto-trigger catchup. Faster for large approvals; no CT/PT customization."
              style={{ padding: '5px 14px', fontSize: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: bulkLoading ? 'wait' : 'pointer', fontWeight: 600, opacity: bulkLoading ? 0.6 : 1 }}>
              {bulkLoading ? 'Approving...' : `⚡ Quick Approve (${selected.size})`}
            </button>
            <button onClick={() => {
              // Auto-fill meterType from the first selected serial's 3rd
              // digit. If the selection is mixed (different types), we
              // still pick from the first — operator can override.
              const firstSerial = items.find(dm => selected.has(dm.communication_address))?.communication_address;
              const autoType = _deriveMeterTypeFromSerial(firstSerial || '');
              setBulkConfig(prev => ({ ...prev, meterType: autoType }));
              setShowBulkConfig(true);
            }}
              style={{ padding: '5px 14px', fontSize: '12px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
              Register Selected ({selected.size})
            </button>
            <button onClick={() => doHide(Array.from(selected))} disabled={hideBusy}
              title="Hide the selected discovered meters from this view and the abnormal-PLC count. Reversible; they re-appear if heard again."
              style={{ padding: '5px 14px', fontSize: '12px', background: 'transparent', color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: hideBusy ? 'wait' : 'pointer', fontWeight: 600, opacity: hideBusy ? 0.6 : 1 }}>
              🚫 Hide ({selected.size})
            </button>
          </>
        )}
        {/* Declutter controls — only meaningful once a read has happened. */}
        {(visibleUnregistered.length > 0 || hiddenItems.length > 0) && !showBulkConfig && (
          <>
            {visibleUnregistered.length > 0 && (
              <>
                <button onClick={selectSeenOnce} disabled={hideBusy}
                  title="Select unregistered meters the DCU intercepted at most once (appeared once, never again) — then Hide or Clear."
                  style={{ padding: '5px 12px', fontSize: '12px', background: 'transparent', color: c.textSecondary || c.textMuted, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                  Select seen-once
                </button>
                <button onClick={clearList} disabled={hideBusy}
                  title="Hide ALL unregistered discovered meters from this DCU's Black List (reversible)."
                  style={{ padding: '5px 12px', fontSize: '12px', background: 'transparent', color: '#ef4444', border: `1px solid #ef444455`, borderRadius: '6px', cursor: hideBusy ? 'wait' : 'pointer', fontWeight: 600, opacity: hideBusy ? 0.6 : 1 }}>
                  Clear List
                </button>
              </>
            )}
            {hiddenItems.length > 0 && (
              <button onClick={() => setShowHidden(s => !s)}
                title="Show/hide the dismissed discovered-meter rows so you can restore them."
                style={{ padding: '5px 12px', fontSize: '12px', background: showHidden ? (c.bgSurface || '#1a1f2e') : 'transparent', color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                {showHidden ? '🙈 Hide dismissed' : `👁 Show hidden (${hiddenItems.length})`}
              </button>
            )}
            {showHidden && hiddenItems.length > 0 && (
              <button onClick={() => doRestore([], true)} disabled={hideBusy}
                title="Restore every hidden discovered meter on this DCU."
                style={{ padding: '5px 12px', fontSize: '12px', background: 'transparent', color: '#3b82f6', border: `1px solid #3b82f655`, borderRadius: '6px', cursor: hideBusy ? 'wait' : 'pointer', fontWeight: 600, opacity: hideBusy ? 0.6 : 1 }}>
                Restore all ({hiddenItems.length})
              </button>
            )}
            {isAdmin && allItems.length > 0 && (
              <button onClick={clearOnDcu} disabled={hideBusy}
                title="Execute the vendor blacklist-clear script on the DCU itself — wipes the device's own discovered-meter table (not just this view). Admin only; kill-switch gated."
                style={{ padding: '5px 12px', fontSize: '12px', background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: '6px', cursor: hideBusy ? 'wait' : 'pointer', fontWeight: 600, opacity: hideBusy ? 0.6 : 1 }}>
                ⚠ Clear on DCU
              </button>
            )}
          </>
        )}
        </div>
      </div>

      {/* Bulk configuration panel */}
      {showBulkConfig && (
        <div style={{ background: c.bgSurface || '#1a1f2e', border: `1px solid #22c55e`, borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
            Configure Registration for {selected.size} Meter(s)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '2px' }}>Connection Type</label>
              <select style={fieldStyle} value={bulkConfig.commType} onChange={e => setBulkConfig(p => ({...p, commType: e.target.value}))}>
                <option value="1">RS-485</option><option value="6">G3 PLC</option><option value="7">RF</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '2px' }}>Password</label>
              <input style={fieldStyle} value={bulkConfig.password} onChange={e => setBulkConfig(p => ({...p, password: e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '2px' }}>Client Address</label>
              <input style={fieldStyle} type="number" value={bulkConfig.clientAddr} onChange={e => setBulkConfig(p => ({...p, clientAddr: e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '2px' }}>CT Ratio</label>
              <select style={fieldStyle} value={bulkConfig.ctRatio} onChange={e => setBulkConfig(p => ({...p, ctRatio: e.target.value}))}>
                {CT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '2px' }}>PT Ratio</label>
              <select style={fieldStyle} value={bulkConfig.ptRatio} onChange={e => setBulkConfig(p => ({...p, ptRatio: e.target.value}))}>
                {PT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '2px' }}>Meter Type</label>
              <select style={fieldStyle} value={bulkConfig.meterType} onChange={e => setBulkConfig(p => ({...p, meterType: e.target.value}))}>
                <option value="0">Single Phase (1P)</option><option value="1">3-Phase Direct</option><option value="2">3-Phase CT</option><option value="3">3-Phase CT/VT</option>
              </select>
            </div>
          </div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '10px' }}>
            {bulkConfig.commType === '1'
              ? 'RS-485: comm address = last 4 digits of serial + 1000. SAP IDs assigned sequentially.'
              : 'PLC/RF: comm address is the full 12-digit meter serial (EUI-64 in DCU Meter Parameter Table). SAP IDs assigned sequentially.'}
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowBulkConfig(false)} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${c.border}`, background: 'transparent', color: c.textPrimary, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleBulkRegister} disabled={bulkLoading}
              style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', background: '#22c55e', color: '#fff', cursor: bulkLoading ? 'wait' : 'pointer', fontWeight: 600, opacity: bulkLoading ? 0.6 : 1 }}>
              {bulkLoading ? 'Registering...' : `Register ${selected.size} Meter(s)`}
            </button>
          </div>
        </div>
      )}

      {allItems.length > 0 ? (
        <div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '8px' }}>
            All meters this DCU has intercepted — {visibleUnregistered.length} unregistered, {registeredFromBlacklist.length} registered
            {hiddenItems.length > 0 ? `, ${hiddenItems.length} hidden` : ''}.
            Tick an unregistered meter to register or hide it. <strong>currentDcuNo</strong> / <strong>oldDcuNo</strong> are as
            reported by the DCU; ⚠ marks a meter that belongs to a different DCU.
          </div>
          <table style={{ fontSize: '11px', width: '100%' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${c.border}` }}>
                <th style={{ padding: '4px', width: '30px' }}>
                  <input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} title="Select all shown unregistered" />
                </th>
                <th style={{ textAlign: 'left', padding: '4px' }}>#</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>Comm Addr</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>Count</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>First Time</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>Last Intercept Time</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>currentDcuNo</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>oldDcuNo</th>
                <th style={{ textAlign: 'left', padding: '4px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((dm, i) => {
                const addr = dm.communication_address || '';
                // "Registered" is only claimed on the strength of a trusted
                // device read; otherwise the row stays actionable and is
                // labelled as unverified rather than asserting device state.
                const registered = paramReadOk && registeredLdns.has(addr);
                const isHidden = !!dm.hidden;
                const curDcu = dm.current_dcu || dm.registered_dcu || null;
                const elsewhere = !!(dm.registered_elsewhere || (dm.current_dcu && dm.current_dcu !== selectedDcu) || dm.old_dcu);
                return (
                  <tr key={i} onClick={(e) => {
                      // Dragging over text to select it must not toggle the row.
                      const sel = window.getSelection && String(window.getSelection() || '');
                      if (sel) return;
                      if (!registered && e.target.type !== 'checkbox') toggleOne(addr, e);
                    }}
                    style={{ borderBottom: `1px solid ${c.border}22`, background: selected.has(addr) ? '#22c55e11' : 'transparent', cursor: registered ? 'default' : 'pointer', userSelect: 'none', opacity: isHidden ? 0.5 : 1 }}>
                    <td style={{ padding: '6px 4px' }}>
                      {registered ? null : <input type="checkbox" checked={selected.has(addr)} onClick={(e) => toggleOne(addr, e)} readOnly />}
                    </td>
                    <td style={{ padding: '6px 4px', color: c.textMuted }}>{i + 1}</td>
                    <td style={{ padding: '6px 4px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}
                        onClick={(e) => e.stopPropagation()}>
                      {/* Selectable on its own: clicking the serial selects
                          text, not the row, so it can be copied and looked up. */}
                      <span style={{ userSelect: 'text', cursor: 'text' }}>{addr}</span>
                      {addr ? (
                        <button type="button" title="Copy serial"
                          onClick={(e) => {
                            e.stopPropagation();
                            const done = () => toast.push(`Copied ${addr}`, 'success');
                            try {
                              navigator.clipboard.writeText(addr).then(done, () => { window.prompt('Copy serial:', addr); });
                            } catch (_) { window.prompt('Copy serial:', addr); }
                          }}
                          style={{ marginLeft: '6px', padding: '0 4px', fontSize: '11px', lineHeight: '16px', background: 'transparent',
                                   color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: '4px', cursor: 'pointer' }}>⧉</button>
                      ) : null}
                    </td>
                    <td style={{ padding: '6px 4px' }}>{dm.intercept_count ?? '—'}</td>
                    <td style={{ padding: '6px 4px' }}>{dm.first_seen || '—'}</td>
                    <td style={{ padding: '6px 4px' }}>{dm.last_seen || '—'}</td>
                    <td style={{ padding: '6px 4px', fontFamily: 'monospace', color: elsewhere ? '#f59e0b' : c.textMuted, fontWeight: elsewhere ? 700 : 400 }}
                        title={dm.registered_elsewhere ? 'Registered to a DIFFERENT DCU in our system' : 'DCU the meter is currently on'}>
                      {curDcu || '—'}{elsewhere ? ' ⚠' : ''}
                    </td>
                    <td style={{ padding: '6px 4px', fontFamily: 'monospace', color: dm.old_dcu ? '#f59e0b' : c.textMuted }}
                        title={dm.old_dcu ? 'DCU the meter moved FROM' : undefined}>
                      {dm.old_dcu || '—'}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      {registered ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 600 }}>Registered</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setMoveRow(dm); }}
                            title={elsewhere
                              ? `Move ${addr} from DCU ${curDcu || '?'} to this DCU (${selectedDcu}), which hears it`
                              : `Move ${addr} to another DCU`}
                            style={{ ..._dcuBtn('#1e40af', false), padding: '2px 9px', fontSize: '10px' }}>
                            Move{elsewhere ? ' here' : ''}
                          </button>
                        </div>
                      ) : (dm.registered_elsewhere && dm.registered_dcu && !isHidden) ? (
                        // Registered on ANOTHER DCU (heard here as a stranger).
                        // Not "Unregistered": adding it here would put it in two
                        // tables. Move here deregisters it there first; Add /
                        // Register Selected are turned into the same move.
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '10px', color: '#f59e0b', fontWeight: 600 }}
                                title={`Registered on DCU ${dm.registered_dcu}. This DCU only hears it.`}>
                            On {dm.registered_dcu}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setMoveRow(dm); }}
                            title={`Move ${addr} from DCU ${dm.registered_dcu} to this DCU (${selectedDcu}) — deregistered there first`}
                            style={{ ..._dcuBtn('#1e40af', false), padding: '2px 9px', fontSize: '10px' }}>
                            Move here
                          </button>
                        </div>
                      ) : isHidden ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '10px', color: c.textMuted, fontWeight: 600 }}>Hidden</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); doRestore([addr]); }}
                            disabled={hideBusy}
                            title={`Restore ${addr} to the Black List`}
                            style={{ ..._dcuBtn('#3b82f6', false), padding: '2px 9px', fontSize: '10px' }}>
                            Restore
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '10px', color: c.warning }}>Unregistered</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic', padding: '10px' }}>
          {blacklistLoading
            ? 'Reading blacklist from the DCU…'
            : blacklist === null
              ? 'Click “📡 Read Blacklist” to scan the DCU bus for discovered meters.'
              : 'No discovered meters. All meters on the bus may already be registered, or the DCU has not performed a scan yet.'}
        </div>
      )}
      {moveRow && (
        <MoveMeterDialog
          meterSerial={moveRow.communication_address}
          fromDcu={moveRow.registered_dcu || moveRow.current_dcu || selectedDcu}
          defaultTarget={(moveRow.registered_dcu && moveRow.registered_dcu !== selectedDcu) ? selectedDcu : ''}
          onClose={() => setMoveRow(null)}
          onMoved={() => { setMoveRow(null); if (onRegistered) onRegistered(); }}
        />
      )}
    </div>
  );
}

// Map the serial-derived meter-type form value to a vendor-style label.
const METER_TYPE_LABEL = {
  '0': 'Single Phase Meter', '1': 'Three Phase Meter',
  '2': 'Three Phase (CT)', '3': 'Three Phase (CT/VT)',
};
// Vendor "Comm Addr": RS-485 (comm_type 1) = last4+1000; PLC/RF = full serial.
function _commAddrFor(serial, commType) {
  if (String(commType) === '1') {
    return serial && serial.length >= 4 ? String(parseInt(serial.slice(-4)) + 1000) : '—';
  }
  return serial || '—';
}
// get_online_status per-row status code -> what it MEANS.
//
// UNCONFIRMED as of 2026-07-27, deliberately left EMPTY. The Holley MDC tool
// renders this exact read as a binary Online / blank and reported
// "Online Meter Count: 233" of 253 on DCU 129255052983; our decode of the same
// DCU returns codes 3 and 4. Which code means online is NOT proven, and this
// is the file where a guessed device-semantics mapping would do damage — so
// the UI shows the raw code plus the per-code counts and asserts nothing.
//
// TO CONFIRM: run Check Online on 129255052983 and read the per-code counts in
// the DCU header. When one code's count is 233, add it here as
// `{ label: 'Online', online: true }` (and the other as offline). The Result
// column, the row highlight and the header's "Online Meter Count" all light up
// from this one constant — nothing else needs to change.
const DCU_ONLINE_STATUS = {};   // e.g. { 4: { label: 'Online', online: true } }

// Never returns null — an unmapped code renders as itself rather than as a
// blank cell, so an operator can see the evidence needed to fill the map above.
function _onlineStatusInfo(code) {
  const e = DCU_ONLINE_STATUS[code];
  if (e) return { label: e.label, online: e.online === true, known: true };
  return { label: code == null ? '—' : `status ${code}`, online: false, known: false };
}

// ── Link quality: the signal that actually decides online ──────────
//
// get_online_status returns codes we have never proven (DCU_ONLINE_STATUS
// above is empty on purpose). get_communication_quality returns something
// we CAN stand behind: node_status is documented on the wire as
// 0 = offline / nonzero = online, and it comes with send/receive frame
// counts. A meter the DCU has actually exchanged frames with is online —
// that is evidence, not an assumption.
//
// Meters do die in the field, so this has to be a real negative as well as
// a real positive: a matched row with node_status 0 AND no frames in either
// direction is genuinely offline, and says so.
function _commOnline(q) {
  return !!q && ((q.node_status != null && q.node_status !== 0)
                 || q.send_frames > 0 || q.recv_frames > 0);
}

// Correlate the two reads: for each get_online_status code, how many of the
// meters carrying it are online by link quality. Run on a DCU where most
// meters are alive, one code should come out ~100% and the other ~0% — which
// is what would let DCU_ONLINE_STATUS be filled in from data rather than a
// guess. Surfaced in the header; nothing consumes it automatically, because
// a mapping this load-bearing should be a person's decision.
function _codeVsLinkQuality(entries, commRows) {
  const byMeter = new Map();
  for (const q of (commRows || [])) {
    const k = _normSerial(q.meter_id);
    if (k) byMeter.set(k, q);
  }
  const agg = new Map();
  for (const e of (entries || [])) {
    const q = byMeter.get(_normSerial(e.meter));
    if (!q) continue;                       // unmatched proves nothing
    const a = agg.get(e.status) || { code: e.status, matched: 0, online: 0 };
    a.matched += 1;
    if (_commOnline(q)) a.online += 1;
    agg.set(e.status, a);
  }
  return Array.from(agg.values()).sort((a, b) => b.matched - a.matched);
}

// 12-digit tail comparison — the DCU reports a 6-byte BCD address whose hex
// rendering IS the serial, while our rows carry LDNs that may be prefixed.
function _normSerial(x) {
  return String(x || '').replace(/\D/g, '').slice(-12);
}

// "read 4m ago" — makes a cached device read's staleness visible instead of
// silently presenting it as current.
function _ageLabel(ts) {
  if (!ts) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function _dcuBtn(color, disabled) {
  return {
    padding: '5px 12px', fontSize: '12px', fontWeight: 600, color: '#fff',
    background: color, border: 'none', borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  };
}

// ── Meters Setting (vendor-tool layout) ──────────────────────────────
// Move a meter's registration from one DCU to another (for meters heard by
// two overlapping DCUs). Deregisters from the source FIRST, then registers on
// the target — the backend enforces the ordering + auto-rollback; this dialog
// picks the target and surfaces whether the target's last blacklist read heard
// the meter ('warn but allow' — never blocks the move).
// Live status of queued (durable) moves. Every move now goes through
// POST /api/device-moves and runs on the server's Activity queue: it waits for
// BOTH DCUs to hold a stable link, runs one at a time fleet-wide, survives a
// restart or a closed tab, and can be cancelled from Activity. This panel only
// mirrors /api/activity/{job_id}, so reopening the page loses nothing.
const MOVE_JOB_LABEL = {
  scheduled: ['Queued', '#64748b'],
  waiting_stable: ['Waiting for a stable link', '#f59e0b'],
  running: ['Moving now', '#3b82f6'],
  done: ['Done', '#22c55e'],
  partial: ['Partly done', '#f59e0b'],
  failed: ['Failed', '#ef4444'],
  cancelled: ['Cancelled', '#64748b'],
  expired: ['Gave up — no stable link in time', '#ef4444'],
};
function QueuedMoveStatus({ jobIds, onAllTerminal }) {
  const c = useThemeColors();
  const toast = useToast();
  const [jobs, setJobs] = React.useState({});
  const [nowTick, setNowTick] = React.useState(Date.now());
  const told = React.useRef(new Set());
  const idsKey = (jobIds || []).join(',');
  React.useEffect(() => {
    if (!idsKey) return undefined;
    let dead = false; let fired = false;
    const poll = async () => {
      const next = {};
      for (const id of idsKey.split(',')) {
        try { next[id] = await api(`/activity/${encodeURIComponent(id)}`); }
        catch (e) { next[id] = { job_id: id, status: '?', note: e.message || String(e) }; }
      }
      if (dead) return;
      setJobs(next);
      // One toast per job as it finishes. Severity follows what LANDED: a move
      // where every meter rolled back is the safety net working, not a
      // success (2026-09-15: it toasted green and the operator retried twice).
      for (const jb of Object.values(next)) {
        if (!jb.terminal || told.current.has(jb.job_id)) continue;
        told.current.add(jb.job_id);
        const r = jb.result || {};
        const landed = (r.moved || []).length + (r.registered_only || []).length;
        const sev = (jb.status === 'failed' || jb.status === 'expired' || landed === 0) ? 'error'
                  : ((r.rolled_back || []).length || jb.status === 'partial') ? 'warning' : 'success';
        toast.push(`Move ${jb.from_dcu || ''} → ${jb.dcu_serial || ''}: ${jb.note || jb.status}`, sev);
        if ((r.source_still_holds || []).length) {
          toast.push(`${r.source_still_holds.length} meter(s) are still registered on an OFFLINE `
            + `source DCU ${jb.from_dcu} as well. Clear that DCU's parameter table when it `
            + `reconnects — its Meters Setting will show them amber.`, 'info');
        }
      }
      const all = Object.values(next);
      if (!fired && all.length && all.every(x => x.terminal)) {
        fired = true; if (onAllTerminal) onAllTerminal(all);
      }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { dead = true; clearInterval(id); };
  }, [idsKey]);
  // Elapsed while something is mid-write: a move onto a poor link can take
  // 10+ minutes of per-block timeouts, which without a clock looks hung.
  const anyRunning = Object.values(jobs).some(x => x.status === 'running');
  React.useEffect(() => {
    if (!anyRunning) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anyRunning]);
  const elapsedOf = (iso) => {
    if (!iso) return null;
    const t = Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + 'Z');
    if (!isFinite(t)) return null;
    const s = Math.max(0, Math.floor((nowTick - t) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  if (!idsKey) return null;
  return (
    <div style={{ border: `1px solid ${c.border}`, borderRadius: '6px', padding: '8px 10px', marginBottom: '10px' }}>
      {idsKey.split(',').map(id => {
        const j = jobs[id] || {};
        const r = j.result || {};
        const landed = (r.moved || []).length + (r.registered_only || []).length;
        let [label, color] = MOVE_JOB_LABEL[j.status] || ['…', '#64748b'];
        if (j.terminal && landed === 0 && j.status !== 'cancelled') { label = '✗ Nothing moved'; color = '#ef4444'; }
        const wait = j.status === 'waiting_stable' || j.status === 'scheduled'
          ? [j.link && !j.link.stable ? `target: ${j.link.reason}` : null,
             j.from_link && !j.from_link.stable ? `source: ${j.from_link.reason}` : null].filter(Boolean).join(' · ')
          : '';
        return (
          <div key={id} style={{ fontSize: '12px', lineHeight: 1.6 }}>
            <span style={{ color, fontWeight: 600 }}>{label}</span>
            {j.status === 'running' && elapsedOf(j.started_at) ? <> · {elapsedOf(j.started_at)} elapsed</> : null}
            {j.from_dcu ? <> · {j.from_dcu} → {j.dcu_serial}</> : null}
            {j.meter_count ? <> · {j.meter_count} meter(s)</> : null}
            {wait ? <div style={{ color: '#f59e0b' }}>{wait}</div>
                  : (j.note ? <div style={{ color: c.textMuted }}>{j.note}</div> : null)}
          </div>
        );
      })}
      <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '4px' }}>
        Runs on the server — you can close this window or reload. Track or cancel it in Activity.
      </div>
    </div>
  );
}

// What stage-meters did besides staging: meters registered on another DCU
// are queued as verified moves, some may be refused — never silently.
function _stageMovesNote(st) {
  const moves = (st && st.moves_queued) || [];
  const skipped = (st && st.skipped) || [];
  const nMoved = moves.reduce((n, j) => n + (j.meters || []).length, 0);
  const parts = [];
  if (nMoved) {
    parts.push(`${nMoved} meter(s) are registered on another DCU (`
      + moves.map(j => `${(j.meters || []).length} on ${j.from_dcu}`).join(', ')
      + `) — queued as a MOVE here instead of a second registration (deregistered there first; see Activity).`);
  }
  if (skipped.length) {
    parts.push(`${skipped.length} not done: `
      + skipped.slice(0, 3).map(x => `${x.meter} (${x.reason})`).join('; ')
      + (skipped.length > 3 ? '…' : ''));
  }
  return parts.join(' ');
}

// sourceDcu: the DCU whose table the dialog was opened from. The server only
// uses it to explain a refusal — a row there that is already routed to the
// target is a duplicate registration, fixed by Clear, not by a move.
async function queueDeviceMoves({ toDcu, meters, allowOfflineSource, sourceDcu }) {
  return api('/device-moves', {
    method: 'POST',
    body: JSON.stringify({
      to_dcu: toDcu, confirm_to_dcu: toDcu, meters,
      allow_offline_source: !!allowOfflineSource,
      source_dcu: sourceDcu ? String(sourceDcu) : null,
    }),
  });
}

function MoveMeterDialog({ meterSerial, fromDcu, defaultTarget, onClose, onMoved }) {
  const c = useThemeColors();
  const toast = useToast();
  const [dcus, setDcus] = React.useState([]);        // [{dcu, last_seen}] — ONLY DCUs that hear the meter
  const [allDcus, setAllDcus] = React.useState([]);  // every other DCU, for the operator override
  const [showAll, setShowAll] = React.useState(false);
  const [loadingDcus, setLoadingDcus] = React.useState(true);
  const [target, setTarget] = React.useState(defaultTarget || '');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [jobIds, setJobIds] = React.useState([]);

  // A move only makes sense to a DCU that can actually SEE (hear) this meter —
  // otherwise it can't collect it. Build the target list from the DCUs whose
  // LAST blacklist read lists this meter's comm address (cached — no DCU I/O).
  React.useEffect(() => {
    let cancelled = false;
    setLoadingDcus(true);
    (async () => {
      let list = [];
      try { list = await api('/dcus'); }
      catch (e) { if (!cancelled) toast.push('Could not load DCUs: ' + e.message, 'error'); }
      const serials = (list || [])
        .map(d => d.serial_number || d.serial)
        .filter(s => s && s !== fromDcu);
      if (!cancelled) setAllDcus(serials);
      const want = String(meterSerial).toUpperCase();
      const checks = await Promise.all(serials.map(async (s) => {
        try {
          const r = await api(`/meters/${encodeURIComponent(s)}/dcu-blacklist?cached=true`);
          const hit = ((r && r.blacklist) || []).find(e =>
            String(e.communication_address || '').toUpperCase() === want);
          return hit ? { dcu: s, last_seen: hit.last_seen || null } : null;
        } catch { return null; }
      }));
      if (cancelled) return;
      const hearing = checks.filter(Boolean);
      // Only keep the pre-selected target if it actually hears the meter.
      setTarget(t => (hearing.some(h => h.dcu === t) ? t : ''));
      // Nothing hears it: go straight to the full list. The operator may know
      // where the meter is when no DCU's last read says so — the saved
      // blacklist views are only as fresh as their last read (129255053298's
      // was from 10.09 on 23.09, so its own four meters were unreachable here).
      if (!hearing.length) setShowAll(true);
      setDcus(hearing);
      setLoadingDcus(false);
    })();
    return () => { cancelled = true; };
  }, [fromDcu, meterSerial]);

  const hears = dcus.some(d => d.dcu === target);
  const doMove = async () => {
    if (!target) return;
    if (!window.confirm(
      `Move ${meterSerial} from DCU ${fromDcu} to DCU ${target}?\n\n`
      + (hears ? '' : `⚠ ${target} has not reported hearing this meter.\n\n`)
      + `Queued on the server: it runs when both DCUs hold a stable link. The meter is `
      + `deregistered from ${fromDcu} first, then registered on ${target}; if registration `
      + `fails it is rolled back onto ${fromDcu}.`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await queueDeviceMoves({ toDcu: target, meters: [String(meterSerial)], sourceDcu: fromDcu });
      setJobIds((r.jobs || []).map(j => j.job_id));
      setResult({ ok: true, queued: true });
      toast.push(`Move of ${meterSerial} → ${target} queued`, 'success');
    } catch (e) {
      setResult({ ok: false, error: e.message || String(e) });
      toast.push(`Could not queue the move: ${e.message || e}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const noneHear = !loadingDcus && dcus.length === 0;
  const options = showAll
    ? allDcus.map(s => {
        const h = dcus.find(d => d.dcu === s);
        return { value: s, label: s,
                 hint: h ? `hears it · last seen ${fmtDT(h.last_seen) || '?'}` : 'not reported hearing it' };
      })
    : dcus.map(d => ({ value: d.dcu, label: d.dcu,
                       hint: d.last_seen ? `last seen ${fmtDT(d.last_seen)}` : '' }));
  return (
    <ModalShell title={`Move meter ${meterSerial} to another DCU`} onClose={onClose}>
      <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '12px', lineHeight: 1.5 }}>
        Currently on DCU <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{fromDcu}</span>.
        DCUs that <strong>hear this meter</strong> (per their last blacklist read) are listed first.
        The move is <strong>queued on the server</strong> and runs when both DCUs hold a stable link:
        deregistered from {fromDcu} first, then registered on the target, rolled back if refused.
      </div>
      <label style={{ fontSize: '12px', color: c.textMuted, display: 'block', marginBottom: '4px' }}>
        Target DCU {loadingDcus ? '' : `(${dcus.length} hear this meter)`}
      </label>
      {loadingDcus ? (
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '10px' }}>Finding DCUs that hear this meter…</div>
      ) : (
        <div style={{ marginBottom: '10px' }}>
          {noneHear && (
            <div style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '6px', lineHeight: 1.5 }}>
              No other DCU has reported hearing this meter in its last blacklist read.
              You can still pick one if you know where the meter is.
            </div>
          )}
          <SearchableSelect
            value={target} onChange={setTarget} disabled={busy || !!jobIds.length}
            allowClear={false} width="100%"
            placeholder="Search DCUs by serial…"
            options={options} />
          {!noneHear && (
            <label style={{ fontSize: '11px', color: c.textMuted, display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} disabled={busy} />
              Show all DCUs, not only the ones that hear it
            </label>
          )}
          {target && !hears && (
            <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '6px' }}>
              ⚠ {target} has not reported hearing this meter — it may not be able to collect it.
            </div>
          )}
        </div>
      )}
      <QueuedMoveStatus jobIds={jobIds} onAllTerminal={() => { if (onMoved) onMoved(); }} />
      {result && !result.ok && (
        <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '10px', lineHeight: 1.5 }}>{result.error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
        <button onClick={onClose} disabled={busy}
          style={{ padding: '7px 14px', fontSize: '13px', background: c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: busy ? 'default' : 'pointer' }}>
          {result && result.ok ? 'Close' : 'Cancel'}
        </button>
        <button onClick={doMove} disabled={busy || !target || (result && result.ok)}
          style={{ padding: '7px 14px', fontSize: '13px', background: (busy || !target || (result && result.ok)) ? c.border : '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', cursor: (busy || !target || (result && result.ok)) ? 'default' : 'pointer' }}>
          {busy ? 'Queuing…' : (result && result.ok ? 'Queued' : 'Move meter')}
        </button>
      </div>
    </ModalShell>
  );
}

// The registered-meter table reshaped to mirror the Holley CAS "Meters
// Setting" view: Point No (SAP) · Comm Addr · Meter No · Meter Type ·
// Status · Operate. Wired to the actions we already back: Refresh,
// Check Online (live DLMS), Download (DCU catchup-historical), Clear
// Selected / Clear All / per-row Clear (deprovision). `meters` is owned
// by DcuDetailPane so the summary header and this table share one source.
// Bulk move: many meters, one verified write each way. Unlike MoveMeterDialog
// (one meter, target chosen from DCUs that HEAR it), the target here is typed
// to confirm — a bulk move rewrites TWO parameter tables. The backend refuses
// outright if either DCU is offline, because a move must deregister from the
// source first; registering without that is what put 95 meters on two DCUs at
// once on 2026-08-12.
function BulkMoveDcuModal({ fromDcu, serials, total, onClose, onMoved }) {
  const c = useThemeColors();
  const toast = useToast();
  const [dcus, setDcus] = React.useState([]);
  const [target, setTarget] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [jobIds, setJobIds] = React.useState([]);
  const [allowOffline, setAllowOffline] = React.useState(false);

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const list = await api('/dcus');
        if (dead) return;
        setDcus((list || [])
          .map(d => d.serial_number || d.serial)
          .filter(s => s && s !== fromDcu));
      } catch (e) { toast.push('Could not load DCUs: ' + e.message, 'error'); }
    })();
    return () => { dead = true; };
  }, [fromDcu]);

  const doMove = async () => {
    if (!target || confirm !== target) return;
    setBusy(true); setResult(null);
    try {
      // Queued, not inline: the move runs on the server once BOTH DCUs hold a
      // stable link, so a flaky DCU or a closed tab no longer loses it.
      const r = await api('/device-moves', {
        method: 'POST',
        body: JSON.stringify({
          to_dcu: target,
          confirm_to_dcu: confirm,
          meters: serials || [],
          from_dcu: serials ? null : fromDcu,
          allow_offline_source: allowOffline,
          source_dcu: fromDcu ? String(fromDcu) : null,
        }),
      });
      setJobIds((r.jobs || []).map(j => j.job_id));
      setResult({ ok: true, skipped: r.skipped || [] });
      toast.push(`Move of ${(r.jobs || []).reduce((n, j) => n + (j.meter_count || 0), 0)} meter(s) → ${target} queued`, 'success');
    } catch (e) {
      setResult({ ok: false, error: e.message || String(e) });
      toast.push(`Could not queue the move: ${e.message || e}`, 'error');
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Move ${total} meter(s) off DCU ${fromDcu}`} onClose={onClose}>
      <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '12px', lineHeight: 1.5 }}>
        {serials
          ? `Moving the ${total} selected meter(s).`
          : `Moving ALL ${total} meter(s) — this consolidates the whole DCU.`}
        {' '}The move is <strong>queued on the server</strong> and runs when both DCUs hold a
        stable link: <strong>deregistered from {fromDcu} first</strong> in one verified write,
        then registered on the target in one batched write — never on both DCUs at once.
        Anything the target refuses is rolled back onto {fromDcu} with its original SAP.
        <strong> Historical readings are always kept</strong> — this changes registration only.
      </div>
      <label style={{ fontSize: '12px', color: c.textMuted, display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: '10px', lineHeight: 1.5 }}>
        <input type="checkbox" checked={allowOffline} onChange={e => setAllowOffline(e.target.checked)} disabled={busy || !!jobIds.length} />
        <span>If {fromDcu} is offline when the move runs, register on the target anyway.
          {fromDcu}'s table then still lists the meters and must be cleared when it returns.
          Ignored while {fromDcu} is up.</span>
      </label>
      <label style={{ fontSize: '12px', color: c.textMuted, display: 'block', marginBottom: '4px' }}>
        Target DCU <span style={{ color: c.textMuted }}>— type any part of the serial</span>
      </label>
      <div style={{ marginBottom: '10px' }}>
        <SearchableSelect
          value={target}
          onChange={v => { setTarget(v); setConfirm(''); }}
          disabled={busy} allowClear={false} width="100%"
          placeholder="Search DCUs by serial…"
          options={dcus.map(d => ({ value: d, label: String(d) }))} />
      </div>
      {target && (
        <>
          <label style={{ fontSize: '12px', color: c.textMuted, display: 'block', marginBottom: '4px' }}>
            Type <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{target}</span> to confirm
          </label>
          <input value={confirm} onChange={e => setConfirm(e.target.value)} disabled={busy}
            style={{ width: '100%', padding: '7px 10px', fontSize: '13px', fontFamily: 'monospace', background: c.bgInput, color: c.textPrimary, border: `1px solid ${confirm && confirm !== target ? '#f43f5e' : c.border}`, borderRadius: '6px', marginBottom: '10px' }} />
        </>
      )}
      {result && result.ok && result.skipped.length > 0 && (
        <div style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '10px', lineHeight: 1.5 }}>
          {result.skipped.length} not queued: {result.skipped.slice(0, 5).map(x => `${x.meter} (${x.reason})`).join('; ')}
          {result.skipped.length > 5 ? '…' : ''}
        </div>
      )}
      <QueuedMoveStatus jobIds={jobIds} onAllTerminal={() => { if (onMoved) onMoved(); }} />
      {result && !result.ok && (
        <div style={{ fontSize: '12px', color: '#f43f5e', marginBottom: '10px', lineHeight: 1.5 }}>{result.error}</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
        <button onClick={onClose} disabled={busy}
          style={{ padding: '7px 14px', fontSize: '13px', background: c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: busy ? 'default' : 'pointer' }}>
          {result && result.ok ? 'Close' : 'Cancel'}
        </button>
        <button onClick={doMove} disabled={busy || !target || confirm !== target || (result && result.ok)}
          style={{ padding: '7px 14px', fontSize: '13px', background: (busy || !target || confirm !== target || (result && result.ok)) ? c.border : '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', cursor: (busy || !target || confirm !== target || (result && result.ok)) ? 'default' : 'pointer' }}>
          {busy ? 'Queuing…' : (result && result.ok ? 'Queued' : `Move ${total} meter(s)`)}
        </button>
      </div>
    </ModalShell>
  );
}

// Bulk move from ARCHIVE CONTROL, which is a fleet view — so a selection there
// routinely spans several source DCUs. BulkMoveDcuModal above cannot express
// that: its endpoint is /dcus/{from_dcu}/bulk-move-dcu, one source per call.
// This groups the selection by the DCU each meter sits on today and runs one
// bulk-move per source.
//
// SEQUENTIALLY, and that is the point of the loop rather than a Promise.all.
// bulk-move-dcu deregisters from the source BEFORE it awaits the target's
// per-DCU register lock. Fire N groups at once and all N sources are cleared
// almost immediately, then the requests queue on that one lock — so every
// meter in the queue is registered on NEITHER DCU for as long as the groups
// ahead of it take, and a group that times out waiting has already given up
// its source rows. Serialising here means a source is only cleared when the
// target is actually free. Each group is independently atomic server-side
// (deregister source → register target → roll back onto the source if the
// target refuses), so a group that fails leaves its own meters exactly where
// they were and the remaining groups still run.
function FleetBulkMoveModal({ rows, onClose, onMoved }) {
  const c = useThemeColors();
  const toast = useToast();
  const [dcus, setDcus] = React.useState([]);          // [{serial, connected}]
  const [target, setTarget] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // Durable Activity jobs (one per source DCU) this dialog queued or found
  // already queued for these meters. The server owns them; this only mirrors.
  const [jobIds, setJobIds] = React.useState([]);
  const [skipped, setSkipped] = React.useState([]);
  // Opt-in to registering on the target without deregistering from a source
  // that will not answer. Deliberately a separate, unticked box rather than
  // something the move infers: it leaves the meter in two parameter tables.
  const [allowOffline, setAllowOffline] = React.useState(false);

  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const list = await api('/dcus');
        if (dead) return;
        setDcus((list || [])
          .map(d => ({ serial: String(d.serial_number || d.serial || ''),
                       connected: !!d.connected }))
          .filter(d => d.serial));
      } catch (e) { toast.push('Could not load DCUs: ' + e.message, 'error'); }
    })();
    return () => { dead = true; };
  }, []);

  // RE-ATTACH: if any selected meter is already in an unfinished move, show
  // that job instead of offering a second one (the server would skip those
  // meters anyway). Reopening the dialog after a reload lands here.
  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api('/activity?active_only=true&kind=move_dcu&limit=200');
        if (dead) return;
        const mine = new Set(rows.map(x => String(x.meter_serial)));
        const live = (r.jobs || []).filter(j => (j.meters || []).some(m => mine.has(String(m))));
        if (live.length) {
          setJobIds(live.map(j => j.job_id));
          setTarget(live[0].dcu_serial); setConfirm(live[0].dcu_serial);
          toast.push(`${live.length} move(s) for these meters are already queued`, 'info');
        }
      } catch (e) { /* listing is a convenience; never block the dialog */ }
    })();
    return () => { dead = true; };
  }, []);

  const online = React.useMemo(
    () => new Set(dcus.filter(d => d.connected).map(d => d.serial)), [dcus]);

  // Group by CURRENT DCU. Two kinds of row are excluded rather than sent:
  // direct meters have no parameter table to move between, and a meter already
  // on the target would make from_dcu == to_dcu, which the endpoint refuses for
  // the whole group. Both are reported so nothing drops silently.
  const { groups, direct, already, unreg } = React.useMemo(() => {
    const g = new Map(), dir = [], on = []; let u = 0;
    for (const r of rows) {
      if (!r.dcu_serial) { dir.push(r); continue; }
      if (target && String(r.dcu_serial) === String(target)) { on.push(r); continue; }
      if (r.unregistered) u += 1;
      const k = String(r.dcu_serial);
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(String(r.meter_serial));
    }
    return { groups: [...g.entries()].sort((a, b) => a[0].localeCompare(b[0])),
             direct: dir, already: on, unreg: u };
  }, [rows, target]);

  const movable = groups.reduce((n, [, s]) => n + s.length, 0);
  // Only claim a DCU is offline once /dcus has actually answered — an empty
  // list is "not loaded yet", not "the whole fleet is down".
  const offlineSrc = dcus.length ? groups.filter(([s]) => !online.has(s)).map(([s]) => s) : [];
  const targetOffline = !!(target && dcus.length && !online.has(target));
  const queued = jobIds.length > 0;
  const planGroups = groups;

  // ONE short request: the server groups by source and records one durable
  // Activity job per source. Nothing touches a device in this request; each
  // job runs when both of its DCUs have held a stable link, one at a time
  // fleet-wide, and survives a restart or a closed tab.
  const doMove = async () => {
    if (!target || confirm !== target || !movable || busy) return;
    setBusy(true);
    try {
      const r = await queueDeviceMoves({
        toDcu: target,
        meters: groups.flatMap(([, serials]) => serials),
        allowOfflineSource: allowOffline,
      });
      setJobIds((r.jobs || []).map(j => j.job_id));
      setSkipped(r.skipped || []);
      toast.push(`${(r.jobs || []).length} move(s) queued onto ${target}`, 'success');
    } catch (e) {
      toast.push(`Could not queue the move: ${e.message || e}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const warn = { fontSize: '12px', color: '#f59e0b', marginBottom: '8px', lineHeight: 1.5 };
  return (
    <ModalShell title={`Move ${rows.length} meter(s) to another DCU`} onClose={onClose}>
      <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '12px', lineHeight: 1.5 }}>
        {movable} meter(s) across <strong>{groups.length} source DCU(s)</strong> — one verified
        move per source, <strong>queued on the server</strong> and run one after another once
        both DCUs hold a stable link. Each meter is
        <strong> deregistered from its current DCU first</strong>, then registered on the
        target: never on both at once, and rolled back onto its own source if the target
        refuses it. <strong>Historical readings are always kept</strong> — this changes
        registration only.
      </div>
      {unreg > 0 && (
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '8px', lineHeight: 1.5 }}>
          <strong>{unreg}</strong> of these are already <strong>Unregistered</strong> — a Clear All
          took them off their DCU, so the deregister half is done. They skip straight to being
          registered on the target, and their source is not written to at all.
        </div>
      )}
      <div style={warn}>
        {unreg === movable && movable > 0
          ? 'These meters are on no DCU, so nothing is deregistered and their old DCU is not '
            + 'contacted — it may stay offline. The move runs once the target has a stable link.'
          : allowOffline
          ? 'Live sources are deregistered properly first; a source still offline when its move '
            + 'runs keeps its rows until you clear them.'
          : 'Each move waits until its source DCU and the target have both held a stable link '
            + '(up to 48 h, then it gives up and says so in Activity).'}
      </div>
      {direct.length > 0 && (
        <div style={warn}>
          {direct.length} selected meter(s) are <strong>direct</strong> (no DCU) — they have no
          parameter table to move between and are skipped.
        </div>
      )}
      {already.length > 0 && (
        <div style={warn}>{already.length} selected meter(s) are already on {target} — skipped.</div>
      )}
      {offlineSrc.length > 0 && (
        <div style={{ ...warn, color: '#f43f5e' }}>
          {offlineSrc.length} source DCU(s) are offline:{' '}
          {offlineSrc.slice(0, 4).join(', ')}{offlineSrc.length > 4 ? ` +${offlineSrc.length - 4} more` : ''}.
          <label style={{ display: 'block', marginTop: '6px', color: c.textSecondary, cursor: 'pointer' }}>
            <input type="checkbox" checked={allowOffline} disabled={busy}
              onChange={e => setAllowOffline(e.target.checked)} style={{ marginRight: '6px' }} />
            Register on {target || 'the target'} anyway — this is the usual reason to move to a
            sister DCU on the same wiring. Collection resumes now; a dead DCU cannot be
            deregistered, so it <strong>keeps its copy of the row</strong> until you clear its
            table when it reconnects. Until then the meter is in two parameter tables — harmless
            while the DCU is down, and it shows amber as "registered to a different DCU" when it
            returns. Unticked, a move waits for its source to come back.
          </label>
        </div>
      )}
      {targetOffline && (
        <div style={{ ...warn, color: '#f43f5e' }}>
          Target DCU {target} is not connected right now — the moves will wait for it.
        </div>
      )}

      <label style={{ fontSize: '12px', color: c.textMuted, display: 'block', marginBottom: '4px' }}>
        Target DCU <span style={{ color: c.textMuted }}>— type any part of the serial</span>
      </label>
      <div style={{ marginBottom: '10px' }}>
        <SearchableSelect
          value={target}
          onChange={v => { setTarget(v); setConfirm(''); }}
          disabled={busy || queued}
          allowClear={false}
          width="100%"
          placeholder="Search DCUs by serial…"
          options={dcus.map(d => ({
            value: d.serial,
            label: d.serial,
            hint: d.connected ? 'online' : 'offline',
          }))} />
      </div>
      {target && (
        <>
          <label style={{ fontSize: '12px', color: c.textMuted, display: 'block', marginBottom: '4px' }}>
            Type <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{target}</span> to confirm
          </label>
          <input value={confirm} onChange={e => setConfirm(e.target.value)} disabled={busy || queued}
            style={{ width: '100%', padding: '7px 10px', fontSize: '13px', fontFamily: 'monospace', background: c.bgInput, color: c.textPrimary, border: `1px solid ${confirm && confirm !== target ? '#f43f5e' : c.border}`, borderRadius: '6px', marginBottom: '10px' }} />
        </>
      )}

      {!queued && planGroups.length > 0 && (
        <div style={{ maxHeight: '190px', overflowY: 'auto', marginBottom: '10px',
          border: `1px solid ${c.border}`, borderRadius: '6px' }}>
          {planGroups.map(([src, serials]) => (
            <div key={src} style={{ display: 'flex', gap: '8px', alignItems: 'baseline',
              padding: '5px 8px', fontSize: '12px', borderBottom: `1px solid ${c.border}22` }}>
              <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{src}</span>
              <span style={{ color: c.textMuted }}>{serials.length} meter(s)</span>
              <span style={{ flex: 1 }} />
              {dcus.length > 0 && !online.has(src) && (
                <span style={{ color: '#f43f5e' }}>offline</span>
              )}
            </div>
          ))}
        </div>
      )}
      {skipped.length > 0 && (
        <div style={warn}>
          {skipped.length} not queued: {skipped.slice(0, 5).map(x => `${x.meter} (${x.reason})`).join('; ')}
          {skipped.length > 5 ? '…' : ''}
        </div>
      )}
      <QueuedMoveStatus jobIds={jobIds} onAllTerminal={() => { if (onMoved) onMoved(); }} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
        <button onClick={onClose}
          style={{ padding: '7px 14px', fontSize: '13px', background: c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: 'pointer' }}>
          {queued ? 'Close (keeps running)' : 'Cancel'}
        </button>
        <button onClick={doMove} disabled={busy || !target || confirm !== target || !movable || queued}
          style={{ padding: '7px 14px', fontSize: '13px', background: (busy || !target || confirm !== target || !movable || queued) ? c.border : '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', cursor: (busy || !target || confirm !== target || !movable || queued) ? 'default' : 'pointer' }}>
          {busy ? 'Queuing…' : queued ? 'Queued' : `Move ${movable} meter(s)`}
        </button>
      </div>
    </ModalShell>
  );
}

function MetersSettingSection({ meters, selectedDcu, onReload, reloading, onChanged, regDcuFor, paramReadState, pending, onPendingChanged, onOnlineRead, onParamConfirmed, paramTable }) {
  const c = useThemeColors();
  const toast = useToast();
  const [selected, setSelected] = React.useState(new Set());
  const [busy, setBusy] = React.useState(null);
  // {serials: string[]|null, total: number} — null serials = move the whole DCU.
  const [bulkMove, setBulkMove] = React.useState(null);
  // Background repair job id — repair runs server-side on the per-DCU
  // collector queue; the table stays fully usable while we poll it.
  const [repairJob, setRepairJob] = React.useState(null);
  const [filter, setFilter] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('');
  const [online, setOnline] = React.useState(null);
  const [onlineTs, setOnlineTs] = React.useState(null);
  // Communication quality, read alongside the status codes. THIS is the
  // signal that actually decides online — see _commOnline below.
  const [comm, setComm] = React.useState(null);
  // UNREGISTERED rows: removed from the device's param table, but kept listed
  // so the operator can finish the job — the "a meter stopped collecting →
  // remove + re-add to kick it" workflow.
  //
  // DURABLE since 2026-07-30 (operator request). The mark lives SERVER-SIDE in
  // dcu_unregistered (see emeter/dcu_routing.py) and ends only on an explicit
  // outcome: Register puts the meter back on the DCU, Delete removes it for
  // good. Refresh no longer drops it, and it survives closing the browser.
  // sessionStorage is retained purely as a warm cache so the table paints
  // instantly on a DCU switch, then _fetchUnreg reconciles against the server.
  const _loadUnreg = (dcu) => {
    try { return JSON.parse(sessionStorage.getItem(`emeter_unreg_${dcu}`) || '[]') || []; }
    catch { return []; }
  };
  // The SERVER is the source of truth for Unregistered rows; sessionStorage is
  // only a warm cache so the table paints instantly on a DCU switch. Marks now
  // persist across browser sessions and survive Refresh — they end only when
  // the operator re-registers the meter or deletes it for good.
  const _fetchUnreg = React.useCallback(async (dcu) => {
    if (!dcu) return;
    try {
      const res = await api(`/dcus/${dcu}/unregistered`);
      const rows = (res?.unregistered || []).map(r => ({
        ldn: r.ldn, meterSerial: r.ldn, commType: r.comm_type,
        comm_addr: r.comm_addr, dev_type: r.dev_type,
        sap_id: '\u2014', _unreg: true, _pendingRemove: false, _pendingAdd: false,
      }));
      if (String(curDcuRef.current) !== String(dcu)) return;  // operator moved on
      setCleared(rows);
    } catch (_) { /* keep the cached copy; a failed fetch is not evidence */ }
  }, []);
  // End the Unregistered state server-side. Called after a successful
  // re-register (the meter is back on the DCU) or a completed delete (gone for
  // good). Local bookkeeping only — it never touches the device.
  const _forgetUnreg = React.useCallback(async (dcu, ldns) => {
    const list = (ldns || []).filter(Boolean);
    if (!dcu || list.length === 0) return;
    try {
      await api(`/dcus/${dcu}/unregistered/forget`, {
        method: 'POST', body: JSON.stringify({ ldns: list }),
      });
    } catch (_) { /* the local mark is already gone; server retries next fetch */ }
  }, []);
  const [cleared, setClearedRaw] = React.useState(() => _loadUnreg(selectedDcu));
  const setCleared = React.useCallback((updater) => {
    setClearedRaw(prev => {
      const next = (typeof updater === 'function' ? updater(prev) : updater) || [];
      try { sessionStorage.setItem(`emeter_unreg_${selectedDcu}`, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, [selectedDcu]);

  // Set when a removal job reports done, consumed by the reconcile effect on
  // the next device read it triggers. Declared above the DCU-switch block so
  // that block can reset it: a flag left over from the previous DCU would
  // reconcile against the NEW DCU's table and could drop a mark whose own job
  // is still in flight.
  const reconcileMarksRef = React.useRef(false);
  // Per-DCU reset happens during RENDER, not in an effect. Effects run after
  // the commit is painted, so resetting there let one frame of the PREVIOUS
  // DCU's rows paint under the new DCU's header. Assigning state during render
  // of this same component makes React throw away this render's output and
  // re-run it with the cleared state before anything reaches the screen.
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) {
    setPrevDcu(selectedDcu);
    setSelected(new Set());
    setOnline(null);
    setOnlineTs(null);
    setClearedRaw(_loadUnreg(selectedDcu));  // warm cache; server refreshes below
    reconcileMarksRef.current = false;
  }
  // Refresh re-reads the DEVICE and KEEPS the Unregistered rows (operator
  // decision 2026-07-30, reversing the 2026-07-24 drop-on-refresh behaviour).
  // A removal is usually the first half of "remove + re-add to kick a stalled
  // meter", and the operator may come back to it hours later or in a new
  // browser session — so the mark now ends ONLY when they finish the job:
  // Register puts the meter back on the DCU, Delete removes it for good.
  // Nothing ever re-registers silently. The reconcile effect still drops a
  // mark the device contradicts (meter still present = the removal failed).
  const handleRefresh = () => {
    _fetchUnreg(selectedDcu);
    if (onReload) onReload();
  };
  const _markUnregistered = (rows) => {
    if (!rows || rows.length === 0) return;
    setCleared(prev => {
      const have = new Set(prev.map(m => m.ldn));
      const add = rows.filter(m => m && m.ldn && !have.has(m.ldn))
        // Drop the staging flags: once the row is Unregistered there is no
        // outstanding staged change on it, and a frozen _pendingRemove would
        // keep rendering a "to remove" badge on a meter already removed.
        .map(m => ({ ...m, sap_id: '—', _unreg: true, _pendingRemove: false, _pendingAdd: false }));
      return add.length ? [...prev, ...add] : prev;
    });
  };
  // Which DCU is on screen RIGHT NOW. Background-job callbacks fire a minute or
  // more after the click, by which time the operator may have moved on; the
  // closure's `selectedDcu` is stale, this ref is not. Assigned during render,
  // matching reloadMeterTablesRef above.
  const curDcuRef = React.useRef(selectedDcu);
  curDcuRef.current = selectedDcu;
  // Remove Unregistered marks. Component state is only touched while that DCU
  // is still on screen; the sessionStorage copy is corrected either way, so a
  // mark we know to be wrong cannot reappear when the operator navigates back.
  const _unmarkUnregistered = React.useCallback((dcu, rows) => {
    const drop = new Set((rows || []).map(m => m.ldn).filter(Boolean));
    if (drop.size === 0) return;
    if (String(curDcuRef.current) === String(dcu)) {
      setCleared(prev => prev.filter(m => !drop.has(m.ldn)));
      return;
    }
    try {
      const key = `emeter_unreg_${dcu}`;
      const cur = JSON.parse(sessionStorage.getItem(key) || '[]') || [];
      sessionStorage.setItem(key, JSON.stringify(cur.filter(m => !drop.has(m.ldn))));
    } catch (_) { /* storage unavailable — the mark is a convenience, not truth */ }
  }, [setCleared]);
  // Removals are marked Unregistered optimistically, at click time. If the
  // DEVICE still reports one after the job lands, it was not actually removed
  // (row rejected, partial write) — drop the mark rather than let the table
  // claim a meter is gone while the DCU still holds it. Gated on a TRUSTED
  // read: a failed param read is not evidence of anything.
  React.useEffect(() => {
    if (!reconcileMarksRef.current) return;
    if (paramReadState !== 'ok') return;
    reconcileMarksRef.current = false;
    const onDevice = new Set(meters.filter(m => m.source === 'dcu').map(m => m.ldn));
    if (onDevice.size === 0) return;
    setCleared(prev => prev.filter(m => !onDevice.has(m.ldn)));
  }, [meters, paramReadState, setCleared]);

  // Reconcile the warm cache against the server on every DCU change.
  React.useEffect(() => { _fetchUnreg(selectedDcu); }, [selectedDcu, _fetchUnreg]);

  const clearedLdns = React.useMemo(() => new Set(cleared.map(m => m.ldn)), [cleared]);
  // Registered rows (from the device) minus any just cleared, then the cleared
  // rows tagged UNREGISTERED at the end.
  const allRows = React.useMemo(
    () => [...meters.filter(m => !clearedLdns.has(m.ldn)), ...cleared],
    [meters, cleared, clearedLdns]);
  const filtered = allRows.filter(m => {
    if (filter && !String(m.ldn).includes(filter.trim())) return false;
    if (typeFilter && _deriveMeterTypeFromSerial(m.ldn) !== typeFilter) return false;
    return true;
  });
  const lastClickedRef = React.useRef(null);
  const toggleOne = (ldn, event) => {
    const idx = filtered.findIndex(m => m.ldn === ldn);
    if (event?.shiftKey && lastClickedRef.current !== null && lastClickedRef.current !== idx && idx >= 0) {
      // Shift+click selects the whole range between the last click and this one.
      const from = Math.min(lastClickedRef.current, idx);
      const to = Math.max(lastClickedRef.current, idx);
      setSelected(prev => {
        const next = new Set(prev);
        for (let i = from; i <= to; i++) { const a = filtered[i]?.ldn; if (a) next.add(a); }
        return next;
      });
    } else {
      setSelected(prev => { const n = new Set(prev); n.has(ldn) ? n.delete(ldn) : n.add(ldn); return n; });
    }
    lastClickedRef.current = idx;
  };
  const toggleAll = () => setSelected(p => p.size === filtered.length ? new Set() : new Set(filtered.map(m => m.ldn)));
  // Malformed = the Quick-Approve bug's LDN shape "HLY" + 12 digits. One-click
  // select just those so an operator can clear/rebuild only the bad rows.
  const _isMalformedHly = (ldn) => /^HLY\d{12}$/.test(String(ldn || ''));
  const malformedCount = filtered.filter(m => _isMalformedHly(m.ldn)).length;
  const selectMalformed = () => {
    setSelected(new Set(filtered.filter(m => _isMalformedHly(m.ldn)).map(m => m.ldn)));
    lastClickedRef.current = null;
  };

  // Vendor parity: MDC's "Check Online Meters" fills a per-row Result column
  // and a header "Online Meter Count". We do both from the same read, and also
  // hand the parent a summary so the DCU header can show it next to Total.
  const checkOnline = async () => {
    setBusy('online');
    try {
      const r = await api(`/dcus/${selectedDcu}/online-status`);
      const entries = r?.entries || [];
      // Then link quality, SEQUENTIALLY — a DCU serves one task at a time.
      // get_online_status returns codes whose meaning has never been proven
      // (see DCU_ONLINE_STATUS), so on its own this report can state nothing.
      // get_communication_quality returns node_status, documented on the wire
      // as 0 = offline / nonzero = online, plus send/receive frame counts.
      // A meter the DCU has exchanged frames with is online; that is evidence,
      // not an assumption. Failure here is non-fatal: the codes still render.
      let commRows = null;
      try {
        const q = await api(`/dcus/${selectedDcu}/communication-quality`);
        // shape_mismatch => the DCU answered, but not in the per-meter shape
        // this decoder understands. That is NOT "no meters": treating it as an
        // empty list is the same conflation the decoder itself used to make,
        // and it is what let 129255052983 present three fabricated rows of
        // link quality built from the ASCII codes of a serial number.
        // null routes it down the existing "non-fatal, codes still render"
        // path instead of showing a confident empty table.
        commRows = q?.shape_mismatch ? null : (q?.meters || []);
      } catch (e) { commRows = null; }
      setComm(commRows);
      // Per-status-code counts. This is the evidence that settles what the
      // codes mean (see DCU_ONLINE_STATUS) — surface it, don't bury it.
      const counts = new Map();
      for (const e of entries) counts.set(e.status, (counts.get(e.status) || 0) + 1);
      const buckets = Array.from(counts.entries())
        .map(([code, n]) => ({ code, n, ..._onlineStatusInfo(code) }))
        .sort((a, b) => b.n - a.n);
      const anyKnown = buckets.some(b => b.known);
      const ts = new Date();
      setOnline(r); setOnlineTs(ts);
      if (onOnlineRead) {
        onOnlineRead({
          total: entries.length,
          buckets,
          // Only a real number once the code mapping is proven; null keeps the
          // header from printing a count it cannot stand behind.
          // Counted from link quality when we have it — a real number we can
          // stand behind. Falls back to the code mapping only if that read
          // failed AND the mapping has since been confirmed.
          online: commRows
            ? commRows.filter(_commOnline).length
            : (anyKnown ? entries.filter(e => _onlineStatusInfo(e.status).online).length : null),
          onlineSource: commRows ? 'link quality' : (anyKnown ? 'status code' : null),
          // Which status code lines up with link-quality-online, so
          // DCU_ONLINE_STATUS can be settled from data rather than a guess.
          codeEvidence: commRows ? _codeVsLinkQuality(entries, commRows) : null,
          ts,
        });
      }
      toast.push(`Online status read — ${entries.length} entr${(entries.length === 1) ? 'y' : 'ies'}`, 'success');
    } catch (e) { toast.push(`Check online failed: ${e.message}`, 'error'); }
    finally { setBusy(null); }
  };

  // Online read, indexed by meter so it can be rendered ON the row it belongs
  // to (vendor "Result" column) instead of in a second table the operator has
  // to cross-reference by hand. Also computes the divergence against our own
  // rows, which the vendor tool has no way to show at all.
  const onlineIndex = React.useMemo(() => {
    const byMeter = new Map();
    for (const e of (online?.entries || [])) byMeter.set(_normSerial(e.meter), e);
    if (byMeter.size === 0) return { byMeter, unknown: [], missing: 0 };
    const ours = new Set((meters || []).map(m => _normSerial(m.ldn)));
    // On the DCU but not in our rows — these are invisible in the grid, so
    // they get their own short list below. The reverse case (in our rows, not
    // reported) shows up as "not reported" in the row's Result cell.
    const unknown = Array.from(byMeter.values()).filter(e => !ours.has(_normSerial(e.meter)));
    const missing = Array.from(ours).filter(x => x && !byMeter.has(x)).length;
    return { byMeter, unknown, missing };
  }, [online, meters]);
  const commIndex = React.useMemo(() => {
    const byMeter = new Map();
    for (const q of (comm || [])) {
      // An all-zero meter_id decodes to "" — the DCU pads its node array with
      // EMPTY slots. Those carry no identity, so they can never be matched to
      // a row: absence from this map means "not reported", NEVER "offline".
      const k = _normSerial(q.meter_id);
      if (k) byMeter.set(k, q);
    }
    return byMeter;
  }, [comm]);

  const download = async () => {
    if (!window.confirm('Trigger historical data collection (Download) for this DCU now?\n\nRuns in the background and may take a while. Collects from all routed meters.')) return;
    setBusy('download');
    try {
      await api(`/dcus/${selectedDcu}/catchup-historical`, { method: 'POST' });
      toast.push('Data collection started for this DCU', 'success');
    } catch (e) { toast.push(`Download failed: ${e.message}`, 'error'); }
    finally { setBusy(null); }
  };
  // Delete by KNOWN LDN — the server uses the targeted spec §5.3 remove-by-LDN
  // command and skips the full-table re-read (which is what times out under
  // collection load). We already have the LDN from the displayed table.
  const deprovOne = async (sap, ldn) => api(
    `/dcus/${selectedDcu}/deprovision-meter/${sap}${ldn ? `?ldn=${encodeURIComponent(ldn)}` : ''}`,
    { method: 'POST' });
  // Clear now STAGES a removal instead of writing to the DCU immediately.
  // The device is only touched by an explicit Upload, so a batch of edits
  // becomes ONE verified clear+re-add, it can be reviewed before committing,
  // and it works while the DCU is offline (these DCUs drop constantly).
  const clearSelected = async () => {
    const targets = filtered.filter(m => selected.has(m.ldn));
    if (targets.length === 0) { toast.push('Nothing selected', 'info'); return; }
    if (!window.confirm(
      `Mark ${targets.length} meter(s) for REMOVAL from DCU ${selectedDcu}?\n\n`
      + `• Nothing is written to the DCU yet — this only stages the change.\n`
      + `• Click "Upload to DCU" to apply all staged changes in one verified write.\n`
      + `• Historical data is always kept.`
    )) return;
    setBusy('clear');
    try {
      const st = await api(`/dcus/${selectedDcu}/stage-meters`, {
        method: 'POST',
        body: JSON.stringify({ remove: targets.map(m => m.ldn) }),
      });
      // A row routed via ANOTHER DCU (amber "on DCU …") is a duplicate here;
      // the server refuses to stage it (that would mark the other DCU's row)
      // and says to use the row's own Clear button.
      const n = (st?.staged_remove || []).length;
      const note = _stageMovesNote(st);
      toast.push(`${n} meter(s) staged for removal — click Upload to DCU to apply.${note ? ` ${note}` : ''}`,
        (st?.skipped || []).length ? 'warning' : 'success');
      setSelected(new Set());
      if (onPendingChanged) onPendingChanged();
    } catch (e) {
      toast.push(`Staging failed: ${e.message}`, 'error');
    } finally { setBusy(null); }
  };

  // Apply every staged add/remove in ONE verified device write.
  const uploadToDcu = async () => {
    const nAdd = (pending?.add || []).length, nRem = (pending?.remove || []).length;
    if (!nAdd && !nRem) { toast.push('No staged changes to upload', 'info'); return; }
    if (!window.confirm(
      `Upload staged changes to DCU ${selectedDcu}?\n\n`
      + `• ${nAdd} meter(s) to add, ${nRem} to remove.\n`
      + `• The DCU's parameter table is read, merged and rewritten ONCE, then\n`
      + `  verified by read-back. Refused server-side if the table cannot be\n`
      + `  read, or if the result would be empty.`
    )) return;
    setBusy('upload');
    // Snapshot the rows this upload will REMOVE before firing. Once the write
    // lands the server drops them from the routing map (clear_pending deletes
    // an applied removal outright), so this is the last moment we still hold
    // their LDN and comm type — the two things a re-register needs.
    const forDcu = String(selectedDcu);
    const removing = meters.filter(m => m._pendingRemove);
    try {
      const res = await api(`/dcus/${selectedDcu}/upload-param-table`, {
        method: 'POST',
        body: JSON.stringify({ confirm_serial: String(selectedDcu) }),
      });
      if (res?.job_id) {
        bgJobs.add({
          id: res.job_id, dcu: forDcu, op: `Upload +${nAdd}/-${nRem}`,
          // The write takes a minute or more on a busy DCU. Refresh the view
          // when it lands rather than relying on the operator to remember —
          // the whole reason removed meters looked "still registered".
          onDone: (state, job) => {
            // A failed upload wrote nothing, so the rows are still on the
            // device: take the Unregistered mark back off or the table would
            // claim they are gone.
            if (state !== 'done') _unmarkUnregistered(forDcu, removing);
            // Adopt the upload's OWN verified read-back as the new device
            // truth. The server already re-read the table to confirm what
            // landed and now returns it as `after_fields`, so there is nothing
            // left to discover: caching it here means a successful Register
            // shows rows as registered immediately instead of falling back to
            // routing-map rows marked "unconfirmed" and pushing the operator
            // into a manual Refresh — a multi-block read on a 91-row table that
            // contends with collection, purely to learn what we just measured.
            const af = job?.result?.after_fields;
            if (state === 'done' && Array.isArray(af) && onParamConfirmed) {
              onParamConfirmed(forDcu, af, job?.result?.after_read_at);
            }
            if (String(curDcuRef.current) !== forDcu) return;  // operator moved on
            reconcileMarksRef.current = (state === 'done');
            if (onReload) onReload();
            if (onPendingChanged) onPendingChanged();
          },
        });
        // Keep the removed rows on screen as UNREGISTERED instead of letting
        // them vanish. A removal is usually the first half of "remove + re-add
        // to kick a stalled meter", and after the upload the routing map no
        // longer carries them — this list is the only way back.
        _markUnregistered(removing);
        toast.push(
          nRem > 0
            ? `Upload queued — the ${nRem} removed meter(s) stay listed as Unregistered so you can re-register them; the list refreshes itself when the job lands`
            : 'Upload queued — the list refreshes itself when the job lands',
          'success');
      } else {
        toast.push('Upload did not queue — check the journal', 'error');
      }
    } catch (e) {
      toast.push(`Upload failed: ${e.message}`, 'error');
    } finally { setBusy(null); }
  };
  const clearAll = async () => {
    if (!window.confirm('Deregister ALL meters from this DCU? The DCU will stop collecting from every meter.\n\nRuns in the background — watch the Background jobs panel. Rows stay listed as Unregistered so you can re-register them this session; Refresh drops them.')) return;
    setBusy('clear');
    try {
      const res = await api(`/meters/${selectedDcu}/deregister-meters`, { method: 'DELETE' });
      if (res?.job_id) {
        const forDcu = String(selectedDcu);
        const wiped = meters.filter(m => typeof m.sap_id === 'number');
        bgJobs.add({
          id: res.job_id, dcu: forDcu, op: 'Clear All',
          onDone: (state) => {
            if (state !== 'done') _unmarkUnregistered(forDcu, wiped);
            if (String(curDcuRef.current) !== forDcu) return;
            reconcileMarksRef.current = (state === 'done');
            if (onReload) onReload();
            if (onPendingChanged) onPendingChanged();
          },
        });
        // Optimistically show every registered row as Unregistered; the job
        // commits the wipe in the background and onDone reconciles.
        _markUnregistered(wiped);
        toast.push('Clear All queued — rows shown as Unregistered (Register to re-add, Refresh to drop); the list refreshes itself when the job lands', 'success');
        setSelected(new Set());
      } else {
        toast.push('Clear All did not queue — check the journal', 'error');
      }
    } catch (e) { toast.push(`Clear all failed: ${e.message}`, 'error'); }
    finally { setBusy(null); }
  };
  // Re-register cleared-this-session rows via the async approve-from-blacklist
  // path — progress shows in the Background jobs panel. On success we drop them
  // from the unregistered list; they re-appear as Registered after Refresh.
  const registerRows = async (rows) => {
    const addrs = (rows || []).map(m => _commAddrFor(m.ldn, m.commType)).filter(Boolean);
    if (addrs.length === 0) { toast.push('No comm address for the selected meters', 'info'); return; }
    if (!window.confirm(
      `Re-register ${addrs.length} meter(s) back onto DCU ${selectedDcu}?\n\n`
      + `• G3 PLC (6), LLS auth, default password; SAPs auto-assigned\n`
      + `• Runs in the background — watch the Background jobs panel.`
    )) return;
    setBusy('register');
    try {
      const res = await api(`/dcus/${selectedDcu}/approve-from-blacklist`, {
        method: 'POST',
        body: JSON.stringify({ communication_addresses: addrs }),
      });
      if (res?.job_id) {
        const forDcu = String(selectedDcu);
        const back = rows.slice();
        bgJobs.add({
          id: res.job_id, dcu: forDcu, op: `Re-register ${res.count || addrs.length}`,
          onDone: (state) => {
            // Re-registration failed: the meters are NOT back on the DCU, so
            // restore the Unregistered rows we optimistically dropped below —
            // otherwise they vanish from the view and the operator loses the
            // only handle they had on them.
            if (state !== 'done') {
              _markUnregistered(back);
            } else {
              // Back on the DCU — end the Unregistered state server-side, or
              // the next fetch would resurrect the row.
              _forgetUnreg(forDcu, back.map(m => m.ldn));
            }
            if (String(curDcuRef.current) !== forDcu) return;
            if (onReload) onReload();
            if (onPendingChanged) onPendingChanged();
          },
        });
        toast.push(`Re-registration queued for ${res.count || addrs.length} meter(s) — the list refreshes itself when the job lands`, 'success');
        const done = new Set(rows.map(m => m.ldn));
        setCleared(prev => prev.filter(m => !done.has(m.ldn)));
        setSelected(prev => { const n = new Set(prev); done.forEach(l => n.delete(l)); return n; });
      } else {
        toast.push('Re-registration did not queue — check the journal', 'error');
      }
    } catch (e) { toast.push(`Re-register failed: ${e.message}`, 'error'); }
    finally { setBusy(null); }
  };
  // PERMANENT delete of Unregistered rows from OUR database — the other half of
  // the choice an Unregistered row offers: put it back (Register) or drop it.
  //
  // Deliberately NO device I/O: `deregister_from_dcu=false`. An Unregistered row
  // is already off the DCU's parameter table, so there is nothing to remove
  // there — and a parameter-table write is the single most dangerous operation
  // in this system (it wiped three live DCUs on 2026-07-20). Delete must never
  // be a reason to touch one.
  //
  // Historical data is NOT deleted. The purge endpoint removes the assignment,
  // the routing entry, the live pool entry and the known-devices record, and
  // leaves SQLite profile_rows/readings untouched by design.
  const purgeRows = async (rows) => {
    const targets = (rows || []).filter(m => m && (m.meterSerial || m.ldn));
    if (targets.length === 0) { toast.push('Nothing to delete', 'info'); return; }
    if (!window.confirm(
      `PERMANENTLY delete ${targets.length} meter(s) from the database?\n\n`
      + `• Removes the customer/transformer assignment, the DCU routing entry,\n`
      + `  the live session entry and the known-devices record.\n`
      + `• Historical readings are KEPT — the readings database is not touched.\n`
      + `• The DCU is NOT contacted: these meters are already off its\n`
      + `  parameter table, so nothing is written to the device.\n\n`
      + `This cannot be undone from the UI.`
    )) return;
    setBusy('purge');
    const okLdns = [];
    const failed = [];
    try {
      for (const m of targets) {
        const serial = String(m.meterSerial || m.ldn);
        try {
          await api(`/meters/${encodeURIComponent(serial)}/purge?deregister_from_dcu=false`,
            { method: 'DELETE' });
          okLdns.push(m.ldn);
        } catch (e) {
          failed.push(`${serial}: ${e.message}`);
        }
      }
    } finally { setBusy(null); }
    // Only drop rows that ACTUALLY deleted. A failed purge leaves the meter in
    // the database, and dropping its row would take away the only handle the
    // operator has on it.
    if (okLdns.length) {
      const done = new Set(okLdns);
      // Deleted for good — end the Unregistered state server-side too, or the
      // row would come back on the next fetch.
      _forgetUnreg(String(selectedDcu), okLdns);
      setCleared(prev => prev.filter(x => !done.has(x.ldn)));
      setSelected(prev => { const n = new Set(prev); done.forEach(l => n.delete(l)); return n; });
      toast.push(`${okLdns.length} meter(s) deleted — historical readings kept`, 'success');
      if (onPendingChanged) onPendingChanged();
    }
    if (failed.length) {
      toast.push(`${failed.length} delete(s) failed — ${failed[0]}`, 'error');
    }
  };
  const clearRow = async (m) => {
    if (typeof m.sap_id !== 'number') { toast.push('Unknown SAP — cannot clear this meter', 'info'); return; }
    if (!window.confirm(`Deregister ${m.ldn} (Point No ${m.sap_id}) from this DCU?\n\nRuns in the background — watch the Background jobs panel.`)) return;
    setBusy('clear');
    try {
      // Route per-row Clear through the same async background path as Clear
      // Selected (reliable read + rewrite on the DCU worker; never hangs).
      const res = await api(`/dcus/${selectedDcu}/deprovision-meters`, {
        method: 'POST',
        body: JSON.stringify({ confirm_serial: String(selectedDcu), sap_ids: [m.sap_id] }),
      });
      if (res?.job_id) {
        const forDcu = String(selectedDcu);
        bgJobs.add({
          id: res.job_id, dcu: forDcu, op: `Clear ${m.ldn}`,
          onDone: (state) => {
            if (state !== 'done') _unmarkUnregistered(forDcu, [m]);
            if (String(curDcuRef.current) !== forDcu) return;
            reconcileMarksRef.current = (state === 'done');
            if (onReload) onReload();
            if (onPendingChanged) onPendingChanged();
          },
        });
        _markUnregistered([m]);
        toast.push(`Clear queued for ${m.ldn} — shown as Unregistered (Register to re-add); the list refreshes itself when the job lands`, 'success');
      } else {
        toast.push('Clear did not queue — check the journal', 'error');
      }
    }
    catch (e) { toast.push(`Clear failed: ${e.message}`, 'error'); }
    finally { setBusy(null); }
  };
  // Rows that are registered in OUR database but not present on the DEVICE.
  // Two ways to get there and the remedy is identical for both:
  //   * _unreg          — a Clear All tombstoned it
  //   * _missingOnDevice — a live read came back without it
  // The second had no action at all: the banner said "select them and use
  // Register" and no such button existed, because the bulk actions were gated
  // on the tombstone alone. Anything in this state can only be put back
  // (Register) or given up on (permanent Delete) — Clear is meaningless, the
  // device does not have it.
  const actionableRows = filtered.filter(m => m._unreg || m._missingOnDevice);
  const selectedUnreg = actionableRows.filter(m => selected.has(m.ldn));
  const registerSelected = () => registerRows(selectedUnreg);
  const deleteSelected = () => purgeRows(selectedUnreg);

  // Repair mis-registered child LDNs (the Quick-Approve "HLY<serial>" bug):
  // dry-run first → show the exact plan (which rows, old → new LDN, and the
  // write strategy) → operator confirms → apply → per-row verified results.
  // Server-side safety: only rows for OUR mapped meters with NO recent data
  // are touched (working meters are always spared), read-back verifies the
  // corrected LDN, and big tables use the per-row remove+add primitives.
  // Render a finished repair result (from the job's result payload, or from
  // a legacy synchronous backend response — same shape either way).
  const finishRepair = (res) => {
    const rep = res?.repaired || [];
    if (rep.length === 0 && res?.note === 'nothing to repair') {
      toast.push('Nothing left to repair — rows were already fixed', 'success');
      if (onChanged) onChanged();
      return;
    }
    const okN = rep.filter(r => r.verified).length;
    const badN = rep.length - okN;
    toast.push(
      `Repaired ${okN}/${rep.length} LDN(s)` + (badN ? ` — ${badN} not verified, re-run or check journal` : '')
      + (res?.lost_saps?.length ? ` ⚠ lost SAPs: ${res.lost_saps.join(',')}` : ''),
      badN || res?.lost_saps?.length ? 'error' : 'success');
    if (onChanged) onChanged();
  };

  const repairLdns = async () => {
    // No upfront dry-run — that live block-transfer read is the slow "hang".
    // The rebuild job re-reads and computes the corrected table at run time, so
    // we just confirm and fire; progress shows in the Background jobs panel.
    if (!window.confirm(
      `Repair malformed LDNs on DCU ${selectedDcu}?\n\n`
      + `• Clears the parameter table and re-adds every row with corrected\n`
      + `  bare-serial LDNs — the vendor-proven method (all firmware, incl. 123223).\n`
      + `• Good meters are re-added unchanged; verified by read-back.\n`
      + `• Runs in the background — watch the Background jobs panel; safe to switch tabs.`
    )) return;
    try {
      const res = await api(`/dcus/${selectedDcu}/param-rebuild`, {
        method: 'POST',
        body: JSON.stringify({ confirm_serial: String(selectedDcu), dry_run: false, source: 'device' }),
      });
      if (res?.job_id) {
        bgJobs.add({ id: res.job_id, dcu: String(selectedDcu), op: 'Repair LDNs' });
        toast.push('Repair queued — see the Background jobs panel (safe to switch tabs)', 'success');
        // Refresh the table once the rebuild has had time to land.
        setTimeout(() => { if (onChanged) onChanged(); }, 30000);
      } else {
        toast.push('Repair did not queue — check the journal', 'error');
      }
    } catch (e) {
      toast.push(`Repair failed: ${e.message}`, 'error');
    }
  };

  const th = { textAlign: 'left', padding: '5px 6px', borderBottom: `1px solid ${c.border}`, fontSize: '11px', color: c.textMuted, textTransform: 'uppercase' };
  const td = { padding: '6px', borderBottom: `1px solid ${c.border}22`, fontSize: '12px' };
  const anyBusy = busy != null || reloading;

  return (
    <div>
      {/* Filter + action toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
        <input placeholder="Meter No." value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding: '5px 10px', fontSize: '12px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', width: '140px' }} />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '5px 10px', fontSize: '12px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px' }}>
          <option value="">All types</option>
          <option value="0">Single Phase</option>
          <option value="1">Three Phase Direct</option>
          <option value="2">Three Phase CT</option>
          <option value="3">Three Phase CT/VT</option>
        </select>
        <div style={{ flex: 1 }} />
        <button style={_dcuBtn('#16a34a', anyBusy)} disabled={anyBusy} onClick={handleRefresh}>{reloading ? '…' : '↻'} Refresh</button>
        <button style={_dcuBtn('#1e40af', anyBusy)} disabled={anyBusy} onClick={checkOnline}>{busy === 'online' ? '…' : '📡'} Check Online</button>
        <button style={_dcuBtn('#16a34a', anyBusy)} disabled={anyBusy} onClick={download}>{busy === 'download' ? '…' : '⬇'} Download</button>
        <button style={_dcuBtn('#9333ea', anyBusy || !!repairJob || meters.length === 0)} disabled={anyBusy || !!repairJob || meters.length === 0} onClick={repairLdns}
          title="Fix mis-registered child LDNs from the old Quick-Approve bug (HLY-prefixed rows that never associate). Dry-run plan first; working meters are never touched. The write runs in the background — the table stays usable.">
          {repairJob ? '⏳ Repairing…' : busy === 'repair' ? '…' : '🔧 Repair LDNs'}</button>
        {malformedCount > 0 && (
          <button style={_dcuBtn('#a16207', anyBusy)} disabled={anyBusy} onClick={selectMalformed}
            title="Select every row whose LDN is the malformed 'HLY'+serial shape from the old Quick-Approve bug.">
            ⚠ Select malformed ({malformedCount})</button>
        )}
        {actionableRows.length > 0 && (
          <button style={_dcuBtn('#16a34a', anyBusy || selectedUnreg.length === 0)} disabled={anyBusy || selectedUnreg.length === 0} onClick={registerSelected}
            title="Write the selected meters back onto this DCU's parameter table (runs in the background). Covers both Unregistered rows and rows a live read found missing from the device.">
            {busy === 'register' ? '…' : '⬆'} Register Selected ({selectedUnreg.length})</button>
        )}
        {actionableRows.length > 0 && (
          <button style={_dcuBtn('#450a0a', anyBusy || selectedUnreg.length === 0)} disabled={anyBusy || selectedUnreg.length === 0} onClick={deleteSelected}
            title="Permanently delete the selected meters from the database (assignment, routing, known-devices). Historical readings are KEPT and the DCU is not contacted. Use when the meter is gone for good.">
            {busy === 'purge' ? '…' : '🗑'} Delete Selected ({selectedUnreg.length})</button>
        )}
        <button style={_dcuBtn('#f43f5e', anyBusy || selected.size === 0)} disabled={anyBusy || selected.size === 0} onClick={clearSelected}>🗑 Clear Selected ({selected.size})</button>
        {(((pending?.add || []).length + (pending?.remove || []).length) > 0) && (
          <button key="upload-to-dcu" style={_dcuBtn('#059669', anyBusy)} disabled={anyBusy} onClick={uploadToDcu}
            title="Write all staged additions and removals to the DCU in one verified operation">
            {busy === 'upload' ? '…' : '⬆'} Upload to DCU (+{(pending?.add || []).length}/−{(pending?.remove || []).length})
          </button>
        )}
        <button style={_dcuBtn('#7c3aed', anyBusy || meters.length === 0)} disabled={anyBusy || meters.length === 0}
          onClick={() => setBulkMove({ serials: selected.size ? [...selected] : null, total: selected.size || meters.length })}
          title="Move meters from this DCU to another one: deregistered here FIRST, then registered there — never on both at once, and rolled back if the target refuses.">
          🛰 Move {selected.size ? `Selected (${selected.size})` : `All (${meters.length})`} to DCU…</button>
        <button style={_dcuBtn('#dc2626', anyBusy || meters.length === 0)} disabled={anyBusy || meters.length === 0} onClick={clearAll}>🧹 Clear All</button>
      </div>
      {bulkMove && (
        <BulkMoveDcuModal
          fromDcu={selectedDcu}
          serials={bulkMove.serials}
          total={bulkMove.total}
          onClose={() => setBulkMove(null)}
          onMoved={() => { setBulkMove(null); setSelected(new Set()); if (onPendingChanged) onPendingChanged(); if (onReload) onReload(); }}
        />
      )}
      {/* Result of the last get_online_status read. The per-meter answer now
          lives in the Result COLUMN of the grid below — vendor parity, and it
          removes the cross-referencing the old separate 253-row sub-table
          forced on the operator. What stays here is the part the vendor tool
          cannot show at all: how the DCU's own list diverges from ours. */}
      {onlineTs && (
        <div style={{ marginBottom: '10px', border: `1px solid ${c.border}`,
                      borderRadius: '6px', padding: '8px 12px', background: c.bgInput,
                      fontSize: '12px', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          <strong>📋 DCU reported {online?.entries?.length ?? 0} meter{(online?.entries?.length === 1) ? '' : 's'}</strong>
          <span style={{ color: c.textMuted, fontWeight: 400 }}>read {onlineTs.toLocaleTimeString()}</span>
          {onlineIndex.unknown.length > 0 && (
            <span style={{ color: '#f59e0b' }}>{onlineIndex.unknown.length} on the DCU but not in our rows</span>
          )}
          {onlineIndex.missing > 0 && (
            <span style={{ color: '#ef4444' }} title="In our rows but the DCU did not report them — see the Result column.">
              {onlineIndex.missing} in our rows but not reported
            </span>
          )}
          {onlineIndex.unknown.length === 0 && onlineIndex.missing === 0 && (
            <span style={{ color: c.success }}>✓ matches our rows exactly</span>
          )}
        </div>
      )}
      {/* Rows the DCU reports that we have no row for. They cannot appear in
          the grid, so without this they would be silently dropped — which is
          precisely the DB-vs-device divergence this read exists to expose. */}
      {onlineIndex.unknown.length > 0 && (
        <div style={{ marginBottom: '10px', border: '1px solid #f59e0b55', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ padding: '6px 12px', background: '#f59e0b14', fontSize: '12px', color: '#f59e0b', fontWeight: 600 }}>
            ⚠ On the DCU but unknown to us — {onlineIndex.unknown.length}
          </div>
          <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: c.bgInput, position: 'sticky', top: 0 }}>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>SAP</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Meter</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Result</th>
                </tr>
              </thead>
              <tbody>
                {onlineIndex.unknown.map((e, i) => (
                  <tr key={`${e.sap_id}-${e.meter}-${i}`} style={{ borderTop: `1px solid ${c.border}` }}>
                    <td style={{ textAlign: 'right', padding: '3px 8px', color: c.textLabel }}>{e.sap_id}</td>
                    <td style={{ padding: '3px 8px', fontFamily: 'monospace' }}>{e.meter}</td>
                    <td style={{ padding: '3px 8px', color: c.textLabel }}>{_onlineStatusInfo(e.status).label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Staged changes are the whole point of the two-step flow, so make them
          impossible to miss — a toolbar button alone was overlooked. */}
      {(((pending?.add || []).length + (pending?.remove || []).length) > 0) && (
        <div key="staged-banner" style={{ fontSize: '12px', color: '#38bdf8', background: '#38bdf818',
          border: '1px solid #38bdf855', borderRadius: '6px', padding: '10px 12px',
          marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>
            ⬆ <strong>{(pending?.add || []).length} to add</strong>
            {(pending?.remove || []).length > 0 && <> · <strong>{(pending?.remove || []).length} to remove</strong></>}
            {' '}— staged in the system but <strong>not yet written to the DCU</strong>.
          </span>
          <button style={_dcuBtn('#059669', anyBusy)} disabled={anyBusy} onClick={uploadToDcu}>
            {busy === 'upload' ? '…' : '⬆'} Upload to DCU now
          </button>
        </div>
      )}
      {/* Say plainly where these rows came from. Without this an empty device
          table looked identical to a healthy one (the rows silently came from
          our routing map), so a Clear that landed — or one that did NOT — was
          indistinguishable. */}
      {paramReadState === 'ok' && meters.length === 0 && (
        <div style={{ fontSize: '12px', color: '#f59e0b', background: '#f59e0b18',
          border: '1px solid #f59e0b55', borderRadius: '6px', padding: '8px 10px', marginBottom: '8px' }}>
          ⚠ <strong>The DCU's parameter table is EMPTY</strong> — confirmed by a live read,
          and we have no registered meters on file for it either.
          Register meters from the <strong>DCU Black List</strong> tab.
        </div>
      )}
      {/* Same meter at two SAPs on the DEVICE (HighTown 26.09: an unacked batch
          plus an add). The server now refuses to write one; this shows any that
          already exist, since the merged list below collapses them to one row. */}
      {(() => {
        const rows = Array.isArray(paramTable) ? paramTable
          : (paramTable && Array.isArray(paramTable.value) ? paramTable.value : []);
        const bySer = {};
        rows.forEach(r => {
          const k = String(r.ldn || r.logical_dev_name || '').trim().toUpperCase();
          if (k) (bySer[k] = bySer[k] || []).push(r.sap_id);
        });
        const dups = Object.entries(bySer).filter(([, saps]) => saps.length > 1);
        if (!dups.length) return null;
        return (
          <div style={{ fontSize: '12px', color: '#ef4444', background: '#ef444418',
            border: '1px solid #ef444455', borderRadius: '6px', padding: '8px 10px', marginBottom: '8px' }}>
            ⚠ <strong>{dups.length} meter(s) are registered TWICE in the DCU's parameter table</strong>
            {' '}— {dups.map(([k, saps]) => `${k} (Point No ${saps.join(' & ')})`).join(', ')}.
            Removing the extra row needs a Clear All and a re-register of the table.
          </div>
        );
      })()}
      {/* The rows are our DATABASE inventory; the device read says which of
          them the DCU actually holds. When some are absent, that gap IS the
          headline — it is the difference between "registered" and "collecting".
          Counting it here keeps the warning true whether the device returned
          nothing at all or merely dropped a few rows. */}
      {paramReadState === 'ok' && meters.some(m => m._missingOnDevice) && (
        <div style={{ fontSize: '12px', color: '#f59e0b', background: '#f59e0b18',
          border: '1px solid #f59e0b55', borderRadius: '6px', padding: '8px 10px', marginBottom: '8px' }}>
          ⚠ <strong>{meters.filter(m => m._missingOnDevice).length} of {meters.length} registered
          meter(s) are NOT in the DCU's parameter table</strong> — confirmed by a live read.
          They are listed below as <strong>Not on DCU</strong> and will collect nothing until re-added.
          Select them and use <strong>Register</strong>, or use Repair to restore from the routing map.
        </div>
      )}
      {paramReadState !== 'ok' && meters.length > 0 && (
        <div style={{ fontSize: '12px', color: '#94a3b8', background: '#94a3b815',
          border: `1px solid ${c.border}`, borderRadius: '6px', padding: '8px 10px', marginBottom: '8px' }}>
          {paramReadState === 'failed'
            ? <>⚠ <strong>Could not read the DCU's parameter table.</strong> The rows below come from our routing map and are <strong>NOT confirmed on the device</strong> — a Clear may or may not have landed. Click <strong>Refresh</strong> to retry the read.</>
            : <>These rows come from our routing map — the device has not been read this session, so they are <strong>unconfirmed</strong>. Click <strong>Refresh</strong> to read the DCU's actual parameter table.</>}
        </div>
      )}
      {filtered.length === 0 ? (
        <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic', padding: '10px' }}>
          {reloading ? 'Loading meters…' : 'No meters configured on this DCU. Register meters from the DCU Black List tab.'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '30px' }}>
                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} />
              </th>
              <th style={th}>#</th>
              <th style={th}>Point No</th>
              <th style={th}>Comm Addr</th>
              <th style={th}>Meter No</th>
              <th style={th}>Meter Type</th>
              {/* Vendor calls this "Download" — whether the row is actually on
                  the device. Ours carries the same fact plus the staged states
                  the vendor has no concept of, so it keeps the wider name. */}
              <th style={th}>Status</th>
              {/* Vendor "Result": filled by Check Online, blank until then. */}
              <th style={th} title="Per-meter result of the last Check Online read of the DCU.">Result</th>
              <th style={th}>Operate</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, i) => {
              const typeForm = _deriveMeterTypeFromSerial(m.ldn);
              const regDcu = regDcuFor ? regDcuFor(m.ldn) : null;
              const elsewhere = regDcu && regDcu !== selectedDcu;
              // Vendor parity: MDC tints the rows its Check Online returned as
              // online. We only tint once DCU_ONLINE_STATUS is proven — an
              // "online" tint driven by a guessed code mapping would be the
              // same class of confident-but-unfounded device claim this pane
              // has been stripped of everywhere else.
              const onlineRow = onlineIndex.byMeter.get(_normSerial(m.ldn));
              const onlineInfo = onlineRow ? _onlineStatusInfo(onlineRow.status) : null;
              const commRow = commIndex.get(_normSerial(m.ldn));
              const commUp = commRow ? _commOnline(commRow) : null;
              const rowBg = selected.has(m.ldn) ? '#1e40af18'
                : m._unreg ? '#f59e0b1e'
                : elsewhere ? '#f59e0b14'
                : commUp === true ? '#22c55e12'
                : (onlineInfo && onlineInfo.known && onlineInfo.online) ? '#22c55e12'
                : 'transparent';
              return (
                <tr key={m.ldn} style={{ background: rowBg }}>
                  <td style={td}><input type="checkbox" checked={selected.has(m.ldn)} onClick={(e) => toggleOne(m.ldn, e)} readOnly title="Shift+click to select a range" /></td>
                  <td style={{ ...td, color: c.textMuted }}>{i + 1}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{m.sap_id}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{_commAddrFor(m.ldn, m.commType)}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{m.ldn}</td>
                  <td style={td}>{METER_TYPE_LABEL[typeForm] || '—'}</td>
                  <td style={td}>
                    {m._unreg
                      ? <span title="Removed from the DCU. Stays listed until you Register it back or Delete it for good — Refresh will not drop it." style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 600 }}>Unregistered</span>
                      : elsewhere
                      ? <span title={`DUPLICATE: in this DCU's table AND in ${regDcu}'s, which is where it is collected. Clear removes only this copy (the customer view then follows ${regDcu}). To keep it here instead, open ${regDcu} and Move it to this DCU.`} style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 600 }}>⚠ duplicate — collected via {regDcu}</span>
                      : m._pendingRemove
                      ? <span title="Staged for removal. Still on the DCU until you click Upload to DCU." style={{ color: '#f87171', fontSize: '11px', fontWeight: 600 }}>Pending removal</span>
                      : m._pendingAdd
                      ? <span title="Added in our system but NOT yet written to the DCU. Click Upload to DCU to apply." style={{ color: '#38bdf8', fontSize: '11px', fontWeight: 600 }}>Pending upload</span>
                      : m.source === 'dcu'
                      ? <span title="Confirmed present in the DCU's parameter table by a live read." style={{ color: '#22c55e', fontSize: '11px' }}>Registered</span>
                      // We DID read the device and this meter was not in it.
                      // Materially different from "Unconfirmed" (we never
                      // looked) and must not be softened into it: this row is
                      // registered in our database but absent from the DCU, so
                      // it collects nothing until it is re-added.
                      : m._missingOnDevice
                      ? <span title="Registered in our database but NOT present in the DCU's parameter table on the last live read. It will collect nothing until re-added — select it and use Register / Upload to DCU." style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 600 }}>Not on DCU</span>
                      // Only the routing map knows about this row — the device
                      // has NOT confirmed it. Never claim "Registered" here:
                      // that is what made a cleared table look healthy.
                      : <span title="From our routing map only — NOT confirmed on the DCU. Click Refresh to read the device parameter table." style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>Unconfirmed</span>}
                  </td>
                  <td style={td}>
                    {!onlineTs
                      ? <span title="Click Check Online to ask the DCU which meters it is holding." style={{ color: c.textMuted, fontSize: '11px' }}>—</span>
                      // LINK QUALITY FIRST. node_status is documented
                      // (0 = offline, nonzero = online) and comes with frame
                      // counts, so this is a verdict we can stand behind —
                      // unlike the status code, whose meaning is unproven.
                      : commUp === true
                      ? <span title={`Link quality: node_status ${commRow.node_status}, ${commRow.send_frames} frames sent / ${commRow.recv_frames} received, ${commRow.success_rate}% success${commRow.relay_level != null ? `, relay level ${commRow.relay_level}` : ''}.`}
                          style={{ color: '#22c55e', fontSize: '11px', fontWeight: 600 }}>
                          Online
                          {commRow.success_rate != null && (
                            <span style={{ color: c.textMuted, fontWeight: 400 }}>
                              {' '}{commRow.success_rate}%
                            </span>
                          )}
                        </span>
                      // A MATCHED row with no traffic in either direction is a
                      // real negative — meters do die in the field, and this is
                      // the case where saying so is correct rather than a guess.
                      : commUp === false
                      ? <span title={`Link quality: node_status ${commRow.node_status}, no frames sent or received. The DCU holds a slot for this meter but is not talking to it.`}
                          style={{ color: '#f43f5e', fontSize: '11px', fontWeight: 600 }}>Offline</span>
                      : !onlineRow
                      // The vendor leaves this blank; blank is indistinguishable
                      // from "not read yet", so we say which it is.
                      ? <span title="The DCU did NOT report this meter in the last Check Online read. Note an empty/short reply is a known DCU behaviour — re-read before acting on it." style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 600 }}>not reported</span>
                      : <span title={onlineInfo.known
                            ? `Reported by the DCU at SAP ${onlineRow.sap_id}.`
                            : `Reported by the DCU at SAP ${onlineRow.sap_id} with status code ${onlineRow.status}. What this code means is not yet confirmed — see the per-code counts in the DCU header.`}
                          style={{ color: onlineInfo.known ? (onlineInfo.online ? '#22c55e' : c.textMuted) : c.textLabel, fontSize: '11px', fontWeight: 600 }}>
                          {onlineInfo.label}
                          {/* The DCU holding this meter at a different SAP than
                              our row claims is a real routing fault — every
                              pass-through read we issue is addressed by SAP. */}
                          {typeof m.sap_id === 'number' && typeof onlineRow.sap_id === 'number'
                            && m.sap_id !== onlineRow.sap_id && (
                            <span style={{ color: '#ef4444', fontWeight: 700 }}
                              title={`SAP mismatch: our row says ${m.sap_id}, the DCU reports ${onlineRow.sap_id}. Pass-through reads address meters by SAP, so this row is being read at the wrong address.`}>
                              {' '}⚠ SAP {onlineRow.sap_id}
                            </span>
                          )}
                        </span>}
                  </td>
                  <td style={td}>
                    {(m._unreg || m._missingOnDevice)
                      // Off the DCU but still in our database — whether a
                      // Clear All tombstoned it (_unreg) or a live read simply
                      // did not find it (_missingOnDevice). Offer the only two
                      // meaningful ways out: put it back, or drop it. "Clear"
                      // is nonsense here — the device does not have the row to
                      // deregister — and offering it as the sole action is why
                      // these rows looked stuck.
                      ? <span style={{ display: 'flex', gap: '6px' }}>
                          <button style={{ ..._dcuBtn('#14532d', anyBusy), color: '#86efac', padding: '3px 10px', fontSize: '11px' }}
                            disabled={anyBusy} onClick={() => registerRows([m])} title="Re-register this meter onto the DCU">Register</button>
                          <button style={{ ..._dcuBtn('#450a0a', anyBusy), color: '#fca5a5', padding: '3px 10px', fontSize: '11px' }}
                            disabled={anyBusy} onClick={() => purgeRows([m])}
                            title="Permanently delete this meter from the database (assignment, routing, known-devices). Historical readings are KEPT and the DCU is not contacted.">Delete</button>
                        </span>
                      : <button style={{ ..._dcuBtn('#7f1d1d', anyBusy), color: '#fca5a5', padding: '3px 10px', fontSize: '11px' }}
                          disabled={anyBusy} onClick={() => clearRow(m)} title="Deregister this meter from the DCU">Clear</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── DCU Communication Quality (manual live read) ─────────────────────
function DcuCommQualitySection({ selectedDcu, knownMeters }) {
  const c = useThemeColors();
  // The per-meter DCU<->meter link. We ALWAYS show the registered connections
  // (knownMeters, from our routing/param tables) so the meter<->DCU links are
  // visible even when the DCU's live read fails (session torn down / slow /
  // offline). A "Read" overlays the live get_communication_quality metrics
  // (OBIS 0.0.96.51.0.255): node_status, success_rate, send/recv frames, relay
  // level, node phase (+ any Extra N). DCU GPRS uplink (/status) is a context line.
  const [cq, setCq] = React.useState(null);       // null=not read, false=failed, {meters,count}=data
  const [status, setStatus] = React.useState(null);
  const [ts, setTs] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // Stale-DCU guard: reset during render (an effect paints one frame of the
  // previous DCU's quality table first) and drop a live read that resolves
  // after the operator switched DCU.
  const curRef = React.useRef(selectedDcu);
  curRef.current = selectedDcu;
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) {
    setPrevDcu(selectedDcu);
    setCq(null); setStatus(null); setTs(null); setSearch(''); setBusy(false);
  }

  const read = async () => {
    const forDcu = selectedDcu;
    setBusy(true);
    try {
      const [q, s] = await Promise.allSettled([
        api(`/dcus/${forDcu}/communication-quality`),
        api(`/dcus/${forDcu}/status`),
      ]);
      if (curRef.current !== forDcu) return;   // switched mid-read — discard
      setCq(q.status === 'fulfilled' && q.value ? q.value : false);
      setStatus(s.status === 'fulfilled' && s.value ? s.value : false);
      setTs(new Date());
    } finally { if (curRef.current === forDcu) setBusy(false); }
  };

  const note = { color: c.textMuted, fontSize: '12px', padding: '10px 0' };
  const readDone = ts != null && !busy;
  const liveFailed = readDone && cq === false;
  const st = status && typeof status === 'object' ? status : null;
  const w = st?.status_word;
  const commFlags = (() => {
    if (w == null) return null;
    const f = [];
    if (w & 0x02) f.push('GPRS modem failure');
    if (w & 0x04) f.push('GPRS signal low');
    if (w & 0x08) f.push('PLC failure');
    if (w & 0x10) f.push('RF failure');
    if (w & 0x200) f.push('RS-485 failure');
    return f;
  })();
  const liveMeters = (cq && typeof cq === 'object' && Array.isArray(cq.meters)) ? cq.meters : [];
  const liveBySerial = {};
  for (const m of liveMeters) liveBySerial[String(m.meter_id)] = m;
  const known = knownMeters || [];
  const knownSerials = new Set(known.map(k => String(k.ldn)));
  // Registered connections (always) + any live nodes the DCU reported that
  // aren't in our records (rogue/unregistered show up here too).
  const rows = [
    ...known.map(k => ({ serial: String(k.ldn), live: liveBySerial[String(k.ldn)] || null, registered: true })),
    ...liveMeters.filter(m => m.meter_id && !knownSerials.has(String(m.meter_id))).map(m => ({ serial: String(m.meter_id), live: m, registered: false })),
  ];
  const filtered = rows.filter(r => !search || r.serial.includes(search.trim()));
  const extraCount = liveMeters.reduce((mx, m) => Math.max(mx, (m.extra || []).length), 0);
  const isOnline = (m) => !!m && ((m.node_status != null && m.node_status !== 0) || (m.send_frames > 0) || (m.recv_frames > 0));
  const phaseLabel = (m) => isOnline(m) ? (['A', 'B', 'C'][m.node_phase] ?? 'Unknown') : 'Unknown';
  const th = { textAlign: 'left', padding: '5px 6px', borderBottom: `1px solid ${c.border}`, fontSize: '11px', color: c.textMuted, textTransform: 'uppercase' };
  const td = { padding: '6px', borderBottom: `1px solid ${c.border}22`, fontSize: '12px' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>DCU Communication Quality</div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {rows.length > 0 && <input placeholder="Comm Addr" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '5px 10px', fontSize: '12px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', width: '130px' }} />}
          {ts && <span style={{ fontSize: '11px', color: c.textMuted }}>Read {ts.toLocaleTimeString()}</span>}
          <button style={_dcuBtn('#1e40af', busy)} disabled={busy} onClick={read}>{busy ? 'Reading…' : (readDone ? '↻ Re-read' : '📄 Read')}</button>
        </div>
      </div>

      {/* DCU uplink context (extra metric: the DCU's own GPRS link) */}
      {st && (
        <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '10px' }}>
          DCU uplink: GPRS signal <strong style={{ color: c.textPrimary }} title={dcuSignalInfo(st).title}>{dcuSignalInfo(st).long}</strong>
          {' · '}downstream <strong style={{ color: c.textPrimary }}>{st.downstream_meter_count ?? '—'}</strong>
          {' · '}link <strong style={{ color: commFlags && commFlags.length ? c.danger : '#22c55e' }}>{commFlags == null ? '—' : (commFlags.length ? commFlags.join(', ') : 'OK')}</strong>
        </div>
      )}

      {/* Status line: pre-read hint, or live-read-failed warning */}
      {!readDone ? (
        <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '8px', fontStyle: 'italic' }}>
          {busy ? 'Reading live link quality from the DCU… (can take up to ~30s if the DCU is slow)'
            : rows.length > 0 ? 'Showing registered meter↔DCU connections. Click “📄 Read” to overlay live link metrics.'
            : 'Click “📄 Read” to read this DCU’s per-node link quality.'}
        </div>
      ) : liveFailed ? (
        <div style={{ ...note, color: c.warning, paddingTop: 0 }}>
          ⚠ The DCU didn’t return live link data this read — it’s slow, on a weak link, or its session was just torn down (DCUs dial in over GPRS). Showing the registered connections below; metrics show “—”. Try “↻ Re-read” shortly.
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div style={note}>
          {liveFailed ? 'No registered meters on this DCU yet.' : 'No meters registered on this DCU, and the DCU reported no live nodes.'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>#</th>
              <th style={th}>Comm Addr</th>
              <th style={th}>Node Status</th>
              <th style={th}>Node Success Rate</th>
              <th style={th}>Send Frame Count</th>
              <th style={th}>Receive Frame Count</th>
              <th style={th}>Relay Level</th>
              <th style={th}>Node Phase</th>
              {Array.from({ length: extraCount }, (_, i) => <th key={i} style={th}>Extra {i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const m = r.live;
              const online = isOnline(m);
              const statusLabel = m ? (online ? 'Online/Connected' : 'Offline/Disconnected')
                : (r.registered ? 'Registered (no live data)' : '—');
              return (
                <tr key={r.serial + i}>
                  <td style={{ ...td, color: c.textMuted }}>{i + 1}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>
                    {r.serial}{!r.registered ? <span title="Not in our system (live node only)" style={{ color: c.warning }}> ⚠</span> : null}
                  </td>
                  <td style={{ ...td, color: m ? (online ? '#22c55e' : c.textMuted) : c.textMuted, fontWeight: 600 }}>{statusLabel}</td>
                  <td style={td}>{m && m.success_rate != null ? `${m.success_rate}%` : '—'}</td>
                  <td style={td}>{m && m.send_frames != null ? m.send_frames : '—'}</td>
                  <td style={td}>{m && m.recv_frames != null ? m.recv_frames : '—'}</td>
                  <td style={td}>{m && m.relay_level != null ? `Level${m.relay_level}` : '—'}</td>
                  <td style={td}>{m ? phaseLabel(m) : '—'}</td>
                  {Array.from({ length: extraCount }, (_, k) => <td key={k} style={td}>{(m && m.extra && m.extra[k] != null) ? m.extra[k] : '—'}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Per-DCU toggle for the daily "abnormal PLC connection" blacklist scan.
// Auto-on for PLC/RF DCUs; this lets an admin force it on/off for one DCU.
function DcuPlcScanToggle({ selectedDcu }) {
  const c = useThemeColors();
  const toast = useToast();
  const [cfg, setCfg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // Stale-DCU guard — without it a slow /plc-scan reply for the DCU you just
  // left renders as THIS DCU's On/Off state (and the toggle would then write
  // the wrong DCU's setting).
  const curRef = React.useRef(selectedDcu);
  curRef.current = selectedDcu;
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) { setPrevDcu(selectedDcu); setCfg(null); setBusy(false); }
  React.useEffect(() => {
    if (!selectedDcu) return;
    const forDcu = selectedDcu;
    api(`/dcus/${forDcu}/plc-scan`)
      .then(d => { if (curRef.current === forDcu) setCfg(d); })
      .catch(() => {});
  }, [selectedDcu]);
  const toggle = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const r = await api(`/dcus/${selectedDcu}/plc-scan`, { method: 'POST', body: JSON.stringify({ enabled: !cfg.enabled }) });
      setCfg(r);
      toast.push(`Daily PLC scan ${r.enabled ? 'enabled' : 'disabled'} for this DCU`, 'success');
    } catch (e) { toast.push(e.message, 'error'); }
    finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 400 }}
      title="Daily scan of this DCU's blacklist for meters not in our system. Auto-on for PLC/RF DCUs.">
      <span style={{ fontSize: '11px', color: c.textMuted }}>PLC auto-scan</span>
      <button onClick={toggle} disabled={busy}
        style={{ padding: '2px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, cursor: busy ? 'wait' : 'pointer', border: 'none', color: '#fff', background: cfg.enabled ? '#16a34a' : '#6b7280', opacity: busy ? 0.6 : 1 }}>
        {cfg.enabled ? 'On' : 'Off'}
      </button>
      {!cfg.is_plc && <span style={{ fontSize: '10px', color: c.textMuted }}>(RS-485)</span>}
    </span>
  );
}

// `onStatusRefreshed` is the parent's setDcuStatus. The Refresh Status
// button used to call a bare `setDcuStatus`, which does not exist in this
// component — dcuStatus arrives as a prop and the state lives in the
// parent. That was a ReferenceError on every click (found 2026-07-26 by
// linting for undefined identifiers, same class of bug as the `_id` crash
// in CustomerCollectionStatsCard).
// Vendor's fourth tab. Deliberately a RAW viewer, not a rendered topology:
// read_dcu_topology returns the decoded value verbatim because the response
// layout for get_topology is not in the captured corpus, and inventing a
// schema for a device response we have never seen is the same mistake that
// produced the get_online_status garbage decode. So this tab does the one
// useful thing available today — issue the read and show exactly what came
// back, which is the evidence needed to model it properly.
// ─── DCU Topology — mesh map from the vendor `get_topology` selector ───
//
// Response layout decoded 2026-08-02 from docs/specs/logs_vendor_v15
// (DCU 129205034829). Per node: address, node id, PARENT node id, and a
// packed flags byte whose LOW nibble is hop depth from the DCU and HIGH
// nibble is PLC phase. Verified by cross-check: for all 85 rows with a
// resolvable parent, child.hop == parent.hop + 1, zero exceptions.
//
// The number printed under each node is the raw flags byte in DECIMAL —
// 0x11 -> 17, 0x12 -> 18, 0x21 -> 33 — matching what the vendor tool shows,
// so a screenshot from either tool can be compared directly.
//
// hop_level 0 on a non-DCU row = the DCU holds NO ROUTE to that meter. That
// orphan signal exists nowhere else: the blacklist only lists UNregistered
// meters, and online-status returns 0 rows even on healthy DCUs.
function buildTopologyTree(nodes) {
  const kids = new Map();
  let root = null;
  for (const n of nodes) {
    if (n.is_dcu) { root = n; continue; }
    const p = n.parent_node;
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(n);
  }
  for (const arr of kids.values()) arr.sort((a, b) => (a.node_id || 0) - (b.node_id || 0));

  const pos = new Map();
  let cursor = 0;
  const walk = (n, depth, guard) => {
    if (guard.has(n.node_id)) return null;   // cycle guard — never trust device data
    guard.add(n.node_id);
    const ch = kids.get(n.node_id) || [];
    let x;
    if (!ch.length) {
      x = cursor++;
    } else {
      const xs = ch.map(c => walk(c, depth + 1, guard)).filter(v => v != null);
      x = xs.length ? (xs[0] + xs[xs.length - 1]) / 2 : cursor++;
    }
    pos.set(n.node_id, { x, depth, node: n });
    return x;
  };
  if (root) walk(root, 0, new Set());

  // Anything the walk never reached — orphans, or rows whose parent isn't in
  // the table. Render them rather than dropping them: an unreachable meter is
  // the whole point of looking at this view.
  const stranded = nodes.filter(n => !pos.has(n.node_id));
  const maxDepth = Math.max(0, ...Array.from(pos.values()).map(p => p.depth));
  stranded.forEach(n => pos.set(n.node_id, { x: cursor++, depth: maxDepth + 1, node: n, stranded: true }));

  return { pos, kids, root, width: Math.max(1, cursor), depth: maxDepth + (stranded.length ? 1 : 0) };
}

function DcuTopologySection({ selectedDcu }) {
  const c = useThemeColors();
  const toast = useToast();
  const [raw, setRaw] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [ts, setTs] = React.useState(null);
  const [view, setView] = React.useState('chart');   // 'chart' | 'data'
  const [zoom, setZoom] = React.useState(1);
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) { setPrevDcu(selectedDcu); setRaw(null); setTs(null); }

  const read = async () => {
    setBusy(true);
    try {
      const forDcu = selectedDcu;
      const r = await api(`/dcus/${forDcu}/topology`);
      if (forDcu !== selectedDcu) return;   // operator switched DCU mid-read
      setRaw(r); setTs(new Date());
      toast.push(`Topology read — ${r?.count ?? 0} node(s)`, 'success');
    } catch (e) { toast.push(`Topology read failed: ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const nodes = (raw && Array.isArray(raw.nodes)) ? raw.nodes : [];
  const tree = React.useMemo(() => nodes.length ? buildTopologyTree(nodes) : null, [nodes]);
  // node_id -> node, so a row can name the meter it hangs off rather than
  // just the parent's index (the vendor table only gives the index).
  const byNode = React.useMemo(
    () => new Map(nodes.map(n => [n.node_id, n])), [nodes]);
  const [sort, setSort] = React.useState('node');
  const sortedNodes = React.useMemo(() => {
    const a = nodes.slice();
    // Orphans first in hop order — the rows an operator is looking for.
    a.sort((x, y) => (sort === 'hop')
      ? (Number(y.orphan) - Number(x.orphan)) || (x.hop_level - y.hop_level) || (x.node_id - y.node_id)
      : (x.node_id - y.node_id));
    return a;
  }, [nodes, sort]);

  const NODE_W = 116, ROW_H = 86, PAD = 30;
  const svgW = tree ? tree.width * NODE_W + PAD * 2 : 0;
  const svgH = tree ? (tree.depth + 1) * ROW_H + PAD * 2 : 0;
  const px = (p) => PAD + p.x * NODE_W + NODE_W / 2;
  const py = (p) => PAD + p.depth * ROW_H + 14;

  const nodeColor = (n) => n.is_dcu ? c.accent : (n.orphan ? c.danger : c.success);

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
        <button style={_dcuBtn('#1e40af', busy)} disabled={busy} onClick={read}>
          {busy ? 'Reading…' : (raw ? '↻ Re-read' : '📄 Read')}
        </button>
        {tree && (
          <>
            <button className="button button-sm button-secondary" onClick={() => setZoom(z => Math.min(2, z + 0.15))} title="Zoom in">＋</button>
            <button className="button button-sm button-secondary" onClick={() => setZoom(z => Math.max(0.35, z - 0.15))} title="Zoom out">－</button>
            <button className="button button-sm button-secondary" onClick={() => setZoom(1)} title="Reset zoom">Fit</button>
          </>
        )}
        {ts && <span style={{ fontSize: '11px', color: c.textMuted }}>read {ts.toLocaleTimeString()}</span>}
      </div>

      {raw && nodes.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '14px' }}>
          <KpiTile label="Nodes" value={raw.count ?? nodes.length} color="#00e5ff" />
          <KpiTile label="Direct (hop 1)" value={raw.direct ?? 0} color="#22c55e" />
          <KpiTile label="Relayed" value={raw.relayed ?? 0} color="#f59e0b" />
          <KpiTile label="No route" value={raw.orphan_count ?? 0} color={(raw.orphan_count ?? 0) ? '#ef4444' : '#64748b'} />
          <KpiTile label="Max depth" value={raw.max_depth ?? 0} color="#7c3aed" />
        </div>
      )}

      {raw && nodes.length > 0 && (
        <div style={{ marginBottom: '10px', display: 'flex', gap: '2px' }}>
          <button style={_dcuBtn(view === 'data' ? '#334155' : 'transparent', false)}
                  onClick={() => setView('data')}>Topology Data</button>
          <button style={_dcuBtn(view === 'chart' ? '#334155' : 'transparent', false)}
                  onClick={() => setView('chart')}>Topology Chart</button>
        </div>
      )}

      {raw == null ? (
        <div style={{ padding: '20px', textAlign: 'center', color: c.textMuted, fontSize: '12px' }}>
          Click “📄 Read” to map this DCU’s PLC mesh.
        </div>
      ) : nodes.length === 0 ? (
        <div style={{ padding: '14px', border: '1px solid #f59e0b55', background: '#f59e0b14',
                      borderRadius: '6px', color: '#f59e0b', fontSize: '12px' }}>
          ⚠ Empty response. This is <strong>not</strong> proof the DCU has no topology — vendor
          selective reads on these DCUs return false empties intermittently. Re-read before
          concluding anything.
        </div>
      ) : view === 'data' ? (
        <div className="table-container" style={{ maxHeight: '520px', overflow: 'auto' }}>
          <table>
            <thead>
              <tr>
                {/* Vendor column names kept verbatim so a screenshot from
                    either tool lines up row-for-row. "Node Information" is
                    the raw flags byte in decimal, exactly as they print it. */}
                <th style={{ width: '44px' }}>#</th>
                <th>Comm Addr</th>
                <th onClick={() => setSort(s2 => s2 === 'node' ? 'hop' : 'node')}
                    style={{ cursor: 'pointer' }} title="Click to sort by node / hop">
                  Node Index {sort === 'node' ? '▾' : ''}
                </th>
                <th>Parent Node Index</th>
                <th>Node Information</th>
                {/* Beyond the vendor: the decoded meaning, and the parent's
                    ADDRESS — the vendor only gives you a parent index, so
                    tracing a route means hand-joining the table to itself. */}
                <th>Connected via</th>
                <th>Hop</th>
                <th>Phase</th>
                <th>Route</th>
              </tr>
            </thead>
            <tbody>
              {sortedNodes.map((n, i) => {
                const parent = n.is_dcu ? null : byNode.get(n.parent_node);
                return (
                  <tr key={`${n.node_id}-${n.meter_id}`}
                      style={{ background: n.orphan ? '#ef444414' : undefined }}>
                    <td style={{ color: c.textMuted }}>{i + 1}</td>
                    <td>
                      <code>{n.meter_id}</code>
                      {n.is_dcu && <span style={{ marginLeft: 6, fontSize: 10, color: c.accent }}>DCU</span>}
                    </td>
                    <td>{n.node_id}</td>
                    <td>{n.is_dcu ? '—' : n.parent_node}</td>
                    <td><code>{n.flags}</code></td>
                    <td style={{ fontSize: '11px' }}>
                      {n.is_dcu ? <span style={{ color: c.textMuted }}>—</span>
                        : n.orphan ? <span style={{ color: c.danger }}>nothing</span>
                        : n.parent_node === 0 ? <span style={{ color: c.accent }}>DCU (direct)</span>
                        : parent ? <code>{parent.meter_id}</code>
                        : <span style={{ color: c.warning }} title="parent index not present in this table">
                            node {n.parent_node} (missing)
                          </span>}
                    </td>
                    <td>{n.is_dcu ? '—' : n.hop_level}</td>
                    <td>{n.node_phase || '—'}</td>
                    <td style={{ color: n.orphan ? c.danger : (n.hop_level === 1 ? c.success : c.textSecondary) }}>
                      {n.is_dcu ? 'root'
                        : n.orphan ? '⚠ no route'
                        : n.hop_level === 1 ? 'direct'
                        : `via ${n.hop_level - 1} relay${n.hop_level > 2 ? 's' : ''}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ border: `1px dashed ${c.border}`, borderRadius: '6px',
                      overflow: 'auto', maxHeight: '560px', background: c.bgInput }}>
          <svg width={svgW * zoom} height={svgH * zoom} viewBox={`0 0 ${svgW} ${svgH}`}
               style={{ display: 'block', minWidth: '100%' }}>
            {/* edges first so nodes paint over them */}
            {Array.from(tree.pos.values()).map(p => {
              const n = p.node;
              if (n.is_dcu || p.stranded) return null;
              const pp = tree.pos.get(n.parent_node);
              if (!pp) return null;
              const x1 = px(pp), y1 = py(pp) + 30, x2 = px(p), y2 = py(p) - 8;
              const mid = (y1 + y2) / 2;
              return (
                <path key={`e-${n.node_id}`}
                      d={`M ${x1} ${y1} V ${mid} H ${x2} V ${y2}`}
                      fill="none" stroke={n.orphan ? c.danger : c.border} strokeWidth="1.2" />
              );
            })}
            {Array.from(tree.pos.values()).map(p => {
              const n = p.node;
              const x = px(p), y = py(p);
              return (
                <g key={`n-${n.node_id}-${n.meter_id}`}>
                  <rect x={x - 9} y={y - 9} width="18" height="18" rx="3"
                        fill={nodeColor(n)} opacity={n.orphan ? 0.9 : 0.85} />
                  <text x={x} y={y + 22} textAnchor="middle" fontSize="9.5"
                        fill={c.textPrimary} fontFamily="monospace">{n.meter_id}</text>
                  <text x={x} y={y + 33} textAnchor="middle" fontSize="8.5"
                        fill={n.orphan ? c.danger : c.textMuted}>
                    {n.is_dcu ? 'DCU' : (p.stranded ? 'no route' : n.flags)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {raw && nodes.length > 0 && (
        <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '10px' }}>
          The number under each node is the raw flags byte in <strong>decimal</strong>, matching
          the vendor tool (17 = 0x11 → phase 1, hop 1; 34 = 0x22 → phase 2, hop 2). Red nodes
          have <strong>no route</strong> — the DCU cannot reach them, which neither the blacklist
          nor online-status will tell you.
        </div>
      )}
    </div>
  );
}

function DcuDetailPane({ selectedDcu, dcuStatus, dcuProfile, dcuOnline, focusSection, onStatusRefreshed }) {
  const c = useThemeColors();
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = userCan(user, 'dcu_control');
  const [blacklist, setBlacklist] = React.useState(null);
  const [blacklistLoading, setBlacklistLoading] = React.useState(false);
  const [blacklistTs, setBlacklistTs] = React.useState(null);
  // Last GOOD param-table read, persisted per DCU. Without this, navigating
  // away or logging out threw the read away and every visit started as
  // "unconfirmed", pushing operators to re-read a table that can take ~60 s and
  // contends with collection. We keep the last device truth and show its AGE so
  // staleness is visible rather than pretended away — same trade-off the
  // blacklist already makes with its cached read.
  const _paramKey = (d) => `emeter_paramtbl_${d}`;
  const _loadParam = (d) => {
    try {
      const raw = localStorage.getItem(_paramKey(d));
      if (!raw) return null;
      const o = JSON.parse(raw);
      return (o && o.value) ? o : null;
    } catch { return null; }
  };
  const _saveParam = (d, value) => {
    try { localStorage.setItem(_paramKey(d), JSON.stringify({ value, ts: Date.now() })); }
    catch (_) { /* quota/private mode — non-fatal */ }
  };
  const [paramTable, setParamTable] = React.useState(null);
  const [paramReadAt, setParamReadAt] = React.useState(null);
  const [routedMeters, setRoutedMeters] = React.useState([]);
  const [metersReloading, setMetersReloading] = React.useState(false);
  // Which Refresh is the newest. `.finally()` used to clear the spinner only
  // `if (fresh())` — i.e. only while the same DCU was still selected — so
  // switching DCU mid-read left "refreshing…" on screen forever. Clearing
  // unconditionally is wrong too: a slow response from the PREVIOUS DCU would
  // clear the spinner of the one now loading. Keyed on a generation counter,
  // the newest request owns the spinner and stale ones stay silent.
  const metersReqIdRef = React.useRef(0);
  // What the collector is doing on this DCU, so a queued Refresh can say so
  // rather than showing a spinner indistinguishable from a hang.
  const [dcuBusy, setDcuBusy] = React.useState(null);
  const [allRoutes, setAllRoutes] = React.useState([]);
  // Summary of the last Check Online read, lifted out of Meters Setting so the
  // DCU header can carry it next to Total — the vendor tool's third counter
  // ("Online Meter Count"), and the one that makes "registered but unreachable"
  // visible at a glance. Declared BEFORE the prevDcu reset block below so that
  // block can clear it; a DCU's online read must never bleed onto another DCU.
  const [onlineSummary, setOnlineSummary] = React.useState(null);

  // Stale-response guard. This pane is NOT remounted per DCU (no key=), so
  // switching DCUs reuses the same instance and a slow in-flight read for the
  // DCU you just left can resolve AFTER the switch and render its meters/status
  // under the new DCU — the "meter creep" that flashes for a second then
  // vanishes when the correct read lands. Every async setter below is applied
  // only if its DCU is still the selected one.
  const curDcuRef = React.useRef(selectedDcu);
  curDcuRef.current = selectedDcu;
  // Adopt a param table we already KNOW is device truth, without re-reading.
  // Called when an upload/register job returns its own verified read-back
  // (`after_fields`), so the view flips from "unconfirmed" to registered the
  // moment the write lands. A table read then happens only when something
  // changes — which is the point: the read is expensive (multi-block, contends
  // with collection) and we had just paid for one inside the upload.
  const adoptParamTable = React.useCallback((dcu, fields, readAt) => {
    if (!dcu || !Array.isArray(fields) || fields.length === 0) return;
    const ts = readAt ? readAt * 1000 : Date.now();
    _saveParam(dcu, fields);
    if (String(curDcuRef.current) !== String(dcu)) return;  // cached, not shown
    setParamTable(fields);
    setParamReadAt(new Date(ts));
  }, []);
  // Seed from the SHARED server snapshot before falling back to this browser's
  // copy. Kept only in localStorage, "registered" was a per-browser opinion: a
  // second operator saw routing-map rows marked unconfirmed and re-read a table
  // someone else had just established — a multi-block read on a DCU that serves
  // one task at a time. The server snapshot is written from trusted reads only
  // (an upload's own verified read-back, or a confirmed live read) and refuses
  // an empty result, so a flaky read cannot erase it.
  React.useEffect(() => {
    if (!selectedDcu) return;
    const forDcu = String(selectedDcu);
    api(`/dcus/${forDcu}/param-snapshot`)
      .then(d => {
        const f = d?.fields || [];
        if (!f.length) return;                                   // nothing shared yet
        if (String(curDcuRef.current) !== forDcu) return;         // operator moved on
        const localCached = _loadParam(forDcu);
        const localTs = localCached?.ts || 0;
        const serverTs = d.read_at ? d.read_at * 1000 : 0;
        // An EMPTY local copy never outranks a populated server snapshot, no
        // matter how recent it is. Empty is the one lie a param read tells
        // (~10% false-empty rate), and the server snapshot is written ONLY
        // from verified non-empty reads and is explicitly dropped by Clear All
        // — so "server has rows" is strictly better evidence than "my browser
        // once read nothing".
        //
        // Without this, a stale empty cached during the 2026-07-30 Clear All
        // survived the restore and the panel announced "43 of 43 registered
        // meters are NOT in the DCU's parameter table" while the device held
        // all 43 and the snapshot agreed. A genuinely cleared DCU still shows
        // as empty, because the clear forgets the snapshot too.
        const localIsEmpty = !(_paramPairs(
          localCached ? { value: localCached.value } : null).length);
        if (serverTs >= localTs || localIsEmpty) {
          _saveParam(forDcu, f);
          setParamTable(f);
          setParamReadAt(serverTs ? new Date(serverTs) : null);
        }
      })
      .catch(() => { /* fall back to the local copy; absence is not evidence */ });
  }, [selectedDcu]);

  // Per-DCU state is cleared during RENDER, not in the effect below. Effects
  // run after React commits and the browser paints, so clearing there painted
  // one frame of the PREVIOUS DCU's param table and meter rows under the new
  // DCU's header — and since the refetch is a round-trip, that stale view stuck
  // around until the response landed. Assigning state during render of this
  // same component makes React discard this render's output and immediately
  // re-run it with the cleared state, so stale rows never reach the screen.
  // Deliberately NOT a key={selectedDcu} remount: that would also reset
  // activeSection, and the operator's section sub-tab must survive a DCU switch.
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) {
    setPrevDcu(selectedDcu);
    setBlacklist(null);
    setBlacklistTs(null);
    setRoutedMeters([]);
    setOnlineSummary(null);
    const cached = _loadParam(selectedDcu);
    setParamTable(cached ? { value: cached.value } : null);
    setParamReadAt(cached ? cached.ts : null);
  }

  // Global meter→DCU map (all DCUs) so Meters Setting can flag a meter that's
  // registered to a DIFFERENT DCU than this one (a meter physically written
  // into two DCUs' parameter tables, e.g. after a re-wire / mis-registration).
  React.useEffect(() => { api('/dcu-routes').then(d => setAllRoutes(d?.routes || [])).catch(() => {}); }, []);
  const regDcuMap = React.useMemo(() => {
    const m = {};
    for (const r of allRoutes) {
      const dcu = r.dcu_serial;
      if (!dcu) continue;
      if (r.ldn) m[String(r.ldn)] = dcu;
      if (r.meter_serial) { m[String(r.meter_serial)] = dcu; m[String(r.meter_serial).replace(/\D/g, '').slice(-12)] = dcu; }
    }
    return m;
  }, [allRoutes]);
  const regDcuFor = React.useCallback((ldn) => {
    if (!ldn) return null;
    const s = String(ldn);
    return regDcuMap[s] || regDcuMap[s.replace(/\D/g, '').slice(-12)] || null;
  }, [regDcuMap]);
  const [regDialog, setRegDialog] = React.useState(null);
  const [regLoading, setRegLoading] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('meters');

  // Deep-link: when the dashboard "Abnormal PLC" box focuses a DCU + section.
  React.useEffect(() => { if (focusSection) setActiveSection(focusSection); }, [focusSection, selectedDcu]);

  // LAZY daily-profile read — only when the Status sub-tab is actually open.
  // This is a live DLMS read of the DCU's aggregate profile and can take tens
  // of seconds on a slow link, so it must never run just because an operator
  // clicked a DCU (see the parent's selection effect). Falls back to the prop
  // for any caller that still supplies one.
  const [lazyProfile, setLazyProfile] = React.useState(null);
  React.useEffect(() => {
    if (activeSection !== 'status' || !selectedDcu) return;
    const forDcu = selectedDcu;
    let stale = false;
    setLazyProfile(null);
    api(`/dcus/${forDcu}/profiles/daily`)
      .then(p => { if (!stale && curDcuRef.current === forDcu) setLazyProfile(p); })
      .catch(() => {});
    return () => { stale = true; };
  }, [activeSection, selectedDcu]);
  const effProfile = dcuProfile || lazyProfile;

  // Local routing map only (registered meters, NO DLMS round-trip / no device
  // contact). Runs on open so landing on a DCU is instant and never touches the
  // meter/DCU over the air.
  const loadRoutes = React.useCallback(() => {
    if (!selectedDcu) return;
    const forDcu = selectedDcu;
    api(`/dcus/${forDcu}/meters?source=routes`)
      .then(r => { if (curDcuRef.current === forDcu) setRoutedMeters(r?.meters || []); })
      .catch(() => {});
  }, [selectedDcu]);

  // Manual reconcile: local routes PLUS a LIVE over-the-air read of the DCU's
  // parameter table. Only ever runs on an explicit Refresh click — never
  // automatically — since the param read is the slow/heavy DLMS call and drives
  // the "refreshing…" indicator.
  const reloadMeterTables = React.useCallback(() => {
    if (!selectedDcu) return;
    const forDcu = selectedDcu;
    const fresh = () => curDcuRef.current === forDcu;  // still the same DCU?
    const myReq = ++metersReqIdRef.current;             // newest request wins
    setMetersReloading(true);
    // Tell the operator WHY this may take minutes. A DCU serves one task at a
    // time, so this read queues behind collection; on 129245047919 (227 meters)
    // that meant the full 240s deadline with a bare spinner and no explanation.
    setDcuBusy(null);
    api(`/dcus/${forDcu}/busy`)
      .then(b => { if (fresh()) setDcuBusy(b); })
      .catch(() => {});
    // Hard client-side deadline. The param-table read serialises on the DCU's
    // per-device lock, which the nightly collection holds for 30-90 s per
    // passthrough read — so on a busy DCU this request can hang indefinitely
    // and, because we only clear the spinner when BOTH promises settle, the UI
    // sat on "refreshing…" forever with no feedback (observed 2026-07-26 on
    // 129205032819). Time it out and say so instead of hanging.
    const _deadline = (p, ms, what) => Promise.race([
      p,
      new Promise((_, rej) => setTimeout(() => rej(new Error(`${what} timed out after ${ms / 1000}s`)), ms)),
    ]);
    Promise.allSettled([
      // 90 s was shorter than the read itself. Measured on 129205032819:
      // 34-197 s, because a contended block read on a 247-row table is slow
      // and the server used to insist on TWO corroborating reads. The client
      // gave up first, so a SUCCESSFUL scan surfaced as "failed" and the pane
      // fell back to routing-map rows marked UNCONFIRMED. The server side is
      // now ~halved (a non-empty block read is trusted on first sighting), but
      // the ceiling still has to clear the worst observed single read.
      _deadline(api(`/meters/${forDcu}/read-raw/0.0.21.0.1.255?attribute=2&class_id=1`), 240000, 'Parameter-table read')
        .then(v => {
          if (!fresh()) return;
          setParamTable(v);
          // Only cache a TRUSTED read — never the "None" failure sentinel.
          if (v && v.value != null && v.value !== 'None') {
            _saveParam(forDcu, v.value);
            setParamReadAt(Date.now());
          }
        })
        .catch(e => {
          // Surface it as an explicit failed read: rows stay "Unconfirmed"
          // rather than the spinner implying work is still happening.
          if (fresh()) { setParamTable({ value: 'None' }); toast.push(
            `${e.message}. The DCU is likely busy with collection — try again shortly.`, 'error'); }
        }),
      api(`/dcus/${forDcu}/meters?source=routes`)
        .then(r => { if (fresh()) setRoutedMeters(r?.meters || []); }),
    ]).finally(() => {
      // Release on generation, not on `fresh()`. Gating the release on "same
      // DCU still selected" meant switching DCU mid-read stranded the spinner
      // on forever; releasing unconditionally would let a slow response from
      // the PREVIOUS DCU clear the spinner of the one now loading. Only the
      // newest request owns it. State updates above stay behind `fresh()` so a
      // stale response can never repaint the table.
      if (metersReqIdRef.current === myReq) {
        setMetersReloading(false);
        setDcuBusy(null);
      }
    });
  }, [selectedDcu, toast]);

  // Manual-only DCU device reads: opening a DCU NEVER contacts it over the air.
  // State for the previous DCU is already cleared in the render-phase block
  // above, so this only LOADS local/persisted data — the routing map
  // (registered meters) and the last cached blacklist. The live param-table
  // read (Refresh), blacklist read (Read Blacklist) and comm-quality read all
  // wait for an explicit click.
  React.useEffect(() => {
    if (!selectedDcu) return;
    const forDcu = selectedDcu;
    loadRoutes();          // local routing map only — no over-the-air read
    // Load the PERSISTED (cached) blacklist — no DCU I/O — so the last read is
    // shown immediately on open. Only adopt it if the DCU has actually been
    // read before (read_at set); otherwise leave it for a manual Read.
    api(`/meters/${forDcu}/dcu-blacklist?cached=1`)
      .then((d) => { if (curDcuRef.current === forDcu && d && d.read_at) { setBlacklist(d); setBlacklistTs(new Date(d.read_at * 1000)); } })
      .catch(() => {});
  }, [selectedDcu, loadRoutes]);

  // The DEVICE Meter Parameter Table is the SOURCE OF TRUTH for what's
  // registered on the DCU — and after a Clear it is the ONLY thing that tells
  // an operator whether the clear actually landed.
  //
  // The read has THREE distinct outcomes and they must not be conflated (this
  // conflation hid a real 5-hour outage on 129205035235, 2026-07-25: a Clear
  // All emptied the table, the device read correctly returned 0 rows, the UI
  // fell back to the routing map's 83 stale entries and displayed them as
  // "Registered", so the wipe was invisible AND the meters could not be
  // re-approved from the Black List because they looked registered):
  //   * 'ok'      — value parsed (INCLUDING an empty "[]"): the device is
  //                 authoritative. Zero rows means zero rows. NO map fallback.
  //   * 'failed'  — backend returned "None": the corroborated read could not be
  //                 trusted. We know nothing; map rows may be shown but are
  //                 labelled UNCONFIRMED, never "Registered".
  //   * 'unread'  — not read this session (opening a DCU never contacts it).
  //                 Same treatment as 'failed': unconfirmed, not registered.
  const paramReadState = React.useMemo(() => {
    if (!paramTable) return 'unread';
    // The STRUCTURED shape (a bare array, or {value: array}) is a confirmed
    // table — it only ever comes from a verified read-back or the shared
    // snapshot. Testing `.value == null` alone called a bare array 'unread',
    // and the {value: array} form 'ok' with zero parsable rows; between them
    // every registered meter could be reported either unconfirmed or "Not on
    // DCU" while the device actually held the lot.
    if (Array.isArray(paramTable)) return paramTable.length ? 'ok' : 'unread';
    if (Array.isArray(paramTable.value)) {
      return paramTable.value.length ? 'ok' : 'unread';
    }
    if (paramTable.value == null) return 'unread';
    if (paramTable.value === 'None') return 'failed';
    return 'ok';
  }, [paramTable]);

  // Live session state beats the snapshot-cached /api/dcus list value.
  const liveOnline = dcuStatus?.session_open ?? dcuOnline;

  // AUTO-REFRESH after a device write finishes. Upload / Clear / Repair all run
  // as background jobs, so the table on screen kept showing the PRE-write state
  // until the operator manually hit Refresh — leaving them guessing whether the
  // meters were actually registered or removed. Watch the background-jobs feed
  // and, when a job for the DCU being viewed reaches a terminal state, re-read
  // the parameter table and the staged set so the view reflects the device.
  const _seenDone = React.useRef(new Set());
  // Refs, not direct calls: reloadMeterTables/loadPending are defined further
  // down, and a ref also avoids re-subscribing on every render.
  const reloadMeterTablesRef = React.useRef(null);
  const loadPendingRef = React.useRef(null);
  React.useEffect(() => bgJobs.subscribe((list) => {
    const mine = (list || []).filter(j =>
      String(j.dcu) === String(selectedDcu) &&
      (j.state === 'done' || j.state === 'failed'));
    const fresh = mine.filter(j => !_seenDone.current.has(j.id));
    if (!fresh.length) return;
    fresh.forEach(j => _seenDone.current.add(j.id));
    // Small delay: the job marks itself done immediately after its read-back,
    // and the DCU can still be settling. This is a read, so it is safe to retry.
    setTimeout(() => {
      if (curDcuRef.current !== selectedDcu) return;
      reloadMeterTablesRef.current && reloadMeterTablesRef.current();
      loadPendingRef.current && loadPendingRef.current();
    }, 1500);
  }), [selectedDcu]);

  // Staged (not-yet-uploaded) changes for this DCU. Local only — no device I/O,
  // so this works while the DCU is offline, which is the point of staging.
  const [pending, setPending] = React.useState({ add: [], remove: [] });
  const loadPending = React.useCallback(() => {
    if (!selectedDcu) { setPending({ add: [], remove: [] }); return Promise.resolve(); }
    const forDcu = selectedDcu;
    return api(`/dcus/${forDcu}/pending-changes`)
      .then(d => { if (curDcuRef.current === forDcu) setPending(d?.pending || { add: [], remove: [] }); })
      .catch(() => {});
  }, [selectedDcu]);
  React.useEffect(() => { loadPending(); }, [loadPending]);

  // LOCAL-ONLY refresh — the routing map plus the staged-changes list, both
  // served with no device I/O. This is what a STAGE needs.
  //
  // reloadMeterTables() is the wrong tool here and was the reason operators
  // had to hit Refresh by hand: it fires a live parameter-table read that
  // takes 34-240 s on a contended DCU (a DCU serves one task at a time, so it
  // queues behind collection) — and then comes back WITHOUT the new rows,
  // because staging deliberately writes nothing to the device. The meters
  // exist in our system the moment the POST returns; they show as "Pending
  // upload" until Upload to DCU actually writes them.
  const reloadLocalTables = React.useCallback(() => {
    if (!selectedDcu) return;
    const forDcu = selectedDcu;
    api(`/dcus/${forDcu}/meters?source=routes`)
      .then(r => { if (curDcuRef.current === forDcu) setRoutedMeters(r?.meters || []); })
      .catch(() => {});
    loadPending();
  }, [selectedDcu, loadPending]);

  const meters = React.useMemo(() => {
    const bySerial = new Map();
    for (const r of routedMeters) {
      const ldn = r.ldn || r.meter_serial;
      if (!ldn) continue;
      // meterSerial is carried separately from ldn: a permanent delete targets
      // the DATABASE record by serial, and on some rows the LDN is a different
      // string. Falls back to the LDN when the map has no serial for the row.
      bySerial.set(ldn, {
        sap_id: r.sap_id ?? '—', ldn, commType: r.comm_type, source: 'routes',
        meterSerial: r.meter_serial || null,
      });
    }
    if (paramReadState === 'ok') {
      try {
        const matches = _paramPairs(paramTable).map(p => [null, String(p.sap_id), p.ldn]);
        for (const m of matches) {
          const prev = bySerial.get(m[2]) || {};
          bySerial.set(m[2], { ...prev, sap_id: parseInt(m[1]), ldn: m[2], source: 'dcu' });
        }
      } catch { /* treated as an unusable read below */ }
      // Device truth ANNOTATES each row; it does not delete rows. Registered
      // meters live in our database, so the table must always show them —
      // this list is the operator's inventory of what SHOULD be on the DCU,
      // and a meter silently vanishing from it is the worst possible way to
      // report "the device no longer has this".
      //
      // The previous rule filtered to device rows only, so an empty (or
      // falsely-empty) read blanked the whole table and 43 registered meters
      // simply disappeared from view — which is exactly how the 2026-07-30
      // Clear All went unnoticed for a day, and what operators reported as
      // "the DCU is not showing its meters".
      //
      // So: keep every row, and mark the ones the device did NOT return as
      // _missingOnDevice. That is a stronger, more actionable statement than
      // "Unconfirmed" — we DID read the device and this meter was absent —
      // and it is what tells the operator to re-add it.
      const addSet = new Set(pending.add || []);
      const remSet = new Set(pending.remove || []);
      const out = Array.from(bySerial.values())
        .map(x => ({
          ...x,
          _pendingAdd: addSet.has(x.ldn) && x.source !== 'dcu',
          _pendingRemove: remSet.has(x.ldn),
          _missingOnDevice: x.source !== 'dcu' && !addSet.has(x.ldn),
        }));
      return out.sort((a, b) => (typeof a.sap_id === 'number' ? a.sap_id : 99999)
                              - (typeof b.sap_id === 'number' ? b.sap_id : 99999));
    }
    // Read failed / not attempted: show what the map knows, rendered as
    // UNCONFIRMED so nobody mistakes them for device state — but still flag
    // STAGED rows, otherwise a meter just added from the Black List looks
    // identical to an unverified map row and the operator cannot see there is
    // something to upload.
    const addSet2 = new Set(pending.add || []);
    const remSet2 = new Set(pending.remove || []);
    return Array.from(bySerial.values()).map(x => ({
      ...x,
      _pendingAdd: addSet2.has(x.ldn),
      _pendingRemove: remSet2.has(x.ldn),
    })).sort(
      (a, b) => (typeof a.sap_id === 'number' ? a.sap_id : 99999)
              - (typeof b.sap_id === 'number' ? b.sap_id : 99999));
  }, [paramTable, routedMeters, paramReadState, pending]);

  reloadMeterTablesRef.current = reloadMeterTables;
  loadPendingRef.current = loadPending;

  const loadBlacklist = React.useCallback(() => {
    if (!selectedDcu) return;
    setBlacklistLoading(true);
    api(`/meters/${selectedDcu}/dcu-blacklist`)
      .then((d) => {
        setBlacklist(d);
        setBlacklistTs(d.read_at ? new Date(d.read_at * 1000) : new Date());
        // The DCU was offline/stale and we served the persisted copy instead.
        if (d.detail) toast.push(d.detail, 'info');
      })
      .catch((err) => toast.push(`Blacklist read failed: ${err.message}`, 'error'))
      .finally(() => setBlacklistLoading(false));
  }, [selectedDcu, toast]);

  // Re-read the PERSISTED blacklist (no DCU I/O) so hide/restore changes show
  // instantly with fresh `hidden` flags. Used after Hide/Restore actions.
  const refreshBlacklistCached = React.useCallback(() => {
    if (!selectedDcu) return Promise.resolve();
    const forDcu = selectedDcu;
    return api(`/meters/${forDcu}/dcu-blacklist?cached=1`)
      .then((d) => {
        if (curDcuRef.current === forDcu && d && d.read_at) {
          setBlacklist(d);
          setBlacklistTs(new Date(d.read_at * 1000));
        }
      })
      .catch(() => {});
  }, [selectedDcu]);

  const handleRegister = async (form) => {
    setRegLoading(true);
    try {
      const params = new URLSearchParams({
        meter_serial: form.serial,
        sap_id: form.sapId || '2',
        comm_type: form.commType || '1',
        hdlc_addr: form.address || '0',
        client_addr: form.clientAddr || '17',
        app_context: '1',
        auth_mech: '1',
      });
      // Operator-picked CT/PT and device-type flow through to the
      // backend, which upserts them onto MeterAssignment so the
      // dashboard CT/VT banner and reports reflect this meter's
      // configuration immediately. Skip when the operator left a
      // field blank — the backend treats omitted params as "no
      // change".
      if (form.ctRatio) params.set('ct_ratio', form.ctRatio);
      if (form.ptRatio) params.set('vt_ratio', form.ptRatio);
      if (form.meterType !== '' && form.meterType != null) {
        const code = METER_TYPE_FORM_TO_CAS[String(form.meterType)];
        if (code) params.set('meter_type_override', String(code));
      }
      if (form.fractionDigits !== '' && form.fractionDigits != null) {
        params.set('fraction_digits', String(form.fractionDigits));
      }
      params.set('queue', 'true');
      const r = await api(`/meters/${selectedDcu}/register-meter?${params}`, { method: 'POST' });
      setRegDialog(null);
      // Queued on the DCU's worker: refresh the param table once it has run.
      const dcu = selectedDcu;
      if (r && r.job_id) {
        bgJobs.add({ id: r.job_id, dcu: String(dcu), op: `Register ${form.serial}`,
          onDone: () => {
            api(`/meters/${dcu}/read-raw/0.0.21.0.1.255?attribute=2&class_id=1`).then(setParamTable).catch(() => {});
          } });
      }
    } catch (e) {
      alert('Registration failed: ' + (e.message || e));
    }
    setRegLoading(false);
  };

  const sectionBtn = (key, label) => ({
    padding: '6px 14px', fontSize: '12px', fontWeight: activeSection === key ? 600 : 400,
    background: activeSection === key ? '#1e40af' : 'transparent',
    color: activeSection === key ? '#fff' : c.textMuted,
    border: `1px solid ${activeSection === key ? '#1e40af' : c.border}`,
    borderRadius: '6px', cursor: 'pointer', marginRight: '6px',
  });

  if (!selectedDcu) return <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '20px', color: c.textMuted, fontStyle: 'italic' }}>Select a DCU from the list</div>;

  return (
    <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px' }}>
      {/* Registration Dialog */}
      {regDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '24px', width: '520px', maxWidth: '90vw' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Register Meter to DCU {selectedDcu}</div>
            <DcuMeterRegForm initial={regDialog} onSubmit={handleRegister} onCancel={() => setRegDialog(null)} loading={regLoading} />
          </div>
        </div>
      )}

      {/* flexWrap on BOTH rows: "🔄 Rescan Parameter Table" and "+ Register
          Meter" side by side need ~330px, which does not fit beside the title
          on a phone. Without wrapping they were clipped at the screen edge
          with no way to reach them (reported from a phone 2026-09-03). */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        <div style={{ fontSize: '16px', fontWeight: 600, wordBreak: 'break-all' }}>DCU {selectedDcu}</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={async () => {
            try {
              await api(`/dcus/${selectedDcu}/rescan-parameter-table`, { method: 'POST' });
              alert('Rescan queued — will run within 30s. The Registered Meters list will refresh automatically.');
              // Re-read our cached copy after a short delay so any
              // newly-discovered meters appear in the UI.
              setTimeout(() => {
                api(`/meters/${selectedDcu}/read-raw/0.0.21.0.1.255?attribute=2&class_id=1`)
                  .then(setParamTable).catch(() => {});
              }, 35000);
            } catch (e) {
              alert(`Rescan failed: ${e?.message || e}`);
            }
          }}
            title="Re-read the DCU Meter Parameter Table on demand. After 24h of continuous operation the server no longer polls this table automatically — click to force a refresh if you suspect the DCU's registered meter list has changed."
            style={{ padding: '6px 14px', fontSize: '12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
            🔄 Rescan Parameter Table
          </button>
          <button onClick={() => setRegDialog({ serial: '', address: '', sapId: '2', commType: '1', clientAddr: '17', password: '00000000', ctRatio: '5/5', ptRatio: '220/220' })}
            style={{ padding: '6px 14px', fontSize: '12px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
            + Register Meter
          </button>
        </div>
      </div>

      {/* DCU summary header (vendor-style counts) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'center', padding: '8px 14px', marginBottom: '12px', background: c.bgSurface || c.bgElevated || c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}>
        <span style={{ color: '#22c55e' }}>DCU No: <span style={{ fontFamily: 'monospace' }}>{selectedDcu}</span></span>
        {/* The count must carry the same confidence as the rows below it. When
            the device has not been (or could not be) read, this is our routing
            map's number, not the DCU's — showing it in confident green was the
            last place the UI still implied a device fact it had not verified. */}
        <span style={{ color: paramReadState === 'ok' ? '#22c55e' : c.textMuted }}
          title={paramReadState === 'ok'
            ? "Confirmed by a live read of the DCU's parameter table."
            : "From our routing map — NOT confirmed on the device. Click Refresh in Meters Setting to read the DCU."}>
          Total Meter Count: {meters.length}
          {paramReadState === 'ok' && paramReadAt && (
            <span style={{ fontWeight: 400, color: c.textMuted }}>
              {' '}· read {_ageLabel(paramReadAt)}
            </span>
          )}
          {paramReadState !== 'ok' && <span style={{ fontWeight: 400 }}> (unconfirmed)</span>}
        </span>
        {/* Vendor header parity: MDC shows Total / Downloaded / Online side by
            side, and the third number is the useful one — 253 registered but
            only 233 reachable is a fact you want without opening a tab.
            We print the per-status-code counts too, because until
            DCU_ONLINE_STATUS is proven those counts ARE the evidence that
            settles which code means online (compare against the vendor's 233). */}
        {onlineSummary && (
          <span style={{ color: c.textLabel }}>
            {onlineSummary.online != null ? (
              <span style={{ color: '#22c55e' }}>Online Meter Count: {onlineSummary.online}</span>
            ) : (
              <span title="get_online_status returned this many rows. Which of them are ONLINE is not yet decidable — the per-code counts that follow are what settles it.">
                DCU-reported: {onlineSummary.total}
              </span>
            )}
            {onlineSummary.buckets.map(b => (
              <span key={String(b.code)} style={{ fontWeight: 400, color: c.textMuted }}
                title={b.known ? undefined : `${b.n} of ${onlineSummary.total} rows carry status code ${b.code}. If this count matches the vendor tool's Online Meter Count for the same DCU, this is the online code.`}>
                {' · '}{b.label}: {b.n}
              </span>
            ))}
            {/* The correlation that would settle DCU_ONLINE_STATUS. Shown,
                never auto-applied: a mapping this load-bearing should be a
                person's call, on a DCU whose meters are known to be alive. */}
            {onlineSummary.codeEvidence && onlineSummary.codeEvidence.length > 1 && (
              <span style={{ fontWeight: 400, color: c.textLabel }}
                title="For each status code, how many of the meters carrying it are online by LINK QUALITY (node_status / frame counts). One code near 100% and the other near 0% identifies the online code.">
                {' · '}code vs link quality:{' '}
                {onlineSummary.codeEvidence.map(e =>
                  `${e.code}=${e.matched ? Math.round(e.online / e.matched * 100) : 0}% (${e.online}/${e.matched})`
                ).join(', ')}
              </span>
            )}
            <span style={{ fontWeight: 400, color: c.textMuted }}>
              {onlineSummary.onlineSource ? ` · by ${onlineSummary.onlineSource}` : ''}
              {' · '}read {_ageLabel(onlineSummary.ts.getTime())}
            </span>
          </span>
        )}
        {/* Prefer the LIVE per-DCU status over the cached /api/dcus list value.
            The list is snapshot-cached, so after a session drops it kept
            reporting "Online" while reads were already failing with "no secure
            session" — the operator saw Online next to an unreadable table. */}
        <span style={{ color: liveOnline ? '#22c55e' : '#ef4444' }}>{liveOnline ? '● Online' : '○ Offline'}</span>
        {metersReloading && (
          <span style={{ color: c.textMuted, fontWeight: 400 }}>
            {dcuBusy?.busy
              ? `refreshing… queued behind collection (${
                  dcuBusy.running.map(r => r.op).join(', ')})`
              : 'refreshing…'}
          </span>
        )}
        <span style={{ marginLeft: 'auto' }}><DcuPlcScanToggle selectedDcu={selectedDcu} /></span>
      </div>

      {/* Section tabs (vendor layout) */}
      <div style={{ marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
        <button style={sectionBtn('meters')} onClick={() => setActiveSection('meters')}>Meters Setting</button>
        <button style={sectionBtn('commq')} onClick={() => setActiveSection('commq')}>Communication Quality</button>
        <button style={sectionBtn('blacklist')} onClick={() => setActiveSection('blacklist')}>DCU Black List</button>
        <button style={sectionBtn('topology')} onClick={() => setActiveSection('topology')}>Topology</button>
        <button style={sectionBtn('data')} onClick={() => setActiveSection('data')}>Collected Data</button>
        <button style={sectionBtn('status')} onClick={() => setActiveSection('status')}>Status</button>
        {/* Network Config + Restart live ONLY here now (moved out of the
            customer tree). Admin-only, kill-switch-gated. */}
        {isAdmin && <button style={sectionBtn('network')} onClick={() => setActiveSection('network')}>🌐 Network Config</button>}
        {isAdmin && <button style={sectionBtn('restart')} onClick={() => setActiveSection('restart')}>🔄 Restart</button>}
      </div>

      {/* Status section */}
      {activeSection === 'status' && dcuStatus && dcuStatus.connected === false && (
        <DcuOfflineNotice status={dcuStatus} />
      )}
      {/* `!== false`, not `=== true`: an older server build omits the
          `connected` field entirely, and that must still render the
          normal panel rather than silently blanking it. */}
      {activeSection === 'status' && dcuStatus && dcuStatus.connected !== false && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', color: c.textMuted }}>
              {dcuStatus.cached_at ? `Cached at ${fmtDT(dcuStatus.cached_at, { utc: true, seconds: true })}` : 'Live data'}
            </div>
            <button className="button button-sm button-secondary" onClick={async () => {
              try { await api(`/dcus/${selectedDcu}/refresh-status`, { method: 'POST' }); } catch {}
              api(`/dcus/${selectedDcu}/status`).then(onStatusRefreshed).catch(() => {});
            }}>Refresh Status</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '14px' }}>
            <div title={dcuSignalInfo(dcuStatus).title}>
              <KpiTile label="Signal" value={dcuSignalInfo(dcuStatus).short} color={dcuSignalInfo(dcuStatus).color} />
            </div>
            <KpiTile label="Downstream" value={dcuStatus.downstream_meter_count ?? 0} color="#00e5ff" />
            <KpiTile label="Session" value={dcuStatus.session_open ? 'Open' : 'Closed'} color={dcuStatus.session_open ? '#22c55e' : '#ef4444'} />
            <KpiTile label="Profile Data" value={effProfile ? (effProfile.has_data ? 'Yes' : 'Empty') : 'Reading…'} color={effProfile ? (effProfile.has_data ? '#22c55e' : '#f59e0b') : '#64748b'} />
          </div>
          <DetailGrid items={[
            ['Storage', dcuStatus.flash_usage ? (() => {
              try {
                const m = dcuStatus.flash_usage.match(/(\d+).*?(\d+)/);
                if (m) {
                  const free = (parseInt(m[1]) / 1048576).toFixed(0);
                  const total = (parseInt(m[2]) / 1048576).toFixed(0);
                  const used = total - free;
                  const pct = ((used / total) * 100).toFixed(1);
                  return `${used} MB / ${total} MB used (${pct}%) — ${free} MB free`;
                }
              } catch {}
              return dcuStatus.flash_usage;
            })() : '—'],
            ['Status', dcuStatus.status_word != null ? (() => {
              const w = dcuStatus.status_word;
              const flags = [];
              if (w & 0x01) flags.push('Power up');
              if (w & 0x02) flags.push('GPRS modem failure');
              if (w & 0x04) flags.push('GPRS signal low');
              if (w & 0x08) flags.push('PLC failure');
              if (w & 0x10) flags.push('RF failure');
              if (w & 0x20) flags.push('Flash full');
              if (w & 0x80) flags.push('DCU unlock');
              if (w & 0x200) flags.push('RS-485 failure');
              if (w & 0x800) flags.push('Clock doubtful');
              return flags.join(', ') || 'OK';
            })() : '—'],
          ]} />
        </div>
      )}

      {/* Network Config — moved here from the customer tree. Admin-only. */}
      {activeSection === 'network' && isAdmin && (
        <MeterDetailNetworkTab
          serial={selectedDcu}
          sessionOpen={!!(dcuStatus?.session_open ?? dcuOnline)}
          assignment={{ meter_serial: selectedDcu, dcu_serial: selectedDcu }}
          live={{ serial: selectedDcu, session_open: !!(dcuStatus?.session_open ?? dcuOnline), device_type: 'dcu' }}
        />
      )}
      {/* Restart — moved here from the customer tree. Admin-only. */}
      {activeSection === 'restart' && isAdmin && (
        (dcuStatus?.session_open ?? dcuOnline)
          ? <DcuRestartCard dcuSerial={selectedDcu} dcuMeter={{ serial: selectedDcu, session_open: true, device_type: 'dcu' }} />
          : <div style={{ padding: '20px', color: c.textMuted, fontStyle: 'italic', background: c.bgCardAlt, border: `1px dashed ${c.border}`, borderRadius: '8px' }}>
              DCU session is not open — restart is unavailable until the DCU is connected.
            </div>
      )}

      {/* Meters Setting section (vendor-style table + supported actions) */}
      {activeSection === 'meters' && (
        <MetersSettingSection
          paramTable={paramTable}
          meters={meters}
          selectedDcu={selectedDcu}
          reloading={metersReloading}
          onOnlineRead={setOnlineSummary}
          onParamConfirmed={adoptParamTable}
          onReload={reloadMeterTables}
          onChanged={reloadMeterTables}
          regDcuFor={regDcuFor}
          paramReadState={paramReadState}
          pending={pending}
          onPendingChanged={() => { loadPending(); loadRoutes(); }}
        />
      )}

      {/* Communication Quality section (manual live read) */}
      {activeSection === 'commq' && (
        <DcuCommQualitySection selectedDcu={selectedDcu} knownMeters={meters} />
      )}

      {/* Blacklist section with multi-select + bulk registration */}
      {activeSection === 'blacklist' && (
        <DcuBlacklistSection
          blacklist={blacklist}
          blacklistLoading={blacklistLoading}
          blacklistTs={blacklistTs}
          onReadBlacklist={loadBlacklist}
          onRefreshCached={refreshBlacklistCached}
          paramTable={paramTable}
          routedMeters={routedMeters}
          selectedDcu={selectedDcu}
          onRegistered={() => { reloadMeterTables(); loadPending(); }}
          onStaged={reloadLocalTables}
        />
      )}

      {/* Topology (manual live read, raw until the response shape is known) */}
      {activeSection === 'topology' && (
        <DcuTopologySection selectedDcu={selectedDcu} />
      )}

      {/* Meter Archive — alive/dead per child */}
      {activeSection === 'archive' && (
        <DcuMeterArchiveSection selectedDcu={selectedDcu} />
      )}

      {/* Collected Data section */}
      {activeSection === 'data' && (
        <DcuCollectedDataSection meters={meters} selectedDcu={selectedDcu} />
      )}
    </div>
  );
}

// ─── DCU Meter Archive — alive/dead-per-child ───
//
// Calls /api/dcus/{serial}/meter-archive and renders one row per
// registered child meter, classifying each as alive / silent_24h /
// silent_7d / never / disabled based on the newest stored row in
// our profile_rows table. Operator use case: when DCU aggregate
// keeps returning 0 rows for a meter, this view distinguishes
// "PLC silent for hours" from "actually dead at the meter".
function DcuMeterArchiveSection({ selectedDcu }) {
  const c = useThemeColors();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState('all');

  // Stale-DCU guard: clear during render (not in an effect, which paints one
  // frame of the previous DCU's archive) and drop late responses for a DCU the
  // operator has already navigated away from.
  const curRef = React.useRef(selectedDcu);
  curRef.current = selectedDcu;
  const [prevDcu, setPrevDcu] = React.useState(selectedDcu);
  if (prevDcu !== selectedDcu) {
    setPrevDcu(selectedDcu);
    setData(null); setLoading(false); setFilter('all');
  }

  const load = React.useCallback(() => {
    if (!selectedDcu) return;
    const forDcu = selectedDcu;
    setLoading(true);
    api(`/dcus/${forDcu}/meter-archive`)
      .then(d => { if (curRef.current === forDcu) { setData(d); setLoading(false); } })
      .catch(() => { if (curRef.current === forDcu) setLoading(false); });
  }, [selectedDcu]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ color: c.textMuted, padding: '20px' }}>Loading meter archive…</div>;
  if (!data) return <div style={{ color: c.textMuted, padding: '20px' }}>No data — DCU may not be connected.</div>;

  const fmtAge = (sec) => {
    if (sec == null) return '—';
    if (sec < 60) return `${Math.round(sec)}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
  };
  const statusBadge = (s) => {
    const map = {
      alive:      { color: c.success, label: '● Alive (<24h)' },
      silent_24h: { color: c.warning, label: '● Silent 24h+' },
      silent_7d:  { color: c.danger,  label: '● Silent 7d+' },
      never:      { color: c.textDimmed, label: '○ Never' },
      disabled:   { color: c.textMuted, label: '⊘ Disabled' },
    };
    const e = map[s] || { color: c.textMuted, label: s };
    return <span style={{ color: e.color, fontSize: '11px', fontWeight: 600 }}>{e.label}</span>;
  };

  const summary = data.summary || {};
  // 'daily_stale' / 'daily_missing' filters apply on top of status —
  // they slice the alive cohort by daily-billing health, not replace
  // the underlying status filter.
  const meters = (data.meters || []).filter(m => {
    if (filter === 'all') return true;
    if (filter === 'daily_stale')   return m.daily_health === 'stale';
    if (filter === 'daily_missing') return m.daily_health === 'missing';
    return m.status === filter;
  });

  const fmtTs = (ts) => ts
    ? fmtDT(ts)
    : '—';
  const dailyHealthBadge = (h) => {
    if (h == null) return null;
    const map = {
      fresh:   { color: c.success, label: '✓ daily fresh'  },
      stale:   { color: c.warning, label: '⚠ daily stale'  },
      missing: { color: c.danger,  label: '✗ daily missing' },
    };
    const e = map[h]; if (!e) return null;
    return <span style={{ color: e.color, fontSize: '10px' }}>{e.label}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', color: c.textMuted }}>
          {data.total || 0} meter(s) registered to DCU {selectedDcu}
        </div>
        <button className="button button-sm button-secondary" onClick={load}>🔄 Refresh</button>
      </div>

      {/* Summary tiles + filter chips */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {[
          ['all', `All (${data.total || 0})`, c.textPrimary],
          ['alive', `● Alive ${summary.alive || 0}`, c.success],
          ['silent_24h', `● Silent 24h+ ${summary.silent_24h || 0}`, c.warning],
          ['silent_7d', `● Silent 7d+ ${summary.silent_7d || 0}`, c.danger],
          ['never', `○ Never ${summary.never || 0}`, c.textDimmed],
          ['disabled', `⊘ Disabled ${summary.disabled || 0}`, c.textMuted],
          // Daily-billing health slices — only the alive cohort.
          ['daily_stale',   `⚠ Daily stale ${summary.daily_stale || 0}`,   c.warning],
          ['daily_missing', `✗ Daily missing ${summary.daily_missing || 0}`, c.danger],
        ].map(([key, label, color]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: '6px 10px',
              fontSize: '11px',
              border: `1px solid ${filter === key ? color : c.border}`,
              background: filter === key ? `${color}22` : 'transparent',
              color: color,
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: filter === key ? 600 : 400,
            }}>
            {label}
          </button>
        ))}
      </div>

      <div className="table-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table style={{ fontSize: '11px', width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
            <tr style={{ color: c.textSecondary, textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>SAP</th>
              <th style={{ padding: '6px 8px' }}>Meter (LDN)</th>
              <th style={{ padding: '6px 8px' }}>Comm</th>
              <th style={{ padding: '6px 8px' }}>Type</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Last contact</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Age</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Last daily</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Last LP</th>
              <th style={{ padding: '6px 8px' }}>Daily health</th>
            </tr>
          </thead>
          <tbody>
            {meters.length === 0 && (
              <tr><td colSpan="10" style={{ color: c.textMuted, padding: '20px', textAlign: 'center', fontStyle: 'italic' }}>
                {filter === 'all' ? 'No meters in the parameter table.' : `No meters in ${filter} state.`}
              </td></tr>
            )}
            {meters.map((m, i) => {
              const obis = m.last_seen_by_obis || {};
              // Pick whichever LP variant is fresher (the scheduler can land
              // either 30-min or hourly depending on the meter's profile).
              const lpTs = Math.max(obis.load_profile_1h || 0, obis.load_profile_30m || 0) || null;
              return (
                <tr key={`${m.sap_id}-${m.meter_serial}-${i}`} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{m.sap_id}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{m.meter_serial}</td>
                  <td style={{ padding: '4px 8px', color: c.textMuted }}>
                    {m.communication_type === 1 ? 'RS-485' :
                     m.communication_type === 6 ? 'PLC' :
                     m.communication_type === 7 ? 'RF' :
                     `type ${m.communication_type ?? '?'}`}
                  </td>
                  <td style={{ padding: '4px 8px', color: c.textMuted }}>
                    {m.dev_type === 0 ? '1P' : m.dev_type === 1 ? '3P' : m.dev_type === 4 ? '3P-CT' : `dt ${m.dev_type ?? '?'}`}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: c.textPrimary }}>
                    {m.last_seen_ts
                      ? fmtDT(m.last_seen_ts, { seconds: true })
                      : '—'}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: c.textMuted }}>
                    {fmtAge(m.last_seen_age_seconds)}
                  </td>
                  <td style={{ padding: '4px 8px' }}>{statusBadge(m.status)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: c.textMuted, fontFamily: 'monospace' }}>
                    {fmtTs(obis.daily_billing)}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: c.textMuted, fontFamily: 'monospace' }}>
                    {fmtTs(lpTs)}
                  </td>
                  <td style={{ padding: '4px 8px' }}>{dailyHealthBadge(m.daily_health)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Meter optimisation ────────────────────────────────────────────────────
//
// A meter only reports if its DCU has it registered AND can hear it on the
// wire. Those are set by different people at different times, so on a site
// with two DCUs on adjacent LV segments an install can register a meter on
// the one it is NOT wired near. It then never delivers a reading and nothing
// in the UI says why — the DCU is online, the parameter table is correct, the
// meter is fine.
//
// The DCU that CAN hear it says so, in its blacklist. This panel surfaces
// those, and turns the fix into a move through the ordinary verified path.
// Deliberately collapsed by default: it is a periodic maintenance action, not
// something an operator needs shouting at them on every visit to this tab.
function MeterOptimisationSection() {
  const c = useThemeColors();
  const toast = useToast();
  const [open, setOpen] = React.useState(false);
  const [plan, setPlan] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [silentDays, setSilentDays] = React.useState(7);
  const [minIntercepts, setMinIntercepts] = React.useState(10);
  const [picked, setPicked] = React.useState({});      // "from>to" -> true
  const [expanded, setExpanded] = React.useState({});
  const [confirm, setConfirm] = React.useState('');
  const [job, setJob] = React.useState(null);

  const key = p => `${p.from_dcu}>${p.to_dcu}`;

  // `force` is the operator pressing Rescan. Everything else — opening the
  // panel, reloading the page — takes the persisted plan, because the scan is
  // a fleet-wide GROUP BY over 11.1M profile rows and its inputs move once a
  // day. Changing a threshold does rescan, but the server decides that: the
  // cached plan simply does not match the new thresholds.
  const scan = React.useCallback(async (force) => {
    setBusy(true);
    try {
      const p = await api(`/dcu-optimisation?silent_days=${silentDays}`
        + `&min_intercepts=${minIntercepts}${force ? '&refresh=true' : ''}`);
      setPlan(p); setPicked({});
    } catch (e) {
      // The bundle is a static file and goes live the moment it is synced,
      // while the routes it calls only exist after the service restarts. In
      // that window this is not a fault and must not be reported as one.
      const msg = String(e.message || e);
      if (/not found/i.test(msg) || /404/.test(msg)) {
        setPlan({ pending_restart: true, pairs: [], totals: null, excluded_counts: {} });
      } else {
        toast.push(`Scan failed: ${msg}`, 'error');
      }
    }
    finally { setBusy(false); }
  }, [silentDays, minIntercepts, toast]);

  // Fetch once when the panel is first opened — never on mount, and never
  // forced, so this is a read of the persisted plan rather than a rescan.
  React.useEffect(() => { if (open && !plan && !busy) scan(false); }, [open]);

  // Follow a run to completion. Polling, not a socket: the job outlives the
  // request by design, so a reload has to be able to re-attach anyway.
  React.useEffect(() => {
    if (!job || job.state !== 'running') return;
    const t = setInterval(async () => {
      try {
        const j = await api(`/dcu-optimisation/runs/${job.job_id}`);
        setJob(j);
        if (j.state !== 'running') {
          const ok = j.totals.moved, bad = j.totals.failed_pairs;
          toast.push(`Optimisation done — ${ok} meter(s) moved`
            + (bad ? `, ${bad} pair(s) failed` : ''), bad ? 'error' : 'success');
          scan(true);   // the parameter tables just changed; the cache is stale
        }
      } catch { /* keep polling; a blip must not orphan the view */ }
    }, 4000);
    return () => clearInterval(t);
  }, [job, scan, toast]);

  const chosen = React.useMemo(
    () => (plan?.pairs || []).filter(p => picked[key(p)]), [plan, picked]);
  const chosenMeters = chosen.reduce((n, p) => n + p.meters.length, 0);
  const needsConfirm = chosen.length > 1;

  const run = React.useCallback(async () => {
    if (!chosen.length) return;
    if (!window.confirm(
      `Move ${chosenMeters} meter(s) across ${chosen.length} DCU pair(s)?\n\n`
      + chosen.map(p => `  ${p.from_dcu} → ${p.to_dcu}  (${p.meters.length})`).join('\n')
      + `\n\nEach pair is a verified deregister-then-register, run one at a time. `
      + `Meters that started collecting since this scan are dropped automatically.`)) return;
    setBusy(true);
    try {
      const j = await api('/dcu-optimisation/run', {
        method: 'POST',
        body: JSON.stringify({
          pairs: chosen.map(p => ({ from_dcu: p.from_dcu, to_dcu: p.to_dcu,
                                    meters: p.meters.map(m => m.meter) })),
          confirm, silent_days: silentDays, min_intercepts: minIntercepts,
        }),
      });
      setJob(j); setConfirm('');
      toast.push(`Optimisation started — job ${j.job_id}`, 'success');
    } catch (e) { toast.push(`Could not start: ${e.message || e}`, 'error'); }
    finally { setBusy(false); }
  }, [chosen, chosenMeters, confirm, silentDays, minIntercepts, toast]);

  const t = plan?.totals;
  const inputSt = {
    width: '64px', padding: '4px 6px', fontSize: '12px', background: c.bgInput,
    color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px',
  };

  return (
    <div style={{ background: c.bgCard, border: `1px solid ${c.border}`,
                  borderRadius: '8px', marginBottom: '14px' }}>
      <div onClick={() => setOpen(o => !o)}
           style={{ display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', cursor: 'pointer' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
          {open ? '▾' : '▸'} 🔧 Meter Optimisation
        </span>
        {t && t.meters > 0 && (
          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                         borderRadius: '10px', background: '#f59e0b', color: '#fff' }}>
            {t.meters} meter{t.meters === 1 ? '' : 's'} on a DCU that cannot hear them
          </span>
        )}
        {t && t.meters === 0 && (
          <span style={{ fontSize: '11px', color: '#16a34a' }}>nothing to optimise</span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '10px', color: c.textMuted }}>
          registered on one DCU, heard by another, not collecting
        </span>
      </div>

      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                        flexWrap: 'wrap', marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', color: c.textMuted }}>
              silent at least{' '}
              <input type="number" min="0" max="365" value={silentDays} style={inputSt}
                     onChange={e => setSilentDays(Number(e.target.value))} /> days
            </label>
            <label style={{ fontSize: '11px', color: c.textMuted }}>
              heard at least{' '}
              <input type="number" min="0" value={minIntercepts} style={inputSt}
                     onChange={e => setMinIntercepts(Number(e.target.value))} /> times
            </label>
            <button className="button button-sm button-secondary"
                    title="Re-read the blacklists and collection history now. Otherwise this refreshes automatically once a day, after the nightly blacklist scan."
                    onClick={() => scan(true)} disabled={busy}>
              {busy ? <Spinner /> : '↻ Rescan'}
            </button>
            {plan && (
              <span style={{ fontSize: '10px', color: c.textMuted }}>
                {/* The age, not just the time. A plan is a snapshot of
                    yesterday's collection against the last blacklist read, and
                    showing a bare clock time invites reading it as live. */}
                scanned {(() => {
                  const mins = Math.max(0, (Date.now() / 1000 - plan.generated_at) / 60);
                  if (mins < 1) return 'just now';
                  if (mins < 60) return `${Math.round(mins)} min ago`;
                  if (mins < 60 * 36) return `${Math.round(mins / 60)}h ago`;
                  return `${Math.round(mins / 1440)}d ago`;
                })()}
                {plan.cached ? '' : ' (fresh)'}
                {' · '}excluded: {Object.entries(plan.excluded_counts || {})
                  .filter(([, n]) => n > 0)
                  .map(([k2, n]) => `${n} ${k2.replace(/_/g, ' ')}`).join(', ') || 'none'}
              </span>
            )}
          </div>

          {job && (
            <div style={{ padding: '8px 10px', marginBottom: '10px', borderRadius: '6px',
                          background: job.state === 'running' ? '#2563eb1a' : '#16a34a1a',
                          border: `1px solid ${job.state === 'running' ? '#2563eb' : '#16a34a'}` }}>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>
                Job {job.job_id} — {job.state} · moved {job.totals.moved}/{job.requested}
                {job.totals.failed_pairs > 0 && ` · ${job.totals.failed_pairs} pair(s) failed`}
                {job.skipped?.length > 0 && ` · ${job.skipped.length} dropped (no longer candidates)`}
              </div>
              {job.pairs.map(p => (
                <div key={`${p.from_dcu}>${p.to_dcu}`} style={{ fontSize: '11px', color: c.textMuted }}>
                  {p.from_dcu} → {p.to_dcu}: {p.state}
                  {p.state === 'ok' || p.state === 'partial' ? ` (${p.moved} moved)` : ''}
                  {p.error ? ` — ${p.error}` : ''}
                </div>
              ))}
            </div>
          )}

          {plan?.pending_restart && (
            <div style={{ fontSize: '12px', color: c.textMuted, fontStyle: 'italic', padding: '8px' }}>
              Meter optimisation is deployed but not active yet — the server needs a
              restart to pick up the new endpoints. Nothing is wrong with the DCUs.
            </div>
          )}

          {plan && !plan.pending_restart && plan.pairs.length === 0 && (
            <div style={{ fontSize: '12px', color: c.textMuted, fontStyle: 'italic', padding: '8px' }}>
              No meter qualifies at these thresholds. Every cross-heard meter is either
              collecting normally, heard too faintly to be worth moving, or heard by its
              own DCU too (which is a re-registration problem, not a placement one).
            </div>
          )}

          {(plan?.pairs || []).map(p => {
            const k = key(p);
            const ex = expanded[k];
            return (
              <div key={k} style={{ border: `1px solid ${c.border}`, borderRadius: '6px',
                                    marginBottom: '6px', padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={!!picked[k]}
                         onChange={e => setPicked(s => ({ ...s, [k]: e.target.checked }))} />
                  <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600 }}>
                    {p.from_dcu} → {p.to_dcu}
                  </span>
                  <span style={{ fontSize: '11px', color: c.textMuted }}>
                    {p.meters.length} meter{p.meters.length === 1 ? '' : 's'}
                    {p.never_collected > 0 && ` · ${p.never_collected} never collected`}
                  </span>
                  {p.mutual_with && (
                    <span title={`${p.mutual_with} also qualifies — these two DCUs hold each other's meters. Both directions run one after the other, never at once.`}
                          style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px',
                                   borderRadius: '8px', background: '#7c3aed', color: '#fff' }}>
                      ⇄ swap
                    </span>
                  )}
                  <span style={{ fontSize: '10px', color: c.textMuted }}>{p.site}</span>
                  <span style={{ flex: 1 }} />
                  <button className="button button-sm button-secondary"
                          onClick={() => setExpanded(s => ({ ...s, [k]: !ex }))}>
                    {ex ? 'Hide' : 'Meters'}
                  </button>
                </div>
                {ex && (
                  <table style={{ width: '100%', marginTop: '8px', fontSize: '11px',
                                  borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ color: c.textMuted, textAlign: 'left' }}>
                        <th>Meter</th><th>Name</th><th>Heard by target</th><th>Silent for</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.meters.map(m => (
                        <tr key={m.meter} style={{ borderTop: `1px solid ${c.border}` }}>
                          <td style={{ fontFamily: 'monospace' }}>{m.meter}</td>
                          <td style={{ color: c.textMuted }}>{m.name || '—'}</td>
                          <td>{m.intercepts}x</td>
                          <td style={{ color: m.silent_days == null ? '#ef4444' : c.textPrimary }}>
                            {m.silent_days == null ? 'never collected'
                              : `${Math.round(m.silent_days)} days`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}

          {chosen.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
                          marginTop: '10px', flexWrap: 'wrap' }}>
              {needsConfirm && (
                <input placeholder="type OPTIMISE to run more than one pair"
                       value={confirm} onChange={e => setConfirm(e.target.value)}
                       style={{ ...inputSt, width: '260px' }} />
              )}
              <button className="button button-sm"
                      disabled={busy || (job && job.state === 'running')
                                || (needsConfirm && confirm.trim().toUpperCase() !== 'OPTIMISE')}
                      onClick={run}>
                Run selected ({chosen.length} pair{chosen.length === 1 ? '' : 's'}, {chosenMeters} meters)
              </button>
              <span style={{ fontSize: '10px', color: c.textMuted }}>
                Pairs run one at a time. Start with a single pair and confirm collection
                begins before committing the rest.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity: every device WRITE and what became of it ────────────────────
//
// Reported 2026-09-04: "we need an activity window showing all the scheduled
// jobs so we can monitor which were completed or not."
//
// Nothing durable existed. Every job registry in the backend is in-memory by
// design (_MOVE_JOBS says so explicitly), which is right for a RUNNING job and
// useless for a finished one — after a restart there was no way to answer "did
// that move ever run?". This reads /api/activity, which is backed by a table.
//
// Reads are deliberately absent: gap-fill and aggregate-refresh alone ran
// 14,104 times in the baseline day and would bury the signal.
function ActivityPanel({ dcuSerial = null, compact = false }) {
  const c = useThemeColors();
  const toast = useToast();
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [filter, setFilter] = React.useState('all');
  // Collapsed by default. The panel used to open itself on every visit to the
  // DCU tab, pushing the DCU list below the fold on mobile for a job log that
  // is usually only wanted after an operator has just run something. The
  // header (▾/▸) toggles it. Closed also means no /api/activity fetch on tab
  // load — the effect below is gated on `open`.
  const [open, setOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams();
      if (dcuSerial) qs.set('dcu_serial', dcuSerial);
      if (filter === 'active') qs.set('active_only', 'true');
      else if (filter !== 'all') qs.set('status', filter);
      qs.set('limit', compact ? '25' : '100');
      setData(await api(`/activity?${qs}`));
    } catch (e) {
      // 404 until the server restarts with the endpoint — not a fault.
      const m = String(e.message || e);
      if (/not found/i.test(m) || /404/.test(m)) setData({ pending_restart: true, jobs: [] });
      else toast.push(`Activity load failed: ${m}`, 'error');
    } finally { setBusy(false); }
  }, [dcuSerial, filter, compact, toast]);

  React.useEffect(() => { if (open) load(); }, [open, load]);
  // Poll only while something is actually in flight — a finished list does
  // not change on its own, and this view must not become another background
  // poller on an already-busy box.
  React.useEffect(() => {
    if (!open || !data?.jobs?.some(j => !j.terminal)) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [open, data, load]);

  const ICON = { scheduled: '⏳', waiting_stable: '⏳', running: '▶', done: '✅',
                 partial: '⚠', failed: '❌', cancelled: '⊘', expired: '⌛' };
  const COLOR = { scheduled: '#f59e0b', waiting_stable: '#f59e0b', running: '#2563eb',
                  done: '#16a34a', partial: '#f59e0b', failed: '#ef4444',
                  cancelled: c.textMuted, expired: c.textMuted };

  const cancel = async (jid) => {
    if (!window.confirm(`Cancel job ${jid}?\n\nOnly a job that has NOT started writing can be cancelled.`)) return;
    try { await api(`/activity/${jid}/cancel`, { method: 'POST' }); toast.push('Cancelled', 'success'); load(); }
    catch (e) { toast.push(`${e.message || e}`, 'error'); }
  };

  const jobs = data?.jobs || [];
  const sum = data?.summary || {};
  const active = jobs.filter(j => !j.terminal).length;

  return (
    <div style={{ background: c.bgCard, border: `1px solid ${c.border}`,
                  borderRadius: '8px', marginBottom: '14px' }}>
      <div onClick={() => setOpen(o => !o)}
           style={{ display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
          {open ? '▾' : '▸'} 📋 Activity
        </span>
        {active > 0 && (
          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px',
                         borderRadius: '10px', background: '#2563eb', color: '#fff' }}>
            {active} in progress
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '10px', color: c.textMuted }}>
          device writes only — moves, provisioning, param writes
        </span>
      </div>

      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center',
                        flexWrap: 'wrap', marginBottom: '10px' }}>
            {['all', 'active', 'done', 'failed'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px',
                         cursor: 'pointer',
                         background: filter === f ? '#1e40af' : 'transparent',
                         color: filter === f ? '#fff' : c.textMuted,
                         border: `1px solid ${filter === f ? '#1e40af' : c.border}` }}>
                {f}{f === 'all' && sum ? ` (${Object.values(sum).reduce((a, b) => a + b, 0)})` : ''}
              </button>
            ))}
            <button className="button button-sm button-secondary" onClick={load} disabled={busy}>
              {busy ? <Spinner /> : '↻'}
            </button>
            {data?.stable_uptime_s && (
              <span style={{ fontSize: '10px', color: c.textMuted }}>
                a queued job runs once its DCU has held a link for{' '}
                {Math.round(data.stable_uptime_s / 60)} min
              </span>
            )}
          </div>

          {data?.pending_restart && (
            <div style={{ fontSize: '12px', color: c.textMuted, fontStyle: 'italic', padding: '8px' }}>
              Activity is deployed but not active yet — the server needs a restart.
            </div>
          )}

          {!data?.pending_restart && jobs.length === 0 && (
            <div style={{ fontSize: '12px', color: c.textMuted, fontStyle: 'italic', padding: '8px' }}>
              No device-write jobs recorded{filter !== 'all' ? ` with status "${filter}"` : ''}.
            </div>
          )}

          {jobs.map(j => (
            <div key={j.job_id}
                 style={{ border: `1px solid ${c.border}`, borderLeft: `3px solid ${COLOR[j.status] || c.border}`,
                          borderRadius: '6px', marginBottom: '6px', padding: '8px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: COLOR[j.status] }}>
                  {ICON[j.status] || '•'} {j.status.replace('_', ' ')}
                </span>
                <span style={{ fontSize: '11px', color: c.textMuted }}>{j.kind.replace(/_/g, ' ')}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                  {j.from_dcu ? `${j.from_dcu} → ${j.dcu_serial}` : j.dcu_serial}
                </span>
                <span style={{ fontSize: '11px', color: c.textMuted }}>
                  {j.meter_count} meter{j.meter_count === 1 ? '' : 's'}
                </span>
                <span style={{ flex: 1 }} />
                {!j.terminal && j.status !== 'running' && (
                  <button className="button button-sm button-secondary"
                          onClick={() => cancel(j.job_id)}>Cancel</button>
                )}
              </div>
              <div style={{ fontSize: '10px', color: c.textMuted, marginTop: '3px',
                            overflowWrap: 'anywhere' }}>
                {j.created_at && fmtDT(j.created_at, { utc: true, seconds: true })}
                {j.created_by ? ` · ${j.created_by}` : ''}
                {j.attempts > 0 ? ` · ${j.attempts} attempt${j.attempts === 1 ? '' : 's'}` : ''}
                {j.note ? ` · ${j.note}` : ''}
                {/* Live link state, so "waiting" is never mistaken for a hang. */}
                {j.link && !j.link.stable ? ` · link: ${j.link.reason}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DcuManagementTab() {
  const c = useThemeColors();
  const nav = useNav();
  const toast = useToast();
  const [dcus, setDcus] = React.useState([]);
  const [selectedDcu, setSelectedDcu] = useSessionState('apm_dcu_selected', null);
  const [dcuStatus, setDcuStatus] = React.useState(null);
  const [dcuProfile, setDcuProfile] = React.useState(null);
  const [dcuFilter, setDcuFilter] = React.useState('');
  const [dcuCustomers, setDcuCustomers] = React.useState({});   // dcu_serial -> {customer_id, customer_name}
  const [customerFilter, setCustomerFilter] = React.useState('');
  const [focusSection, setFocusSection] = React.useState(null);
  const [plcFlags, setPlcFlags] = React.useState({});           // dcu_serial -> {count, cross}

  // Master/detail on a phone, the same pattern the customer tree uses. The
  // grid below is a fixed '280px 1fr', which on a ~390px viewport leaves the
  // detail column about 90px wide — narrow enough that "DCU No: 129205034829"
  // rendered ONE CHARACTER PER LINE, and Rescan / + Register were cut off at
  // the screen edge. Reported from a phone 2026-09-03. So on mobile show one
  // pane at a time: the DCU list until one is picked, then its detail with a
  // way back. `dcuOpen` is the mobile-only "am I in the detail pane" flag —
  // selectedDcu itself must keep its value, because the detail pane and every
  // desktop layout still read it.
  const isMobile = useIsMobile();
  const [dcuOpen, setDcuOpen] = React.useState(false);
  const showDetailOnMobile = isMobile && dcuOpen;
  // Leaving mobile must never strand the user in a detail-only view.
  React.useEffect(() => { if (!isMobile) setDcuOpen(false); }, [isMobile]);
  // Open the pane at its top rather than inheriting the list's scroll offset.
  React.useEffect(() => {
    if (showDetailOnMobile) { try { window.scrollTo(0, 0); } catch (_) {} }
  }, [showDetailOnMobile, selectedDcu]);

  const loadDcus = React.useCallback(() => {
    Promise.all([
      // /dcus, NOT /meters. The meters list is built from the live connection
      // pool plus meter records, so a DCU that is merely OFFLINE — or that has
      // never dialled in at all — simply is not in it. This view then silently
      // omitted DCUs the operator has assigned: customer Discovery showed 1 of
      // its 2, Compas 2 of its 3, and 8 of 42 assigned DCUs fleet-wide were
      // invisible, including one carrying 168 meters that had gone dark.
      // /dcus returns the union of the pool, known devices and assignments,
      // each flagged with connected / never_connected.
      api('/dcus').then(m => m || []).catch(() => []),
      // Exclude DCUs already soft-deleted (removed from the system → Delete Manager).
      api('/admin/deleted-devices?type=dcu')
        .then(d => new Set((d.devices || []).map(x => x.meter_serial))).catch(() => new Set()),
    ]).then(([meters, deleted]) => {
      const dcuList = meters.filter(m => m.device_type === 'dcu' && !deleted.has(m.serial_number || m.serial));
      setDcus(dcuList);
      setSelectedDcu(prev => (prev && !deleted.has(prev))
        ? prev
        : ((dcuList[0] && (dcuList[0].serial_number || dcuList[0].serial)) || null));
    }).catch(() => {});
    api('/dcu-customers').then(setDcuCustomers).catch(() => {});
    // Per-DCU anomaly flags (from the persisted daily scan) → ⚠ in the list.
    api('/abnormal-plc-connections').then(s => {
      const m = {};
      for (const d of (s?.dcus || [])) m[d.dcu_serial] = { count: d.count || 0, cross: d.cross_count || 0 };
      setPlcFlags(m);
    }).catch(() => {});
  }, []);
  React.useEffect(() => { loadDcus(); }, [loadDcus]);

  // Remove a retired/test DCU from the system: soft-delete → Delete Manager
  // (Restore or permanently Purge there). For DCUs that won't reconnect.
  const removeDcu = React.useCallback(async (serial) => {
    if (!window.confirm(
      `Remove DCU ${serial} from the system?\n\n`
      + `It moves to the Delete Manager (Device Management → Delete Manager), where you can `
      + `Restore it or permanently Purge it. Use this for retired/test DCUs that won't reconnect.`)) return;
    try {
      await api('/admin/devices/bulk-soft-delete', { method: 'POST', body: JSON.stringify({ serials: [serial] }) });
      toast.push(`DCU ${serial} removed → Delete Manager`, 'success');
      setSelectedDcu(prev => prev === serial ? null : prev);
      loadDcus();
    } catch (e) {
      toast.push(`Remove failed: ${e.message || e}`, 'error');
    }
  }, [toast, loadDcus, setSelectedDcu]);

  // Deep-link from the dashboard "Abnormal PLC" box → select that DCU + section.
  React.useEffect(() => {
    if (nav.dcuFocus?.dcu) {
      setSelectedDcu(nav.dcuFocus.dcu);
      setFocusSection(nav.dcuFocus.section || null);
      nav.setDcuFocus(null);
    }
    // eslint-disable-next-line
  }, [nav.dcuFocus]);

  React.useEffect(() => {
    if (!selectedDcu) return;
    // Stale-response guard: clear + ignore a late status/profile reply for a
    // DCU we've already switched away from, so the header's Downstream tile /
    // status word never shows the previous DCU's numbers (see DcuDetailPane).
    let stale = false;
    const forDcu = selectedDcu;
    setDcuStatus(null);
    setDcuProfile(null);
    api(`/dcus/${forDcu}/status`).then(s => { if (!stale) setDcuStatus(s); }).catch(() => {});
    // NOTE: /profiles/daily is deliberately NOT fetched here. It is a LIVE
    // over-the-air DLMS read of the DCU's on-device aggregate profile
    // (0.0.99.2.18.255) — measured 2026-07-24 at 1.5 s on one DCU and 47 s on
    // another — and firing it on every DCU click both made opening a DCU feel
    // laggy and held the DCU's session against collection. It also broke this
    // pane's rule that opening a DCU never contacts the device. Its only
    // consumer is the "Profile Data" tile in the Status sub-tab (and that
    // profile is empty fleet-wide), so DcuDetailPane now loads it lazily when
    // that sub-tab is actually opened.
    return () => { stale = true; };
  }, [selectedDcu]);

  // Distinct customers among the DCUs, for the filter dropdown.
  const customerOptions = React.useMemo(() => {
    const seen = new Map();
    for (const v of Object.values(dcuCustomers)) {
      if (v && v.customer_id != null) seen.set(String(v.customer_id), v.customer_name);
    }
    return Array.from(seen.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [dcuCustomers]);

  const visibleDcus = dcus.filter(d => {
    const ds = d.serial_number || d.serial;
    if (dcuFilter && !String(ds).includes(dcuFilter.trim())) return false;
    if (customerFilter && String((dcuCustomers[ds] || {}).customer_id ?? '') !== customerFilter) return false;
    return true;
  });

  return (
    <div>
      <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>Data Concentrator Units</div>

      <ActivityPanel />
      <MeterOptimisationSection />

      {dcus.length === 0 ? (
        <div style={{ color: c.textMuted, fontStyle: 'italic', padding: '20px' }}>
          No DCUs currently connected. DCUs connect on port 8002 via GPRS.
        </div>
      ) : (
        <div style={{ display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '280px 1fr',
                      gap: '16px' }}>
          {/* DCU List — hidden on mobile once a DCU is open, so the detail
              pane gets the whole width instead of ~90px of it. */}
          <div style={{ display: showDetailOnMobile ? 'none' : undefined,
                        background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>DCUs ({visibleDcus.length}{visibleDcus.length !== dcus.length ? `/${dcus.length}` : ''})</div>
              <button onClick={loadDcus} title="Refresh DCU list"
                style={{ padding: '3px 8px', fontSize: '11px', background: 'transparent', color: c.textMuted, border: `1px solid ${c.border}`, borderRadius: '6px', cursor: 'pointer' }}>↻</button>
            </div>
            {customerOptions.length > 0 && (
              <select value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', fontSize: '12px', marginBottom: '6px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px' }}>
                <option value="">All customers</option>
                {customerOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            )}
            <input placeholder="Filter DCU No." value={dcuFilter} onChange={e => setDcuFilter(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', fontSize: '12px', marginBottom: '8px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px' }} />
            {visibleDcus.map(d => {
              const ds = d.serial_number || d.serial;
              const on = !!d.session_open;
              const cust = dcuCustomers[ds];
              return (
              <div key={ds}
                onClick={() => { setSelectedDcu(ds); setDcuOpen(true); }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px',
                  padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', marginBottom: '4px',
                  background: selectedDcu === ds ? '#1e40af33'
                    : d.health?.crash_looping ? '#a855f712'
                    : (on ? '#22c55e0d' : '#ef44440d'),
                  border: selectedDcu === ds ? '1px solid #1e40af' : '1px solid transparent',
                }}>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.health?.crash_looping && <span title={`Crash-looping: ${d.health.reboots_24h} reboot(s) in 24h (its own event log). The DCU keeps restarting, so it connects but never completes a session. A site reset will NOT help — it is already resetting itself.`} style={{ color: '#a855f7' }}>⟳ </span>}
                    {(plcFlags[ds]?.count > 0) && <span title={`${plcFlags[ds].count} unknown meter(s) on this DCU's blacklist`} style={{ color: '#ef4444' }}>⚠ </span>}
                    {(!(plcFlags[ds]?.count > 0) && plcFlags[ds]?.cross > 0) && <span title={`${plcFlags[ds].cross} meter(s) registered to a nearby DCU heard here`} style={{ color: '#f59e0b' }}>⚠ </span>}
                    {ds}
                  </div>
                  {/* customer · TP. The row used to show the customer alone,
                      so FoodCity's three DCUs all read "FoodCity" and there was
                      no way to tell which substation a row referred to before
                      clicking it. */}
                  {cust && <div style={{ fontSize: '10px', color: c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={[cust.customer_name, cust.transformer_name].filter(Boolean).join(' · ')}>
                    {cust.customer_name}
                    {cust.transformer_name && <span style={{ opacity: 0.75 }}> · {cust.transformer_name}</span>}
                  </div>}
                </div>
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* "Never seen" is NOT the same as "Offline" and must not be
                      shown as it: offline means it was here and will likely be
                      back, never-seen means it has never dialled in at all and
                      needs someone on site (power / SIM / APN / server addr).
                      Collapsing the two is how a DCU carrying 168 meters sat
                      dark without anyone being able to tell why. */}
                  <span title={d.never_connected
                        ? 'Assigned to a customer but has NEVER connected — nothing on the server can fix this; it needs a field check (power, SIM, APN, server address).'
                        : (on ? 'Session open now.'
                              : ('Has connected before, not connected right now.'
                                 + (d.first_connect_at
                                    ? ` First seen ${fmtDT(d.first_connect_at, { seconds: true })}.` : '')
                                 + (d.last_param_scan_at
                                    ? ` Last read ${fmtDT(d.last_param_scan_at, { seconds: true })}.` : '')))}
                    style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', color: '#fff',
                             background: on ? '#16a34a' : (d.never_connected ? '#7c3aed' : '#dc2626') }}>
                    {on ? 'Online' : (d.never_connected ? 'Never seen' : 'Offline')}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeDcu(ds); }}
                    title="Remove this DCU from the system (→ Delete Manager). For retired/test DCUs."
                    style={{ fontSize: '11px', lineHeight: 1, padding: '3px 6px', borderRadius: '4px', border: `1px solid ${c.border}`, background: 'transparent', color: '#ef4444', cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              </div>
              );
            })}
            {visibleDcus.length === 0 && <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic', padding: '8px' }}>No DCUs match.</div>}
          </div>

          {/* DCU Detail */}
          <div style={{ display: (isMobile && !showDetailOnMobile) ? 'none' : undefined,
                        minWidth: 0 }}>
            {/* The only way out on a phone: the list is display:none, not
                merely scrolled off, so there is nothing to scroll back to. */}
            {showDetailOnMobile && (
              <button className="button button-sm button-secondary"
                      onClick={() => setDcuOpen(false)}
                      style={{ marginBottom: '10px' }}>
                ← All DCUs
              </button>
            )}
          <DcuDetailPane
            selectedDcu={selectedDcu}
            dcuStatus={dcuStatus}
            dcuProfile={dcuProfile}
            dcuOnline={!!(dcus.find(d => (d.serial_number || d.serial) === selectedDcu) || {}).session_open}
            focusSection={focusSection}
            onStatusRefreshed={setDcuStatus}
          />
          </div>
        </div>
      )}
    </div>
  );
}


function MapTab() {
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const { meters } = useMeterList();
  const { assignments, refreshAssignments } = useAssignments();
  const mapRef = useRef(null);          // Leaflet map instance
  const containerRef = useRef(null);     // <div> the map mounts into
  const markerLayerRef = useRef(null);   // L.layerGroup we wipe on each refresh

  const [transformers, setTransformers] = useState([]);
  const items = React.useMemo(
    () => ({ transformers, assignments, meters }),
    [transformers, assignments, meters],
  );
  const mapAssignmentsByTransformer = React.useMemo(() => {
    const index = new Map();
    for (const a of assignments) {
      const list = index.get(a.transformer_id) || [];
      list.push(a);
      index.set(a.transformer_id, list);
    }
    return index;
  }, [assignments]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Initialise the Leaflet map exactly once when the tab mounts.
  useEffect(() => {
    if (!window.L) {
      toast.push(t('toast.map_load_failed'), 'error');
      return;
    }
    if (mapRef.current || !containerRef.current) return;
    const map = window.L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([41.3111, 69.2406], 11); // Tashkent

    // Tile provider: CartoDB "Dark Matter" — free, no API key, designed
    // for embed in dark-themed apps, and (unlike the public OSM tile
    // server) does not 403 automated browser clients. Falls back to
    // OSM if the CartoDB CDN itself is unreachable. A failed-tile
    // event handler swaps to the fallback layer on the first error
    // so the user always sees *something*.
    const tileLayer = window.L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      {
        maxZoom: 19,
        subdomains: 'abcd',
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, © <a href="https://carto.com/attributions">CARTO</a>',
      }
    );
    let triedFallback = false;
    tileLayer.on('tileerror', () => {
      if (triedFallback) return;
      triedFallback = true;
      try { map.removeLayer(tileLayer); } catch (_) {}
      window.L.tileLayer(
        'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        { maxZoom: 19, attribution: '© OpenStreetMap contributors' }
      ).addTo(map);
      toast.push(t('toast.map_tiles_fallback'), 'info');
    });
    tileLayer.addTo(map);

    markerLayerRef.current = window.L.layerGroup().addTo(map);
    mapRef.current = map;
    // Leaflet needs a kick when its container is rendered inside a
    // tab that was hidden — invalidate size after a frame so it picks
    // up the real width/height.
    setTimeout(() => map.invalidateSize(), 100);
    return () => {
      try { map.remove(); } catch (_) {}
      mapRef.current = null;
      markerLayerRef.current = null;
    };
  }, []);

  // Meter and assignment lists come from the session caches; only the small
  // transformer list is tab-local.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api('/transformers').then(d => d.transformers || []),
      refreshAssignments({ force: refreshKey > 0 }),
    ])
      .then(([t]) => {
        if (!mounted) return;
        setTransformers(t);
      })
      .catch(err => mounted && toast.push(`Map load failed: ${err.message}`, 'error'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [refreshKey, refreshAssignments]);

  // Re-paint markers whenever the data changes.
  useEffect(() => {
    if (!mapRef.current || !markerLayerRef.current || !window.L) return;
    const layer = markerLayerRef.current;
    layer.clearLayers();

    const liveBySerial = Object.fromEntries(
      items.meters.map(m => [m.serial_number || m.serial, m])
    );

    // Helper: pick a colour based on whether the related meter is
    // currently dialled in to the CAS server.
    const colorFor = (serial) => {
      const live = liveBySerial[serial];
      if (!live) return '#94a3b8';                  // grey: offline / never seen
      if (live.session_open) return '#22c55e';      // green: live session
      if (live.identified)   return '#fbbf24';      // yellow: connected but no session
      return '#ef4444';                              // red: connected but unidentified
    };

    const bounds = [];

    // Transformers — square-ish marker via L.divIcon
    items.transformers.forEach(t => {
      if (t.lat == null || t.lon == null) return;
      // Find any meter assigned to this transformer to determine status.
      const tAssigns = mapAssignmentsByTransformer.get(t.id) || [];
      const anyOnline = tAssigns.some(a => liveBySerial[a.meter_serial]?.session_open);
      const color = anyOnline ? '#22c55e' : (tAssigns.length > 0 ? '#fbbf24' : '#94a3b8');

      const marker = window.L.marker([t.lat, t.lon], {
        icon: window.L.divIcon({
          className: '',
          html: `<div style="width:18px;height:18px;background:${color};border:2px solid #0a0e17;border-radius:3px;box-shadow:0 0 6px rgba(0,0,0,0.6);"></div>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        }),
      });
      const popupHtml = `
        <div style="min-width:200px;font-family:system-ui;color:#111;">
          <div style="font-weight:600;font-size:14px;">⚡ ${escapeHtml(t.name)}</div>
          <div style="font-size:12px;color:#555;">${escapeHtml(t.location || '—')}</div>
          <hr style="margin:6px 0;border:none;border-top:1px solid #ddd;" />
          <div style="font-size:11px;">
            VT: ${escapeHtml(t.vt_ratio || '—')} · CT: ${escapeHtml(t.ct_ratio_default || '—')}<br/>
            Meters: ${tAssigns.length} (${tAssigns.filter(a => liveBySerial[a.meter_serial]?.session_open).length} online)
          </div>
        </div>
      `;
      marker.bindPopup(popupHtml);
      layer.addLayer(marker);
      bounds.push([t.lat, t.lon]);
    });

    // Meter assignments — circle markers
    items.assignments.forEach(a => {
      if (a.lat == null || a.lon == null) return;
      const color = colorFor(a.meter_serial);
      const marker = window.L.circleMarker([a.lat, a.lon], {
        radius: 6,
        fillColor: color,
        color: '#0a0e17',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.9,
      });
      const live = liveBySerial[a.meter_serial];
      const popupHtml = `
        <div style="min-width:200px;font-family:system-ui;color:#111;">
          <div style="font-weight:600;font-size:14px;">${a.role === 'main' ? '🟧' : '🔌'} ${escapeHtml(a.object_name || a.meter_serial)}</div>
          <div style="font-size:12px;color:#555;font-family:monospace;">${escapeHtml(a.meter_serial)}</div>
          <hr style="margin:6px 0;border:none;border-top:1px solid #ddd;" />
          <div style="font-size:11px;">
            Role: ${a.role}<br/>
            CT: ${escapeHtml(a.ct_ratio || '—')} · VT: ${escapeHtml(a.vt_ratio || '—')}<br/>
            Status: ${live ? (live.session_open ? '🟢 live session' : (live.identified ? '🟡 connected' : '🔴 unidentified')) : '⚪ offline'}
          </div>
        </div>
      `;
      marker.bindPopup(popupHtml);
      layer.addLayer(marker);
      bounds.push([a.lat, a.lon]);
    });

    if (bounds.length > 0) {
      try {
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      } catch (_) {}
    }
  }, [items, mapAssignmentsByTransformer]);

  return (
    <div className="tab-content">
      <div className="section-title">🗺️ Map</div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="button button-secondary" onClick={() => setRefreshKey(k => k + 1)}>
          🔄 Refresh
        </button>
        <div style={{ fontSize: '12px', color: c.textSecondary, display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#22c55e',marginRight:6}} />Live session</span>
          <span><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#fbbf24',marginRight:6}} />Identified, no session</span>
          <span><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#ef4444',marginRight:6}} />Connected, unidentified</span>
          <span><span style={{display:'inline-block',width:10,height:10,borderRadius:'50%',background:'#94a3b8',marginRight:6}} />Offline / never seen</span>
        </div>
      </div>

      {loading && <div style={{ color: c.textMuted, fontSize: '12px', marginBottom: '8px' }}>Loading map data…</div>}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          minHeight: '600px',
          height: '70vh',
          background: c.bgElevated,
          border: `1px solid ${c.border}`,
          borderRadius: '8px',
        }}
      />

      {!loading && items.transformers.length === 0 && items.assignments.length === 0 && (
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          background: 'rgba(251, 191, 36, 0.1)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '6px',
          fontSize: '12px',
          color: c.warning,
        }}>
          ⚠️ No transformers or meter assignments have lat/lon coordinates set.
          Open the Customers tab, edit a transformer or meter, and fill in the
          Latitude/Longitude fields to make them appear here.
        </div>
      )}
    </div>
  );
}

// HTML-escape helper for the Leaflet popup HTML strings (Leaflet
// renders popup content as raw HTML, so anything coming from the DB
// has to be sanitised — we don't want a customer name with `<script>`
// in it executing on someone else's session).
function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ═══════════════════════════════════════════════════════════════════════
// Hierarchy Tab — Customer / Transformer / DCU / Meter tree
//
// Left half: collapsible tree view of every customer the current user
// can see, with their transformers, DCUs (when present) and meters
// nested below. Right half: a context-aware detail panel that flips
// based on which node is selected.
//
// Admin-only buttons in the toolbar (visible only when user.role==='admin'):
//   ➕ Customer       — opens AddCustomerModal
//   ➕ Transformer    — opens AddTransformerModal (preselects current customer)
//   🔗 Assign Meter   — opens AssignMeterModal (preselects current transformer
//                       if one is selected, otherwise lets you pick one)
//
// The tree pulls three resource lists in parallel and merges them
// client-side: /api/customers, /api/transformers, /api/assignments.
// Unassigned meters (those that have a row in /api/meters but no
// corresponding /api/assignments entry) are shown in a separate
// "🔌 Unassigned" group at the bottom of the tree so admins can
// claim them.
//
// All write operations go through the existing CRUD endpoints. The
// notes timeline is part of CustomerDetail.
// ═══════════════════════════════════════════════════════════════════════

// VirtualMeterList — hand-rolled fixed-height virtualizer for big
// meter-leaf lists in HierarchyTab. Used when a single DCU /
// transformer / unassigned-bucket has 100+ children. Below that
// threshold the cost of virtualization (extra spacer divs, scroll
// listeners) outweighs its benefit, so callers should fall back to
// a plain .map() for short lists.
//
// Why hand-rolled instead of react-window: the SPA loads React via
// CDN UMD with no node_modules / package.json on the deploy target.
// Adding react-window means another CDN dependency or a new build
// step. The whole component is ~40 lines — cheaper to maintain in
// place than the dependency.
function VirtualMeterList({
  items,
  itemHeight = 26,
  maxHeight = 360,
  overscan = 6,
  renderItem,
}) {
  const [scrollTop, setScrollTop] = React.useState(0);
  const onScroll = React.useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);
  const total = items.length;
  const totalHeight = total * itemHeight;
  const visibleCount = Math.ceil(maxHeight / itemHeight) + overscan * 2;
  const startIdx = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIdx = Math.min(total, startIdx + visibleCount);
  const offsetY = startIdx * itemHeight;
  const visible = items.slice(startIdx, endIdx);
  return (
    <div
      onScroll={onScroll}
      style={{
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetY}px)` }}>
          {visible.map((item, i) => (
            <div
              key={item.id ?? item.serial ?? item.meter_serial ?? (startIdx + i)}
              style={{ height: `${itemHeight}px` }}
            >
              {renderItem(item, startIdx + i)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HierarchyTab() {
  const c = useThemeColors();
  const t = useT();
  const { user } = useAuth();
  const toast = useToast();
  const { selected: ctxSelectedSerial, explicit: ctxSerialExplicit } = useMeterSelection();
  const { meters: ctxMeters } = useMeterList();
  const nav = useNav();
  const isAdmin = userCan(user, 'manage_records');

  const [customers, setCustomers] = useState([]);
  const [transformers, setTransformers] = useState([]);
  const { assignments, refreshAssignments } = useAssignments();
  // Meter list comes from the always-mounted context (delta-updated via
  // websocket + 60 s reconcile) — re-downloading the multi-MB /api/meters on
  // every tab entry was pure waste and a navigation-lag source at fleet scale.
  const inboundMeters = ctxMeters || [];
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  // Bulk soft-delete of unassigned meters/DCUs (admin). `deletedSerials` are the
  // already-soft-deleted serials we hide from the Unassigned list.
  const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
  const [deletedSerials, setDeletedSerials] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Selection: { type: 'customer'|'transformer'|'dcu'|'meter', id: ... }
  // Persisted in sessionStorage so a page RELOAD lands the operator back on
  // the same meter/customer within the session, but a NEW session (browser
  // reopened, or after logout which clears the key) starts on "All Customers"
  // instead of whatever was last open. Cleared on logout.
  const TREE_SELECTION_KEY = 'apm_last_selection';
  const [selectionRaw, setSelectionRaw] = useState(() => {
    try {
      const saved = sessionStorage.getItem(TREE_SELECTION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (_) { return null; }
  });
  const setSelection = useCallback((sel) => {
    setSelectionRaw(sel);
    try {
      if (sel) sessionStorage.setItem(TREE_SELECTION_KEY, JSON.stringify(sel));
      else sessionStorage.removeItem(TREE_SELECTION_KEY);
    } catch (_) {}
    if (sel && sel.type && sel.type !== 'all') {
      nav.collapseSidebar();
    }
  }, [nav]);
  const selection = selectionRaw;

  // MOBILE MASTER/DETAIL.
  // On a phone the two-column split collapses to one, which stacked the
  // detail pane underneath a tree that is itself up to 80vh tall and
  // scrollable. Tapping a meter therefore appeared to do nothing: the
  // readings and reports it opened were a full screen further down, behind
  // a scroll the operator had no reason to suspect. On mobile we show one
  // pane at a time, the standard master/detail pattern — the tree until
  // something is picked, then the detail with a way back.
  const isMobile = useIsMobile();
  const showDetailOnMobile = isMobile && !!selection && selection.type !== 'all';

  // Bring the detail pane to the top when a selection opens it. Without this
  // the new pane inherits the tree's scroll position and opens mid-content.
  React.useEffect(() => {
    if (showDetailOnMobile) {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
    }
  }, [showDetailOnMobile, selection?.type, selection?.id]);

  // Modal toggles
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddTransformer, setShowAddTransformer] = useState(false);
  const [showAssignMeter, setShowAssignMeter] = useState(false);
  const [showAssignDcu, setShowAssignDcu] = useState(false);

  // Track which customers are expanded in the tree (default: all)
  const [expandedCustomers, setExpandedCustomers] = useSessionSet('apm_tree_exp_customers');
  const [expandedTransformers, setExpandedTransformers] = useSessionSet('apm_tree_exp_transformers');
  // DCU nodes default to collapsed — a DCU with 100-200 children
  // floods the tree and makes siblings unreachable otherwise.
  const [expandedDcus, setExpandedDcus] = useSessionSet('apm_tree_exp_dcus');
  // Search-driven expansion is DERIVED, never stored.
  //
  // These used to be written straight into the three useSessionSet sets above,
  // which persist to sessionStorage on every write. A search for a common
  // substring expands most of the tree, so that expansion was saved — and the
  // only thing that undid it was clearing the search box in the SAME mounted
  // component, because the undo snapshot was plain useState. Navigate away or
  // reload with text still in the box and the snapshot died while the expanded
  // set survived, leaving the operator's tree fully open on every subsequent
  // visit with no way to explain it. (Before the 2026-09-06 persistence work
  // the sets were plain state, so a reload happened to wash it out; adding
  // persistence made a pre-existing leak permanent.)
  //
  // Keeping them separate makes the bug unrepresentable: the persisted sets
  // only ever receive deliberate operator toggles, and search expansion
  // evaporates when the query clears or the page reloads — no snapshot, no
  // restore, nothing to lose.
  const [searchExpandedCustomers, setSearchExpandedCustomers] = useState(() => new Set());
  const [searchExpandedTransformers, setSearchExpandedTransformers] = useState(() => new Set());

  // Client-side search that filters customer/transformer/meter rows
  // by substring match on name, account#, location, meter serial, or
  // object name. A node stays visible if it matches OR any of its
  // descendants match, so typing a meter serial reveals the whole
  // ancestry.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 150);

  // (Polling-health badge that used to live next to each customer
  // node moved into the customer's Home tab via
  // CustomerCollectionStatsCard — keeps the tree clean.)

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  // Pull all four resources whenever refreshKey changes.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      api('/customers').then(d => d.customers || []),
      api('/transformers').then(d => d.transformers || []),
      refreshAssignments({ force: refreshKey > 0 }),
      // Soft-deleted serials (admin) so the Unassigned list hides items already
      // sent to the Delete Manager; harmless empty for non-admins.
      isAdmin ? api('/admin/deleted-devices').then(d => d.devices || []).catch(() => []) : Promise.resolve([]),
    ])
      .then(([cs, ts, as, del]) => {
        if (!mounted) return;
        setCustomers(cs);
        setTransformers(ts);
        setDeletedSerials(new Set((del || []).map(d => d.meter_serial)));
        setSelectedUnassigned(new Set());
        // Tree starts collapsed — operators land on the
        // AllCustomersAggregateView and explicitly expand whichever
        // customer they want to drill into. The search box auto-
        // expands matching ancestors via the effect below.
      })
      .catch(err => toast.push(`Hierarchy load failed: ${err.message}`, 'error'))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [refreshKey, isAdmin, refreshAssignments]);

  // Compute the unassigned-meters set: every inbound meter whose
  // serial isn't already in assignments[].meter_serial. Only admins
  // get to see/claim these; for everyone else the backend already
  // strips unassigned meters from /api/meters so the list would be
  // empty anyway, but we also gate in the UI to avoid showing an
  // empty "Unassigned (0)" section.
  // Treat any serial that appears as a dcu_serial on some child's
  // assignment as implicitly assigned — the DCU itself is a physical
  // device in the inbound pool, but the tree renders it under its
  // children's transformer/customer, not as a separate meter row.
  // Without this, a DCU with no self-assignment row (e.g. after a
  // stale-row cleanup) would incorrectly surface in "Unassigned".
  // A serial counts as "assigned" only when it (or a child pointing at it via
  // dcu_serial) actually has a transformer — i.e. is placed under a customer.
  // A row with transformer_id = null is an UNASSIGNED device (e.g. a DCU that
  // was assigned then unassigned): it must surface in Unassigned + the Assign
  // DCU picker, not stay hidden in limbo.
  const assignedSerials = React.useMemo(() => {
    const serials = new Set();
    for (const a of assignments) {
      if (!a.transformer_id) continue;
      serials.add(a.meter_serial);
      if (a.dcu_serial) serials.add(a.dcu_serial);
    }
    return serials;
  }, [assignments]);
  const unassignedMeters = React.useMemo(() => (
    isAdmin
      ? inboundMeters.filter(m => {
          const s = m.serial_number || m.serial;
          return !assignedSerials.has(s) && !deletedSerials.has(s);
        })
      : []
  ), [isAdmin, inboundMeters, assignedSerials, deletedSerials]);

  // O(1) hierarchy indexes. The previous render path repeatedly filtered the
  // complete transformer and assignment arrays inside customer/transformer
  // loops, making a search/render effectively O(customers × assignments).
  const hierarchyIndex = React.useMemo(() => {
    const transformerById = new Map();
    const transformersByCustomer = new Map();
    const assignmentsByTransformer = new Map();
    const assignmentByMeter = new Map();
    const assignmentById = new Map();
    for (const tr of transformers) {
      transformerById.set(tr.id, tr);
      const list = transformersByCustomer.get(tr.customer_id) || [];
      list.push(tr);
      transformersByCustomer.set(tr.customer_id, list);
    }
    // A DCU LIVES UNDER ITS OWN TRANSFORMER, and shows every meter registered
    // to it. Previously a DCU node was a label recomputed inside each
    // transformer — "group this feeder's meters by dcu_serial" — so a DCU whose
    // meters spanned two feeders rendered as TWO nodes, each showing only that
    // feeder's share. On 2026-08-12 DCU 129255053298 appeared under ввод-1 (87)
    // and ввод-2 (60); one device, 147 meters, drawn as two.
    //
    // dcuHome: dcu_serial -> the transformer on the DCU's OWN assignment row.
    // metersByDcu: dcu_serial -> every meter routed to it, regardless of the
    // METER's transformer_id. Moving a meter between DCUs therefore moves it
    // between DCU nodes in the tree, which is what an operator means by a move
    // — while its own transformer_id (who bills it) is left alone.
    const dcuHome = new Map();
    const metersByDcu = new Map();
    for (const a of assignments) {
      assignmentByMeter.set(a.meter_serial, a);
      assignmentById.set(a.id, a);
      const list = assignmentsByTransformer.get(a.transformer_id) || [];
      list.push(a);
      assignmentsByTransformer.set(a.transformer_id, list);
      if (a.dcu_serial && a.meter_serial === a.dcu_serial) {
        dcuHome.set(a.dcu_serial, a.transformer_id);
      } else if (a.dcu_serial) {
        const kids = metersByDcu.get(a.dcu_serial) || [];
        kids.push(a);
        metersByDcu.set(a.dcu_serial, kids);
      }
    }
    // A DCU with no self-row (or one with no transformer) has no home of its
    // own. Falling back to where its meters already sit keeps it visible
    // instead of silently dropping its whole subtree out of the tree.
    const dcusByTransformer = new Map();
    for (const dcu of metersByDcu.keys()) {
      let home = dcuHome.get(dcu);
      if (home == null) {
        const kids = metersByDcu.get(dcu) || [];
        home = (kids.find(k => k.transformer_id != null) || {}).transformer_id;
      }
      if (home == null) continue;
      const list = dcusByTransformer.get(home) || [];
      list.push(dcu);
      dcusByTransformer.set(home, list);
    }
    // A DCU that is placed but currently carries nothing still belongs on the
    // tree — an empty DCU is a fact worth seeing, not an absence.
    for (const [dcu, home] of dcuHome) {
      if (home == null || metersByDcu.has(dcu)) continue;
      const list = dcusByTransformer.get(home) || [];
      list.push(dcu);
      dcusByTransformer.set(home, list);
    }
    return { transformerById, transformersByCustomer, assignmentsByTransformer,
             assignmentByMeter, assignmentById, dcuHome, metersByDcu, dcusByTransformer };
  }, [transformers, assignments]);
  const q = debouncedSearch.trim().toLowerCase();

  // ── Bulk soft-delete of selected unassigned meters/DCUs ──────────────
  const toggleUnassigned = useCallback((serial) => {
    setSelectedUnassigned(prev => { const n = new Set(prev); n.has(serial) ? n.delete(serial) : n.add(serial); return n; });
  }, []);
  const toggleAllUnassigned = useCallback(() => {
    setSelectedUnassigned(prev => {
      const all = unassignedMeters.map(m => m.serial_number || m.serial);
      return prev.size === all.length ? new Set() : new Set(all);
    });
  }, [unassignedMeters]);
  const bulkDeleteUnassigned = useCallback(async () => {
    const serials = [...selectedUnassigned];
    if (serials.length === 0) return;
    if (!window.confirm(
      `Soft-delete ${serials.length} unassigned device(s)?\n\n`
      + `They move to the Delete Manager (Device Management → Delete Manager), where you can `
      + `Restore them or permanently Purge them. This does not touch assigned meters.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api('/admin/devices/bulk-soft-delete', {
        method: 'POST', body: JSON.stringify({ serials }),
      });
      toast.push(`Moved ${(r.deleted || 0) + (r.tombstoned || 0)} device(s) to the Delete Manager`, 'success');
      setSelectedUnassigned(new Set());
      refresh();
    } catch (e) {
      toast.push(`Delete failed: ${e.message || e}`, 'error');
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedUnassigned, toast, refresh]);

  // Search-driven tree expansion: when the operator types in the
  // search box, auto-expand the customer + transformer ancestors
  // of every matching meter / transformer so the matched node is
  // visible without clicking. Clearing the search collapses
  // everything back to the manually-expanded set.
  useEffect(() => {
    const q = (debouncedSearch || '').trim().toLowerCase();
    if (!q) {
      // Search cleared — drop the derived expansion. The operator's own
      // toggles live in the persisted sets and are untouched, so there is
      // nothing to restore and no snapshot to keep.
      setSearchExpandedCustomers(new Set());
      setSearchExpandedTransformers(new Set());
      return;
    }
    // Walk every assignment whose meter serial / object name /
    // account number contains the query and collect the ancestor
    // transformer + customer ids.
    const m = (txt) => !!txt && String(txt).toLowerCase().includes(q);
    const hitT = new Set();
    const hitC = new Set();
    for (const a of assignments) {
      if (m(a.meter_serial) || m(a.object_name) || m(a.account_number)) {
        // Expand where the meter is actually DRAWN: under its DCU's node,
        // which lives on the DCU's transformer — not necessarily its own.
        const home = (a.dcu_serial && a.meter_serial !== a.dcu_serial
          && hierarchyIndex.dcuHome.get(a.dcu_serial) != null)
          ? hierarchyIndex.dcuHome.get(a.dcu_serial)
          : a.transformer_id;
        if (home) hitT.add(home);
        const t = hierarchyIndex.transformerById.get(home);
        if (t?.customer_id) hitC.add(t.customer_id);
      }
    }
    // Also expand transformers whose own name / location matches.
    for (const t of transformers) {
      if (m(t.name) || m(t.location)) {
        hitT.add(t.id);
        if (t.customer_id) hitC.add(t.customer_id);
      }
    }
    // And customers whose name / account matches (so the customer
    // node is visibly in the result list, even if no descendant
    // matched separately).
    for (const c of customers) {
      if (m(c.name) || m(c.account_number)) hitC.add(c.id);
    }
    // Assigned, not merged: each query's expansion replaces the last one's,
    // so narrowing a search closes what it no longer matches instead of
    // piling every query's hits together until the whole tree is open.
    setSearchExpandedCustomers(hitC);
    setSearchExpandedTransformers(hitT);
    /* eslint-disable-next-line */
  }, [debouncedSearch, assignments, transformers, customers, hierarchyIndex]);

  // Cross-tab navigation hook: when the user lands on HierarchyTab
  // with a meter serial already pinned in MeterContext (e.g. they
  // clicked a row in the 🔌 Meters list or hit a KPI-card shortcut),
  // auto-select that meter in the tree so MeterDetailView renders
  // immediately without a second click.
  // ONE-SHOT PER SERIAL. This used to re-run on every `assignments` /
  // `hierarchyIndex` change — i.e. on every Refresh, every meter assignment,
  // every background reload — and each time it re-applied whatever serial was
  // still pinned in MeterContext, dragging the operator off the customer they
  // were reading. That pin is rarely something they chose: the fleet loader
  // auto-selects `data[0].serial` (the first meter in /api/meters) when
  // nothing is set, so "Refresh jumps to a random meter" was really "Refresh
  // jumps to the alphabetically-first meter on the fleet", for ever, because
  // the pin is never cleared.
  //
  // The hook's actual job is cross-tab navigation: honour a serial ONCE when
  // the user arrives from the Meters list or a KPI shortcut. The ref records
  // which serial we have already honoured, so a data reload cannot replay it
  // and an explicit tree click is never overridden.
  const honouredCtxSerial = React.useRef(null);
  useEffect(() => {
    if (!ctxSelectedSerial || assignments.length === 0) return;
    // Only a serial the user actually picked navigates the tree. The fleet
    // loader's auto-default must never move the operator's view.
    if (!ctxSerialExplicit) return;
    if (honouredCtxSerial.current === ctxSelectedSerial) return;
    const a = hierarchyIndex.assignmentByMeter.get(ctxSelectedSerial);
    if (!a) return;
    honouredCtxSerial.current = ctxSelectedSerial;
    setSelection({ type: 'meter', id: a.id });
    // Make sure the ancestor rows are expanded so the node is visible.
    if (a.transformer_id) {
      setExpandedTransformers(prev => {
        const next = new Set(prev);
        next.add(a.transformer_id);
        return next;
      });
      const t = hierarchyIndex.transformerById.get(a.transformer_id);
      if (t?.customer_id) {
        setExpandedCustomers(prev => {
          const next = new Set(prev);
          next.add(t.customer_id);
          return next;
        });
      }
    }
  }, [ctxSelectedSerial, ctxSerialExplicit, assignments, transformers, hierarchyIndex]);

  // Search helpers — a node is visible if it matches OR any of its
  // descendants match, so searching 'power' reveals any meter whose
  // object_name contains 'power' with its whole ancestry.
  const treeSearch = React.useMemo(() => {
    const matches = (txt) => !!txt && String(txt).toLowerCase().includes(q);
    const assignmentMatches = (a) =>
      !q || matches(a.meter_serial) || matches(a.object_name) || matches(a.account_number);
    const transformerMatches = (tr) => {
      if (!q || matches(tr.name) || matches(tr.location)) return true;
      if ((hierarchyIndex.assignmentsByTransformer.get(tr.id) || []).some(assignmentMatches)) return true;
      // A DCU living here can carry meters that BILL to another feeder; they
      // render under this node, so this node must match when they do —
      // otherwise the search reveals a branch the meter isn't drawn in.
      return (hierarchyIndex.dcusByTransformer.get(tr.id) || []).some(dcu =>
        matches(dcu) || (hierarchyIndex.metersByDcu.get(dcu) || []).some(assignmentMatches));
    };
    const customerMatches = (customer) => {
      if (!q || matches(customer.name) || matches(customer.account_number)) return true;
      return (hierarchyIndex.transformersByCustomer.get(customer.id) || []).some(transformerMatches);
    };
    return {
      matches,
      assignmentMatches,
      transformerMatches,
      visibleCustomers: customers.filter(customerMatches),
    };
  }, [q, customers, hierarchyIndex]);

  // Toggle on the EFFECTIVE state, not just the manual set. A node opened by
  // a search is not in the persisted set, so a plain toggle would ADD it —
  // the chevron would look dead on exactly the nodes a search just revealed.
  // Collapsing therefore clears the id from both sets; expanding only ever
  // touches the manual one.
  const toggleCustomer = (id) => {
    if (expandedCustomers.has(id) || searchExpandedCustomers.has(id)) {
      setExpandedCustomers(prev => { const n = new Set(prev); n.delete(id); return n; });
      setSearchExpandedCustomers(prev => { const n = new Set(prev); n.delete(id); return n; });
    } else {
      setExpandedCustomers(prev => new Set(prev).add(id));
    }
  };
  const toggleTransformer = (id) => {
    if (expandedTransformers.has(id) || searchExpandedTransformers.has(id)) {
      setExpandedTransformers(prev => { const n = new Set(prev); n.delete(id); return n; });
      setSearchExpandedTransformers(prev => { const n = new Set(prev); n.delete(id); return n; });
    } else {
      setExpandedTransformers(prev => new Set(prev).add(id));
    }
  };
  const toggleDcu = (dcuSerial) => {
    setExpandedDcus(prev => {
      const next = new Set(prev);
      if (next.has(dcuSerial)) next.delete(dcuSerial); else next.add(dcuSerial);
      return next;
    });
  };

  return (
    <div className="tab-content">
      <div className="section-title">👥 Customers</div>

      {/* Admin toolbar */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button className="button button-primary" onClick={() => setShowAddCustomer(true)}>
            ➕ {t('btn.new_customer')}
          </button>
          <button
            className="button button-secondary"
            onClick={() => setShowAddTransformer(true)}
            disabled={customers.length === 0}
            title={customers.length === 0 ? t('tip.create_customer_first') : ''}
          >
            ➕ {t('btn.new_transformer')}
          </button>
          <button
            className="button button-secondary"
            onClick={() => setShowAssignMeter(true)}
            disabled={transformers.length === 0}
            title={transformers.length === 0 ? t('tip.create_transformer_first') : ''}
          >
            🔗 {t('btn.assign_meter')}
          </button>
          <button
            className="button button-secondary"
            onClick={() => setShowAssignDcu(true)}
            disabled={transformers.length === 0}
            title={transformers.length === 0 ? t('tip.create_transformer_first') : ''}
          >
            🛰️ {t('btn.assign_dcu')}
          </button>
          <button className="button button-secondary" onClick={refresh}>
            🔄 {t('btn.refresh')}
          </button>
        </div>
      )}
      {!isAdmin && (
        <div style={{ marginBottom: '16px', fontSize: '12px', color: c.textMuted }}>
          {t('msg.readonly_hierarchy')} ({user?.role})
        </div>
      )}

      {loading && <div style={{ color: c.textMuted }}><Spinner /> {t('msg.loading_hierarchy')}</div>}

      {!loading && customers.length === 0 && unassignedMeters.length === 0 && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: c.textMuted,
          background: c.bgElevated,
          border: `1px dashed ${c.borderInput}`,
          borderRadius: '8px',
        }}>
          {t('msg.no_customers_yet')}
        </div>
      )}

      {!loading && (customers.length > 0 || unassignedMeters.length > 0) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 380px) 1fr',
          gap: '16px',
          alignItems: 'start',
        }}>
          {/* ─── Tree column ─── */}
          {/* Hidden on mobile once a node is picked — see showDetailOnMobile. */}
          <div style={{
            display: showDetailOnMobile ? 'none' : undefined,
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: '8px',
            padding: '12px',
            maxHeight: '80vh',
            overflowY: 'auto',
            fontSize: '13px',
          }}>
            <button
              className="button button-sm"
              onClick={() => setSelection(null)}
              style={{
                width: '100%',
                marginBottom: '8px',
                padding: '8px 10px',
                background: !selection ? '#1e40af' : c.bgCardAlt,
                color: !selection ? c.textPrimary : c.textSecondary,
                border: `1px solid ${!selection ? '#1e40af' : c.border}`,
                fontSize: '12px',
                fontWeight: !selection ? 600 : 400,
                textAlign: 'left',
              }}
              title="Show consolidated dashboard across all customers"
            >
              📊 {t('tree.all_customers_home')}
            </button>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`🔍 ${t('ph.search_hierarchy')}`}
              className="input-field"
              style={{ width: '100%', marginBottom: '6px', padding: '6px 10px', fontSize: '12px' }}
            />
            {/* Collapse all. Clearing the three sets through their own setters
                also clears the sessionStorage copy, so a refresh does not
                helpfully restore the tree the operator just collapsed —
                which is what the persistence added on 2026-09-06 would
                otherwise do. Hidden when nothing is open, so it never
                occupies a row it cannot act on. */}
            {(expandedCustomers.size + expandedTransformers.size + expandedDcus.size
              + searchExpandedCustomers.size + searchExpandedTransformers.size) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                <button
                  className="button button-sm button-secondary"
                  style={{ fontSize: '11px', padding: '3px 8px' }}
                  title={t('tree.collapse_all_hint')}
                  onClick={() => {
                    setExpandedCustomers(new Set());
                    setExpandedTransformers(new Set());
                    setExpandedDcus(new Set());
                    // Also clear the derived sets and the query itself —
                    // leaving a live search would re-expand the tree on the
                    // next render and the button would look broken.
                    setSearchExpandedCustomers(new Set());
                    setSearchExpandedTransformers(new Set());
                    setSearch('');
                  }}
                >
                  ⊟ {t('tree.collapse_all')}
                </button>
              </div>
            )}
            {q && treeSearch.visibleCustomers.length === 0 && (
              <div style={{ color: c.textDimmed, fontSize: '11px', fontStyle: 'italic', padding: '6px 0' }}>
                No matches for "{search}"
              </div>
            )}
            {treeSearch.visibleCustomers.map(cu => {
              // If the customer itself matches the query on name/account,
              // show its whole subtree unfiltered — otherwise the user
              // searches "Alice Corp", the customer node appears, but
              // every transformer fails the substring test and the node
              // lies that there are "no transformers".
              const customerSelfHit = !q || treeSearch.matches(cu.name) || treeSearch.matches(cu.account_number);
              const cTransformers = (hierarchyIndex.transformersByCustomer.get(cu.id) || []).filter(
                t => customerSelfHit || treeSearch.transformerMatches(t)
              );
              const isExp = expandedCustomers.has(cu.id) || searchExpandedCustomers.has(cu.id);
              return (
                <div key={cu.id} style={{ marginBottom: '4px' }}>
                  <TreeNode
                    emoji="👤"
                    label={cu.name}
                    sub={cu.account_number}
                    selected={selection?.type === 'customer' && selection.id === cu.id}
                    expanded={isExp}
                    onToggle={() => toggleCustomer(cu.id)}
                    onClick={() => setSelection({ type: 'customer', id: cu.id })}
                  />
                  {isExp && cTransformers.map(t => {
                    // Same descendant-reveal fix one level down:
                    // if the query was a customer/transformer hit,
                    // show every assignment under it unfiltered.
                    const transformerSelfHit = !q || treeSearch.matches(t.name) || treeSearch.matches(t.location);
                    const tAssigns = (hierarchyIndex.assignmentsByTransformer.get(t.id) || []).filter(a =>
                      customerSelfHit || transformerSelfHit || treeSearch.assignmentMatches(a)
                    );
                    const tExp = expandedTransformers.has(t.id) || searchExpandedTransformers.has(t.id);
                    // DCUs that LIVE on this transformer (their own row says so),
                    // each showing every meter registered to it — not just the
                    // share that happens to bill to this feeder. That is what
                    // makes one DCU render exactly once.
                    const byDcu = {};
                    for (const dcu of (hierarchyIndex.dcusByTransformer.get(t.id) || [])) {
                      const all = hierarchyIndex.metersByDcu.get(dcu) || [];
                      const selfHit = customerSelfHit || transformerSelfHit
                        || treeSearch.matches(dcu);
                      const kids = selfHit ? all : all.filter(treeSearch.assignmentMatches);
                      // While searching, a DCU with no hit and no name match is
                      // noise — drop it rather than render an empty node.
                      if (!q || selfHit || kids.length) byDcu[dcu] = kids;
                    }
                    // Direct meters only — anything routed via a DCU is drawn
                    // under that DCU's node, wherever the DCU lives.
                    const directs = tAssigns.filter(a => !a.dcu_serial);
                    return (
                      <div key={t.id} style={{ marginLeft: '20px', marginTop: '4px' }}>
                        <TreeNode
                          emoji="⚡"
                          label={t.name}
                          sub={t.location}
                          selected={selection?.type === 'transformer' && selection.id === t.id}
                          expanded={tExp}
                          onToggle={() => toggleTransformer(t.id)}
                          onClick={() => setSelection({ type: 'transformer', id: t.id })}
                        />
                        {tExp && (
                          <>
                            {Object.keys(byDcu).map(dcuSerial => {
                              const dcuChildren = byDcu[dcuSerial];
                              const dcuExp = expandedDcus.has(dcuSerial);
                              return (
                              <div key={dcuSerial} style={{ marginLeft: '20px', marginTop: '4px' }}>
                                <TreeNode
                                  emoji="🛰️"
                                  label={`DCU ${dcuSerial}`}
                                  sub={dcuChildren.length > 0
                                    ? `${dcuChildren.length} meter${dcuChildren.length === 1 ? '' : 's'}`
                                    : undefined}
                                  selected={selection?.type === 'dcu' && selection.id === dcuSerial}
                                  expanded={dcuChildren.length > 0 ? dcuExp : undefined}
                                  onToggle={dcuChildren.length > 0
                                    ? () => toggleDcu(dcuSerial)
                                    : undefined}
                                  onClick={() => setSelection({ type: 'dcu', id: dcuSerial, transformer_id: t.id })}
                                />
                                {dcuExp && dcuChildren.length < 100 && dcuChildren.map(a => (
                                  <div key={a.id} style={{ marginLeft: '20px', marginTop: '2px' }}>
                                    <TreeNode
                                      icon="•"
                                      emoji={a.role === 'main' ? '🟧' : '🔌'}
                                      label={a.object_name || a.meter_serial}
                                      sub={a.meter_serial}
                                      selected={selection?.type === 'meter' && selection.id === a.id}
                                      onClick={() => setSelection({ type: 'meter', id: a.id })}
                                    />
                                  </div>
                                ))}
                                {dcuExp && dcuChildren.length >= 100 && (
                                  <div style={{ marginLeft: '20px', marginTop: '2px' }}>
                                    <VirtualMeterList
                                      items={dcuChildren}
                                      itemHeight={28}
                                      maxHeight={Math.min(420, dcuChildren.length * 28 + 4)}
                                      renderItem={(a) => (
                                        <TreeNode
                                          icon="•"
                                          emoji={a.role === 'main' ? '🟧' : '🔌'}
                                          label={a.object_name || a.meter_serial}
                                          sub={a.meter_serial}
                                          selected={selection?.type === 'meter' && selection.id === a.id}
                                          onClick={() => setSelection({ type: 'meter', id: a.id })}
                                        />
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                              );
                            })}
                            {directs.length < 100 && directs.map(a => (
                              <div key={a.id} style={{ marginLeft: '20px', marginTop: '2px' }}>
                                <TreeNode
                                  icon="•"
                                  emoji={a.role === 'main' ? '🟧' : '🔌'}
                                  label={a.object_name || a.meter_serial}
                                  sub={a.meter_serial}
                                  selected={selection?.type === 'meter' && selection.id === a.id}
                                  onClick={() => setSelection({ type: 'meter', id: a.id })}
                                />
                              </div>
                            ))}
                            {directs.length >= 100 && (
                              <div style={{ marginLeft: '20px', marginTop: '2px' }}>
                                <VirtualMeterList
                                  items={directs}
                                  itemHeight={28}
                                  maxHeight={Math.min(420, directs.length * 28 + 4)}
                                  renderItem={(a) => (
                                    <TreeNode
                                      icon="•"
                                      emoji={a.role === 'main' ? '🟧' : '🔌'}
                                      label={a.object_name || a.meter_serial}
                                      sub={a.meter_serial}
                                      selected={selection?.type === 'meter' && selection.id === a.id}
                                      onClick={() => setSelection({ type: 'meter', id: a.id })}
                                    />
                                  )}
                                />
                              </div>
                            )}
                            {tAssigns.length === 0 && (
                              <div style={{
                                marginLeft: '20px',
                                marginTop: '2px',
                                fontSize: '11px',
                                color: c.textDimmed,
                                fontStyle: 'italic',
                              }}>
                                no meters
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  {isExp && cTransformers.length === 0 && (
                    <div style={{
                      marginLeft: '40px',
                      marginTop: '2px',
                      fontSize: '11px',
                      color: c.textDimmed,
                      fontStyle: 'italic',
                    }}>
                      no transformers
                    </div>
                  )}
                </div>
              );
            })}

            {unassignedMeters.length > 0 && (
              <div style={{
                marginTop: '16px',
                paddingTop: '12px',
                borderTop: `1px solid ${c.border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '11px', color: c.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Unassigned ({unassignedMeters.length})
                  </div>
                  {isAdmin && (
                    <>
                      <label style={{ fontSize: '10px', color: c.textMuted, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginLeft: 'auto' }}>
                        <input type="checkbox"
                          checked={unassignedMeters.length > 0 && selectedUnassigned.size === unassignedMeters.length}
                          onChange={toggleAllUnassigned} />
                        select all
                      </label>
                      <button
                        disabled={selectedUnassigned.size === 0 || bulkDeleting}
                        onClick={bulkDeleteUnassigned}
                        title="Soft-delete the selected unassigned devices (they move to the Delete Manager)"
                        style={{ fontSize: '10px', padding: '3px 9px', borderRadius: '4px', border: 'none',
                          background: (selectedUnassigned.size === 0 || bulkDeleting) ? c.border : '#dc2626',
                          color: '#fff', cursor: (selectedUnassigned.size === 0 || bulkDeleting) ? 'default' : 'pointer' }}>
                        {bulkDeleting ? '…' : `🗑 Delete Selected (${selectedUnassigned.size})`}
                      </button>
                    </>
                  )}
                </div>
                {unassignedMeters.length < 100 && unassignedMeters.map(m => {
                  const s = m.serial_number || m.serial;
                  return (
                  <TreeNode
                    key={s}
                    icon="•"
                    emoji="🔌"
                    label={s}
                    sub={m.device_type === 'dcu' ? 'DCU' : m.status}
                    selected={selection?.type === 'unassigned' && selection.id === s}
                    onCheck={isAdmin ? () => toggleUnassigned(s) : undefined}
                    checkChecked={selectedUnassigned.has(s)}
                    onClick={() => setSelection({ type: 'unassigned', id: s, meter: m })}
                  />
                  );
                })}
                {unassignedMeters.length >= 100 && (
                  <VirtualMeterList
                    items={unassignedMeters}
                    itemHeight={28}
                    maxHeight={Math.min(420, unassignedMeters.length * 28 + 4)}
                    renderItem={(m) => {
                      const s = m.serial_number || m.serial;
                      return (
                      <TreeNode
                        icon="•"
                        emoji="🔌"
                        label={s}
                        sub={m.device_type === 'dcu' ? 'DCU' : m.status}
                        selected={selection?.type === 'unassigned' && selection.id === s}
                        onCheck={isAdmin ? () => toggleUnassigned(s) : undefined}
                        checkChecked={selectedUnassigned.has(s)}
                        onClick={() => setSelection({ type: 'unassigned', id: s, meter: m })}
                      />
                      );
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* ─── Detail column ─── */}
          {/* On mobile this is hidden until a node is picked, so the tree
              gets the whole screen instead of sharing it with an empty
              placeholder. */}
          <div style={{
            display: (isMobile && !showDetailOnMobile) ? 'none' : undefined,
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: '8px',
            padding: isMobile ? '14px' : '20px',
            minHeight: isMobile ? 'auto' : '60vh',
          }}>
            {/* Back to the tree. The only other way out on a phone would be
                scrolling blindly, since the tree is display:none rather than
                merely off-screen. */}
            {showDetailOnMobile && (
              <button
                onClick={() => setSelection(null)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'transparent', border: `1px solid ${c.border}`,
                  color: c.accent, borderRadius: '8px',
                  padding: '9px 13px', marginBottom: '14px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  minHeight: '44px',
                }}
              >
                {t('btn.back_to_list')}
              </button>
            )}
            {/* Key-by-selection: React only preserves internal state
                (sub-tab, fetched data, form inputs, etc.) across renders
                when the component instance is reused. Using the
                selection's identity as the key forces a full unmount +
                remount every time the user clicks a different node in
                the tree — so switching from meter A to meter B (or
                from a customer to a DCU, etc.) resets every sub-tab
                and data-fetch back to the default/fresh state instead
                of showing stale data from the previous selection. */}
            {(!selection || selection?.type === 'customer' || selection?.type === 'transformer' || selection?.type === 'dcu') && (
              <SubscriberTabbedView
                key={`subs:${selection?.type || 'all'}:${selection?.id ?? ''}`}
                selection={selection || { type: 'all' }}
                customers={customers}
                transformers={transformers}
                assignments={assignments}
                inboundMeters={inboundMeters}
                isAdmin={isAdmin}
                onChanged={refresh}
              />
            )}
            {selection?.type === 'meter' && (
              <MeterDetailView
                key={`meter:${selection.id}`}
                assignment={hierarchyIndex.assignmentById.get(selection.id)}
                inboundMeters={inboundMeters}
                customers={customers}
                transformers={transformers}
                isAdmin={isAdmin}
                onChanged={refresh}
              />
            )}
            {selection?.type === 'unassigned' && (
              <UnassignedMeterDetail
                key={`unassigned:${selection.meter?.serial_number || selection.meter?.serial || ''}`}
                meter={selection.meter}
                customers={customers}
                transformers={transformers}
                isAdmin={isAdmin}
                /* Keep the operator where they were. Clearing the selection
                   here sent them back to All Customers every time they
                   assigned a meter — the same "my view moved" complaint as
                   the Refresh jump above. The tree re-renders from the
                   refreshed data on its own; the selection is by id and stays
                   valid. */
                onAssigned={() => { refresh(); }}
              />
            )}
          </div>
        </div>
      )}

      {/* ─── Modals ─── */}
      {showAddCustomer && (
        <AddCustomerModal
          onClose={() => setShowAddCustomer(false)}
          onSaved={() => { refresh(); setShowAddCustomer(false); }}
        />
      )}
      {showAddTransformer && (
        <AddTransformerModal
          customers={customers}
          defaultCustomerId={selection?.type === 'customer' ? selection.id : null}
          onClose={() => setShowAddTransformer(false)}
          onSaved={(created) => {
            // Auto-expand the customer the new transformer landed under
            // so the user actually SEES the row they just created —
            // otherwise the tree stays collapsed and "nothing happened".
            if (created && created.customer_id != null) {
              setExpandedCustomers(prev => new Set(prev).add(created.customer_id));
            }
            refresh();
            setShowAddTransformer(false);
          }}
        />
      )}
      {showAssignMeter && (
        <AssignMeterModal
          transformers={transformers}
          customers={customers}
          inboundMeters={inboundMeters}
          assignedSerials={assignedSerials}
          defaultTransformerId={selection?.type === 'transformer' ? selection.id : null}
          onClose={() => setShowAssignMeter(false)}
          onSaved={() => { refresh(); setShowAssignMeter(false); }}
        />
      )}
      {showAssignDcu && (
        <AssignDcuModal
          transformers={transformers}
          customers={customers}
          inboundMeters={inboundMeters}
          assignedSerials={assignedSerials}
          defaultTransformerId={selection?.type === 'transformer' ? selection.id : null}
          onClose={() => setShowAssignDcu(false)}
          onSaved={() => { refresh(); setShowAssignDcu(false); }}
        />
      )}
    </div>
  );
}

// Tiny shared tree-node row used by HierarchyTab.
function TreeNode({ icon, emoji, label, sub, selected, onClick, onToggle, expanded, onCheck, checkChecked }) {
  const c = useThemeColors();
  // When `onToggle` is provided, the leading glyph becomes a real
  // toggle button instead of being part of the clickable row. This
  // splits two previously-conflated behaviours (select + expand)
  // so operators can click the row body to drill into details
  // without losing their place in the tree, and click the chevron
  // only when they want to collapse/expand a subtree.
  const expandable = typeof onToggle === 'function';
  // Filled triangles read as obviously-clickable buttons; the lighter
  // ▾/▸ glyphs were too easy to mistake for static decoration.
  const chevron = expandable ? (expanded ? '▼' : '▶') : icon;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 8px',
        borderRadius: '4px',
        background: selected ? `${c.accent}1f` : 'transparent',
        borderLeft: selected ? `3px solid ${c.accent}` : '3px solid transparent',
        marginLeft: '-3px',
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = c.bgElevated; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      {expandable ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          title={expanded ? 'Collapse' : 'Expand'}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={!!expanded}
          style={{
            width: '28px',
            height: '28px',
            padding: 0,
            border: `1px solid ${c.border}`,
            background: c.bgElevated,
            color: c.textPrimary,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            lineHeight: 1,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = c.accent; e.currentTarget.style.color = c.bgBase; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = c.bgElevated; e.currentTarget.style.color = c.textPrimary; }}
        >
          {chevron}
        </button>
      ) : (
        // Match the expandable button's footprint (28px) so leaf rows
        // stay aligned with parent rows.
        <span style={{ color: c.textDimmed, width: '28px', fontSize: '12px', textAlign: 'center', flexShrink: 0 }}>{chevron}</span>
      )}
      {typeof onCheck === 'function' && (
        <input
          type="checkbox"
          checked={!!checkChecked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); onCheck(); }}
          title="Select for bulk delete"
          style={{ flexShrink: 0, cursor: 'pointer' }}
        />
      )}
      <div
        onClick={onClick}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        <span style={{ fontSize: '14px' }}>{emoji}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: selected ? c.accent : c.textPrimary,
            fontWeight: selected ? 600 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {label}
          </div>
          {sub && (
            <div style={{
              fontSize: '11px',
              color: c.textMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {sub}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// All-Customers Aggregate View
//
// Top-level "AMI Home" dashboard that renders three consolidated
// tables across every customer the user can see — modelled after
// the CAS/Kaifa reference product's root-level Home page.
//
// Layout:
//   [ Header: total meters, online, consumption, collection % ]
//   [ Billing Data Collection table | Status Summary table ]
//   [ Consumption table spanning full width ]
//
// Data source: single GET /api/customers/aggregate call returns
// everything in one round-trip (customers rolled up with status,
// billing coverage, yesterday's consumption delta, and a totals
// row).
// ─────────────────────────────────────────────────────────────
function AllCustomersAggregateView() {
  const c = useThemeColors();
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = () => {
    setLoading(true);
    api('/customers-aggregate').then(d => {
      setData(d);
    }).catch(err => toast.push(`Aggregate load failed: ${err.message}`, 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading && !data) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: c.textMuted }}>
        <Spinner /> Loading aggregate dashboard…
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: c.danger }}>
        Failed to load aggregate data.
      </div>
    );
  }

  const pctColor = (pct) => pct == null ? c.textMuted : pct >= 90 ? c.success : pct >= 50 ? c.warning : c.danger;
  const totals = data.totals || {};

  return (
    <div>
      {/* ── Page header: breadcrumb + system totals ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: '12px',
        marginBottom: '14px',
      }}>
        <div>
          <div style={{ fontSize: '13px', color: c.textMuted }}>All Customers · AMI Home</div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>
            📊 Consolidated Dashboard
            <span style={{
              marginLeft: '10px',
              fontSize: '11px',
              background: '#1e40af',
              color: c.textPrimary,
              padding: '2px 8px',
              borderRadius: '10px',
              fontWeight: 500,
            }}>
              TR Count: {(data.customers || []).length}
            </span>
          </div>
        </div>
        <button className="button button-secondary" onClick={load}>
          🔄 Refresh
        </button>
      </div>

      {/* ── KPI strip ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '10px',
        marginBottom: '14px',
      }}>
        <KpiTile label="Total meters"        value={totals.meter_count}             color={c.accent} />
        <KpiTile label="Installed"           value={totals.installed}               color={c.accent} />
        <KpiTile label="Commissioned"        value={totals.commissioned}            color={c.accentSecondary} />
        <KpiTile label="On (live session)"   value={totals.on}                      color={c.success} />
        <KpiTile label="Off"                 value={totals.off}                     color={c.danger} />
        <KpiTile label="No Relay"            value={totals.no_relay}                color={c.textMuted} />
        <KpiTile label="Daily coverage"      value={totals.daily_pct != null ? `${totals.daily_pct}%` : '—'} color={pctColor(totals.daily_pct)} />
        <KpiTile label="Load coverage"       value={totals.load_pct != null ? `${totals.load_pct}%` : '—'}  color={pctColor(totals.load_pct)} />
        <KpiTile label={`Consumption (${data.window?.consumption_day || 'yday'})`}
                 value={totals.consumption_kwh != null ? `${(totals.consumption_kwh).toLocaleString(undefined, {maximumFractionDigits: 3})} kWh` : '—'}
                 color={c.warning} />
      </div>

      {/* ── Two-column layout: Billing Data Collection + Status Summary ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
        gap: '12px',
        marginBottom: '14px',
      }}>
        <AggSection title="Billing Data Collection" icon="📥">
          <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
            <table style={{ fontSize: '11px', width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
                <tr>
                  <th>{t('common.customer')}</th>
                  <th style={{ textAlign: 'right' }}>Meters</th>
                  <th style={{ textAlign: 'right' }}>Load Profile</th>
                  <th style={{ textAlign: 'right' }}>Daily Data</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `2px solid ${c.border}`, fontWeight: 700, background: c.bgCardAlt }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{totals.meter_count ?? 0}</td>
                  <td style={{ textAlign: 'right', color: pctColor(totals.load_pct) }}>
                    {totals.load_pct != null ? `${totals.load_pct}%` : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: pctColor(totals.daily_pct) }}>
                    {totals.daily_pct != null ? `${totals.daily_pct}%` : '—'}
                  </td>
                </tr>
                {(data.customers || []).map(cust => (
                  <tr key={cust.customer_id}>
                    <td>
                      <div>{cust.name}</div>
                      {cust.account_number && (
                        <div style={{ color: c.textMuted, fontSize: '10px' }}>{cust.account_number}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{cust.meter_count}</td>
                    <td style={{ textAlign: 'right', color: pctColor(cust.billing?.load_profile?.pct) }}>
                      {cust.billing?.load_profile?.pct != null ? `${cust.billing.load_profile.pct}%` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: pctColor(cust.billing?.daily_billing?.pct) }}>
                      {cust.billing?.daily_billing?.pct != null ? `${cust.billing.daily_billing.pct}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AggSection>

        <AggSection title="Status Summary (Device Status)" icon="🧰">
          <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
            <table style={{ fontSize: '11px', width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
                <tr>
                  <th>{t('common.customer')}</th>
                  <th style={{ textAlign: 'right' }}>Inst.</th>
                  <th style={{ textAlign: 'right' }}>Comm.</th>
                  <th style={{ textAlign: 'right' }}>On</th>
                  <th style={{ textAlign: 'right' }}>Off</th>
                  <th style={{ textAlign: 'right' }}>No Relay</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: `2px solid ${c.border}`, fontWeight: 700, background: c.bgCardAlt }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{totals.installed ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>{totals.commissioned ?? 0}</td>
                  <td style={{ textAlign: 'right', color: c.success }}>{totals.on ?? 0}</td>
                  <td style={{ textAlign: 'right', color: c.danger }}>{totals.off ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>{totals.no_relay ?? 0}</td>
                </tr>
                {(data.customers || []).map(cust => (
                  <tr key={cust.customer_id}>
                    <td>{cust.name}</td>
                    <td style={{ textAlign: 'right' }}>{cust.status?.installed ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{cust.status?.commissioned ?? 0}</td>
                    <td style={{ textAlign: 'right', color: c.success }}>{cust.status?.on ?? 0}</td>
                    <td style={{ textAlign: 'right', color: c.danger }}>{cust.status?.off ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{cust.status?.no_relay ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AggSection>
      </div>

      {/* ── Full-width Consumption table ── */}
      <AggSection title={`Consumption (${data.window?.consumption_day || 'yesterday'})`} icon="⚡">
        <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
          <table style={{ fontSize: '11px', width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
              <tr>
                <th>{t('common.customer')}</th>
                <th style={{ textAlign: 'right' }}>Meters</th>
                <th style={{ textAlign: 'right' }}>Daily Consumption (kWh)</th>
                <th style={{ textAlign: 'right' }}>PF</th>
                <th style={{ textAlign: 'right' }}>Daily Line Loss (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: `2px solid ${c.border}`, fontWeight: 700, background: c.bgCardAlt }}>
                <td>Total</td>
                <td style={{ textAlign: 'right' }}>{totals.meter_count ?? 0}</td>
                <td style={{ textAlign: 'right' }}>
                  {totals.consumption_kwh != null ? totals.consumption_kwh.toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—'}
                </td>
                <td style={{ textAlign: 'right', color: c.textMuted }}>—</td>
                <td style={{ textAlign: 'right', color: c.textMuted }}>—</td>
              </tr>
              {(data.customers || []).map(cust => (
                <tr key={cust.customer_id}>
                  <td>{cust.name}</td>
                  <td style={{ textAlign: 'right' }}>{cust.meter_count}</td>
                  <td style={{ textAlign: 'right' }}>
                    {cust.consumption_kwh != null ? cust.consumption_kwh.toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: c.textMuted }}>—</td>
                  <td style={{ textAlign: 'right', color: c.textMuted }}>—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '10px', color: c.textMuted, marginTop: '6px' }}>
          Consumption = (yesterday's +A register − day-before-yesterday's +A register) summed
          across all meters under each customer, pulled from the local readings store. PF and
          Daily Line Loss — work in progress, will be added soon.
        </div>
      </AggSection>
    </div>
  );
}

// GPRS signal as the DCU reports it. 0.0.96.12.5.255 is the modem's CSQ
// (0–31, 99 = unknown; dBm = −113 + 2×CSQ), NOT dBm: all 44 values across the
// fleet on 2026-09-15 fell in 4–31. It used to render as "4 dBm", which reads
// as a strong signal when 4 is about −105 dBm — nearly no link at all.
//
// A MISSING value was blamed on the health probe being disabled. Wrong: the
// connect-time onboarding reads this object on every DCU, probe or not (37 of
// the 39 connected DCUs NOT enrolled in the probe had a value). It is missing
// because either this session never ran the connect-time read — a session
// re-opened by the keepalive skips onboarding (129245049876, 2026-09-15) — or
// the read failed/timed out or returned a desynced value the API rejected.
// Both are fixed by Refresh Status, so say that instead.
function dcuSignalInfo(st) {
  const v = st?.signal_strength;
  const n = (typeof v === 'number') ? v : (v != null && v !== '' && !isNaN(Number(v)) ? Number(v) : null);
  if (n == null) {
    if (!st || st.connected === false || !st.session_open) {
      return { short: '—', long: '—', color: '#64748b', title: 'No open session — signal is read when the DCU connects' };
    }
    const why = st.cached_at
      ? `last read at ${fmtDT(st.cached_at, { utc: true, seconds: true })} failed or returned an unusable value`
      : 'not read on this session (it was re-opened without the connect-time status read)';
    return { short: 'not read', long: `${why}; use Refresh Status`, color: '#f59e0b', title: `Signal ${why}. Refresh Status reads it from the DCU now.` };
  }
  if (n === 99) return { short: 'unknown', long: 'unknown (modem reports 99 — no measurement)', color: '#f59e0b', title: 'CSQ 99: the modem has no signal measurement' };
  if (n < 0 || n > 31) return { short: String(n), long: `${n} (outside CSQ 0–31)`, color: '#f59e0b', title: 'Unexpected value — not a CSQ reading' };
  const dbm = -113 + 2 * n;
  const [word, color] = n <= 1 ? ['no signal', '#ef4444'] : n < 10 ? ['weak', '#ef4444'] : n < 15 ? ['fair', '#f59e0b'] : n < 20 ? ['good', '#22c55e'] : ['excellent', '#22c55e'];
  return { short: `${n}/31 · ${word}`, long: `${n}/31 (≈ ${dbm} dBm, ${word})`, color, title: `CSQ ${n} of 31 ≈ ${dbm} dBm — ${word}` };
}

// Small KPI tile used by AllCustomersAggregateView header strip.
function KpiTile({ label, value, color }) {
  const c = useThemeColors();
  return (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '6px',
      padding: '10px 12px',
      borderLeft: `3px solid ${color || c.accent}`,
    }}>
      <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      {/* String() rather than the raw value: React renders an ARRAY child by
          concatenating its elements with NO separator, so a desynced DLMS
          structure arriving here as [1, '00002a0000ff', 2, 0, 8, ...] rendered
          as "100002a0000ff2080000..." — the same value the header showed
          comma-separated, because that site interpolated it into a template
          literal instead. Measured 2026-09-03 on DCU 129235042419.
          overflowWrap because an unbroken ~200-char token in an 18px bold div
          has no break opportunity and pushed the whole page into horizontal
          overflow, clipping the customer tree. The API now rejects non-scalar
          readings upstream; this keeps ANY long value inside its tile. */}
      <div style={{ fontSize: '18px', fontWeight: 700, color: color || c.textPrimary, marginTop: '2px',
                    overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        {value == null ? '—'
          : (typeof value === 'object' && !React.isValidElement(value)) ? String(value)
          : value}
      </div>
    </div>
  );
}

// Card container used for each of the three aggregate tables.
function AggSection({ title, icon, children }) {
  const c = useThemeColors();
  return (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '14px',
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '10px' }}>
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subscriber Tabbed View
//
// The reference CAS/Kaifa AMI product uses the SAME six tabs on
// both the subscriber (customer / transformer) detail page and the
// meter detail page — Home, Event, Meter Readings, Relay Control,
// Collection Status, Installation Status — but the content of each
// tab is scoped to whatever's selected in the tree.
//
// This component is the subscriber-side counterpart of
// MeterDetailView (defined further down). It renders the same
// tab bar and routes each tab to a subscriber-specific sub-view:
//   Home           -> existing CustomerDetail / TransformerDetail
//   Event          -> aggregate events placeholder (per-meter drill-down)
//   Meter Readings -> flat table of child meters with their latest
//                     daily_billing row
//   Relay Control  -> N/A for subscriber context
//   Collection     -> /api/customers/{id}/polling-stats summary +
//                     per-meter coverage table
//   Installation   -> Status Summary breakdown (count by meter type
//                     × online/off/relay state), matches the
//                     reference product's "Device Status" card
//   Tariff         -> N/A (per-meter only)
// ─────────────────────────────────────────────────────────────
function SubscriberTabbedView({
  selection,
  customers,
  transformers,
  assignments,
  inboundMeters,
  isAdmin,
  onChanged,
}) {
  const c = useThemeColors();
  const t = useT();
  const { user } = useAuth();
  // Relay is available to admins AND operators (operators scoped to their
  // assigned meters server-side — /relay/meters + the submit endpoints filter
  // by the user's customer/transformer/meter access). Network config and DCU
  // restart stay admin-only.
  const canRelay = userCan(user, 'relay_control');
  const [sub, setSub] = useState('home');
  // When the operator switches between contexts (e.g. picks a meter
  // and then jumps back to the aggregate), snap the sub-tab back to
  // Home if the currently-active tab isn't valid in the new context.
  // For the all-customers view, Event / Relay / Tariff are hidden.
  useEffect(() => {
    const hidden = selection?.type === 'all'
      ? new Set(['event', 'relay', 'tariff'])
      : new Set();
    if (hidden.has(sub)) setSub('home');
    /* eslint-disable-next-line */
  }, [selection?.type]);
  const tabBtn = (active) => ({
    padding: '8px 14px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });

  // Resolve the scope (list of MeterAssignment rows under this node).
  // 'all'         -> every visible assignment (consolidated dashboard)
  // 'customer'    -> all assignments via transformer join.
  // 'transformer' -> just its assignments.
  // 'dcu'         -> assignments mapped to this dcu_serial.
  const scopedAssignments = React.useMemo(() => {
    // Always exclude DCU self-assignments (where meter_serial === dcu_serial)
    // — the DCU device itself is not a meter and shouldn't appear in meter lists.
    const excludeDcuSelf = (list) => list.filter(a => !(a.dcu_serial && a.meter_serial === a.dcu_serial));
    if (!selection) return [];
    if (selection.type === 'all') return excludeDcuSelf(assignments);
    if (selection.type === 'customer') {
      const tIds = new Set(transformers.filter(t => t.customer_id === selection.id).map(t => t.id));
      return excludeDcuSelf(assignments.filter(a => tIds.has(a.transformer_id)));
    }
    if (selection.type === 'transformer') {
      return excludeDcuSelf(assignments.filter(a => a.transformer_id === selection.id));
    }
    if (selection.type === 'dcu') {
      return excludeDcuSelf(assignments.filter(a => a.dcu_serial === selection.id));
    }
    return [];
  }, [selection, assignments, transformers]);

  // Titles / badges for each selection kind.
  let title = 'Subscriber';
  let icon = '👤';
  if (selection?.type === 'all') {
    title = 'All Customers · AMI Home';
    icon = '📊';
  } else if (selection?.type === 'customer') {
    const cust = customers.find(x => x.id === selection.id);
    title = cust?.name || 'Customer';
    icon = '👤';
  } else if (selection?.type === 'transformer') {
    const t = transformers.find(x => x.id === selection.id);
    title = t?.name || 'Transformer';
    icon = '⚡';
  } else if (selection?.type === 'dcu') {
    title = `DCU ${selection.id}`;
    icon = '🛰️';
  }

  return (
    <div>
      {/* Header */}
      <div style={{ fontSize: '20px', fontWeight: 600, marginBottom: '12px' }}>
        {icon} {title}{' '}
        {/* Real space + not selectable: double-clicking the serial must not
            pick up the meter count glued to it ("1292050308242"). */}
        <span style={{ fontSize: '12px', color: c.textMuted, marginLeft: '10px', fontWeight: 400, userSelect: 'none' }}>
          {scopedAssignments.length} meter{scopedAssignments.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Sub-tab bar — Event / Relay / Tariff are inherently
          per-meter actions, so they're hidden when the user is
          viewing the aggregate "all customers" landing. They
          re-appear as soon as the user picks a customer,
          transformer, DCU or meter. */}
      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: `1px solid ${c.border}`,
        flexWrap: 'wrap',
      }}>
        <button style={tabBtn(sub === 'home')}         onClick={() => setSub('home')}        >🏠 {t('tab.home')}</button>
        {selection?.type !== 'all' && (
          <button style={tabBtn(sub === 'event')}        onClick={() => setSub('event')}       >⚡ {t('tab.event')}</button>
        )}
        <button style={tabBtn(sub === 'readings')}     onClick={() => setSub('readings')}    >📊 {t('tab.readings')}</button>
        {selection?.type !== 'all' && selection?.type !== 'dcu' && (
          <button style={tabBtn(sub === 'relay')}        onClick={() => setSub('relay')}       >⚡ {t('tab.relay')}</button>
        )}
        <button style={tabBtn(sub === 'collection')}   onClick={() => setSub('collection')}  >📥 {t('tab.collection')}</button>
        <button style={tabBtn(sub === 'installation')} onClick={() => setSub('installation')}>🧰 {t('tab.installation')}</button>
        {selection?.type !== 'all' && selection?.type !== 'dcu' && (
          <button style={tabBtn(sub === 'tariff')}       onClick={() => setSub('tariff')}      >💰 {t('tab.tariff')}</button>
        )}
        {/* DCU-only top-level action. Bulk Relay is open to operators (scoped
            to their meters). Network Config + Restart were MOVED to Device
            Management → DCU (admin-only) and no longer appear in the customer
            tree. */}
        {selection?.type === 'dcu' && canRelay && (
          <button style={tabBtn(sub === 'bulk_relay')} onClick={() => setSub('bulk_relay')}  >⚡ Bulk Relay</button>
        )}
      </div>

      {sub === 'home' && (
        <SubscriberHomePane
          selection={selection}
          customers={customers}
          transformers={transformers}
          assignments={assignments}
          inboundMeters={inboundMeters}
          isAdmin={isAdmin}
          onChanged={onChanged}
        />
      )}

      {sub === 'event' && (
        <SubscriberEventsPane scopedAssignments={scopedAssignments} selection={selection} />
      )}

      {sub === 'readings' && (
        <SubscriberReadingsPane
          scopedAssignments={scopedAssignments}
          inboundMeters={inboundMeters}
        />
      )}

      {sub === 'relay' && (
        <div style={{
          padding: '24px', textAlign: 'center', color: c.textMuted,
          background: c.bgCardAlt, border: `1px dashed ${c.border}`, borderRadius: '8px',
        }}>
          Relay control is a per-meter action — select a specific
          meter in the tree to disconnect / reconnect its relay.
        </div>
      )}

      {sub === 'collection' && (
        <SubscriberCollectionPane
          selection={selection}
          scopedAssignments={scopedAssignments}
        />
      )}

      {sub === 'installation' && (
        <SubscriberInstallationPane
          scopedAssignments={scopedAssignments}
          inboundMeters={inboundMeters}
        />
      )}

      {sub === 'tariff' && (
        <div style={{
          padding: '24px', textAlign: 'center', color: c.textMuted,
          background: c.bgCardAlt, border: `1px dashed ${c.border}`, borderRadius: '8px',
        }}>
          Tariff / activity calendar is a per-meter setting — select
          a specific meter in the tree to read or write its calendar.
        </div>
      )}

      {/* DCU Bulk Relay pane. (Network Config + Restart moved to Device
          Management → DCU.) */}
      {selection?.type === 'dcu' && sub === 'bulk_relay' && canRelay && (
        // Fine-grained selectable relay control scoped to this DCU: tick
        // individual meters (shift-click for a range), immediate or deferred
        // delivery, plus this DCU's command history. For operators the
        // /relay/meters + /relay/commands endpoints filter to their assigned
        // meters, so they only ever see/act on their own on this DCU.
        <RelayControlTab dcuSerial={selection.id} />
      )}
    </div>
  );
}

// Compact polling-health card. Used to live as a colour-coded
// badge inside the HierarchyTab tree but cluttered the layout, so
// it now lives at the top of the customer's Home tab. Pulls
// /api/customers/{id}/polling-stats once per mount.
function CustomerCollectionStatsCard({ customerId }) {
  const c = useThemeColors();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    api(`/customers/${customerId}/polling-stats`).then(setStats).catch(() => {});
  }, [customerId]);
  if (!stats) return null;
  const pctColor = (pct) => pct == null ? c.textMuted
    : pct >= 90 ? c.success
    : pct >= 50 ? c.warning
    : c.danger;
  const Bar = ({ label, pct, stored, expected }) => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
        <span style={{ color: c.textSecondary }}>{label}</span>
        <span style={{ color: c.textMuted }}>{stored} / {expected}</span>
      </div>
      <div style={{ background: c.bgCardAlt, borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
        <div style={{
          width: `${pct ?? 0}%`,
          height: '100%',
          background: pctColor(pct),
          transition: 'width 300ms ease',
        }} />
      </div>
      <div style={{ fontSize: '14px', fontWeight: 700, color: pctColor(pct), marginTop: '4px' }}>
        {pct == null ? '—' : `${pct}%`}
      </div>
    </div>
  );
  return (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '14px',
      marginBottom: '14px',
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '12px' }}>
        📥 Collection Health · current month
        <span style={{ marginLeft: '10px', fontSize: '11px', color: c.textMuted, fontWeight: 400 }}>
          {stats.meter_count} meter{stats.meter_count === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <Bar
          label="Daily Billing"
          pct={stats.daily_billing?.pct}
          stored={stats.daily_billing?.stored}
          expected={stats.daily_billing?.expected}
        />
        <Bar
          label="Load Profile"
          pct={stats.load_profile?.pct}
          stored={stats.load_profile?.stored}
          expected={stats.load_profile?.expected}
        />
      </div>
    </div>
  );
}

// Per-customer polling/collection metrics — period filter + per-day rate +
// drill-down. All data comes from the server's STORED profile_rows (SQL), not
// live meter reads — the scheduler/poller already populate it.
function CustomerCollectionDetailModal({ customerId, customerName, date, profile, onClose, scope = 'customers', scopeId }) {
  const _id = scopeId != null ? scopeId : customerId;
  const c = useThemeColors();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all');   // all | collected | uncollected
  const [search, setSearch] = useState('');
  useEffect(() => {
    setData(null); setErr(null);
    api(`/${scope}/${_id}/collection-metrics/${date}?profile=${profile}`)
      .then(setData).catch(e => setErr(e.message));
  }, [scope, _id, date, profile]);
  const meters = data?.meters || [];
  const filtered = meters.filter(m => {
    if (filter === 'collected' && !m.collected) return false;
    if (filter === 'uncollected' && m.collected) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      if (!String(m.serial).toLowerCase().includes(q) && !String(m.name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const th = { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${c.border}`, fontSize: '11px', color: c.textMuted, textTransform: 'uppercase', position: 'sticky', top: 0, background: c.bgCard };
  const td = { padding: '6px 8px', borderBottom: `1px solid ${c.border}22`, fontSize: '12px' };
  const pill = (label, active, count, color) => (
    <button onClick={() => setFilter(active)} style={{
      padding: '4px 12px', fontSize: '12px', borderRadius: '14px', cursor: 'pointer', fontWeight: 600,
      border: `1px solid ${filter === active ? color : c.border}`,
      background: filter === active ? color : 'transparent', color: filter === active ? '#fff' : c.textMuted,
    }}>{label}{count != null ? ` (${count})` : ''}</button>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '18px', width: '940px', maxWidth: '95vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '15px', fontWeight: 600 }}>Collection Detail — {customerName} — {date}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: c.textMuted, fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          {pill('All', 'all', data?.total, '#1e40af')}
          {pill('Collected', 'collected', data?.collected_count, '#16a34a')}
          {pill('Uncollected', 'uncollected', data?.uncollected_count, '#dc2626')}
          <input placeholder="Meter No / name" value={search} onChange={e => setSearch(e.target.value)}
            style={{ marginLeft: 'auto', padding: '5px 10px', fontSize: '12px', background: c.bgInput || c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px', width: '180px' }} />
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          {err ? <div style={{ color: c.danger, fontSize: '12px', padding: '10px' }}>{err}</div>
            : !data ? <div style={{ color: c.textMuted, padding: '10px' }}><Spinner /></div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>#</th><th style={th}>Meter No</th><th style={th}>Meter Type</th>
                    <th style={th}>Device Type</th><th style={th}>DCU No</th><th style={th}>Customer</th>
                    <th style={th}>Meter Name</th><th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m, i) => (
                    <tr key={m.connection_serial || m.serial}>
                      <td style={{ ...td, color: c.textMuted }}>{i + 1}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>{m.serial}</td>
                      <td style={td}>{m.meter_type || '—'}</td>
                      <td style={td}>{m.device_type}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{m.dcu || '—'}</td>
                      <td style={td}>{m.customer}</td>
                      <td style={td}>{m.name}</td>
                      <td style={td}>
                        <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', color: '#fff', background: m.collected ? '#16a34a' : '#dc2626' }}>
                          {m.collected ? 'Collected' : 'Uncollected'}
                        </span>
                        <span style={{ marginLeft: '6px', fontSize: '11px', color: m.online ? '#22c55e' : c.textMuted }}>
                          {m.online ? '● online' : '○ offline'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td style={{ ...td, color: c.textMuted }} colSpan={8}>No meters match.</td></tr>}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

// Per-day collection-rate card. Originally customer-only; now also used at the
// TRANSFORMER and DCU levels, because a per-meter OK/Partial/Fail badge cannot tell an
// operator WHEN collection degraded — a per-day rate makes a step change
// visible on the day it happened. Both scopes hit identically-computed
// endpoints (same denominator: every registered downstream meter).
function CustomerPollingMetricsCard({ customerId, customerName, scope = 'customers', scopeId, scopeName }) {
  const _id = scopeId != null ? scopeId : customerId;
  const _name = scopeName != null ? scopeName : customerName;
  const c = useThemeColors();
  const toast = useToast();
  const isoD = (d) => d.toISOString().slice(0, 10);
  const [from, setFrom] = useState(isoD(new Date(Date.now() - 13 * 86400000)));
  const [to, setTo] = useState(isoD(new Date()));
  const [profile, setProfile] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailDate, setDetailDate] = useState(null);

  const load = React.useCallback(async () => {
    if (!_id) return;
    setLoading(true);
    try {
      const d = await api(`/${scope}/${_id}/collection-metrics?from_date=${from}&to_date=${to}&profile=${profile}`);
      setData(d);
    } catch (e) { toast.push(`Polling metrics failed: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [scope, _id, from, to, profile, toast]);

  // Load once on mount for the default range; Search re-runs with new filters.
  const didLoad = React.useRef(false);
  useEffect(() => { didLoad.current = false; }, [scope, _id]);
  useEffect(() => { if (_id && !didLoad.current) { didLoad.current = true; load(); } }, [_id, load]);

  const rateColor = (p) => p >= 95 ? '#22c55e' : p >= 80 ? '#f59e0b' : '#ef4444';
  const days = data?.days || [];
  const PROFILES = [['daily', 'Daily Billing'], ['monthly', 'Monthly Billing'], ['load', 'Load Profile'], ['pq', 'Power Quality']];
  const inp = { padding: '5px 8px', fontSize: '12px', background: c.bgInput || c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px' };
  const th = { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${c.border}`, fontSize: '11px', color: c.textMuted, textTransform: 'uppercase' };
  const td = { padding: '7px 8px', borderBottom: `1px solid ${c.border}22`, fontSize: '12px' };

  return (
    <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '10px' }}>
        📊 Polling Metrics — daily collection rate
        {_name ? <span style={{ marginLeft: '8px', fontSize: '11px', color: c.textMuted, fontWeight: 400 }}>
          · {scope === 'transformers' ? 'transformer' : scope === 'dcus' ? 'DCU' : 'customer'} {_name}</span> : null}
        {data ? <span style={{ marginLeft: '10px', fontSize: '11px', color: c.textMuted, fontWeight: 400 }}>{data.total_meters} meter{data.total_meters === 1 ? '' : 's'}</span> : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        <select value={profile} onChange={e => setProfile(e.target.value)} style={inp}>
          {PROFILES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} />
        <span style={{ color: c.textMuted }}>~</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} />
        <button style={_dcuBtn('#1e40af', loading)} disabled={loading} onClick={load}>{loading ? 'Loading…' : '🔍 Search'}</button>
      </div>
      {!data ? (
        <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic', padding: '10px' }}>
          {loading ? 'Loading collection metrics…' : 'Pick a period and Search to see the daily collection rate.'}
        </div>
      ) : days.length === 0 ? (
        <div style={{ color: c.textMuted, fontSize: '12px', padding: '10px' }}>No days in range.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>#</th><th style={th}>Date</th><th style={th}>Total Meters</th>
              <th style={th}>Collected</th><th style={{ ...th, width: '180px' }}>Collection Rate</th><th style={th}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => (
              <tr key={d.date}>
                <td style={{ ...td, color: c.textMuted }}>{i + 1}</td>
                <td style={{ ...td, fontWeight: 600 }}>{d.date}</td>
                <td style={td}>{d.total_meters}</td>
                <td style={td}>{d.collected}<span style={{ color: c.textMuted }}> / {d.total_meters}</span></td>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1, background: c.bgCardAlt || `${c.border}33`, borderRadius: '10px', height: '14px', overflow: 'hidden' }}>
                      <div style={{ width: `${d.rate}%`, height: '100%', background: rateColor(d.rate) }} />
                    </div>
                    <span style={{ fontWeight: 700, color: rateColor(d.rate), minWidth: '46px', textAlign: 'right' }}>{d.rate}%</span>
                  </div>
                </td>
                <td style={td}>
                  <button style={{ ..._dcuBtn('#16a34a', false), padding: '3px 12px', fontSize: '11px' }} onClick={() => setDetailDate(d.date)}>Detail</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {detailDate && (
        <CustomerCollectionDetailModal
          scope={scope}
          scopeId={_id}
          customerId={customerId}
          customerName={data?.customer_name || data?.transformer_name || _name}
          date={detailDate}
          profile={profile}
          onClose={() => setDetailDate(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SubscriberCustomerHome — the per-customer Home pane used by the
// Customers tree view. Combines (top to bottom):
//
//   1. Header card  — name, account#, quick KPI strip, edit/delete
//   2. Billing Data Statistics card  (existing CustomerCollectionStatsCard)
//   3. Device Status breakdown by meter type (client-side pivot over
//      the customer's assignments + live meter status)
//   4. Transformers + meters summary table (compact, dense)
//   5. Contact info card
//   6. Notes section (category-tagged timeline, same shape as old
//      CustomerDetail — the notes wiring moved here verbatim)
//
// Replaces the old CustomerDetail call, which was a bland "detail
// grid + transformer bullet list + notes" view that didn't surface
// any of the data operators actually need when they land on a
// subscriber. The new layout matches the CAS reference product's
// subscriber landing page.
// ─────────────────────────────────────────────────────────────
// Which meters under a customer cannot commission, and why.
//
// The "Installed 78 / Commissioned 70" strip says eight meters are stuck but
// not which — and the gap is never one problem. A meter with no GMAC keys can
// never commission however good its link (commissioned_at is stamped the first
// time HLS5 succeeds, and HLS5 needs the auth + block-cipher pair); a meter
// that HAS its keys and still has not commissioned is a comms or registration
// fault, and importing keys for it achieves nothing. So the two groups are
// rendered apart, each with its own copyable serial block — the key-import
// list and the go-look-at-the-link list are different pieces of paper.
function CustomerKeyGapModal({ customer, onClose }) {
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api(`/customers/${customer.id}/key-readiness`)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [customer.id]);

  const copy = (serials, label) => {
    navigator.clipboard?.writeText(serials.join('\n'));
    toast.push(`${serials.length} ${label} ${t('keygap.copied')}`, 'success');
  };

  const group = (title, tone, hint, rows) => {
    if (!rows.length) return null;
    return (
      <div key={title} style={{ marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: tone }}>{title}</span>
          <span style={{ fontSize: '12px', color: c.textMuted }}>{rows.length}</span>
          <span style={{ flex: 1 }} />
          <button className="button button-sm button-secondary"
                  onClick={() => copy(rows.map((r) => r.serial), title)}>
            📋 {t('keygap.copy')}
          </button>
        </div>
        <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '6px' }}>{hint}</div>
        <div style={{ maxHeight: '220px', overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: '6px' }}>
          {rows.map((r) => (
            <div key={r.serial} style={{
              display: 'flex', gap: '10px', alignItems: 'center',
              padding: '5px 10px', borderBottom: `1px solid ${c.border}`,
              fontSize: '12px', flexWrap: 'wrap',
            }}>
              <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{r.serial}</span>
              <span style={{ color: c.textMuted }}>{r.transformer || '—'}</span>
              <span style={{ flex: 1 }} />
              {r.key_state === 'incomplete_gmac' && (
                <span style={{ fontSize: '10px', color: c.warning }}>
                  {r.has_auth_key ? '' : 'no auth key'}
                  {(!r.has_auth_key && !r.has_cipher_key) ? ' · ' : ''}
                  {r.has_cipher_key ? '' : 'no cipher key'}
                </span>
              )}
              {r.commissioned_at && (
                <span style={{ fontSize: '10px', color: c.danger }}>{t('keygap.was_commissioned')}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const blockedNone = (data?.meters || []).filter((m) => m.key_state === 'no_key_record');
  const blockedPart = (data?.meters || []).filter((m) => m.key_state === 'incomplete_gmac');
  const keyedOnly   = (data?.meters || []).filter((m) => !m.blocked_on_keys);

  return (
    <ModalShell title={`🔑 ${t('keygap.title')} — ${customer.name}`} onClose={onClose} width="640px">
      {err && <div style={{ color: c.danger, fontSize: '13px' }}>{err}</div>}
      {!data && !err && <div style={{ color: c.textMuted, fontSize: '13px' }}><Spinner /> {t('common.loading')}</div>}
      {/* An unloaded key store must never render as a worklist: every meter
          would look unkeyed and the operator would re-import keys that are
          already correct. Explicit === false, so an older server that does
          not send the field is not mistaken for a broken one. */}
      {data && data.key_store_loaded === false && (
        <div style={{
          background: c.bgBase, border: `1px solid ${c.danger}`,
          borderRadius: '6px', padding: '12px', fontSize: '13px', color: c.danger,
        }}>
          ⚠️ {data.warning}
        </div>
      )}
      {data && data.key_store_loaded !== false && (
        <>
          <div style={{ fontSize: '13px', color: c.textSecondary, marginBottom: '16px' }}>
            {data.installed} {t('keygap.installed')} · {data.commissioned} {t('keygap.commissioned')} ·{' '}
            <strong style={{ color: data.blocked_on_keys ? c.warning : c.success }}>
              {data.blocked_on_keys} {t('keygap.blocked')}
            </strong>
          </div>
          {group(t('keygap.g_none'), c.danger, t('keygap.g_none_hint'), blockedNone)}
          {group(t('keygap.g_partial'), c.warning, t('keygap.g_partial_hint'), blockedPart)}
          {group(t('keygap.g_keyed'), c.accent, t('keygap.g_keyed_hint'), keyedOnly)}
          {!data.meters.length && (
            <div style={{ color: c.success, fontSize: '13px' }}>{t('keygap.all_clear')}</div>
          )}
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button className="button button-secondary" onClick={onClose}>{t('btn.close')}</button>
      </div>
    </ModalShell>
  );
}

function SubscriberCustomerHome({ customer, transformers, scopedAssignments, inboundMeters, isAdmin, onChanged }) {
  const c = useThemeColors();
  const toast = useToast();
  const t = useT();
  const confirm = useConfirm();
  const { user } = useAuth();
  const canEditNotes = userCan(user, 'customer_notes');

  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [posting, setPosting] = useState(false);
  const [catchupBusy, setCatchupBusy] = useState(false);
  const [showKeyGap, setShowKeyGap] = useState(false);

  useEffect(() => {
    if (!customer) return;
    api(`/customers/${customer.id}/notes`)
      .then(d => setNotes(d.notes || []))
      .catch(err => toast.push(`Notes load failed: ${err.message}`, 'error'));
  }, [customer?.id]);

  if (!customer) return <div style={{ color: c.textMuted }}>Customer not found</div>;

  // Index the live snapshot by serial ONCE. The three passes below used
  // to call inboundMeters.find() per assignment, which is O(assignments ×
  // meters) — on the largest customer (396 meters against a ~1 700-meter
  // fleet) that is ~2M string compares per render, re-run on every
  // /ws/meters tick. Phones simply locked up. Build costs one pass.
  const liveBySerial = new Map();
  for (const m of inboundMeters) {
    const key = m.serial_number || m.serial;
    if (key != null) liveBySerial.set(key, m);
  }

  // Quick KPI counts from the live + assigned state.
  const assignedCount = scopedAssignments.length;
  let onlineCount = 0;
  let offlineCount = 0;
  let commissionedCount = 0;
  let relayOffCount = 0;
  for (const a of scopedAssignments) {
    const live = liveBySerial.get(a.meter_serial);
    if (live?.identified) onlineCount += 1; else offlineCount += 1;
    // Commissioned is a permanent latch on the MeterAssignment row
    // (stamped the first time HLS5 succeeds) — NOT the same as an
    // open session right now. See commissioned_at in metadata.py.
    if (a.commissioned_at) commissionedCount += 1;
    // Relay state lives on the live snapshot when available.
    const relayRaw = live?.relay_state || live?.relay_status;
    if (relayRaw && String(relayRaw).toLowerCase().includes('off')) relayOffCount += 1;
  }

  // Device status breakdown: one row per known meter type code.
  //
  // Installed   = has an assignment (operator placed it in the tree)
  // Commissioned= commissioned_at is non-null (HLS5 has succeeded at
  //               least once, keys are proven to work). Permanent.
  // On          = has an open HLS5 session right now (transient)
  // Off         = commissioned but no live session right now
  // No relay    = meter type doesn't have a disconnect relay (reserved)
  const typeBuckets = {};
  for (const opt of METER_TYPE_OPTIONS) {
    typeBuckets[opt.code] = {
      code: opt.code,
      label: opt.label,
      installed: 0, commissioned: 0, on: 0, off: 0, no_relay: 0,
    };
  }
  typeBuckets.unknown = {
    code: null, label: 'Unknown', installed: 0, commissioned: 0, on: 0, off: 0, no_relay: 0,
  };
  for (const a of scopedAssignments) {
    const live = liveBySerial.get(a.meter_serial);
    const mtCode = a.meter_type?.code ?? live?.meter_type?.code ?? null;
    const bucket = mtCode != null ? typeBuckets[mtCode] : typeBuckets.unknown;
    if (!bucket) continue;
    bucket.installed += 1;
    if (a.commissioned_at) bucket.commissioned += 1;
    if (live?.session_open) bucket.on += 1;
    else if (a.commissioned_at) bucket.off += 1;
  }
  const deviceRows = [...METER_TYPE_OPTIONS.map(o => typeBuckets[o.code]), typeBuckets.unknown]
    .filter(r => r.installed > 0);

  // Transformer summary: meter count + online per transformer.
  const transformerRows = transformers.map(t => {
    const tAssigns = scopedAssignments.filter(a => a.transformer_id === t.id);
    const online = tAssigns.filter(a => !!liveBySerial.get(a.meter_serial)?.identified).length;
    return { t, count: tAssigns.length, online };
  });

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setPosting(true);
    try {
      const n = await api(`/customers/${customer.id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body: newNote.trim(), category: newCategory }),
      });
      setNotes(prev => [n, ...prev]);
      setNewNote('');
      toast.push(t('toast.note_added'), 'success');
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setPosting(false);
    }
  };

  const deleteNote = async (id) => {
    if (!(await confirm(t('msg.confirm_delete'), t('msg.confirm_delete')))) return;
    try {
      await api(`/customers/${customer.id}/notes/${id}`, { method: 'DELETE' });
      setNotes(prev => prev.filter(n => n.id !== id));
      toast.push(t('toast.note_deleted'), 'success');
    } catch (err) {
      toast.push(err.message, 'error');
    }
  };

  const deleteCustomer = async () => {
    if (transformers.length > 0) {
      toast.push(`Cannot delete "${customer.name}" — remove ${transformers.length} transformer(s) first.`, 'error');
      return;
    }
    if (!(await confirm(`Delete "${customer.name}"?`, 'This customer will be permanently removed. This action cannot be undone.'))) return;
    try {
      await api(`/customers/${customer.id}`, { method: 'DELETE' });
      toast.push(t('toast.customer_deleted'), 'success');
      onChanged();
    } catch (err) {
      toast.push(err.message, 'error');
    }
  };

  // Shared card styling.
  const card = {
    background: c.bgCard,
    border: `1px solid ${c.border}`,
    borderRadius: '8px',
    padding: '14px',
    marginBottom: '14px',
  };
  const cardTitle = {
    fontSize: '13px',
    fontWeight: 600,
    color: c.textLabel,
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };
  const miniKpi = (label, value, accent, onClick, hint) => (
    <div
      onClick={onClick}
      title={hint}
      style={{
      background: c.bgBase,
      borderLeft: `3px solid ${accent}`,
      borderRadius: '4px',
      padding: '8px 12px',
      flex: 1,
      minWidth: '110px',
      cursor: onClick ? 'pointer' : 'default',
    }}>
      <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '20px', color: c.textPrimary, fontWeight: 600, marginTop: '2px' }}>{value}</div>
    </div>
  );

  return (
    <div>
      {/* ── Header card ── */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
          <div>
            <div style={{ fontSize: '22px', fontWeight: 600, color: c.textPrimary }}>
              👤 {customer.name}
            </div>
            {customer.account_number && (
              <div style={{ fontSize: '13px', color: c.textMuted, marginTop: '2px' }}>
                {t('common.account_number')} #{customer.account_number}
              </div>
            )}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                className="button button-secondary"
                disabled={catchupBusy}
                onClick={async () => {
                  if (!(await confirm(
                    'Catch up all meters',
                    `Scan every meter under ${customer.name} for missing recent daily data and pull up to 31 days back to fill any gap. Already-current meters skipped. Runs in the background.`,
                    { confirmLabel: 'Run catch-up', confirmColor: '#16a34a' },
                  ))) return;
                  setCatchupBusy(true);
                  const started = Date.now();
                  toast.push(`Scanning ${customer.name} meters for gaps…`, 'info');
                  try {
                    const r = await api(`/customers/${customer.id}/catchup?days=31`, { method: 'POST' });
                    const secs = Math.round((Date.now() - started) / 1000);
                    toast.push(
                      `Catch-up (${secs}s): ${r.triggered.length} triggered, ${r.up_to_date.length} already current` +
                      (r.errors.length ? `, ${r.errors.length} failed` : ''),
                      r.errors.length ? 'warning' : 'success',
                    );
                  } catch (err) {
                    toast.push(`Catch-up failed: ${err.message}`, 'error');
                  } finally {
                    setCatchupBusy(false);
                  }
                }}
                title="Fill missing recent daily data for every meter under this customer"
              >{catchupBusy ? <><Spinner /> Catching up…</> : '🩹 Catch-up all'}</button>
              <button className="button button-secondary" onClick={() => setEditing(true)}>✏️ {t('btn.edit')}</button>
              <button className="button button-secondary" onClick={deleteCustomer}>🗑 {t('btn.delete')}</button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {miniKpi(t('kpi.installed'), assignedCount, c.accent)}
          {miniKpi(t('common.online'), onlineCount, '#22c55e')}
          {miniKpi(t('common.offline'), offlineCount, offlineCount > 0 ? '#ef4444' : c.textSecondary)}
          {miniKpi(
            t('kpi.commissioned'),
            assignedCount > commissionedCount
              ? <>{commissionedCount}<span style={{ fontSize: '12px', color: c.warning, marginLeft: '6px' }}>
                  {assignedCount - commissionedCount} to go ›</span></>
              : commissionedCount,
            c.accentSecondary,
            () => setShowKeyGap(true),
            t('keygap.tile_hint'),
          )}
          {miniKpi(t('kpi.relay_off'), relayOffCount, c.danger)}
        </div>
      </div>

      {showKeyGap && (
        <CustomerKeyGapModal customer={customer} onClose={() => setShowKeyGap(false)} />
      )}

      {/* ── Billing Data Statistics (existing component) ── */}
      <CustomerCollectionStatsCard customerId={customer.id} />

      <CustomerPollingMetricsCard customerId={customer.id} customerName={customer.name} />

      {/* ── Device Status breakdown ── */}
      <div style={card}>
        <div style={cardTitle}>
          <span>📟 {t('card.device_status')}</span>
          <span style={{ fontSize: '11px', color: c.textMuted, fontWeight: 400 }}>{t('common.live')}</span>
        </div>
        {deviceRows.length === 0 ? (
          <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic' }}>{t('common.no_meters_assigned')}</div>
        ) : (
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: c.textSecondary, textAlign: 'right' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>{t('th.meter_type')}</th>
                <th style={{ padding: '6px 8px' }}>{t('th.installed')}</th>
                <th style={{ padding: '6px 8px' }}>{t('th.commissioned')}</th>
                <th style={{ padding: '6px 8px', color: c.success }}>{t('th.on')}</th>
                <th style={{ padding: '6px 8px', color: c.textSecondary }}>{t('th.off')}</th>
                <th style={{ padding: '6px 8px' }}>{t('th.no_relay')}</th>
              </tr>
            </thead>
            <tbody>
              {deviceRows.map(r => (
                <tr key={r.code ?? 'unk'} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '6px 8px', color: c.textPrimary }}>{r.label}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: c.textPrimary }}>{r.installed}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: c.textPrimary }}>{r.commissioned}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: c.success }}>{r.on}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: c.textSecondary }}>{r.off}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: c.textMuted }}>{r.no_relay}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Transformers + meters quick summary ── */}
      <div style={card}>
        <div style={cardTitle}>
          <span>⚡ {t('card.transformers')} ({transformers.length})</span>
        </div>
        {transformers.length === 0 ? (
          <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic' }}>{t('common.no_transformers')}</div>
        ) : (
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: c.textSecondary }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>{t('th.name')}</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>{t('th.location')}</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>{t('th.meters_count')}</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>{t('th.online_status')}</th>
              </tr>
            </thead>
            <tbody>
              {transformerRows.map(({ t, count, online }) => (
                <tr key={t.id} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '6px 8px', color: c.textPrimary }}>{t.name}</td>
                  <td style={{ padding: '6px 8px', color: c.textSecondary }}>{t.location || '—'}</td>
                  <td style={{ padding: '6px 8px', color: c.textPrimary, textAlign: 'right' }}>{count}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: online === count && count > 0 ? c.success : c.warning }}>
                    {online}/{count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Contact info ── */}
      <div style={card}>
        <div style={cardTitle}><span>📇 {t('card.contact')}</span></div>
        <DetailGrid items={[
          [t('common.address'), customer.address],
          [t('common.phone'), customer.phone],
          [t('common.email'), customer.email],
          [t('common.created'), fmtDT(customer.created_at, { utc: true, seconds: true })],
        ]} />
      </div>

      {/* ── Notes timeline ── */}
      <div style={card}>
        <div style={cardTitle}>
          <span>📝 {t('card.notes_count')} ({notes.length})</span>
        </div>
        {canEditNotes && (
          <form onSubmit={addNote} style={{ marginBottom: '14px' }}>
            <textarea
              className="input-field"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={t('ph.add_note')}
              rows={3}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
              disabled={posting}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <select
                className="input-field"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={{ width: '160px' }}
                disabled={posting}
              >
                <option value="general">General</option>
                <option value="payment">💰 Payment</option>
                <option value="service">🔧 Service</option>
                <option value="ops">⚙ Ops</option>
              </select>
              <button type="submit" className="button button-primary" disabled={posting || !newNote.trim()}>
                {posting ? <Spinner /> : 'Add note'}
              </button>
            </div>
          </form>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {notes.length === 0 ? (
            <div style={{ color: c.textDimmed, fontSize: '12px', fontStyle: 'italic' }}>No notes yet.</div>
          ) : (
            notes.map(n => (
              <div key={n.id} style={{
                background: c.bgBase,
                border: `1px solid ${c.border}`,
                borderLeft: `3px solid ${categoryColor(n.category)}`,
                borderRadius: '6px',
                padding: '10px 12px',
                fontSize: '13px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ fontSize: '11px', color: c.textMuted }}>
                    <strong style={{ color: categoryColor(n.category) }}>{categoryLabel(n.category)}</strong>
                    {' · '}
                    {fmtDT(n.created_at, { utc: true })}
                    {n.author_username && <> · {n.author_username}</>}
                  </div>
                  {canEditNotes && (
                    <button
                      onClick={() => deleteNote(n.id)}
                      style={{
                        background: 'transparent', border: 'none', color: c.textDimmed,
                        cursor: 'pointer', fontSize: '14px', padding: '2px 6px',
                      }}
                      title="Delete note"
                    >×</button>
                  )}
                </div>
                <div style={{ color: c.textLabel, whiteSpace: 'pre-wrap' }}>{n.body}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {editing && (
        <EditCustomerModal
          customer={customer}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
        />
      )}
    </div>
  );
}


// Home sub-pane of SubscriberTabbedView. Routes the current selection
// (all / customer / transformer / dcu) to the appropriate detail
// component. The 'all' branch renders the consolidated
// AllCustomersAggregateView (Billing / Status Summary / Consumption
// tables); 'customer' renders the new SubscriberCustomerHome built in
// R3; transformer / dcu keep their existing detail components.
function SubscriberHomePane({ selection, customers, transformers, assignments, inboundMeters, isAdmin, onChanged }) {
  const c = useThemeColors();
  if (selection?.type === 'all') {
    return <AllCustomersAggregateView />;
  }
  if (selection?.type === 'customer') {
    const customer = customers.find(c => c.id === selection.id);
    const customerTransformers = transformers.filter(t => t.customer_id === selection.id);
    const customerTransformerIds = new Set(customerTransformers.map(t => t.id));
    const scopedAssignments = assignments.filter(a =>
      customerTransformerIds.has(a.transformer_id) &&
      !(a.dcu_serial && a.meter_serial === a.dcu_serial) // exclude DCU self-assignments
    );
    return (
      <SubscriberCustomerHome
        customer={customer}
        transformers={customerTransformers}
        scopedAssignments={scopedAssignments}
        inboundMeters={inboundMeters}
        isAdmin={isAdmin}
        onChanged={onChanged}
      />
    );
  }
  if (selection?.type === 'transformer') {
    const transformer = transformers.find(t => t.id === selection.id);
    const customer = transformer && customers.find(c => c.id === transformer.customer_id);
    const tAssignments = assignments.filter(a => a.transformer_id === selection.id);
    return (
      <TransformerDetail
        transformer={transformer}
        customer={customer}
        assignments={tAssignments}
        inboundMeters={inboundMeters}
        isAdmin={isAdmin}
        onChanged={onChanged}
      />
    );
  }
  if (selection?.type === 'dcu') {
    const dcuAssigns = assignments.filter(a => a.dcu_serial === selection.id && a.meter_serial !== a.dcu_serial);
    const dcuSelfAssign = assignments.find(a => a.meter_serial === selection.id);
    return (
      <DcuNodeDetail
        dcuSerial={selection.id}
        assignments={dcuAssigns}
        dcuSelfAssign={dcuSelfAssign}
        inboundMeters={inboundMeters}
        transformers={transformers}
        customers={customers}
        isAdmin={isAdmin}
        onChanged={onChanged}
      />
    );
  }
  return <div style={{ color: c.textMuted }}>Nothing selected</div>;
}

// Meter Readings sub-pane of SubscriberTabbedView. Three report tabs
// — Daily, Monthly, Period Consumption — share a single CT/VT
// Primary/Secondary toggle at the pane level so the operator sets
// units once and every report tab honours it.
function SubscriberReadingsPane({ scopedAssignments, inboundMeters }) {
  const c = useThemeColors();
  const t = useT();
  const [activeReport, setActiveReport] = useState('daily');
  const [applyCTVT, setApplyCTVT] = useState(true);

  const reportBtn = (active) => ({
    padding: '6px 14px',
    background: active ? c.accent : 'transparent',
    color: active ? '#fff' : c.textSecondary,
    border: `1px solid ${active ? c.accent : c.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: active ? 600 : 400,
  });

  const reports = [
    { key: 'daily', label: t('mr.daily') },
    { key: 'monthly', label: t('mr.monthly') },
    { key: 'period', label: t('mr.period') },
  ];

  return (
    <div>
      {/* Top bar: report-type tabs + shared Primary/Secondary toggle */}
      <div style={{
        display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap',
        marginBottom: '10px', padding: '8px 10px',
        background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: '6px',
      }}>
        {reports.map(r => (
          <button key={r.key} style={reportBtn(activeReport === r.key)}
                  onClick={() => setActiveReport(r.key)}>
            {r.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: !applyCTVT ? c.textPrimary : c.textMuted,
                          fontWeight: !applyCTVT ? 600 : 400 }}>
            Secondary
          </span>
          <div
            onClick={() => setApplyCTVT(v => !v)}
            style={{
              width: '40px', height: '22px', borderRadius: '11px',
              background: applyCTVT ? c.accent : c.border,
              position: 'relative', cursor: 'pointer', flexShrink: 0,
            }}
            title="Primary = CT × VT multiplied, Secondary = raw meter values"
          >
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
              position: 'absolute', top: '2px', left: applyCTVT ? '20px' : '2px',
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
          <span style={{ fontSize: '11px', color: applyCTVT ? c.textPrimary : c.textMuted,
                          fontWeight: applyCTVT ? 600 : 400 }}>
            Primary
          </span>
        </div>
      </div>

      {activeReport === 'daily' && (
        <SubscriberDailyBillingTab
          scopedAssignments={scopedAssignments}
          inboundMeters={inboundMeters}
          applyCTVT={applyCTVT}
        />
      )}
      {activeReport === 'monthly' && (
        <SubscriberMonthlyBillingTab
          scopedAssignments={scopedAssignments}
          inboundMeters={inboundMeters}
          applyCTVT={applyCTVT}
        />
      )}
      {activeReport === 'period' && (
        <SubscriberPeriodConsumptionTab
          scopedAssignments={scopedAssignments}
          applyCTVT={applyCTVT}
        />
      )}
    </div>
  );
}

// Daily-billing snapshot table across the subscriber's scoped meters.
// Extracted from the original SubscriberReadingsPane so the new
// per-report tab layout can mount it alongside Monthly and Period
// Consumption siblings.
function SubscriberDailyBillingTab({ scopedAssignments, inboundMeters, applyCTVT }) {
  const reportCols = useReportColumns();
  const [latest, setLatest] = useState({});
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin = userCan(user, 'manage_dcu_meters');

  // Default to TODAY IN TASHKENT. Was `new Date().toISOString()`, which
  // is the UTC date and therefore a day behind between 00:00 and 05:00
  // local — the picker opened on yesterday and "Refresh from DCU" then
  // pulled a window that could not contain the displayed day's row.
  const [selectedDate, setSelectedDate] = useState(tashkentYmd());
  const [mode, setMode] = useState('latest');
  // Admin-only "pull this many days back" selector for the on-demand
  // refresh. 1 = today only (the fast path — one DCU aggregate call
  // fills ~1 recent row per child). 7/14/31 = wider windows that use
  // the meter's on-board daily_billing buffer (typically 30 days
  // deep). Larger windows are slower on 2G since per-DCU aggregate
  // may need multiple block-transfer chunks.
  const [refreshDays, setRefreshDays] = useState(1);

  // Sort state — one column at a time, click toggles direction.
  // `key` forms: 'meter' | 'latest_row' | 'col:<OBIS>'. null = no sort
  // (preserve original assignment order).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    setSortKey(prev => {
      if (prev !== key) { setSortDir('desc'); return key; }
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
      return key;
    });
  };

  const loadData = useCallback(async ({ forceRefresh = false, daysBack = null, modeOverride = null } = {}) => {
    if (scopedAssignments.length === 0) {
      setLatest({});
      return;
    }
    // React batches setMode+loadData in a click handler — the closure's
    // `mode` is still the PREVIOUS value when Search/Latest is clicked
    // for the first time after mode changes. Callers that want a
    // specific mode for THIS fetch pass it in directly; the `mode`
    // state still drives the UI header text and the subsequent renders.
    const effectiveMode = modeOverride || mode;
    setBusy(true);
    try {
      // Admin-only "Refresh from DCU" on-demand pull. Two modes:
      //
      //   daysBack=null → legacy 1-day/latest behaviour: ONE per-DCU
      //     aggregate call (or per-meter for direct meters), narrow
      //     window matching the currently-selected date.
      //
      //   daysBack=N → range-pull for the last N days. Groups
      //     assignments by DCU and fires one aggregate call per DCU
      //     (which refills cache for all 168 children in one go),
      //     plus one per-meter call for each direct meter. All in
      //     parallel with concurrency=4 to keep the GPRS uplink from
      //     saturating.
      if (forceRefresh && isAdmin) {
        // Calendar-day aligned in Tashkent, anchored on the day the
        // operator is LOOKING AT — not a rolling 24 h ending now.
        //
        // daily_billing rows are stamped at exactly 00:00 local, so the
        // old `now - N*86400 .. now` window straddled two dates and
        // excluded the 00:00 row of the displayed day: measured at
        // 03:04 TAS on 2026-08-07 it ran [08-06 03:04 .. 08-07 03:04],
        // which cannot fetch 08-06's row while the picker reads 08-06.
        // "Last 1 day" now means exactly the selected date.
        //
        // from_ts is inclusive of exactly 00:00:00 (verified against
        // /api/meters/{s}/profile/daily_billing on 2026-08-07), so the
        // day boundary needs no padding.
        const days = typeof daysBack === 'number' && daysBack > 0 ? daysBack : 1;
        const anchorYmd = (effectiveMode === 'date' && selectedDate)
          ? selectedDate
          : tashkentYmd();
        const fromYmd = tashkentYmd(dayStart(anchorYmd) - (days - 1) * 86400);
        const fromTs = dayStart(fromYmd);
        const toTs = dayEnd(anchorYmd);

        // Group by dcu_serial. Each unique DCU gets ONE call (via any
        // child meter — backend routes it to the aggregate fill).
        // Direct meters each get their own call.
        const dcuGroups = new Map(); // dcu_serial → first child serial
        const directSerials = [];
        for (const a of scopedAssignments) {
          const dcu = a.dcu_serial && a.dcu_serial !== a.meter_serial
            ? a.dcu_serial : null;
          if (dcu) {
            if (!dcuGroups.has(dcu)) dcuGroups.set(dcu, a.meter_serial);
          } else {
            directSerials.push(a.meter_serial);
          }
        }

        const q = `from_ts=${fromTs}&to_ts=${toTs}&limit=5000&refresh=force&primary=false`;
        // Profiles to refresh on the operator click. Daily billing is
        // 1 row/day so cheap; LP and PQ are 96 rows/day each, so the
        // total transfer is ~96× larger per profile. We still pull
        // them because operators reported "some meters have PQ and LP
        // and some don't" — refresh-from-DCU should fix that gap.
        // The DCU-aggregate path collapses N children into one call,
        // so the cost is per-DCU not per-meter for routed meters.
        const profiles = ['daily_billing', 'load_profile', 'power_quality'];
        const calls = [];
        for (const p of profiles) {
          for (const s of dcuGroups.values()) calls.push({ serial: s, profile: p, kind: 'dcu' });
          for (const s of directSerials)     calls.push({ serial: s, profile: p, kind: 'direct' });
        }
        if (calls.length === 0) {
          // fallthrough — nothing to pull
        } else {
          toast.push(
            `⏳ Pulling daily + LP + PQ for last ${days} day${days > 1 ? 's' : ''} `
            + `(${dcuGroups.size} DCU + ${directSerials.length} direct meter${directSerials.length !== 1 ? 's' : ''}, ${calls.length} call${calls.length !== 1 ? 's' : ''})…`,
            'info'
          );
          // Concurrency-limited parallel fetch. Each DCU aggregate can
          // take 60–180s on 2G; direct meters typically 30–90s. LP/PQ
          // pulls of 7-day windows can stretch that to several minutes
          // per call; the worker runs them sequentially within one slot.
          const CONCURRENCY = 4;
          let i = 0;
          // Report what actually came back. "Refresh complete" used to show
          // even when the DCU did not hold the day yet (Cola, 30.09): each
          // response carries cache.reason / cache.fetched_rows.
          let newRows = 0;
          const dailyNotYet = new Set();
          const unreachable = new Set();
          async function worker() {
            while (i < calls.length) {
              const { serial, profile } = calls[i++];
              try {
                const r = await api(`/meters/${serial}/profile/${profile}?${q}`);
                const c = (r && r.cache) || {};
                newRows += Number(c.fetched_rows || 0);
                const why = String(c.reason || '');
                if (profile === 'daily_billing' && why.startsWith('dcu_not_collected_yet')) dailyNotYet.add(serial);
                if (why.startsWith('dcu_unreachable')) unreachable.add(serial);
              } catch (_) { /* keep going */ }
            }
          }
          await Promise.all(
            Array.from({ length: Math.min(CONCURRENCY, calls.length) }, worker)
          );
          const notes = [];
          if (dailyNotYet.size) notes.push(`daily not collected by the DCU yet (${dailyNotYet.size} DCU) — it reads meters on its own schedule, try again later`);
          if (unreachable.size) notes.push(`${unreachable.size} DCU not connected`);
          toast.push(
            `✓ Refresh complete — ${newRows} new row${newRows === 1 ? '' : 's'}`
            + (notes.length ? `; ${notes.join('; ')}` : ''),
            notes.length ? 'info' : 'success'
          );
        }
      }

      // Fast path — one HTTP round-trip regardless of fleet size.
      // The backend /profile/{name}/latest-bulk endpoint runs a single
      // correlated-subquery pull over profile_rows for every serial
      // at once. Measured: 168-meter DCU ~2 ms of SQLite vs. the
      // previous ~250 ms + React-re-render penalty from 168 parallel
      // /profile fetches.
      const dateParams = (effectiveMode === 'date' && selectedDate)
        ? { from_ts: dayStart(selectedDate), to_ts: dayEnd(selectedDate) }
        : {};
      // Chunked under the 500-serial cap so the All-Customers scope (670+
      // meters) loads instead of 400-ing into an empty table.
      const { captures, meters } = await latestBulkChunked(
        'daily_billing', scopedAssignments.map(a => a.meter_serial), dateParams);
      // Reshape to the per-meter {rows, captures} shape the existing
      // render code expects, so the downstream render logic doesn't
      // change.
      const next = {};
      for (const a of scopedAssignments) {
        const row = meters[a.meter_serial];
        next[a.meter_serial] = row
          ? { rows: [row], captures, count: 1 }
          : null;
      }
      setLatest(next);
    } finally {
      setBusy(false);
    }
  }, [scopedAssignments, mode, selectedDate, isAdmin]);

  // Clear stale rows when the tree selection changes, but don't
  // auto-fetch — operators asked for Search-only so picking a
  // customer / transformer / DCU doesn't fire a silent DLMS pull.
  useEffect(() => { setLatest({}); }, [scopedAssignments]);

  // Pick a few headline columns to surface in this flat summary
  // table — +A, -A, +R, -R (total buckets). We use the captures list
  // of whichever meter responded first to establish the label order,
  // since all CAS meters share the same daily_billing schema via the
  // backend _PROFILE_FALLBACK_COLUMNS fallback.
  // Show all energy registers including T1-T4 tariff buckets.
  // The daily_billing profile stores 20 energy columns per row —
  // the subscriber table surfaces all of them so operators can see
  // per-tariff consumption at the transformer/customer level.
  const HEADLINE_OBIS = [
    '1.0.1.8.0.255',  // +A Total
    '1.0.1.8.1.255',  // +A T1
    '1.0.1.8.2.255',  // +A T2
    '1.0.1.8.3.255',  // +A T3
    '1.0.1.8.4.255',  // +A T4
    '1.0.2.8.0.255',  // -A Total
    '1.0.3.8.0.255',  // +R Total
    '1.0.4.8.0.255',  // -R Total
  ];
  let headlineInfo = null;
  for (const a of scopedAssignments) {
    const r = latest[a.meter_serial];
    if (!r || !r.captures || r.captures.length === 0) continue;
    headlineInfo = resolveProfileColumns(r.captures);
    headlineInfo.obisIndex = {};
    r.captures.forEach((c, i) => { headlineInfo.obisIndex[c.obis] = i; });
    break;
  }
  const headlineCols = HEADLINE_OBIS.map(obis => {
    // Fallback chain for the column label:
    //   1. the capture's own ``label`` (authoritative, comes from the
    //      meter's profile-generic attribute 4 when available)
    //   2. the curated PROFILE_COLUMN_LABELS entry for this OBIS
    //      (covers the case where no meter has responded yet and the
    //      captures list is empty — otherwise the header renders the
    //      raw OBIS code, which is unreadable)
    //   3. the raw OBIS string (last resort)
    if (!headlineInfo) {
      return {
        obis,
        label: PROFILE_COLUMN_LABELS[obis] || obis,
        idx: -1,
        scale: 1,
      };
    }
    const idx = headlineInfo.obisIndex[obis];
    return {
      obis,
      label: headlineInfo.labels[idx] || PROFILE_COLUMN_LABELS[obis] || obis,
      idx: idx ?? -1,
      scale: headlineInfo.scales[idx] || 1,
    };
  });

  // Per-assignment CT × VT factor. Used when ``applyCTVT`` is on so
  // the on-screen + CSV numbers match the Primary-side xlsx the
  // backend produces when called with primary=true.
  const ctvtFor_inline = (a) => ({
    ct: applyCTVT ? parseRatio(a?.ct_ratio) : 1,
    vt: applyCTVT ? parseRatio(a?.vt_ratio) : 1,
  });

  // Return the currently-visible table list, sorted by sortKey/sortDir.
  // Energy columns compare numerically (nulls sink on desc); meter
  // label compares as lowercase string; latest_row compares as epoch.
  const sortedAssignments = React.useMemo(() => {
    if (!sortKey) return scopedAssignments;
    const arr = scopedAssignments.slice();
    const dir = sortDir === 'desc' ? -1 : 1;
    const extract = (a) => {
      if (sortKey === 'meter') {
        return (a.object_name || a.meter_serial || '').toLocaleLowerCase();
      }
      if (sortKey === 'latest_row') {
        const row = latest[a.meter_serial]?.rows?.[0];
        return row?.row_ts ?? (row?.ts ? Date.parse(row.ts) / 1000 : null);
      }
      if (sortKey.startsWith('col:')) {
        const obis = sortKey.slice(4);
        const col = headlineCols.find(cc => cc.obis === obis);
        if (!col || col.idx < 0) return null;
        const row = latest[a.meter_serial]?.rows?.[0];
        const raw = (row?.values || [])[col.idx];
        if (typeof raw !== 'number') return null;
        const { ct, vt } = ctvtFor_inline(a);
        const pf = primaryFactor(col.obis || '', ct, vt);
        return effectiveValue(raw, col.scale, a.fraction_digits) * pf;
      }
      return null;
    };
    arr.sort((a, b) => {
      const va = extract(a); const vb = extract(b);
      if (va == null && vb == null) return 0;
      if (va == null) return dir === -1 ? 1 : -1;
      if (vb == null) return dir === -1 ? -1 : 1;
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
    return arr;
  }, [scopedAssignments, sortKey, sortDir, latest, headlineCols, applyCTVT]);

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'desc' ? ' ▼' : ' ▲';
  };

  const { lang } = useLang();

  // Per-assignment CT × VT factor. Used when ``applyCTVT`` is on so
  // the on-screen + CSV numbers match the Primary-side xlsx the
  // backend produces when called with primary=true.
  const ctvtFor = (a) => ({
    ct: applyCTVT ? parseRatio(a?.ct_ratio) : 1,
    vt: applyCTVT ? parseRatio(a?.vt_ratio) : 1,
  });

  const exportReadingsCsv = () => {
    if (scopedAssignments.length === 0) return;
    const header = ['#', 'DCU', 'Meter', 'Object Name', 'Type', 'CT', 'VT', 'Online', 'Latest Row', ...headlineCols.map(col => col.label)];
    const csvRows = scopedAssignments.map((a, i) => {
      const live = inboundMeters.find(m => (m.serial_number || m.serial) === a.meter_serial);
      const resp = latest[a.meter_serial];
      const row = resp?.rows?.[0];
      const { ct, vt } = ctvtFor(a);
      const fd = a.fraction_digits;
      const vals = headlineCols.map(col => {
        const v = col.idx >= 0 ? (row?.values || [])[col.idx] : undefined;
        if (v == null) return '';
        if (typeof v !== 'number') return v;
        const scaled = effectiveValue(v, col.scale, fd);
        const pf = primaryFactor(col.obis || '', ct, vt);
        const out = scaled * pf;
        if (fd == null) return out.toFixed(2);
        // Primary-side values trim trailing zeros so scaling-up by
        // CT×VT doesn't leave "10,718.400" noise; secondary pads to fd.
        return pf > 1 ? String(parseFloat(out.toFixed(fd))) : out.toFixed(fd);
      });
      const onlineStatus = a.dcu_serial ? 'via DCU' : (live?.identified ? 'yes' : 'no');
      return [i + 1, a.dcu_serial || '', a.meter_serial, a.object_name || '',
        formatMeterType(a.meter_type || live?.meter_type),
        a.ct_ratio || '', a.vt_ratio || '', onlineStatus,
        fmtD(row?.ts) || (row?.row_ts ? fmtD(row.row_ts) : ''), ...vals];
    });
    const csv = [header, ...csvRows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const tag = applyCTVT ? 'primary' : 'secondary';
    downloadCsv(csv, `meter_readings_${tag}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportReadingsXlsx = async () => {
    try {
      const params = new URLSearchParams({ lang: getReportLang() || lang });
      params.set('primary', applyCTVT ? 'true' : 'false');
      if (scopedAssignments.length > 0) {
        const tIds = [...new Set(scopedAssignments.map(a => a.transformer_id).filter(Boolean))];
        if (tIds.length === 1) {
          params.set('transformer_id', String(tIds[0]));
        } else {
          scopedAssignments.forEach(a => params.append('meter_serial', a.meter_serial));
        }
      }
      if (mode === 'date' && selectedDate) {
        params.set('from_ts', String(dayStart(selectedDate)));
        params.set('to_ts', String(dayEnd(selectedDate)));
      }
      applyColumnParams(params, reportCols.catalog && reportCols.catalog.daily_billing,
                        reportCols.prefs.daily_billing);
      await downloadXlsx(
        `/api/reports/daily-billing.xlsx?${params}`,
        `daily_billing_${new Date().toISOString().slice(0, 10)}.xlsx`,
        { toast },
      );
    } catch (_) { /* toast already shown by helper */ }
  };

  // Both 'latest' and 'date' modes show one row per meter — the
  // difference is that 'date' fetches the snapshot for a specific day
  // while 'latest' fetches the most recent snapshot regardless of date.

  return (
    <div>
      {/* Date picker + mode toggle + export buttons */}
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: '10px', padding: '10px 12px',
        background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>{t('common.date')}</div>
          <input type="date" className="input-field" value={selectedDate}
                 onChange={(e) => setSelectedDate(e.target.value)}
                 style={{ padding: '4px 6px', fontSize: '11px', width: '140px' }} />
        </div>
        <button className="button button-sm button-primary"
                onClick={() => { setMode('date'); loadData({ modeOverride: 'date' }); }}
                disabled={busy}>
          {busy ? <Spinner /> : '🔍'} {t('btn.search')}
        </button>
        <button className="button button-sm button-secondary"
                onClick={() => { setMode('latest'); loadData({ modeOverride: 'latest' }); }}
                disabled={busy}
                title="Show latest snapshot only">
          Latest
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          {isAdmin && (
            <>
              <select
                value={refreshDays}
                onChange={(e) => setRefreshDays(parseInt(e.target.value, 10))}
                disabled={busy}
                title="How many days back to request from the meter / DCU on the next Refresh click"
                style={{
                  padding: '6px 8px', fontSize: '11px',
                  background: c.bgCardAlt, color: c.textPrimary,
                  border: `1px solid ${c.border}`, borderRadius: '4px',
                }}
              >
                <option value={1}>Last 1 day</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={31}>Last 31 days</option>
              </select>
              <button
                className="button button-sm button-secondary"
                onClick={() => loadData({ forceRefresh: true, daysBack: refreshDays })}
                disabled={busy || scopedAssignments.length === 0}
                title="Admin: pull fresh daily_billing rows from the DCU / meters for the selected window. One aggregate call per DCU + one call per direct meter. Slow on 2G — wider windows can take several minutes."
              >
                ⟲ Refresh from DCU
              </button>
            </>
          )}
          <button className="button button-sm button-secondary" onClick={exportReadingsXlsx}
                  disabled={scopedAssignments.length === 0}>⬇ XLSX</button>
          <ReportColumnsButton
            compact
            reportKeys={['daily_billing']}
            catalog={reportCols.catalog}
            prefs={reportCols.prefs}
            savePrefs={reportCols.savePrefs}
            summary={reportCols.summary}
          />
          <span style={{ marginLeft: '8px', alignSelf: 'center' }}><ReportLangToggle /></span>
        </div>
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '6px' }}>
        {mode === 'date'
          ? `Daily billing snapshot for ${selectedDate} across ${scopedAssignments.length} meter(s).`
          : 'Latest daily_billing snapshot per meter. Click a meter for full date-range view.'}
      </div>
      <div className="table-container" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
        <table style={{ fontSize: '11px', width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
            <tr>
              <th>#</th>
              <th>DCU</th>
              <th
                onClick={() => toggleSort('meter')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                {t('common.meter')}{sortIndicator('meter')}
              </th>
              <th>{t('th.type')}</th>
              <th>CT · VT</th>
              <th>{t('th.online_status')}</th>
              <th
                onClick={() => toggleSort('latest_row')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                {t('th.latest_row')}{sortIndicator('latest_row')}
              </th>
              {headlineCols.map((col, i) => (
                <th
                  key={i}
                  onClick={() => toggleSort(`col:${col.obis}`)}
                  style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort"
                >
                  {col.label}{sortIndicator(`col:${col.obis}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedAssignments.map((a, i) => {
              const live = inboundMeters.find(m => (m.serial_number || m.serial) === a.meter_serial);
              const resp = latest[a.meter_serial];
              const row = resp?.rows?.[0];
              const { ct, vt } = ctvtFor(a);
              return (
                <tr key={a.id}>
                  <td style={{ color: c.textMuted }}>{i + 1}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '10px', color: c.textMuted }}>{a.dcu_serial || '—'}</td>
                  <td>
                    <div>{a.object_name || a.meter_serial}</div>
                    <div style={{ fontFamily: 'monospace', color: c.textMuted, fontSize: '10px' }}>{a.meter_serial}</div>
                  </td>
                  <td><MeterTypeBadge mt={a.meter_type || live?.meter_type} compact /></td>
                  <td style={{ fontSize: '10px', color: c.textSecondary }}>
                    {a.ct_ratio || '—'}{a.vt_ratio ? ` · ${a.vt_ratio}` : ''}
                  </td>
                  <td style={{ fontSize: '10px' }}>
                    {a.dcu_serial ? <span style={{ color: '#22c55e' }}>● via DCU</span>
                      : live?.session_open ? <span style={{ color: c.success }}>● live</span>
                      : live?.identified ? <span style={{ color: c.warning }}>● connected</span>
                      : <span style={{ color: c.textMuted }}>● offline</span>}
                  </td>
                  <td style={{ fontSize: '10px' }}>
                    {fmtD(row?.ts) || (row?.row_ts ? fmtD(row.row_ts) : '—')}
                  </td>
                  {headlineCols.map((col, j) => {
                    const v = col.idx >= 0 ? (row?.values || [])[col.idx] : undefined;
                    const pf = primaryFactor(col.obis || '', ct, vt);
                    return (
                      <td key={j} style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {renderProfileCell(v, col.scale, pf, a.fraction_digits, col.obis)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* range mode removed — single date picker shows one row per meter */}
            {busy && (
              <tr><td colSpan={6 + headlineCols.length} style={{ color: c.textMuted, padding: '12px', textAlign: 'center' }}>
                <Spinner /> {t('common.loading')}
              </td></tr>
            )}
            {/* empty state for date mode handled by the per-meter rows showing '—' */}
            {!busy && scopedAssignments.length === 0 && (
              <tr><td colSpan={6 + headlineCols.length} style={{ color: c.textDimmed, padding: '12px', textAlign: 'center', fontStyle: 'italic' }}>
                No meters under this subscriber.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Monthly-billing snapshot table across the subscriber's scoped
// meters. Shape mirrors the Daily tab but pulls the monthly
// profile (OBIS 0.0.98.1.0.255). Monthly rows rotate on the 1st of
// each month so "latest" here means the most recent rollover.
function SubscriberMonthlyBillingTab({ scopedAssignments, inboundMeters, applyCTVT }) {
  const reportCols = useReportColumns();
  const [latest, setLatest] = useState({});
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const { lang } = useLang();
  const { user } = useAuth();
  const isAdmin = userCan(user, 'manage_dcu_meters');

  // Month dropdown. Monthly billing is inherently month-bucketed, so a
  // calendar date picker was never the right UX. Year + Month selects
  // also make the tz-correct window trivial: `monthStart(y,m)` and
  // `monthEnd(y,m)` produce the exact meter-local range.
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1-12
  // Legacy `mode` kept for minimal diff — `'date'` means the Year+Month
  // dropdowns are in effect; `'latest'` asks the meter for its most
  // recent monthly row (ignoring the dropdown).
  const [mode, setMode] = useState('date');

  const loadData = useCallback(async ({ forceRefresh = false, modeOverride = null } = {}) => {
    if (scopedAssignments.length === 0) {
      setLatest({});
      return;
    }
    // See SubscriberDailyBillingTab for why modeOverride exists:
    // React batches setMode+loadData in a click handler so the closure's
    // `mode` lags by one click. Callers pass the intended mode in.
    const effectiveMode = modeOverride || mode;
    setBusy(true);
    try {
      // Admin-only on-demand refill: kick the DCU aggregate via ONE
      // per-meter refresh=force call; other meters on the same DCU
      // piggy-back on the per-DCU lock. See the Daily tab for the
      // same pattern.
      if (forceRefresh && isAdmin) {
        const trigger = scopedAssignments[0]?.meter_serial;
        if (trigger) {
          try {
            const q = effectiveMode === 'date'
              ? `from_ts=${monthStart(selectedYear, selectedMonth)}&to_ts=${monthEnd(selectedYear, selectedMonth)}&`
              : '';
            await api(`/meters/${trigger}/profile/monthly_billing?${q}limit=1&refresh=force&primary=false`);
          } catch (_) { /* keep going; cache may already cover the ask */ }
        }
      }

      const dateParams = (effectiveMode === 'date')
        ? { from_ts: monthStart(selectedYear, selectedMonth),
            to_ts: monthEnd(selectedYear, selectedMonth) }
        : {};
      // Chunked under the 500-serial cap (same fix as the daily tab).
      const { captures, meters } = await latestBulkChunked(
        'monthly_billing', scopedAssignments.map(a => a.meter_serial), dateParams);
      const next = {};
      for (const a of scopedAssignments) {
        const row = meters[a.meter_serial];
        next[a.meter_serial] = row
          ? { rows: [row], captures, count: 1 }
          : null;
      }
      setLatest(next);
    } finally {
      setBusy(false);
    }
  }, [scopedAssignments, mode, selectedYear, selectedMonth, isAdmin]);

  // Search-only — clear stale rows on selection change but don't
  // auto-fetch (see SubscriberDailyBillingTab for the same pattern).
  useEffect(() => { setLatest({}); }, [scopedAssignments]);

  // Monthly uses the same OBIS set as daily for totals, so the
  // headline row is identical — reusing the column definition keeps
  // the two reports visually comparable.
  const HEADLINE_OBIS = [
    '1.0.1.8.0.255', '1.0.1.8.1.255', '1.0.1.8.2.255', '1.0.1.8.3.255',
    '1.0.1.8.4.255', '1.0.2.8.0.255', '1.0.3.8.0.255', '1.0.4.8.0.255',
  ];
  let headlineInfo = null;
  for (const a of scopedAssignments) {
    const r = latest[a.meter_serial];
    if (!r || !r.captures || r.captures.length === 0) continue;
    headlineInfo = resolveProfileColumns(r.captures);
    headlineInfo.obisIndex = {};
    r.captures.forEach((cap, i) => { headlineInfo.obisIndex[cap.obis] = i; });
    break;
  }
  const headlineCols = HEADLINE_OBIS.map(obis => {
    // Same label-fallback chain as SubscriberDailyBillingTab —
    // uses PROFILE_COLUMN_LABELS when captures are empty so the
    // monthly table never shows raw OBIS codes as column headers.
    if (!headlineInfo) {
      return {
        obis,
        label: PROFILE_COLUMN_LABELS[obis] || obis,
        idx: -1,
        scale: 1,
      };
    }
    const idx = headlineInfo.obisIndex[obis];
    return {
      obis,
      label: headlineInfo.labels[idx] || PROFILE_COLUMN_LABELS[obis] || obis,
      idx: idx ?? -1,
      scale: headlineInfo.scales[idx] || 1,
    };
  });

  const ctvtFor = (a) => ({
    ct: applyCTVT ? parseRatio(a?.ct_ratio) : 1,
    vt: applyCTVT ? parseRatio(a?.vt_ratio) : 1,
  });

  const exportCsv = () => {
    if (scopedAssignments.length === 0) return;
    const header = ['#', 'DCU', 'Meter', 'Object Name', 'Type', 'CT', 'VT', 'Month Row', ...headlineCols.map(col => col.label)];
    const csvRows = scopedAssignments.map((a, i) => {
      const resp = latest[a.meter_serial];
      const row = resp?.rows?.[0];
      const { ct, vt } = ctvtFor(a);
      const fd = a.fraction_digits;
      const vals = headlineCols.map(col => {
        const v = col.idx >= 0 ? (row?.values || [])[col.idx] : undefined;
        if (v == null) return '';
        if (typeof v !== 'number') return v;
        const scaled = effectiveValue(v, col.scale, fd);
        const pf = primaryFactor(col.obis || '', ct, vt);
        const out = scaled * pf;
        if (fd == null) return out.toFixed(2);
        return pf > 1 ? String(parseFloat(out.toFixed(fd))) : out.toFixed(fd);
      });
      return [i + 1, a.dcu_serial || '', a.meter_serial, a.object_name || '',
        formatMeterType(a.meter_type),
        a.ct_ratio || '', a.vt_ratio || '',
        fmtD(row?.ts) || (row?.row_ts ? fmtD(row.row_ts) : ''), ...vals];
    });
    const csv = [header, ...csvRows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const tag = applyCTVT ? 'primary' : 'secondary';
    downloadCsv(csv, `monthly_billing_${tag}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams({ lang: getReportLang() || lang });
      params.set('primary', applyCTVT ? 'true' : 'false');
      if (scopedAssignments.length > 0) {
        const tIds = [...new Set(scopedAssignments.map(a => a.transformer_id).filter(Boolean))];
        if (tIds.length === 1) {
          params.set('transformer_id', String(tIds[0]));
        } else {
          scopedAssignments.forEach(a => params.append('meter_serial', a.meter_serial));
        }
      }
      if (mode === 'date') {
        params.set('from_ts', String(monthStart(selectedYear, selectedMonth)));
        params.set('to_ts', String(monthEnd(selectedYear, selectedMonth)));
      }
      applyColumnParams(params, reportCols.catalog && reportCols.catalog.monthly_billing,
                        reportCols.prefs.monthly_billing);
      await downloadXlsx(
        `/api/reports/monthly-billing.xlsx?${params}`,
        `monthly_billing_${new Date().toISOString().slice(0, 10)}.xlsx`,
        { toast },
      );
    } catch (_) { /* toast already shown by helper */ }
  };

  return (
    <div>
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: '10px', padding: '10px 12px',
        background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>Month</div>
          <select className="input-field"
                  value={`${selectedYear}-${String(selectedMonth).padStart(2,'0')}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split('-').map(Number);
                    setSelectedYear(y); setSelectedMonth(m);
                  }}
                  style={{ padding: '4px 6px', fontSize: '11px', width: '170px' }}>
            {(() => {
              // Last 12 months (current + 11 previous). Meters store 12
              // months of billing data on-board; older selections would
              // return no rows.
              const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const opts = [];
              for (let i = 0; i < 12; i++) {
                const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
                const y = d.getFullYear();
                const m = d.getMonth() + 1;
                const key = `${y}-${String(m).padStart(2,'0')}`;
                opts.push(<option key={key} value={key}>{MONTH_NAMES[m-1]} {y}</option>);
              }
              return opts;
            })()}
          </select>
        </div>
        <button className="button button-sm button-primary"
                onClick={() => { setMode('date'); loadData({ modeOverride: 'date' }); }}
                disabled={busy}>
          {busy ? <Spinner /> : '🔍'} {t('btn.search')}
        </button>
        <button className="button button-sm button-secondary"
                onClick={() => { setMode('latest'); loadData({ modeOverride: 'latest' }); }}
                disabled={busy} title="Show latest monthly rollover">
          Latest
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          {isAdmin && (
            <button
              className="button button-sm button-secondary"
              onClick={() => loadData({ forceRefresh: true })}
              disabled={busy || scopedAssignments.length === 0}
              title="Admin: pull fresh monthly rows from the DCU / meters now (bypasses cache). Non-admin users always see cached data."
            >
              ⟲ Refresh from DCU
            </button>
          )}
          <button className="button button-sm button-secondary" onClick={exportXlsx}
                  disabled={scopedAssignments.length === 0}>⬇ XLSX</button>
          <ReportColumnsButton
            compact
            reportKeys={['monthly_billing']}
            catalog={reportCols.catalog}
            prefs={reportCols.prefs}
            savePrefs={reportCols.savePrefs}
            summary={reportCols.summary}
          />
          <span style={{ marginLeft: '8px', alignSelf: 'center' }}><ReportLangToggle /></span>
        </div>
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '6px' }}>
        {mode === 'date'
          ? `Monthly rollover for ${selectedYear}-${String(selectedMonth).padStart(2, '0')} across ${scopedAssignments.length} meter(s).`
          : 'Latest monthly rollover per meter. Meters roll the monthly profile on the 1st at 00:00 local.'}
      </div>
      <div className="table-container" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
        <table style={{ fontSize: '11px', width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
            <tr>
              <th>#</th>
              <th>DCU</th>
              <th>{t('common.meter')}</th>
              <th>{t('th.type')}</th>
              <th>CT · VT</th>
              <th>Month Row</th>
              {headlineCols.map((col, i) => (
                <th key={i} style={{ textAlign: 'right' }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scopedAssignments.map((a, i) => {
              const resp = latest[a.meter_serial];
              const row = resp?.rows?.[0];
              const { ct, vt } = ctvtFor(a);
              return (
                <tr key={a.id}>
                  <td style={{ color: c.textMuted }}>{i + 1}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '10px', color: c.textMuted }}>{a.dcu_serial || '—'}</td>
                  <td>
                    <div>{a.object_name || a.meter_serial}</div>
                    <div style={{ fontFamily: 'monospace', color: c.textMuted, fontSize: '10px' }}>{a.meter_serial}</div>
                  </td>
                  <td><MeterTypeBadge mt={a.meter_type} compact /></td>
                  <td style={{ fontSize: '10px', color: c.textSecondary }}>
                    {a.ct_ratio || '—'}{a.vt_ratio ? ` · ${a.vt_ratio}` : ''}
                  </td>
                  <td style={{ fontSize: '10px' }}>
                    {fmtD(row?.ts) || (row?.row_ts ? fmtD(row.row_ts) : '—')}
                  </td>
                  {headlineCols.map((col, j) => {
                    const v = col.idx >= 0 ? (row?.values || [])[col.idx] : undefined;
                    const pf = primaryFactor(col.obis || '', ct, vt);
                    return (
                      <td key={j} style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {renderProfileCell(v, col.scale, pf, a.fraction_digits, col.obis)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {busy && (
              <tr><td colSpan={6 + headlineCols.length} style={{ color: c.textMuted, padding: '12px', textAlign: 'center' }}>
                <Spinner /> {t('common.loading')}
              </td></tr>
            )}
            {!busy && scopedAssignments.length === 0 && (
              <tr><td colSpan={6 + headlineCols.length} style={{ color: c.textDimmed, padding: '12px', textAlign: 'center', fontStyle: 'italic' }}>
                No meters under this subscriber.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Period Consumption across a subscriber's scoped meters. Wraps the
// existing /api/reports/period-consumption JSON endpoint; same
// backend that powers the individual-meter Period Consumption panel,
// just aggregated over every meter the subscriber can see.
function SubscriberPeriodConsumptionTab({ scopedAssignments, applyCTVT }) {
  const reportCols = useReportColumns();
  const c = useThemeColors();
  const { lang } = useLang();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  // Default = previous calendar month — the most common billing
  // window operators pull at transformer level.
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfPrevMonth = new Date(firstOfThisMonth.getTime() - 86400 * 1000);
  const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
  const [fromDate, setFromDate] = useState(fmt(firstOfPrevMonth));
  const [toDate, setToDate] = useState(fmt(lastOfPrevMonth));

  const runReport = async () => {
    if (scopedAssignments.length === 0) {
      setData(null);
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        lang,
        primary: applyCTVT ? 'true' : 'false',
      });
      const tIds = [...new Set(scopedAssignments.map(a => a.transformer_id).filter(Boolean))];
      if (tIds.length === 1) {
        params.set('transformer_id', String(tIds[0]));
      } else {
        scopedAssignments.forEach(a => params.append('meter_serial', a.meter_serial));
      }
      const r = await api(`/reports/period-consumption?${params}`);
      setData(r);
    } catch (_) {
      setData(null);
    }
    setBusy(false);
  };

  // Search-only. Clear old data on selection change so the empty
  // state shows until the operator clicks Run; don't auto-fetch.
  useEffect(() => { setData(null); }, [scopedAssignments]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const regs = data.registers || [];
    const header = ['#', 'Meter', 'Object Name', 'Start', 'End', ...regs.map(r => r.label)];
    const csvRows = data.rows.map((r, i) => {
      const regMap = r.registers || {};
      const asn = scopedAssignments.find(x => x.meter_serial === r.meter_serial);
      const fd = asn?.fraction_digits;
      const regVals = regs.map(reg => {
        const v = regMap[reg.obis]?.consumption;
        if (v == null) return '';
        if (typeof v !== 'number') return v;
        return fd == null ? v.toFixed(2) : v.toFixed(fd);
      });
      // object_name comes from the API row (backend now includes it
      // via _build_holley_context); fall back to the assignment from
      // the local scope if for some reason the API didn't populate it.
      const objName = r.object_name || asn?.object_name || '';
      return [i + 1, r.meter_serial || '', objName, r.start_time || '', r.end_time || '', ...regVals];
    });
    const csv = [header, ...csvRows]
      .map(row => row.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(','))
      .join('\n');
    const tag = applyCTVT ? 'primary' : 'secondary';
    downloadCsv(csv, `period_consumption_${tag}_${fromDate}_to_${toDate}.csv`);
  };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        lang,
        primary: applyCTVT ? 'true' : 'false',
      });
      const tIds = [...new Set(scopedAssignments.map(a => a.transformer_id).filter(Boolean))];
      if (tIds.length === 1) {
        params.set('transformer_id', String(tIds[0]));
      } else {
        scopedAssignments.forEach(a => params.append('meter_serial', a.meter_serial));
      }
      applyColumnParams(params, reportCols.catalog && reportCols.catalog.period_consumption,
                        reportCols.prefs.period_consumption);
      await downloadXlsx(
        `/api/reports/period-consumption.xlsx?${params}`,
        `period_consumption_${fromDate}_to_${toDate}.xlsx`,
        { toast },
      );
    } catch (_) { /* toast already shown by helper */ }
  };

  const rows = data?.rows || [];
  const regs = data?.registers || [];

  return (
    <div>
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: '10px', padding: '10px 12px',
        background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>From</div>
          <input type="date" className="input-field" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 style={{ padding: '4px 6px', fontSize: '11px', width: '140px' }} />
        </div>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>To</div>
          <input type="date" className="input-field" value={toDate}
                 onChange={(e) => setToDate(e.target.value)}
                 style={{ padding: '4px 6px', fontSize: '11px', width: '140px' }} />
        </div>
        <button className="button button-sm button-primary" onClick={runReport} disabled={busy}>
          {busy ? <Spinner /> : '🔍'} Run
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
          <ReportLangToggle />
          <button className="button button-sm button-secondary" onClick={exportXlsx}
                  disabled={scopedAssignments.length === 0}>⬇ XLSX</button>
          <ReportColumnsButton
            compact
            reportKeys={['period_consumption']}
            catalog={reportCols.catalog}
            prefs={reportCols.prefs}
            savePrefs={reportCols.savePrefs}
            summary={reportCols.summary}
          />
        </div>
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '6px' }}>
        Period consumption = end-of-period − start-of-period from each
        meter's stored daily-billing snapshots. Change the range and
        click Run.
      </div>
      <div className="table-container" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
        <table style={{ fontSize: '11px', width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
            <tr>
              <th>#</th>
              <th>{regs.length > 0 ? 'Meter' : ''}</th>
              <th>Start</th>
              <th>End</th>
              {regs.map((r, i) => (
                <th key={i} style={{ textAlign: 'right' }}>{r.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const regMap = r.registers || {};
              // Match the JSON row back to the operator's assignment
              // so we can honour this meter's own decimal preference.
              // The API returns r.meter_serial when _build_holley_context
              // was able to resolve it; fall back to Auto otherwise.
              const asn = scopedAssignments.find(
                x => x.meter_serial === r.meter_serial
              );
              const fd = asn?.fraction_digits;
              return (
                <tr key={i}>
                  <td style={{ color: c.textMuted }}>{i + 1}</td>
                  <td>
                    <div>{r.object_name || r.meter_serial}</div>
                    <div style={{ fontFamily: 'monospace', color: c.textMuted, fontSize: '10px' }}>{r.meter_serial}</div>
                  </td>
                  <td style={{ fontSize: '10px' }}>{r.start_time || '—'}</td>
                  <td style={{ fontSize: '10px' }}>{r.end_time || '—'}</td>
                  {regs.map((reg, j) => {
                    const v = regMap[reg.obis]?.consumption;
                    return (
                      <td key={j} style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                        {v == null ? '—' : formatMeterNumber(v, fd)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {busy && (
              <tr><td colSpan={4 + regs.length} style={{ color: c.textMuted, padding: '12px', textAlign: 'center' }}>
                <Spinner /> Loading…
              </td></tr>
            )}
            {!busy && rows.length === 0 && (
              <tr><td colSpan={4 + regs.length} style={{ color: c.textDimmed, padding: '12px', textAlign: 'center', fontStyle: 'italic' }}>
                {scopedAssignments.length === 0 ? 'No meters under this subscriber.' : 'No data — pick a range with daily-billing snapshots and click Run.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Collection Status sub-pane of SubscriberTabbedView. Uses
// /api/customers/{id}/polling-stats for the top summary and then
// fans out /api/meters/{serial}/collection-stats per child meter
// for the breakdown table. Transformer / DCU selections don't have
// their own aggregate endpoint yet — they compute client-side from
// the per-meter breakdown.
// Subscriber-level event aggregation — fans out event-log profile
// reads across all meters under the subscriber and merges the rows
// into a single sortable/filterable table with CSV export.
function SubscriberEventsPane({ scopedAssignments, selection }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [searchText, setSearchText] = useState('');

  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86400 * 1000);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(fmt(weekAgo));
  const [toDate, setToDate] = useState(fmt(today));

  useEffect(() => {
    api('/event-codes').then(setCatalog).catch(() => {});
  }, []);

  const loadEvents = async () => {
    if (scopedAssignments.length === 0) return;
    setLoading(true);
    setEvents([]);
    try {
      // Calculate days between fromDate and toDate
      const fromMs = new Date(fromDate).getTime();
      const toMs = new Date(toDate).getTime() + 86400 * 1000;
      const days = Math.max(1, Math.ceil((toMs - fromMs) / (86400 * 1000)));

      // Use batched endpoint: /api/customers/{id}/power-events for customer
      // selection, or /api/power-events?serials=... for transformer/dcu/all.
      let url;
      if (selection?.type === 'customer') {
        url = `/customers/${selection.id}/power-events?days=${days}&limit=5000`;
      } else {
        const serials = scopedAssignments.map(a => a.meter_serial).join(',');
        url = `/power-events?serials=${encodeURIComponent(serials)}&days=${days}&limit=5000`;
      }

      const r = await api(url);
      const all = (r?.events || []).map((ev, i) => ({
        no: i + 1,
        meter: ev.serial,
        occurred: ev.row_ts ? fmtDT(ev.row_ts, { seconds: true }) : '',
        category: ev.category,
        code: ev.code,
        name: ev.name,
      }));

      setEvents(all);
      toast.push(t('toast.loaded_n', { n: all.length, what: 'events' }), 'success');
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filtered = searchText
    ? events.filter(e =>
        e.name.toLowerCase().includes(searchText.toLowerCase()) ||
        e.meter.includes(searchText) ||
        e.category.toLowerCase().includes(searchText.toLowerCase())
      )
    : events;

  const exportCsv = () => {
    if (!filtered.length) return;
    const header = ['No.', 'Meter', 'Occurred Time', 'Event Name', 'Category', 'Code'];
    const rows = filtered.map(e => [e.no, e.meter, e.occurred, e.name, e.category, e.code]);
    const csv = [header, ...rows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    downloadCsv(csv, `events_${fromDate}_${toDate}.csv`);
  };

  return (
    <div>
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px',
      }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>{t('common.from')}</div>
          <input type="date" className="input-field" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 style={{ padding: '6px 8px', fontSize: '12px' }} />
        </div>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>{t('common.to')}</div>
          <input type="date" className="input-field" value={toDate}
                 onChange={(e) => setToDate(e.target.value)}
                 style={{ padding: '6px 8px', fontSize: '12px' }} />
        </div>
        <input
          className="input-field"
          placeholder="Search event or meter..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ padding: '6px 8px', fontSize: '12px', width: '200px' }}
        />
        <button className="button button-primary" onClick={loadEvents}
                disabled={loading || scopedAssignments.length === 0}>
          {loading ? <Spinner /> : '🔍'} {t('btn.search')}
        </button>
        <button className="button button-secondary" onClick={exportCsv} disabled={!filtered.length}>
          ⬇ CSV
        </button>
      </div>

      {loading && <div style={{ color: c.textMuted, fontSize: '12px' }}>{t('common.loading')}</div>}

      {!loading && events.length === 0 && (
        <div style={{ color: c.textMuted, fontSize: '12px', padding: '24px', textAlign: 'center' }}>
          Select a date range and click Search to aggregate events across {scopedAssignments.length} meter(s).
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{
          background: c.bgCard, border: `1px solid ${c.border}`,
          borderRadius: '8px', overflow: 'auto', maxHeight: '60vh',
        }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: c.bgCardAlt }}>
              <tr>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>#</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>{t('common.meter')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>{t('common.time')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>{t('th.event_name')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>Category</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>{t('th.event_code')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.no} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 8px', color: c.textMuted }}>{e.no}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: c.textPrimary }}>{e.meter}</td>
                  <td style={{ padding: '4px 8px', color: c.textSecondary, whiteSpace: 'nowrap' }}>{e.occurred}</td>
                  <td style={{ padding: '4px 8px', color: c.textPrimary }}>{e.name}</td>
                  <td style={{ padding: '4px 8px', color: c.textSecondary }}>{e.category}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: c.textMuted }}>
                    0x{Number(e.code || 0).toString(16).padStart(4, '0')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '8px 12px', fontSize: '11px', color: c.textMuted }}>
            {filtered.length} event(s){filtered.length !== events.length ? ` (${events.length} total, ${events.length - filtered.length} filtered)` : ''}
          </div>
        </div>
      )}
    </div>
  );
}


// Admin-only card that shows which meters under the current tree
// selection have yesterday's daily + PQ + LP rows persisted in
// SQLite. A quick "did the midnight scheduler succeed?" self-check.
function YesterdayCoverageCard({ scopedAssignments }) {
  const { user } = useAuth();
  const c = useThemeColors();
  const toast = React.useContext(ToastContext);
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(async (opts) => {
    const interactive = opts && opts.interactive;
    setBusy(true);
    setErr(null);
    const started = Date.now();
    try {
      const r = await api('/coverage/yesterday');
      setData(r);
      if (interactive && toast) {
        toast.push('Coverage refreshed', 'success');
      }
    } catch (e) {
      const msg = e.message || String(e);
      setErr(msg);
      if (interactive && toast) {
        toast.push(`Refresh failed: ${msg}`, 'error');
      }
    } finally {
      // Keep the spinner visible for at least 350 ms so there's a
      // noticeable flash on click — prevents the "did anything happen?"
      // feel when the endpoint responds in <100 ms.
      const elapsed = Date.now() - started;
      const minShow = 350;
      if (elapsed < minShow) {
        setTimeout(() => setBusy(false), minShow - elapsed);
      } else {
        setBusy(false);
      }
    }
  }, [toast]);

  // Manual only (2026-07-09): do NOT auto-fetch coverage on open. Operators
  // refresh via the 🔄 button; a scheduled 09:00 Tashkent collection pass keeps
  // the stored data fresh for the morning. (These reads are SQLite-only, but the
  // constant on-open fetch churned the Collection Status view.)

  if (!userCan(user, 'dcu_diagnostics')) return null;

  const scopedSet = new Set(
    (scopedAssignments || []).map(a => a.meter_serial)
  );
  const visible = (data?.meters || []).filter(m => scopedSet.has(m.serial));
  const summary = visible.reduce((acc, m) => {
    acc.total++;
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, { total: 0, ok: 0, partial: 0, missing: 0 });

  const statusDot = (s) =>
    s === 'ok' ? '🟢' : s === 'partial' ? '🟡' : '🔴';

  return (
    <div style={{
      background: c.bgCard, border: `1px solid ${c.border}`,
      borderRadius: '8px', padding: '12px 14px', marginBottom: '12px',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '10px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
          📅 Yesterday's coverage
          {data?.date && (
            <span style={{ fontSize: '11px', color: c.textMuted, marginLeft: '8px', fontWeight: 400 }}>
              {data.date} · {data.tz}
            </span>
          )}
          {visible.length > 0 && (
            <span style={{ fontSize: '11px', color: c.textMuted, marginLeft: '10px', fontWeight: 400 }}>
              · 🟢 {summary.ok} · 🟡 {summary.partial || 0} · 🔴 {summary.missing || 0} / {summary.total}
            </span>
          )}
        </div>
        <button
          className="button button-sm button-secondary"
          onClick={() => load({ interactive: true })}
          disabled={busy}
          title="Refresh coverage"
        >
          {busy ? <Spinner /> : '🔄'}
        </button>
      </div>
      {err && (
        <div style={{ fontSize: '12px', color: '#f87171' }}>Load failed: {err}</div>
      )}
      {/* Scheduler fire banner. If the midnight scheduler didn't
          fire today (or didn't run at all), the per-meter counts
          below don't mean much — the primary source of yesterday's
          data is the 01:00 local pull. Surface that clearly. */}
      {data?.scheduler && (() => {
        const s = data.scheduler;
        if (!s.running) {
          return (
            <div style={{
              fontSize: '12px', color: '#fbbf24', marginBottom: '8px',
              padding: '6px 10px', background: '#3d2f0a',
              border: '1px solid #5c4410', borderRadius: '4px',
            }}>
              ⚠ Midnight scheduler is not running — per-meter counts
              below reflect only ad-hoc pulls.
            </div>
          );
        }
        if (!s.fired_for_yesterday) {
          return (
            <div style={{
              fontSize: '12px', color: '#fbbf24', marginBottom: '8px',
              padding: '6px 10px', background: '#3d2f0a',
              border: '1px solid #5c4410', borderRadius: '4px',
            }}>
              ⚠ Scheduler hasn't fired yet for {data.date} — last run
              {' '}<strong>{s.last_run_date || 'never'}</strong> at
              {' '}{s.hour_of_day}:00 {s.tz}. Counts below may be
              stale until today's run completes.
            </div>
          );
        }
        return (
          <div style={{
            fontSize: '11px', color: c.textMuted, marginBottom: '8px',
            padding: '6px 10px', background: c.bgCardAlt,
            border: `1px solid ${c.border}`, borderRadius: '4px',
          }}>
            ✓ Midnight scheduler fired for {data.date}
            {s.last_duration_seconds != null && <> in {s.last_duration_seconds}s</>}
            {s.last_meters_succeeded != null && s.last_meters_attempted != null && (
              <> · {s.last_meters_succeeded}/{s.last_meters_attempted} meters succeeded</>
            )}
            {' '}· scheduler runs daily at {s.hour_of_day}:00 {s.tz}
          </div>
        );
      })()}
      {!err && !busy && data === null && (
        <div style={{ fontSize: '12px', color: c.textMuted, fontStyle: 'italic' }}>
          Coverage not loaded — click 🔄 to check (manual; auto-collected daily at 09:00 Tashkent).
        </div>
      )}
      {!err && visible.length === 0 && !busy && data !== null && (
        <div style={{ fontSize: '12px', color: c.textMuted, fontStyle: 'italic' }}>
          No meters in this scope to check.
        </div>
      )}
      {visible.length > 0 && (
        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: c.textSecondary }}>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>#</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Meter</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Daily</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>PQ</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>LP</th>
              <th style={{ textAlign: 'center', padding: '4px 6px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((m, i) => (
              <tr key={m.serial} style={{ borderTop: `1px solid ${c.border}` }}>
                <td style={{ padding: '4px 6px', color: c.textMuted }}>{i + 1}</td>
                <td style={{ padding: '4px 6px', fontFamily: 'monospace', color: c.textPrimary }}>
                  {m.serial}
                  {m.dcu_routed && <span style={{ color: c.textMuted, marginLeft: '6px' }}>(via DCU)</span>}
                </td>
                <td style={{
                  padding: '4px 6px', textAlign: 'right',
                  color: m.daily >= 1 ? c.success : c.danger,
                }}>{m.daily}</td>
                <td style={{
                  padding: '4px 6px', textAlign: 'right',
                  color: m.dcu_routed ? c.textMuted : m.pq >= 48 ? c.success : m.pq > 0 ? c.warning : c.danger,
                }}>{m.dcu_routed ? '—' : m.pq}</td>
                <td style={{
                  padding: '4px 6px', textAlign: 'right',
                  color: m.dcu_routed ? c.textMuted : m.lp >= 48 ? c.success : m.lp > 0 ? c.warning : c.danger,
                }}>{m.dcu_routed ? '—' : m.lp}</td>
                <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                  {statusDot(m.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SubscriberCollectionPane({ selection, scopedAssignments }) {
  const [summary, setSummary] = useState(null);
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const [perMeter, setPerMeter] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastAt, setLastAt] = useState(null);

  // Stable signature of the current scope. The subscriber tree re-fetches
  // assignments every ~10s and hands us a NEW scopedAssignments array
  // reference each time; keying the load on the array reference re-fanned
  // out collection-stats for every meter on that 10s loop (200+ requests
  // per cycle on a large DCU). Key on selection + the sorted serial list
  // instead so we only reload when the scope ACTUALLY changes — then it's
  // a manual 🔄 button from there (no background auto-refresh).
  const serialSig = scopedAssignments.map(a => a.meter_serial).sort().join(',');
  const scopeKey = `${selection?.type || ''}:${selection?.id || ''}:${serialSig}`;

  const load = useCallback(async (opts) => {
    const interactive = opts && opts.interactive;
    setLoading(true);
    try {
      if (selection?.type === 'customer') {
        api(`/customers/${selection.id}/polling-stats`)
          .then(s => setSummary(s)).catch(() => {});
      } else {
        setSummary(null);
      }
      await Promise.all(scopedAssignments.map(a =>
        api(`/meters/${a.meter_serial}/collection-stats?days=14`)
          .then(s => setPerMeter(prev => ({ ...prev, [a.meter_serial]: s })))
          .catch(() => {})
      ));
      setLastAt(new Date());
      if (interactive) toast.push('Collection status refreshed', 'success');
    } finally {
      setLoading(false);
    }
    // selection/scopedAssignments captured via scopeKey identity.
    // eslint-disable-next-line
  }, [scopeKey]);

  // Manual only (2026-07-09): clear stale rows when the scope changes, but do
  // NOT auto-fan collection-stats on open — the operator clicks 🔄 Refresh.
  // The 09:00 Tashkent scheduled pass keeps the underlying data fresh.
  useEffect(() => {
    setSummary(null);
    setPerMeter({});
    setLastAt(null);
    // eslint-disable-next-line
  }, [scopeKey]);

  const pctColor = (pct) => pct == null ? '#64748b' : pct >= 90 ? '#22c55e' : pct >= 50 ? '#fbbf24' : '#ef4444';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '12px', color: c.textMuted }}>
          {loading
            ? 'Loading collection status…'
            : (lastAt ? `Last refreshed ${lastAt.toLocaleTimeString()}` : '—')}
          <span style={{ marginLeft: '8px' }}>· manual refresh only (no auto-refresh)</span>
        </div>
        <button className="button button-sm button-secondary"
          onClick={() => load({ interactive: true })} disabled={loading}>
          {loading ? <Spinner /> : '🔄 Refresh'}
        </button>
      </div>
      <YesterdayCoverageCard scopedAssignments={scopedAssignments} />
      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '10px',
          marginBottom: '14px',
        }}>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '12px' }}>
            <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>Daily Billing (month)</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: pctColor(summary.daily_billing?.pct) }}>
              {summary.daily_billing?.pct ?? '—'}%
            </div>
            <div style={{ fontSize: '10px', color: c.textMuted }}>
              {summary.daily_billing?.stored} / {summary.daily_billing?.expected}
            </div>
          </div>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '12px' }}>
            <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>Load Profile (month)</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: pctColor(summary.load_profile?.pct) }}>
              {summary.load_profile?.pct ?? '—'}%
            </div>
            <div style={{ fontSize: '10px', color: c.textMuted }}>
              {summary.load_profile?.stored} / {summary.load_profile?.expected}
            </div>
          </div>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '12px' }}>
            <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>Meters in scope</div>
            <div style={{ fontSize: '24px', fontWeight: 700 }}>{summary.meter_count}</div>
          </div>
        </div>
      )}
      <div className="table-container" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
        <table style={{ fontSize: '11px', width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
            <tr>
              <th>{t('common.meter')}</th>
              <th style={{ textAlign: 'right' }}>Daily 14d</th>
              <th style={{ textAlign: 'right' }}>Load 14d</th>
              <th style={{ textAlign: 'right' }}>ok / partial / missing</th>
            </tr>
          </thead>
          <tbody>
            {scopedAssignments.map(a => {
              const cs = perMeter[a.meter_serial];
              const days = cs?.days || [];
              const dbTotal = days.reduce((s, d) => s + d.daily_billing.stored, 0);
              const dbExp = days.reduce((s, d) => s + d.daily_billing.expected, 0);
              const lpTotal = days.reduce((s, d) => s + d.load_profile.stored, 0);
              const lpExp = days.reduce((s, d) => s + d.load_profile.expected, 0);
              const dbPct = dbExp ? Math.min(100, Math.round(100 * dbTotal / dbExp)) : null;
              const lpPct = lpExp ? Math.min(100, Math.round(100 * lpTotal / lpExp)) : null;
              const counts = { ok: 0, partial: 0, missing: 0, today: 0 };
              days.forEach(d => { counts[d.status] = (counts[d.status] || 0) + 1; });
              return (
                <tr key={a.id}>
                  <td>
                    <div>{a.object_name || a.meter_serial}</div>
                    <div style={{ fontFamily: 'monospace', color: c.textMuted, fontSize: '10px' }}>{a.meter_serial}</div>
                  </td>
                  <td style={{ textAlign: 'right', color: pctColor(dbPct) }}>
                    {dbPct == null ? '—' : `${dbPct}%`}
                  </td>
                  <td style={{ textAlign: 'right', color: pctColor(lpPct) }}>
                    {lpPct == null ? '—' : `${lpPct}%`}
                  </td>
                  <td style={{ textAlign: 'right', fontSize: '10px' }}>
                    <span style={{ color: c.success }}>{counts.ok}</span>
                    {' / '}
                    <span style={{ color: c.warning }}>{counts.partial}</span>
                    {' / '}
                    <span style={{ color: c.danger }}>{counts.missing}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Installation Status sub-pane of SubscriberTabbedView. Renders the
// "Device Status" table from the CAS reference product: one row per
// meter type (1-Phase / 3-Phase Direct / 3-Phase CT / 3-Phase CT/VT)
// with counts of Installed / Commissioned / On / Off / No Relay.
// All computed client-side from the scoped assignment list + live
// inboundMeters. "Commissioned" here means the meter has a secure
// session open at least once (identified). "On / Off" reflects the
// live session_open flag as a proxy for power state. "No Relay"
// counts meters whose device_type is known to lack a relay.
function SubscriberInstallationPane({ scopedAssignments, inboundMeters }) {
  const c = useThemeColors();
  const t = useT();
  const liveBySerial = React.useMemo(() => {
    const out = {};
    for (const m of inboundMeters) out[m.serial_number || m.serial] = m;
    return out;
  }, [inboundMeters]);

  const rows = React.useMemo(() => {
    // Bucket by meter_type.code (from the backend-resolved block).
    const buckets = {};
    const total = { installed: 0, commissioned: 0, on: 0, off: 0, no_relay: 0 };
    const label = (code) => ({ 1: '1-Phase', 3: '3-Phase Direct', 4: '3-Phase CT', 5: '3-Phase CT/VT' }[code] || 'Unknown');
    for (const a of scopedAssignments) {
      const live = liveBySerial[a.meter_serial];
      const mt = a.meter_type || live?.meter_type;
      const code = mt?.code ?? 0;
      const key = label(code);
      if (!buckets[key]) buckets[key] = { installed: 0, commissioned: 0, on: 0, off: 0, no_relay: 0 };
      buckets[key].installed += 1;
      total.installed += 1;
      if (live?.identified) {
        buckets[key].commissioned += 1;
        total.commissioned += 1;
      }
      if (live?.session_open) {
        buckets[key].on += 1;
        total.on += 1;
      } else {
        buckets[key].off += 1;
        total.off += 1;
      }
      // Meters with device_type_code outside {1,3,4,5} don't have a
      // disconnect relay per the CAS spec (DCUs, for example).
      if (code && ![1, 3, 4, 5].includes(code)) {
        buckets[key].no_relay += 1;
        total.no_relay += 1;
      }
    }
    return { buckets, total };
  }, [scopedAssignments, liveBySerial]);

  return (
    <div>
      <div className="table-container">
        <table style={{ fontSize: '12px', width: '100%' }}>
          <thead>
            <tr>
              <th>{t('form.meter_type')}</th>
              <th style={{ textAlign: 'right' }}>Installed</th>
              <th style={{ textAlign: 'right' }}>Commissioned</th>
              <th style={{ textAlign: 'right' }}>On</th>
              <th style={{ textAlign: 'right' }}>Off</th>
              <th style={{ textAlign: 'right' }}>No Relay</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '2px solid #1e293b', fontWeight: 700 }}>
              <td>Total</td>
              <td style={{ textAlign: 'right' }}>{rows.total.installed}</td>
              <td style={{ textAlign: 'right' }}>{rows.total.commissioned}</td>
              <td style={{ textAlign: 'right', color: c.success }}>{rows.total.on}</td>
              <td style={{ textAlign: 'right', color: c.danger }}>{rows.total.off}</td>
              <td style={{ textAlign: 'right' }}>{rows.total.no_relay}</td>
            </tr>
            {Object.entries(rows.buckets).map(([name, b]) => (
              <tr key={name}>
                <td>{name}</td>
                <td style={{ textAlign: 'right' }}>{b.installed}</td>
                <td style={{ textAlign: 'right' }}>{b.commissioned}</td>
                <td style={{ textAlign: 'right', color: c.success }}>{b.on}</td>
                <td style={{ textAlign: 'right', color: c.danger }}>{b.off}</td>
                <td style={{ textAlign: 'right' }}>{b.no_relay}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '10px' }}>
        Commissioned = meter has identified at the CAS server at least once ·
        On / Off reflects the current live secure-session status ·
        No Relay = device_type without a disconnect relay.
      </div>
    </div>
  );
}

// (CustomerDetail removed in Phase 4.1 — replaced by SubscriberCustomerHome.)

function categoryLabel(c) {
  return ({ general: 'GENERAL', payment: '💰 PAYMENT', service: '🔧 SERVICE', ops: '⚙ OPS' })[c] || c.toUpperCase();
}
function categoryColor(c) {
  return ({ general: '#64748b', payment: '#fbbf24', service: '#00e5ff', ops: '#a855f7' })[c] || '#64748b';
}

function TransformerDetail({ transformer, customer, assignments, inboundMeters, isAdmin, onChanged }) {
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  if (!transformer) return <div>Transformer not found</div>;
  const liveBySerial = React.useMemo(() => {
    const m = {};
    for (const l of (inboundMeters || [])) m[l.serial_number || l.serial] = l;
    return m;
  }, [inboundMeters]);
  const tOnline = assignments.filter(a => liveBySerial[a.meter_serial]?.online || liveBySerial[a.meter_serial]?.session_open).length;
  const tOffline = assignments.length - tOnline;

  const deleteTransformer = async () => {
    if (assignments.length > 0) {
      toast.push(`Cannot delete "${transformer.name}" — unassign ${assignments.length} meter(s) first.`, 'error');
      return;
    }
    if (!(await confirm(`Delete "${transformer.name}"?`, 'This transformer will be permanently removed. This action cannot be undone.'))) return;
    try {
      await api(`/transformers/${transformer.id}`, { method: 'DELETE' });
      toast.push(t('toast.transformer_deleted'), 'success');
      onChanged();
    } catch (err) {
      toast.push(err.message, 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>⚡ {transformer.name}</div>
          {customer && (
            <div style={{ fontSize: '13px', color: c.textMuted }}>{t('common.owner')}: {customer.name}</div>
          )}
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="button button-secondary" onClick={() => setEditing(true)}>✏ {t('btn.edit')}</button>
            <button className="button button-secondary" onClick={deleteTransformer}>🗑 {t('btn.delete')}</button>
          </div>
        )}
        {!isAdmin && userCan(user, 'rename') && (
          <button className="button button-secondary" onClick={() => setRenaming(true)}>✏ {t('btn.rename')}</button>
        )}
      </div>
      <DetailGrid items={[
        [t('common.location'), transformer.location],
        [t('common.created'), fmtDT(transformer.created_at, { utc: true, seconds: true })],
      ]} />

      <div style={{ marginTop: '14px' }}>
        {/* Per-day collection rate — same component/computation as the
            customer view, so an operator sees WHEN collection changed rather
            than only a per-meter OK/Partial/Fail badge. */}
        <CustomerPollingMetricsCard
          scope="transformers"
          scopeId={transformer.id}
          scopeName={transformer.name}
        />
      </div>

      {/* Online/Offline summary */}
      {assignments.length > 0 && (
        <div style={{ display: 'flex', gap: '10px', marginTop: '14px', marginBottom: '8px' }}>
          <div style={{ padding: '6px 14px', borderRadius: '8px', background: '#22c55e22', border: '1px solid #22c55e44', fontSize: '12px' }}>
            <span style={{ color: '#22c55e', fontWeight: 700 }}>{tOnline}</span> <span style={{ color: c.textMuted }}>{t('common.online')}</span>
          </div>
          {tOffline > 0 && (
            <div style={{ padding: '6px 14px', borderRadius: '8px', background: '#ef444422', border: '1px solid #ef444444', fontSize: '12px' }}>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>{tOffline}</span> <span style={{ color: c.textMuted }}>{t('common.offline')}</span>
            </div>
          )}
          <div style={{ padding: '6px 14px', borderRadius: '8px', background: `${c.accent}22`, border: `1px solid ${c.accent}44`, fontSize: '12px' }}>
            <span style={{ color: c.accent, fontWeight: 700 }}>{assignments.length}</span> <span style={{ color: c.textMuted }}>{t('common.total')}</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: '14px', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: c.textSecondary }}>
        {t('tf.meters_on')} ({assignments.length})
      </div>
      {assignments.length === 0 ? (
        <div style={{ color: c.textDimmed, fontSize: '12px', fontStyle: 'italic' }}>{t('common.none_assigned')}</div>
      ) : (
        <table style={{ width: '100%', fontSize: '13px' }}>
          <thead>
            <tr style={{ color: c.textSecondary, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
              <th style={{ padding: '6px 8px' }}>{t('th.serial')}</th>
              <th style={{ padding: '6px 8px' }}>{t('th.name')}</th>
              <th style={{ padding: '6px 8px' }}>{t('th.type')}</th>
              <th style={{ padding: '6px 8px' }}>{t('th.role')}</th>
              <th style={{ padding: '6px 8px' }}>{t('th.conn')}</th>
              <th style={{ padding: '6px 8px' }}>{t('th.ct_vt')}</th>
            </tr>
          </thead>
          <tbody>
            {assignments.map(a => (
              <tr key={a.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{a.meter_serial}</td>
                <td style={{ padding: '6px 8px' }}>{a.object_name || '—'}</td>
                <td style={{ padding: '6px 8px' }}><MeterTypeBadge mt={a.meter_type} compact /></td>
                <td style={{ padding: '6px 8px' }}>{a.role === 'main' ? `🟧 ${t('role.main')}` : `🔌 ${t('role.customer')}`}</td>
                <td style={{ padding: '6px 8px' }}>{a.connection_type || '—'}</td>
                <td style={{ padding: '6px 8px' }}>{a.ct_ratio || '—'} / {a.vt_ratio || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditTransformerModal
          transformer={transformer}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
        />
      )}
      {renaming && (
        <RenameModal
          title={t('modal.rename_transformer')}
          initial={transformer.name}
          path={`/transformers/${transformer.id}/rename`}
          onClose={() => setRenaming(false)}
          onSaved={() => { setRenaming(false); onChanged(); }}
        />
      )}
    </div>
  );
}

// LossCalculator removed 2026-04-26 per operator request — the
// energy-loss view wasn't matching how the fleet is operated
// (most transformers have no main meter assigned, operators read
// loss from upstream SCADA). Backend /api/transformers/{id}/loss
// is left in place in case the calculator is re-surfaced later.

// ─── DCU node detail ───
// DCU Home pane. When the DCU has multiple meters routed through
// it, the operator should see an aggregate summary BEFORE drilling
// into a specific meter (per CAS reference product behaviour).
//
// The summary mirrors the per-customer Home tab — Collection Health
// card, Device Status breakdown, and a meter list with quick links
// to the per-meter detail. Single-meter DCUs collapse to a thin
// card so the operator just clicks through.
function DcuMeterRegForm({ initial, onSubmit, onCancel, loading }) {
  const c = useThemeColors();
  const [form, setForm] = React.useState({
    serial: initial?.serial || '',
    address: initial?.address || '',
    sapId: initial?.sapId || '2',
    // Default to G3 PLC. RS-485 is picked explicitly when needed.
    commType: initial?.commType || '6',
    clientAddr: initial?.clientAddr || '17',
    serverAddr: initial?.serverAddr || '1',
    password: initial?.password || '00000000',
    ctRatio: initial?.ctRatio || '5/5',
    ptRatio: initial?.ptRatio || '220/220',
    // Auto-detect from serial's 3rd digit (CAS §4.3). Operator can
    // override the dropdown. Falls back to '1' (3P Direct) only if
    // the serial isn't available yet at initial-state time.
    meterType: initial?.meterType
      || _deriveMeterTypeFromSerial(initial?.serial || '')
      || '1',
    // '' = Auto (NULL on server), '0'..'3' = exact decimal places.
    fractionDigits: initial?.fractionDigits ?? '',
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const fieldStyle = { width: '100%', padding: '6px 10px', borderRadius: '6px', border: `1px solid ${c.border}`, background: c.bgInput || c.bgCard, color: c.textPrimary, fontSize: '13px' };
  const labelStyle = { fontSize: '12px', color: c.textLabel, marginBottom: '4px', display: 'block' };
  const rowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' };

  return (
    <div>
      <div style={rowStyle}>
        <div>
          <label style={labelStyle}>Meter Serial</label>
          <input style={fieldStyle} value={form.serial} onChange={e => set('serial', e.target.value)} placeholder="124200356974" />
        </div>
        <div>
          <label style={labelStyle}>Password</label>
          <input style={fieldStyle} value={form.password} onChange={e => set('password', e.target.value)} />
        </div>
      </div>
      <div style={rowStyle}>
        <div>
          <label style={labelStyle}>Meter Type</label>
          <select style={fieldStyle} value={form.commType} onChange={e => set('commType', e.target.value)}>
            <option value="1">RS-485</option>
            <option value="6">G3 PLC</option>
            <option value="7">RF</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Comm Address</label>
          <input style={fieldStyle} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Auto: last4+1000" />
        </div>
      </div>
      <div style={rowStyle}>
        <div>
          <label style={labelStyle}>CT Ratio</label>
          <select style={fieldStyle} value={form.ctRatio} onChange={e => set('ctRatio', e.target.value)}>
            {CT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>PT Ratio</label>
          <select style={fieldStyle} value={form.ptRatio} onChange={e => set('ptRatio', e.target.value)}>
            {['220/220','380/220','6000/220','10000/220','20000/220','35000/220','110000/220','35000/100','40000/100'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div style={rowStyle}>
        <div>
          <label style={labelStyle}>Server Address</label>
          <input style={fieldStyle} type="number" value={form.serverAddr} onChange={e => set('serverAddr', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Client Address</label>
          <input style={fieldStyle} type="number" value={form.clientAddr} onChange={e => set('clientAddr', e.target.value)} />
        </div>
      </div>
      <div style={rowStyle}>
        <div>
          <label style={labelStyle}>SAP ID</label>
          <input style={fieldStyle} type="number" value={form.sapId} onChange={e => set('sapId', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Device Type</label>
          <select style={fieldStyle} value={form.meterType} onChange={e => set('meterType', e.target.value)}>
            <option value="0">Single Phase (1P)</option>
            <option value="1">3-Phase Direct</option>
            <option value="2">3-Phase CT</option>
            <option value="3">3-Phase CT/VT</option>
          </select>
        </div>
      </div>
      <div style={rowStyle}>
        <div>
          <label style={labelStyle}>Decimal places (display)</label>
          <select style={fieldStyle} value={form.fractionDigits} onChange={e => set('fractionDigits', e.target.value)}>
            <option value="">Auto (from meter scaler)</option>
            <option value="0">0 — whole numbers</option>
            <option value="1">1 — e.g. 7,817.0</option>
            <option value="2">2 — e.g. 7,816.98</option>
            <option value="3">3 — e.g. 7,816.980</option>
          </select>
        </div>
        <div />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
        <button onClick={onCancel} style={{ padding: '8px 20px', borderRadius: '6px', border: `1px solid ${c.border}`, background: c.bgCard, color: c.textPrimary, cursor: 'pointer' }}>Cancel</button>
        <button onClick={() => onSubmit(form)} disabled={loading || !form.serial}
          style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Registering...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}

// Module-level cache for DCU health / network data so we don't re-pull
// from the physical DCU every time the admin navigates back to the
// page. Keyed by DCU serial. Each component reads first, shows the
// stale timestamp, and pulls fresh only when the operator clicks
// Refresh (or on first-ever mount). Kill-switch flips + LIVE writes
// invalidate the relevant entry.
// DCU info-bundle cache. Persists across page reloads via localStorage
// because the data (firmware, IMEI, ICCID, modem FW, signal levels)
// only changes when something physically changes at the meter site —
// so re-pulling on every page load just costs us a DLMS round-trip
// for no operator benefit. Operator ask 2026-04-26: "DCU information
// doesn't change daily unless its the heartbeat. rest should be
// readily avalible in the system already."
const _DCU_CACHE_LS_KEY = 'emeter.dcuDataCache.v1';
const _dcuDataCache = (() => {
  try {
    const raw = localStorage.getItem(_DCU_CACHE_LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
})();
function _persistDcuCache() {
  try { localStorage.setItem(_DCU_CACHE_LS_KEY, JSON.stringify(_dcuDataCache)); }
  catch (_) { /* quota exceeded or disabled — keep in-memory only */ }
}
function dcuCacheGet(serial, key) {
  return _dcuDataCache[serial]?.[key] ?? null;
}
function dcuCacheGetTs(serial, key) {
  return _dcuDataCache[serial]?.[`${key}Ts`] ?? null;
}
function dcuCacheSet(serial, key, value) {
  if (!_dcuDataCache[serial]) _dcuDataCache[serial] = {};
  _dcuDataCache[serial][key] = value;
  _dcuDataCache[serial][`${key}Ts`] = Date.now();
  _persistDcuCache();
}
function dcuCacheInvalidate(serial, key) {
  if (!_dcuDataCache[serial]) return;
  delete _dcuDataCache[serial][key];
  delete _dcuDataCache[serial][`${key}Ts`];
}


function DcuNodeDetail({ dcuSerial, assignments, dcuSelfAssign, inboundMeters, transformers, customers, isAdmin, onChanged }) {
  const { setSelected } = useMeterSelection();
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [unassignBusy, setUnassignBusy] = React.useState(false);
  const [showReassign, setShowReassign] = React.useState(false);

  // Unassign DCU from its transformer + cascade to child meters so
  // they follow the DCU instead of getting orphaned under a transformer
  // whose DCU has moved away. Admin-only.
  const handleUnassignDcu = React.useCallback(async () => {
    if (!dcuSelfAssign || dcuSelfAssign.transformer_id == null) {
      toast.push('DCU is already unassigned', 'info');
      return;
    }
    const txName = transformers?.find(t => t.id === dcuSelfAssign.transformer_id)?.name
                 || `transformer ${dcuSelfAssign.transformer_id}`;
    const childCount = assignments.length;
    const ok = await confirm(
      `Unassign DCU ${dcuSerial}?`,
      `This DCU is currently under "${txName}". Unassigning it will also ` +
      `unassign its ${childCount} downstream meter${childCount === 1 ? '' : 's'} ` +
      `so they follow the DCU. You can reassign them together to a new ` +
      `transformer / customer from the Subscribers tree.`,
      { confirmLabel: 'Unassign', confirmColor: '#dc2626' },
    );
    if (!ok) return;
    setUnassignBusy(true);
    try {
      // Single POST — backend cascades the transformer_id change to
      // every meter routed through this DCU (see upsert_assignment).
      // Previous client-side N+1 loop hung the UI for 200+ meter DCUs.
      await api('/assignments', {
        method: 'POST',
        body: JSON.stringify({
          meter_serial: dcuSerial,
          dcu_serial: dcuSerial,
          role: dcuSelfAssign.role || 'customer',
          transformer_id: null,
          object_name: dcuSelfAssign.object_name || dcuSerial,
        }),
      });
      toast.push(
        `Unassigned DCU + ${childCount} meter(s). Reassign from the Unassigned pool.`,
        'success',
      );
      if (onChanged) onChanged();
    } catch (err) {
      toast.push(`Unassign failed: ${err.message || err}`, 'error');
    } finally {
      setUnassignBusy(false);
    }
  }, [dcuSerial, dcuSelfAssign, assignments, transformers, toast, confirm, onChanged]);
  const dcuMeter = inboundMeters.find(m => (m.serial_number || m.serial) === dcuSerial);
  const [dcuStatus, setDcuStatus] = React.useState(null);
  const [dcuProfile, setDcuProfile] = React.useState(null);
  const [blacklist, setBlacklist] = React.useState(null);
  const [regDialog, setRegDialog] = React.useState(null);
  const [regLoading, setRegLoading] = React.useState(false);
  // Health bundle for the DCU Info panel. Cached module-level so
  // navigating away and back shows the last-known values without
  // re-hitting the DCU. First-mount fetch; manual refresh on demand.
  const [health, setHealth] = React.useState(() => dcuCacheGet(dcuSerial, 'health'));
  const [healthTs, setHealthTs] = React.useState(() => dcuCacheGetTs(dcuSerial, 'health'));
  const [healthBusy, setHealthBusy] = React.useState(false);

  // Fetch DCU live status + profiles. The blacklist is NOT auto-read here
  // (it drives a DLMS multi-block read on the DCU); read it manually from
  // Device Management > DCU > Blacklist. The info banner below only shows
  // once a blacklist has been read there in this session.
  React.useEffect(() => {
    if (!dcuSerial || !isAdmin) return;
    setDcuStatus(null);
    setDcuProfile(null);
    api(`/dcus/${dcuSerial}/status`).then(setDcuStatus).catch(() => {});
    // /profiles/daily is NOT auto-fetched — it is a LIVE DLMS read of the DCU's
    // aggregate profile (measured 1.5 s .. 47 s on 2026-07-24) that takes the
    // per-DCU lock, so every later action on this DCU queued behind it. That is
    // what made opening a DCU feel laggy. It feeds one informational tile, and
    // the profile is empty fleet-wide, so it is now read on demand only.
  }, [dcuSerial]);

  // On-demand aggregate-profile read (see above).
  const [profileBusy, setProfileBusy] = React.useState(false);
  const loadDcuProfile = React.useCallback(() => {
    if (!dcuSerial || profileBusy) return;
    const forDcu = dcuSerial;
    setProfileBusy(true);
    api(`/dcus/${forDcu}/profiles/daily`)
      .then(p => { if (forDcu === dcuSerial) setDcuProfile(p); })
      .catch(() => {})
      .finally(() => setProfileBusy(false));
  }, [dcuSerial, profileBusy]);

  // Load DCU health bundle — cached per session. Only pull fresh
  // when there's no cached entry OR the operator clicks Refresh.
  const readSimpleRaw = React.useCallback(async (obis, attr, cls) => {
    try {
      const r = await api(
        `/meters/${dcuSerial}/read-raw/${obis}?attribute=${attr}&class_id=${cls}`,
      );
      if (r?.value == null || r.value === 'None') return null;
      let v = String(r.value);
      const m = v.match(/bytearray\(b['"]([^'"]*)['"]\)/);
      if (m) return m[1];
      return v;
    } catch (_) { return null; }
  }, [dcuSerial]);

  const loadHealth = React.useCallback(async () => {
    setHealthBusy(true);
    try {
      const [cq, os, imei, iccid, fw, modemFw, traffic] = await Promise.all([
        api(`/dcus/${dcuSerial}/communication-quality`).catch(() => null),
        api(`/dcus/${dcuSerial}/online-status`).catch(() => null),
        readSimpleRaw('0.0.96.12.6.255', 2, 1),
        readSimpleRaw('0.1.25.1.0.255',  3, 42),
        readSimpleRaw('0.0.2.0.0.255',   2, 1),
        readSimpleRaw('1.0.0.2.0.255',   2, 1),
        readSimpleRaw('0.0.96.12.5.255', 2, 1),
      ]);
      const bundle = { cq, os, imei, iccid, fw, modemFw, traffic };
      dcuCacheSet(dcuSerial, 'health', bundle);
      setHealth(bundle);
      setHealthTs(Date.now());
    } finally {
      setHealthBusy(false);
    }
  }, [dcuSerial, readSimpleRaw]);

  // No first-mount auto-fire (operator ask 2026-04-26). DCU info
  // doesn't change daily — opening the DCU node should NOT trigger
  // 7 parallel DLMS reads every time. Operator clicks Refresh when
  // they want fresh values; otherwise the cached bundle from
  // localStorage renders instantly.

  // Register meter handler
  const handleRegister = async (formData) => {
    setRegLoading(true);
    try {
      const params = new URLSearchParams({
        meter_serial: formData.serial,
        sap_id: formData.sapId || '2',
        comm_type: formData.commType || '1',
        hdlc_addr: formData.address || '0',
        client_addr: formData.clientAddr || '17',
        app_context: '1',
        auth_mech: '1',
      });
      // Persist operator-picked CT/PT/meter-type onto MeterAssignment
      // via the same endpoint (mirrors handleRegister in DcuDetailPane).
      if (formData.ctRatio) params.set('ct_ratio', formData.ctRatio);
      if (formData.ptRatio) params.set('vt_ratio', formData.ptRatio);
      if (formData.meterType !== '' && formData.meterType != null) {
        const code = METER_TYPE_FORM_TO_CAS[String(formData.meterType)];
        if (code) params.set('meter_type_override', String(code));
      }
      // '' = Auto (omit so backend keeps existing value or stays NULL).
      if (formData.fractionDigits !== '' && formData.fractionDigits != null) {
        params.set('fraction_digits', String(formData.fractionDigits));
      }
      params.set('queue', 'true');
      const res = await api(`/meters/${dcuSerial}/register-meter?${params}`, { method: 'POST' });
      if (res && res.job_id) {
        setRegDialog(null);
        const dcu = dcuSerial;
        bgJobs.add({ id: res.job_id, dcu: String(dcu), op: `Register ${formData.serial}`,
          onDone: () => { api(`/dcus/${dcu}/status`).then(setDcuStatus).catch(() => {}); } });
      }
    } catch (e) {
      alert('Registration failed: ' + (e.message || e));
    }
    setRegLoading(false);
  };

  const firstAssign = assignments[0];
  const transformer = firstAssign && transformers?.find(t => t.id === firstAssign.transformer_id);
  const customer = transformer && customers?.find(c => c.id === transformer.customer_id);

  const onlineCount = assignments.filter(a => {
    const live = inboundMeters.find(m => (m.serial_number || m.serial) === a.meter_serial);
    return live?.session_open;
  }).length;
  const offlineCount = assignments.length - onlineCount;

  const buckets = {};
  const typeName = (code) => ({ 1: '1-Phase', 3: '3-Phase Direct', 4: '3-Phase CT', 5: '3-Phase CT/VT' }[code] || 'Unknown');
  for (const a of assignments) {
    const live = inboundMeters.find(m => (m.serial_number || m.serial) === a.meter_serial);
    const code = a.meter_type?.code ?? live?.meter_type?.code ?? 0;
    const key = typeName(code);
    if (!buckets[key]) buckets[key] = { installed: 0, on: 0, off: 0 };
    buckets[key].installed += 1;
    if (live?.session_open) buckets[key].on += 1;
    else buckets[key].off += 1;
  }

  // Parse energy data from DCU profile
  const profileData = React.useMemo(() => {
    if (!dcuProfile?.has_data || !dcuProfile?.value) return null;
    const val = dcuProfile.value;
    // Extract numeric values — look for energy readings pattern
    const hasEnergy = val.includes('781698') || val.includes('124200');
    return hasEnergy ? { raw: val, hasData: true } : null;
  }, [dcuProfile]);

  return (
    <div>
      {/* Registration Dialog */}
      {regDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '24px', width: '500px', maxWidth: '90vw' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Register Meter to DCU</div>
            <DcuMeterRegForm
              initial={regDialog}
              onSubmit={handleRegister}
              onCancel={() => setRegDialog(null)}
              loading={regLoading}
            />
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      {(customer || transformer) && (
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '6px' }}>
          {customer?.name}{transformer ? ` > ${transformer.name}` : ''} > <strong style={{ color: c.textLabel }}>DCU {dcuSerial}</strong>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 600 }}>
            DCU {dcuSerial}{' '}
            <span style={{
              userSelect: 'none',
              marginLeft: '10px', fontSize: '11px',
              background: '#1e40af', color: c.textPrimary,
              padding: '2px 8px', borderRadius: '10px', fontWeight: 500,
            }}>
              {dcuStatus?.downstream_meter_count ?? assignments.length} meter{(dcuStatus?.downstream_meter_count ?? assignments.length) === 1 ? '' : 's'}
            </span>
          </div>
          <div style={{ fontSize: '12px', color: c.textMuted }}>
            {dcuMeter ? `Connected` : 'Not currently connected'}
            {dcuStatus?.signal_strength != null && ` · Signal: ${dcuSignalInfo(dcuStatus).long}`}
          </div>
        </div>
        {isAdmin && dcuSelfAssign?.transformer_id != null && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="button button-primary"
              onClick={() => setShowReassign(true)}
              disabled={unassignBusy}
              title="Move this DCU (and its child meters) to a different customer / transformer in one step"
              style={{ padding: '6px 14px', fontSize: '12px' }}
            >
              🔀 Reassign DCU
            </button>
            <button
              className="button button-secondary"
              onClick={handleUnassignDcu}
              disabled={unassignBusy}
              title="Unassign this DCU from its transformer and cascade to child meters so they can be reassigned together"
              style={{ padding: '6px 14px', fontSize: '12px' }}
            >
              {unassignBusy ? <><Spinner /> Unassigning…</> : '↩ Unassign DCU'}
            </button>
          </div>
        )}
      </div>

      {showReassign && (
        <AssignDcuModal
          transformers={transformers}
          customers={customers}
          inboundMeters={inboundMeters}
          lockedDcu={dcuSerial}
          defaultTransformerId={dcuSelfAssign?.transformer_id}
          onClose={() => setShowReassign(false)}
          onSaved={() => { setShowReassign(false); if (onChanged) onChanged(); }}
        />
      )}

      {/* Top KPI strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '10px',
        marginBottom: '14px',
      }}>
        {/* `??` and NOT `||`, deliberately. A zero here is now a real zero:
            the API overrules an empty parameter-table read with the confirmed
            snapshot before it ever gets sent (_honest_downstream_count).
            Falling back to assignments.length on a falsy 0 would put the old
            roster back on screen for a DCU that was emptied on purpose —
            129235042050 holds 0 and is supposed to show 0. */}
        <KpiTile label="Downstream meters" value={dcuStatus?.downstream_meter_count ?? assignments.length} color="#00e5ff" />
        <div title={dcuSignalInfo(dcuStatus).title}>
          <KpiTile label="Signal strength" value={dcuSignalInfo(dcuStatus).short} color={dcuSignalInfo(dcuStatus).color} />
        </div>
        <KpiTile label="Status"            value={dcuStatus?.session_open ? 'Online' : 'Offline'} color={dcuStatus?.session_open ? '#22c55e' : '#ef4444'} />
        <div onClick={!dcuProfile && !profileBusy ? loadDcuProfile : undefined}
          title={dcuProfile ? 'DCU aggregate profile' : 'Click to read the DCU aggregate profile (live device read — can take up to a minute)'}
          style={{ cursor: !dcuProfile && !profileBusy ? 'pointer' : 'default' }}>
          <KpiTile label="Profile data"
            value={profileBusy ? 'Reading…' : (dcuProfile ? (dcuProfile.has_data ? 'Available' : 'Empty') : 'Tap to read')}
            color={profileBusy ? '#64748b' : (dcuProfile ? (dcuProfile.has_data ? '#22c55e' : '#f59e0b') : '#64748b')} />
        </div>
      </div>

      {/* Per-day collection rate for the meters routed through this DCU —
          the same card as the transformer and customer views. Its meter count
          is the same set as the tree's "N meters" label. Shown first,
          above DCU Information (operator ask, 2026-10-15). */}
      <CustomerPollingMetricsCard
        scope="dcus"
        scopeId={dcuSerial}
        scopeName={dcuSerial}
      />

      {/* DCU Info + Live Status + Health bundle. Merged to one card
          per operator feedback — two cards side-by-side showed the
          same identity twice. Health fields pull from the module-
          level cache; a Refresh button re-hits the DCU when needed. */}
      {dcuMeter && (
        <div style={{
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '8px',
          padding: '14px',
          marginBottom: '14px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
              DCU Information
            </div>
            {isAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {healthTs && (
                  <span style={{ fontSize: '10px', color: c.textMuted }}>
                    read at {new Date(healthTs).toLocaleTimeString()}
                  </span>
                )}
                <button className="button button-sm button-secondary" onClick={loadHealth}
                        disabled={healthBusy || !dcuMeter?.session_open}>
                  {healthBusy ? <Spinner /> : '🔄 Refresh'}
                </button>
              </div>
            )}
          </div>
          <DetailGrid items={[
            // Identity
            ['Serial',          dcuSerial],
            ['Address',         dcuMeter.address],
            ['Firmware',        dcuMeter.firmware_version || health?.fw || '—'],
            ['Session',         dcuMeter.session_open ? 'Open (LLS Management)' : 'Closed'],
            ['Last heartbeat',  fmtDT(dcuMeter.last_heartbeat, { utc: true, seconds: true })],
            // Signal + link health (from /communication-quality)
            ['Signal',          dcuSignalInfo(dcuStatus).long],
            ['RSSI',            health?.cq?.rssi != null ? String(health.cq.rssi) : '—'],
            ['BER',             health?.cq?.ber  != null ? String(health.cq.ber)  : '—'],
            ['PLC signal',      health?.cq?.plc_level != null ? String(health.cq.plc_level) : '—'],
            ['RF signal',       health?.cq?.rf_level != null ? String(health.cq.rf_level) : '—'],
            ['Frames down / up', health?.cq ? `${health.cq.dl_frames ?? '—'} / ${health.cq.ul_frames ?? '—'}` : '—'],
            ['Online poll entries', health?.os?.entries?.length != null ? String(health.os.entries.length) : '—'],
            // Identification / SIM
            ['IMEI (modem)',    health?.imei  || '—'],
            ['SIM / ICCID',     health?.iccid || '—'],
            ['GPRS modem FW',   health?.modemFw || '—'],
            ['Data traffic',    health?.traffic || '—'],
            // Resources
            ['Storage', dcuStatus?.flash_usage ? (() => {
              try {
                const m = dcuStatus.flash_usage.match(/(\d+).*?(\d+)/);
                if (m) { const free = (parseInt(m[1]) / 1048576).toFixed(0); const total = (parseInt(m[2]) / 1048576).toFixed(0); return `${total - free} MB / ${total} MB used — ${free} MB free`; }
              } catch {} return dcuStatus.flash_usage;
            })() : '—'],
            ['Status', dcuStatus?.status_word != null ? (() => {
              const w = dcuStatus.status_word; const flags = [];
              if (w & 0x01) flags.push('Power up'); if (w & 0x02) flags.push('GPRS modem failure');
              if (w & 0x04) flags.push('GPRS signal low'); if (w & 0x08) flags.push('PLC failure');
              if (w & 0x10) flags.push('RF failure'); if (w & 0x20) flags.push('Flash full');
              if (w & 0x80) flags.push('DCU unlock'); if (w & 0x200) flags.push('RS-485 failure');
              if (w & 0x800) flags.push('Clock doubtful');
              return flags.join(', ') || 'OK';
            })() : '—'],
          ]} />
        </div>
      )}

      {/* Network Config / Bulk Relay / Restart were hoisted to the
          top-level Subscriber tab bar (next to Tariff, admin-only,
          DCU selection only). DcuNodeDetail is now just the Home
          content: identity + device status + meter list. */}

      {/* Blacklist info — registration done via Device Management > DCU tab */}
      {isAdmin && blacklist && blacklist.count > 0 && (
        <div style={{
          background: c.bgCard, border: `1px solid ${c.border}`,
          borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
          fontSize: '11px', color: c.textMuted,
        }}>
          {blacklist.count} meter(s) discovered on RS-485/PLC bus.
          Manage registration via <strong>Device Management &gt; DCU &gt; Blacklist</strong> tab.
        </div>
      )}

      {/* Device Status table per meter type */}
      {Object.keys(buckets).length > 0 && (
        <div style={{
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '8px',
          padding: '14px',
          marginBottom: '14px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '10px' }}>
            🧰 Device Status by meter type
          </div>
          <table style={{ fontSize: '12px', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'right' }}>Installed</th>
                <th style={{ textAlign: 'right' }}>On</th>
                <th style={{ textAlign: 'right' }}>Off</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(buckets).map(([name, b]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td style={{ textAlign: 'right' }}>{b.installed}</td>
                  <td style={{ textAlign: 'right', color: c.success }}>{b.on}</td>
                  <td style={{ textAlign: 'right', color: c.danger }}>{b.off}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Routed meters list with click-to-drill */}
      <div style={{
        background: c.bgCard,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '14px',
      }}>
        {(() => {
          // Filter out DCU's own assignment — only show actual meters
          // Registered = in the DCU's own Parameter Table (server: dcu_registered
          // from the last table read), not merely assigned here. Unregistered
          // first: they collect nothing until registered on the DCU again.
          const meterAssigns = assignments
            .filter(a => a.meter_serial !== dcuSerial)
            .map((a, i) => ({ a, i }))
            .sort((x, y) => (y.a.dcu_unregistered ? 1 : 0) - (x.a.dcu_unregistered ? 1 : 0) || x.i - y.i)
            .map(x => x.a);
          const unregCount = meterAssigns.filter(a => a.dcu_unregistered).length;
          const elsewhereCount = meterAssigns.filter(a => !a.dcu_unregistered && a.dcu_registered_on).length;
          const regKnown = meterAssigns.some(a => a.dcu_registered != null);
          return (
            <>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '10px' }}>
          Meters under this DCU ({meterAssigns.length})
          {regKnown && (
            <span style={{ fontWeight: 400, fontSize: '12px', marginLeft: '8px' }}>
              · <span style={{ color: c.success }}>{meterAssigns.length - unregCount - elsewhereCount} registered</span>
              {elsewhereCount > 0 && <> · <span style={{ color: c.textMuted }}>{elsewhereCount} on another DCU</span></>}
              {unregCount > 0 && (
                <> · <span style={{ color: c.warning, fontWeight: 600 }}
                  title="Assigned here but not in the DCU's Parameter Table — the DCU does not poll them, so no data arrives until they are registered on it again.">
                  {unregCount} unregistered</span></>
              )}
            </span>
          )}
        </div>
        {meterAssigns.length === 0 ? (
          <div style={{ color: c.textDimmed, fontSize: '12px', fontStyle: 'italic' }}>
            No meters configured under this DCU yet.
          </div>
        ) : (
          <table style={{ fontSize: '11px', width: '100%' }}>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('th.serial')}</th>
                <th>{t('form.object_name')}</th>
                <th>{t('th.type')}</th>
                <th>{t('th.online_status')}</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {meterAssigns.map((a, i) => {
                const live = inboundMeters.find(m => (m.serial_number || m.serial) === a.meter_serial);
                return (
                  <tr key={a.id}>
                    <td style={{ color: c.textMuted }}>{i + 1}</td>
                    <td style={{ fontFamily: 'monospace' }}>{a.meter_serial}</td>
                    <td>{a.object_name || '—'}</td>
                    <td><MeterTypeBadge mt={a.meter_type || live?.meter_type} compact /></td>
                    <td style={{ fontSize: '10px' }}>
                      {a.dcu_unregistered
                        ? <span style={{ color: c.warning, fontWeight: 600 }}
                            title="Not in this DCU's Parameter Table — the DCU does not poll it. Register it on the DCU to resume collection.">
                            ⚠ Unregistered — not in DCU table</span>
                        : a.dcu_registered_on
                        ? <span style={{ color: c.textMuted }}
                            title={`Assigned here but registered in DCU ${a.dcu_registered_on}'s Parameter Table — it collects through that DCU.`}>
                            ● registered on DCU {a.dcu_registered_on}</span>
                        : live?.session_open ? <span style={{ color: c.success }}>● live</span>
                        : live?.identified ? <span style={{ color: c.warning }}>● connected</span>
                        : <span style={{ color: '#22c55e' }}>{a.dcu_registered ? '● registered' : '● via DCU'}</span>}
                    </td>
                    <td>
                      <button
                        className="button button-sm button-secondary"
                        onClick={() => setSelected(a.meter_serial)}
                        title="Open this meter's full detail view"
                      >
                        Open →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
            </>
          );
        })()}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// DcuHealthCard — DEPRECATED. Health fields now merged directly
// into DcuNodeDetail's "DCU Information" card. Kept only as a
// no-op stub to avoid breaking imports if something still
// references it during a partial deploy. Safe to delete once the
// next commit rolls.
// on its "Device Information" pane, now that we've wired the
// vendor-selective keyword reads:
//   * get_communication_quality → RSSI / BER / frame counters / PLC+RF levels
//   * get_online_status         → per-meter online-poll stats
// Plus on-demand direct reads of IMEI / SIM-ID / traffic counter
// / DCU firmware / modem firmware.
// ─────────────────────────────────────────────────────────────
function DcuHealthCard({ dcuSerial, dcuMeter, dcuStatus }) {
  const c = useThemeColors();
  const toast = useToast();
  const [cq, setCq] = React.useState(null);
  const [os, setOs] = React.useState(null);
  const [info, setInfo] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [loadedAt, setLoadedAt] = React.useState(null);

  // Try one OBIS via /read-raw and parse the stringified gurux value.
  // Different attributes come back in different string forms — this is
  // best-effort: return the raw string if we can't pattern-match.
  const readSimple = async (obis, attr, cls) => {
    try {
      const r = await api(
        `/meters/${dcuSerial}/read-raw/${obis}?attribute=${attr}&class_id=${cls}`,
      );
      if (r?.value == null || r.value === 'None') return null;
      // bytearray(b'...') → take the inner string
      let v = String(r.value);
      const m = v.match(/bytearray\(b['"]([^'"]*)['"]\)/);
      if (m) return m[1];
      return v;
    } catch (_) {
      return null;
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [cqR, osR, imei, iccid, fw, modemFw, traffic] = await Promise.all([
        api(`/dcus/${dcuSerial}/communication-quality`).catch(() => null),
        api(`/dcus/${dcuSerial}/online-status`).catch(() => null),
        readSimple('0.0.96.12.6.255', 2, 1),       // IMEI
        readSimple('0.1.25.1.0.255', 3, 42),       // SIM / ICCID
        readSimple('0.0.2.0.0.255',  2, 1),        // DCU firmware
        readSimple('1.0.0.2.0.255',  2, 1),        // GPRS modem firmware
        readSimple('0.0.96.12.5.255', 2, 1),       // Traffic counter (uint32)
      ]);
      setCq(cqR);
      setOs(osR);
      setInfo({ imei, iccid, fw, modemFw, traffic });
      setLoadedAt(new Date().toLocaleTimeString());
    } catch (err) {
      toast.push(`Health load failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => { if (dcuMeter?.session_open) load(); /* eslint-disable-line */ }, [dcuSerial]);

  // Translate RSSI byte (0-31 scale per GSM) + dBm. Holley returns
  // raw uint8 — no units — so we show both the number and a soft
  // signal bar hint.
  const rssiHint = (v) => {
    if (v == null) return '—';
    // Heuristic bands: 0-50 bad, 50-100 ok, 100-180 good, 180+ excellent
    const q = v >= 180 ? 'excellent' : v >= 100 ? 'good' : v >= 50 ? 'ok' : 'weak';
    return `${v} (${q})`;
  };

  return (
    <div style={{
      background: c.bgCard, border: `1px solid ${c.border}`,
      borderRadius: '8px', padding: '14px', marginBottom: '14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
          📶 DCU Health
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {loadedAt && (
            <span style={{ fontSize: '10px', color: c.textMuted }}>
              read at {loadedAt}
            </span>
          )}
          <button className="button button-sm button-secondary" onClick={load}
                  disabled={busy || !dcuMeter?.session_open}>
            {busy ? <Spinner /> : '🔄'} Refresh
          </button>
        </div>
      </div>

      {!dcuMeter?.session_open && (
        <div style={{ color: c.textMuted, fontSize: '12px', fontStyle: 'italic' }}>
          DCU session not open — health reads unavailable.
        </div>
      )}

      {dcuMeter?.session_open && (
        <DetailGrid items={[
          ['RSSI',              rssiHint(cq?.rssi)],
          ['BER',               cq?.ber != null ? `${cq.ber}` : '—'],
          ['PLC signal level',  cq?.plc_level != null ? `${cq.plc_level}` : '—'],
          ['RF signal level',   cq?.rf_level != null ? `${cq.rf_level}` : '—'],
          ['Frames down / up',  cq ? `${cq.dl_frames ?? '—'}  /  ${cq.ul_frames ?? '—'}` : '—'],
          ['Online poll entries', os?.entries?.length != null ? `${os.entries.length}` : '—'],
          // Identification bundle
          ['IMEI (modem)',      info?.imei || '—'],
          ['SIM / ICCID',       info?.iccid || '—'],
          ['DCU firmware',      info?.fw || '—'],
          ['GPRS modem FW',     info?.modemFw || '—'],
          ['Data traffic',      info?.traffic || '—'],
          // Re-surface the status-word readable form from dcuStatus,
          // so operators see "GPRS signal low" etc. right here.
          ['Status word', dcuStatus?.status_word != null ? (() => {
            const w = dcuStatus.status_word; const flags = [];
            if (w & 0x01) flags.push('Power up');
            if (w & 0x02) flags.push('GPRS modem failure');
            if (w & 0x04) flags.push('GPRS signal low');
            if (w & 0x08) flags.push('PLC failure');
            if (w & 0x10) flags.push('RF failure');
            if (w & 0x20) flags.push('Flash full');
            if (w & 0x80) flags.push('DCU unlock');
            if (w & 0x200) flags.push('RS-485 failure');
            if (w & 0x800) flags.push('Clock doubtful');
            return flags.join(', ') || 'OK';
          })() : '—'],
        ]} />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// DcuRestartCard — admin-only.
// Triggers the Holley Script Table entry #1 (DCU reboot) via
// POST /api/dcus/{serial}/restart. Same kill-switch + confirm-
// serial pattern as relay / network-config. Blocks for a couple
// of seconds while the DCU ACKs, then the DCU drops its TCP and
// redials us in ~60-120 s.
// ─────────────────────────────────────────────────────────────
function DcuRestartCard({ dcuSerial, dcuMeter }) {
  const c = useThemeColors();
  const toast = useToast();
  const confirm = useConfirm();
  const [confirmInput, setConfirmInput] = React.useState('');
  const [forceDry, setForceDry] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [killSwitch, setKillSwitch] = React.useState(null);

  React.useEffect(() => {
    api('/admin/remote-writes-mode')
      .then(r => setKillSwitch(r.mode))
      .catch(() => setKillSwitch('unknown'));
  }, []);

  const fire = async () => {
    if (confirmInput !== dcuSerial) return;
    if (killSwitch === 'disabled') {
      toast.push('Kill-switch is disabled — flip via System → Security', 'error');
      return;
    }
    if (!(await confirm(
      forceDry ? 'Dry-run DCU restart' : 'RESTART DCU — LIVE',
      (forceDry
        ? `DRY-RUN: the restart PDU will be built and logged, nothing will be sent to ${dcuSerial}.`
        : `The DCU ${dcuSerial} will reboot. All child meters (RS-485 / PLC / RF) below it become unreachable for ~60-120 s while it comes back up.\n\nEvery data push / relay / IP write in flight is aborted.`),
      {
        confirmLabel: forceDry ? 'Dry-run restart' : 'RESTART DCU',
        confirmColor: forceDry ? '#f59e0b' : '#dc2626',
      },
    ))) return;
    setBusy(true);
    try {
      const r = await api(`/dcus/${dcuSerial}/restart`, {
        method: 'POST',
        body: JSON.stringify({ confirm_serial: dcuSerial, dry_run: forceDry }),
      });
      toast.push(
        forceDry ? `Dry-run OK — PDU built & logged` : `DCU restart command sent`,
        'success',
      );
      setConfirmInput('');
    } catch (err) {
      toast.push(`Restart failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!dcuMeter || !dcuMeter.session_open) return null;

  return (
    <div style={{
      background: c.bgCard, border: `1px solid ${c.border}`,
      borderRadius: '8px', padding: '16px', marginTop: '14px',
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '10px' }}>
        🔄 DCU restart
      </div>
      <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '10px', lineHeight: 1.5 }}>
        <strong style={{ color: c.warning }}>All child meters below this DCU become unreachable</strong>
        {' '}for 60-120 s while it reboots.
      </div>
      {killSwitch === 'disabled' && (
        <div style={{
          background: c.bgElevated, border: `1px solid ${c.danger}`,
          color: c.danger, fontSize: '11px', padding: '6px 10px',
          borderRadius: '4px', marginBottom: '10px',
        }}>
          🔒 Remote writes kill-switched. Go to System → Security to flip.
        </div>
      )}
      <label style={{ display: 'block', fontSize: '11px', color: c.textMuted, marginBottom: '8px' }}>
        <input type="checkbox" checked={forceDry}
               onChange={(e) => setForceDry(e.target.checked)} disabled={busy} />
        {' '}Dry-run (build PDU, don't send — safe default)
      </label>
      <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>
        Type DCU serial ({dcuSerial}) to unlock:
      </div>
      <input
        type="text"
        value={confirmInput}
        onChange={(e) => setConfirmInput(e.target.value.trim())}
        placeholder={dcuSerial}
        disabled={busy}
        style={{
          width: '100%', maxWidth: '260px', padding: '6px 10px',
          fontFamily: 'monospace', fontSize: '13px',
          border: `1px solid ${confirmInput === dcuSerial ? c.success : c.border}`,
          borderRadius: '6px', background: c.bgInput, color: c.textPrimary,
          marginBottom: '10px',
        }}
      />
      <button
        className={`button ${forceDry ? 'button-warning' : 'button-danger'}`}
        onClick={fire}
        disabled={busy || confirmInput !== dcuSerial || killSwitch === 'disabled'}
      >
        {busy ? <Spinner /> : '🔄'} {forceDry ? 'Dry-run restart' : 'RESTART DCU'}
      </button>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// DcuBulkRelayPanel — admin-only.
// Iterates every PLC / RF meter under this DCU and fires the
// chosen relay action against each via the existing dispatcher.
// Serial execution — matches the physical PLC bus constraint,
// and keeps the blast radius explicit (one meter at a time, so
// if the operator spots trouble mid-run they can cancel).
//
// Safety gates (in this order):
//   * EMETER_REMOTE_WRITES must be 'enabled'  (or 'dry_run' for trial)
//   * type DCU serial to unlock Start
//   * extra confirm dialog listing "N consumers will be affected"
//   * per-meter audit row, WS broadcast per completion
// ─────────────────────────────────────────────────────────────
function DcuBulkRelayPanel({ dcuSerial, dcuMeter, assignments, inboundMeters }) {
  const c = useThemeColors();
  const toast = useToast();
  const confirm = useConfirm();

  const [action, setAction] = React.useState('disconnect');
  const [forceDry, setForceDry] = React.useState(true);
  const [confirmInput, setConfirmInput] = React.useState('');
  const [killSwitch, setKillSwitch] = React.useState(null);
  const [progress, setProgress] = React.useState([]); // [{serial, state, error, result}]
  const [running, setRunning] = React.useState(false);
  const [cancelRequested, setCancelRequested] = React.useState(false);

  // Load kill-switch mode so the UI can show the banner and block
  // Start when writes are disabled.
  React.useEffect(() => {
    api('/admin/remote-writes-mode')
      .then(r => setKillSwitch(r.mode))
      .catch(() => setKillSwitch('unknown'));
  }, []);

  // Target list: every meter assigned under this DCU whose assignment
  // row points at a PLC/RF child (meter_serial != dcu_serial). The
  // backend dispatcher picks the right bus per meter, so we include
  // RS-485 too — it still queues + serialises correctly.
  const targets = React.useMemo(() => {
    return assignments.filter(a => a.meter_serial !== dcuSerial);
  }, [assignments, dcuSerial]);

  const startCycle = async () => {
    if (confirmInput !== dcuSerial || !targets.length) return;
    if (killSwitch === 'disabled') {
      toast.push('Kill-switch is disabled — flip via System → Security', 'error');
      return;
    }

    const N = targets.length;
    const noun = action === 'disconnect' ? 'DISCONNECT' : 'RECONNECT';
    if (!(await confirm(
      `${noun} ${N} meter${N === 1 ? '' : 's'}`,
      (forceDry
        ? `DRY-RUN: Build ${noun} PDUs for ${N} meter${N === 1 ? '' : 's'} under DCU ${dcuSerial}.\nNothing will be transmitted.`
        : `${noun} will ACTUALLY fire on ${N} meter${N === 1 ? '' : 's'} under DCU ${dcuSerial}.\n\n` +
          (action === 'disconnect'
            ? `This CUTS POWER to ${N} consumer${N === 1 ? '' : 's'} one after another.\n`
            : `This restores power on ${N} meter${N === 1 ? '' : 's'}.\n`) +
          `\nProceeds serially — ~10-30 s per meter over PLC.`),
      {
        confirmLabel: forceDry ? `Build ${N} PDU${N === 1 ? '' : 's'}` : `${noun} ALL ${N}`,
        confirmColor: forceDry ? '#f59e0b'
                     : action === 'disconnect' ? '#dc2626' : '#16a34a',
      },
    ))) return;

    setCancelRequested(false);
    setRunning(true);
    setProgress(targets.map(t => ({ serial: t.meter_serial, state: 'pending' })));

    for (let i = 0; i < targets.length; i++) {
      if (cancelRequested) {
        setProgress(p => p.map((row, idx) => idx >= i ? { ...row, state: 'cancelled' } : row));
        break;
      }
      const meterSerial = targets[i].meter_serial;
      setProgress(p => p.map((row, idx) => idx === i ? { ...row, state: 'running' } : row));
      try {
        const r = await api(`/meters/${meterSerial}/relay`, {
          method: 'POST',
          body: JSON.stringify({
            action,
            confirm_serial: meterSerial,
            dry_run: forceDry,
          }),
        });
        setProgress(p => p.map((row, idx) => idx === i ? {
          ...row, state: 'done', result: r.post_state,
        } : row));
      } catch (err) {
        setProgress(p => p.map((row, idx) => idx === i ? {
          ...row, state: 'failed', error: err.message,
        } : row));
      }
    }

    setRunning(false);
    setConfirmInput('');
    toast.push(`Bulk ${action} complete`, 'success');
  };

  const cancelCycle = () => {
    setCancelRequested(true);
    toast.push('Cancel requested — finishing current meter…', 'info');
  };

  if (!dcuMeter || !dcuMeter.session_open) {
    return (
      <div style={{ color: c.textMuted, padding: '20px', fontStyle: 'italic' }}>
        Bulk relay requires the DCU to have an open Management session.
      </div>
    );
  }

  const doneCount = progress.filter(p => p.state === 'done').length;
  const failCount = progress.filter(p => p.state === 'failed').length;

  return (
    <div>
      <div style={{
        background: c.bgElevated, border: `1px solid ${c.warning}`,
        color: c.warning, fontSize: '12px', padding: '10px 14px',
        borderRadius: '6px', marginBottom: '12px',
      }}>
        ⚠ Bulk relay cycles every meter on this DCU one after another.
        Disconnect cuts power to real consumers. Always dry-run first.
      </div>
      {killSwitch === 'disabled' && (
        <div style={{
          background: c.bgElevated, border: `1px solid ${c.danger}`,
          color: c.danger, fontSize: '12px', padding: '8px 12px',
          borderRadius: '6px', marginBottom: '10px',
        }}>
          🔒 Remote writes kill-switched. Go to System → Security to flip.
        </div>
      )}

      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '16px', marginBottom: '14px',
      }}>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '11px', color: c.textMuted }}>Target DCU</div>
            <code style={{ fontSize: '13px', color: c.textLabel }}>{dcuSerial}</code>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: c.textMuted }}>Meters affected</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: c.textPrimary }}>
              {targets.length}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px', color: c.textMuted, marginRight: '10px' }}>
            <input type="radio" checked={action === 'disconnect'}
                   onChange={() => setAction('disconnect')} disabled={running} /> Disconnect (cut power)
          </label>
          <label style={{ fontSize: '11px', color: c.textMuted }}>
            <input type="radio" checked={action === 'reconnect'}
                   onChange={() => setAction('reconnect')} disabled={running} /> Reconnect (restore power)
          </label>
        </div>

        <label style={{ fontSize: '11px', color: c.textMuted, display: 'block', marginBottom: '10px' }}>
          <input type="checkbox" checked={forceDry}
                 onChange={(e) => setForceDry(e.target.checked)} disabled={running} />
          {' '}Dry-run (build PDUs, don't transmit — safe default)
        </label>

        <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>
          Type the DCU serial ({dcuSerial}) to unlock Start:
        </div>
        <input
          type="text"
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value.trim())}
          placeholder={dcuSerial}
          disabled={running}
          style={{
            width: '100%', maxWidth: '260px', padding: '6px 10px',
            fontFamily: 'monospace', fontSize: '13px',
            border: `1px solid ${confirmInput === dcuSerial ? c.success : c.border}`,
            borderRadius: '6px', background: c.bgInput, color: c.textPrimary,
            marginBottom: '10px',
          }}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className={`button ${forceDry ? 'button-warning' : action === 'disconnect' ? 'button-danger' : 'button-success'}`}
            onClick={startCycle}
            disabled={running || confirmInput !== dcuSerial || !targets.length || killSwitch === 'disabled'}
          >
            {running ? <Spinner /> : '⚡'} Start bulk {action}
            {forceDry && ' (DRY-RUN)'}
          </button>
          {running && (
            <button className="button button-secondary" onClick={cancelCycle}>
              Cancel after current
            </button>
          )}
        </div>
      </div>

      {/* Progress panel */}
      {progress.length > 0 && (
        <div style={{
          background: c.bgCard, border: `1px solid ${c.border}`,
          borderRadius: '8px', padding: '14px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '10px' }}>
            Progress — {doneCount} done, {failCount} failed, {progress.length} total
          </div>
          <table style={{ fontSize: '11px', width: '100%' }}>
            <thead>
              <tr style={{ color: c.textMuted, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
                <th style={{ padding: '4px 6px' }}>#</th>
                <th style={{ padding: '4px 6px' }}>Serial</th>
                <th style={{ padding: '4px 6px' }}>State</th>
                <th style={{ padding: '4px 6px' }}>Post-state</th>
                <th style={{ padding: '4px 6px' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {progress.map((p, i) => (
                <tr key={p.serial} style={{ borderBottom: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 6px', color: c.textMuted }}>{i + 1}</td>
                  <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{p.serial}</td>
                  <td style={{ padding: '4px 6px' }}>
                    {p.state === 'pending'   && <span style={{ color: c.textMuted }}>⏸ pending</span>}
                    {p.state === 'running'   && <span style={{ color: c.accent }}>▶ running</span>}
                    {p.state === 'done'      && <span style={{ color: c.success }}>✓ done</span>}
                    {p.state === 'failed'    && <span style={{ color: c.danger }}>✗ failed</span>}
                    {p.state === 'cancelled' && <span style={{ color: c.warning }}>⏹ cancelled</span>}
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    {p.result?.output_state === true && <span style={{ color: c.success }}>connected</span>}
                    {p.result?.output_state === false && <span style={{ color: c.danger }}>disconnected</span>}
                    {p.result?.output_state == null && '—'}
                  </td>
                  <td style={{ padding: '4px 6px', color: c.danger, fontSize: '10px' }}>
                    {p.error || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── Assigned meter detail ───
// Relay Control sub-tab of MeterDetailView. Top card shows current
// relay state + identifying info, action buttons drive
// disconnect/reconnect/refresh, history table parses the CAS
// Disconnect Control Log (OBIS 0.0.99.98.2.255) via the same
// profile read-through cache the Events tab uses.
// ─────────────────────────────────────────────────────────────
// MeterDetailNetworkTab — admin-only remote IP / port / APN /
// heartbeat writes. Works for both meter and DCU targets because
// the backend endpoint auto-detects device_type and encodes the
// right DLMS wire shape (class 29 array-of-octet-string for the
// meter vs class 1 structure{u8, bytes, u16, u16} for the DCU).
//
// Safety in this UI:
//   - whole tab is gated on isAdmin at render time
//   - confirm-serial input required (mirrors backend confirm_serial)
//   - "Test TCP reachability" button pings the new target from the
//     eMeter server BEFORE Apply enables
//   - kill-switch banner from the read endpoint's remote_writes_mode
//     ("disabled" → action blocked, "dry_run" → shows DRY-RUN badge)
//   - dry_run toggle lets the admin force-dry even when env says
//     enabled (no way to force-enabled from the UI — SSH-only)
//   - APN on PLC is greyed out with an explanation (backend rejects
//     too, but UI surfaces the reason up-front)
// ─────────────────────────────────────────────────────────────
function MeterDetailNetworkTab({ serial, sessionOpen, assignment, live }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const confirm = useConfirm();

  // Network config snapshot. Cached per-session per-serial via the
  // module-level cache so the operator doesn't re-read the DCU/meter
  // every time they open this tab. A successful live write
  // invalidates the cache; manual Refresh button re-reads on demand.
  const [cfg, setCfg] = useState(() => dcuCacheGet(serial, 'netcfg'));
  const [cfgTs, setCfgTs] = useState(() => dcuCacheGetTs(serial, 'netcfg'));
  const [busy, setBusy] = useState(null);         // 'read' | 'apply' | 'probe'
  const [confirmInput, setConfirmInput] = useState('');
  const [forceDry, setForceDry] = useState(true); // default-safe
  const [probeResult, setProbeResult] = useState(null); // 'ok' | 'fail' | null
  const [targetKind, setTargetKind] = useState(null); // 'dcu' | 'meter'

  // Form state — tracks the edited values. `null` means "don't send".
  const [fIp, setFIp] = useState('');
  const [fPort, setFPort] = useState('');
  const [fApn, setFApn] = useState('');
  const [fHb, setFHb] = useState('');

  // Is the target a DCU? We detect via the live inbound-meter blob's
  // device_type === 'dcu'. Falls back to assignment.meter_serial ==
  // dcu_serial (operator registered the DCU via AssignDcuModal).
  const isDcuTarget =
    live?.device_type === 'dcu' ||
    (assignment?.dcu_serial && assignment.meter_serial === assignment.dcu_serial);
  // For meters behind a DCU the backend uses SAP passthrough; for
  // meters without any DCU route it uses the direct GPRS session.
  const isPlcRoute = !isDcuTarget && !!assignment?.dcu_serial;

  const killSwitch = cfg?.remote_writes_mode || null;

  // Fire on mount + whenever target changes.
  const readCurrent = async () => {
    setBusy('read');
    try {
      // For DCUs we read directly; for meters we hit the meter-config
      // endpoint. Both consolidated via /network-config POST in the
      // backend — but for READ we use direct raw reads against the
      // correct OBIS so we don't need to flip kill-switch off.
      if (isDcuTarget) {
        const r = await api(
          `/meters/${serial}/read-raw/0.0.96.50.8.255?attribute=2&class_id=1`,
        );
        // Parse "[1, bytearray(b'72.62.114.176'), 8002, 120]" style
        // string pragmatically — the raw endpoint stringifies GXStructure.
        const m = String(r.value || '').match(
          /^\[\s*(\d+)\s*,\s*bytearray\(b['"]([^'"]+)['"]\)\s*,\s*(\d+)\s*,\s*(\d+)/,
        );
        let server_ip = null, server_port = null, heartbeat_s = null;
        if (m) {
          server_ip = m[2];
          server_port = parseInt(m[3], 10);
          heartbeat_s = parseInt(m[4], 10);
        }
        // Best-effort APN read.
        let apn = null;
        try {
          const a = await api(
            `/meters/${serial}/read-raw/0.0.25.4.0.255?attribute=2&class_id=45`,
          );
          if (a?.value && a.value !== 'None') {
            apn = a.value.replace(/^bytearray\(b['"]/, '').replace(/['"]\)$/, '');
          }
        } catch (_) {}
        // Probe remote_writes_mode via the GET /relay-state endpoint
        // (which surfaces it).
        let remote_writes_mode = null;
        try {
          const rs = await api(`/meters/${serial}/relay-state`).catch(() => null);
          remote_writes_mode = rs?.remote_writes_mode;
        } catch (_) {}
        setTargetKind('dcu');
        const bundle = { server_ip, server_port, heartbeat_s, apn,
                         bus: 'direct (this IS the DCU)', remote_writes_mode };
        setCfg(bundle);
        setCfgTs(Date.now());
        dcuCacheSet(serial, 'netcfg', bundle);
      } else {
        // Meter target — read via the DCU passthrough (or direct).
        const dcuSerial = assignment?.dcu_serial;
        const sap = '2';
        let server = null;
        if (dcuSerial) {
          const r = await api(
            `/dcus/${dcuSerial}/passthrough/${sap}/0.0.2.1.0.255?attribute=6&class_id=29`,
          );
          // GXArray of octet-string comes back as array e.g.
          // ["72.62.114.176:8002"].
          if (Array.isArray(r.value) && r.value.length) {
            server = String(r.value[0]);
          }
        } else {
          const r = await api(
            `/meters/${serial}/read-raw/0.0.2.1.0.255?attribute=6&class_id=29`,
          );
          // Best-effort; raw reads stringify arrays differently.
          const m = String(r.value || '').match(/['"]([^'"]+)['"]/);
          if (m) server = m[1];
        }
        let [server_ip, server_port] = [null, null];
        if (server) {
          const parts = server.split(':');
          server_ip = parts[0];
          server_port = parts[1] ? parseInt(parts[1], 10) : null;
        }
        let remote_writes_mode = null;
        try {
          const rs = await api(`/meters/${serial}/relay-state`).catch(() => null);
          remote_writes_mode = rs?.remote_writes_mode;
        } catch (_) {}
        setTargetKind('meter');
        const bundle = {
          server_ip, server_port, heartbeat_s: null, apn: null,
          bus: dcuSerial ? `PLC/RF via DCU ${dcuSerial}` : 'direct GPRS',
          remote_writes_mode,
        };
        setCfg(bundle);
        setCfgTs(Date.now());
        dcuCacheSet(serial, 'netcfg', bundle);
      }
      // Pre-fill form with current values.
    } catch (err) {
      toast.push(`Read failed: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  // Only pull fresh on first-ever visit. Navigating back reuses
  // the cached value — user feedback: "we know its right once since
  // they are connected to our network ip". Refresh button still
  // works for manual re-reads.
  useEffect(() => {
    if (!dcuCacheGet(serial, 'netcfg')) readCurrent();
    /* eslint-disable-next-line */
  }, [serial]);

  const handleProbe = async () => {
    const ip = fIp || cfg?.server_ip;
    const port = fPort || cfg?.server_port || 8002;
    if (!ip) {
      toast.push('Enter the new IP first', 'warning');
      return;
    }
    setBusy('probe');
    try {
      const r = await api(`/diagnostics/tcp-probe?host=${encodeURIComponent(ip)}&port=${port}`);
      setProbeResult(r.ok ? 'ok' : 'fail');
      toast.push(r.ok ? `${ip}:${port} reachable` : `${ip}:${port} unreachable — do NOT apply`,
                 r.ok ? 'success' : 'error');
    } catch (err) {
      setProbeResult('fail');
      toast.push(`Probe failed: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleApply = async () => {
    if (confirmInput !== serial) return;
    // Require probe pass unless the user is writing the SAME ip back
    // (no-op live write) or only changing APN/heartbeat.
    const ipChanging = fIp && fIp !== cfg?.server_ip;
    const portChanging = fPort && parseInt(fPort, 10) !== cfg?.server_port;
    if ((ipChanging || portChanging) && probeResult !== 'ok') {
      toast.push('TCP probe must succeed before changing IP/port', 'warning');
      return;
    }
    const plainBody = {
      confirm_serial: serial,
      dry_run: forceDry,
    };
    if (fIp) plainBody.server_ip = fIp;
    if (fPort) plainBody.server_port = parseInt(fPort, 10);
    if (fApn) plainBody.apn = fApn;
    if (fHb && isDcuTarget) plainBody.heartbeat_s = parseInt(fHb, 10);
    const changesDesc = Object.entries(plainBody)
      .filter(([k, v]) => !['confirm_serial', 'dry_run'].includes(k) && v !== '' && v != null)
      .map(([k, v]) => `${k}=${v}`)
      .join(', ') || '(no changes)';
    if (!(await confirm(
      forceDry ? 'Apply (dry-run)' : 'Apply change — LIVE',
      `Target: ${targetKind?.toUpperCase()} ${serial}\n` +
      `Mode: ${forceDry ? 'DRY-RUN (PDU built, not sent)' : 'LIVE (writes the meter)'}\n\n` +
      `Changes: ${changesDesc}\n\n` +
      (forceDry ? 'Safe — no wire traffic.'
                : 'This will actually change the target config. Wrong values may require optical-port recovery.'),
      {
        confirmLabel: forceDry ? 'Build PDU (dry-run)' : 'Apply LIVE',
        confirmColor: forceDry ? '#f59e0b' : '#dc2626',
      },
    ))) return;
    setBusy('apply');
    try {
      const r = await api(`/meters/${serial}/network-config`, {
        method: 'POST',
        body: JSON.stringify(plainBody),
      });
      toast.push(
        `${forceDry ? 'DRY-RUN' : 'LIVE'}: ${r.status || 'done'}${r.writes ? '' : ''}`,
        'success',
      );
      // Clear confirm input so each cycle requires re-typing.
      setConfirmInput('');
      setProbeResult(null);
      // Live write changed something on the device → invalidate the
      // session cache and pull the fresh value so the displayed
      // current-config matches reality.
      if (!forceDry) {
        dcuCacheInvalidate(serial, 'netcfg');
        await readCurrent();
      }
    } catch (err) {
      toast.push(`Apply failed: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const fieldRow = (label, cur, placeholder, val, setter, disabled = false) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '6px 0', borderBottom: `1px dashed ${c.border}`,
    }}>
      <div style={{ width: '140px', fontSize: '12px', color: c.textMuted }}>{label}</div>
      <code style={{ minWidth: '180px', fontSize: '12px', color: c.textLabel }}>
        {cur ?? '—'}
      </code>
      <input
        type="text"
        value={val}
        placeholder={placeholder}
        onChange={(e) => setter(e.target.value.trim())}
        disabled={disabled}
        style={{
          flex: 1, maxWidth: '220px', padding: '4px 8px',
          fontFamily: 'monospace', fontSize: '12px',
          border: `1px solid ${c.border}`, borderRadius: '4px',
          background: disabled ? c.bgCardAlt : c.bgInput, color: c.textPrimary,
        }}
      />
    </div>
  );

  return (
    <div>
      {/* Admin-only notice (the tab is only rendered for admins, but
          a belt-and-braces banner helps the operator understand the
          blast radius). */}
      <div style={{
        background: c.bgElevated, border: `1px solid ${c.warning}`,
        color: c.warning, fontSize: '12px', padding: '10px 14px',
        borderRadius: '6px', marginBottom: '12px',
      }}>
        {t('net.blast_warn')}
      </div>
      {killSwitch === 'disabled' && (
        <div style={{
          background: c.bgElevated, border: `1px solid ${c.danger}`,
          color: c.danger, fontSize: '12px', padding: '8px 12px',
          borderRadius: '6px', marginBottom: '10px',
        }}>
          🔒 {t('net.kill_switch')}
        </div>
      )}
      {killSwitch === 'dry_run' && (
        <div style={{
          background: c.bgElevated, border: `1px solid ${c.warning}`,
          color: c.warning, fontSize: '12px', padding: '8px 12px',
          borderRadius: '6px', marginBottom: '10px',
        }}>
          🧪 {t('net.kill_switch_dry')}
        </div>
      )}

      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '16px', marginBottom: '14px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
            🌐 {t('net.current')}
          </div>
          <div style={{ fontSize: '11px', color: c.textMuted }}>
            {isDcuTarget ? t('net.target_dcu')
              : isPlcRoute ? t('net.target_meter')
              : t('net.target_direct')}
            {cfg?.bus && ` · ${cfg.bus}`}
            {cfgTs && (
              <span style={{ marginLeft: '8px' }}>
                · cached {new Date(cfgTs).toLocaleTimeString()}
              </span>
            )}
          </div>
          <button className="button button-sm button-primary" onClick={readCurrent}
                  disabled={busy === 'read'}>
            {busy === 'read' ? <Spinner /> : '🔄'} {t('net.read')}
          </button>
        </div>
        <DetailGrid items={[
          [t('net.server_ip'), cfg?.server_ip ?? '—'],
          [t('net.server_port'), cfg?.server_port ?? '—'],
          [t('net.apn'), cfg?.apn ?? '—'],
          isDcuTarget ? [t('net.heartbeat_s'), cfg?.heartbeat_s ?? '—'] : null,
        ].filter(Boolean)} />
      </div>

      {/* Change form */}
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '8px', padding: '16px', marginBottom: '14px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel, marginBottom: '8px' }}>
          ✏ Change network configuration
        </div>
        {fieldRow(t('net.new_server_ip'), cfg?.server_ip, '72.62.114.176', fIp, setFIp)}
        {fieldRow(t('net.new_port'), cfg?.server_port, '8002', fPort, setFPort)}
        {isDcuTarget && fieldRow(t('net.new_hb'), cfg?.heartbeat_s, '120', fHb, setFHb)}
        {fieldRow(t('net.new_apn'), cfg?.apn, 'internet', fApn, setFApn, isPlcRoute)}
        {isPlcRoute && (
          <div style={{ fontSize: '11px', color: c.warning, marginTop: '6px' }}>
            ⓘ {t('net.apn_over_plc')}
          </div>
        )}

        {/* Probe row */}
        <div style={{
          marginTop: '12px', paddingTop: '12px',
          borderTop: `1px dashed ${c.border}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: '11px', color: c.textMuted, flex: 1 }}>
            {t('net.probe_hint')}
          </div>
          <button className="button button-sm button-secondary"
                  onClick={handleProbe}
                  disabled={busy === 'probe' || !(fIp || cfg?.server_ip)}>
            {busy === 'probe' ? <Spinner /> : '🌐'} {t('net.probe_tcp')}
          </button>
          {probeResult === 'ok' && <span style={{ color: c.success, fontSize: '12px' }}>{t('net.probe_ok')}</span>}
          {probeResult === 'fail' && <span style={{ color: c.danger, fontSize: '12px' }}>{t('net.probe_fail')}</span>}
        </div>

        {/* Dry-run toggle + confirm-serial + apply */}
        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px dashed ${c.border}` }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px',
                         fontSize: '12px', color: c.textMuted, marginBottom: '10px' }}>
            <input type="checkbox" checked={forceDry}
                   onChange={(e) => setForceDry(e.target.checked)} />
            Dry-run (build the PDU, don't send — safe default)
          </label>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>
            {t('net.type_serial')}
          </div>
          <input
            type="text"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value.trim())}
            placeholder={serial}
            style={{
              width: '100%', maxWidth: '260px', padding: '6px 10px',
              fontFamily: 'monospace', fontSize: '13px',
              border: `1px solid ${confirmInput === serial ? c.success : c.border}`,
              borderRadius: '6px', background: c.bgInput, color: c.textPrimary,
              marginBottom: '10px',
            }}
          />
          <button
            className={`button ${forceDry ? 'button-warning' : 'button-danger'}`}
            onClick={handleApply}
            disabled={
              busy != null ||
              confirmInput !== serial ||
              killSwitch === 'disabled' ||
              (!fIp && !fPort && !fApn && !fHb) ||
              (!forceDry &&
                (fIp && fIp !== cfg?.server_ip ||
                 fPort && parseInt(fPort, 10) !== cfg?.server_port) &&
                probeResult !== 'ok')
            }
          >
            {busy === 'apply' ? <Spinner /> : '⚡'} {t('net.apply')}
            {forceDry && ` ${t('net.dry_suffix')}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function MeterDetailRelayTab({ serial, sessionOpen, assignment, live, meterType }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = userCan(user, 'manage_records');
  // Relay disconnect/reconnect is available to admins AND operators. Operators
  // are constrained server-side to their assigned meters (control_inbound_meter_relay
  // enforces role ∈ {admin,operator} + _require_meter_access), and they only ever
  // reach a meter detail that's already inside their customer/transformer/meter scope.
  const canRelay = userCan(user, 'relay_control');

  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [catalog, setCatalog] = useState(null);
  // Admin-only: the operator must type the full 12-digit serial here
  // before any disconnect/reconnect action is allowed through. Mirrors
  // the backend's `confirm_serial` payload requirement.
  const [confirmInput, setConfirmInput] = useState('');
  const [mode, setMode] = useState('status'); // 'status' | 'history'
  // Delivery mode for the action: 'immediate' = try once now, timeout if it
  // doesn't land in ~60s; 'deferred' = queue a task that runs when the meter
  // next comes online (retries across reconnects). + reason for the history.
  const [delivery, setDelivery] = useState('immediate');
  const [reason, setReason] = useState('debt');

  // `sessionOpen` comes from live.session_open, which reflects THIS
  // meter's own TCP connection. PLC/RF meters behind a DCU don't have
  // one — they're reachable via the DCU's session instead. The new
  // GET /relay-state endpoint walks that path server-side, so don't
  // gate the UI on sessionOpen when the meter is DCU-routed.
  const isDcuRouted = !!assignment?.dcu_serial;
  const canTalk = sessionOpen || isDcuRouted;

  useEffect(() => {
    api('/event-codes').then(setCatalog).catch(() => {});
  }, []);

  // Manual-only: do NOT auto-read relay status. The admin must click
  // "Read Status" to fetch it — no auto check/refresh on open or on a
  // timer. Reset any prior reading (and the typed-serial confirm) when
  // the meter changes so a stale status from another meter is never
  // shown, and immediate-action buttons stay disabled until a fresh read.
  useEffect(() => {
    setStatus(null);
    setConfirmInput('');
    /* eslint-disable-next-line */
  }, [serial]);

  // Admin-gated read: uses the new bus-agnostic GET endpoint which
  // walks direct / RS-485 / PLC paths uniformly. Returns output_state
  // (bool), control_state (enum), control_mode (0..6 per DLMS §15),
  // aux_present, bus, and the current EMETER_REMOTE_WRITES mode.
  const readStatus = async () => {
    setBusy('refresh');
    try {
      const r = await api(`/meters/${serial}/relay-state`);
      setStatus({
        output: r.output_state,              // bool | null
        controlState: r.control_state,       // 0/1/2
        controlMode: r.control_mode,         // 0..6
        auxPresent: !!r.aux_present,
        bus: r.bus,
        remoteMode: r.remote_writes_mode,    // disabled / dry_run / enabled
        error: r.error,
        at: new Date().toLocaleTimeString(),
      });
      toast.push(t('toast.relay_refreshed'), 'success');
    } catch (err) {
      toast.push(`${t('relay.refresh_failed')}: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  // Route per-meter actions through the unified relay command system
  // (POST /api/relay/commands) so they get history + attempts + the two
  // delivery modes. Immediate = one shot, ~60s then timeout. Deferred =
  // queue a task that runs when the meter next comes online (works even if
  // the meter is offline right now — that's the whole point of an offline
  // command). aux retains the old direct path (aux relay, immediate only).
  const fireRelay = async (action, { aux = false } = {}) => {
    if (!canRelay) return;
    if (confirmInput !== serial) return;

    const pretty = action === 'disconnect' ? t('relay.disconnect_upper') : t('relay.reconnect_upper');
    const deliveryLabel = delivery === 'deferred'
      ? 'DEFERRED — runs when the meter next comes online'
      : 'IMMEDIATE — try now, timeout in ~60s if it does not land';
    const confirmText =
      `${pretty} ${t('relay.confirm_prefix')} ${serial}?\n\n` +
      `Delivery: ${deliveryLabel}\n` +
      `${t('relay.confirm_power')} ${action === 'disconnect' ? t('relay.off') : t('relay.on')} ` +
      `${t('relay.confirm_suffix')}`;
    if (!(await confirm(pretty, confirmText, {
      confirmLabel: pretty,
      confirmColor: action === 'disconnect' ? '#dc2626' : '#16a34a',
    }))) return;
    setBusy(action);
    try {
      if (aux) {
        // Aux relay still uses the direct single-meter endpoint (the
        // command system targets the main relay).
        await api(`/meters/${serial}/relay`, {
          method: 'POST',
          body: JSON.stringify({ action, confirm_serial: serial, aux: true }),
        });
        toast.push(t('toast.relay_sent', { action }), 'success');
      } else {
        const r = await api('/relay/commands', {
          method: 'POST',
          body: JSON.stringify({
            serials: [serial], action, mode: delivery,
            reason_code: reason, confirm_phrase: serial,
          }),
        });
        const okN = (r.created || []).length;
        const errN = (r.errors || []).length;
        if (errN && !okN) {
          toast.push(`Command rejected: ${r.errors[0]?.reason || 'unknown'}`, 'error');
        } else {
          toast.push(
            delivery === 'deferred'
              ? `Queued (deferred) — will run when ${serial} is online`
              : `Queued (immediate)${r.gated_mode === 'dry_run' ? ' — DRY-RUN' : ''}`,
            'success',
          );
        }
      }
      // Clear the confirm input — operator must retype for next action.
      setConfirmInput('');
      // Re-read state shortly after (immediate may have landed by then).
      if (delivery === 'immediate' || aux) setTimeout(readStatus, 3000);
    } catch (err) {
      toast.push(`${pretty} ${t('relay.action_failed')}: ${err.message}`, 'error');
    } finally {
      setBusy(null);
    }
  };

  const modeHint = (m) => {
    if (m == null) return null;
    if (m === 0) return t('relay.mode_hint_0');
    if (m === 1) return t('relay.mode_hint_1');
    if (m === 2) return t('relay.mode_hint_2');
    if (m === 3) return t('relay.mode_hint_3');
    if (m === 5) return t('relay.mode_hint_5');
    return t('relay.mode_hint_other').replace('{n}', String(m));
  };

  const busLabel = (b) => {
    if (b === 'direct') return t('relay.bus_direct');
    if (b === 1) return t('relay.bus_rs485');
    if (b === 6) return t('relay.bus_plc');
    if (b === 7) return t('relay.bus_rf');
    return String(b ?? '—');
  };

  // Reconnect is safe iff control_mode in {1, 3, 5, 6}. Mode 2 would
  // disconnect but refuse remote reconnect (local button required).
  // Mode 0 refuses both. Keep the button visible but disabled with
  // an explanatory hint.
  const canReconnect = status?.controlMode == null || [1, 3, 4, 5, 6].includes(status.controlMode);
  const canDisconnect = status?.controlMode == null || [1, 2, 3, 4, 5, 6].includes(status.controlMode);

  const loadHistory = async () => {
    if (!serial) return;
    setHistLoading(true);
    try {
      const toTs = Math.floor(Date.now() / 1000);
      const fromTs = toTs - 30 * 86400;
      const params = new URLSearchParams({
        from_ts: String(fromTs),
        to_ts: String(toTs),
        limit: '500',
        refresh: 'force',
      });
      const r = await api(
        `/meters/${serial}/profile/0.0.99.98.2.255?${params}`
      );
      const rows = (r?.rows || []).map((row, i) => {
        // CAS spec §17: captured objects = {clock, code}, so the
        // event code is values[1], not values[0] (which is the clock).
        const vals = Array.isArray(row.values) ? row.values : [];
        const code = vals.length > 1 ? vals[1] : vals[0];
        const name = catalog?.codes?.[String(code)] || `Unknown (code=${code})`;
        return {
          no: i + 1,
          ts: row.row_ts,
          code,
          name,
        };
      });
      rows.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      setHistory(rows);
      toast.push(t('toast.loaded_n', { n: rows.length, what: t('relay.events') }), 'success');
    } catch (err) {
      toast.push(`${t('relay.history_load_failed')}: ${err.message}`, 'error');
    } finally {
      setHistLoading(false);
    }
  };

  // Primary state for the headline indicator is output_state (bool).
  // Fall back to control_state (0/1/2 enum) when output_state is null
  // (e.g. DCU session transiently closed). Colour green for connected,
  // red for disconnected, yellow when meter is in "ready for reconnection".
  const _out = status?.output;
  const _cs = status?.controlState;
  const _connected = _out === true || _cs === 1;
  const _disconnected = _out === false || _cs === 0;
  const _readyReconnect = _cs === 2;
  const relayColor = _connected ? c.success
                   : _disconnected ? c.danger
                   : _readyReconnect ? c.warning
                   : c.textMuted;
  const relayIcon  = _connected ? '🟢'
                   : _disconnected ? '🔴'
                   : _readyReconnect ? '🟡'
                   : '⚪';
  const relayLabel = _connected ? t('relay.connected').toUpperCase()
                   : _disconnected ? t('relay.disconnected').toUpperCase()
                   : _readyReconnect ? t('relay.ready_reconnect').toUpperCase()
                   : t('relay.unknown').toUpperCase();

  // Auto-load history on first switch into history mode, but don't
  // re-fetch every mode flip — the Refresh button in the history card
  // handles re-pulls. This mirrors the Meter Readings tab's lazy load.
  useEffect(() => {
    if (mode === 'history' && history.length === 0 && !histLoading && catalog) {
      loadHistory();
    }
    /* eslint-disable-next-line */
  }, [mode, catalog]);

  const tabBtn = (active, color = c.accent) => ({
    padding: '6px 14px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? color : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });

  return (
    <div>
      {!canTalk && (
        <div style={{ marginBottom: '12px', fontSize: '11px', color: c.warning }}>
          ⚠ {t('relay.session_required')}
        </div>
      )}

      {/* ── Mode toggle: Read Status ↔ History ── */}
      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '14px',
        borderBottom: `1px solid ${c.border}`,
      }}>
        <button style={tabBtn(mode === 'status')}  onClick={() => setMode('status')}>📡 {t('btn.read_status')}</button>
        <button style={tabBtn(mode === 'history')} onClick={() => setMode('history')}>📜 {t('relay.history')}</button>
      </div>

      {mode === 'status' && (
        <div style={{
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '11px', color: c.textMuted, textTransform: 'uppercase' }}>{t('relay.relay_status')}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: relayColor }}>
                {relayIcon} {status ? relayLabel : `— ${t('relay.click_read')} →`}
              </div>
              {status && <div style={{ fontSize: '11px', color: c.textMuted }}>{t('relay.read_at')} {status.at}</div>}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                className="button button-primary"
                onClick={readStatus}
                disabled={!canTalk || busy === 'refresh'}
              >
                {busy === 'refresh' ? <Spinner /> : '📡'} {t('btn.read_status')}
              </button>
              <button
                className="button button-secondary"
                onClick={() => setStatus(null)}
                disabled={!status}
                title={t('relay.clear_status_title')}
              >
                ↺ {t('btn.reset')}
              </button>
            </div>
          </div>

          {/* Action panel — admins and operators (operators scoped to their
              assigned meters server-side). Viewers/developers see a notice
              explaining why the buttons are hidden. The action panel gives a
              type-to-confirm serial input, context banners for kill-switch
              state + control-mode, then the real buttons. */}
          {canRelay ? (
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: `1px dashed ${c.border}`,
            }}>
              {/* Kill-switch / dry-run banner */}
              {status?.remoteMode === 'disabled' && (
                <div style={{
                  background: c.bgElevated, border: `1px solid ${c.danger}`,
                  color: c.danger, fontSize: '12px', padding: '8px 12px',
                  borderRadius: '6px', marginBottom: '10px',
                }}>
                  🔒 {t('relay.kill_switch')}
                </div>
              )}
              {status?.remoteMode === 'dry_run' && (
                <div style={{
                  background: c.bgElevated, border: `1px solid ${c.warning}`,
                  color: c.warning, fontSize: '12px', padding: '8px 12px',
                  borderRadius: '6px', marginBottom: '10px',
                }}>
                  🧪 {t('relay.kill_switch_dry')}
                </div>
              )}
              {/* Control-mode hint */}
              {status?.controlMode != null && (
                <div style={{
                  fontSize: '11px', color: c.textMuted, marginBottom: '8px',
                }}>
                  ⓘ {modeHint(status.controlMode)}
                </div>
              )}
              {/* Confirm-serial input */}
              <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>
                {t('relay.type_serial')}
              </div>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value.trim())}
                placeholder={serial}
                style={{
                  width: '100%', maxWidth: '260px', padding: '6px 10px',
                  fontFamily: 'monospace', fontSize: '13px',
                  border: `1px solid ${confirmInput === serial ? c.success : c.border}`,
                  borderRadius: '6px', background: c.bgInput, color: c.textPrimary,
                  marginBottom: '10px',
                }}
              />
              {/* Delivery mode + reason (apply to the main-relay buttons). */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '12px', color: c.textMuted }}>
                  Delivery:&nbsp;
                  <select value={delivery} onChange={(e) => setDelivery(e.target.value)}
                    style={{ padding: '4px 8px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px' }}>
                    <option value="immediate">Immediate (≤60s, else timeout)</option>
                    <option value="deferred">Deferred (runs when meter online)</option>
                  </select>
                </label>
                <label style={{ fontSize: '12px', color: c.textMuted }}>
                  Reason:&nbsp;
                  <select value={reason} onChange={(e) => setReason(e.target.value)}
                    style={{ padding: '4px 8px', background: c.bgInput, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: '6px' }}>
                    <option value="debt">Debt</option>
                    <option value="debt_paid">Debt Paid</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="tamper">Tamper</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>
              {delivery === 'deferred' && (
                <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '8px' }}>
                  ⏳ Deferred: queued and sent automatically the next time this meter is online (retries across reconnects). Works even if it's offline now.
                </div>
              )}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {/*
                 * IMMEDIATE requires a reachable path + a prior status read +
                 * a control_mode that supports the action (read-before-write).
                 * DEFERRED skips those — the whole point is to queue for a meter
                 * that may be offline right now. Both require: admin, exact
                 * serial typed, not busy, kill-switch not 'disabled'.
                 */}
                <button
                  className="button button-danger"
                  onClick={() => fireRelay('disconnect')}
                  disabled={busy != null || confirmInput !== serial
                            || status?.remoteMode === 'disabled'
                            || (delivery === 'immediate' && (!canTalk || status == null || !canDisconnect))}
                  title={delivery === 'immediate' && !canDisconnect ? modeHint(status?.controlMode) : undefined}
                >
                  {busy === 'disconnect' ? <Spinner /> : '⚡'} {t('btn.disconnect')}
                </button>
                <button
                  className="button button-success"
                  onClick={() => fireRelay('reconnect')}
                  disabled={busy != null || confirmInput !== serial
                            || status?.remoteMode === 'disabled'
                            || (delivery === 'immediate' && (!canTalk || status == null || !canReconnect))}
                  title={delivery === 'immediate' && !canReconnect ? modeHint(status?.controlMode) : undefined}
                >
                  {busy === 'reconnect' ? <Spinner /> : '⚡'} {t('btn.reconnect')}
                </button>
                {/* Aux-relay pair — only rendered if the probe found one. */}
                {status?.auxPresent && (
                  <>
                    <button
                      className="button button-danger"
                      onClick={() => fireRelay('disconnect', { aux: true })}
                      disabled={!canTalk || busy != null || confirmInput !== serial
                                || status == null
                                || status?.remoteMode === 'disabled' || !canDisconnect}
                    >
                      {busy === 'disconnect' ? <Spinner /> : '⚡'} {t('btn.disconnect')} (aux)
                    </button>
                    <button
                      className="button button-success"
                      onClick={() => fireRelay('reconnect', { aux: true })}
                      disabled={!canTalk || busy != null || confirmInput !== serial
                                || status == null
                                || status?.remoteMode === 'disabled' || !canReconnect}
                    >
                      {busy === 'reconnect' ? <Spinner /> : '⚡'} {t('btn.reconnect')} (aux)
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              borderTop: `1px dashed ${c.border}`,
              color: c.textMuted,
              fontSize: '12px',
              fontStyle: 'italic',
            }}>
              🔒 {t('relay.admin_only')}
            </div>
          )}

          <DetailGrid items={[
            [t('meter.meter_no'), assignment.meter_serial],
            [t('meter.meter_type'), formatMeterType(meterType)],
            [t('form.account_number'), assignment.account_number],
            [t('relay.logical_device_name'), live?.logical_device_name],
            [t('th.manufacturer'), live?.manufacturer],
            [t('relay.last_heartbeat'), live?.last_heartbeat ? fmtDT(live.last_heartbeat, { utc: true, seconds: true }) : '—'],
            // New rows surfaced from the relay-state endpoint.
            status ? [t('relay.bus'), busLabel(status.bus)] : null,
            status?.output != null ? [
              t('relay.output_state'),
              status.output ? `${t('relay.connected')} (true)` : `${t('relay.disconnected')} (false)`,
            ] : null,
            status?.controlState != null ? [
              t('relay.ctrl_state'),
              status.controlState === 0 ? `${t('relay.disconnected')} (0)`
              : status.controlState === 1 ? `${t('relay.connected')} (1)`
              : status.controlState === 2 ? `${t('relay.ready_reconnect')} (2)`
              : String(status.controlState),
            ] : null,
            status?.controlMode != null ? [t('relay.control_mode'), String(status.controlMode)] : null,
          ].filter(Boolean)} />
        </div>
      )}

      {mode === 'history' && (
      <div style={{
        background: c.bgCard,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '14px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
            📜 {t('relay.history_30d')}
          </div>
          <button className="button button-sm button-secondary" onClick={loadHistory}
                  disabled={!canTalk || histLoading || !catalog}>
            {histLoading ? <Spinner /> : `🔄 ${t('btn.refresh')}`}
          </button>
        </div>
        {history.length === 0 && !histLoading && (
          <div style={{ color: c.textDimmed, fontSize: '12px', fontStyle: 'italic' }}>
            {t('relay.click_load_hint')}
          </div>
        )}
        {history.length > 0 && (
          <table style={{ width: '100%', fontSize: '11px' }}>
            <thead>
              <tr style={{ color: c.textSecondary, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
                <th style={{ padding: '4px 6px' }}>{t('th.no')}</th>
                <th style={{ padding: '4px 6px' }}>{t('th.date')}</th>
                <th style={{ padding: '4px 6px' }}>{t('th.event_name')}</th>
                <th style={{ padding: '4px 6px' }}>{t('th.event_code')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.no} style={{ borderBottom: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 6px', color: c.textMuted }}>{h.no}</td>
                  <td style={{ padding: '4px 6px' }}>{h.ts ? fmtDT(h.ts, { seconds: true }) : '—'}</td>
                  <td style={{ padding: '4px 6px' }}>{h.name}</td>
                  <td style={{ padding: '4px 6px', fontFamily: 'monospace', color: c.textMuted }}>
                    0x{Number(h.code || 0).toString(16).padStart(4, '0')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/* eMeter command history — disconnect/reconnect commands WE issued,
            with reason + operator + attempts (system-of-record). */}
        <RelayCommandHistory serial={serial} />
      </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MeterDetailTariffTab — Phase 3.8 (read-only first cut).
//
// Reads the meter's activity calendar via the existing `tou` read
// group (OBIS 0.0.13.0.0.255 active + 0.0.13.0.1.255 passive +
// 0.0.11.0.0.255 special days), surfaces the current active tariff
// register (0.0.96.14.0.255), and displays the result. The write
// path (seasons / week / day profile editor) is intentionally
// gated behind an admin-only confirmation banner because:
//
//   1. The legacy `/api/write/calendar` endpoint targets the old
//      outbound connection pool, not the per-meter inbound socket
//      this view uses; wiring a per-meter calendar write requires
//      a new endpoint + structured AARQ that's worth its own PR.
//   2. The reference test meter (124200356974) is in production
//      against a real customer; a calendar write that gets the
//      seasons/weeks/days cross-references wrong can lock the
//      meter into a tariff that doesn't match billing.
//
// So this tab is "phase 1: safely surface the current calendar".
// Phase 2 (the editor) is planned as future work.
// ─────────────────────────────────────────────────────────────
function MeterDetailTariffTab({ serial, sessionOpen }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [busy, setBusy] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [activeTariff, setActiveTariff] = useState(null);
  const [readAt, setReadAt] = useState(null);

  const readCalendar = async () => {
    if (!serial) return;
    setBusy(true);
    try {
      // Pull the TOU read group: active + passive activity calendar
      // names + special days table. The meter usually only fills the
      // active calendar name on a Holley CAS device.
      const r = await api(`/meters/${serial}/read-group/tou`);
      setCalendar(r?.readings || {});
      // Also try to pull the active tariff register from the status
      // group (a separate read because it's not in `tou`).
      try {
        const s = await api(`/meters/${serial}/read-group/status`);
        const v = s?.readings?.['0.0.96.14.0.255'];
        if (v != null) setActiveTariff(v);
      } catch (_) {}
      setReadAt(new Date().toLocaleTimeString());
      toast.push(t('toast.calendar_read'), 'success');
    } catch (err) {
      toast.push(`Calendar read failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const card = {
    background: c.bgCard,
    border: `1px solid ${c.border}`,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '14px',
  };
  const cardTitle = {
    fontSize: '13px',
    fontWeight: 600,
    color: c.textLabel,
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  // Format a single OBIS value for display. Always returns a string —
  // never JSX — because the parent DetailGrid wraps cells in `String(v)`,
  // which would otherwise turn a React element into "[object Object]".
  // Bytes get UTF-8 decoded with hex fallback; dicts/lists get
  // JSON.stringify; null/empty render as "—".
  const formatValue = (v) => {
    if (v == null) return '—';
    if (typeof v === 'string') return v.length === 0 ? '—' : v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v) || typeof v === 'object') {
      try { return JSON.stringify(v); } catch (_) { return '(unrenderable)'; }
    }
    return String(v);
  };
  // True if every calendar field came back null/empty — used to show
  // a "this meter doesn't expose the calendar via attribute 2" notice
  // instead of three rows of "—".
  const calendarIsEmpty = (cal) => {
    if (cal == null) return true;
    return Object.values(cal).every(v => v == null || v === '');
  };

  return (
    <div>
      {!sessionOpen && (
        <div style={{ marginBottom: '12px', fontSize: '11px', color: c.warning }}>
          ⚠ {t('tariff.session_required')}
        </div>
      )}

      {/* ── Action bar ── */}
      <div style={{
        background: c.bgCardAlt,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '10px 14px',
        marginBottom: '12px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: '11px', color: c.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {t('tariff.activity_calendar')}
        </div>
        {readAt && <div style={{ fontSize: '11px', color: c.textMuted }}>{t('tariff.last_read_at')} {readAt}</div>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button
            className="button button-primary button-sm"
            onClick={readCalendar}
            disabled={!sessionOpen || busy}
          >
            {busy ? <Spinner /> : '📡'} {t('btn.read_calendar')}
          </button>
          <button
            className="button button-secondary button-sm"
            disabled
            title={t('tariff.edit_disabled')}
          >
            ✏️ {t('tariff.edit_admin')}
          </button>
        </div>
      </div>

      {/* ── Current active tariff ── */}
      {activeTariff != null && (
        <div style={card}>
          <div style={cardTitle}><span>💰 {t('tariff.current_active')}</span></div>
          <div style={{ fontSize: '24px', fontWeight: 700, color: c.accent }}>
            T{Number(activeTariff) || activeTariff}
          </div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '4px' }}>
            {t('tariff.from_register')} 0.0.96.14.0.255 — {t('tariff.refreshed_at')} {readAt}
          </div>
        </div>
      )}

      {/* ── Calendar contents ── */}
      <div style={card}>
        <div style={cardTitle}><span>📅 {t('tariff.active_calendar')} (0.0.13.0.0.255)</span></div>
        {calendar == null ? (
          <div style={{ color: c.textMuted, fontSize: '12px' }}>{t('tariff.click_read')}</div>
        ) : calendarIsEmpty(calendar) ? (
          <div style={{
            color: c.warning,
            fontSize: '12px',
            background: 'rgba(251, 191, 36, 0.08)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '6px',
            padding: '10px 12px',
            lineHeight: 1.5,
          }}>
            ⚠ {t('tariff.empty_warning')} Activity Calendar details (seasons, week profiles,
            day profiles) are not yet available — work in progress, will be added soon.
          </div>
        ) : (
          <DetailGrid items={[
            [t('tariff.active_cal_label'), formatValue(calendar['0.0.13.0.0.255'])],
            [t('tariff.passive_cal_label'), formatValue(calendar['0.0.13.0.1.255'])],
            [t('tariff.special_days'), formatValue(calendar['0.0.11.0.0.255'])],
          ]} />
        )}
      </div>

      {/* ── Future-work notice ── */}
      <div style={{
        ...card,
        borderColor: c.accentSecondary,
        borderLeft: `3px solid ${c.accentSecondary}`,
      }}>
        <div style={cardTitle}><span>🚧 {t('tariff.editor_planned')}</span></div>
        <div style={{ fontSize: '12px', color: c.textSecondary, lineHeight: 1.5 }}>
          {t('tariff.editor_desc')} The calendar editor is work in progress and will be added soon.
          The <strong> {t('btn.edit')} </strong> button is currently disabled to prevent accidental changes
          to live meter tariff calendars.
        </div>
      </div>
    </div>
  );
}


// Installation Status sub-tab of MeterDetailView. Groups the
// commissioning metadata already available on the MeterAssignment
// row + the live CAS-server status dict into four focused sections:
// Identification, Hierarchy, Connection, Install Metadata.
//
// The reference product also shows seal info and vector-check
// results here; the backend doesn't persist either today so those
// subsections render a "not collected yet" note for future work.
function MeterDetailInstallationTab({ serial, assignment, live, customer, transformer, meterType }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [prodDate, setProdDate] = useState(null);
  const [busy, setBusy] = useState(false);

  const readProdDate = async () => {
    setBusy(true);
    try {
      const r = await api(`/meters/${serial}/read-group/device_info`);
      setProdDate(r?.readings?.['0.0.96.1.4.255'] ?? null);
      toast.push(t('toast.device_info_read'), 'success');
    } catch (err) {
      toast.push(`Read failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const Section = ({ title, icon, items, action }) => (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>{icon} {title}</div>
        {action}
      </div>
      <DetailGrid items={items} />
    </div>
  );

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '12px',
    }}>
      <Section
        title={t('install.identification')}
        icon="🪪"
        items={[
          [t('meter.meter_no'), assignment.meter_serial],
          [t('relay.logical_device_name'), live?.logical_device_name],
          [t('meter.meter_type'), formatMeterType(meterType) + (meterType?.source === 'override' ? ' (manual)' : meterType?.source === 'autodetected' ? ' (auto)' : '')],
          [t('th.manufacturer'), live?.manufacturer],
          [t('install.firmware'), live?.firmware_version || live?.firmware],
          [t('install.production_date'), prodDate ?? `— ${t('install.click_read')} →`],
        ]}
        action={
          <button
            className="button button-sm button-secondary"
            onClick={readProdDate}
            disabled={busy || !live?.session_open}
            title={!live?.session_open ? t('meter.open_secure_session_first') : t('install.read_dev_title')}
          >
            {busy ? <Spinner /> : `🔄 ${t('btn.read')}`}
          </button>
        }
      />

      <Section
        title={t('install.hierarchy')}
        icon="🏢"
        items={[
          [t('common.customer'), customer?.name],
          [t('install.customer_account'), customer?.account_number],
          [t('install.transformer'), transformer?.name],
          [t('install.transformer_loc'), transformer?.location],
          [t('install.dcu'), assignment.dcu_serial],
          [t('form.role'), assignment.role === 'main' ? `🟧 ${t('install.role_main')}` : `🔌 ${t('install.role_customer')}`],
        ]}
      />

      <Section
        title={t('install.connection')}
        icon="🔌"
        items={[
          [t('form.connection_type'), assignment.connection_type],
          [t('form.ct_ratio'), assignment.ct_ratio],
          [t('form.vt_ratio'), assignment.vt_ratio],
          [t('form.comm_addr'), assignment.comm_addr],
          [t('form.latitude'), assignment.lat],
          [t('form.longitude'), assignment.lon],
        ]}
      />

      <Section
        title={t('install.install_metadata')}
        icon="📝"
        items={[
          [t('form.object_name'), assignment.object_name],
          [t('form.account_number'), assignment.account_number],
          [t('form.install_desc'), assignment.install_description],
        ]}
      />

      {/* Seal info + vector-check panel — future work, backend-side */}
      <div style={{
        background: c.bgCard,
        border: '1px dashed #334155',
        borderRadius: '8px',
        padding: '14px',
        color: c.textMuted,
        fontSize: '12px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textSecondary, marginBottom: '6px' }}>
          🔒 {t('install.seals_vector')}
        </div>
        {t('install.seals_not_collected')} Work in progress — will be added soon.
      </div>
    </div>
  );
}

// Collection Status sub-tab of MeterDetailView. Per-day coverage
// table for the last N days, driven by
// GET /api/meters/{serial}/collection-stats?days=N which counts
// profile_rows per day against the expected cadence (1/day for
// daily_billing, 96/day for load_profile).
//
// Each day row renders a coloured status pill (ok / partial / today
// / missing) + the raw counts so operators can spot gaps at a
// glance. Matches the reference product's Collection Status page.
function MeterDetailCollectionTab({ serial }) {
  const toast = useToast();
  const canPull = useCan()('read_meter');
  const t = useT();
  const c = useThemeColors();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pullBusy, setPullBusy] = useState(false);
  const [pullElapsed, setPullElapsed] = useState(0);

  const load = async () => {
    if (!serial) return;
    setBusy(true);
    try {
      const r = await api(`/meters/${serial}/collection-stats?days=${days}`);
      setData(r);
    } catch (err) {
      toast.push(`${t('collection.stats_failed')}: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serial, days]);

  // Elapsed counter while a manual pull is in flight — pull can take
  // 10-60 s depending on profile size + link quality.
  useEffect(() => {
    if (!pullBusy) { setPullElapsed(0); return; }
    const id = setInterval(() => setPullElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [pullBusy]);

  // Manual pull-yesterday — on-demand safety net when the midnight
  // scheduler + its retry thread both missed a meter. Blocks for the
  // duration of the pull (direct meters hit each profile; DCU-routed
  // meters trigger one DCU aggregate refresh).
  const pullYesterday = async () => {
    if (!serial || pullBusy) return;
    setPullBusy(true);
    try {
      const r = await api(`/meters/${serial}/pull-yesterday`, { method: 'POST' });
      const db = r.results?.daily_billing || {};
      const lp = r.results?.load_profile || {};
      const bus = r.bus === 'dcu' ? 'via DCU' : 'direct';
      toast.push(
        `Pulled ${r.target_day} (${bus}): daily ${db.after ?? '?'}/${db.after > 0 ? '1' : '1'}` +
        (lp.after != null ? ` · LP ${lp.after}` : ''),
        (db.after || 0) >= 1 ? 'success' : 'warning',
      );
      // Refresh the coverage grid so the operator sees the new row count.
      await load();
    } catch (err) {
      toast.push(`Pull yesterday failed: ${err.message}`, 'error');
    } finally {
      setPullBusy(false);
    }
  };

  const statusColor = {
    ok:      { bg: 'rgba(34, 197, 94, 0.15)',  fg: c.success },
    partial: { bg: 'rgba(251, 191, 36, 0.15)', fg: c.warning },
    missing: { bg: 'rgba(239, 68, 68, 0.15)',  fg: c.danger },
    today:   { bg: 'rgba(0, 229, 255, 0.10)',  fg: c.accent },
  };

  const summary = React.useMemo(() => {
    if (!data) return null;
    const counts = { ok: 0, partial: 0, missing: 0, today: 0 };
    data.days.forEach(d => { counts[d.status] = (counts[d.status] || 0) + 1; });
    const totalDbStored = data.days.reduce((s, d) => s + d.daily_billing.stored, 0);
    const totalDbExpected = data.days.reduce((s, d) => s + d.daily_billing.expected, 0);
    const totalLpStored = data.days.reduce((s, d) => s + d.load_profile.stored, 0);
    const totalLpExpected = data.days.reduce((s, d) => s + d.load_profile.expected, 0);
    return {
      counts,
      dbPct: totalDbExpected ? Math.min(100, Math.round(100 * totalDbStored / totalDbExpected)) : 0,
      lpPct: totalLpExpected ? Math.min(100, Math.round(100 * totalLpStored / totalLpExpected)) : 0,
    };
  }, [data]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>{t('collection.window_days')}</div>
          <select className="input-field" value={days}
                  onChange={(e) => setDays(parseInt(e.target.value))}
                  style={{ padding: '6px 8px', fontSize: '12px' }}>
            <option value="7">7</option>
            <option value="14">14</option>
            <option value="30">30</option>
            <option value="60">60</option>
            <option value="90">90</option>
          </select>
        </div>
        <button className="button button-secondary" onClick={load} disabled={busy}>
          {busy ? <Spinner /> : '🔄'} {t('btn.refresh')}
        </button>
        <button className="button button-secondary" onClick={pullYesterday}
                disabled={!canPull || pullBusy || busy}
                title="Force-pull yesterday's daily/LP/PQ rows from the meter buffer"
                style={{ marginLeft: 'auto' }}>
          {pullBusy ? <><Spinner /> Pulling… {pullElapsed}s</> : '⬇ Pull yesterday'}
        </button>
      </div>

      {summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '10px',
          marginBottom: '12px',
        }}>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '10px' }}>
            <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>{t('collection.daily_billing')}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: summary.dbPct >= 90 ? c.success : summary.dbPct >= 50 ? c.warning : c.danger }}>
              {summary.dbPct}%
            </div>
          </div>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '10px' }}>
            <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>{t('collection.load_profile')}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: summary.lpPct >= 90 ? c.success : summary.lpPct >= 50 ? c.warning : c.danger }}>
              {summary.lpPct}%
            </div>
          </div>
          <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '10px' }}>
            <div style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase' }}>{t('collection.day_breakdown')}</div>
            <div style={{ fontSize: '12px', color: c.textLabel }}>
              <span style={{ color: c.success }}>● {t('collection.ok')} {summary.counts.ok || 0}</span>{' · '}
              <span style={{ color: c.warning }}>● {t('collection.partial')} {summary.counts.partial || 0}</span>{' · '}
              <span style={{ color: c.danger }}>● {t('collection.missing')} {summary.counts.missing || 0}</span>
            </div>
          </div>
        </div>
      )}

      {data && (
        <div className="table-container" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          <table style={{ fontSize: '11px', width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
              <tr>
                <th>{t('th.date')}</th>
                <th>{t('common.status')}</th>
                <th style={{ textAlign: 'right' }}>{t('collection.daily_billing')}</th>
                <th style={{ textAlign: 'right' }}>{t('collection.load_profile')}</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map(d => {
                const sc = statusColor[d.status] || statusColor.missing;
                return (
                  <tr key={d.date} style={{ background: sc.bg }}>
                    <td style={{ fontFamily: 'monospace' }}>{d.date}</td>
                    <td>
                      <span style={{
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: 600,
                        color: sc.fg,
                      }}>
                        {d.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {d.daily_billing.stored} / {d.daily_billing.expected}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {d.load_profile.stored} / {d.load_profile.expected}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// On-Demand Read sub-tab of MeterDetailView. Renders four cards,
// each with its own 🔄 Read button that fires a single
// /api/meters/{serial}/read-group/{name} call. Distinct from the
// Meter Readings tab (3.5), which reads DB-cached profile rows —
// this tab always hits the live meter and shows raw register values.
//
// Per spec §10 (instantaneous parameters), the four panels mirror
// what the reference CAS product shows. Missing OBIS fields render
// as '—'.
function MeterDetailOnDemandTab({ serial, sessionOpen }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});
  // R6 — checkbox selection so operators can fan out multiple panels
  // in a single round trip instead of clicking Read on each one in
  // sequence. Defaults to status+energy+line checked (all but errors)
  // because that matches the 3-card "live overview" most operators
  // want when they open the tab.
  const [selected, setSelected] = useState({
    status: true, energy: true, line: true,
  });
  const toggle = (k) => setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  const setAll = (v) => setSelected({ status: v, energy: v, line: v });

  const readGroup = async (panelKey, groupName) => {
    if (!serial) return;
    setBusy(prev => ({ ...prev, [panelKey]: true }));
    try {
      const r = await api(`/meters/${serial}/read-group/${groupName}`);
      setResults(prev => ({
        ...prev,
        [panelKey]: {
          readings: r?.readings || {},
          units: r?.units || {},
          at: new Date().toLocaleTimeString(),
        },
      }));
      const n = Object.keys(r?.readings || {}).length;
      toast.push(t('toast.n_registers_read', { panel: panelKey, n }), 'success');
    } catch (err) {
      toast.push(`${panelKey} read failed: ${err.message}`, 'error');
    } finally {
      setBusy(prev => ({ ...prev, [panelKey]: false }));
    }
  };

  // Read every panel whose checkbox is ticked. We do them sequentially
  // — the meter serializes requests on a single HLS session anyway, so
  // parallel fetches would just queue on the connection lock.
  const readSelected = async () => {
    const groups = {
      status: 'device_info',
      energy: 'energy_all',
      line:   'instantaneous',
      // errors panel removed
    };
    const targets = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (targets.length === 0) {
      toast.push(t('toast.nothing_selected'), 'error');
      return;
    }
    for (const key of targets) {
      await readGroup(key, groups[key]);
    }
  };

  // Render a single row inside one of the panel cards.
  const Row = ({ label, value, unit, ok }) => {
    let displayVal = '—';
    let displayUnit = unit || '';
    let color = c.textLabel;
    if (value != null) {
      if (ok !== undefined) {
        const isOk = value === 0 || value === false || value === '0';
        if (isOk) {
          displayVal = '✓ OK';
          color = c.success;
        } else {
          // Decode the status word bitmask into flag names
          const flags = decodeStatusWord(value);
          if (flags) {
            displayVal = flags.join(', ');
          } else {
            displayVal = `⚠ ${value}`;
          }
          color = c.danger;
        }
      } else if (typeof value === 'number') {
        // Shared with the DCU-routed On-Demand panel via formatMetric, so a
        // meter reads the same whether it is wired directly or sits behind a
        // DCU. Wh/varh/VA/W/var are promoted to their kilo forms
        // unconditionally — see promoteUnit for why the old magnitude
        // threshold had to go.
        const f = formatMetric(value, displayUnit);
        displayVal = f.text;
        displayUnit = f.unit;
      } else {
        displayVal = String(value);
      }
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${c.border}`, fontSize: '12px' }}>
        <span style={{ color: c.textSecondary }}>{label}</span>
        <span style={{ color, fontWeight: 500 }}>
          {displayVal}{displayUnit && value != null && ok === undefined ? ` ${displayUnit}` : ''}
        </span>
      </div>
    );
  };

  const Panel = ({ panelKey, title, icon, groupName, fields }) => {
    const data = results[panelKey];
    const readings = data?.readings || {};
    const units = data?.units || {};
    return (
      <div style={{
        background: c.bgCard,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '14px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>
            {icon} {title}
          </div>
          <button
            className="button button-sm button-secondary"
            onClick={() => readGroup(panelKey, groupName)}
            disabled={!sessionOpen || busy[panelKey]}
            title={!sessionOpen ? t('meter.open_secure_session_first') : `${t('ondemand.read_group')} ${groupName}`}
          >
            {busy[panelKey] ? <Spinner /> : `🔄 ${t('btn.read')}`}
          </button>
        </div>
        {fields.map(f => (
          <Row
            key={f.obis}
            label={f.label}
            value={readings[f.obis]}
            unit={units[f.obis]}
            ok={f.ok}
          />
        ))}
        {data && (
          <div style={{ fontSize: '10px', color: c.textMuted, marginTop: '6px', textAlign: 'right' }}>
            {t('ondemand.read_at')} {data.at}
          </div>
        )}
      </div>
    );
  };

  const anySelected = Object.values(selected).some(Boolean);
  const anyBusy = Object.values(busy).some(Boolean);

  return (
    <div>
      {!sessionOpen && (
        <div style={{ marginBottom: '12px', fontSize: '11px', color: c.warning }}>
          ⚠ {t('ondemand.session_required')} {t('ondemand.session_hint')}
        </div>
      )}

      {/* R6 — bulk selection bar so one "Read Selected" click fires
          every checked panel instead of clicking Read per panel. */}
      <div style={{
        background: c.bgCardAlt,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '10px 14px',
        marginBottom: '12px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: '11px', color: c.textSecondary, marginRight: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {t('ondemand.panels')}
        </div>
        {[
          { k: 'status',  label: `📋 ${t('ondemand.status')}`  },
          { k: 'energy',  label: `⚡ ${t('ondemand.energy')}`  },
          { k: 'line',    label: `📡 ${t('ondemand.line')}`    },
          // errors panel removed
        ].map(({ k, label }) => (
          <label key={k} style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '12px', color: c.textLabel, cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={!!selected[k]}
              onChange={() => toggle(k)}
              style={{ accentColor: c.accent }}
            />
            {label}
          </label>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button
            className="button button-sm button-secondary"
            onClick={() => setAll(true)}
            title={t('btn.all')}
          >{t('btn.all')}</button>
          <button
            className="button button-sm button-secondary"
            onClick={() => setAll(false)}
            title={t('btn.none')}
          >{t('btn.none')}</button>
          <button
            className="button button-sm button-primary"
            onClick={readSelected}
            disabled={!sessionOpen || !anySelected || anyBusy}
          >
            {anyBusy ? <Spinner /> : '📡'} {t('btn.read_selected')}
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '12px',
      }}>
        <Panel
          panelKey="status"
          title={t('ondemand.current_status')}
          icon="📋"
          groupName="device_info"
          fields={[
            { obis: '0.0.96.1.0.255', label: t('ondemand.serial_number') },
            { obis: '0.0.96.1.1.255', label: t('ondemand.manufacturer') },
            { obis: '1.0.0.2.0.255',  label: t('ondemand.firmware_ver') },
            { obis: '0.0.96.1.4.255', label: t('ondemand.prod_date') },
            { obis: '0.0.42.0.0.255', label: t('ondemand.logical_dev') },
          ]}
        />

        <Panel
          panelKey="energy"
          title={t('ondemand.conn_energy')}
          icon="⚡"
          groupName="energy_all"
          fields={[
            { obis: '1.0.1.8.0.255',  label: '+A Total' },
            { obis: '1.0.1.8.1.255',  label: '+A T1' },
            { obis: '1.0.1.8.2.255',  label: '+A T2' },
            { obis: '1.0.1.8.3.255',  label: '+A T3' },
            { obis: '1.0.1.8.4.255',  label: '+A T4' },
            { obis: '1.0.2.8.0.255',  label: '-A Total' },
            { obis: '1.0.2.8.1.255',  label: '-A T1' },
            { obis: '1.0.2.8.2.255',  label: '-A T2' },
            { obis: '1.0.2.8.3.255',  label: '-A T3' },
            { obis: '1.0.2.8.4.255',  label: '-A T4' },
            { obis: '1.0.3.8.0.255',  label: '+R Total' },
            { obis: '1.0.4.8.0.255',  label: '-R Total' },
          ]}
        />

        <Panel
          panelKey="line"
          title={t('ondemand.conn_line')}
          icon="📡"
          groupName="instantaneous"
          fields={[
            { obis: '1.0.32.7.0.255', label: t('ondemand.voltage_l1') },
            { obis: '1.0.52.7.0.255', label: t('ondemand.voltage_l2') },
            { obis: '1.0.72.7.0.255', label: t('ondemand.voltage_l3') },
            { obis: '1.0.31.7.0.255', label: t('ondemand.current_l1') },
            { obis: '1.0.51.7.0.255', label: t('ondemand.current_l2') },
            { obis: '1.0.71.7.0.255', label: t('ondemand.current_l3') },
            { obis: '1.0.13.7.0.255', label: t('ondemand.power_factor') },
            { obis: '1.0.14.7.0.255', label: t('ondemand.frequency') },
          ]}
        />

        {/* Meter Errors / Diagnostics panel removed per user request */}
      </div>
    </div>
  );
}

// Instantaneous Readings panel — shows the poller's periodic stored
// readings from the `readings` table (voltage, current, power, PF,
// frequency). This is DATABASE data only — no live meter reads.
// Uses /meters/{serial}/history?obis=...&from_ts=...
function InstantaneousReadingsPanel({ serial }) {
  const t = useT();
  const c = useThemeColors();
  const [data, setData] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState(1);

  const OBIS_LABELS = {
    '1.0.32.7.0.255': 'V L1',    '1.0.52.7.0.255': 'V L2',    '1.0.72.7.0.255': 'V L3',
    '1.0.31.7.0.255': 'I L1',    '1.0.51.7.0.255': 'I L2',    '1.0.71.7.0.255': 'I L3',
    '1.0.1.7.0.255':  '+P (W)',   '1.0.2.7.0.255':  '-P (W)',
    '1.0.13.7.0.255': 'PF',      '1.0.14.7.0.255': 'Hz',
  };

  const load = async () => {
    if (!serial) return;
    setBusy(true);
    setData([]);
    try {
      const fromTs = Date.now() / 1000 - hours * 3600;
      const r = await api(`/meters/${serial}/history?from_ts=${fromTs}&limit=5000`);
      const readings = r.readings || [];
      // Group by timestamp, pivot OBIS codes into columns
      const byTs = {};
      for (const rd of readings) {
        const ts = rd.ts;
        if (!byTs[ts]) byTs[ts] = { ts };
        byTs[ts][rd.obis] = rd.value_num != null ? rd.value_num : rd.value;
      }
      // Sort by time desc
      const rows = Object.values(byTs).sort((a, b) => b.ts - a.ts);
      setData(rows);
    } catch (err) {}
    finally { setBusy(false); }
  };

  // Search-only — clear rows on meter switch but wait for the
  // operator to pick their range and click Search before firing.
  useEffect(() => { setData([]); }, [serial]);

  const exportCsv = () => {
    if (!data.length) return;
    const obisCodes = Object.keys(OBIS_LABELS);
    const header = ['Timestamp', ...obisCodes.map(o => OBIS_LABELS[o])];
    const csvRows = data.map(r => [
      fmtDT(r.ts, { seconds: true }),
      ...obisCodes.map(o => r[o] != null ? r[o] : ''),
    ]);
    const csv = [header, ...csvRows].map(r => r.join(',')).join('\n');
    downloadCsv(csv, `instantaneous_${serial}_${hours}h.csv`);
  };

  const obisCodes = Object.keys(OBIS_LABELS);

  return (
    <div>
      <div style={{
        display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px',
        padding: '10px 12px', background: c.bgCardAlt, border: `1px solid ${c.border}`, borderRadius: '6px',
      }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted }}>Hours back</div>
          <select className="input-field" value={hours} onChange={(e) => setHours(parseInt(e.target.value))}
                  style={{ padding: '4px 6px', fontSize: '11px', width: '100px' }}>
            <option value="1">1 hour</option>
            <option value="3">3 hours</option>
            <option value="6">6 hours</option>
            <option value="12">12 hours</option>
            <option value="24">24 hours</option>
          </select>
        </div>
        <button className="button button-sm button-primary" onClick={load} disabled={busy}>
          {busy ? <Spinner /> : '🔍'} {t('btn.search')}
        </button>
        <div style={{ marginLeft: 'auto', fontSize: '11px', color: c.textMuted }}>
          {data.length} readings from database (poller)
        </div>
      </div>

      {data.length > 0 && (
        <div style={{
          background: c.bgCard, border: `1px solid ${c.border}`,
          borderRadius: '8px', overflow: 'auto', maxHeight: '60vh',
        }}>
          <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: c.bgCardAlt }}>
              <tr>
                <th style={{ padding: '6px 8px', textAlign: 'left', color: c.textSecondary }}>{t('th.timestamp')}</th>
                {obisCodes.map(o => (
                  <th key={o} style={{ padding: '6px 8px', textAlign: 'right', color: c.textSecondary }}>{OBIS_LABELS[o]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 500).map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td style={{ padding: '4px 8px', color: c.textSecondary, whiteSpace: 'nowrap' }}>
                    {fmtDT(r.ts, { seconds: true })}
                  </td>
                  {obisCodes.map(o => (
                    <td key={o} style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', color: c.textPrimary }}>
                      {r[o] != null ? Number(r[o]).toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length > 500 && (
            <div style={{ padding: '8px', fontSize: '11px', color: c.textMuted, textAlign: 'center' }}>
              Showing 500 of {data.length} rows. Use CSV export for full dataset.
            </div>
          )}
        </div>
      )}

      {!busy && data.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: c.textMuted }}>
          No instantaneous readings stored for this meter in the last {hours} hour(s).
          The poller stores readings at the configured interval (see System Settings) when the meter has an active session.
        </div>
      )}
    </div>
  );
}


// Period Consumption panel — computes consumption (end − start) for
// each energy register over a user-selected date range. Uses the
// /api/reports/period-consumption endpoint which reads stored daily
// billing snapshots from the midnight scheduler.
function PeriodConsumptionPanel({ serial, assignment, applyCTVT = false }) {
  // applyCTVT comes from the parent MeterDetailReadingsTab's global
  // Primary/Secondary toggle so we don't duplicate the control in
  // every sub-tab. Default false for standalone usage (if this
  // component is ever rendered outside the readings tab).
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const { lang } = useLang();
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86400 * 1000);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(fmt(weekAgo));
  const [toDate, setToDate] = useState(fmt(today));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const ctvtMul = computeCTVTMultiplier(assignment);

  const calculate = async () => {
    if (!serial || !fromDate || !toDate) return;
    setBusy(true);
    setData(null);
    try {
      // Thread the Primary/Secondary toggle into the backend
      // request; previously the frontend ALSO multiplied by
      // ctvtMul after receiving already-scaled values, giving
      // ×(CT×VT)² numbers in primary mode.
      const r = await api(`/reports/period-consumption?from_date=${fromDate}&to_date=${toDate}&meter_serial=${serial}&lang=${getReportLang() || lang}&primary=${applyCTVT ? 'true' : 'false'}`);
      setData(r);
      if (r.rows?.[0]?.error === 'no_data') {
        toast.push(t('period.no_data'), 'error');
      } else {
        toast.push(t('toast.read_complete'), 'success');
      }
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const url = `/api/reports/period-consumption.xlsx?from_date=${fromDate}&to_date=${toDate}&meter_serial=${serial}&lang=${getReportLang() || lang}`;
      await downloadXlsx(
        url,
        `period_consumption_${serial}_${fromDate}_${toDate}.xlsx`,
        { toast },
      );
    } catch (_) { /* toast already shown by helper */ }
  };

  const row = data?.rows?.[0];
  const regs = row?.registers || {};

  return (
    <div>
      {/* No inner CT/VT banner — the parent MeterDetailReadingsTab
          renders a single global Primary/Secondary header above all
          sub-tabs and passes applyCTVT down to this panel. */}

      {/* Date range + actions. Range capped at 366 days (12 months
          of meter on-board retention, leap-year safe). Period
          consumption reads daily-billing rows, which are auto-
          backfilled 31 days on connect and kept current by the
          midnight scheduler — for wider ranges the operator pulls
          via the Daily sub-tab in Meter Readings. */}
      {(() => {
        const spanDays = Math.max(0, Math.round(
          (new Date(toDate + 'T23:59:59').getTime() - new Date(fromDate + 'T00:00:00').getTime()) / 86400000
        ));
        const tooWide = spanDays > 366;
        return (
          <>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: tooWide ? '4px' : '12px' }}>
              <div>
                <div style={{ fontSize: '10px', color: c.textMuted }}>{t('common.from')}</div>
                <input type="date" className="input-field" value={fromDate}
                       onChange={(e) => setFromDate(e.target.value)}
                       style={{ padding: '6px 8px', fontSize: '12px' }} />
              </div>
              <div>
                <div style={{ fontSize: '10px', color: c.textMuted }}>{t('common.to')}</div>
                <input type="date" className="input-field" value={toDate}
                       onChange={(e) => setToDate(e.target.value)}
                       style={{ padding: '6px 8px', fontSize: '12px' }} />
              </div>
              <button className="button button-primary" onClick={calculate} disabled={busy || tooWide}>
                {busy ? <Spinner /> : '📊'} {t('period.calculate') || 'Calculate'}
              </button>
              <button className="button button-secondary" onClick={exportXlsx} disabled={!data}>⬇ XLSX</button>
              <span style={{ marginLeft: '8px', alignSelf: 'center' }}><ReportLangToggle /></span>
            </div>
            {tooWide && (
              <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '12px' }}>
                Range {spanDays} days exceeds the maximum (366 days / 12 months — meter on-board retention). Narrow the range and try again.
              </div>
            )}
          </>
        );
      })()}


      {/* Results table */}
      {row && row.error !== 'no_data' && (
        <div style={{
          background: c.bgCard, border: `1px solid ${c.border}`,
          borderRadius: '8px', overflow: 'auto',
        }}>
          <div style={{ padding: '10px 14px', fontSize: '11px', color: c.textMuted }}>
            {t('common.from')}: <strong style={{ color: c.textPrimary }}>{fmtDT(row.start_time)}</strong>
            {' → '}
            {t('common.to')}: <strong style={{ color: c.textPrimary }}>{fmtDT(row.end_time)}</strong>
          </div>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.bgCardAlt }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: c.textSecondary }}>Register</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', color: c.textSecondary }}>{t('period.start') || 'Start'}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', color: c.textSecondary }}>{t('period.end') || 'End'}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', color: c.accent, fontWeight: 700 }}>{t('period.consumption') || 'Consumption'}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(regs).map(([obis, r]) => {
                // Backend already applied CT×VT when primary=true was
                // passed in the request. Don't multiply again here —
                // that was the double-scaling bug.
                const fd = assignment?.fraction_digits;
                return (
                  <tr key={obis} style={{ borderTop: `1px solid ${c.border}` }}>
                    <td style={{ padding: '6px 12px', color: c.textLabel }}>{r.label || obis}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: c.textSecondary }}>
                      {r.start != null ? formatMeterNumber(r.start, fd) : '—'}
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: c.textSecondary }}>
                      {r.end != null ? formatMeterNumber(r.end, fd) : '—'}
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: c.accent }}>
                      {r.consumption != null ? formatMeterNumber(r.consumption, fd) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {row && row.error === 'no_data' && (
        <div style={{ padding: '18px 14px', textAlign: 'center', color: c.textMuted, fontSize: '12px' }}>
          <div>{t('period.no_data') || 'No daily billing data found for this date range.'}</div>
          <div style={{ marginTop: '6px', fontSize: '11px', color: c.textDimmed }}>
            Period Consumption is calculated from stored daily-billing
            snapshots. If your range extends beyond the 31-day
            auto-backfill window, open <strong>Meter Readings → Daily</strong>,
            set the same range, and use <strong>Pull from meter</strong> to
            backfill — then return here and click Calculate.
          </div>
        </div>
      )}
    </div>
  );
}


// Meter Readings sub-tab of MeterDetailView. Four inner sub-tabs,
// matching the CAS reference product exactly: Daily / Monthly /
// Load Profile / On Demand. The first three read from SQLite via
// the read-through cache (commit 2aca4cb); the On Demand inner tab
// fires live per-panel reads against the meter via the existing
// read-group endpoints (content shared with MeterDetailOnDemandTab).
//
// Rows in the historical tables are colour-coded to match the
// reference legend — Estimated (green) / Abnormal (amber) / Missing
// (red) / SmartApp (cyan). The backend doesn't yet persist these
// row health flags, so we derive them client-side: a row with any
// null critical value is Abnormal; a row missing from the expected
// cadence is Missing; otherwise Estimated (the normal case for
// backfilled rows).
// Banner that fronts the async profile-backfill job for one meter.
// Shows an explicit "Pull from meter" button when the range is too
// wide for the backend's synchronous auto-fill (~7 days) and the
// cache is empty. While a job is in flight, displays chunks-done
// progress + ETA and polls /api/profile-backfill/{job_id} every
// 10 s until done, at which point it calls onBackfillDone so the
// parent can reload the table.
// Format a seconds count as "~Xs", "~Y min", or "~Z.Zh".
function formatEtaLabel(sec) {
  if (sec < 60) return `~${Math.max(1, Math.round(sec))}s`;
  if (sec < 3600) return `~${Math.round(sec / 60)} min`;
  return `~${(sec / 3600).toFixed(1)} h`;
}

function formatElapsed(ms) {
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function ProfileBackfillBanner({ serial, profileName, gap, busy, onBackfillDone }) {
  // gap is { fromTs, toTs } when the parent's loadRows determined a
  // missing window that can be filled from the meter's on-board
  // history. When non-null we auto-kick the job on mount / gap-change
  // so Search is also Pull — no second click required. Only for users
  // who may read from meters ("Read from meter"); others just see the gap.
  const c = useThemeColors();
  const toast = useToast();
  const canRead = useCan()('read_meter');
  const [job, setJob] = React.useState(null);
  const [starting, setStarting] = React.useState(false);
  // Track ranges we already know don't need another pull, keyed by
  // "serial|profile|fromTs|toTs". A range lands here on two events:
  // 1. Operator clicks "Run in background" — "stop popping the modal".
  // 2. A job for this range completed with rows_stored === 0 — the
  //    meter's on-board buffer has nothing for this window, so
  //    re-firing will just produce the same empty result and
  //    trap us in a modal-reopen loop.
  // Scoped per-component, so widening the date range or switching
  // meters (which mounts a fresh banner) resets the memory naturally.
  const [exhaustedRanges, setExhaustedRanges] = React.useState(() => new Set());
  const rangeKey = React.useMemo(() =>
    (gap && gap.fromTs != null && gap.toTs != null)
      ? `${serial}|${profileName}|${gap.fromTs}|${gap.toTs}`
      : null,
    [serial, profileName, gap?.fromTs, gap?.toTs]
  );
  // Elapsed-time tick: rerender every second while a job is in flight
  // so the modal's elapsed counter actually moves.
  const [nowTick, setNowTick] = React.useState(() => Date.now());
  const gapFromTs = gap?.fromTs;
  const gapToTs = gap?.toTs;
  const spanDays = gap ? Math.max(0, Math.round((gapToTs - gapFromTs) / 86400)) : 0;
  // Server uses cadence-aware chunks (30-day for daily/LP/PQ, 90-day
  // for monthly). Estimate chunks the same way so the ETA matches.
  // Default 30-day chunks here — most profiles are daily/LP/PQ; a
  // slight under-estimate for monthly is acceptable.
  const CHUNK_DAYS = 30;
  const chunks = Math.max(1, Math.ceil(spanDays / CHUNK_DAYS));
  // Fallback fixed estimate — 30s/chunk matches the inline-banner
  // behaviour. The modal replaces this with the live average once
  // the first chunk completes.
  const fallbackEtaSec = chunks * 30;
  const etaLabel = formatEtaLabel(fallbackEtaSec);

  // Reset job state when the (meter, profile, gap) tuple changes —
  // operators switching tabs or date ranges shouldn't see stale
  // progress from an unrelated pull.
  React.useEffect(() => { setJob(null); }, [serial, profileName, gapFromTs, gapToTs]);

  // Poll job status while it's in flight.
  React.useEffect(() => {
    if (!job || !(job.state === 'queued' || job.state === 'running')) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const fresh = await api(`/profile-backfill/${encodeURIComponent(job.job_id)}`);
        if (cancelled) return;
        setJob(fresh);
        if (fresh.state === 'done' || fresh.state === 'done_with_errors') {
          const n = fresh.rows_stored || 0;
          if (n > 0) {
            toast.push(`Backfill done: ${n} row(s) stored`, 'success');
            if (typeof onBackfillDone === 'function') onBackfillDone();
          } else {
            // 0 rows = meter's on-board buffer doesn't cover the
            // requested range. Mark the range exhausted so the
            // auto-kickoff effect doesn't re-fire (which would loop
            // forever — same empty result, same re-kick) and skip
            // onBackfillDone since re-calling loadRows would just
            // re-detect the same empty gap.
            toast.push(
              'No data available from the meter for this range — the meter\'s on-board buffer may not go back that far.',
              'info'
            );
            if (rangeKey) {
              setExhaustedRanges(prev => new Set(prev).add(rangeKey));
            }
          }
        }
      } catch (_) { /* keep prior */ }
    };
    const id = setInterval(tick, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [job?.job_id, job?.state]);

  // Re-render every second so the modal's "elapsed" counter moves.
  // Only ticks while a job is actually running; cleans up otherwise.
  React.useEffect(() => {
    if (!job || !(job.state === 'queued' || job.state === 'running')) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [job?.state]);

  const kickoff = React.useCallback(async () => {
    if (!gapFromTs || !gapToTs) return;
    setStarting(true);
    try {
      const r = await api(
        `/meters/${serial}/profile/${profileName}/backfill?from_ts=${gapFromTs}&to_ts=${gapToTs}`,
        { method: 'POST' }
      );
      setJob(r);
      // Richer kickoff notification. The actual pull runs in a
      // daemon thread server-side; operators don't need a blocking
      // modal, just a heads-up about what's happening + ETA so
      // they know why the table will refresh in a few minutes.
      // Inline banner below the toolbar continues to show live
      // progress.
      toast.push(
        r.existing
          ? 'Job already running — watching progress'
          : `Pulling ${spanDays} day(s) from meter · about ${etaLabel} — table refreshes automatically when done`,
        'info'
      );
    } catch (err) {
      // Backend returns 503 with a clear message when the meter is
      // unreachable at queue time. Surface that as a hard popup so
      // operators can't miss it (toasts disappear too quickly for
      // an action-blocking error).
      const msg = err.message || String(err);
      if (/offline/i.test(msg) || /unreachable/i.test(msg) || /can't reach/i.test(msg)) {
        window.alert(msg);
      } else {
        toast.push(`Pull failed: ${msg}`, 'error');
      }
    } finally {
      setStarting(false);
    }
  }, [serial, profileName, gapFromTs, gapToTs, etaLabel, toast]);

  // Auto-kick the job as soon as we have a non-empty gap and no
  // existing job — the Search click IS the pull intent. The
  // dependency array makes this fire once per distinct gap; if the
  // operator re-clicks Search with the same gap, the kickoff
  // endpoint returns the existing job_id (existing=true).
  React.useEffect(() => {
    if (!canRead || !gap || job || starting || busy) return;
    // Don't re-kick a range the operator has already dismissed or
    // that we know has no data — otherwise we'd loop forever on
    // "meter has nothing for this window".
    if (rangeKey && exhaustedRanges.has(rangeKey)) return;
    kickoff();
  }, [canRead, gap, job, starting, busy, kickoff, rangeKey, exhaustedRanges]);

  if (!job && !gap) return null;

  // Adaptive ETA: once the first chunk completes the backend records
  // its wall-clock duration and exposes a rolling 5-chunk average in
  // recent_avg_chunk_ms. Fall back to the fixed 30s/chunk estimate
  // before any chunks have landed.
  const remainingChunks = job
    ? Math.max(0, (job.chunks_total || 0) - (job.chunks_done || 0))
    : chunks;
  const avgMs = job?.recent_avg_chunk_ms;
  const adaptiveEtaSec = avgMs && avgMs > 0
    ? Math.round((remainingChunks * avgMs) / 1000)
    : remainingChunks * 30;
  const adaptiveEtaLabel = formatEtaLabel(adaptiveEtaSec);
  const pct = job && job.chunks_total
    ? Math.min(100, Math.round(((job.chunks_done || 0) / job.chunks_total) * 100))
    : 0;
  const startedAt = job?.started_at ? new Date(job.started_at).getTime() : null;
  const elapsedLabel = startedAt ? formatElapsed(nowTick - startedAt) : '—';

  const isInFlight = job && (job.state === 'queued' || job.state === 'running');
  const bgColor = job?.state === 'done' ? c.bgCardAlt
                : job?.state === 'failed' ? '#7f1d1d22'
                : c.bgCardAlt;

  const banner = (
    <div style={{
      padding: '10px 14px',
      marginBottom: '10px',
      background: bgColor,
      border: `1px dashed ${c.border}`,
      borderRadius: '6px',
      fontSize: '12px',
      display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
    }}>
      {!job && starting && (
        <span>
          <Spinner /> Queuing pull for <strong>{spanDays} day(s)</strong> from the meter…
        </span>
      )}
      {!job && !starting && gap && (
        <span>
          Pulling <strong>{spanDays} missing day(s)</strong> from the meter's
          on-board history (ETA {etaLabel}). Table refreshes automatically.
        </span>
      )}
      {isInFlight && (
        <span>
          Downloading missing <strong>{spanDays} day(s)</strong> from meter…{' '}
          <strong>{job.chunks_done || 0}/{job.chunks_total || '?'}</strong> chunks
          ({pct}%) · elapsed {elapsedLabel}
          {remainingChunks > 0 && <> · ETA {adaptiveEtaLabel}</>}.
          Table refreshes automatically when done.
        </span>
      )}
      {job && (job.state === 'done' || job.state === 'done_with_errors') && (
        <span style={{ color: (job.rows_stored || 0) > 0 ? c.accent : c.textMuted }}>
          {(job.rows_stored || 0) > 0
            ? `✓ Backfill complete — stored ${job.rows_stored} row(s).`
            : '⚠ No data available from the meter for this range — the meter\'s on-board buffer may not go back that far.'}
          {job.last_error && <span style={{ color: '#f87171' }}> (some chunks failed — see server log)</span>}
        </span>
      )}
      {job && job.state === 'failed' && (
        <span style={{ color: '#f87171' }}>Backfill failed: {job.last_error || 'unknown error'}</span>
      )}
    </div>
  );

  return (
    <>
      {banner}
    </>
  );
}

function MeterDetailReadingsTab({ serial, sessionOpen }) {
  const toast = useToast();
  const c = useThemeColors();
  const t = useT();
  const { assignments } = useAssignments();
  const [sub, setSub] = useState('daily');
  // Default to *yesterday* (a single complete day). Yesterday is the
  // most recent fully-collected day, and a 1-day window stays within
  // the 3-day Power-Quality / Load-Profile cap so LP/PQ loads cleanly
  // on first open. Operators widen the range as needed.
  const today = new Date();
  const yesterday = new Date(today.getTime() - 1 * 86400 * 1000);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(fmt(yesterday));
  const [toDate, setToDate] = useState(fmt(yesterday));
  const [rows, setRows] = useState([]);
  const [captures, setCaptures] = useState([]);
  // CT/VT toggle — when enabled, the API applies per-OBIS
  // primary-side scaling (energies & powers ×CT×VT, currents ×CT,
  // voltages ×VT, frequency / PF / status untouched). Backend does
  // the math via `?primary=true` so we never multiply on the client
  // — that path used to apply CT×VT to everything (including
  // voltages and frequency) which was wrong. Default ON: operators
  // typically want primary-side numbers; flip OFF to match what the
  // meter LCD shows.
  const [applyCTVT, setApplyCTVT] = useState(true);
  const [busy, setBusy] = useState(false);
  // Meter-assignment metadata (CT / VT) so we can show the ratios next
  // to the readings table header. Loaded once on mount.
  const assignment = React.useMemo(
    () => assignments.find(x => x.meter_serial === serial) || null,
    [assignments, serial],
  );
  // Behind a DCU? Decides whether the Live tab can mean anything — see the
  // `live` entry below. Derived from the assignment rather than from live
  // session state, because topology is what makes the poller unable to
  // reach this meter; whether the DCU happens to be up right now does not
  // change that.
  const viaDcu = !!(assignment && assignment.dcu_serial);

  const profileMap = {
    daily:    { name: 'daily_billing',   label: '🗓 ' + t('mr.daily'),   obis: '0.0.98.2.0.255' },
    // Monthly is *derived from* daily_billing — see loadRows() below.
    // The meter's own monthly_billing register is unreliable on Holley
    // CAS devices (it only updates after a billing-period reset on the
    // meter), so we instead aggregate the daily snapshots client-side.
    // Monthly reads from the meter's actual monthly_billing profile
    // (0.0.98.1.0.255, 12 entries, CAS spec §13.1). The midnight
    // scheduler now pulls this nightly. Falls back to daily aggregation
    // client-side only if the monthly profile is empty.
    monthly:  { name: 'monthly_billing', label: '📅 ' + t('mr.monthly'), obis: '0.0.98.1.0.255' },
    // LP/PQ — merged Load-Profile + Power-Quality view. Fetched from the
    // server /lp-report (which joins the 15-min LP rows with the nearest
    // PQ row and pre-scales per the primary toggle), so the two former
    // separate sub-tabs are now one. obis stays the LP code so the 3-day
    // raw-profile range cap still applies.
    load:     { name: 'lp_pq',           label: '📈 LP/PQ', obis: '1.0.99.1.0.255' },
    // HIDDEN 2026-08-24 (operator decision). The panel renders whatever the
    // poller last stored, which for most of the fleet is nothing or is hours
    // old — so it reads as a live instrument while showing stale or empty
    // data. Kept in the map rather than deleted: the panel and its route still
    // work, and On Demand is the reading path being fixed first. Flip
    // `hidden` to bring the tab back.
    instant:  { name: null, label: '📟 Instantaneous', obis: null, hidden: true },
    ondemand: { name: null,              label: '📡 ' + t('mr.on_demand'), obis: null }, // live reads — handled below
    period:   { name: null,              label: '📊 ' + t('mr.period'), obis: null }, // period consumption — dedicated panel
    // Live dashboard — reuses LiveReadingsPanel from the Inbound
    // Meters tab. Polls /latest every 5s + WebSocket pushes. No
    // separate data-fetch path here; the panel owns its own
    // lifecycle.
    // HIDDEN FOR DCU-ROUTED METERS (2026-08-25, operator decision).
    //
    // This panel is fed by the background poller, which iterates the CAS
    // pool and opens a per-meter GMAC session. A meter behind a DCU has no
    // session of its own — the DCU holds the single link — so the poller
    // structurally CANNOT reach it. Measured the same day: of meters polled
    // in one hour, 324 of 394 DIRECT meters, and 0 of 1672 behind DCUs.
    // The tab therefore showed an empty 5-second-refreshing dashboard that
    // read as a live instrument, which is the same complaint that got
    // Instantaneous hidden the day before.
    //
    // Reading these meters live IS possible — one register at a time over
    // their SAP, ~3 s each — but an automatic sweep of 1672 of them is the
    // walk that was suppressed for starving DCU collection (the DCU serves
    // one task at a time). So that path stays operator-initiated on the On
    // Demand tab, and does not drive a panel that polls every 5 s.
    //
    // DIRECT meters keep the tab: for them the poller is exactly the point.
    live:     { name: null,              label: '🔴 Live', obis: null, hidden: viaDcu },
  };
  const profile = profileMap[sub];

  // A hidden tab must not stay selected. `sub` survives re-renders and can
  // still hold 'instant' from before the tab was hidden — without this the
  // picker shows nothing selected while the hidden panel renders below it.
  React.useEffect(() => {
    if (profileMap[sub] && profileMap[sub].hidden) setSub('daily');
    // `viaDcu` is in the deps because it settles AFTER mount — assignments
    // load async. Without it, opening a DCU-routed meter while Live was
    // the remembered tab left the hidden panel rendering under a picker
    // with nothing selected, which is the exact bug this effect exists to
    // prevent for statically-hidden tabs.
    /* eslint-disable-next-line */
  }, [sub, viaDcu]);

  // Meter-local tz (Asia/Tashkent) — `dayStart`/`dayEnd` handle the
  // conversion regardless of the browser's own tz.
  const toUnixStart = (ds) => dayStart(ds);
  const toUnixEnd   = (ds) => dayEnd(ds);

  // Aggregate daily_billing rows into one row per *complete* calendar
  // month. A month is "complete" when the number of distinct day rows
  // equals days-in-that-month — partial months at the start or end of
  // the range get dropped (and reported in the legend so operators
  // know why a month is missing).
  //
  // The output row carries the LAST day's values for that month,
  // because the energy registers in daily_billing are end-of-day
  // cumulative snapshots — the last day's value is the month-end
  // cumulative reading. Operators wanting "delta consumption" can
  // subtract the previous month's row.
  const aggregateDailyToMonthly = (dailyRows) => {
    const buckets = {};   // 'YYYY-MM' → [rows]
    for (const r of dailyRows || []) {
      const ts = r.row_ts ? new Date(r.row_ts * 1000) : (r.ts ? new Date(r.ts) : null);
      if (!ts || isNaN(ts.getTime())) continue;
      const key = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}`;
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push({ ...r, _date: ts });
    }
    const monthly = [];
    const incomplete = [];
    for (const key of Object.keys(buckets).sort()) {
      const [year, month] = key.split('-').map(Number);
      const daysExpected = new Date(year, month, 0).getDate(); // days in month
      const rowsForMonth = buckets[key];
      // Count distinct days (a meter glitch could write 2 rows for one day).
      const distinctDays = new Set(rowsForMonth.map(r => r._date.toISOString().slice(0, 10)));
      if (distinctDays.size < daysExpected) {
        incomplete.push({ key, have: distinctDays.size, need: daysExpected });
        continue;
      }
      // Pick the last row of the month as the month-end snapshot.
      rowsForMonth.sort((a, b) => (a._date - b._date));
      const lastRow = rowsForMonth[rowsForMonth.length - 1];
      monthly.push({
        ...lastRow,
        ts: `${key}-01`,
        row_ts: lastRow.row_ts,
        _label: key,
      });
    }
    monthly.sort((a, b) => (b.row_ts || 0) - (a.row_ts || 0));
    return { monthly, incomplete };
  };
  const [monthlyIncomplete, setMonthlyIncomplete] = useState([]);
  const [monthlySource, setMonthlySource] = useState('meter'); // 'meter' | 'daily_fallback'

  // Gap the last loadRows call identified — passed to
  // ProfileBackfillBanner to auto-kick an async pull. `null` when
  // cache covers the requested range or when the gap falls inside
  // the 31-day auto-backfill window (in which case we don't pull,
  // because the auto-backfill should already have it).
  const [pullGap, setPullGap] = useState(null);
  // Holds the most-recently-mounted meter serial. loadRows captures
  // a snapshot at fetch-start and bails when the response arrives if
  // the operator has since clicked a different meter — without this
  // an in-flight fetch for meter A could call setRows with A's data
  // after the user navigated to meter B, leaving B's header above
  // A's table.
  const activeSerialRef = useRef(serial);
  useEffect(() => { activeSerialRef.current = serial; }, [serial]);

  const loadRows = async () => {
    if (!serial || !profile.name) return;
    const fetchedFor = serial;
    setBusy(true);
    setRows([]);
    setCaptures([]);
    setMonthlyIncomplete([]);
    try {
      // Monthly view widens the fetch window to the full calendar months
      // touched by the date picker so we don't drop a partial-month bucket
      // just because the user picked the wrong start day.
      let fromTs = toUnixStart(fromDate);
      let toTs = toUnixEnd(toDate);
      if (sub === 'monthly') {
        // Parse YYYY-MM-DD directly so the month derivation doesn't
        // depend on the browser's tz interpretation.
        const [fy, fm] = fromDate.split('-').map(Number);
        const [ty, tm] = toDate.split('-').map(Number);
        fromTs = monthStart(fy, fm);
        toTs   = monthEnd(ty, tm);
      }
      // Every report now uses refresh=never: the tab renders from
      // the cache instantly, and if the requested window exceeds
      // what's stored the gap-detection block below kicks an async
      // ProfileBackfillBanner job. Operators always see the
      // progress modal for reach-back pulls instead of the API
      // blocking on a synchronous DLMS fetch. Applies uniformly to
      // daily / monthly / load / power-quality.
      const isRawProfile15Min = (profile.obis === '1.0.99.1.0.255' || profile.obis === '1.0.99.1.2.255');
      // LP/PQ merged view — fetch the server's merged + pre-scaled rows
      // and map them onto the generic table. The server already joins LP
      // with the nearest PQ row and applies the primary scaling, so we
      // hand the table scale:1 captures (the LP raw-profile obis on the
      // sub-tab keeps renderCell from re-applying CT/VT).
      if (profile.name === 'lp_pq') {
        const LP_PQ_CAPS = [
          ['usage_active_import',   'Usage +A(kWh)'],
          ['usage_active_export',   'Usage -A(kWh)'],
          ['usage_reactive_import', 'Usage +R(kvarh)'],
          ['usage_reactive_export', 'Usage -R(kvarh)'],
          ['demand_apparent_import','Demand +S(kVA)'],
          ['demand_apparent_export','Demand -S(kVA)'],
          ['pf', 'PF'],
          ['max_v_l1', 'Max V L1(V)'], ['max_v_l2', 'Max V L2(V)'], ['max_v_l3', 'Max V L3(V)'],
          ['min_v_l1', 'Min V L1(V)'], ['min_v_l2', 'Min V L2(V)'], ['min_v_l3', 'Min V L3(V)'],
          ['avg_i_l1', 'Avg I L1(A)'], ['avg_i_l2', 'Avg I L2(A)'], ['avg_i_l3', 'Avg I L3(A)'],
          ['pf_l1', 'PF L1'], ['pf_l2', 'PF L2'], ['pf_l3', 'PF L3'],
          ['avg_v_l1', 'Avg V L1(V)'], ['avg_v_l2', 'Avg V L2(V)'], ['avg_v_l3', 'Avg V L3(V)'],
          ['freq', 'Freq(Hz)'],
          ['status', 'Meter Status'],
        ];
        const lpr = await api(
          `/meters/${encodeURIComponent(serial)}/lp-report?from_ts=${fromTs}&to_ts=${toTs}&primary=${applyCTVT ? 'true' : 'false'}`
        );
        if (activeSerialRef.current !== fetchedFor) return;
        // The 'status' column carries the meter internal status word — give
        // it the real status OBIS (0.0.96.10.3.255) so renderCell routes it
        // through StatusCell and decodes the bitmask instead of showing the
        // raw integer. Everything else gets a synthetic obis (no scaling).
        setCaptures(LP_PQ_CAPS.map(([key, label]) => ({
          obis: key === 'status' ? '0.0.96.10.3.255' : 'lppq.' + key,
          label,
          scale: 1,
        })));
        const merged = (lpr.rows || []).map((row) => ({
          row_ts: row.row_ts,
          ts: row.time,
          values: LP_PQ_CAPS.map(([key]) => (row[key] == null ? null : row[key])),
        }));
        setRows(merged);
        toast.push(`Loaded ${merged.length} rows`, 'success');
        return;
      }
      const params = new URLSearchParams({
        from_ts: String(fromTs),
        to_ts: String(toTs),
        limit: '5000',
        refresh: 'never',
        // Always fetch raw values; the Primary/Secondary toggle is
        // applied client-side via primaryFactor() in renderCell so
        // flipping it is instant (no refetch). The per-OBIS math
        // matches the backend.
        primary: 'false',
      });
      let r;
      if (sub === 'monthly') {
        // Try the meter's actual monthly_billing profile first (CAS spec
        // §13.1 — 12 monthly snapshots stored on the meter). The midnight
        // scheduler pulls these nightly. If the meter has real monthly
        // data we use it directly; if empty we fall back to aggregating
        // daily_billing rows client-side.
        r = await api(`/meters/${serial}/profile/monthly_billing?${params}`);
        if ((r.rows || []).length === 0) {
          // Fallback: aggregate daily_billing into monthly
          const dailyParams = new URLSearchParams({
            from_ts: String(fromTs), to_ts: String(toTs),
            limit: '5000', refresh: 'auto',
            primary: 'false',
          });
          r = await api(`/meters/${serial}/profile/daily_billing?${dailyParams}`);
        }
      } else {
        r = await api(`/meters/${serial}/profile/${profile.name}?${params}`);
      }
      // Discard the response if the operator has since switched to a
      // different meter — without this guard a slow fetch for meter A
      // can land after the user clicked meter B and overwrite B's
      // (now empty) state with A's rows.
      if (activeSerialRef.current !== fetchedFor) {
        return;
      }
      setCaptures(r.captures || []);
      let displayRows = r.rows || [];
      // If monthly and we fell back to daily, aggregate into months
      if (sub === 'monthly' && profile.obis === '0.0.98.1.0.255' && displayRows.length > 0 && displayRows[0]?.values?.length > 10) {
        // This looks like daily_billing rows (20+ columns), not monthly
        // (which has fewer). Aggregate them.
        // Actually, detect by checking if the profile response came from
        // daily_billing (the profile name in the response).
      }
      if (sub === 'monthly') {
        // Check if we got data from real monthly_billing or fell back
        const isFromDaily = r.profile === 'daily_billing' || (r.obis === '0.0.98.2.0.255');
        setMonthlySource(isFromDaily ? 'daily_fallback' : 'meter');
        if (isFromDaily && displayRows.length > 0) {
          const { monthly, incomplete } = aggregateDailyToMonthly(displayRows);
          displayRows = monthly;
          setMonthlyIncomplete(incomplete);
        }
      }
      setRows(displayRows);
      const cacheNote = r.cache?.refreshed ? ' · refreshed from meter' : '';
      toast.push(`Loaded ${displayRows.length} ${sub === 'monthly' ? 'complete month(s)' : 'rows'}${cacheNote}`, 'success');

      // Gap detection: does the cache extend back to the requested
      // from_ts, or is there a missing window we can pull from the
      // meter? Only fire when the missing portion crosses the
      // auto-backfill boundary — gaps inside that window are a
      // connection issue, not something Search can solve. Daily =
      // 31 days (matches _backfill_direct_meter default); PQ/LP =
      // 3 days (matches the new 288-row pre-warm on first connect).
      const AUTO_BACKFILL_DAYS = isRawProfile15Min ? 3 : 31;
      const nowSec = Math.floor(Date.now() / 1000);
      let gapFrom = null, gapTo = null;
      // Defensive: the backend returns cache_extent as
      // { oldest_stored_ts, newest_stored_ts } with EITHER field
      // possibly null when no rows are stored. Also tolerate older
      // backend builds that don't return the key at all.
      const extent = r.cache_extent || {};
      const oldestTs = (typeof extent.oldest_stored_ts === 'number')
        ? extent.oldest_stored_ts : null;
      // Monthly billing has exactly 1 row per calendar month. If we
      // got ≥1 row for the picked month that IS complete — any sliver
      // between `fromTs` (month-start Tashkent) and `oldestTs` (stored
      // row UTC) is a tz-representation artifact, NOT a real missing
      // window. Skip gap detection so the backfill banner doesn't
      // fire and toast "No data available" while the main view is
      // already showing the month's row.
      const isMonthlyProfile = profile.obis === '0.0.98.1.0.255';
      const skipGapDetection = isMonthlyProfile && (r.rows || []).length > 0;
      if (skipGapDetection) {
        // fall through — gapFrom / gapTo stay null
      } else if ((r.rows || []).length === 0) {
        gapFrom = fromTs;
        gapTo = toTs;
      } else if (oldestTs != null && fromTs < oldestTs) {
        gapFrom = fromTs;
        gapTo = Math.floor(oldestTs) - 1;
      }
      // Clamp: only pull the portion of the gap that's outside the
      // auto-backfill window (31d for daily, 3d for PQ/LP).
      //
      // Quantize the boundary to the start of the current UTC day
      // so successive loadRows calls within the same day produce
      // the SAME gapTo value. Without this, autoBoundary shifted
      // by whatever seconds elapsed between clicks, making the
      // backend job_id (serial|profile|from_ts|to_ts) different
      // every time → the backfill banner kept seeing a "new" job
      // and re-opened the modal, triggering an infinite loop when
      // the meter had no data to serve.
      if (gapFrom != null && gapTo != null) {
        const autoBoundary = Math.floor(
          (nowSec - AUTO_BACKFILL_DAYS * 86400) / 86400
        ) * 86400;
        if (gapFrom >= autoBoundary) {
          // Whole gap inside the auto-backfill window — don't pull.
          gapFrom = null;
          gapTo = null;
        } else if (gapTo > autoBoundary) {
          // Clip the recent edge to the auto-boundary so we don't
          // re-pull data the auto-backfill already owns.
          gapTo = autoBoundary - 1;
        }
      }
      if (gapFrom != null && gapTo != null && gapTo > gapFrom) {
        setPullGap({ fromTs: gapFrom, toTs: gapTo });
      } else {
        setPullGap(null);
      }
    } catch (err) {
      toast.push(`Readings load failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Auto-load when sub-tab changes OR when the operator picks a
  // different meter from the tree. Without ``serial`` in the dep
  // list a switch from meter A → meter B kept the old rows visible
  // because no fetch refired and no state was reset, so the table
  // showed A's data under B's header until the operator hit Search
  // manually. Reset rows immediately on serial change so there's no
  // visible flash of stale data while the new fetch is in flight,
  // and use the latest-serial guard inside loadRows to discard any
  // late-arriving response from the previous meter.
  // Reports no longer auto-load on mount / sub-change / meter switch —
  // operators asked for Search-only fetches so picking a date range
  // feels intentional, and a DLMS round-trip doesn't fire while
  // they're just browsing the tree. Only clear stale rows so the
  // empty-state hint shows until the operator clicks Search.
  useEffect(() => {
    setRows([]);
    setCaptures([]);
    setMonthlyIncomplete([]);
    setPullGap(null);
  }, [serial, sub]);

  // Row health classifier. Our data is always DB-sourced (via the
  // read-through cache) so there's no "Estimated" case — normal
  // rows are tagged "Stored". Reference product also has Estimated
  // and SmartApp states but we don't emit them today.
  // Columns the METER never records are not per-row anomalies. The
  // merged LP/PQ view renders a fixed 24-column layout (LP_PQ_CAPS),
  // but the CAS Meter Spec V2.3.0 §14 power-quality capture list is
  // only a DEFAULT — meters may be commissioned with a subset. Measured
  // 2026-08-07 across 360 meters: every `123…` unit captures 18 objects
  // and omits Frequency (1.0.14.7.0.255), while every `124…`/`125…`
  // unit captures all 19. That split is exact — 164 vs 196, no
  // exceptions — so it is a model trait, not a collection failure.
  // Flagging those 164 meters ABNORMAL on every row they will ever
  // produce buries real anomalies in permanent amber (all of
  // Piramit_Tower, 157 meters, was solid amber).
  //
  // Detected from the data rather than from a capture list, so it also
  // covers any other object a meter doesn't record: a column null in
  // EVERY returned row is structural. Trade-off — a column that
  // genuinely failed for the whole selected range now reads as Stored
  // instead of Abnormal; per-row gaps, the case the flag exists for,
  // still flag normally.
  const structuralNullCols = React.useMemo(() => {
    const out = new Set();
    if (!rows.length) return out;
    const width = rows.reduce(
      (w, r) => Math.max(w, Array.isArray(r.values) ? r.values.length : 0), 0);
    for (let i = 0; i < width; i++) {
      if (rows.every(r => (Array.isArray(r.values) ? r.values[i] : null) == null)) {
        out.add(i);
      }
    }
    return out;
  }, [rows]);

  const classifyRow = (row) => {
    const values = Array.isArray(row.values) ? row.values : [];
    if (values.length === 0) return 'missing';
    const nonEmpty = values.filter(v => v != null);
    if (nonEmpty.length === 0) return 'missing';
    const hasAnyNull = values.some(
      (v, i) => v == null && !structuralNullCols.has(i));
    if (hasAnyNull) return 'abnormal';
    return 'stored';
  };
  const ROW_BG = {
    stored:    'transparent',
    abnormal:  'rgba(251, 191, 36, 0.08)',
    missing:   'rgba(239, 68, 68, 0.12)',
  };

  const { lang } = useLang();
  // LP/PQ rows are pre-scaled server-side per the primary toggle, so a
  // toggle flip must re-fetch (other profiles scale client-side in
  // renderCell and need no reload). Only fires on the merged tab once
  // rows are already loaded.
  useEffect(() => {
    if (profile.name === 'lp_pq' && rows.length > 0) loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyCTVT]);
  const exportXlsx = async () => {
    if (!serial) return;
    try {
      const params = new URLSearchParams({
        from_ts: String(toUnixStart(fromDate)),
        to_ts: String(toUnixEnd(toDate)),
        lang,
        primary: applyCTVT ? 'true' : 'false',
      });
      let url, fname;
      if (profile.name === 'lp_pq') {
        // Merged report — the dedicated server XLSX endpoint.
        params.append('meter_serial', serial);
        url = `/api/reports/load-profile.xlsx?${params}`;
        fname = `lp_pq_${serial}_${fromDate}_${toDate}.xlsx`;
      } else {
        url = `/api/meters/${encodeURIComponent(serial)}/profile/${encodeURIComponent(profile.name)}/export.xlsx?${params}`;
        fname = `${profile.name}_${serial}_${fromDate}_${toDate}.xlsx`;
      }
      await downloadXlsx(url, fname, { toast });
    } catch (_) { /* toast already shown by helper */ }
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    // Column headers come from our curated PROFILE_COLUMN_LABELS
    // dict first, then the capture's own meter-reported label,
    // then the raw OBIS string as the final fallback. The meter-
    // reported labels often carry internal scaling hints like
    // "V L1 avg (x10 -> V)" which we rewrite to just "(V)" —
    // operators only want the unit, not the firmware's internal
    // multiplier math.
    const cleanLabel = (c) => {
      const raw = PROFILE_COLUMN_LABELS[c.obis] || c.label || c.obis;
      return String(raw)
        .replace(/\(\s*x\d+\s*->\s*([^)]+?)\s*\)/g, '($1)')
        .replace(/\s*×\s*\d+\s*/g, ' ')
        .replace(/\s+\)/g, ')')
        .trim();
    };
    const header = ['#', 'Timestamp', ...(captures.map(cleanLabel))];
    // CSV cells must match what the on-screen table shows: per-column
    // scale divisor (centi-kWh → kWh), then per-OBIS primary multiplier
    // when the toggle is on. Without this, CSV always exported the
    // raw secondary register integers regardless of the operator's
    // toggle setting — primary downloads were silently wrong.
    const formatCell = (v, j) => {
      if (v == null) return '';
      if (typeof v !== 'number') {
        const s = String(v);
        return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s;
      }
      const scale = columnScales[j] || 1;
      const fd = assignment?.fraction_digits;
      // Raw profiles (PQ/LP) skip fd + CT×VT entirely — just apply
      // the OBIS-captured scale (usually 1) so the CSV matches the
      // on-screen value the operator compares to the meter LCD.
      if (isRawProfile) {
        const raw = scale > 0 && scale !== 1 ? v / scale : v;
        return Number.isInteger(raw) ? String(raw) : String(Number(raw.toFixed(6)));
      }
      let out = effectiveValue(v, scale, fd);
      let inflated = false;
      if (applyCTVT) {
        const cap = captures[j];
        const obis = (cap && typeof cap === 'object') ? cap.obis : (typeof cap === 'string' ? cap : null);
        const f = obis ? primaryFactor(obis, ctMult, vtMult) : 1;
        if (f !== 1) { out = out * f; inflated = true; }
      }
      // Plain numeric — no thousands separator (would collide with
      // CSV comma); period decimal point. When the operator pinned a
      // per-meter decimal count via fraction_digits, pad/truncate
      // exactly UNLESS we just multiplied by CT×VT — primary values
      // trim trailing zeros so fd decimals don't become pure padding
      // after the scale-up. For Auto, keep the existing "trim if
      // integer, else 6 decimals significant" behaviour.
      if (fd != null) {
        const padded = out.toFixed(fd);
        return inflated ? String(parseFloat(padded)) : padded;
      }
      return Number.isInteger(out) ? String(out) : String(Number(out.toFixed(6)));
    };
    const csvRows = rows.map((r, i) => {
      const cells = (r.values || []).map((v, j) => formatCell(v, j));
      return [i + 1, r.ts || '', ...cells].join(',');
    });
    const modeTag = applyCTVT ? 'primary' : 'secondary';
    const csv = [header.join(','), ...csvRows].join('\n');
    downloadCsv(csv, `${profile.name}_${serial}_${fromDate}_${toDate}_${modeTag}.csv`);
  };

  const subBtn = (active) => ({
    padding: '6px 12px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: `1px solid ${active ? c.accent : c.border}`,
    fontSize: '12px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    borderRadius: '4px',
  });

  // Shared helper — see resolveProfileColumns / renderProfileCell
  // at the top of the file. Both ReportsTab and SubscriberReadings
  // Pane use the same helpers so CT/VT-aware scaling is consistent
  // across every code path that renders profile rows.
  const { labels: columnLabels, scales: columnScales } = resolveProfileColumns(
    captures.length > 0 ? captures : (rows[0]?.values || []).map((_, i) => ({ obis: `col_${i}`, label: `Col ${i}`, scale: 1 }))
  );
  // Apply per-OBIS primary scaling client-side so the toggle is
  // instant — no refetch needed. captures[j].obis tells us what
  // each column represents; primaryFactor() picks ×CT, ×VT, ×CT*VT,
  // or ×1 (frequency / PF / status / time stay raw).
  const ctMult = applyCTVT ? parseRatio(assignment?.ct_ratio) : 1;
  const vtMult = applyCTVT ? parseRatio(assignment?.vt_ratio) : 1;
  const fd = assignment?.fraction_digits;
  // Raw profiles (PQ / LP) skip all operator-preference math — no
  // fd divisor, no CT×VT multiplier. Operators compare these to the
  // meter LCD or an analyser; any scaling would corrupt them.
  const isRawProfile = !!(profile.obis && RAW_PROFILE_OBIS_SET.has(profile.obis));
  const renderCell = (v, j) => {
    const scale = columnScales[j] || 1;
    const cap = captures[j];
    const obis = (cap && typeof cap === 'object') ? cap.obis : (typeof cap === 'string' ? cap : null);
    // Status / event-type columns short-circuit past CT/VT scaling
    // and decimal formatting — they're integer bitmasks, not energy
    // values, and the operator wants "Main relay error, …" not
    // "3,072.000".
    if (obis && STATUS_OBIS_SET.has(obis)) {
      return v == null ? '—' : <StatusCell value={v} />;
    }
    if (isRawProfile) {
      return renderProfileCell(v, scale, 1, null, obis, profile.obis);
    }
    if (applyCTVT && typeof v === 'number') {
      const f = obis ? primaryFactor(obis, ctMult, vtMult) : 1;
      if (f !== 1) {
        // effectiveValue picks the divisor: OBIS scale when fd is
        // null, 10^fd when the operator pinned a decimal count.
        // Trim trailing zeros — primary-side values inflate the
        // number by CT×VT and turn the fd decimals into padding.
        const out = effectiveValue(v, scale, fd) * f;
        return formatMeterNumber(out, fd, { trimZeros: true });
      }
    }
    return renderProfileCell(v, scale, 1, fd, obis, profile.obis);
  };

  const LegendPill = ({ color, label }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '10px', fontSize: '10px', color: c.textSecondary }}>
      <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '2px', background: color }} />
      {label}
    </span>
  );

  // Always-on Primary / Secondary toggle. Visible regardless of
  // sub-tab and regardless of whether the meter has a CT/VT
  // configured — operators asked for one consistent control next
  // to the Connection metadata. With a 1:1 ratio the toggle is a
  // no-op (backend multiplier = 1) but it still flips the mode
  // marker that any future endpoint can honor.
  const ctMul = computeCTVTMultiplier(assignment);
  const PrimarySecondaryHeader = (
    assignment ? (
      <div style={{
        marginBottom: '12px',
        padding: '10px 14px',
        background: c.bgCardAlt,
        border: `1px solid ${c.border}`,
        borderRadius: '6px',
        fontSize: '12px',
        color: c.textSecondary,
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <span><strong style={{ color: c.textLabel }}>CT:</strong> {assignment.ct_ratio || '—'}</span>
        <span><strong style={{ color: c.textLabel }}>VT:</strong> {assignment.vt_ratio || '—'}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span><strong style={{ color: c.textLabel }}>Connection:</strong> {assignment.connection_type || '—'}</span>
          <label
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              cursor: 'pointer', userSelect: 'none',
            }}
            title={ctMul > 1 ? `CT×VT = ${ctMul}` : 'No CT/VT configured (multiplier = 1)'}
          >
          <span style={{ fontSize: '12px', color: !applyCTVT ? c.textPrimary : c.textMuted, fontWeight: !applyCTVT ? 600 : 400 }}>
            Secondary
          </span>
          <div
            onClick={() => setApplyCTVT(v => !v)}
            style={{
              width: '40px', height: '22px',
              borderRadius: '11px',
              background: applyCTVT ? c.accent : c.border,
              position: 'relative',
              transition: 'background 0.2s',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <div style={{
              width: '18px', height: '18px',
              borderRadius: '50%',
              background: '#ffffff',
              position: 'absolute',
              top: '2px',
              left: applyCTVT ? '20px' : '2px',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }} />
          </div>
          <span style={{ fontSize: '12px', color: applyCTVT ? c.textPrimary : c.textMuted, fontWeight: applyCTVT ? 600 : 400 }}>
            Primary {ctMul > 1 ? `(×${ctMul})` : ''}
          </span>
        </label>
        </span>
      </div>
    ) : null
  );

  return (
    <div>
      {/* ── Profile picker ── */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {Object.entries(profileMap).filter(([, p]) => !p.hidden).map(([k, p]) => (
          <button key={k} style={subBtn(sub === k)} onClick={() => setSub(k)}>{p.label}</button>
        ))}
      </div>

      {/* Primary / Secondary toggle is visible across every sub-tab
          so operators always know which view they're in. The backend
          honors it on stored profile reads (Daily / Monthly / Load /
          PQ) and report exports; OnDemand reads use scaled register
          values directly from the meter. */}
      {sub !== 'live' && PrimarySecondaryHeader}

      {/* ── On Demand / Instantaneous: use DCU profiles for DCU-routed meters ── */}
      {(sub === 'ondemand' || sub === 'instant') && assignment?.dcu_serial && assignment.dcu_serial !== serial && (
        <DcuMeterOnDemandPanel dcuSerial={assignment.dcu_serial} meterSerial={serial} />
      )}
      {sub === 'ondemand' && !(assignment?.dcu_serial && assignment.dcu_serial !== serial) && (
        <MeterDetailOnDemandTab serial={serial} sessionOpen={sessionOpen} />
      )}

      {sub === 'period' && (
        <PeriodConsumptionPanel serial={serial} assignment={assignment} applyCTVT={applyCTVT} />
      )}

      {sub === 'instant' && !(assignment?.dcu_serial && assignment.dcu_serial !== serial) && (
        <InstantaneousReadingsPanel serial={serial} />
      )}

      {sub === 'live' && (
        <LiveReadingsPanel serial={serial} onClose={() => setSub('daily')} />
      )}

      {sub !== 'ondemand' && sub !== 'period' && sub !== 'instant' && sub !== 'live' && (
        <>

          {/* ── Row health legend ── */}
          <div style={{ marginBottom: '8px' }}>
            <LegendPill color="#22c55e" label="Stored" />
            <LegendPill color="#fbbf24" label="Abnormal (has nulls)" />
            <LegendPill color="#ef4444" label="Missing" />
          </div>

          {/* ── Date range + action bar. Max span 366 days (12
              months) for daily/monthly — meters retain 12 months of
              data per the CAS spec; daily holds 365 (366 leap year)
              date entries and monthly holds 12 month-end snapshots.
              Exceeding = red warning + disabled Search. Load Profile
              and Power Quality have shorter on-meter buffers but
              the same upper bound applies; the meter just returns
              fewer rows than requested. */}
          {(() => {
            const spanDays = Math.max(0, Math.round(
              (new Date(toDate + 'T23:59:59').getTime() - new Date(fromDate + 'T00:00:00').getTime()) / 86400000
            ));
            // 15-min-cadence profiles (PQ + LP) are capped at 3 days
            // — at 96 rows/day × 3 = 288 rows per profile it's already
            // close to what the meter retains on-board. Wider ranges
            // across hundreds of meters balloon the DB and the pull
            // duration for little benefit.
            const isRawProfile15Min = (profile.obis === '1.0.99.1.0.255' || profile.obis === '1.0.99.1.2.255');
            const maxSpan = isRawProfile15Min ? 3 : 366;
            const tooWide = spanDays > maxSpan;
            // Monthly billing uses a Year+Month dropdown instead of the
            // From/To date pair: monthly data is inherently month-
            // bucketed, and the dropdown sidesteps the tz/boundary
            // pitfalls of a raw date input for Tashkent operators.
            // We still drive the downstream `fromDate` / `toDate`
            // state so loadRows + exportXlsx don't need their own
            // branch — we just set fromDate to "YYYY-MM-01" and
            // toDate to "YYYY-MM-<last-day>".
            const isMonthly = sub === 'monthly';
            const yearFromFrom = parseInt((fromDate || '').split('-')[0], 10) || new Date().getFullYear();
            const monthFromFrom = parseInt((fromDate || '').split('-')[1], 10) || (new Date().getMonth() + 1);
            const pickMonth = (y, m) => {
              const mm = String(m).padStart(2, '0');
              const lastDay = new Date(y, m, 0).getDate(); // m=1..12 → next month's day-0 = last day of month
              setFromDate(`${y}-${mm}-01`);
              setToDate(`${y}-${mm}-${String(lastDay).padStart(2, '0')}`);
            };
            return (
              <>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: tooWide ? '4px' : '12px' }}>
                  {isMonthly ? (
                    <div>
                      <div style={{ fontSize: '10px', color: c.textMuted }}>Month</div>
                      <select className="input-field"
                              value={`${yearFromFrom}-${String(monthFromFrom).padStart(2,'0')}`}
                              onChange={(e) => {
                                const [y, m] = e.target.value.split('-').map(Number);
                                pickMonth(y, m);
                              }}
                              style={{ padding: '6px 8px', fontSize: '12px', width: '180px' }}>
                        {(() => {
                          // Last 12 months — the on-board meter buffer depth.
                          const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                          const now = new Date();
                          const opts = [];
                          for (let i = 0; i < 12; i++) {
                            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                            const y = d.getFullYear();
                            const m = d.getMonth() + 1;
                            const key = `${y}-${String(m).padStart(2,'0')}`;
                            opts.push(<option key={key} value={key}>{MONTH_NAMES[m-1]} {y}</option>);
                          }
                          return opts;
                        })()}
                      </select>
                    </div>
                  ) : (
                    <>
                      <div>
                        <div style={{ fontSize: '10px', color: c.textMuted }}>From</div>
                        <input type="date" className="input-field" value={fromDate}
                               onChange={(e) => setFromDate(e.target.value)}
                               style={{ padding: '6px 8px', fontSize: '12px' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '10px', color: c.textMuted }}>To</div>
                        <input type="date" className="input-field" value={toDate}
                               onChange={(e) => setToDate(e.target.value)}
                               style={{ padding: '6px 8px', fontSize: '12px' }} />
                      </div>
                    </>
                  )}
                  <button className="button button-primary" onClick={loadRows} disabled={busy || tooWide}>
                    {busy ? <Spinner /> : '🔍'} Search
                  </button>
                  <button
                    className="button button-secondary"
                    onClick={exportXlsx}
                    disabled={!serial || sub === 'monthly' || tooWide}
                    title={sub === 'monthly' ? 'XLSX export uses the daily-billing rows directly. Switch to the Daily sub-tab for the full XLSX.' : 'Download XLSX from server-side cache (no Search needed)'}
                  >⬇ XLSX</button>
                </div>
                {tooWide && (
                  <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '12px' }}>
                    Range {spanDays} days exceeds the maximum ({maxSpan} days{isRawProfile15Min ? ' — Power Quality / Load Profile are capped at 3 days since they produce ~96 rows/day' : ' / 12 months — meter on-board retention'}). Narrow the range and try again.
                  </div>
                )}
              </>
            );
          })()}

          {/* Async pull-from-meter banner. `gap` is set by loadRows
              when the cache doesn't cover the requested range AND
              the missing window extends beyond the 31-day auto-
              backfill boundary. The banner auto-kicks the pull on
              mount / gap-change — Search is also the Pull click. */}
          <ProfileBackfillBanner
            serial={serial}
            profileName={profile.name}
            gap={pullGap}
            busy={busy}
            onBackfillDone={loadRows}
          />

          {!busy && rows.length === 0 && !pullGap && (
            <div style={{ color: c.textDimmed, fontSize: '12px', padding: '12px', fontStyle: 'italic' }}>
              No rows in this range. Widen the date picker or pick a different profile.
            </div>
          )}

          {rows.length > 0 && (
            <div className="table-container" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
              <table style={{ fontSize: '11px' }}>
                <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
                  <tr>
                    <th>#</th>
                    <th>Status</th>
                    <th>{t('th.timestamp')}</th>
                    {columnLabels.map((lbl, i) => <th key={i}>{lbl}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const klass = classifyRow(r);
                    return (
                      <tr key={i} style={{ background: ROW_BG[klass] }}>
                        <td style={{ color: c.textMuted }}>{i + 1}</td>
                        <td style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: klass === 'missing' ? '#ef4444' : klass === 'abnormal' ? '#fbbf24' : '#22c55e' }}>
                          {klass}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDT(r.ts) || (r.row_ts ? fmtDT(r.row_ts, { seconds: true }) : '—')}</td>
                        {columnLabels.map((_, j) => {
                          const v = (r.values || [])[j];
                          return (
                            <td key={j} style={{ textAlign: typeof v === 'number' ? 'right' : 'left' }}>
                              {renderCell(v, j)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '6px 8px', fontSize: '11px', color: c.textMuted }}>
                {rows.length} row(s) shown · profile <code>{profile.obis}</code>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Events sub-tab of MeterDetailView. Loads all 11 event logs in
// parallel via the profile endpoint (with refresh=force so the
// read-through cache always goes back to the meter), decodes rows
// against the /api/event-codes catalogue, and renders a filterable
// table per spec §17 (Meter detail → Events tab).
function MeterDetailEventsTab({ serial, sessionOpen }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [catalog, setCatalog] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  // Default date range: last 7 days
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86400 * 1000);
  const toDate = (d) => d.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(toDate(weekAgo));
  const [toDateStr, setToDateStr] = useState(toDate(today));
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 150);

  // Fetch the event-code catalogue once per session.
  useEffect(() => {
    api('/event-codes').then(setCatalog).catch(() => {});
  }, []);

  const loadEvents = async () => {
    if (!serial) return;
    setLoading(true);
    setEvents([]);
    try {
      const fromTs = dayStart(fromDate);
      const toTs = dayEnd(toDateStr);
      // 2026-05-12: events are populated by the nightly scheduler +
      // the every-4h DCU aggregate sweep. The Events tab reads from
      // the local cache only (refresh=never) — never triggers a live
      // DLMS pull, which kept hammering meters every time an operator
      // opened the tab. If a category filter is active, fan-out only
      // that one OBIS instead of all 11.
      const allObis = Object.keys(catalog?.categories || {});
      const obisCodes = (categoryFilter === 'all' || !categoryFilter)
        ? allObis
        : allObis.filter(ob =>
            (catalog?.categories?.[ob] || '') === categoryFilter
          );
      const all = [];
      const results = await Promise.allSettled(
        obisCodes.map(async (obis) => {
          const r = await api(
            `/meters/${serial}/profile/${obis}?from_ts=${fromTs}&to_ts=${toTs}&limit=5000&refresh=never`
          );
          return { obis, rows: r?.rows || [] };
        })
      );
      let nextNo = 1;
      for (const res of results) {
        if (res.status !== 'fulfilled') continue;
        const { obis, rows } = res.value;
        const categoryName = catalog?.categories?.[obis] || obis;
        for (const row of rows) {
          // Profile rows are { row_ts, fetched_at, ts, values }.
          // For an event log row, the CAS spec §17 capture objects
          // are {clock, code} — so values[0] is the clock timestamp
          // and values[1] is the actual event code integer. Some
          // meters may omit the clock (single-column buffer), so
          // fall back to values[0] if values[1] doesn't exist.
          const vals = Array.isArray(row.values) ? row.values : [];
          const code = vals.length > 1 ? vals[1] : vals[0];
          const name = catalog?.codes?.[String(code)] || `Unknown (code=${code})`;
          all.push({
            no: nextNo++,
            meter: serial,
            occurred: row.row_ts,
            received: row.fetched_at,
            category: categoryName,
            obis,
            code,
            name,
          });
        }
      }
      // Sort by occurred time descending.
      all.sort((a, b) => (b.occurred || 0) - (a.occurred || 0));
      setEvents(all);
      toast.push(t('events.loaded_events', { n: all.length }), 'success');
    } catch (err) {
      toast.push(`${t('events.load_failed')}: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 2026-05-12: derive category list from the catalog (all 11 CAS
  // event-log categories) so the operator can pick a category BEFORE
  // running the search — used to filter the fan-out to one OBIS
  // instead of always pulling all 11. Falls back to scanning loaded
  // events when the catalog isn't ready yet (rare).
  const categories = React.useMemo(() => {
    if (catalog?.categories) {
      const fromCatalog = Array.from(
        new Set(Object.values(catalog.categories))
      ).sort();
      return ['all', ...fromCatalog];
    }
    const s = new Set();
    events.forEach(e => s.add(e.category));
    return ['all', ...Array.from(s).sort()];
  }, [catalog, events]);

  const filtered = React.useMemo(() => {
    return events.filter(e => {
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        if (!e.name.toLowerCase().includes(q) && !String(e.code).includes(q)) return false;
      }
      return true;
    });
  }, [events, categoryFilter, debouncedSearch]);

  const exportCsv = () => {
    const header = ['No.', 'Meter', 'Occurred Time', 'Received Time', 'Event Name', 'Event Category', 'Code'];
    const rows = filtered.map(e => [
      e.no,
      e.meter,
      e.occurred ? new Date(e.occurred * 1000).toISOString() : '',
      e.received ? new Date(e.received * 1000).toISOString() : '',
      e.name,
      e.category,
      e.code,
    ]);
    const csv = [header, ...rows].map(r =>
      r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    downloadCsv(csv, `${serial}_events_${fromDate}_to_${toDateStr}.csv`);
    toast.push(t('events.exported_rows', { n: filtered.length }), 'success');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted, marginBottom: '2px' }}>{t('common.from')}</div>
          <input type="date" className="input-field" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 style={{ padding: '6px 8px', fontSize: '12px' }} />
        </div>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted, marginBottom: '2px' }}>{t('common.to')}</div>
          <input type="date" className="input-field" value={toDateStr}
                 onChange={(e) => setToDateStr(e.target.value)}
                 style={{ padding: '6px 8px', fontSize: '12px' }} />
        </div>
        <div>
          <div style={{ fontSize: '10px', color: c.textMuted, marginBottom: '2px' }}>{t('events.category')}</div>
          <select className="input-field" value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  style={{ padding: '6px 8px', fontSize: '12px' }}>
            {categories.map(cat => <option key={cat} value={cat}>{cat === 'all' ? t('events.all_categories') : cat}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '10px', color: c.textMuted, marginBottom: '2px' }}>{t('events.event_search')}</div>
          <input type="text" className="input-field" value={searchText}
                 onChange={(e) => setSearchText(e.target.value)}
                 placeholder={t('events.search_hint')}
                 style={{ padding: '6px 8px', fontSize: '12px' }} />
        </div>
        <button className="button button-primary" onClick={loadEvents}
                disabled={loading || !catalog}>
          {loading ? <Spinner /> : '🔍'} {t('btn.search')}
        </button>
        <button className="button button-secondary" onClick={exportCsv} disabled={filtered.length === 0}>
          ⬇ {t('btn.export_csv')}
        </button>
      </div>

      {loading && <div style={{ color: c.textMuted, fontSize: '12px' }}><Spinner /> {t('events.reading_logs')}</div>}

      {!loading && events.length > 0 && (
        <div className="table-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <table>
            <thead style={{ position: 'sticky', top: 0, background: c.bgCard }}>
              <tr>
                <th>{t('th.no')}</th>
                <th>{t('events.occurred')}</th>
                <th>{t('events.received')}</th>
                <th>{t('events.category')}</th>
                <th>{t('th.event_name')}</th>
                <th>{t('th.event_code')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={`${e.obis}-${e.no}`}>
                  <td style={{ fontSize: '11px', color: c.textMuted }}>{e.no}</td>
                  <td style={{ fontSize: '11px' }}>{e.occurred ? fmtDT(e.occurred, { seconds: true }) : '—'}</td>
                  <td style={{ fontSize: '11px', color: c.textMuted }}>{e.received ? fmtDT(e.received, { seconds: true }) : '—'}</td>
                  <td style={{ fontSize: '11px' }}>{e.category}</td>
                  <td style={{ fontSize: '12px' }}>{e.name}</td>
                  <td style={{ fontSize: '10px', fontFamily: 'monospace', color: c.textMuted }}>0x{Number(e.code || 0).toString(16).padStart(4, '0')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '6px 8px', fontSize: '11px', color: c.textMuted }}>
            {filtered.length} {t('events.of_total')} {events.length} {t('events.events_label')}
            {categoryFilter !== 'all' && ` · ${t('events.filtered_by')} ${categoryFilter}`}
            {searchText && ` · ${t('events.search_label')} "${searchText}"`}
          </div>
        </div>
      )}

      {!loading && events.length === 0 && (
        <div style={{ color: c.textDimmed, fontSize: '12px', padding: '12px', fontStyle: 'italic' }}>
          {t('events.no_events')}
        </div>
      )}
    </div>
  );
}

// Home sub-tab of MeterDetailView — replicates the CAS/Kaifa
// reference layout (screenshot #2):
//
//   [ Meter No. · Logical No · Meter Type · Meter Status ]  summary bar
//   [    Status    |    Meter Collection    |   Relay Tasks    ]  3-card row
//   [ General | Archive | Installations | Seals | Vector | Work Reply ]  sub-sub-tabs
//   [ General pane:  [Task / Billing / Comm / Check] | 2-col meter field grid ]
//
// The Status card and Relay Control Tasks card auto-refresh when
// the operator first opens the tab (if the meter has a secure
// session). The Meter Collection card reuses the
// /api/meters/{serial}/collection-stats endpoint from realign B.
// DLMS FLAG → human manufacturer name. The Blue Book defines the
// 3-character FLAG prefix every meter's Logical Device Name (OBIS
// 0.0.42.0.0.255) starts with; we decode a few common ones so the
// Home tab can show "Holley" instead of "HLY". Unknown FLAGs fall
// through to the raw 3-char code.
const _FLAG_CODES = {
  HLY: 'Holley',
  LGZ: 'Landis+Gyr',
  ISK: 'Iskra',
  EMH: 'EMH',
  SAG: 'Sagemcom',
  EBZ: 'eBZ',
  KFM: 'Kaifa',
  ITZ: 'Itron',
};
// Holley CAS meters report a numeric manufacturer code in
// 0.0.96.1.1.255 (Device ID 2) instead of a 3-char FLAG. The test
// meter returns "120020" which is Holley's internal vendor ID.
// Extend this dict as new fleets land.
const _NUMERIC_MFG_CODES = {
  '120020': 'Holley',
};

function deriveModel(fwString) {
  // Holley firmware version strings look like "HMS3XX25122G6G000000000200911110"
  // where the first ~6 chars encode the hardware platform /
  // model family (HMS = Holley Metering System; 3XX = 3-phase).
  // If the FW starts with 4+ alphanumeric chars, take that slice
  // as the model; otherwise return null and let the caller render
  // "—". Keep it narrow so a random numeric firmware doesn't get
  // mis-rendered as a model name.
  if (!fwString || typeof fwString !== 'string') return null;
  const m = fwString.match(/^([A-Z]{2,}[A-Z0-9]{1,6})/);
  return m ? m[1] : null;
}

function deriveManufacturer(ldn, numericMfgCode) {
  // Prefer the FLAG prefix from the Logical Device Name — that's the
  // DLMS-standard way to identify the vendor and matches what the
  // meter label on the physical device usually shows. Fall back to
  // the numeric manufacturer code if we've seen it before.
  if (ldn && typeof ldn === 'string' && ldn.length >= 3) {
    const flag = ldn.slice(0, 3).toUpperCase();
    if (_FLAG_CODES[flag]) return _FLAG_CODES[flag];
    return flag;
  }
  if (numericMfgCode != null) {
    const key = String(numericMfgCode);
    if (_NUMERIC_MFG_CODES[key]) return _NUMERIC_MFG_CODES[key];
    return `Code ${key}`;
  }
  return null;
}

function MeterDetailHomeTab({ assignment, live, customer, transformer, meterType }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const isDcuRouted = !!(assignment?.dcu_serial && assignment.dcu_serial !== assignment.meter_serial);
  const [relay, setRelay] = useState(null);
  const [relayBusy, setRelayBusy] = useState(false);
  const [extra, setExtra] = useState(null);
  const [extraBusy, setExtraBusy] = useState(false);
  const [collection, setCollection] = useState(null);
  const [subsub, setSubsub] = useState('general');
  const [generalNav, setGeneralNav] = useState('archive');

  const serial = assignment.meter_serial;

  // Pull per-meter collection stats for the Meter Collection card.
  useEffect(() => {
    api(`/meters/${serial}/collection-stats?days=30`).then(setCollection).catch(() => {});
  }, [serial]);

  const readRelay = async () => {
    setRelayBusy(true);
    try {
      const r = await api(`/meters/${serial}/read-group/relay_status`);
      const v = r?.readings?.['0.0.96.3.10.255'];
      const status = v == null ? 'unknown'
        : v === 0 ? 'disconnected'
        : v === 1 ? 'connected'
        : v === 2 ? 'ready for reconnection'
        : `raw=${v}`;
      setRelay({ status, raw: v, at: new Date().toLocaleTimeString() });
      toast.push(`Relay: ${status}`, 'success');
    } catch (err) {
      toast.push(`Relay read failed: ${err.message}`, 'error');
    } finally {
      setRelayBusy(false);
    }
  };

  const readExtra = async () => {
    setExtraBusy(true);
    try {
      if (isDcuRouted) {
        // DCU-routed meter: read from DCU profile instead of direct session
        const profile = await api(`/dcus/${assignment.dcu_serial}/profiles/daily`);
        const info = { at: new Date().toLocaleTimeString(), source: 'DCU profile' };
        if (profile?.has_data && profile?.value) {
          info.serial_from_meter = serial;
          info.ldn = assignment.dcu_serial ? `via DCU ${assignment.dcu_serial}` : '';
          info.comm_module_serial = `DCU ${assignment.dcu_serial}`;
          // Extract energy value as confirmation meter is communicating
          if (profile.value.includes(serial)) {
            info.fw = 'Data available via DCU';
          }
        }
        setExtra(info);
      } else {
        const r = await api(`/meters/${serial}/read-group/device_info`);
        setExtra({
          serial_from_meter: r?.readings?.['0.0.96.1.0.255'],
          production_date:   r?.readings?.['0.0.96.1.4.255'],
          mfg:               r?.readings?.['0.0.96.1.1.255'],
          fw:                r?.readings?.['1.0.0.2.0.255'],
          ldn:               r?.readings?.['0.0.42.0.0.255'],
          comm_module_serial: r?.readings?.['0.0.96.128.0.255'],
          sim_iccid:          r?.readings?.['0.0.96.1.9.255'],
          at: new Date().toLocaleTimeString(),
        });
      }
      toast.push(t('toast.device_info_read'), 'success');
    } catch (err) {
      toast.push(`Device info read failed: ${err.message}`, 'error');
    } finally {
      setExtraBusy(false);
    }
  };

  // Strip trailing BCD padding from a SIM ICCID (see backend
  // _clean_iccid for the canonical rationale — real ICCIDs are
  // 19 digits per ITU-T E.118, Holley pads with a final hex F).
  const cleanIccid = (raw) => {
    if (raw == null) return null;
    let s = String(raw).trim();
    if (!s) return null;
    if (s.endsWith('F') || s.endsWith('f')) s = s.slice(0, -1);
    return s;
  };

  // True if the Communication Module Serial Number at OBIS
  // 0.0.96.128.0.255 looks like a real programmed value, not the
  // factory all-zeros placeholder. Matches backend
  // _comm_addr_is_real.
  const commModuleIsReal = (v) => {
    if (v == null) return false;
    const s = String(v).trim();
    if (!s) return false;
    // Non-zero means it was programmed.
    return !/^0+$/.test(s);
  };

  // ── Derived values for the three cards ──
  const online = !!live?.session_open;
  const relayLabel = relay?.status ? relay.status : 'unknown';
  const relayColor = relay?.raw === 1 ? c.success : relay?.raw === 0 ? c.danger : c.warning;
  const powerLabel = online ? t('meter.power_on') : t('meter.power_off');
  const powerColor = online ? c.success : c.danger;

  const dbDays = collection?.days || [];
  const dbStored = dbDays.reduce((s, d) => s + d.daily_billing.stored, 0);
  const dbExp = dbDays.reduce((s, d) => s + d.daily_billing.expected, 0);
  const lpStored = dbDays.reduce((s, d) => s + d.load_profile.stored, 0);
  const lpExp = dbDays.reduce((s, d) => s + d.load_profile.expected, 0);
  // Clamp to [0, 100]. Stored can exceed expected when a backfill
  // pulls more rows than the calendar window asked for, when DCU
  // captures land at intraday times in addition to the midnight
  // row, or when the same day is represented by more than one
  // capture (event-driven snapshot + scheduled). 100 % means
  // "complete coverage" and that's what operators read it as.
  const dailyPct = dbExp ? Math.min(100, Math.round(100 * dbStored / dbExp)) : 0;
  const monthlyPct = lpExp ? Math.min(100, Math.round(100 * lpStored / lpExp)) : 0;

  // ── Reusable Card shell ──
  const Card = ({ title, icon, children, right }) => (
    <div style={{
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
      padding: '16px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: c.textLabel }}>{icon} {title}</div>
        {right}
      </div>
      {children}
    </div>
  );

  // ── Summary bar rendered at the very top ──
  const SummaryBar = () => (
    <div style={{
      background: c.bgCardAlt,
      border: `1px solid ${c.border}`,
      borderRadius: '6px',
      padding: '12px 16px',
      marginBottom: '14px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '12px',
      fontSize: '12px',
    }}>
      <div>
        <div style={{ color: c.textMuted, textTransform: 'uppercase', fontSize: '10px' }}>{t('meter.meter_no')}</div>
        <div style={{ color: c.textPrimary, fontFamily: 'monospace' }}>{serial}</div>
      </div>
      <div>
        <div style={{ color: c.textMuted, textTransform: 'uppercase', fontSize: '10px' }}>{t('meter.logical_no')}</div>
        <div style={{ color: c.textPrimary, fontFamily: 'monospace' }}>{live?.logical_device_name || '—'}</div>
      </div>
      <div>
        <div style={{ color: c.textMuted, textTransform: 'uppercase', fontSize: '10px' }}>{t('meter.meter_type')}</div>
        <div style={{ color: c.textPrimary }}>{formatMeterType(meterType)}</div>
      </div>
      <div>
        <div style={{ color: c.textMuted, textTransform: 'uppercase', fontSize: '10px' }}>{t('meter.meter_status')}</div>
        <div style={{
          color: assignment.commissioned_at
            ? (live?.session_open ? c.success : c.textSecondary)
            : c.warning,
        }}>
          {assignment.commissioned_at
            ? (live?.session_open ? t('meter.commissioned_live') : t('meter.commissioned_off'))
            : t('meter.not_commissioned')}
        </div>
      </div>
      {/* Last seen online — newest stored row across any profile.
          For direct meters this is the most recent poll; for DCU-
          routed meters it's the most recent aggregate-sourced row.
          When the meter is currently online we show "● Live now"
          instead, since "0s ago" is misleading. */}
      <div>
        <div style={{ color: c.textMuted, textTransform: 'uppercase', fontSize: '10px' }}>{t('meter.last_seen') || 'Last seen'}</div>
        <div style={{ color: c.textPrimary }}>
          {live?.session_open ? (
            <span style={{ color: c.success }}>● Live now</span>
          ) : collection?.last_seen_ts ? (
            <span title={fmtDT(collection.last_seen_ts, { seconds: true })}>
              {(() => {
                const sec = collection.last_seen_age_seconds;
                if (sec == null) return '—';
                if (sec < 60) return `${Math.round(sec)}s ago`;
                if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
                if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
                return `${Math.round(sec / 86400)}d ago`;
              })()}
            </span>
          ) : (
            <span style={{ color: c.textMuted }}>never</span>
          )}
        </div>
      </div>
    </div>
  );

  // ── Status dot row used inside the Status card ──
  const StatusRow = ({ dotColor, label, sub }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0' }}>
      <span style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: dotColor,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: c.textPrimary, fontSize: '12px', fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ color: c.textMuted, fontSize: '10px' }}>{sub}</div>}
      </div>
    </div>
  );

  // ── Archive sub-sub-tab content: two-column meter-field grid ──
  //
  // Comm Addr resolution chain — per CAS Meter Spec v2.3.0 §7.3:
  //
  //   1. Operator-entered  assignment.comm_addr  (manual override)
  //   2. Communication Module Serial Number  (OBIS 0.0.96.128.0.255)
  //      This is the spec-canonical "Comm Addr". Only used when the
  //      field was actually programmed — the test meter returns 24
  //      ASCII zeros, which means the factory never populated it,
  //      so we fall through.
  //   3. SIM ICCID / Device ID 10  (OBIS 0.0.96.1.9.255)
  //      The GPRS modem's SIM identifier — useful when #2 is empty.
  //   4. Logical Device Name       (OBIS 0.0.42.0.0.255)
  //      Always populated; starts with 3-char FLAG prefix.
  //
  // Model comes from the firmware string prefix (HMS3XX...).
  // Manufacturer comes from the LDN FLAG prefix or the numeric
  // manufacturer code on Device ID 2. Production Date comes from
  // 0.0.96.1.4.255 (usually null on Holley until the operator
  // clicks "Read device info").
  const ArchiveGrid = () => {
    const fw = live?.firmware_version || live?.firmware || extra?.fw || '';
    const ldn = extra?.ldn || live?.logical_device_name || '';
    const derivedModel = deriveModel(fw);
    const derivedMfg = deriveManufacturer(
      ldn,
      extra?.mfg ?? live?.manufacturer,
    );
    // Comm Addr resolution — see comment above.
    let commAddr = assignment.comm_addr || '';
    let commAddrSource = commAddr ? 'manual' : null;
    if (!commAddr && commModuleIsReal(extra?.comm_module_serial)) {
      commAddr = String(extra.comm_module_serial);
      commAddrSource = 'spec §7.3';
    }
    if (!commAddr) {
      const iccid = cleanIccid(extra?.sim_iccid);
      if (iccid) {
        commAddr = iccid;
        commAddrSource = 'SIM ICCID';
      }
    }
    if (!commAddr && ldn) {
      commAddr = ldn;
      commAddrSource = 'LDN fallback';
    }
    if (!commAddr) {
      commAddr = '— click "Read device info"';
    }
    const commAddrDisplay = commAddrSource
      ? { text: `${commAddr}  ·  ${commAddrSource}`, color: c.textPrimary }
      : commAddr;

    // SIM / Modem ID — always shown as its own row for
    // transparency, even when it's also being used as the Comm Addr
    // fallback above. Gives ops a quick way to match a SIM to a
    // meter when troubleshooting carrier-side connectivity.
    const simIccid = cleanIccid(extra?.sim_iccid);
    const simDisplay = simIccid
      ? simIccid
      : (extra ? '— not programmed' : '— click "Read device info"');
    // Meter Status is the commissioning latch — see
    // MeterAssignment.commissioned_at in metadata.py. Live session
    // gets a separate badge so operators can tell at a glance
    // whether this commissioned meter is currently reachable.
    let statusText;
    let statusColor;
    if (isDcuRouted) {
      statusText = `Connected via DCU ${assignment.dcu_serial}`;
      statusColor = '#22c55e';
    } else if (assignment.commissioned_at) {
      const when = fmtD(assignment.commissioned_at, { utc: true });
      statusText = live?.session_open
        ? `Commissioned · Live (since ${when})`
        : `Commissioned · Off (since ${when})`;
      statusColor = live?.session_open ? c.success : c.textSecondary;
    } else if (live?.identified) {
      statusText = 'Identified · not yet commissioned';
      statusColor = c.warning;
    } else {
      statusText = 'Installed · pending first session';
      statusColor = c.textMuted;
    }

    const pairs = [
      ['Meter No.',          serial,                                                      'Meter Status',      { text: statusText, color: statusColor }],
      ['Comm Addr',          commAddrDisplay,                                             'Meter Type',        formatMeterType(meterType)],
      ['SIM / Modem ID',     simDisplay,                                                  'Communication Type', live?.connection_kind === 'lls_modem'
                                                                                            ? `GPRS · LLS modem (ID ${live.modem_login_id || '?'}, serial from ${live.serial_source || '?'})`
                                                                                            : 'GPRS'],
      ['Logical Device Name', ldn || '—',                                                 'Manufacturer',      derivedMfg || '—'],
      ['Model Name',         derivedModel || '—',                                         'Firmware Version',  fw || '—'],
      ['Relay Status',       relay ? relay.status : '— click refresh',                    'Manufacture Date',  extra?.production_date
                                                                                            ? String(extra.production_date)
                                                                                            : '— click "Read device info"'],
    ];
    // Cells may be plain strings or {text, color} objects (used for
    // status-coloured rows like Meter Status and source-tagged Comm
    // Addr). The renderer below normalises both shapes.
    const cellText = (v) => {
      if (v == null || v === '') return '—';
      if (typeof v === 'object' && 'text' in v) return v.text;
      return String(v);
    };
    const cellColor = (v, def) => {
      if (v && typeof v === 'object' && 'color' in v) return v.color;
      return def;
    };
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px', fontSize: '12px' }}>
        {pairs.map((p, i) => (
          <React.Fragment key={i}>
            <div style={{ padding: '8px 10px', background: c.bgCardAlt, borderLeft: `3px solid ${c.border}` }}>
              <div style={{ color: c.textMuted, fontSize: '10px' }}>{p[0]}</div>
              <div style={{ color: cellColor(p[1], c.textPrimary), wordBreak: 'break-word' }}>
                {cellText(p[1])}
              </div>
            </div>
            <div style={{ padding: '8px 10px', background: c.bgCardAlt, borderLeft: `3px solid ${c.border}` }}>
              {p[2] ? (
                <>
                  <div style={{ color: c.textMuted, fontSize: '10px' }}>{p[2]}</div>
                  <div style={{ color: cellColor(p[3], c.textPrimary), wordBreak: 'break-word' }}>
                    {cellText(p[3])}
                  </div>
                </>
              ) : null}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const TaskPane = () => (
    <div style={{ padding: '16px', color: c.textMuted, fontSize: '12px' }}>
      <div style={{ marginBottom: '8px', color: c.textSecondary, fontWeight: 600 }}>{t('meter.operator_tasks')}</div>
      No recent tasks on this meter. Use <strong>🔄 Refresh</strong> on
      the Relay Control Tasks card above to pull the last 30 days of
      relay operations.
    </div>
  );

  const BillingPane = () => (
    <div style={{ padding: '16px', fontSize: '12px' }}>
      <div style={{ marginBottom: '8px', color: c.textSecondary, fontWeight: 600 }}>
        {t('meter.billing_data_snapshot')}
      </div>
      <div style={{ color: c.textMuted, marginBottom: '12px' }}>
        Daily billing and load profile rows are pulled from the local
        database (populated by the midnight scheduler and the
        read-through cache). Switch to the <strong>📊 Meter Readings</strong>
        tab for the full table view with date range + export.
      </div>
      <DetailGrid items={[
        ['Daily billing rows (30d)', `${dbStored} / ${dbExp}`],
        ['Load profile rows (30d)',  `${lpStored} / ${lpExp}`],
        ['Daily coverage',           `${dailyPct}%`],
        ['Load coverage',            `${monthlyPct}%`],
      ]} />
    </div>
  );

  const CommPane = () => (
    <div style={{ padding: '16px', fontSize: '12px' }}>
      <div style={{ marginBottom: '8px', color: c.textSecondary, fontWeight: 600 }}>{t('meter.communication')}</div>
      <DetailGrid items={[
        ['Last heartbeat', live?.last_heartbeat ? fmtDT(live.last_heartbeat, { utc: true, seconds: true }) : '—'],
        ['Session open',   live?.session_open ? 'yes' : 'no'],
        ['Comm address',   assignment.comm_addr || '—'],
        ['Connection type', assignment.connection_type || '—'],
      ]} />
    </div>
  );

  const CheckPane = () => (
    <div style={{ padding: '16px', color: c.textMuted, fontSize: '12px' }}>
      Field-check records are not yet collected from the meter.
      Work in progress — will be added soon.
    </div>
  );

  // ── Sub-sub-tab button style + General-pane nav style ──
  const subBtn = (active) => ({
    padding: '6px 14px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    fontSize: '12px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });
  const navBtn = (active) => ({
    padding: '8px 12px',
    width: '100%',
    textAlign: 'left',
    background: active ? 'rgba(0,229,255,0.08)' : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderLeft: active ? `3px solid ${c.accent}` : '3px solid transparent',
    fontSize: '12px',
    cursor: 'pointer',
  });

  return (
    <div>
      <SummaryBar />

      {/* ── 3-card top row ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '12px',
        marginBottom: '14px',
      }}>
        <Card
          title={t('meter.status')}
          icon="📡"
          right={
            <button className="button button-sm button-secondary"
                    onClick={readRelay}
                    disabled={relayBusy || !live?.session_open}>
              {relayBusy ? <Spinner /> : '🔄 Refresh'}
            </button>
          }
        >
          <StatusRow
            dotColor={online ? c.success : c.textMuted}
            label={online ? t('meter.online') : t('meter.offline')}
            sub={live?.last_heartbeat ? fmtDT(live.last_heartbeat, { utc: true, seconds: true }) : null}
          />
          <StatusRow
            dotColor={relayColor}
            label={`${t('label.relay_status')}: ${relay?.raw === 1 ? t('relay.on') : relay?.raw === 0 ? t('relay.off') : t('msg.relay_unknown')}`}
            sub={relay?.at ? `${t('label.read_at')} ${relay.at}` : `${t('msg.click_refresh_relay')} →`}
          />
          <StatusRow
            dotColor={powerColor}
            label={powerLabel}
          />
        </Card>

        <Card title={t('meter.meter_collection')} icon="📥">
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>{t('meter.daily_profile')}</div>
          <div style={{
            background: c.bgCardAlt,
            borderRadius: '4px',
            height: '10px',
            overflow: 'hidden',
            marginBottom: '8px',
          }}>
            <div style={{
              width: `${dailyPct}%`,
              height: '100%',
              background: dailyPct >= 90 ? c.success : dailyPct >= 50 ? c.warning : c.danger,
              transition: 'width 300ms ease',
            }} />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: c.textPrimary, marginBottom: '10px' }}>{dailyPct}%</div>

          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '4px' }}>{t('meter.monthly_load_profile')}</div>
          <div style={{
            background: c.bgCardAlt,
            borderRadius: '4px',
            height: '10px',
            overflow: 'hidden',
            marginBottom: '8px',
          }}>
            <div style={{
              width: `${monthlyPct}%`,
              height: '100%',
              background: monthlyPct >= 90 ? c.success : monthlyPct >= 50 ? c.warning : c.danger,
              transition: 'width 300ms ease',
            }} />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: c.textPrimary }}>{monthlyPct}%</div>
        </Card>

        <Card title={t('meter.relay_control_tasks')} icon="⚡">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '10px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: c.danger }}>0</div>
              <div style={{ fontSize: '10px', color: c.textMuted }}>{t('meter.failed')}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: c.success }}>0</div>
              <div style={{ fontSize: '10px', color: c.textMuted }}>{t('meter.succeed')}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: c.textMuted }}>0</div>
              <div style={{ fontSize: '10px', color: c.textMuted }}>{t('meter.cancelled')}</div>
            </div>
          </div>
          <div style={{ fontSize: '10px', color: c.textMuted, textAlign: 'center' }}>
            No relay operations logged. Use the <strong>⚡ Relay Control</strong>
            tab to see the full disconnect event history.
          </div>
        </Card>
      </div>

      {/* ── Sub-sub-tab bar ── */}
      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '14px',
        borderBottom: `1px solid ${c.border}`,
        flexWrap: 'wrap',
      }}>
        <button style={subBtn(subsub === 'general')}      onClick={() => setSubsub('general')}     >{t('meter.general')}</button>
        <button style={subBtn(subsub === 'vector')}       onClick={() => setSubsub('vector')}      >{t('meter.vector_chart')}</button>
      </div>

      {/* ── Sub-sub-tab content ── */}
      {subsub === 'general' && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '160px 1fr',
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{ borderRight: `1px solid ${c.border}`, background: c.bgCardAlt, padding: '8px 0' }}>
            <button style={navBtn(generalNav === 'archive')}  onClick={() => setGeneralNav('archive')} >📦 Archive</button>
            <button style={navBtn(generalNav === 'task')}     onClick={() => setGeneralNav('task')}    >📋 Task</button>
            <button style={navBtn(generalNav === 'billing')}  onClick={() => setGeneralNav('billing')} >💰 Billing Data</button>
            <button style={navBtn(generalNav === 'comm')}     onClick={() => setGeneralNav('comm')}    >📡 Communication</button>
            <button style={navBtn(generalNav === 'check')}    onClick={() => setGeneralNav('check')}   >✔ Check Record</button>
          </div>
          <div style={{ padding: '14px' }}>
            {generalNav === 'archive' && <ArchiveGrid />}
            {generalNav === 'task'    && <TaskPane />}
            {generalNav === 'billing' && <BillingPane />}
            {generalNav === 'comm'    && <CommPane />}
            {generalNav === 'check'   && <CheckPane />}
          </div>
        </div>
      )}

      {subsub === 'vector' && (
        <VectorChartTab serial={serial} />
      )}

      {/* Helper buttons for the live reads — floating action below */}
      <div style={{ marginTop: '14px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          className="button button-sm button-secondary"
          onClick={readExtra}
          disabled={extraBusy || (!live?.session_open && !isDcuRouted)}
          title={isDcuRouted ? 'Device info from DCU profile' : (live?.session_open ? t('meter.read_device_info_title') : t('meter.open_secure_session_first'))}
        >
          {extraBusy ? <Spinner /> : '🔄'} {t('meter.read_device_info')}
        </button>
        {!live?.session_open && !isDcuRouted && (
          <span style={{ fontSize: '11px', color: c.warning }}>
            ⚠ {t('meter.live_reads_require_session')}
          </span>
        )}
        {isDcuRouted && (
          <span style={{ fontSize: '11px', color: '#22c55e' }}>
            Connected via DCU — data from profile collection
          </span>
        )}
      </div>
    </div>
  );
}

// VectorChartTab — phase-diagram (phasor) vector display on the
// Meter Detail → Home → Vector Chart sub-tab. Calls the cookie-auth
// /api/meters/{serial}/phase-diagram endpoint, which returns live
// instantaneous V/I/PF/freq for direct-TCP meters and falls back to
// the latest 30-min PQ profile row for DCU-routed meters.
//
// Phase angles aren't exposed by Holley meters (per CAS spec §10.1
// + reference-UI DLMS dumps), so we render balanced 3-phase voltages
// at 0°/240°/120° and offset the current vectors by acos(PF) per
// phase — same approximation the Holley reference UI uses. Banner
// makes the approximation explicit.
function VectorChartTab({ serial }) {
  const c = useThemeColors();
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const read = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api(`/meters/${encodeURIComponent(serial)}/phase-diagram`));
    } catch (e) {
      setError(e?.detail || e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { read(); }, [serial]);

  return (
    <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0 }}>{t('meter.vector_chart')}</h3>
        <button className="button button-sm" onClick={read} disabled={loading}>
          {loading ? <Spinner /> : '🔄'} Refresh
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', background: '#3b1d1d', border: `1px solid ${c.danger}`, borderRadius: '6px', color: '#fca5a5', marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {data && data.data_source === 'pq_profile' && (
        <div style={{ padding: '8px 12px', background: c.bgCardAlt, border: `1px dashed ${c.border}`, borderRadius: '6px', color: c.textMuted, marginBottom: '12px', fontSize: '12px' }}>
          <strong>Source:</strong> 30-min Power Quality profile row (avg V/I/PF per phase). This meter is DCU-routed — no direct live polling, so the freshest snapshot is the PQ row.
        </div>
      )}
      {data && data.data_source === 'mixed' && (
        <div style={{ padding: '8px 12px', background: c.bgCardAlt, border: `1px dashed ${c.border}`, borderRadius: '6px', color: c.textMuted, marginBottom: '12px', fontSize: '12px' }}>
          <strong>Source:</strong> mix of live instantaneous and 30-min PQ profile averages.
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
          <PhasorSVG data={data} />
          <PhasorDetailTable data={data} />
        </div>
      )}

      {data && (
        <div style={{ marginTop: '14px', fontSize: '11px', color: c.textMuted }}>
          {data.ts_utc ? `Latest readings as of ${fmtDT(data.ts_utc, { utc: true, seconds: true })} · ` : 'No fresh readings — meter may be offline. '}
          angles_source: <code>{data.angles_source}</code>
          {data.data_source && data.data_source !== 'none' && <> · data_source: <code>{data.data_source}</code></>}
        </div>
      )}
    </div>
  );
}

function PhasorSVG({ data }) {
  // 320×320 SVG. Voltages drawn at radius 110 (red), currents at
  // radius 75 (green). Angles in degrees, 0° = east, increasing
  // counter-clockwise (mathematical convention — matches Holley UI).
  const cx = 160, cy = 160;
  const vRadius = 110;
  const iRadius = 75;
  const polar = (mag, deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + mag * Math.cos(rad), y: cy - mag * Math.sin(rad) };
  };
  const vectors = [
    { label: 'Ua', color: '#ef4444', angle: data.angles?.v_l1, mag: vRadius },
    { label: 'Ub', color: '#ef4444', angle: data.angles?.v_l2, mag: vRadius },
    { label: 'Uc', color: '#ef4444', angle: data.angles?.v_l3, mag: vRadius },
    { label: 'Ia', color: '#22c55e', angle: data.angles?.i_l1, mag: iRadius },
    { label: 'Ib', color: '#22c55e', angle: data.angles?.i_l2, mag: iRadius },
    { label: 'Ic', color: '#22c55e', angle: data.angles?.i_l3, mag: iRadius },
  ];
  return (
    <svg width="320" height="320" viewBox="0 0 320 320" style={{ background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
      <defs>
        <marker id="vchart-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#cbd5e1" />
        </marker>
      </defs>
      <circle cx={cx} cy={cy} r={vRadius} stroke="#475569" strokeWidth="1" fill="none" />
      <circle cx={cx} cy={cy} r={iRadius} stroke="#475569" strokeWidth="1" strokeDasharray="2,3" fill="none" />
      <line x1={cx - vRadius} y1={cy} x2={cx + vRadius} y2={cy} stroke="#334155" strokeWidth="0.5" />
      <line x1={cx} y1={cy - vRadius} x2={cx} y2={cy + vRadius} stroke="#334155" strokeWidth="0.5" />
      {vectors.map((v) => {
        if (v.angle == null) return null;
        const tip = polar(v.mag, v.angle);
        const labelPos = polar(v.mag + 14, v.angle);
        return (
          <g key={v.label}>
            <line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke={v.color} strokeWidth="2" markerEnd="url(#vchart-arrow)" />
            <text x={labelPos.x} y={labelPos.y} fill={v.color} fontSize="12" textAnchor="middle" dominantBaseline="middle" fontFamily="monospace">{v.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function PhasorDetailTable({ data }) {
  const c = useThemeColors();
  const fmt = (v, suffix = '') => v == null ? '—' : `${Number(v).toFixed(2)}${suffix}`;
  const interAngle = (a, b) => {
    if (a == null || b == null) return '—';
    let d = a - b;
    while (d < 0) d += 360;
    return `${d.toFixed(1)}°`;
  };
  const cellMuted = { color: c.textMuted, padding: '4px 10px', fontSize: '12px' };
  const cellMono = { padding: '4px 10px', fontFamily: 'monospace', fontSize: '12px' };
  return (
    <table style={{ borderCollapse: 'collapse', minWidth: '420px' }}>
      <tbody>
        <tr><td style={cellMuted}>Voltage L1 (Ua)</td><td style={cellMono}>{fmt(data.voltages?.L1, ' V')}</td>
            <td style={cellMuted}>Current L1 (Ia)</td><td style={cellMono}>{fmt(data.currents?.L1, ' A')}</td></tr>
        <tr><td style={cellMuted}>Voltage L2 (Ub)</td><td style={cellMono}>{fmt(data.voltages?.L2, ' V')}</td>
            <td style={cellMuted}>Current L2 (Ib)</td><td style={cellMono}>{fmt(data.currents?.L2, ' A')}</td></tr>
        <tr><td style={cellMuted}>Voltage L3 (Uc)</td><td style={cellMono}>{fmt(data.voltages?.L3, ' V')}</td>
            <td style={cellMuted}>Current L3 (Ic)</td><td style={cellMono}>{fmt(data.currents?.L3, ' A')}</td></tr>
        <tr><td style={cellMuted}>Angle of Phase A</td><td style={cellMono}>{fmt(data.angles?.v_l1, '°')}</td>
            <td style={cellMuted}>Angle of Phase B</td><td style={cellMono}>{fmt(data.angles?.v_l2, '°')}</td></tr>
        <tr><td style={cellMuted}>Angle of Phase C</td><td style={cellMono}>{fmt(data.angles?.v_l3, '°')}</td>
            <td style={cellMuted}>Angle of Voltage(BA)</td><td style={cellMono}>{interAngle(data.angles?.v_l2, data.angles?.v_l1)}</td></tr>
        <tr><td style={cellMuted}>Angle of Voltage(CA)</td><td style={cellMono}>{interAngle(data.angles?.v_l3, data.angles?.v_l1)}</td>
            <td style={cellMuted}>Frequency</td><td style={cellMono}>{fmt(data.frequency_hz, ' Hz')}</td></tr>
        <tr><td style={cellMuted}>PF total</td><td style={cellMono}>{fmt(data.pf_total)}</td>
            <td style={cellMuted}>PF L1</td><td style={cellMono}>{fmt(data.pf_per_phase?.L1)}</td></tr>
        <tr><td style={cellMuted}>PF L2</td><td style={cellMono}>{fmt(data.pf_per_phase?.L2)}</td>
            <td style={cellMuted}>PF L3</td><td style={cellMono}>{fmt(data.pf_per_phase?.L3)}</td></tr>
      </tbody>
    </table>
  );
}


// MeterDetailView — tabbed container for everything-about-one-meter.
// Replaces the old single-panel AssignedMeterDetail and is the v3 UI
// refresh's new home for the Read Meter / Write Meter / Load Curve /
// Events / Reports tabs that used to live at the top level. Renders
// as the right column of HierarchyTab when a meter is selected AND
// as the sole content of the new 📟 Meter top-level tab.
//
// Sub-tabs: Home | Events | Meter Readings | On-Demand | Relay | Tariff
//
// Phase 3.2 scaffolds the container with only the Home tab wired up
// (it ports the old DetailGrid content). Phases 3.3–3.8 fill the
// other tabs in place.
function MeterDetailView({ assignment, inboundMeters, customers, transformers, isAdmin, onChanged }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [sub, setSub] = useState('home');

  if (!assignment) {
    return (
      <div style={{ color: c.textMuted, padding: '24px', textAlign: 'center' }}>
        {t('msg.pick_meter')}
      </div>
    );
  }

  const live = inboundMeters.find(m => (m.serial_number || m.serial) === assignment.meter_serial);
  const transformer = transformers.find(t => t.id === assignment.transformer_id);
  const customer = transformer && customers.find(c => c.id === transformer.customer_id);

  // The backend resolves meter_type into {code,label,source} for every
  // assignment — fall back to the live inbound-meter dict if the
  // assignment row was cached before the backend gained the resolver.
  const meterType = assignment.meter_type || live?.meter_type;

  const deleteAssignment = async () => {
    if (!(await confirm(
      `Remove meter ${assignment.meter_serial}?`,
      `This removes the meter from the hierarchy and frees its serial for re-assignment. The physical meter is NOT disconnected — it will keep dialling in to the CAS server.`
    ))) return;
    try {
      await api(`/assignments/${assignment.id}`, { method: 'DELETE' });
      toast.push(t('toast.assignment_deleted'), 'success');
      onChanged && onChanged();
    } catch (err) {
      toast.push(err.message, 'error');
    }
  };

  const tabBtn = (active) => ({
    padding: '8px 14px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    marginBottom: '-1px',
  });

  const Placeholder = ({ icon, title, body }) => (
    <div style={{
      padding: '32px',
      textAlign: 'center',
      color: c.textMuted,
      background: c.bgCardAlt,
      borderRadius: '8px',
      border: `1px dashed ${c.border}`,
    }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '14px', fontWeight: 600, color: c.textLabel, marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '12px' }}>{body}</div>
    </div>
  );

  return (
    <div>
      {/* ── Header: title, type badge, admin actions ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span>{assignment.role === 'main' ? '🟧' : '🔌'} {assignment.object_name || assignment.meter_serial}</span>
            <MeterTypeBadge mt={meterType} />
          </div>
          <div style={{ fontSize: '13px', color: c.textMuted, fontFamily: 'monospace' }}>
            {assignment.meter_serial}
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="button button-secondary" onClick={() => setEditing(true)}>✏️ Edit</button>
            <button className="button button-secondary" onClick={deleteAssignment}>🗑 Delete</button>
          </div>
        )}
        {!isAdmin && (userCan(user, 'edit_account_number') || userCan(user, 'edit_ct_ratio')) ? (
          <button className="button button-secondary" onClick={() => setRenaming(true)}>✏️ {t('btn.edit')}</button>
        ) : !isAdmin && userCan(user, 'rename') && (
          <button className="button button-secondary" onClick={() => setRenaming(true)}>✏️ {t('btn.rename')}</button>
        )}
      </div>

      {/* ── Sub-tab bar (matches CAS reference product) ── */}
      {/* On a phone these eight tabs wrapped onto four rows and ate ~150px
          of every meter view before any content appeared. A single
          horizontally scrolling row is the standard mobile pattern and keeps
          the selected tab, the meter header and the panel on one screen.
          Desktop keeps wrapping, where there is width for it. */}
      <div className="subtab-bar" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '16px',
        borderBottom: `1px solid ${c.border}`,
        flexWrap: 'wrap',
      }}>
        <button style={tabBtn(sub === 'home')}         onClick={() => setSub('home')}        >🏠 {t('mdt.home')}</button>
        <button style={tabBtn(sub === 'event')}        onClick={() => setSub('event')}       >⚡ {t('mdt.event')}</button>
        <button style={tabBtn(sub === 'readings')}     onClick={() => setSub('readings')}    >📊 {t('mdt.readings')}</button>
        <button style={tabBtn(sub === 'relay')}        onClick={() => setSub('relay')}       >⚡ {t('mdt.relay')}</button>
        <button style={tabBtn(sub === 'collection')}   onClick={() => setSub('collection')}  >📥 {t('mdt.collection')}</button>
        <button style={tabBtn(sub === 'installation')} onClick={() => setSub('installation')}>🧰 {t('mdt.installation')}</button>
        <button style={tabBtn(sub === 'tariff')}       onClick={() => setSub('tariff')}      >💰 {t('mdt.tariff')}</button>
        {isAdmin && (
          <button style={tabBtn(sub === 'network')}     onClick={() => setSub('network')}     >🌐 {t('mdt.network')}</button>
        )}
      </div>

      {/* ── Home tab ── */}
      {sub === 'home' && (
        <MeterDetailHomeTab
          assignment={assignment}
          live={live}
          customer={customer}
          transformer={transformer}
          meterType={meterType}
        />
      )}

      {sub === 'event' && (
        <MeterDetailEventsTab
          serial={assignment.meter_serial}
          sessionOpen={!!live?.session_open}
        />
      )}
      {sub === 'readings' && (
        <MeterDetailReadingsTab
          serial={assignment.meter_serial}
          sessionOpen={!!live?.session_open}
        />
      )}
      {sub === 'relay' && (
        <MeterDetailRelayTab
          serial={assignment.meter_serial}
          sessionOpen={!!live?.session_open}
          assignment={assignment}
          live={live}
          meterType={meterType}
        />
      )}
      {sub === 'collection' && (
        <MeterDetailCollectionTab serial={assignment.meter_serial} />
      )}
      {sub === 'installation' && (
        <MeterDetailInstallationTab
          serial={assignment.meter_serial}
          assignment={assignment}
          live={live}
          customer={customer}
          transformer={transformer}
          meterType={meterType}
        />
      )}
      {sub === 'tariff' && (
        <MeterDetailTariffTab
          serial={assignment.meter_serial}
          sessionOpen={!!live?.session_open}
        />
      )}
      {sub === 'network' && isAdmin && (
        <MeterDetailNetworkTab
          serial={assignment.meter_serial}
          sessionOpen={!!live?.session_open}
          assignment={assignment}
          live={live}
        />
      )}

      {editing && (
        <AssignMeterModal
          transformers={transformers}
          customers={customers}
          inboundMeters={inboundMeters}
          assignedSerials={new Set()}
          existingAssignment={assignment}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged && onChanged(); }}
        />
      )}
      {renaming && (
        <MeterFieldsModal
          assignment={assignment}
          onClose={() => setRenaming(false)}
          onSaved={() => { setRenaming(false); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

// ─── Unassigned meter detail (admin can claim) ───
function UnassignedMeterDetail({ meter, customers, transformers, isAdmin, onAssigned }) {
  const c = useThemeColors();
  const [showAssign, setShowAssign] = useState(false);
  const serial = meter.serial_number || meter.serial;
  return (
    <div>
      <div style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>
        🔌 {serial}
      </div>
      <div style={{ fontSize: '13px', color: c.textMuted, marginBottom: '16px' }}>
        Connected but not yet assigned to a customer.
      </div>
      <DetailGrid items={[
        ['Address', meter.address],
        ['Status', meter.status],
        ['Device type', meter.device_type],
        ['Meter type', formatMeterType(meter.meter_type)],
        ['Firmware', meter.firmware_version],
        ['Identified', meter.identified ? 'yes' : 'no'],
      ]} />
      {isAdmin && (
        <div style={{ marginTop: '20px' }}>
          <button
            className="button button-primary"
            onClick={() => setShowAssign(true)}
            disabled={transformers.length === 0}
          >
            🔗 Assign to a transformer
          </button>
          {transformers.length === 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: c.warning }}>
              ⚠️ No transformers exist yet. Create one first.
            </div>
          )}
        </div>
      )}
      {showAssign && (
        <AssignMeterModal
          transformers={transformers}
          customers={customers}
          inboundMeters={[meter]}
          assignedSerials={new Set()}
          presetMeterSerial={serial}
          onClose={() => setShowAssign(false)}
          onSaved={() => { setShowAssign(false); onAssigned(); }}
        />
      )}
    </div>
  );
}

// ─── Tiny detail-grid helper used by every detail panel ───
function DetailGrid({ items }) {
  const c = useThemeColors();
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '160px 1fr',
      rowGap: '6px',
      columnGap: '12px',
      fontSize: '13px',
    }}>
      {items.map(([k, v], i) => (
        <React.Fragment key={i}>
          <div style={{ color: c.textMuted }}>{k}</div>
          <div style={{ color: v == null || v === '' ? c.textDimmed : c.textPrimary }}>
            {v == null || v === '' ? '—' : String(v)}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Modals — admin-only forms for creating/editing customers, transformers,
// and meter assignments.
// ═══════════════════════════════════════════════════════════════════════

function ModalShell({ title, onClose, children, width = '460px' }) {
  const c = useThemeColors();
  // Escape closes, like the ✕. Backdrop-tap alone was the only way out, and on
  // a phone the modal fills the screen, leaving almost no backdrop to tap.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.6)', zIndex: 9000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: c.bgCard, padding: '24px',
        borderRadius: '10px', border: `1px solid ${c.border}`,
        width, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          position: 'sticky', top: '-24px', zIndex: 1, background: c.bgCard,
          margin: '-24px -24px 16px', padding: '16px 12px 12px 24px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
        }}>
          <div className="section-title" style={{ margin: 0, paddingTop: '6px' }}>{title}</div>
          <button type="button" onClick={onClose} aria-label="Close" title="Close"
            style={{
              flex: '0 0 auto', width: '40px', height: '40px', borderRadius: '8px',
              border: `1px solid ${c.border}`, background: 'transparent', color: c.textPrimary,
              fontSize: '20px', lineHeight: 1, cursor: 'pointer',
            }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AddCustomerModal({ onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState({ name: '', account_number: '', address: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/customers', { method: 'POST', body: JSON.stringify(form) });
      toast.push(t('toast.customer_created'), 'success');
      onSaved();
    } catch (err) { toast.push(err.message, 'error'); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={'➕ ' + t('modal.new_customer')} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <FormField label={t('form.name_required')} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <FormField label={t('form.account_number')} value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} />
        <FormField label={t('form.address')} value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
        <FormField label={t('form.phone')} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <FormField label={t('form.email')} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <ModalButtons busy={busy} onClose={onClose} disabled={!form.name} />
      </form>
    </ModalShell>
  );
}

function EditCustomerModal({ customer, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState({
    name: customer.name || '',
    account_number: customer.account_number || '',
    address: customer.address || '',
    phone: customer.phone || '',
    email: customer.email || '',
  });
  const [busy, setBusy] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/customers/${customer.id}`, { method: 'PATCH', body: JSON.stringify(form) });
      toast.push(t('toast.customer_updated'), 'success');
      onSaved();
    } catch (err) { toast.push(err.message, 'error'); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={'✏️ ' + t('modal.edit_customer')} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <FormField label={t('form.name')} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <FormField label={t('form.account_number')} value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} />
        <FormField label={t('form.address')} value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
        <FormField label={t('form.phone')} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <FormField label={t('form.email')} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <ModalButtons busy={busy} onClose={onClose} disabled={!form.name} label={t('btn.save')} />
      </form>
    </ModalShell>
  );
}

function AddTransformerModal({ customers, defaultCustomerId, onClose, onSaved }) {
  const c = useThemeColors();
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState({
    customer_id: defaultCustomerId || (customers[0]?.id || ''),
    name: '',
    location: '',
  });
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.customer_id = parseInt(payload.customer_id);
      if (!payload.location) payload.location = null;
      const created = await api('/transformers', { method: 'POST', body: JSON.stringify(payload) });
      toast.push(t('toast.transformer_created'), 'success');
      onSaved(created);
    } catch (err) { toast.push(err.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={'➕ ' + t('modal.new_transformer')} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.customer_required')}
          <select
            className="input-field"
            value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
            required
          >
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <FormField label={t('form.name_required')} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <FormField label={t('form.location')} value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
        <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '12px' }}>
          {t('form.ct_vt_help')}
        </div>
        <ModalButtons busy={busy} onClose={onClose} disabled={!form.customer_id || !form.name} />
      </form>
    </ModalShell>
  );
}

// Edit form for an existing transformer. Same field set as the add
// modal but pre-populated and PATCHes instead of POSTing, and the
// customer selector is disabled — moving a transformer between
// customers would orphan every downstream assignment and loss
// calculation, so we force a delete-and-recreate for that case.
// Name-only rename for operators (admins use the full Edit dialogs). The
// server allows it for admin + operator only, within the operator's scope;
// viewers, developers and API keys never see the button and are refused.
function RenameModal({ title, initial, path, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const [name, setName] = useState(initial || '');
  const [busy, setBusy] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api(path, { method: 'POST', body: JSON.stringify({ name }) });
      toast.push(t('toast.renamed'), 'success');
      onSaved();
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModalShell title={'✏️ ' + title} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <FormField label={t('form.new_name')} value={name} onChange={setName} required />
        <ModalButtons busy={busy} onClose={onClose} disabled={!name.trim() || name.trim() === (initial || '')} label={t('btn.save')} />
      </form>
    </ModalShell>
  );
}

// The fields of a meter a non-admin was individually granted: object name
// (rename), account number (edit_account_number), KTT (edit_ct_ratio). Shows
// only those and sends only what changed; the server re-checks each one.
function MeterFieldsModal({ assignment, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const { user } = useAuth();
  const initial = {
    object_name: assignment.object_name || '',
    account_number: assignment.account_number || '',
    ct_ratio: assignment.ct_ratio || '',
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const changed = Object.keys(initial).filter(k => form[k].trim() !== initial[k].trim());
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      changed.forEach(k => { body[k] = form[k]; });
      await api(`/meters/${encodeURIComponent(assignment.meter_serial)}/fields`,
                { method: 'PATCH', body: JSON.stringify(body) });
      toast.push(t('toast.assignment_updated'), 'success');
      onSaved();
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModalShell title={'✏️ ' + t('modal.edit_meter_fields')} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        {userCan(user, 'rename') && (
          <FormField label={t('form.object_name')} value={form.object_name} onChange={(v) => setForm({ ...form, object_name: v })} />
        )}
        {userCan(user, 'edit_account_number') && (
          <FormField label={t('form.account_number')} value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} />
        )}
        {userCan(user, 'edit_ct_ratio') && (
          <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
            {t('form.ct_ratio')}
            <select className="input-field" value={form.ct_ratio} onChange={(e) => setForm({ ...form, ct_ratio: e.target.value })}>
              <option value="">— none / direct —</option>
              {initial.ct_ratio && !CT_RATIOS.includes(initial.ct_ratio) && (
                <option value={initial.ct_ratio}>{initial.ct_ratio} (as stored)</option>
              )}
              {CT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {changed.includes('ct_ratio') && (
              <div style={{ fontSize: '12px', color: c.warning || '#fbbf24', marginTop: '6px' }}>
                ⚠ {t('form.ct_ratio_warning')}
              </div>
            )}
          </label>
        )}
        <ModalButtons busy={busy} onClose={onClose} disabled={changed.length === 0} label={t('btn.save')} />
      </form>
    </ModalShell>
  );
}

function EditTransformerModal({ transformer, onClose, onSaved }) {
  const c = useThemeColors();
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState({
    name: transformer.name || '',
    location: transformer.location || '',
  });
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // PATCH body mirrors TransformerIn. customer_id is not editable
      // here so we include the existing one to satisfy the pydantic
      // model. CT/VT defaults and lat/lon are not editable from this
      // form any more (Phase 1.3 of the v3 UI refresh); they remain
      // in the DB so legacy rows are preserved and the existing
      // meter-assignment auto-fill still works when present.
      const payload = {
        customer_id: transformer.customer_id,
        name: form.name,
        location: form.location || null,
      };
      await api(`/transformers/${transformer.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      toast.push(t('toast.transformer_updated'), 'success');
      onSaved();
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={'✏️ ' + t('modal.edit_transformer')} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <FormField label={t('form.name_required')} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <FormField label={t('form.location')} value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
        <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '12px' }}>
          {t('form.ct_vt_help')}
        </div>
        <ModalButtons busy={busy} onClose={onClose} disabled={!form.name} label={t('btn.save')} />
      </form>
    </ModalShell>
  );
}

// CAS spec §7.3 meter-type label map, kept in sync with
// _METER_TYPE_LABELS in emeter/api_server.py. The backend already
// resolves the meter type for every assignment and live meter into a
// { code, label, source } object — these helpers are only used when
// we need to render something client-side without waiting for a
// backend round-trip (e.g. the <select> in the modal form).
const METER_TYPE_OPTIONS = [
  { code: 1, label: '1-Phase' },
  { code: 3, label: '3-Phase Direct' },
  { code: 4, label: '3-Phase CT' },
  { code: 5, label: '3-Phase CT/VT' },
];

function formatMeterType(mt) {
  // `mt` is the { code, label, source } block returned by the API.
  // Falls back gracefully when the backend hasn't filled it in yet.
  if (!mt || mt.code == null) return '—';
  return mt.label || `Type ${mt.code}`;
}

function MeterTypeBadge({ mt, compact }) {
  const c = useThemeColors();
  if (!mt || mt.code == null) {
    return <span style={{ color: c.textMuted, fontSize: '11px' }}>unknown</span>;
  }
  const bg = mt.source === 'override' ? '#7c3aed' : '#1e40af';
  const icon = mt.source === 'override' ? '✏' : '⚙';
  return (
    <span
      title={`${mt.label} (${mt.source})`}
      style={{
        display: 'inline-block',
        background: bg,
        color: '#ffffff',
        fontSize: compact ? '10px' : '11px',
        padding: compact ? '1px 6px' : '2px 8px',
        borderRadius: '10px',
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      {icon} {mt.label}
    </span>
  );
}

function AssignDcuModal({ transformers, customers, inboundMeters, assignedSerials = new Set(), defaultTransformerId, lockedDcu, onClose, onSaved }) {
  const toast = useToast();
  const c = useThemeColors();
  // Assign mode: only offer DCUs not already under a transformer (re-listing
  // assigned ones is confusing). Reassign mode (lockedDcu): show ONLY that DCU,
  // locked — the POST upserts, so submitting a new transformer moves it (and
  // cascades to its child meters) without an unassign-first round-trip.
  const dcus = lockedDcu
    ? [inboundMeters.find(m => (m.serial_number || m.serial) === lockedDcu) || { serial_number: lockedDcu, device_type: 'dcu' }]
    : inboundMeters.filter(m => m.device_type === 'dcu' && !assignedSerials.has(m.serial_number || m.serial));
  const [form, setForm] = useState({
    dcu_serial: lockedDcu || dcus[0]?.serial_number || dcus[0]?.serial || '',
    transformer_id: defaultTransformerId || (transformers[0]?.id || ''),
    object_name: '',
  });
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.dcu_serial || !form.transformer_id) return;
    setBusy(true);
    try {
      await api('/assignments', {
        method: 'POST',
        body: JSON.stringify({
          meter_serial: form.dcu_serial,
          transformer_id: parseInt(form.transformer_id),
          role: 'customer',
          object_name: form.object_name || `DCU ${form.dcu_serial}`,
          dcu_serial: form.dcu_serial,
        }),
      });
      toast.push(lockedDcu ? 'DCU reassigned successfully' : 'DCU assigned successfully', 'success');
      onSaved();
    } catch (err) {
      toast.push('Failed to assign DCU: ' + (err.message || err), 'error');
    }
    setBusy(false);
  };

  const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${c.border}`, background: c.bgInput || c.bgCard, color: c.textPrimary, fontSize: '13px' };

  return (
    <ModalShell title={lockedDcu ? '🔀 Reassign DCU to a different Transformer' : '🛰️ Assign DCU to Transformer'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: c.textLabel, display: 'block', marginBottom: '4px' }}>DCU</label>
          <select style={{ ...fieldStyle, opacity: lockedDcu ? 0.7 : 1 }} disabled={!!lockedDcu} value={form.dcu_serial} onChange={e => setForm(f => ({ ...f, dcu_serial: e.target.value }))}>
            {dcus.length === 0 && <option value="">No unassigned DCUs available</option>}
            {dcus.map(d => {
              const s = d.serial_number || d.serial;
              return <option key={s} value={s}>{s} {d.session_open ? '(online)' : '(offline)'}</option>;
            })}
          </select>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: c.textLabel, display: 'block', marginBottom: '4px' }}>Customer / Transformer</label>
          <select style={fieldStyle} value={form.transformer_id} onChange={e => setForm(f => ({ ...f, transformer_id: e.target.value }))}>
            {transformers.map(t => {
              const cust = customers.find(c2 => c2.id === t.customer_id);
              return <option key={t.id} value={t.id}>{cust?.name || '?'} &gt; {t.name}</option>;
            })}
          </select>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: c.textLabel, display: 'block', marginBottom: '4px' }}>Name / Description</label>
          <input style={fieldStyle} value={form.object_name} onChange={e => setForm(f => ({ ...f, object_name: e.target.value }))} placeholder={`DCU ${form.dcu_serial}`} />
        </div>
        <ModalButtons busy={busy} onClose={onClose} disabled={!form.dcu_serial || !form.transformer_id} label={lockedDcu ? 'Reassign DCU' : 'Assign DCU'} />
      </form>
    </ModalShell>
  );
}

// ─── Customer → Transformer picker, sorted + searchable ───
//
// The raw list is whatever order /transformers returned, which is neither
// alphabetical nor grouped — with 67 transformers across 26 customers an
// operator was scrolling a jumbled list to find one entry.
//
// Sorting uses localeCompare so Cyrillic customer names (Аккумуляторный,
// Промзона) collate correctly against Latin ones rather than being dumped
// at the end by code-point order.
//
// The filter is a plain input over a native <select> rather than a custom
// dropdown: it keeps keyboard behaviour, screen-reader semantics and the
// existing styling for free, which a hand-rolled combobox would all have to
// re-implement. Matching is case-insensitive on "Customer → Transformer",
// so typing either half narrows the list.
function TransformerPicker({ transformers, customers, value, onChange, label, required = true }) {
  const c = useThemeColors();
  const [q, setQ] = React.useState('');

  const options = React.useMemo(() => {
    const byId = new Map((customers || []).map(x => [x.id, x.name || '']));
    return (transformers || [])
      .map(t => ({
        id: t.id,
        text: `${byId.get(t.customer_id) || '—'} → ${t.name || ''}`,
      }))
      .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
  }, [transformers, customers]);

  // STRICT filter — the list shows matches and nothing else. An earlier
  // version also kept the current selection in the list so a filter could
  // never blank it out; that made the filter look broken (typing "piramit"
  // still showed the selected "Akfa → Almalik"). The selection is now
  // surfaced BELOW the list instead, which keeps the safety without lying
  // about what matched.
  const shown = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o => o.text.toLowerCase().includes(needle));
  }, [options, q]);

  const hiddenCount = options.length - shown.length;
  const selected = options.find(o => String(o.id) === String(value));
  const selectionHidden = !!selected && !shown.some(o => String(o.id) === String(value));

  return (
    <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
      {label}
      <input
        className="input-field"
        placeholder={`🔍 Filter ${options.length} transformers — customer or TP name`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: '4px' }}
      />
      <select
        className="input-field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        size={shown.length > 8 ? 8 : undefined}
      >
        {shown.length === 0 && <option value="">— no match for “{q}” —</option>}
        {shown.map(o => <option key={o.id} value={o.id}>{o.text}</option>)}
      </select>
      {selectionHidden && (
        /* The chosen transformer is filtered out of the list above. Say so
           plainly rather than leaving the select looking empty or, worse,
           appearing to have silently changed. */
        <div style={{ fontSize: '11px', marginTop: '3px', color: c.warning }}>
          ⓘ Still selected: <strong>{selected.text}</strong> — clear the filter to see it
        </div>
      )}
      {hiddenCount > 0 && (
        <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '2px' }}>
          {shown.length} of {options.length} shown · {hiddenCount} filtered out
        </div>
      )}
    </label>
  );
}

function AssignMeterModal({
  transformers, customers, inboundMeters, assignedSerials,
  defaultTransformerId, presetMeterSerial, existingAssignment,
  onClose, onSaved,
}) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const isEdit = !!existingAssignment;
  const eligible = inboundMeters.filter(m => {
    const s = m.serial_number || m.serial;
    if (isEdit) return s === existingAssignment.meter_serial;
    if (presetMeterSerial) return s === presetMeterSerial;
    return !assignedSerials.has(s);
  });
  const [form, setForm] = useState(() => {
    if (isEdit) {
      return {
        meter_serial: existingAssignment.meter_serial,
        transformer_id: existingAssignment.transformer_id || '',
        role: existingAssignment.role || 'customer',
        object_name: existingAssignment.object_name || '',
        account_number: existingAssignment.account_number || '',
        comm_addr: existingAssignment.comm_addr || '',
        install_description: existingAssignment.install_description || '',
        connection_type: existingAssignment.connection_type || 'ct',
        // '' in the form state means "auto-detect" (sent as null).
        meter_type_override: existingAssignment.meter_type_override == null
          ? ''
          : String(existingAssignment.meter_type_override),
        ct_ratio: existingAssignment.ct_ratio || '',
        vt_ratio: existingAssignment.vt_ratio || '',
        fraction_digits: existingAssignment.fraction_digits != null ? String(existingAssignment.fraction_digits) : '',
        lat: existingAssignment.lat != null ? String(existingAssignment.lat) : '',
        lon: existingAssignment.lon != null ? String(existingAssignment.lon) : '',
      };
    }
    return {
      meter_serial: presetMeterSerial || (eligible[0]?.serial_number || eligible[0]?.serial || ''),
      transformer_id: defaultTransformerId || (transformers[0]?.id || ''),
      role: 'customer',
      object_name: '',
      account_number: '',
      comm_addr: '',
      install_description: '',
      connection_type: 'ct',
      meter_type_override: '',
      ct_ratio: '',
      vt_ratio: '',
      fraction_digits: '',
      lat: '',
      lon: '',
    };
  });
  const [busy, setBusy] = useState(false);

  // Auto-detected type (from the live inbound meter, if any) — shown
  // as the placeholder of the override select so the operator can see
  // what the meter reported before deciding whether to override.
  const liveMeter = inboundMeters.find(
    m => (m.serial_number || m.serial) === form.meter_serial
  );
  const autoType = liveMeter?.meter_type
    || (existingAssignment && existingAssignment.meter_type_override == null
          ? existingAssignment.meter_type
          : null);

  // Pre-fill CT/VT defaults from the chosen transformer when one is
  // configured at the transformer level. If the transformer doesn't
  // supply a default, the dropdown stays at "— none / direct —" and
  // the operator picks explicitly — we never inject a hidden default.
  // Skip in edit mode so we don't stomp on operator-set values.
  useEffect(() => {
    if (isEdit) return;
    const t = transformers.find(x => x.id === parseInt(form.transformer_id));
    if (t) {
      setForm(prev => ({
        ...prev,
        ct_ratio: prev.ct_ratio || t.ct_ratio_default || '',
        vt_ratio: prev.vt_ratio || t.vt_ratio || '',
      }));
    }
  }, [form.transformer_id]);

  // Auto-resolve comm_addr when the meter selection changes and the
  // operator hasn't filled it in. For DCU-routed meters this returns
  // the meter's LDN, which is the routing key the DCU uses to address
  // it (CAS DCU spec §3.3 SAP_assignment). Edit mode never refetches —
  // we trust the existing stored value.
  useEffect(() => {
    if (isEdit) return;
    if (!form.meter_serial) return;
    let cancelled = false;
    api(`/meters/${form.meter_serial}/comm-addr`)
      .then(d => {
        if (cancelled) return;
        if (!d || !d.comm_addr) return;
        setForm(prev => prev.comm_addr ? prev : { ...prev, comm_addr: d.comm_addr });
      })
      .catch(() => {/* silent — operator can still type it manually */});
    return () => { cancelled = true; };
  }, [form.meter_serial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.transformer_id = parseInt(payload.transformer_id);
      payload.lat = payload.lat ? parseFloat(payload.lat) : null;
      payload.lon = payload.lon ? parseFloat(payload.lon) : null;
      // '' => null so the backend clears the override and falls back
      // to the auto-detected CAS type. Any other value is the int code.
      payload.meter_type_override = payload.meter_type_override === ''
        ? null
        : parseInt(payload.meter_type_override);
      payload.fraction_digits = payload.fraction_digits === ''
        ? null
        : parseInt(payload.fraction_digits);
      ['object_name', 'account_number', 'comm_addr', 'install_description', 'ct_ratio', 'vt_ratio', 'connection_type'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      await api('/assignments', { method: 'POST', body: JSON.stringify(payload) });
      toast.push(isEdit ? t('toast.assignment_updated') : t('toast.meter_assigned'), 'success');
      onSaved();
    } catch (err) { toast.push(err.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={isEdit ? ('✏️ ' + t('modal.edit_assignment')) : ('🔗 ' + t('modal.assign_meter'))} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('common.meter')}
          <select
            className="input-field"
            value={form.meter_serial}
            onChange={(e) => setForm({ ...form, meter_serial: e.target.value })}
            required
            disabled={isEdit || !!presetMeterSerial}
          >
            {eligible.length === 0 && <option value="">— no unassigned meters —</option>}
            {eligible.map(m => {
              const s = m.serial_number || m.serial;
              return <option key={s} value={s}>{s} ({m.status})</option>;
            })}
          </select>
        </label>
        <TransformerPicker
          transformers={transformers}
          customers={customers}
          value={form.transformer_id}
          onChange={(v) => setForm({ ...form, transformer_id: v })}
          label={t('form.customer')}
        />
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.role')}
          <select className="input-field" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="customer">🔌 {t('assign.role_customer')}</option>
            <option value="main">🟧 {t('assign.role_main')}</option>
          </select>
        </label>
        <FormField label={t('form.object_name')} value={form.object_name} onChange={(v) => setForm({ ...form, object_name: v })} />
        <FormField label={t('form.account_number')} value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} />
        <FormField label={t('form.comm_addr')} value={form.comm_addr} onChange={(v) => setForm({ ...form, comm_addr: v })} />
        <FormField label={t('form.install_desc')} value={form.install_description} onChange={(v) => setForm({ ...form, install_description: v })} />

        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.meter_type')}
          <select
            className="input-field"
            value={form.meter_type_override}
            onChange={(e) => setForm({ ...form, meter_type_override: e.target.value })}
          >
            <option value="">
              — auto-detect{autoType && autoType.code != null ? ` (${autoType.label})` : ''} —
            </option>
            {METER_TYPE_OPTIONS.map(opt => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </select>
          <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '4px' }}>
            {t('form.ct_vt_help')}
          </div>
        </label>

        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.connection_type')}
          <select className="input-field" value={form.connection_type} onChange={(e) => setForm({ ...form, connection_type: e.target.value })}>
            <option value="direct">{t('assign.connection_direct')}</option>
            <option value="ct">{t('assign.connection_ct')}</option>
            <option value="ct_vt">{t('assign.connection_ct_vt')}</option>
          </select>
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.ct_ratio')}
          <select className="input-field" value={form.ct_ratio} onChange={(e) => setForm({ ...form, ct_ratio: e.target.value })}>
            <option value="">— none / direct —</option>
            {/* A stored ratio that is not one of the canonical options still
                has to be SHOWN. A <select> whose value matches no <option>
                renders as the placeholder, so "300/5A" — a perfectly good
                value written by a bulk upload — displayed as "none / direct"
                while the data underneath was correct. The value was never
                lost (form state seeds from the assignment and round-trips on
                save), but the operator was told the opposite.
                Deliberately NOT solved by stripping the unit on write: that
                generalises wrongly, because a VT ratio encodes magnitude in
                its unit and "10кВ/100В" would collapse to 10/100. */}
            {form.ct_ratio && !CT_RATIOS.includes(form.ct_ratio) && (
              <option value={form.ct_ratio}>{form.ct_ratio} (as stored)</option>
            )}
            {CT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.vt_ratio')}
          <select className="input-field" value={form.vt_ratio} onChange={(e) => setForm({ ...form, vt_ratio: e.target.value })}>
            <option value="">— none / direct —</option>
            {/* Same reason as CT above: show what is stored, don't hide it. */}
            {form.vt_ratio && !VT_RATIOS.includes(form.vt_ratio) && (
              <option value={form.vt_ratio}>{form.vt_ratio} (as stored)</option>
            )}
            {VT_RATIOS.map(r => <option key={r} value={r}>{vtRatioLabel(r)}</option>)}
          </select>
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          Decimal places
          <select className="input-field" value={form.fraction_digits} onChange={(e) => setForm({ ...form, fraction_digits: e.target.value })}>
            <option value="">Auto (from meter scaler)</option>
            <option value="0">0 — whole numbers</option>
            <option value="1">1 — e.g. 7,817.0</option>
            <option value="2">2 — e.g. 7,816.98</option>
            <option value="3">3 — e.g. 7,816.980</option>
          </select>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <FormField label={t('form.latitude')} type="number" step="0.000001" value={form.lat} onChange={(v) => setForm({ ...form, lat: v })} />
          <FormField label={t('form.longitude')} type="number" step="0.000001" value={form.lon} onChange={(v) => setForm({ ...form, lon: v })} />
        </div>
        <ModalButtons busy={busy} onClose={onClose} disabled={!form.meter_serial || !form.transformer_id} label={t('btn.assign_meter')} />
      </form>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────
// BulkAssignMetersModal
//
// The many-meter counterpart to AssignMeterModal, opened from the
// Connected Meters tab once the operator has ticked a set of
// unassigned meters. Same nameplate fields, minus the three that are
// genuinely per-meter:
//
//   * comm_addr      — the server resolves it per meter (CAS §7.3).
//   * object_name    — a template here; {serial} expands per meter, so
//                      a batch doesn't land as 50 identical names.
//   * account_number — omitted entirely. It is unique per subscriber;
//                      one shared value would be wrong on every row but
//                      look filled in, which is worse than blank.
//
// Runs a dry-run first so the operator sees the skip buckets (already
// claimed by another admin, unknown to the server) BEFORE committing.
// ─────────────────────────────────────────────────────────────
function BulkAssignMetersModal({ serials, transformers, customers, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [form, setForm] = useState({
    transformer_id: transformers[0]?.id || '',
    role: 'customer',
    object_name_template: '',
    install_description: '',
    connection_type: 'ct',
    meter_type_override: '',
    ct_ratio: '',
    vt_ratio: '',
    fraction_digits: '',
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // dry-run report

  // Same transformer-default prefill as the single-meter modal: if the
  // transformer declares CT/VT defaults, adopt them, but never stomp a
  // value the operator has already chosen.
  useEffect(() => {
    const tx = transformers.find(x => x.id === parseInt(form.transformer_id));
    if (!tx) return;
    setForm(prev => ({
      ...prev,
      ct_ratio: prev.ct_ratio || tx.ct_ratio_default || '',
      vt_ratio: prev.vt_ratio || tx.vt_ratio || '',
    }));
  }, [form.transformer_id]);

  // Any change to the settings invalidates a preview taken against the
  // previous ones — otherwise the operator commits while reading a
  // stale report.
  const set = (patch) => { setPreview(null); setForm(prev => ({ ...prev, ...patch })); };

  const buildPayload = (dryRun) => ({
    meter_serials: serials,
    transformer_id: parseInt(form.transformer_id),
    role: form.role,
    object_name_template: form.object_name_template || null,
    install_description: form.install_description || null,
    connection_type: form.connection_type || null,
    meter_type_override: form.meter_type_override === '' ? null : parseInt(form.meter_type_override),
    ct_ratio: form.ct_ratio || null,
    vt_ratio: form.vt_ratio || null,
    fraction_digits: form.fraction_digits === '' ? null : parseInt(form.fraction_digits),
    dry_run: dryRun,
  });

  const run = async (dryRun) => {
    setBusy(true);
    try {
      const r = await api('/assignments/bulk-assign', {
        method: 'POST',
        body: JSON.stringify(buildPayload(dryRun)),
      });
      const s = r.summary || {};
      if (dryRun) {
        setPreview(r);
        toast.push(`Dry run: ${s.assigned} would be assigned, ${s.skipped} skipped`, s.skipped ? 'warning' : 'info');
      } else {
        const rev = s.revived ? ` (${s.revived} restored from delete)` : '';
        toast.push(
          s.skipped
            ? `Assigned ${s.assigned} meter(s)${rev}, skipped ${s.skipped}`
            : `Assigned ${s.assigned} meter(s)${rev} to ${r.transformer_name}`,
          s.skipped ? 'warning' : 'success',
        );
        onSaved(r);
      }
    } catch (err) {
      toast.push(`Bulk assign failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  const tx = transformers.find(x => x.id === parseInt(form.transformer_id));
  const cust = tx && customers.find(x => x.id === tx.customer_id);
  const sampleName = (form.object_name_template || '').trim()
    ? form.object_name_template.replace('{serial}', serials[0] || '…')
    : `Meter ${serials[0] || '…'}`;

  return (
    <ModalShell title={`🔗 Assign ${serials.length} meter${serials.length === 1 ? '' : 's'}`} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); run(false); }}>
        <div style={{
          padding: '8px 10px', marginBottom: '12px', borderRadius: '6px',
          background: c.bgCardAlt, border: `1px solid ${c.border}`,
          fontSize: '11px', color: c.textSecondary,
        }}>
          <strong style={{ color: c.textPrimary }}>{serials.length}</strong> meter{serials.length === 1 ? '' : 's'} selected
          {cust && tx && <> → <strong style={{ color: c.accent }}>{cust.name} → {tx.name}</strong></>}
          <div style={{ marginTop: '4px', color: c.textMuted, wordBreak: 'break-all' }}>
            {serials.slice(0, 8).join(', ')}{serials.length > 8 ? ` … +${serials.length - 8} more` : ''}
          </div>
        </div>

        <TransformerPicker
          transformers={transformers}
          customers={customers}
          value={form.transformer_id}
          onChange={(v) => set({ transformer_id: v })}
          label={t('form.customer')}
        />

        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.role')}
          <select className="input-field" value={form.role} onChange={(e) => set({ role: e.target.value })}>
            <option value="customer">🔌 {t('assign.role_customer')}</option>
            <option value="main">🟧 {t('assign.role_main')}</option>
          </select>
        </label>

        <FormField
          label="Object name template"
          value={form.object_name_template}
          onChange={(v) => set({ object_name_template: v })}
          placeholder="Meter {serial}"
        />
        <div style={{ fontSize: '11px', color: c.textMuted, margin: '-6px 0 12px' }}>
          <code>{'{serial}'}</code> is replaced per meter. First one becomes:{' '}
          <span style={{ color: c.textSecondary }}>{sampleName}</span>
          <br />
          Account numbers are per-subscriber — set them individually after assigning.
        </div>

        <FormField label={t('form.install_desc')} value={form.install_description}
                   onChange={(v) => set({ install_description: v })} />

        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.meter_type')}
          <select className="input-field" value={form.meter_type_override}
                  onChange={(e) => set({ meter_type_override: e.target.value })}>
            <option value="">— auto-detect (per meter) —</option>
            {METER_TYPE_OPTIONS.map(opt => (
              <option key={opt.code} value={opt.code}>{opt.label}</option>
            ))}
          </select>
          <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '4px' }}>
            Leave on auto unless the whole batch is the same model — the
            override applies to every selected meter.
          </div>
        </label>

        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.connection_type')}
          <select className="input-field" value={form.connection_type}
                  onChange={(e) => set({ connection_type: e.target.value })}>
            <option value="direct">{t('assign.connection_direct')}</option>
            <option value="ct">{t('assign.connection_ct')}</option>
            <option value="ct_vt">{t('assign.connection_ct_vt')}</option>
          </select>
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.ct_ratio')}
          <select className="input-field" value={form.ct_ratio} onChange={(e) => set({ ct_ratio: e.target.value })}>
            <option value="">— none / direct —</option>
            {form.ct_ratio && !CT_RATIOS.includes(form.ct_ratio) && (
              <option value={form.ct_ratio}>{form.ct_ratio} (as stored)</option>
            )}
            {CT_RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.vt_ratio')}
          <select className="input-field" value={form.vt_ratio} onChange={(e) => set({ vt_ratio: e.target.value })}>
            <option value="">— none / direct —</option>
            {form.vt_ratio && !VT_RATIOS.includes(form.vt_ratio) && (
              <option value={form.vt_ratio}>{form.vt_ratio} (as stored)</option>
            )}
            {VT_RATIOS.map(r => <option key={r} value={r}>{vtRatioLabel(r)}</option>)}
          </select>
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          Decimal places
          <select className="input-field" value={form.fraction_digits}
                  onChange={(e) => set({ fraction_digits: e.target.value })}>
            <option value="">Auto (from meter scaler)</option>
            <option value="0">0 — whole numbers</option>
            <option value="1">1 — e.g. 7,817.0</option>
            <option value="2">2 — e.g. 7,816.98</option>
            <option value="3">3 — e.g. 7,816.980</option>
          </select>
        </label>

        {preview && (
          <div style={{
            padding: '8px 10px', marginBottom: '12px', borderRadius: '6px',
            background: c.bgCardAlt,
            border: `1px solid ${preview.summary.skipped ? c.warning : c.success}`,
            fontSize: '11px',
          }}>
            <strong style={{ color: c.textPrimary }}>Dry run</strong> — nothing was written.
            <div style={{ marginTop: '4px', color: c.textSecondary }}>
              ✓ {preview.summary.assigned} would be assigned
              {preview.summary.revived > 0 && <> · ♻ {preview.summary.revived} restored from delete</>}
              {preview.summary.skipped > 0 && <> · ⚠ {preview.summary.skipped} skipped</>}
            </div>
            {preview.skipped.slice(0, 6).map(s => (
              <div key={s.meter_serial} style={{ color: c.warning, marginTop: '2px' }}>
                {s.meter_serial} — {s.detail}
              </div>
            ))}
            {preview.skipped.length > 6 && (
              <div style={{ color: c.textMuted, marginTop: '2px' }}>
                … and {preview.skipped.length - 6} more
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t('btn.cancel')}
          </button>
          <button type="button" className="button button-secondary" onClick={() => run(true)}
                  disabled={busy || !form.transformer_id} style={{ marginLeft: 'auto' }}>
            {busy ? '…' : '🔍 Dry run'}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || !form.transformer_id}>
            {busy ? '⌛ Assigning…' : `Assign ${serials.length}`}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared profile-column helpers
//
// Used by MeterDetailReadingsTab, ReportsTab, and
// SubscriberReadingsPane to consistently render column labels and
// apply the per-column scale divisor that comes back from the
// backend _PROFILE_FALLBACK_COLUMNS dict.
//
// resolveProfileColumns(captures)
//   Returns { labels: [string], scales: [number] } aligned 1:1 with
//   the captures list. Labels are preferred in this order:
//     1. PROFILE_COLUMN_LABELS map keyed on OBIS (most common codes)
//     2. The `label` field from the backend capture (reference-style
//        shorthand like "+A T1 (kWh ×100)")
//     3. Raw OBIS as last resort.
//   The "×N" scaling annotation is stripped from the label when the
//   matching scale is applied to the cell (otherwise the operator
//   sees both "×100" in the header AND a pre-divided number).
//
// renderProfileCell(value, scale)
//   Applies the scale divisor and pretty-prints the result with
//   locale-aware thousands separators + max 3 fraction digits.
//   Non-numeric values fall through unchanged. Null returns "—".
// ─────────────────────────────────────────────────────────────
function resolveProfileColumns(captures) {
  if (!Array.isArray(captures) || captures.length === 0) {
    return { labels: [], scales: [] };
  }
  const labels = captures.map(c => {
    const fromMap = PROFILE_COLUMN_LABELS[c.obis];
    const fromCapture = c.label;
    const raw = String(fromMap || fromCapture || c.obis || '');
    // Drop " ×100" / " ×1000" scaling suffix when we'll divide below.
    return raw.replace(/\s*×\s*\d+\s*/g, ' ').replace(/\s+\)/, ')').trim();
  });
  const scales = captures.map(c => (typeof c.scale === 'number' && c.scale > 0) ? c.scale : 1);
  return { labels, scales };
}

// Canonical number formatter for anything rendered from a meter
// reading. ``fractionDigits === null/undefined`` means "auto" — show
// up to 3 decimals with trailing zeros trimmed. A specific integer
// (0/1/2/3) pads AND truncates to exactly that many, matching the
// operator's per-meter `fraction_digits` pick on MeterAssignment.
//
// ``opts.trimZeros`` — when the caller is rendering a primary-side
// (CT×VT-multiplied) value we trim trailing zeros so a fd=3 meter
// with CT×VT=4800 doesn't show "10,718.400" (only the first decimal
// is actually meaningful after scaling up by 4800). Secondary-side
// renders still pad to exact fd.
function formatMeterNumber(value, fractionDigits, opts) {
  if (typeof value !== 'number') return String(value);
  if (fractionDigits == null) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  }
  if (opts && opts.trimZeros) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits,
    });
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

// Convert a raw meter register value into its display unit. When
// ``fractionDigits`` is ``null`` (Auto) we use the OBIS-reported
// scale exactly as captured (e.g. centi-kWh ÷ 100 → kWh). When the
// operator pinned an exact decimal preference we OVERRIDE the scale
// with 10^fractionDigits — that's the operator declaring "this
// meter actually stores values shifted by N decimal places". For
// example raw 2233 with fd=2 still renders as 22.33, but fd=3 shifts
// the decimal one place to the left → 2.233. Keeps the meter LCD
// and the dashboard agreeing for meters whose factory scaler
// disagrees with what the firmware actually emits.
function effectiveValue(rawValue, scale, fractionDigits) {
  if (typeof rawValue !== 'number') return rawValue;
  if (fractionDigits == null) {
    const s = typeof scale === 'number' && scale > 0 ? scale : 1;
    return s === 1 ? rawValue : rawValue / s;
  }
  const div = Math.pow(10, fractionDigits);
  return div === 1 ? rawValue : rawValue / div;
}

function renderProfileCell(value, scale, primaryMul, fractionDigits, obis, profileObis) {
  if (value == null) return '—';
  // Clock / capture-time columns arrive as ISO text ("2026-10-04T00:00:00+05:00");
  // show them in the one date format (dd/mm/yyyy HH:MM) like every other date.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value)) {
    return fmtDT(value);
  }
  // Status / event-type columns are integer bitmasks per CAS §18.2 —
  // route them through StatusCell so the operator sees "No Status"
  // when clean or a clickable "See Status" pill that pops a
  // bit-by-bit modal when flags are set. Keeps the table row tight
  // regardless of how many bits are active.
  if (obis && STATUS_OBIS_SET.has(obis)) {
    return <StatusCell value={value} />;
  }
  // PQ / LP profiles render raw values — bypass both fd and CT×VT.
  // Keep the OBIS-captured scale only (usually 1 for these columns)
  // so a meter reporting 54 W stays "54" not "0.054" or "259200".
  if (profileObis && RAW_PROFILE_OBIS_SET.has(profileObis)) {
    if (typeof value !== 'number') return String(value);
    const s = typeof scale === 'number' && scale > 0 ? scale : 1;
    const out = s === 1 ? value : value / s;
    return formatMeterNumber(out, null);
  }
  const pm = typeof primaryMul === 'number' && primaryMul > 0 ? primaryMul : 1;
  if (typeof value === 'number') {
    const scaled = effectiveValue(value, scale, fractionDigits) * pm;
    // Trim trailing zeros only when the primary multiplier actually
    // inflated the value — secondary-side renders still pad to fd.
    return formatMeterNumber(scaled, fractionDigits, { trimZeros: pm > 1 });
  }
  return String(value);
}

// CAS Meter Spec §18.2 — Meter Internal Status Word bit definitions.
// 32-bit bitmask snapshotted into the daily / monthly / power-quality
// profiles. Each set bit indicates one condition. Lifted to module
// scope so every render path (individual meter tabs, aggregate Daily
// /  Monthly tabs, instantaneous panel) shares the same canonical
// mapping.
const STATUS_WORD_BITS = {
  0: 'Unlock meter', 1: 'Measurement error', 2: 'EEPROM error',
  3: 'Flash error', 4: 'RAM error', 5: 'Low battery',
  6: 'Watchdog reset', 7: 'RTC oscillator error',
  8: 'MCU oscillator error', 9: 'RTC reset', 10: 'Main relay error',
  11: 'Relay disconnected', 16: 'Current reversal L1',
  17: 'Current reversal L2', 18: 'Current reversal L3',
  19: 'Current unbalance', 20: 'Main cover open',
  21: 'Terminal cover open', 22: 'Comm module removed',
  23: 'Meter dismantle', 24: 'Strong DC magnetic field',
  25: 'Phase sequence reversal', 26: 'Voltage missing L1',
  27: 'Voltage missing L2', 28: 'Voltage missing L3',
  29: 'Over temperature', 30: 'High voltage',
  31: 'Phase & neutral handling',
};

// OBIS codes whose value is a status bitmask, not a measurement.
// Daily, monthly and power-quality profiles all carry the meter's
// internal-status word at midnight (in different OBIS slots per
// profile, per CAS §13.1 / §14.2).
const STATUS_OBIS_SET = new Set([
  '0.0.96.10.1.255', // monthly profile status
  '0.0.96.10.2.255', // power-quality profile status
  '0.0.96.10.3.255', // daily profile status
]);

// Profile OBIS codes whose columns must render as raw meter values —
// no fraction_digits divisor and no CT×VT multiplier. Load Profile
// (demand columns in W / var / VA) and Power Quality (voltages,
// currents, PF, frequency) are instrument-side measurements that
// operators compare to the meter LCD and the analyser, so any
// scaling corrupts the value. Daily / monthly billing profiles stay
// CT×VT-aware because those registers ARE primary-side cumulative
// energy counters.
const RAW_PROFILE_OBIS_SET = new Set([
  '1.0.99.1.0.255', // load profile
  '1.0.99.1.2.255', // power quality
]);

function decodeStatusWord(val) {
  const n = typeof val === 'number' ? val : parseInt(val, 10);
  if (isNaN(n)) return null;
  if (n === 0) return [];
  const flags = [];
  for (const [bit, name] of Object.entries(STATUS_WORD_BITS)) {
    if (n & (1 << Number(bit))) flags.push(name);
  }
  return flags;
}

// Render a status-word value as a compact cell that never overflows
// the table. Zero → "No Status" (muted). Non-zero → "See Status"
// pill that opens a modal with the bit-by-bit breakdown so the
// operator can read each active flag without the row blowing up
// to a 300-pixel-wide comma-joined string. Falls back to the hex
// fingerprint when the bits don't map to our table so anomalies
// are still visible.
function StatusCell({ value }) {
  const [open, setOpen] = React.useState(false);
  const c = useThemeColors();
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  if (isNaN(n)) return <span>{String(value)}</span>;
  if (n === 0) {
    return <span style={{ color: c.textMuted, fontSize: '11px' }}>No Status</span>;
  }
  const flags = decodeStatusWord(n) || [];
  const hexTag = `0x${n.toString(16).toUpperCase()}`;
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          background: 'transparent',
          border: `1px solid ${c.accent}`,
          color: c.accent,
          borderRadius: '4px',
          padding: '2px 8px',
          fontSize: '11px',
          cursor: 'pointer',
          fontWeight: 600,
        }}
        title={`Raw: ${n} (${hexTag})`}
      >
        See Status ({flags.length || '?'})
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9600,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: c.bgCard, border: `1px solid ${c.border}`,
              borderRadius: '10px', padding: '20px 22px',
              minWidth: '340px', maxWidth: '520px',
              boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
              fontSize: '13px',
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px', color: c.textPrimary }}>
              Meter status word
            </div>
            <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '12px' }}>
              Raw value: <code>{n}</code> · {hexTag} · {flags.length} active flag(s)
            </div>
            {flags.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {flags.map((label, i) => (
                  <li key={i} style={{
                    padding: '6px 10px', background: c.bgCardAlt,
                    border: `1px solid ${c.border}`, borderRadius: '4px',
                    color: c.textPrimary,
                  }}>
                    {label}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ padding: '10px', color: c.textMuted, fontStyle: 'italic' }}>
                No flags mapped — value falls outside the documented
                CAS §18.2 bit table. Check with the meter firmware
                reference.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button
                className="button button-secondary button-sm"
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Primary-side OBIS classification. Canonical source is the backend
// at emeter/api_server.py (_PRIMARY_CT_FIELDS / _VT_FIELDS /
// _CTVT_FIELDS). The FE fetches /api/profile-types once at app
// start and overwrites these defaults with the server's sets — so
// adding an OBIS group to one side flows to the other with no code
// change here. The hard-coded defaults below are the bootstrap
// layer: they keep the toggle working if the initial fetch is slow
// or fails, and they document the shape the server returns.
//   currents  → ×CT
//   voltages  → ×VT
//   powers/E  → ×CT × VT
//   anything else (frequency, PF, status, time) → ×1
let _PRIMARY_CT_FIELDS = new Set([11, 31, 51, 71, 91]);
let _PRIMARY_VT_FIELDS = new Set([12, 32, 52, 72, 92]);
let _PRIMARY_CTVT_FIELDS = new Set([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
  21, 22, 23, 24, 29, 30,
  41, 42, 43, 44, 49, 50,
  61, 62, 63, 64, 69, 70,
]);

// Fire-and-forget — called once from AuthenticatedApp on mount.
// Silent on error: the defaults above are already correct for every
// CAS meter we ship against today; the fetch just protects against
// future drift.
async function _refreshPrimaryScalingFromServer() {
  try {
    const r = await api('/profile-types');
    const s = r && r.scaling;
    if (!s) return;
    if (Array.isArray(s.ct_fields))   _PRIMARY_CT_FIELDS   = new Set(s.ct_fields);
    if (Array.isArray(s.vt_fields))   _PRIMARY_VT_FIELDS   = new Set(s.vt_fields);
    if (Array.isArray(s.ctvt_fields)) _PRIMARY_CTVT_FIELDS = new Set(s.ctvt_fields);
  } catch (_) { /* keep hard-coded defaults */ }
}

function primaryFactor(obis, ctFactor, vtFactor) {
  if (typeof obis !== 'string') return 1;
  const parts = obis.split('.');
  if (parts.length < 6 || parts[0] !== '1') return 1;
  const c = parseInt(parts[2], 10);
  if (isNaN(c)) return 1;
  if (_PRIMARY_CT_FIELDS.has(c)) return ctFactor;
  if (_PRIMARY_VT_FIELDS.has(c)) return vtFactor;
  if (_PRIMARY_CTVT_FIELDS.has(c)) return ctFactor * vtFactor;
  return 1;
}

// Promote SI energy/power units to their kilo equivalents. Used by
// every live-read display so operators see kWh / kvarh / kVA / kW
// everywhere without per-callsite conversion.
//
// This used to promote only at |value| >= 1000, to keep tiny test-mode
// readings (5 Wh) from becoming "0.005 kWh". That threshold is what made
// the same register read as "19.18 kW" on one meter and "412 W" on the
// next, and the DCU On-Demand panel — which did not promote at all — read
// in W while the direct tab read in kW. Operators compare those numbers
// side by side, so a unit that changes with magnitude is worse than a
// small number with leading zeros. Promotion is now unconditional and
// `formatMetric` below carries the decimals needed to keep small values
// readable.
const _UNIT_PROMOTIONS = {
  Wh:   { unit: 'kWh',   factor: 1000 },
  varh: { unit: 'kvarh', factor: 1000 },
  VAh:  { unit: 'kVAh',  factor: 1000 },
  W:    { unit: 'kW',    factor: 1000 },
  var:  { unit: 'kvar',  factor: 1000 },
  VA:   { unit: 'kVA',   factor: 1000 },
};
function promoteUnit(value, unit) {
  if (typeof value !== 'number' || !unit) return { value, unit: unit || '' };
  const p = _UNIT_PROMOTIONS[unit];
  if (!p) return { value, unit };
  return { value: value / p.factor, unit: p.unit };
}

// The single place a live register value becomes display text. Both On-Demand
// panels (direct meter and DCU-routed) go through this, so the same register
// cannot render as "19.18 kW" in one and "19180 W" in the other.
//
// Decimals scale with magnitude rather than being fixed: after unconditional
// promotion a 2 W reading is 0.002 kW, and a fixed 3-digit cap would round a
// 0.4 W standby load to a flat "0". Large values keep 2-3 decimals; anything
// under 1 gets enough to stay non-zero.
function formatMetric(value, unit) {
  const promoted = promoteUnit(value, unit || '');
  if (typeof promoted.value !== 'number' || !isFinite(promoted.value)) {
    return { text: String(value), unit: promoted.unit || '' };
  }
  const a = Math.abs(promoted.value);
  let maxFrac;
  if (a === 0) maxFrac = 0;
  else if (a >= 100) maxFrac = 2;
  else if (a >= 1) maxFrac = 3;
  else if (a >= 0.001) maxFrac = 4;
  else maxFrac = 6;
  return {
    text: promoted.value.toLocaleString(undefined, { maximumFractionDigits: maxFrac }),
    unit: promoted.unit || '',
  };
}

// Standard CT ratios from the spec — used by transformer + assignment forms.
// Maps the DcuMeterRegForm `meterType` select (0/1/2/3) to the CAS
// spec §7.3 codes (1/3/4/5) the backend's meter_type_override
// expects. Keep in sync with the <option> values in DcuMeterRegForm.
// Used by BOTH handleRegister sites (DcuDetailPane and DcuNodeDetail).
const METER_TYPE_FORM_TO_CAS = { '0': 1, '1': 3, '2': 4, '3': 5 };

// Auto-detect meter type from the 3rd digit of a 12-char Holley serial
// (CAS spec §4.3: 1=1P, 3=3P Direct, 4=3P CT, 5=3P CT/PT, 9=DCU).
// Returns the DcuMeterRegForm-internal form value or '' if the digit
// isn't one of the known meter types (operator falls back to manual).
function _deriveMeterTypeFromSerial(serial) {
  if (!serial || serial.length < 3) return '';
  switch (serial.charAt(2)) {
    case '1': return '0'; // 1P
    case '3': return '1'; // 3P Direct
    case '4': return '2'; // 3P CT
    case '5': return '3'; // 3P CT/VT
    default:  return '';
  }
}

const CT_RATIOS = [
  '5/5', '10/5', '15/5', '20/5', '25/5', '30/5', '40/5', '50/5',
  '75/5', '100/5', '150/5', '200/5', '250/5', '300/5',
  '400/5', '500/5', '600/5', '750/5', '800/5', '1000/5',
  '1200/5', '1250/5', '1500/5', '2000/5', '2500/5', '3000/5',
  '4000/5', '5000/5',
  // 1 A secondaries
  '400/1',
];

// VT (КТН) ratios offered wherever a meter's nameplate is set.
const VT_RATIOS = ['6000/100', '10000/100', '35000/100', '40000/100'];
const vtRatioLabel = (r) => r.replace('/', ' / ') + ' V';

// Tiny FormField primitive — label + input.
function FormField({ label, value, onChange, type = 'text', required = false, placeholder = '', step, autoComplete }) {
  return (
    <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
      {label}
      <input
        type={type}
        className="input-field"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        step={step}
        autoComplete={autoComplete || (type === 'password' ? 'new-password' : undefined)}
      />
    </label>
  );
}

// Save / Cancel button row used by every modal.
function ModalButtons({ busy, onClose, disabled, label }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
      <button type="submit" className="button button-primary" disabled={busy || disabled} style={{ flex: 1 }}>
        {busy ? <Spinner /> : (label || t('btn.create'))}
      </button>
      <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
        {t('btn.cancel')}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Users tab — admin-only user management
// ═══════════════════════════════════════════════════════════════════════

const PERM_GROUP_LABELS = {
  viewing: ['Viewing', 'Просмотр'], meters: ['Meters', 'Счётчики'],
  records: ['Names & records', 'Названия и справочники'], dcu: ['DCU & network', 'DCU и сеть'],
  system: ['System', 'Система'],
};
const permLabel = (p, lang) => (lang === 'ru' && p.label_ru) ? p.label_ru : p.label;
const FLEET_HINT = ['Applies to the whole fleet — the user\'s customer scope does not narrow it',
                    'Действует на весь парк — область доступа пользователя его не ограничивает'];

// Users × permissions matrix (System → Users). Each tick saves at once; ↺
// puts the user back on their role's preset. Admins hold everything and are
// not editable; only an Administrator can change permissions (server-enforced).
function PermissionGrid({ users, catalogue, canEdit, onUserUpdated }) {
  const c = useThemeColors();
  const toast = useToast();
  const { lang } = useLang();
  const ru = lang === 'ru';
  const [busyId, setBusyId] = useState(null);
  const groups = [];
  for (const p of catalogue) {
    const g = groups.find(x => x.key === p.group);
    if (g) g.perms.push(p); else groups.push({ key: p.group, perms: [p] });
  }
  const save = async (u, body) => {
    setBusyId(u.id);
    try {
      const r = await api(`/users/${u.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      onUserUpdated({ ...u, ...r });
    } catch (e) { toast.push(e.message, 'error'); }
    finally { setBusyId(null); }
  };
  const toggle = (u, key) => {
    const has = (u.permissions || []).includes(key);
    const next = has ? u.permissions.filter(k => k !== key) : [...(u.permissions || []), key];
    save(u, { permissions: next });
  };
  const th = { padding: '6px 4px', fontSize: '11px', color: c.textMuted, fontWeight: 600, borderBottom: `1px solid ${c.border}` };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '12px', minWidth: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>{ru ? 'Пользователь' : 'User'}</th>
            <th style={{ ...th, textAlign: 'left' }} rowSpan={2}>{ru ? 'Роль' : 'Role'}</th>
            {groups.map(g => (
              <th key={g.key} colSpan={g.perms.length} style={{ ...th, textAlign: 'center', borderLeft: `1px solid ${c.border}` }}>
                {(PERM_GROUP_LABELS[g.key] || [g.key, g.key])[ru ? 1 : 0]}
              </th>
            ))}
            <th style={th} rowSpan={2}></th>
          </tr>
          <tr>
            {groups.map(g => g.perms.map((p, i) => (
              <th key={p.key} title={p.fleet ? FLEET_HINT[ru ? 1 : 0] : ''}
                  style={{ ...th, fontWeight: 400, minWidth: '74px', verticalAlign: 'bottom',
                           borderLeft: i === 0 ? `1px solid ${c.border}` : undefined }}>
                {permLabel(p, lang)}{p.fleet ? ' 🌐' : ''}
              </th>
            )))}
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const locked = u.role === 'admin' || !canEdit || busyId === u.id;
            return (
              <tr key={u.id} style={{ borderBottom: `1px solid ${c.border}`, opacity: u.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{u.username}</td>
                <td style={{ padding: '6px 8px', fontSize: '11px', textTransform: 'uppercase', color: c.textMuted }}>
                  {u.role}{u.permissions_custom ? (ru ? ' · изменено' : ' · custom') : ''}
                </td>
                {groups.map(g => g.perms.map((p, i) => (
                  <td key={p.key} style={{ textAlign: 'center', borderLeft: i === 0 ? `1px solid ${c.border}` : undefined }}>
                    <input type="checkbox" checked={(u.permissions || []).includes(p.key)}
                      disabled={locked} onChange={() => toggle(u, p.key)} />
                  </td>
                )))}
                <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                  {busyId === u.id ? <Spinner /> : (u.permissions_custom && canEdit && (
                    <button className="button button-secondary" style={{ padding: '2px 8px', fontSize: '11px' }}
                      title={ru ? 'Вернуть набор роли' : "Back to the role's preset"}
                      onClick={() => save(u, { reset_permissions: true })}>↺</button>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '8px' }}>
        🌐 {FLEET_HINT[ru ? 1 : 0]}.
      </div>
    </div>
  );
}

// Grouped checkboxes for one user (New / Edit user dialog).
function PermissionChecklist({ catalogue, value, locked, onChange }) {
  const c = useThemeColors();
  const { lang } = useLang();
  const ru = lang === 'ru';
  const groups = [];
  for (const p of catalogue) {
    const g = groups.find(x => x.key === p.group);
    if (g) g.perms.push(p); else groups.push({ key: p.group, perms: [p] });
  }
  const toggle = (k) => onChange(value.includes(k) ? value.filter(x => x !== k) : [...value, k]);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '12px' }}>
      {groups.map(g => (
        <div key={g.key}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', marginBottom: '4px' }}>
            {(PERM_GROUP_LABELS[g.key] || [g.key, g.key])[ru ? 1 : 0]}
          </div>
          {g.perms.map(p => (
            <label key={p.key} title={p.fleet ? FLEET_HINT[ru ? 1 : 0] : ''}
                   style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', marginBottom: '3px' }}>
              <input type="checkbox" checked={value.includes(p.key)} disabled={locked} onChange={() => toggle(p.key)} />
              {permLabel(p, lang)}{p.fleet ? ' 🌐' : ''}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const c = useThemeColors();
  const t = useT();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const toast = useToast();
  const { assignments, refreshAssignments } = useAssignments();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transformers, setTransformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [permCatalogue, setPermCatalogue] = useState(null);   // { permissions, presets }
  const [view, setView] = useState('list');                   // list | permissions

  useEffect(() => {
    if (!userCan(me, 'manage_users')) return;
    setLoading(true);
    Promise.all([
      api('/users').then(d => d.users || []),
      api('/customers').then(d => d.customers || []),
      refreshAssignments({ force: refreshKey > 0 }),
      api('/transformers').then(d => d.transformers || []),
      api('/permissions').catch(() => null),
    ])
      .then(([us, cs, _as, ts, pc]) => { setUsers(us); setCustomers(cs); setTransformers(ts); setPermCatalogue(pc); })
      .catch(err => toast.push(err.message, 'error'))
      .finally(() => setLoading(false));
  }, [refreshKey, me?.permissions, refreshAssignments]);

  const refresh = () => setRefreshKey(k => k + 1);

  const deleteUser = async (u) => {
    if (u.id === me.id) return toast.push(t('toast.refuse_delete_self'), 'error');
    if (!(await confirm(`Delete user "${u.username}"?`, 'This action cannot be undone. The user will lose all access immediately.'))) return;
    try {
      await api(`/users/${u.id}`, { method: 'DELETE' });
      toast.push(t('toast.user_deleted'), 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  if (!userCan(me, 'manage_users')) {
    return (
      <div className="tab-content">
        <div className="section-title">👥 {t('section.users')}</div>
        <div style={{ padding: '40px', textAlign: 'center', color: c.warning }}>
          ⛔ Admin role required.
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="section-title">👥 {t('section.users')}</div>
      <div className="subtab-bar" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button className="button button-primary" onClick={() => setShowCreate(true)}>➕ New user</button>
        <button className="button button-secondary" onClick={refresh}>🔄 Refresh</button>
        {permCatalogue && (
          <button className="button button-secondary" onClick={() => setView(v => v === 'list' ? 'permissions' : 'list')}>
            {view === 'list' ? '🔐 Permissions grid' : '👥 User list'}
          </button>
        )}
      </div>
      {loading ? <Spinner /> : view === 'permissions' && permCatalogue ? (
        <PermissionGrid
          users={users}
          catalogue={permCatalogue.permissions}
          canEdit={me?.role === 'admin'}
          onUserUpdated={(nu) => setUsers(prev => prev.map(x => x.id === nu.id ? { ...x, ...nu } : x))}
        />
      ) : (
        <table style={{ width: '100%', fontSize: '13px' }}>
          <thead>
            <tr style={{ color: c.textSecondary, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
              <th style={{ padding: '8px' }}>Username</th>
              <th style={{ padding: '8px' }}>Full name</th>
              <th style={{ padding: '8px' }}>Role</th>
              <th style={{ padding: '8px' }}>Active</th>
              <th style={{ padding: '8px' }}>Customers</th>
              <th style={{ padding: '8px' }}>Last login</th>
              <th style={{ padding: '8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                <td style={{ padding: '8px', fontFamily: 'monospace' }}>{u.username}</td>
                <td style={{ padding: '8px' }}>{u.full_name || '—'}</td>
                <td style={{ padding: '8px' }}>
                  <span style={{
                    background: u.role === 'admin' ? 'rgba(168, 85, 247, 0.15)' : u.role === 'operator' ? 'rgba(0, 229, 255, 0.15)' : 'rgba(100, 116, 139, 0.15)',
                    color: u.role === 'admin' ? '#a855f7' : u.role === 'operator' ? '#00e5ff' : '#94a3b8',
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px', textTransform: 'uppercase',
                  }}>
                    {u.role}
                  </span>
                </td>
                <td style={{ padding: '8px' }}>{u.is_active ? '✓' : '✗'}</td>
                <td style={{ padding: '8px' }}>
                  {u.role === 'admin' ? <span style={{ color: c.textMuted }}>all</span>
                    : u.customer_ids?.length ? `${u.customer_ids.length} scoped` : <span style={{ color: c.textDimmed }}>none</span>}
                </td>
                <td style={{ padding: '8px', color: c.textMuted }}>{fmtDT(u.last_login_at, { utc: true }) || '—'}</td>
                <td style={{ padding: '8px' }}>
                  <button className="button button-secondary" onClick={() => setEditingUser(u)} style={{ padding: '4px 8px', fontSize: '11px' }}>✏️</button>
                  {u.id !== me.id && (
                    <button className="button button-secondary" onClick={() => deleteUser(u)} style={{ padding: '4px 8px', fontSize: '11px', marginLeft: '4px' }}>🗑</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showCreate && (
        <UserFormModal permCatalogue={permCatalogue} canEditPerms={me?.role === 'admin'} customers={customers} assignments={assignments} transformers={transformers} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refresh(); }} />
      )}
      {editingUser && (
        <UserFormModal user={editingUser} permCatalogue={permCatalogue} canEditPerms={me?.role === 'admin'} customers={customers} assignments={assignments} transformers={transformers} onClose={() => setEditingUser(null)} onSaved={() => { setEditingUser(null); refresh(); }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// API Keys admin — issue / list / revoke API keys for service users.
//
// Each key is bound to a User via user_id. That user's customer +
// per-meter access (UserCustomerAccess / UserMeterAccess) is the
// scope for the key. To restrict an API key to a subset of meters:
// (1) create a user with role=viewer or operator, (2) set their
// customer/meter scope from the Users tab, (3) issue an API key
// for that user here.
// ─────────────────────────────────────────────────────────────
function ApiKeysAdminTab() {
  const c = useThemeColors();
  const toast = useToast();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const [keys, setKeys] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [newKeyClear, setNewKeyClear] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    if (!userCan(me, 'manage_users')) return;
    setLoading(true);
    Promise.all([
      api('/api-keys'),
      api('/users').then(d => d.users || []),
    ])
      .then(([ks, us]) => { setKeys(ks); setUsers(us); })
      .catch(err => toast.push(err.message, 'error'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);

  const revoke = async (k) => {
    if (!(await confirm(`Revoke key "${k.label}" (id ${k.id})?`,
      'Subsequent requests with this key will return 401. Cannot be undone — operator must issue a new key.'))) return;
    try {
      await api(`/api-keys/${k.id}`, { method: 'DELETE' });
      toast.push('Key revoked', 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  const hardDelete = async (k) => {
    if (!(await confirm(`Permanently delete key "${k.label}" (id ${k.id})?`,
      'This removes the api_keys row entirely. Cannot be undone. Use Revoke instead if you might need to inspect this key later — Revoke keeps the row + history; Delete only keeps the audit trail (without the bound-key link).'))) return;
    try {
      await api(`/api-keys/${k.id}?permanent=true`, { method: 'DELETE' });
      toast.push('Key permanently deleted', 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  if (!userCan(me, 'manage_users')) {
    return <div style={{ color: c.warning, padding: '40px', textAlign: 'center' }}>⛔ Admin role required.</div>;
  }

  const userById = Object.fromEntries(users.map(u => [u.id, u]));

  return (
    <div className="tab-content">
      <div className="section-title">🔌 API Keys</div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '16px', maxWidth: '900px' }}>
        Each API key is bound to a user account. The user's customer + per-meter
        scope determines what the key can read via <code>/api/v1/*</code>. To
        restrict a key to a subset of customers or meters, configure that scope
        on the user from the <strong>Users</strong> tab first, then issue a key here.
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button className="button button-primary" onClick={() => { setShowCreate(true); setNewKeyClear(null); }}>
          ➕ Issue new key
        </button>
        <button className="button button-secondary" onClick={refresh}>🔄 Refresh</button>
      </div>

      {newKeyClear && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: `1px solid rgba(34, 197, 94, 0.4)`,
          borderRadius: '6px', padding: '14px', marginBottom: '14px',
        }}>
          <div style={{ fontWeight: 600, color: c.success, marginBottom: '6px' }}>
            ✓ New key issued — copy it NOW. It will never be shown again.
          </div>
          <code style={{
            display: 'block', padding: '8px 10px', background: c.bgBase,
            border: `1px solid ${c.border}`, borderRadius: '4px',
            fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all',
          }}>{newKeyClear}</code>
          <button className="button button-sm button-secondary" style={{ marginTop: '8px' }}
            onClick={() => { navigator.clipboard?.writeText(newKeyClear); toast.push('Copied to clipboard', 'success'); }}>
            📋 Copy to clipboard
          </button>
          <button className="button button-sm button-secondary" style={{ marginTop: '8px', marginLeft: '6px' }}
            onClick={() => setNewKeyClear(null)}>Dismiss</button>
        </div>
      )}

      {loading ? <Spinner /> : (
        <table style={{ width: '100%', fontSize: '12px' }}>
          <thead>
            <tr style={{ color: c.textSecondary, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
              <th style={{ padding: '8px' }}>Label</th>
              <th style={{ padding: '8px' }}>Prefix</th>
              <th style={{ padding: '8px' }}>Bound user</th>
              <th style={{ padding: '8px' }}>Scope</th>
              <th style={{ padding: '8px' }}>Created</th>
              <th style={{ padding: '8px' }}>Last used</th>
              <th style={{ padding: '8px' }}>State</th>
              <th style={{ padding: '8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: c.textMuted, fontStyle: 'italic' }}>
                No API keys issued yet. Click "Issue new key" to create one.
              </td></tr>
            )}
            {keys.map(k => {
              const u = userById[k.user_id];
              const revoked = !!k.revoked_at;
              return (
                <tr key={k.id} style={{ borderBottom: `1px solid ${c.border}`, opacity: revoked ? 0.5 : 1 }}>
                  <td style={{ padding: '8px' }}>{k.label || <span style={{ color: c.textDimmed }}>—</span>}</td>
                  <td style={{ padding: '8px', fontFamily: 'monospace' }}>{k.prefix}…</td>
                  <td style={{ padding: '8px' }}>
                    {u ? <>
                      <span style={{ fontFamily: 'monospace' }}>{u.username}</span>
                      <span style={{ color: c.textMuted, marginLeft: '6px', fontSize: '11px' }}>({u.role})</span>
                    </> : <span style={{ color: c.danger }}>missing user (id {k.user_id})</span>}
                  </td>
                  <td style={{ padding: '8px', fontSize: '11px', color: c.textMuted }}>
                    {!u ? '—' : u.role === 'admin' ? 'all customers / all meters'
                       : u.customer_ids?.length ? `${u.customer_ids.length} customer(s)` : 'none'}
                  </td>
                  <td style={{ padding: '8px', color: c.textMuted, fontSize: '11px' }}>
                    {fmtDT(k.created_at, { utc: true }) || '—'}
                  </td>
                  <td style={{ padding: '8px', color: c.textMuted, fontSize: '11px' }}>
                    {fmtDT(k.last_used_at, { utc: true }) || <span style={{ color: c.textDimmed }}>never</span>}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {revoked ? <span style={{ color: c.danger, fontSize: '11px' }}>● Revoked</span>
                      : <span style={{ color: c.success, fontSize: '11px' }}>● Active</span>}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {!revoked && (
                      <button className="button button-sm button-secondary"
                        onClick={() => revoke(k)}
                        style={{ padding: '4px 8px', fontSize: '11px', marginRight: '4px' }}>🗑 Revoke</button>
                    )}
                    <button className="button button-sm"
                      onClick={() => hardDelete(k)}
                      title="Permanently delete this key row (irreversible)"
                      style={{
                        padding: '4px 8px', fontSize: '11px',
                        background: 'transparent',
                        border: `1px solid ${c.danger}`,
                        color: c.danger,
                      }}>✕ Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showCreate && (
        <ApiKeyCreateModal
          users={users}
          onClose={() => setShowCreate(false)}
          onCreated={(cleartext) => { setShowCreate(false); setNewKeyClear(cleartext); refresh(); }}
        />
      )}
    </div>
  );
}

function ApiKeyCreateModal({ users, onClose, onCreated }) {
  const c = useThemeColors();
  const toast = useToast();
  const [label, setLabel] = React.useState('');
  const [userId, setUserId] = React.useState('');
  const [rateLimit, setRateLimit] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!userId) { toast.push('Pick a user to bind the key to', 'error'); return; }
    setBusy(true);
    try {
      const body = {
        user_id: parseInt(userId, 10),
        label: label || null,
      };
      if (rateLimit) body.rate_limit_rpm = parseInt(rateLimit, 10);
      const r = await api('/api-keys', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!r?.key) {
        toast.push('Key created but cleartext missing in response', 'error');
        return;
      }
      onCreated(r.key);
    } catch (err) {
      toast.push(`Create failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '12px', padding: '24px', width: '500px', maxWidth: '90vw',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
          Issue new API key
        </div>

        <label className="input-label">
          Bind to user
          <select className="input-field" value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">— pick one —</option>
            {users.filter(u => u.is_active).map(u => (
              <option key={u.id} value={u.id}>
                {u.username} ({u.role}{u.role !== 'admin' && u.customer_ids?.length ? ` · ${u.customer_ids.length} customer(s)` : ''})
              </option>
            ))}
          </select>
        </label>

        <label className="input-label">
          Label (free text, shows in the key list)
          <input type="text" className="input-field" value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="e.g. BigBag billing integration" />
        </label>

        <label className="input-label">
          Rate limit (requests per minute, optional — default 60)
          <input type="number" className="input-field" value={rateLimit}
            onChange={e => setRateLimit(e.target.value)}
            placeholder="60" min="1" max="3000" />
        </label>

        <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '6px' }}>
          The key's scope is <strong>inherited from the bound user</strong>:
          their customer access (Users tab → Customers) and per-meter access
          (Users tab → Meters) determine what the key can read via
          <code> /api/v1/*</code>. Admin users grant unrestricted access.
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button className="button button-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button button-primary" onClick={submit} disabled={busy || !userId}>
            {busy ? <Spinner /> : 'Issue key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// API Management top-level tab. Visible to all roles. Two sub-
// sections:
//   * 🔑 My API Keys  — admin + developer self-service: issue /
//     list / revoke their own keys. Keys inherit the user's
//     customer + meter scope, so this tab is safe for non-admins.
//   * 📖 API Reference — the public v1 documentation. Visible to
//     every role so an operator or viewer can hand the link to a
//     developer they're integrating with.
// Renamed from "Documentation" 2026-04-26 once it grew the
// self-service key-issuance UI.
// ─────────────────────────────────────────────────────────────
function ApiManagementTab() {
  const c = useThemeColors();
  const t = useT();
  const { user } = useAuth();
  const canManageOwnKeys = userCan(user, 'api_keys');
  // Default landing for users who CAN manage keys is the keys tab —
  // they came here to do that, not to read the reference. Viewers /
  // operators (who only see the Reference) land on docs.
  const [sub, setSub] = React.useState(canManageOwnKeys ? 'mykeys' : 'docs');

  const tabBtnStyle = (active) => ({
    padding: '8px 16px',
    background: active ? c.bgElevated : 'transparent',
    color: active ? c.accent : c.textSecondary,
    border: 'none',
    borderBottom: active ? `2px solid ${c.accent}` : '2px solid transparent',
    fontSize: '14px', fontWeight: active ? 600 : 400,
    cursor: 'pointer', marginBottom: '-1px',
  });

  return (
    <div className="tab-content">
      <div className="section-title">📘 {t('tab.api_management') || 'API Management'}</div>

      <div className="subtab-bar" style={{
        display: 'flex', gap: '4px', marginBottom: '16px',
        borderBottom: `1px solid ${c.border}`, flexWrap: 'wrap',
      }}>
        {canManageOwnKeys && (
          <button style={tabBtnStyle(sub === 'mykeys')} onClick={() => setSub('mykeys')}>{t('apim.subtab_keys')}</button>
        )}
        <button style={tabBtnStyle(sub === 'docs')}    onClick={() => setSub('docs')}>{t('apim.subtab_docs')}</button>
      </div>

      {sub === 'docs'   && <ApiDocsTab />}
      {sub === 'mykeys' && canManageOwnKeys && <MyApiKeysSelfService />}
    </div>
  );
}

// Self-service API key panel for developer (own-keys-only) and admin
// (sees own keys only on this tab — admins still have the fleet-wide
// view under System → API Keys). Backend filters to own keys for
// the developer role; admins can see/manage all from System.
function MyApiKeysSelfService() {
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { user: me } = useAuth();
  const [keys, setKeys] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [showCreate, setShowCreate] = React.useState(false);
  const [newKeyClear, setNewKeyClear] = React.useState(null);

  React.useEffect(() => {
    setLoading(true);
    api('/api-keys')
      .then(d => {
        // Admin call returns ALL keys; we filter client-side to "mine"
        // so this self-service view is consistent across roles.
        setKeys((d || []).filter(k => k.user_id === me?.id));
      })
      .catch(err => toast.push(err.message, 'error'))
      .finally(() => setLoading(false));
  }, [refreshKey, me?.id]);

  const refresh = () => setRefreshKey(k => k + 1);

  const create = async (label) => {
    try {
      const r = await api('/api-keys', {
        method: 'POST',
        body: JSON.stringify({ user_id: me.id, label: label || null }),
      });
      if (!r?.key) {
        toast.push('Created but cleartext missing in response', 'error');
        return;
      }
      setNewKeyClear(r.key);
      setShowCreate(false);
      refresh();
    } catch (err) {
      toast.push(`Create failed: ${err.message}`, 'error');
    }
  };

  const revoke = async (k) => {
    if (!(await confirm(t('apim.confirm_revoke_q'), t('apim.confirm_revoke_b')))) return;
    try {
      await api(`/api-keys/${k.id}`, { method: 'DELETE' });
      toast.push(t('apim.toast_revoked'), 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  const hardDelete = async (k) => {
    if (!(await confirm(t('apim.confirm_delete_q'), t('apim.confirm_delete_b')))) return;
    try {
      await api(`/api-keys/${k.id}?permanent=true`, { method: 'DELETE' });
      toast.push(t('apim.toast_deleted'), 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  return (
    <div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '14px', maxWidth: '800px' }}>
        {t('apim.intro')}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <button className="button button-primary" onClick={() => { setShowCreate(true); setNewKeyClear(null); }}>
          {t('apim.btn_issue')}
        </button>
        <button className="button button-secondary" onClick={refresh}>{t('apim.btn_refresh')}</button>
      </div>

      {newKeyClear && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: `1px solid rgba(34, 197, 94, 0.4)`,
          borderRadius: '6px', padding: '14px', marginBottom: '14px',
        }}>
          <div style={{ fontWeight: 600, color: c.success, marginBottom: '6px' }}>
            {t('apim.flash_issued')}
          </div>
          <code style={{
            display: 'block', padding: '8px 10px', background: c.bgBase,
            border: `1px solid ${c.border}`, borderRadius: '4px',
            fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all',
          }}>{newKeyClear}</code>
          <div style={{ marginTop: '8px' }}>
            <button className="button button-sm button-secondary"
              onClick={() => { navigator.clipboard?.writeText(newKeyClear); toast.push(t('apim.toast_copied'), 'success'); }}>
              {t('apim.btn_copy')}
            </button>
            <button className="button button-sm button-secondary" style={{ marginLeft: '6px' }}
              onClick={() => setNewKeyClear(null)}>{t('apim.btn_dismiss')}</button>
          </div>
        </div>
      )}

      {showCreate && (
        <MyApiKeyCreateModal onClose={() => setShowCreate(false)} onSubmit={create} />
      )}

      {loading ? <Spinner /> : (
        <table style={{ width: '100%', fontSize: '12px' }}>
          <thead>
            <tr style={{ color: c.textSecondary, textAlign: 'left', borderBottom: `1px solid ${c.border}` }}>
              <th style={{ padding: '8px' }}>{t('apim.col_label')}</th>
              <th style={{ padding: '8px' }}>{t('apim.col_prefix')}</th>
              <th style={{ padding: '8px' }}>{t('apim.col_created')}</th>
              <th style={{ padding: '8px' }}>{t('apim.col_last_used')}</th>
              <th style={{ padding: '8px' }}>{t('apim.col_state')}</th>
              <th style={{ padding: '8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: c.textMuted, fontStyle: 'italic' }}>
                {t('apim.empty_keys')}
              </td></tr>
            )}
            {keys.map(k => {
              const revoked = !!k.revoked_at;
              return (
                <tr key={k.id} style={{ borderBottom: `1px solid ${c.border}`, opacity: revoked ? 0.5 : 1 }}>
                  <td style={{ padding: '8px' }}>{k.label || <span style={{ color: c.textDimmed }}>—</span>}</td>
                  <td style={{ padding: '8px', fontFamily: 'monospace' }}>{k.prefix}…</td>
                  <td style={{ padding: '8px', color: c.textMuted, fontSize: '11px' }}>
                    {fmtDT(k.created_at, { utc: true }) || '—'}
                  </td>
                  <td style={{ padding: '8px', color: c.textMuted, fontSize: '11px' }}>
                    {fmtDT(k.last_used_at, { utc: true }) || <span style={{ color: c.textDimmed }}>{t('apim.cell_never')}</span>}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {revoked ? <span style={{ color: c.danger, fontSize: '11px' }}>{t('apim.cell_revoked')}</span>
                      : <span style={{ color: c.success, fontSize: '11px' }}>{t('apim.cell_active')}</span>}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {!revoked && (
                      <button className="button button-sm button-secondary"
                        onClick={() => revoke(k)}
                        style={{ padding: '4px 8px', fontSize: '11px', marginRight: '4px' }}>{t('apim.btn_revoke')}</button>
                    )}
                    <button className="button button-sm"
                      onClick={() => hardDelete(k)}
                      title={t('apim.confirm_delete_b')}
                      style={{
                        padding: '4px 8px', fontSize: '11px',
                        background: 'transparent',
                        border: `1px solid ${c.danger}`,
                        color: c.danger,
                      }}>{t('apim.btn_delete')}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function MyApiKeyCreateModal({ onClose, onSubmit }) {
  const c = useThemeColors();
  const [label, setLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(label); }
    finally { setBusy(false); }
  };
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`,
        borderRadius: '12px', padding: '24px', width: '440px', maxWidth: '90vw',
      }}>
        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '14px' }}>
          Issue new API key
        </div>
        <label className="input-label">
          Label (free text — shows in the key list)
          <input type="text" className="input-field" value={label}
            onChange={e => setLabel(e.target.value)} autoFocus
            placeholder="e.g. BI dashboard integration" />
        </label>
        <div style={{ fontSize: '11px', color: c.textMuted, marginTop: '6px' }}>
          The key inherits your customer / meter scope automatically.
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button className="button button-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="button button-primary" onClick={submit} disabled={busy}>
            {busy ? <Spinner /> : 'Issue key'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// API Docs — renders the v1 reference Markdown from the backend.
// Stable contract for third-party clients; visible to admins so they
// can share the URL or contents with API consumers.
// ─────────────────────────────────────────────────────────────
function ApiDocsTab() {
  const c = useThemeColors();
  const { lang } = useLang();
  const [md, setMd] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  // Re-fetch when the dashboard's EN/RU toggle flips. Backend serves
  // ``external_api.md`` for ``lang=en`` and ``external_api_ru.md``
  // for ``lang=ru``; missing translations fall back to English on
  // the server side.
  React.useEffect(() => {
    setLoading(true);
    fetch(`/api/v1/docs?lang=${encodeURIComponent(lang || 'en')}`)
      .then(r => r.text())
      .then(setMd)
      .catch(e => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [lang]);

  // Tiny inline Markdown renderer — handles headings, code blocks
  // (fenced), inline code, lists, tables, links, paragraphs. Good
  // enough for our reference doc; no third-party dep.
  const rendered = React.useMemo(() => {
    if (!md) return null;
    const lines = md.split('\n');
    const out = [];
    let i = 0;
    let key = 0;
    while (i < lines.length) {
      const line = lines[i];
      // Fenced code block — supports indented opening fence (e.g.
      // a code block nested inside a numbered list item).
      const fenceMatch = /^(\s*)```(.*)$/.exec(line);
      if (fenceMatch) {
        const indent = fenceMatch[1].length;
        const lang = fenceMatch[2].trim();
        const buf = [];
        i++;
        while (i < lines.length && !/^\s*```/.test(lines[i])) {
          // Strip the same leading indent the opening fence had,
          // so indented blocks render flush-left in <pre>.
          const raw = lines[i];
          buf.push(raw.startsWith(' '.repeat(indent)) ? raw.slice(indent) : raw);
          i++;
        }
        i++; // skip closing fence
        out.push(<pre key={key++} style={{
          background: c.bgBase, border: `1px solid ${c.border}`,
          borderRadius: '6px', padding: '12px',
          overflow: 'auto', fontSize: '12px',
          fontFamily: 'monospace', margin: '8px 0',
        }}>
          {lang && <div style={{ color: c.textDimmed, fontSize: '10px', marginBottom: '6px' }}>{lang}</div>}
          <code>{buf.join('\n')}</code>
        </pre>);
        continue;
      }
      // Heading
      const h = /^(#{1,6})\s+(.*)$/.exec(line);
      if (h) {
        const lvl = h[1].length;
        const sizes = { 1: '22px', 2: '18px', 3: '15px', 4: '13px', 5: '12px', 6: '11px' };
        out.push(React.createElement(`h${lvl}`, {
          key: key++,
          style: {
            fontSize: sizes[lvl], color: c.textPrimary,
            margin: lvl <= 2 ? '20px 0 10px' : '14px 0 6px',
            fontWeight: 600,
          },
        }, h[2]));
        i++; continue;
      }
      // Table block (`|` columns + a separator row)
      if (line.startsWith('|') && i + 1 < lines.length && /^\|\s*-+/.test(lines[i+1])) {
        const headers = line.split('|').slice(1, -1).map(s => s.trim());
        const rows = [];
        i += 2;
        while (i < lines.length && lines[i].startsWith('|')) {
          rows.push(lines[i].split('|').slice(1, -1).map(s => s.trim()));
          i++;
        }
        out.push(<table key={key++} style={{ borderCollapse: 'collapse', margin: '10px 0', fontSize: '12px' }}>
          <thead><tr>{headers.map((h, hi) => (
            <th key={hi} style={{ border: `1px solid ${c.border}`, padding: '6px 10px', background: c.bgCardAlt, textAlign: 'left' }}>{h}</th>
          ))}</tr></thead>
          <tbody>{rows.map((r, ri) => (
            <tr key={ri}>{r.map((cell, ci) => (
              <td key={ci} style={{ border: `1px solid ${c.border}`, padding: '6px 10px' }}>
                {cell.split(/(`[^`]+`)/g).map((part, pi) =>
                  part.startsWith('`')
                    ? <code key={pi} style={{ background: c.bgBase, padding: '1px 4px', borderRadius: '3px', fontSize: '11px' }}>{part.slice(1, -1)}</code>
                    : part)}
              </td>
            ))}</tr>
          ))}</tbody>
        </table>);
        continue;
      }
      // Helper — render inline `code` spans + bold **text** in a string.
      const renderInline = (s) => {
        // Bold first, then code so code wraps text() not the asterisks.
        const boldSplit = s.split(/(\*\*[^*]+\*\*)/g);
        return boldSplit.flatMap((seg, si) => {
          if (seg.startsWith('**') && seg.endsWith('**')) {
            return [<strong key={`b${si}`}>{seg.slice(2, -2)}</strong>];
          }
          return seg.split(/(`[^`]+`)/g).map((part, pi) =>
            part.startsWith('`')
              ? <code key={`b${si}c${pi}`} style={{ background: c.bgBase, padding: '1px 4px', borderRadius: '3px', fontSize: '12px' }}>{part.slice(1, -1)}</code>
              : part);
        });
      };

      // Unordered list — `-` or `*`
      if (/^\s*[-*]\s+/.test(line)) {
        const items = [];
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
          i++;
        }
        out.push(<ul key={key++} style={{ margin: '6px 0 6px 20px', fontSize: '13px', lineHeight: 1.6 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ margin: '4px 0' }}>{renderInline(it)}</li>
          ))}
        </ul>);
        continue;
      }
      // Ordered list — `1.`, `2.`, etc. Each item may span multiple
      // input lines (indented continuation) AND may contain a nested
      // fenced code block. Collect items; render code blocks inline
      // inside the corresponding <li>.
      if (/^\s*\d+\.\s+/.test(line)) {
        const items = []; // each item is { text: string, blocks: ReactNode[] }
        while (i < lines.length && (/^\s*\d+\.\s+/.test(lines[i]) || /^\s+/.test(lines[i]) || lines[i].trim() === '')) {
          if (/^\s*\d+\.\s+/.test(lines[i])) {
            items.push({ text: lines[i].replace(/^\s*\d+\.\s+/, ''), blocks: [] });
            i++;
          } else if (lines[i].trim() === '') {
            // Blank line — peek at the next non-blank to decide if
            // we're still inside the list. If next line is another
            // numbered item OR an indented continuation, keep going.
            const lookahead = lines.slice(i + 1).find(l => l.trim() !== '');
            if (lookahead && (/^\s*\d+\.\s+/.test(lookahead) || /^\s+/.test(lookahead))) {
              i++; continue;
            }
            break;
          } else if (/^\s+/.test(lines[i])) {
            // Indented continuation — could be a code block or
            // continuation prose for the current item.
            const indentFence = /^(\s*)```(.*)$/.exec(lines[i]);
            if (indentFence && items.length) {
              const indent = indentFence[1].length;
              const lang = indentFence[2].trim();
              const buf = [];
              i++;
              while (i < lines.length && !/^\s*```/.test(lines[i])) {
                const raw = lines[i];
                buf.push(raw.startsWith(' '.repeat(indent)) ? raw.slice(indent) : raw);
                i++;
              }
              i++;
              items[items.length - 1].blocks.push(
                <pre key={`it-${items.length}-pre`} style={{
                  background: c.bgBase, border: `1px solid ${c.border}`,
                  borderRadius: '6px', padding: '10px',
                  overflow: 'auto', fontSize: '12px',
                  fontFamily: 'monospace', margin: '6px 0',
                }}>
                  {lang && <div style={{ color: c.textDimmed, fontSize: '10px', marginBottom: '6px' }}>{lang}</div>}
                  <code>{buf.join('\n')}</code>
                </pre>
              );
            } else if (items.length) {
              // Append continuation text to the last item's prose.
              items[items.length - 1].text += ' ' + lines[i].trim();
              i++;
            } else {
              break;
            }
          } else {
            break;
          }
        }
        out.push(<ol key={key++} style={{ margin: '8px 0 8px 24px', fontSize: '13px', lineHeight: 1.6 }}>
          {items.map((it, idx) => (
            <li key={idx} style={{ margin: '8px 0' }}>
              <div>{renderInline(it.text)}</div>
              {it.blocks}
            </li>
          ))}
        </ol>);
        continue;
      }
      // Horizontal rule
      if (/^---+\s*$/.test(line)) {
        out.push(<hr key={key++} style={{ border: 'none', borderTop: `1px solid ${c.border}`, margin: '16px 0' }} />);
        i++; continue;
      }
      // Blank line
      if (line.trim() === '') { i++; continue; }
      // Paragraph (collect until blank or special line)
      const para = [line];
      i++;
      while (i < lines.length && lines[i].trim() !== ''
             && !lines[i].startsWith('#') && !/^\s*```/.test(lines[i])
             && !lines[i].startsWith('|') && !/^\s*[-*]\s+/.test(lines[i])
             && !/^\s*\d+\.\s+/.test(lines[i]) && !/^---+\s*$/.test(lines[i])) {
        para.push(lines[i]); i++;
      }
      out.push(<p key={key++} style={{ margin: '6px 0', fontSize: '13px', lineHeight: 1.6, color: c.textPrimary }}>
        {renderInline(para.join(' '))}
      </p>);
    }
    return out;
  }, [md, c]);

  return (
    <div className="tab-content">
      <div className="section-title">📘 API Documentation — <code>/api/v1</code></div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '16px' }}>
        Stable HTTP reference for third-party clients. Source:
        <code style={{ marginLeft: '4px' }}>docs/external_api.md</code>.
        Also fetchable raw via <code>GET /api/v1/docs</code>.
      </div>
      {loading && <Spinner />}
      {err && <div style={{ color: c.danger }}>Failed to load: {err}</div>}
      {!loading && !err && (
        <div style={{ background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '24px', maxWidth: '1000px' }}>
          {rendered}
        </div>
      )}
    </div>
  );
}

function UserFormModal({ user, permCatalogue, canEditPerms, customers, assignments, transformers, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const isEdit = !!user;
  const [form, setForm] = useState({
    username: user?.username || '',
    password: '',
    role: user?.role || 'viewer',
    full_name: user?.full_name || '',
    email: user?.email || '',
    is_active: user?.is_active ?? true,
    customer_ids: user?.customer_ids || [],
    meter_serials: user?.meter_serials || [],
    transformer_ids: user?.transformer_ids || [],
  });
  // Permissions: start from the user's effective list (or the role preset
  // for a new user); `permsCustom` = differs from the preset on purpose.
  const presetOf = (role) => (permCatalogue?.presets?.[role]) || [];
  const [perms, setPerms] = useState(() => user?.permissions || presetOf(user?.role || 'viewer'));
  const [permsCustom, setPermsCustom] = useState(!!user?.permissions_custom);
  const changeRole = (role) => {
    setForm(f => ({ ...f, role }));
    setPerms(presetOf(role));
    setPermsCustom(false);
  };
  const [busy, setBusy] = useState(false);
  const [expandedTxf, setExpandedTxf] = useState(new Set());  // transformer ids showing individual meters

  const tById = React.useMemo(() => {
    const m = {};
    for (const tr of (transformers || [])) m[tr.id] = tr;
    return m;
  }, [transformers]);

  // customer_id → [transformer, ...]
  const transformersByCustomer = React.useMemo(() => {
    const m = {};
    for (const tr of (transformers || [])) {
      if (!m[tr.customer_id]) m[tr.customer_id] = [];
      m[tr.customer_id].push(tr);
    }
    return m;
  }, [transformers]);

  // transformer_id → [{ serial, name }, ...]
  const metersByTransformer = React.useMemo(() => {
    const m = {};
    for (const a of (assignments || [])) {
      if (a.transformer_id == null) continue;
      if (!m[a.transformer_id]) m[a.transformer_id] = [];
      m[a.transformer_id].push({ serial: a.meter_serial, name: a.object_name || a.meter_serial });
    }
    return m;
  }, [assignments]);

  // customer_id → [{ serial, name }, ...] (all meters under the customer, any transformer)
  const metersByCustomer = React.useMemo(() => {
    const m = {};
    for (const a of (assignments || [])) {
      const tr = tById[a.transformer_id];
      if (!tr) continue;
      const cid = tr.customer_id;
      if (!m[cid]) m[cid] = [];
      m[cid].push({ serial: a.meter_serial, name: a.object_name || a.meter_serial });
    }
    return m;
  }, [assignments, tById]);

  const toggleCustomer = (id) => {
    setForm(prev => {
      const removing = prev.customer_ids.includes(id);
      const newCids = removing
        ? prev.customer_ids.filter(x => x !== id)
        : [...prev.customer_ids, id];
      // Removing a customer clears any narrowing under it (its transformers + meters).
      let newMeters = prev.meter_serials;
      let newTxf = prev.transformer_ids;
      if (removing) {
        const custMeters = new Set((metersByCustomer[id] || []).map(m => m.serial));
        newMeters = newMeters.filter(s => !custMeters.has(s));
        const custTxf = new Set((transformersByCustomer[id] || []).map(t => t.id));
        newTxf = newTxf.filter(t => !custTxf.has(t));
      }
      return { ...prev, customer_ids: newCids, meter_serials: newMeters, transformer_ids: newTxf };
    });
  };

  const toggleTransformer = (tid) => {
    setForm(prev => {
      const removing = prev.transformer_ids.includes(tid);
      const newTxf = removing
        ? prev.transformer_ids.filter(x => x !== tid)
        : [...prev.transformer_ids, tid];
      // Granting a whole transformer supersedes any individual meter ticks under
      // it — drop those so the union stays clean and the UI isn't contradictory.
      let newMeters = prev.meter_serials;
      if (!removing) {
        const tMeters = new Set((metersByTransformer[tid] || []).map(m => m.serial));
        newMeters = newMeters.filter(s => !tMeters.has(s));
      }
      return { ...prev, transformer_ids: newTxf, meter_serials: newMeters };
    });
  };

  const toggleTxfExpand = (tid) => {
    setExpandedTxf(prev => { const n = new Set(prev); n.has(tid) ? n.delete(tid) : n.add(tid); return n; });
  };

  const toggleMeter = (serial) => {
    setForm(prev => ({
      ...prev,
      meter_serials: prev.meter_serials.includes(serial)
        ? prev.meter_serials.filter(s => s !== serial)
        : [...prev.meter_serials, serial],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Only send meter_serials if the user has explicitly selected specific
      // meters (not all). Empty array = "no meters" (restrict to none);
      // null/undefined = "all meters under customer" (no per-meter restriction).
      const meterPayload = form.meter_serials.length > 0 ? form.meter_serials : [];
      const txfPayload = form.transformer_ids.length > 0 ? form.transformer_ids : [];

      if (isEdit) {
        const payload = {
          role: form.role,
          full_name: form.full_name || null,
          email: form.email || null,
          is_active: form.is_active,
          customer_ids: form.role === 'admin' ? [] : form.customer_ids,
          meter_serials: form.role === 'admin' ? [] : meterPayload,
          transformer_ids: form.role === 'admin' ? [] : txfPayload,
        };
        if (form.password) payload.new_password = form.password;
        if (canEditPerms && form.role !== 'admin') {
          if (permsCustom) payload.permissions = perms;
          else if (user?.permissions_custom) payload.reset_permissions = true;
        }
        await api(`/users/${user.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        toast.push(t('toast.user_updated'), 'success');
      } else {
        if (!form.password || form.password.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        await api('/users', {
          method: 'POST',
          body: JSON.stringify({
            username: form.username,
            password: form.password,
            role: form.role,
            full_name: form.full_name || null,
            email: form.email || null,
            customer_ids: form.role === 'admin' ? null : form.customer_ids,
            meter_serials: form.role === 'admin' ? null : meterPayload,
            transformer_ids: form.role === 'admin' ? null : txfPayload,
            ...(canEditPerms && permsCustom && form.role !== 'admin' ? { permissions: perms } : {}),
          }),
        });
        toast.push(t('toast.user_created'), 'success');
      }
      onSaved();
    } catch (err) { toast.push(err.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={isEdit ? `Edit user: ${user.username}` : 'New user'} onClose={onClose} width="680px">
      <form onSubmit={handleSubmit}>
        {!isEdit && (
          <FormField label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} required />
        )}
        <FormField
          label={isEdit ? 'New password (leave blank to keep current)' : 'Password (min 8 chars)'}
          type="password"
          value={form.password}
          onChange={(v) => setForm({ ...form, password: v })}
          required={!isEdit}
        />
        <FormField label="Full name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <FormField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          Role (preset)
          <select className="input-field" value={form.role} onChange={(e) => changeRole(e.target.value)}>
            <option value="viewer">Viewer (read-only on assigned customers)</option>
            <option value="developer">Developer (viewer + own API keys + API docs)</option>
            <option value="operator">Operator (read+write on assigned customers)</option>
            <option value="admin">Admin (full access to everything)</option>
          </select>
        </label>
        {permCatalogue && (
          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '6px' }}>
              <b>Permissions</b>
              <span style={{ fontSize: '11px', color: c.textMuted }}>
                {form.role === 'admin' ? 'admin — everything'
                  : permsCustom ? 'customised' : `${form.role} preset`}
              </span>
              {permsCustom && canEditPerms && form.role !== 'admin' && (
                <button type="button" className="button button-secondary" style={{ padding: '2px 8px', fontSize: '11px' }}
                  onClick={() => { setPerms(presetOf(form.role)); setPermsCustom(false); }}>↺ preset</button>
              )}
            </div>
            <PermissionChecklist
              catalogue={permCatalogue.permissions}
              value={form.role === 'admin' ? permCatalogue.permissions.map(p => p.key) : perms}
              locked={!canEditPerms || form.role === 'admin'}
              onChange={(next) => { setPerms(next); setPermsCustom(true); }}
            />
          </div>
        )}
        {isEdit && (
          <label style={{ display: 'block', marginBottom: '12px', fontSize: '13px' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {' '}Account active
          </label>
        )}
        {form.role !== 'admin' && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: c.textSecondary, marginBottom: '2px' }}>
              Access ({form.customer_ids.length} customer{form.customer_ids.length === 1 ? '' : 's'}
              {form.transformer_ids.length ? `, ${form.transformer_ids.length} transformer${form.transformer_ids.length === 1 ? '' : 's'}` : ''}
              {form.meter_serials.length ? `, ${form.meter_serials.length} meter${form.meter_serials.length === 1 ? '' : 's'}` : ''}):
            </div>
            <div style={{ fontSize: '10px', color: c.textMuted, marginBottom: '6px', fontStyle: 'italic' }}>
              Tick a customer for its whole fleet, or drill in and tick specific transformers / meters to restrict to just those.
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto', background: c.bgBase, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '8px' }}>
              {customers.length === 0 && <div style={{ color: c.textDimmed, fontSize: '12px', fontStyle: 'italic' }}>No customers exist yet.</div>}
              {customers.map(cust => {
                const checked = form.customer_ids.includes(cust.id);
                const custTxf = transformersByCustomer[cust.id] || [];
                const custMeters = metersByCustomer[cust.id] || [];
                const selTxf = custTxf.filter(tr => form.transformer_ids.includes(tr.id)).length;
                const selMtr = custMeters.filter(m => form.meter_serials.includes(m.serial)).length;
                const narrowed = selTxf > 0 || selMtr > 0;
                return (
                  <div key={cust.id}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleCustomer(cust.id)} />
                      <span style={{ fontWeight: 500 }}>{cust.name}</span>
                      {checked && (
                        <span style={{ fontSize: '10px', color: narrowed ? c.accent : c.textMuted, marginLeft: 'auto' }}>
                          {narrowed
                            ? `${selTxf ? selTxf + ' transformer' + (selTxf === 1 ? '' : 's') : ''}${selTxf && selMtr ? ' + ' : ''}${selMtr ? selMtr + ' meter' + (selMtr === 1 ? '' : 's') : ''}`
                            : 'all meters'}
                        </span>
                      )}
                    </label>
                    {checked && custTxf.length > 0 && (
                      <div style={{ marginLeft: '22px', marginBottom: '4px', padding: '4px 8px', background: c.bgCardAlt, borderRadius: '4px', fontSize: '11px' }}>
                        {!narrowed && (
                          <div style={{ color: c.accent, fontStyle: 'italic', marginBottom: '2px' }}>
                            No transformer/meter ticked → sees all meters under this customer.
                          </div>
                        )}
                        {custTxf.map(tr => {
                          const txfChecked = form.transformer_ids.includes(tr.id);
                          const tMeters = metersByTransformer[tr.id] || [];
                          const expanded = expandedTxf.has(tr.id);
                          const tSel = tMeters.filter(m => form.meter_serials.includes(m.serial)).length;
                          return (
                            <div key={tr.id} style={{ marginBottom: '2px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                                <input type="checkbox" checked={txfChecked} onChange={() => toggleTransformer(tr.id)}
                                  title="Grant this whole transformer (all its meters)" />
                                <span style={{ cursor: 'pointer', color: c.textPrimary, fontWeight: 500 }}
                                  onClick={() => toggleTxfExpand(tr.id)}>
                                  {tMeters.length > 0 ? (expanded ? '▾ ' : '▸ ') : ''}{tr.name || `Transformer ${tr.id}`}
                                </span>
                                <span style={{ color: c.textMuted, fontSize: '10px' }}>
                                  ({tMeters.length} meter{tMeters.length === 1 ? '' : 's'}{txfChecked ? ', all' : tSel ? `, ${tSel} ticked` : ''})
                                </span>
                              </div>
                              {expanded && !txfChecked && tMeters.length > 0 && (
                                <div style={{ marginLeft: '22px' }}>
                                  {tMeters.map(m => (
                                    <label key={m.serial} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '1px 0', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={form.meter_serials.includes(m.serial)} onChange={() => toggleMeter(m.serial)} />
                                      <span style={{ fontFamily: 'monospace', color: c.textPrimary }}>{m.serial}</span>
                                      {m.name !== m.serial && <span style={{ color: c.textMuted }}>({m.name})</span>}
                                    </label>
                                  ))}
                                </div>
                              )}
                              {expanded && txfChecked && (
                                <div style={{ marginLeft: '22px', color: c.textMuted, fontStyle: 'italic' }}>
                                  All {tMeters.length} meters granted via the transformer.
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <ModalButtons busy={busy} onClose={onClose} disabled={!isEdit && (!form.username || !form.password)} label={isEdit ? 'Save' : 'Create'} />
      </form>
    </ModalShell>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Reports Tab — pulls historical profile data (load profile, power
// quality, daily billing, monthly billing) from the meter for a chosen
// date range and displays it in a sortable table with CSV export.
//
// How it works under the hood
// ───────────────────────────
// 1. When the user picks a profile, we hit `/profile/<name>/info` to
//    learn the meter's `entries_in_use`, `first_ts`, `last_ts`, and
//    `interval_seconds`. That gives us enough to map a wall-clock date
//    to a 1-based entry index without the meter having to do a date
//    lookup itself (Holley meters return 0 rows for `readRowsByRange`).
// 2. The user picks from/to dates. We compute start_index and count
//    from those + the cached info, clamp to [1, entries_in_use], and
//    POST `/profile/<name>/fetch?entries=N&start_index=I`. The
//    backend's auto-batcher splits the request into the meter's safe
//    APDU-sized chunks transparently.
// 3. We render the rows in a table with friendly column names looked
//    up from the captures metadata, plus a "Download CSV" button.
//
// Friendly column labels for the OBIS codes that show up in the
// profiles we currently support. Anything not in this map falls
// through to the raw OBIS string.
const PROFILE_COLUMN_LABELS = {
  // Common
  '0.0.1.0.0.255':   'Clock',
  '0.0.96.10.1.255': 'Status',
  '0.0.96.10.2.255': 'Status',
  '0.0.96.10.3.255': 'Status',
  // Daily / monthly billing — energy registers (IEC 62056 / CAS spec §13).
  // Used as a fallback when a meter hasn't returned captures yet
  // (e.g. DCU-routed meters whose daily_billing cache is empty on
  // first render). Without these, the column headers display the raw
  // OBIS codes which are unreadable.
  '1.0.1.8.0.255':   '+A Total (kWh)',
  '1.0.1.8.1.255':   '+A T1 (kWh)',
  '1.0.1.8.2.255':   '+A T2 (kWh)',
  '1.0.1.8.3.255':   '+A T3 (kWh)',
  '1.0.1.8.4.255':   '+A T4 (kWh)',
  '1.0.2.8.0.255':   '-A Total (kWh)',
  '1.0.2.8.1.255':   '-A T1 (kWh)',
  '1.0.2.8.2.255':   '-A T2 (kWh)',
  '1.0.2.8.3.255':   '-A T3 (kWh)',
  '1.0.2.8.4.255':   '-A T4 (kWh)',
  '1.0.3.8.0.255':   '+R Total (kvarh)',
  '1.0.3.8.1.255':   '+R T1 (kvarh)',
  '1.0.3.8.2.255':   '+R T2 (kvarh)',
  '1.0.3.8.3.255':   '+R T3 (kvarh)',
  '1.0.3.8.4.255':   '+R T4 (kvarh)',
  '1.0.4.8.0.255':   '-R Total (kvarh)',
  '1.0.4.8.1.255':   '-R T1 (kvarh)',
  '1.0.4.8.2.255':   '-R T2 (kvarh)',
  '1.0.4.8.3.255':   '-R T3 (kvarh)',
  '1.0.4.8.4.255':   '-R T4 (kvarh)',
  // Load profile (W / var / VA)
  '1.0.1.27.0.255':  'Active demand + (W)',
  '1.0.2.27.0.255':  'Active demand − (W)',
  '1.0.3.27.0.255':  'Reactive demand + (var)',
  '1.0.4.27.0.255':  'Reactive demand − (var)',
  '1.0.9.27.0.255':  'Apparent demand + (VA)',
  '1.0.10.27.0.255': 'Apparent demand − (VA)',
  // Power quality. Scaling annotations like "(×10 → V)" are kept
  // OUT of these labels — the backend divides by the captured
  // scale (see _merge_fallback_scales) and the operator sees real
  // units, so exposing the internal multiplier in the column
  // header just clutters exports and confuses consumers.
  '1.0.32.24.0.255': 'V L1 avg (V)',
  '1.0.52.24.0.255': 'V L2 avg (V)',
  '1.0.72.24.0.255': 'V L3 avg (V)',
  '1.0.32.26.0.255': 'V L1 max (V)',
  '1.0.52.26.0.255': 'V L2 max (V)',
  '1.0.72.26.0.255': 'V L3 max (V)',
  '1.0.32.23.0.255': 'V L1 min (V)',
  '1.0.52.23.0.255': 'V L2 min (V)',
  '1.0.72.23.0.255': 'V L3 min (V)',
  '1.0.31.24.0.255': 'I L1 avg (A)',
  '1.0.51.24.0.255': 'I L2 avg (A)',
  '1.0.71.24.0.255': 'I L3 avg (A)',
  '1.0.13.24.0.255': 'PF total',
  '1.0.33.24.0.255': 'PF L1',
  '1.0.53.24.0.255': 'PF L2',
  '1.0.73.24.0.255': 'PF L3',
  '1.0.14.7.0.255':  'Frequency (Hz)',
};

// Profiles the dashboard offers. Each entry is { value: 'symbolic_name'
// (the same key the backend's PROFILE_OBIS dict uses), label: 'Human',
// hint: 'help text shown under the dropdown' }. The 'columns' array, if
// set, gives an explicit fallback column layout for profiles whose
// capture_objects can't be read from Read SAP (daily_billing).
const PROFILE_CHOICES = [
  {
    value: 'load_profile',
    label: '⚡ Load Profile (15-min power)',
    hint: 'Active/reactive/apparent demand — typically 15-minute intervals.',
  },
  {
    value: 'power_quality',
    label: '🔌 Power Quality (15-min V/I/Hz)',
    hint: 'Voltage, current, power factor and frequency per phase.',
  },
  {
    value: 'daily_billing',
    label: '🗓 Daily Billing',
    hint: 'One row per calendar day — energy registers + tariff buckets.',
    // Read SAP can't read attribute 3 for daily_billing on Holley meters,
    // so the backend returns rows with no captures. These labels match
    // the order in vendor PDFs and the CAS Meter spec §13.3.
    columns: [
      'Clock', 'Status',
      'kWh + (×100)', 'kWh − (×100)',
      'kvarh + (×100)', 'kvarh − (×100)',
      'kWh + T1 (×100)', 'kWh + T2 (×100)', 'kWh + T3 (×100)', 'kWh + T4 (×100)',
      'kWh − T1', 'kWh − T2', 'kWh − T3', 'kWh − T4',
      'kvarh + T1 (×100)', 'kvarh + T2 (×100)',
      'kvarh + T3 (×100)', 'kvarh + T4 (×100)',
      'kvarh − T1', 'kvarh − T2', 'kvarh − T3', 'kvarh − T4',
    ],
  },
  {
    value: 'monthly_billing',
    label: '📅 Monthly Billing',
    hint: 'One row per billing period.',
  },
];

// ──────────────────────────────────────────────────────────────────
// Customer-first reporting panel — primary Reports tab workflow.
//
// Design rationale
// ────────────────
// The original per-meter explorer forced operators to pick an
// individual connected meter before they could pull any report,
// which doesn't match how utilities actually think about billing
// and power-quality data. Customers typically own multiple meters
// and sometimes multiple transformers or DCUs, and reports need
// to cover that whole footprint in one shot.
//
// This panel flips the workflow: pick a customer first, see the
// full hierarchy (transformers, DCUs, meters) that falls under
// them, then generate a Holley-format Excel report for the entire
// customer in one click. No meter selection needed.
//
// The previous per-meter explorer still exists below as an
// "Advanced" collapsible section for the cases where operators
// need to drill into one specific meter's load-profile or
// on-demand fetch from the meter buffer.
//
// Backend endpoints used (all already customer-aware):
//   GET /api/customers         — customer list
//   GET /api/transformers      — filtered client-side by customer_id
//   GET /api/assignments       — filtered client-side by transformer_id
//   GET /api/reports/power-quality.xlsx?customer_id=X&from_ts&to_ts
//   GET /api/reports/daily-billing.xlsx?customer_id=X&from_ts&to_ts
//
// Historical range: omit from_ts/to_ts to pull the full stored
// buffer for every meter under that customer. The midnight
// scheduler keeps the buffers fresh without needing a manual
// pre-fetch from the per-meter explorer.
// ──────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────
// Report column configuration
// ──────────────────────────────────────────────────────────────────
//
// Every report used to hand back its full column set — 21 columns for
// daily billing, 25 for LP/PQ, 47 for period consumption. An operator
// who wanted four registers downloaded all of them and deleted the
// rest in Excel, every time.
//
// THE CATALOG IS FETCHED, NOT HARD-CODED HERE. The server derives it
// from the same constants its renderers use, so a column added to a
// report appears in this dialog without anyone remembering to update a
// second list on this side.
//
// SELECTIONS ARE STORED BY OBIS CODE (or LP row key), NEVER BY LABEL.
// Labels are translated by the en/ru report toggle and carry units, so
// a label-keyed selection would silently empty itself the first time
// an operator switched language.
//
// A report with no saved selection sends NO column params at all, so
// every existing link, bookmark and integration behaves exactly as it
// did before this existed.

const REPORT_COLS_KEY = 'emeter.reportColumns.v1';

function loadReportColumnPrefs() {
  try { return JSON.parse(localStorage.getItem(REPORT_COLS_KEY)) || {}; }
  catch (_) { return {}; }
}

function saveReportColumnPrefs(prefs) {
  try { localStorage.setItem(REPORT_COLS_KEY, JSON.stringify(prefs)); }
  catch (_) { /* private mode / quota — selection just won't persist */ }
}

// One fetch per language for the whole app, not one per panel that
// happens to render a download button.
const _reportCatalogCache = {};
function fetchReportCatalog(lang) {
  const key = lang || 'en';
  if (!_reportCatalogCache[key]) {
    _reportCatalogCache[key] = api(`/reports/column-catalog?lang=${key}`)
      .then(d => d.reports || {})
      .catch(() => { delete _reportCatalogCache[key]; return {}; });
  }
  return _reportCatalogCache[key];
}

// Resolve a SAVED selection against the LIVE catalog.
//
// Returns null for "send nothing, the operator wants everything". That
// is deliberately also what an unusable selection resolves to: a saved
// key for a column since removed from the report, or a selection left
// with nothing but mandatory columns, would otherwise produce either a
// 400 on download or a sheet of bare timestamps. Falling back to the
// full report is the behaviour operators can always make sense of.
function resolveSelection(spec, saved) {
  if (!spec || !saved) return null;
  const pick = (all, chosen) => {
    const valid = new Set(all.map(x => x.key));
    const mandatory = all.filter(x => x.mandatory).map(x => x.key);
    if (!chosen) return all.map(x => x.key);
    return Array.from(new Set([
      ...chosen.filter(k => valid.has(k)),
      ...mandatory,
    ]));
  };
  const metricMandatory = new Set(
    (spec.metrics || []).filter(m => m.mandatory).map(m => m.key));
  const metrics = pick(spec.metrics || [], saved.metrics);
  if (!metrics.filter(k => !metricMandatory.has(k)).length) return null;
  return {
    metrics,
    headers: pick(spec.headers || [], saved.headers),
    parts: spec.parts ? pick(spec.parts, saved.parts) : null,
  };
}

// Add the column params to a download URL. A selection that covers the
// whole report adds nothing, keeping the URL byte-identical to what it
// was before — worth it because these URLs end up pasted into tickets.
function applyColumnParams(params, spec, saved) {
  const sel = resolveSelection(spec, saved);
  if (!sel) return;
  if (sel.metrics.length < (spec.metrics || []).length) {
    params.set('columns', sel.metrics.join(','));
  }
  if ((spec.headers || []).length &&
      sel.headers.length < spec.headers.length) {
    params.set('header_columns', sel.headers.join(','));
  }
  if (spec.parts && sel.parts && sel.parts.length < spec.parts.length) {
    params.set('parts', sel.parts.join(','));
  }
}

// Shared by every panel with a download button, so configuring Daily
// Billing here and downloading it from the subscriber view agree.
function useReportColumns() {
  const { lang } = useLang();
  const reportLang = getReportLang() || lang;
  const [catalog, setCatalog] = useState(null);
  const [prefs, setPrefs] = useState(loadReportColumnPrefs);
  useEffect(() => {
    let alive = true;
    fetchReportCatalog(reportLang).then(c => { if (alive) setCatalog(c); });
    return () => { alive = false; };
  }, [reportLang]);
  const savePrefs = useCallback((next) => {
    setPrefs(next);
    saveReportColumnPrefs(next);
  }, []);
  // Count of columns a report will actually emit, for the button badge.
  const summary = useCallback((reportKey) => {
    const spec = catalog && catalog[reportKey];
    if (!spec) return null;
    const total = (spec.metrics || []).length;
    const sel = resolveSelection(spec, prefs[reportKey]);
    return { total, chosen: sel ? sel.metrics.length : total,
             configured: !!sel };
  }, [catalog, prefs]);
  return { catalog, prefs, savePrefs, summary, reportLang };
}

// One group of checkboxes — "Active energy — import", "Voltage L1", …
function ColumnGroup({ title, items, chosen, onToggle, onSetMany }) {
  const c = useThemeColors();
  const keys = items.map(i => i.key);
  const optional = items.filter(i => !i.mandatory).map(i => i.key);
  const allOn = optional.every(k => chosen.has(k));
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: '8px',
        marginBottom: '6px',
      }}>
        <div style={{
          fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', color: c.textMuted,
        }}>{title}</div>
        {optional.length > 1 && (
          <button
            type="button"
            onClick={() => onSetMany(optional, !allOn)}
            style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: '11px', color: c.accent, cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >{allOn ? 'none' : 'all'}</button>
        )}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
        gap: '2px 14px',
      }}>
        {items.map(item => (
          <label
            key={item.key}
            title={item.mandatory ? 'Always included' : item.key}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              fontSize: '12px', padding: '3px 0',
              color: item.mandatory ? c.textMuted : c.textPrimary,
              cursor: item.mandatory ? 'default' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={item.mandatory || chosen.has(item.key)}
              disabled={item.mandatory}
              onChange={() => onToggle(item.key)}
              style={{ width: '14px', height: '14px', flexShrink: 0 }}
            />
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {item.label}{item.mandatory ? ' *' : ''}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function groupItems(items) {
  const out = [];
  for (const it of items || []) {
    const g = it.group || 'Other';
    let bucket = out.find(b => b.title === g);
    if (!bucket) { bucket = { title: g, items: [] }; out.push(bucket); }
    bucket.items.push(it);
  }
  return out;
}

// The dialog itself. `reportKeys` is the set of reports the calling
// panel can download; a tab strip appears when there is more than one.
function ReportColumnsModal({ reportKeys, catalog, prefs, onSave, onClose }) {
  const c = useThemeColors();
  const available = reportKeys.filter(k => catalog && catalog[k]);
  const [active, setActive] = useState(available[0] || reportKeys[0]);
  // Edited locally and only written on Apply, so Cancel really cancels.
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(prefs || {})));

  const spec = catalog && catalog[active];
  if (!spec) {
    return (
      <ModalShell title="Report configuration" onClose={onClose}>
        <div style={{ fontSize: '13px', color: c.textMuted }}>
          Loading the column list…
        </div>
      </ModalShell>
    );
  }

  const sectionOf = (kind) => (kind === 'metrics' ? spec.metrics
    : kind === 'headers' ? (spec.headers || []) : (spec.parts || []));

  // A report with nothing saved starts with everything ticked, which is
  // both the current behaviour and the right default to edit down from.
  const chosenSet = (kind) => {
    const saved = draft[active] && draft[active][kind];
    if (!saved) return new Set(sectionOf(kind).map(i => i.key));
    return new Set(saved);
  };

  const writeBack = (kind, nextSet) => {
    setDraft(prev => {
      const next = { ...prev };
      const entry = { ...(next[active] || {}) };
      entry[kind] = Array.from(nextSet);
      next[active] = entry;
      return next;
    });
  };

  const toggle = (kind) => (key) => {
    const set = chosenSet(kind);
    if (set.has(key)) set.delete(key); else set.add(key);
    writeBack(kind, set);
  };

  const setMany = (kind) => (keys, on) => {
    const set = chosenSet(kind);
    keys.forEach(k => (on ? set.add(k) : set.delete(k)));
    writeBack(kind, set);
  };

  const metricsChosen = chosenSet('metrics');
  const metricMandatory = new Set(
    spec.metrics.filter(m => m.mandatory).map(m => m.key));
  const pickedCount = spec.metrics
    .filter(m => m.mandatory || metricsChosen.has(m.key)).length;
  const selectableCount = spec.metrics
    .filter(m => !m.mandatory && metricsChosen.has(m.key)).length;
  const canApply = selectableCount > 0;

  const resetThisReport = () => {
    setDraft(prev => {
      const next = { ...prev };
      delete next[active];
      return next;
    });
  };

  const btn = (bg, disabled) => ({
    padding: '7px 14px', fontSize: '13px', borderRadius: '6px',
    border: `1px solid ${bg}`, background: disabled ? c.bgBase : bg,
    color: disabled ? c.textMuted : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600,
  });

  return (
    <ModalShell title="⚙ Report configuration" onClose={onClose} width="760px">
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '14px', lineHeight: 1.5 }}>
        Choose which columns the downloaded Excel file contains. Untick what
        you do not need — the report is generated with exactly the columns
        left ticked, in their normal order. Columns marked <strong>*</strong>{' '}
        are always included. The selection is remembered in this browser and
        applies wherever this report is downloaded.
      </div>

      {available.length > 1 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {available.map(k => {
            const on = k === active;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setActive(k)}
                style={{
                  padding: '5px 12px', fontSize: '12px',
                  border: `1px solid ${on ? c.accent : c.border}`,
                  borderRadius: '14px',
                  background: on ? c.accent + '22' : 'transparent',
                  color: on ? c.accent : c.textSecondary,
                  cursor: 'pointer', fontWeight: on ? 600 : 400,
                }}
              >{catalog[k].label}</button>
            );
          })}
        </div>
      )}

      <div style={{
        maxHeight: '46vh', overflowY: 'auto', paddingRight: '6px',
        borderTop: `1px solid ${c.border}`, paddingTop: '14px',
      }}>
        {(spec.headers || []).length > 0 && (
          <ColumnGroup
            title="Identification"
            items={spec.headers}
            chosen={chosenSet('headers')}
            onToggle={toggle('headers')}
            onSetMany={setMany('headers')}
          />
        )}
        {(spec.parts || []).length > 0 && (
          <ColumnGroup
            title="Value columns (per register)"
            items={spec.parts}
            chosen={chosenSet('parts')}
            onToggle={toggle('parts')}
            onSetMany={setMany('parts')}
          />
        )}
        {groupItems(spec.metrics).map(g => (
          <ColumnGroup
            key={g.title}
            title={g.title}
            items={g.items}
            chosen={metricsChosen}
            onToggle={toggle('metrics')}
            onSetMany={setMany('metrics')}
          />
        ))}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        marginTop: '16px', paddingTop: '14px',
        borderTop: `1px solid ${c.border}`, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: '12px', color: canApply ? c.textSecondary : '#f87171' }}>
          {canApply
            ? <><strong>{pickedCount}</strong> of {spec.metrics.length} metric columns</>
            : 'Pick at least one metric column.'}
        </div>
        <button
          type="button"
          onClick={() => setMany('metrics')(
            spec.metrics.filter(m => !m.mandatory).map(m => m.key), true)}
          style={{
            background: 'none', border: 'none', fontSize: '12px',
            color: c.accent, cursor: 'pointer', textDecoration: 'underline',
          }}
        >select all</button>
        <button
          type="button"
          onClick={resetThisReport}
          style={{
            background: 'none', border: 'none', fontSize: '12px',
            color: c.textMuted, cursor: 'pointer', textDecoration: 'underline',
          }}
        >reset to default</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button type="button" onClick={onClose}
                  style={{ ...btn(c.border), color: c.textSecondary }}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!canApply}
            onClick={() => { onSave(draft); onClose(); }}
            style={btn(c.accent, !canApply)}
          >Apply</button>
        </div>
      </div>
    </ModalShell>
  );
}

// The button that opens the dialog, with a live "18 of 21 columns"
// badge so a narrowed report is visible without opening anything —
// otherwise an operator who configured this weeks ago wonders why
// their file is missing columns.
function ReportColumnsButton({ reportKeys, catalog, prefs, savePrefs, summary,
                              compact = false }) {
  const c = useThemeColors();
  const [open, setOpen] = useState(false);
  const configured = reportKeys
    .map(k => summary(k))
    .filter(s => s && s.configured);
  return (
    <>
      <button
        type="button"
        className={compact ? 'button button-sm button-secondary'
                           : 'button button-secondary'}
        onClick={() => setOpen(true)}
        disabled={!catalog}
        title="Choose which columns the Excel download contains"
      >
        {compact ? '⚙ Columns' : '⚙ Report columns'}
        {configured.length > 0 && (
          <span style={{
            marginLeft: '7px', padding: '1px 7px', borderRadius: '10px',
            background: c.accent + '33', color: c.accent,
            fontSize: '11px', fontWeight: 700,
          }}>
            {configured.length === 1
              ? `${configured[0].chosen}/${configured[0].total}`
              : `${configured.length} customised`}
          </span>
        )}
      </button>
      {open && (
        <ReportColumnsModal
          reportKeys={reportKeys}
          catalog={catalog}
          prefs={prefs}
          onSave={savePrefs}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}


function CustomerReportsPanel() {
  const toast = useToast();
  const c = useThemeColors();
  const t = useT();
  const { lang } = useLang();
  const reportCols = useReportColumns();
  const { assignments, refreshAssignments } = useAssignments();
  const [customers, setCustomers] = useState([]);
  const [transformers, setTransformers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [busyKind, setBusyKind] = useState(null); // 'power_quality' | 'daily_billing' | null
  const [selectedTransformerIds, setSelectedTransformerIds] = useState([]); // empty = all
  // Default ON: report shows primary-side values (CT*VT applied).
  // Operators usually want primary; flip OFF to verify against the
  // raw register values stamped on the meter LCD or in vendor tools.
  const [primaryMode, setPrimaryMode] = useState(true);
  // Active backfill jobs we kicked off — { meter_serial: jobObj }.
  // Refreshed by a 10s poll while any job is in-flight.
  const [backfillJobs, setBackfillJobs] = useState({});

  // Load lookup data once. The three endpoints are cheap lists and
  // the full set fits easily in memory on any reasonable install.
  useEffect(() => {
    api('/customers').then(d => setCustomers(d.customers || [])).catch(() => {});
    api('/transformers').then(d => setTransformers(d.transformers || [])).catch(() => {});
    refreshAssignments().catch(() => {});
  }, [refreshAssignments]);

  // Auto-pick the first customer so the panel is immediately useful
  // after opening. Operators can switch via the dropdown.
  useEffect(() => {
    if (!customerId && customers.length > 0) {
      setCustomerId(String(customers[0].id));
    }
  }, [customers, customerId]);

  const selectedCustomer = customers.find(c => String(c.id) === String(customerId));

  // Reset transformer selection when customer changes.
  const prevCustomerRef = useRef(customerId);
  useEffect(() => {
    if (customerId !== prevCustomerRef.current) {
      setSelectedTransformerIds([]);
      prevCustomerRef.current = customerId;
    }
  }, [customerId]);

  // Client-side filtering: transformers belonging to the picked
  // customer, then the assignments (meters) under those
  // transformers, then the distinct DCU serials the assignments
  // reference. A utility customer may have hundreds of meters so
  // we keep the UI compact by showing counts + an expandable list.
  const customerTransformers = customerId
    ? transformers.filter(t => String(t.customer_id) === String(customerId))
    : [];

  // If specific transformers are selected, filter to just those;
  // otherwise include all transformers under the customer.
  const activeTransformers = selectedTransformerIds.length > 0
    ? customerTransformers.filter(t => selectedTransformerIds.includes(t.id))
    : customerTransformers;

  const customerAssignments = customerId
    ? assignments.filter(a =>
        activeTransformers.some(t => t.id === a.transformer_id)
      )
    : [];
  const customerDcuSerials = Array.from(new Set(
    customerAssignments.map(a => a.dcu_serial).filter(Boolean)
  ));

  // Build a job_id matching the backend's _backfill_job_key shape.
  const backfillJobKey = (serial, profileObis, fromTs, toTs) =>
    `${serial}|${profileObis}|${Math.trunc(fromTs)}|${Math.trunc(toTs)}`;

  const PROFILE_OBIS_MAP = {
    daily_billing: '0.0.98.2.0.255',
    power_quality: '1.0.99.1.2.255',
  };

  const handlePrepull = async (reportKind) => {
    if (!customerId || customerAssignments.length === 0) {
      toast.push(t('toast.pick_customer_first') || 'Pick a customer first', 'error');
      return;
    }
    if (!fromDate || !toDate) {
      toast.push('Select a From and To date before pre-pulling', 'error');
      return;
    }
    const fromTs = dayStart(fromDate);
    const toTs = dayEnd(toDate);
    if (fromTs >= toTs) return;
    const spanDays = Math.max(0, Math.round((toTs - fromTs) / 86400));
    if (spanDays > 366) {
      toast.push(`Range ${spanDays} days exceeds the 12-month / 366-day meter retention`, 'error');
      return;
    }
    // Skip the pull if the range is within the 31-day auto-backfill
    // window — data should already be in cache and if it isn't,
    // that's a connection issue, not something a Pre-pull solves.
    if (spanDays <= 31) {
      toast.push(`Range is within the 31-day auto-backfill window — data should already be cached`, 'info');
      return;
    }
    // Parallel kickoff: one POST per assigned meter. Each job chunks
    // its own range server-side; total wall time ≈ max single-meter
    // time, not sum, because backend job workers run concurrently.
    const profileObis = PROFILE_OBIS_MAP[reportKind];
    const newJobs = {};
    const offline = [];
    for (const a of customerAssignments) {
      try {
        const r = await api(
          `/meters/${a.meter_serial}/profile/${reportKind}/backfill`
            + `?from_ts=${fromTs}&to_ts=${toTs}`,
          { method: 'POST' }
        );
        newJobs[a.meter_serial] = { ...r, meter_serial: a.meter_serial };
      } catch (err) {
        const msg = err.message || String(err);
        if (/offline/i.test(msg) || /unreachable/i.test(msg) || /can't reach/i.test(msg)) {
          offline.push(a.meter_serial);
          newJobs[a.meter_serial] = { meter_serial: a.meter_serial, state: 'offline', last_error: 'meter offline' };
        } else {
          newJobs[a.meter_serial] = { meter_serial: a.meter_serial, state: 'failed', last_error: msg };
        }
      }
    }
    if (offline.length > 0) {
      // One popup summarising every offline meter, rather than N
      // individual alerts. Operators retry the whole batch later.
      window.alert(
        `${offline.length} meter(s) are offline right now and can't be `
        + `pulled — try again once they reconnect:\n\n`
        + offline.join('\n')
      );
    }
    setBackfillJobs(newJobs);
    const chunks = Math.max(1, Math.ceil(spanDays / 7));
    const etaSec = chunks * 30;
    const eta = etaSec < 60 ? `~${etaSec}s`
              : etaSec < 3600 ? `~${Math.round(etaSec / 60)} min`
              : `~${(etaSec / 3600).toFixed(1)} h`;
    toast.push(
      `Pre-pull queued for ${Object.keys(newJobs).length} meter(s) · ${spanDays} days · ${eta} per meter (parallel)`,
      'info'
    );
  };

  // Poll status of any in-flight backfill jobs every 10s.
  useEffect(() => {
    const inflight = Object.values(backfillJobs).filter(
      j => j && (j.state === 'queued' || j.state === 'running')
    );
    if (inflight.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      const updated = { ...backfillJobs };
      for (const j of inflight) {
        try {
          const fresh = await api(`/profile-backfill/${encodeURIComponent(j.job_id)}`);
          updated[j.meter_serial] = { ...fresh, meter_serial: j.meter_serial };
        } catch (_) {/* keep prior */}
      }
      if (!cancelled) setBackfillJobs(updated);
    };
    const id = setInterval(tick, 10_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [backfillJobs]);

  const handleDownload = async (reportKind) => {
    if (!customerId) {
      toast.push(t('toast.pick_customer_first'), 'error');
      return;
    }
    if (customerAssignments.length === 0) {
      toast.push(t('toast.no_assignments'), 'error');
      return;
    }
    setBusyKind(reportKind);
    try {
      const params = new URLSearchParams();
      params.set('lang', getReportLang() || lang);
      params.set('primary', primaryMode ? 'true' : 'false');
      // If specific transformers are selected, send individual meter
      // serials so the report only covers the chosen scope. Otherwise
      // send the customer_id for a full customer report.
      if (selectedTransformerIds.length > 0) {
        for (const a of customerAssignments) {
          params.append('meter_serial', a.meter_serial);
        }
      } else {
        params.set('customer_id', String(customerId));
      }
      if (fromDate) params.set('from_ts', String(dayStart(fromDate)));
      if (toDate)   params.set('to_ts',   String(dayEnd(toDate)));

      // Column selection, if the operator narrowed this report. Adds
      // nothing when they haven't, so the URL stays what it always was.
      applyColumnParams(
        params,
        reportCols.catalog && reportCols.catalog[reportKind],
        reportCols.prefs[reportKind],
      );

      const endpoint = reportKind === 'power_quality'
        ? '/api/reports/power-quality.xlsx'
        : reportKind === 'load_profile'
        ? '/api/reports/load-profile.xlsx'
        : '/api/reports/daily-billing.xlsx';
      const url = `${endpoint}?${params.toString()}`;
      const stamp = new Date().toISOString().slice(0, 10);
      const safeName = (selectedCustomer?.name || 'customer')
        .replace(/[^a-zA-Z0-9]/g, '_');
      await downloadXlsx(
        url,
        `${reportKind}_${safeName}_${stamp}.xlsx`,
        { toast },
      );
    } catch (_) { /* toast already shown by helper */
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <div style={{
      padding: '20px 24px',
      marginBottom: '24px',
      background: c.bgCard,
      border: `1px solid ${c.border}`,
      borderRadius: '8px',
    }}>
      <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
        🏢 Customer reports
      </div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '16px' }}>
        Pick a customer to pull a Holley-format Excel report covering every meter,
        transformer and DCU under them. Historical range optional — blank pulls
        the full stored buffer. The midnight scheduler keeps data current, so no
        meter needs to be manually selected or active.
      </div>

      <label className="input-label" style={{ display: 'block', marginBottom: '16px' }}>
        Customer
        <select
          className="input-field"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
        >
          <option value="">— pick a customer —</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.customer_no ? ` (#${c.customer_no})` : ''}
              {c.zone ? ` — ${c.zone}` : ''}
            </option>
          ))}
        </select>
      </label>

      {selectedCustomer && (
        <div style={{
          padding: '14px 16px',
          marginBottom: '16px',
          background: c.bgBase,
          border: `1px solid ${c.border}`,
          borderRadius: '6px',
        }}>
          {/* Customer header */}
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            marginBottom: '6px',
            color: c.textPrimary,
          }}>
            {selectedCustomer.name}
          </div>
          {/* Customer detail grid — only shows fields that are populated */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '6px 16px',
            fontSize: '12px',
            color: c.textSecondary,
            marginBottom: '12px',
          }}>
            {selectedCustomer.customer_no && (
              <div><strong style={{ color: c.textMuted }}>Customer #:</strong> {selectedCustomer.customer_no}</div>
            )}
            {selectedCustomer.account_number && (
              <div><strong style={{ color: c.textMuted }}>Account #:</strong> {selectedCustomer.account_number}</div>
            )}
            {selectedCustomer.zone && (
              <div><strong style={{ color: c.textMuted }}>Zone:</strong> {selectedCustomer.zone}</div>
            )}
            {selectedCustomer.phone && (
              <div><strong style={{ color: c.textMuted }}>Phone:</strong> {selectedCustomer.phone}</div>
            )}
            {selectedCustomer.email && (
              <div><strong style={{ color: c.textMuted }}>Email:</strong> {selectedCustomer.email}</div>
            )}
            {selectedCustomer.address && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong style={{ color: c.textMuted }}>Address:</strong> {selectedCustomer.address}
              </div>
            )}
          </div>

          {/* Hierarchy summary — what the report will actually include */}
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap',
            fontSize: '12px',
            color: c.textLabel,
            padding: '10px 12px',
            background: c.bgCard,
            border: `1px solid ${c.border}`,
            borderRadius: '4px',
          }}>
            <span>🟧 <strong>{customerTransformers.length}</strong> transformer{customerTransformers.length === 1 ? '' : 's'}</span>
            <span>📡 <strong>{customerDcuSerials.length}</strong> DCU{customerDcuSerials.length === 1 ? '' : 's'}</span>
            <span>🔌 <strong>{customerAssignments.length}</strong> meter{customerAssignments.length === 1 ? '' : 's'}</span>
          </div>

          {/* Transformer filter — pick specific transformers or keep blank for all. */}
          {customerTransformers.length > 1 && (
            <div style={{ marginTop: '10px', fontSize: '12px' }}>
              <div style={{ color: c.textMuted, marginBottom: '4px' }}>
                Filter by transformer ({selectedTransformerIds.length > 0 ? `${selectedTransformerIds.length} of ${customerTransformers.length} selected` : 'all included'}):
              </div>
              <div style={{
                display: 'flex', gap: '6px', flexWrap: 'wrap',
              }}>
                {customerTransformers.map(tr => {
                  const active = selectedTransformerIds.includes(tr.id);
                  const tMeters = assignments.filter(a => a.transformer_id === tr.id).length;
                  return (
                    <button
                      key={tr.id}
                      type="button"
                      onClick={() => {
                        setSelectedTransformerIds(prev =>
                          prev.includes(tr.id)
                            ? prev.filter(x => x !== tr.id)
                            : [...prev, tr.id]
                        );
                      }}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: `1px solid ${active ? c.accent : c.border}`,
                        borderRadius: '14px',
                        background: active ? c.accent + '22' : 'transparent',
                        color: active ? c.accent : c.textSecondary,
                        cursor: 'pointer',
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {tr.name} ({tMeters})
                    </button>
                  );
                })}
                {selectedTransformerIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedTransformerIds([])}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      border: `1px solid ${c.border}`,
                      borderRadius: '14px',
                      background: 'transparent',
                      color: c.textMuted,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    clear filter
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Expandable per-meter list — so operators can sanity-check
              scope before hitting download on a large customer. */}
          {customerAssignments.length > 0 && (
            <details style={{ marginTop: '10px', fontSize: '12px', color: c.textSecondary }}>
              <summary style={{ cursor: 'pointer', padding: '4px 0' }}>
                View meters ({customerAssignments.length})
              </summary>
              <div style={{
                marginTop: '6px',
                maxHeight: '160px',
                overflowY: 'auto',
                padding: '8px 10px',
                background: c.bgBase,
                border: `1px solid ${c.border}`,
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '11px',
              }}>
                {customerAssignments.map(a => {
                  const t = customerTransformers.find(x => x.id === a.transformer_id);
                  return (
                    <div key={a.id} style={{ padding: '2px 0' }}>
                      {a.role === 'main' ? '🟧' : '🔌'} {a.meter_serial}
                      {a.object_name ? ` — ${a.object_name}` : ''}
                      {t ? <span style={{ color: c.textDimmed }}>  (via {t.name})</span> : null}
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        marginBottom: '12px',
        background: c.bgBase,
        border: `1px solid ${c.border}`,
        borderRadius: '6px',
        fontSize: '13px',
        color: c.textPrimary,
      }}>
        <input
          id="primary-mode-toggle"
          type="checkbox"
          checked={primaryMode}
          onChange={(e) => setPrimaryMode(e.target.checked)}
          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        />
        <label htmlFor="primary-mode-toggle" style={{ cursor: 'pointer', userSelect: 'none' }}>
          <strong>Primary-side values</strong>
          <span style={{ color: c.textMuted, marginLeft: '8px' }}>
            {primaryMode
              ? '(CT × VT applied — primary side)'
              : '(raw register values from the meter)'}
          </span>
        </label>
      </div>

      <div className="form-group">
        <label className="input-label">
          From (optional — blank = full history)
          <input
            type="date"
            className="input-field"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label className="input-label">
          To (optional — blank = now)
          <input
            type="date"
            className="input-field"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
      </div>

      <div style={{ marginTop: '4px', marginBottom: '4px' }}>
        <QuickRangeButtons setFromDate={setFromDate} setToDate={setToDate} compact />
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
        <button
          className="button button-primary"
          onClick={() => handleDownload('daily_billing')}
          disabled={!customerId || customerAssignments.length === 0 || busyKind !== null}
        >
          {busyKind === 'daily_billing'
            ? <><Spinner /> Generating…</>
            : '🗓 Daily Billing (HistoryData 2)'}
        </button>
        <button
          className="button button-primary"
          onClick={() => handleDownload('load_profile')}
          disabled={!customerId || customerAssignments.length === 0 || busyKind !== null}
        >
          {busyKind === 'load_profile'
            ? <><Spinner /> Generating…</>
            : '📈 LP/PQ Report (merged)'}
        </button>
        <ReportColumnsButton
          reportKeys={['daily_billing', 'load_profile']}
          catalog={reportCols.catalog}
          prefs={reportCols.prefs}
          savePrefs={reportCols.savePrefs}
          summary={reportCols.summary}
        />
        <span style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <ReportLangToggle />
        </span>
      </div>

      {/* Pre-pull controls — explicit async historical fetch from
          the meters when the requested range exceeds the 31-day
          auto-backfill window. Max range 366 days / 12 months
          (meter on-board retention). Runs one job per meter in
          parallel, polled every 10s below. */}
      <div style={{ marginTop: '14px', padding: '10px 12px', background: c.bgBase, border: `1px dashed ${c.border}`, borderRadius: '6px' }}>
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '8px' }}>
          For ranges beyond 31 days, pre-pull from each meter's on-board
          history (up to 12 months / 366 days). One job per meter, runs
          in parallel (~30s per 7-day chunk). Re-download the report
          once status shows <strong>done</strong>.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="button button-secondary"
            onClick={() => handlePrepull('daily_billing')}
            disabled={!customerId || customerAssignments.length === 0 || !fromDate || !toDate}
          >
            ⤓ Pre-pull Daily Billing
          </button>
          <button
            className="button button-secondary"
            onClick={() => handlePrepull('power_quality')}
            disabled={!customerId || customerAssignments.length === 0 || !fromDate || !toDate}
          >
            ⤓ Pre-pull Power Quality
          </button>
        </div>
        {Object.keys(backfillJobs).length > 0 && (
          <div style={{ marginTop: '10px', maxHeight: '180px', overflowY: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
            {Object.values(backfillJobs).map(j => {
              const pct = j.chunks_total > 0
                ? Math.round((j.chunks_done || 0) / j.chunks_total * 100)
                : 0;
              return (
                <div key={j.meter_serial} style={{ padding: '3px 0', color: j.state === 'done' ? c.accent : (j.state === 'failed' ? '#f87171' : c.textPrimary) }}>
                  {j.meter_serial}: <strong>{j.state}</strong>
                  {j.state === 'running' && ` — ${j.chunks_done}/${j.chunks_total} chunks (${pct}%)`}
                  {j.state === 'done' && ` — ${j.rows_stored} rows stored`}
                  {j.state === 'queued' && ` — eta ~${j.estimated_seconds || '?'}s`}
                  {j.last_error && <span style={{ color: '#f87171' }}> · err: {j.last_error}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportsTab() {
  const { selected } = useMeterSelection();
  const c = useThemeColors();
  const toast = useToast();
  const t = useT();
  const { lang } = useLang();

  const [profileName, setProfileName] = useState('load_profile');
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);

  // Date range — initialised once info is loaded.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [rows, setRows] = useState([]);
  const [captures, setCaptures] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {fetched, total}

  // Re-fetch profile info whenever the meter or profile changes. The
  // info call walks the meter to read row 1, row 2 and row N — slow,
  // so we cache the result keyed by `${serial}|${profileName}`.
  const infoCache = useRef({});
  useEffect(() => {
    if (!selected) return;
    const key = `${selected}|${profileName}`;
    if (infoCache.current[key]) {
      setInfo(infoCache.current[key]);
      return;
    }
    setInfo(null);
    setLoadingInfo(true);
    api(`/meters/${selected}/profile/${profileName}/info`)
      .then(d => {
        infoCache.current[key] = d;
        setInfo(d);
        // Default the date pickers to "yesterday → today" when info
        // arrives, clamped to the range the meter actually has.
        if (d.first_ts && d.last_ts) {
          const last = new Date(d.last_ts);
          const first = new Date(d.first_ts);
          const yesterday = new Date(last.getTime() - 86400000);
          const initFrom = yesterday < first ? first : yesterday;
          setFromDate(toDateInput(initFrom));
          setToDate(toDateInput(last));
        }
      })
      .catch(err => {
        toast.push(`Profile info failed: ${err.message}`, 'error');
        setInfo({ entries_in_use: 0, error: err.message });
      })
      .finally(() => setLoadingInfo(false));
  }, [selected, profileName]);

  // Format Date → YYYY-MM-DD for the <input type="date"> fields.
  function toDateInput(d) {
    if (!(d instanceof Date) || isNaN(d)) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // Convert a YYYY-MM-DD picker value into a Date at local midnight.
  function fromDateInput(s) {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  // Map a target Date to a 1-based entry index using the meter's
  // first_ts + interval. This is the inverse of `index → ts`.
  // Caveat: works perfectly when the buffer is contiguous, slightly
  // off when the meter had downtime (extra entries are missing). The
  // user can always nudge from/to dates outward to compensate.
  function dateToIndex(target) {
    if (!info || !info.first_ts || !info.interval_seconds) return null;
    const first = new Date(info.first_ts).getTime();
    const t = target.getTime();
    const idx = 1 + Math.round((t - first) / (info.interval_seconds * 1000));
    return Math.max(1, Math.min(info.entries_in_use, idx));
  }

  const handleRun = async () => {
    if (!selected) return toast.push(t('toast.pick_meter_first'), 'error');
    if (!info || !info.entries_in_use) {
      return toast.push(t('toast.profile_meta_missing'), 'error');
    }
    const fromD = fromDateInput(fromDate);
    const toD = fromDateInput(toDate);
    if (!fromD || !toD) {
      return toast.push(t('toast.pick_dates'), 'error');
    }
    if (toD < fromD) {
      return toast.push('"to" date is before "from" date', 'error');
    }
    // End-of-day for the "to" picker.
    const toEnd = new Date(toD.getTime() + 86400000 - 1000);

    const startIdx = dateToIndex(fromD);
    const endIdx = dateToIndex(toEnd);
    if (startIdx == null || endIdx == null) {
      return toast.push(t('toast.to_before_from'), 'error');
    }
    const count = Math.max(1, endIdx - startIdx + 1);
    if (count > 5000) {
      return toast.push(
        `That date range covers ${count} rows — please narrow it (max 5000).`,
        'error',
      );
    }

    setBusy(true);
    setProgress({ fetched: 0, total: count });
    setRows([]);
    setCaptures([]);
    try {
      const url = `/meters/${selected}/profile/${profileName}/fetch?entries=${count}&start_index=${startIdx}`;
      const res = await api(url, { method: 'POST' });
      const fetched = (res.rows || []);
      setRows(fetched);
      setCaptures(res.captures || []);
      setProgress({ fetched: fetched.length, total: count });
      toast.push(
        `Loaded ${fetched.length} rows (stored ${res.stored_rows ?? '?'})`,
        'success',
      );
    } catch (err) {
      toast.push(`Report fetch failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleQuickRange = (label) => {
    if (!info || !info.last_ts) return;
    const last = new Date(info.last_ts);
    const ranges = {
      today: 0,
      '7d': 7,
      '30d': 30,
      '90d': 90,
      all: null,
    };
    const days = ranges[label];
    if (days === null) {
      // "all" — clamp to the meter's available range
      setFromDate(toDateInput(new Date(info.first_ts)));
      setToDate(toDateInput(last));
      return;
    }
    const from = new Date(last.getTime() - days * 86400000);
    setFromDate(toDateInput(from));
    setToDate(toDateInput(last));
  };

  const choice = PROFILE_CHOICES.find(p => p.value === profileName) || PROFILE_CHOICES[0];

  // Resolve column labels + scales through the shared helper so
  // ReportsTab stays in sync with MeterDetailReadingsTab.
  // PROFILE_CHOICES.columns is used as a last-ditch fallback when we
  // have no captures at all (e.g. the meter never reported any
  // profile_meta and the backend's _PROFILE_FALLBACK_COLUMNS
  // synthesis didn't match the profile).
  const { labels: columnLabels, scales: columnScales } = (() => {
    if (captures && captures.length > 0) {
      return resolveProfileColumns(captures);
    }
    if (choice.columns) {
      return { labels: choice.columns, scales: choice.columns.map(() => 1) };
    }
    if (rows.length > 0) {
      return {
        labels: rows[0].values.map((_, i) => `Col ${i}`),
        scales: rows[0].values.map(() => 1),
      };
    }
    return { labels: [], scales: [] };
  })();
  const renderCell = (v, j) => renderProfileCell(v, columnScales[j]);

  const handleExportCSV = () => {
    if (rows.length === 0) return;
    const header = ['#', 'Timestamp', ...columnLabels];
    const csvRows = rows.map((r, i) => {
      // Apply the same scale divisor to CSV cells so the exported
      // file matches what the operator sees on screen. Numeric
      // cells go through renderProfileCell (which divides by scale
      // and formats); strings are CSV-escaped.
      const cells = (r.values || []).map((v, j) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'number') {
          const scale = columnScales[j] || 1;
          const scaled = scale === 1 ? v : v / scale;
          return String(scaled);
        }
        const s = String(v);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      });
      return [i + 1, r.ts || '', ...cells].join(',');
    });
    const csv = [header.join(','), ...csvRows].join('\n');
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `${profileName}_${selected}_${fromDate}_to_${toDate || stamp}.csv`);
  };

  // XLSX export — generated server-side via openpyxl. Hits the new
  // /api/meters/{serial}/profile/{name}/export.xlsx endpoint with the
  // same date filter. Has to bypass the api() helper because the
  // response is binary, not JSON. Auth header is added manually.
  const handleExportXLSX = async () => {
    if (!selected) return;
    try {
      const params = new URLSearchParams();
      params.set('lang', getReportLang() || lang);
      if (fromDate) params.set('from_ts', String(dayStart(fromDate)));
      if (toDate)   params.set('to_ts',   String(dayEnd(toDate)));
      const url = `/api/meters/${encodeURIComponent(selected)}/profile/${encodeURIComponent(profileName)}/export.xlsx?${params}`;
      const filename = `${profileName}_${selected}_${fromDate}_to_${toDate || new Date().toISOString().slice(0, 10)}.xlsx`;
      await downloadXlsx(url, filename, { toast });
    } catch (_) { /* toast already shown by helper */ }
  };

  return (
    <div className="tab-content">
      <div className="section-title">📊 {t('section.reports')}</div>

      {/* Primary workflow — customer-first Holley reports. */}
      <CustomerReportsPanel />

      {/* Advanced per-meter flow lives in a collapsible below so it
          stays accessible without cluttering the default view. It's
          the right tool for load-profile drill-down on a single
          meter, or for forcing an on-demand fetch from the meter
          buffer before pulling a customer report. */}
      <details style={{
        background: c.bgBase,
        border: `1px solid ${c.border}`,
        borderRadius: '8px',
        padding: '12px 20px',
        marginTop: '8px',
      }}>
        <summary style={{
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          color: c.textLabel,
          padding: '4px 0',
        }}>
          🔍 Advanced — per-meter profile explorer
        </summary>
        <div style={{ fontSize: '12px', color: c.textMuted, marginTop: '4px', marginBottom: '12px' }}>
          Drill into a single meter's profile buffer (load profile,
          power quality, daily/monthly billing) and force an on-demand
          fetch from the meter itself. Requires an active session with
          the meter.
        </div>
      <MeterSelector requireSession={true} />

      {/* Profile picker */}
      <div className="form-group">
        <label className="input-label" style={{ flex: 1 }}>
          Report type
          <select
            className="input-field"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
          >
            {PROFILE_CHOICES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginTop: '-8px', marginBottom: '12px' }}>
        {choice.hint}
      </div>

      {/* Profile info banner */}
      {loadingInfo && (
        <div style={{
          padding: '12px 16px',
          background: c.bgElevated,
          border: `1px solid ${c.borderInput}`,
          borderRadius: '6px',
          marginBottom: '16px',
          color: c.textSecondary,
          fontSize: '13px',
        }}>
          <Spinner /> Reading profile metadata from meter…
        </div>
      )}
      {info && info.entries_in_use > 0 && !loadingInfo && (
        <div style={{
          padding: '12px 16px',
          background: c.bgElevated,
          border: `1px solid ${c.borderInput}`,
          borderRadius: '6px',
          marginBottom: '16px',
          color: c.textLabel,
          fontSize: '13px',
          display: 'flex',
          gap: '24px',
          flexWrap: 'wrap',
        }}>
          <span><strong>Stored on meter:</strong> {info.entries_in_use.toLocaleString()} rows</span>
          <span><strong>Earliest:</strong> {info.first_ts ? fmtDT(info.first_ts) : '—'}</span>
          <span><strong>Latest:</strong> {info.last_ts ? fmtDT(info.last_ts) : '—'}</span>
          <span><strong>Interval:</strong> {info.interval_seconds ? formatInterval(info.interval_seconds) : '—'}</span>
        </div>
      )}
      {info && info.entries_in_use === 0 && !loadingInfo && (
        <div style={{
          padding: '12px 16px',
          background: '#3f1d1d',
          border: '1px solid #7c2d2d',
          borderRadius: '6px',
          marginBottom: '16px',
          color: '#fca5a5',
          fontSize: '13px',
        }}>
          ⚠️ This profile has no entries on the meter, or it's not accessible from this association.
        </div>
      )}

      {/* Date range */}
      <div className="form-group">
        <label className="input-label">
          From
          <input
            type="date"
            className="input-field"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            disabled={!info || info.entries_in_use === 0}
          />
        </label>
        <label className="input-label">
          To
          <input
            type="date"
            className="input-field"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            disabled={!info || info.entries_in_use === 0}
          />
        </label>
      </div>

      {/* Quick presets */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <button className="button button-secondary" onClick={() => handleQuickRange('today')} disabled={!info}>
          Today
        </button>
        <button className="button button-secondary" onClick={() => handleQuickRange('7d')} disabled={!info}>
          Last 7 days
        </button>
        <button className="button button-secondary" onClick={() => handleQuickRange('30d')} disabled={!info}>
          Last 30 days
        </button>
        <button className="button button-secondary" onClick={() => handleQuickRange('90d')} disabled={!info}>
          Last 90 days
        </button>
        <button className="button button-secondary" onClick={() => handleQuickRange('all')} disabled={!info}>
          All available
        </button>
      </div>

      {/* Run + export */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <button
          className="button button-primary"
          onClick={handleRun}
          disabled={busy || !selected || !info || info.entries_in_use === 0}
        >
          {busy ? <><Spinner /> Fetching…</> : '📥 Generate Report'}
        </button>
        <button
          className="button button-secondary"
          onClick={handleExportCSV}
          disabled={rows.length === 0}
        >
          📤 Export CSV
        </button>
        <button
          className="button button-secondary"
          onClick={handleExportXLSX}
          disabled={rows.length === 0}
          title="Server-side Excel export (.xlsx)"
        >
          📊 Export Excel
        </button>
        {progress && rows.length > 0 && (
          <span style={{ alignSelf: 'center', color: c.textMuted, fontSize: '13px' }}>
            {rows.length.toLocaleString()} rows
          </span>
        )}
      </div>

      {/* Results */}
      {rows.length > 0 && (
        <div className="table-container" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <table>
            <thead style={{ position: 'sticky', top: 0, background: c.bgCard, zIndex: 1 }}>
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th style={{ minWidth: '160px' }}>Timestamp</th>
                {columnLabels.map((label, i) => (
                  <th key={i}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td style={{ color: c.textMuted }}>{idx + 1}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {row.ts ? fmtDT(row.ts) : '—'}
                  </td>
                  {columnLabels.map((_, i) => (
                    <td key={i} style={{ fontFamily: 'monospace', textAlign: 'right' }}>
                      {renderCell((row.values || [])[i], i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!busy && rows.length === 0 && info && info.entries_in_use > 0 && (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: c.textMuted,
          fontSize: '13px',
        }}>
          Pick a date range and click <strong>Generate Report</strong> to pull rows from the meter.
        </div>
      )}
      </details>
    </div>
  );
}

function formatInterval(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(0)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(0)} day${seconds >= 172800 ? 's' : ''}`;
}

// Billing Tab — reads the billing group from a meter via the read-group API.
function BillingTab() {
  const t = useT();
  const { selected } = useMeterSelection();
  const toast = useToast();
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);

  const handleLoadBilling = async () => {
    if (!selected) {
      toast.push(t('toast.pick_meter_first'), 'error');
      return;
    }
    setBusy(true);
    try {
      const result = await api(`/meters/${selected}/read-group/billing`);
      setResults(result.results || {});
      toast.push(t('toast.billing_loaded'), 'success');
    } catch (err) {
      toast.push(`Read billing failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleExportCSV = () => {
    if (data.length === 0) return;
    const rows = Object.entries(results).map(([obis, val]) => [obis, String(val ?? '')]);
    const csv = [['OBIS', 'Value'], ...rows].map(r => r.join(',')).join('\n');
    downloadCsv(csv, `billing_${selected || 'meter'}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="tab-content">
      <div className="section-title">💰 {t('section.billing')}</div>
      <MeterSelector requireSession={true} />

      <div style={{ display: 'flex', gap: '12px' }}>
        <button className="button button-primary" onClick={handleLoadBilling} disabled={busy || !selected}>
          {busy ? <Spinner /> : '📈 Load Billing'}
        </button>
        <button
          className="button button-secondary"
          onClick={handleExportCSV}
          disabled={Object.keys(results).length === 0}
        >
          📥 Export CSV
        </button>
      </div>

      {Object.keys(results).length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table>
            <thead>
              <tr><th>{t('th.obis')}</th><th>{t('th.value')}</th></tr>
            </thead>
            <tbody>
              {Object.entries(results).map(([obis, val]) => (
                <tr key={obis}>
                  <td><code>{obis}</code></td>
                  <td>{val === null ? '—' : <strong>{String(val)}</strong>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// TOU Config Tab
// (TOUConfigTab removed in Phase 4.1 — replaced by MeterDetailTariffTab.)

// COM Log Tab
function COMLogTab() {
  const { messages, connected, clear } = useWebSocket('/ws/frames');
  const t = useT();
  const c = useThemeColors();
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, autoScroll]);

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
          />
          <span>Auto-scroll</span>
        </label>
        <button className="button button-danger button-sm" onClick={clear}>
          🗑️ Clear
        </button>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: '12px', color: c.textSecondary }}>
            {connected ? `🟢 ${t('comlog.connected')}` : `🔴 ${t('comlog.disconnected')}`}
          </span>
        </div>
      </div>

      <div
        className="table-container"
        ref={scrollRef}
        style={{
          maxHeight: '600px',
          overflow: 'auto'
        }}
      >
        <table>
          <thead>
            <tr>
              <th style={{ width: '160px' }}>{t('th.timestamp')}</th>
              <th style={{ width: '80px' }}>{t('th.direction')}</th>
              <th style={{ width: '60px' }}>{t('th.length')}</th>
              <th>{t('th.hex_data')}</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', color: c.textMuted }}>
                  {t('comlog.waiting')}
                </td>
              </tr>
            ) : (
              messages.map((msg, idx) => (
                <React.Fragment key={idx}>
                  <tr onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontSize: '11px' }}>
                      {fmtDT(msg.timestamp, { utc: true, seconds: true })}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          background: msg.direction === 'TX' ? `${c.accent}33` : `${c.success}33`,
                          color: msg.direction === 'TX' ? c.accent : c.success,
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}
                      >
                        {msg.direction}
                      </span>
                    </td>
                    <td style={{ fontSize: '11px' }}>{msg.length}</td>
                    <td style={{ fontSize: '11px', fontFamily: 'monospace', color: c.accent, maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {msg.hex?.slice(0, 60)}...
                    </td>
                  </tr>
                  {expandedIdx === idx && (
                    <tr>
                      <td colSpan="4" style={{ background: c.bgCardAlt, padding: '12px' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', color: c.accent, wordBreak: 'break-all' }}>
                          {msg.hex}
                        </div>
                        {msg.description && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: c.textSecondary }}>
                            📝 {msg.description}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Keys Tab
function KeysTab() {
  const c = useThemeColors();
  const t = useT();
  const confirm = useConfirm();
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  // Per-row selection for partial deletes. Set of serial strings;
  // cleared on every refresh so a stale serial that vanished from
  // the server doesn't sit checked in the UI.
  const [selected, setSelected] = useState(() => new Set());

  const refreshKeys = useCallback(async () => {
    try {
      const data = await api('/keys');
      const list = data.keys || [];
      setKeys(list);
      // Drop any selected serials that no longer exist server-side.
      setSelected(prev => {
        const live = new Set(list.map(k => k.serial));
        const next = new Set();
        for (const s of prev) if (live.has(s)) next.add(s);
        return next;
      });
    } catch (err) {
      console.error('Keys fetch error:', err);
    }
  }, []);

  useEffect(() => {
    refreshKeys();
    // 15 s (was 5 s): the key list only changes on imports; a 5 s poll over a
    // fleet-sized key table was needless load on the System tab.
    const t = setInterval(refreshKeys, 15000);
    return () => clearInterval(t);
  }, [refreshKeys]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Multipart upload — bypasses the api() helper because that hard-
      // codes Content-Type: application/json which would override the
      // multipart boundary. credentials:'include' attaches the auth
      // cookie. State-changing request → must echo CSRF token.
      const headers = {};
      const csrf = getCsrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;
      const res = await fetch('/api/keys/upload', {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'include',
      });

      if (res.status === 401) {
        try { window.dispatchEvent(new Event('auth-expired')); } catch (_) {}
      }
      if (!res.ok) {
        let detail = res.statusText;
        try { detail = (await res.json()).detail || res.statusText; } catch (_) {}
        throw new Error(detail);
      }

      const result = await res.json();
      toast.push(
        `Imported ${result.keys_imported} keys (store now contains ${result.store_size})`,
        'success'
      );
      await refreshKeys();
    } catch (err) {
      toast.push(`Upload failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearAll = async () => {
    if (!(await confirm('Clear all DLMS keys?', 'Connected meters will lose authentication. This action cannot be undone.'))) return;
    try {
      await api('/keys', { method: 'DELETE' });
      toast.push(t('toast.keys_cleared'), 'info');
      await refreshKeys();
    } catch (err) {
      toast.push(`Clear failed: ${err.message}`, 'error');
    }
  };

  const toggleOne = (serial) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial); else next.add(serial);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === keys.length
      ? new Set()
      : new Set(keys.map(k => k.serial))
    );
  };

  const handleDeleteSelected = async () => {
    const serials = Array.from(selected);
    if (serials.length === 0) return;
    // Two-step confirmation: first scopes the action, second is the
    // hard "are you absolutely sure" gate. Same protective pattern
    // GitHub uses for deleting a repo — the operator has to read the
    // count twice before the request goes out.
    const first = await confirm(
      `Delete ${serials.length} selected key(s)?`,
      'Connected meters will lose authentication on their next dial-in. This action cannot be undone.'
    );
    if (!first) return;
    const second = await confirm(
      'Are you absolutely sure?',
      `Deleting ${serials.length} key(s): ${serials.slice(0, 5).join(', ')}${serials.length > 5 ? ` … (+${serials.length - 5} more)` : ''}`
    );
    if (!second) return;

    setBusy(true);
    let ok = 0, failed = 0;
    for (const serial of serials) {
      try {
        await api(`/keys/by-serial/${encodeURIComponent(serial)}`, { method: 'DELETE' });
        ok++;
      } catch (_) {
        failed++;
      }
    }
    setBusy(false);
    if (failed === 0) {
      toast.push(`Deleted ${ok} key(s)`, 'success');
    } else {
      toast.push(`Deleted ${ok} key(s); ${failed} failed`, 'error');
    }
    await refreshKeys();
  };

  return (
    <div className="tab-content">
      <div className="section-title">🔑 {t('section.meter_keys')}</div>

      <div className="drag-drop" onClick={() => fileInputRef.current?.click()}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📤</div>
        <div>{busy ? 'Uploading…' : 'Click to upload .xls / .xlsx / .csv keyfile'}</div>
        <div style={{ fontSize: '12px', color: c.textSecondary, marginTop: '8px' }}>
          Holley CAS keyfile format auto-detected
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx,.csv"
          onChange={handleUpload}
          disabled={busy}
          style={{ display: 'none' }}
        />
      </div>

      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '20px',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ fontSize: '14px', color: c.textSecondary }}>
          📊 Keys loaded: <strong>{keys.length}</strong>
          {selected.size > 0 && (
            <span style={{ marginLeft: '12px', color: c.accent }}>
              · {selected.size} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="button button-danger button-sm"
            onClick={handleDeleteSelected}
            disabled={selected.size === 0 || busy}
          >
            🗑️ Delete Selected ({selected.size})
          </button>
          <button
            className="button button-danger button-sm"
            onClick={handleClearAll}
            disabled={keys.length === 0 || busy}
          >
            🗑️ Clear All
          </button>
        </div>
      </div>

      {keys.length > 0 && (
        <div className="table-container" style={{ marginTop: '20px' }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: '32px' }}>
                  <input
                    type="checkbox"
                    checked={selected.size === keys.length && keys.length > 0}
                    ref={(el) => {
                      if (el) el.indeterminate =
                        selected.size > 0 && selected.size < keys.length;
                    }}
                    onChange={toggleAll}
                    title="Select / deselect all"
                  />
                </th>
                <th>{t('th.serial')}</th>
                <th>Logical Device Name</th>
                <th>Mfr</th>
                <th>Auth Key</th>
                <th>Cipher Key</th>
                <th>Master Key</th>
                <th>Dedicated</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.serial}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(key.serial)}
                      onChange={() => toggleOne(key.serial)}
                    />
                  </td>
                  <td><code>{key.serial}</code></td>
                  <td>{key.logical_device_name || '-'}</td>
                  <td>{key.manufacturer_id || '-'}</td>
                  <td>{key.has_auth_key ? '✓' : '—'}</td>
                  <td>{key.has_cipher_key ? '✓' : '—'}</td>
                  <td>{key.has_master_key ? '✓' : '—'}</td>
                  <td>{key.has_dedicated_key ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '16px', fontSize: '12px', color: c.textSecondary }}>
        Once a key is loaded, the next time the matching meter dials in (within 2 minutes),
        the server will automatically authenticate, open a secure session, and start polling
        live voltage/current/power/energy values.
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// AuthProvider — owns the current user state and exposes login / logout.
//
// On mount: if a token is in localStorage, hit /api/auth/me to validate
// it. If valid, populate the user; if 401, clear it and show login.
//
// On login(): POST /api/auth/login, store the token, populate user.
// On logout(): clear localStorage and reset user to null.
//
// Listens for the global `auth-expired` event so any 401 from the api()
// helper bounces the user back to the login screen automatically.
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Cookie-based auth: the only way to know if we're logged in is to
    // hit /auth/me and see whether the cookie is accepted. If the
    // browser has no emeter_auth cookie the backend returns 401 and
    // api() throws — we interpret that as "show the login screen".
    //
    // First call only: index.html already fired this request from a tiny
    // inline <script> before the bundle was even downloaded, so the
    // answer is usually waiting by the time we mount. Issuing it here
    // instead cost a full round trip that could not start until 744 KB
    // had parsed — measured p50 0.72 s of blank "Authenticating…" screen.
    // Take the pre-flight ONCE and clear it, so the refresh() after
    // login (and any later one) still asks the server.
    const preflight = window.__emeterAuthPromise;
    if (preflight) {
      window.__emeterAuthPromise = null;
      try {
        const me = await preflight;
        // null = server said 401 (definitively logged out).
        // undefined = the pre-flight itself failed (offline, blocked,
        // no fetch) and told us nothing — fall through and ask properly
        // rather than bouncing a logged-in operator to the login screen.
        if (me !== undefined) {
          setUser(me || null);
          setLoading(false);
          return;
        }
      } catch (_) {
        // Defensive: the pre-flight is written not to reject, but a
        // rejection here must not be fatal — fall through to api().
      }
    }
    try {
      const me = await api('/auth/me');
      setUser(me);
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username, password) => {
    // The backend sets emeter_auth + emeter_csrf cookies on the
    // response; api() uses credentials:'include' so the browser
    // stores them. Response body still carries user info.
    const res = await api('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Backend clears both cookies; safe to call even if already logged out.
      await api('/auth/logout', { method: 'POST' });
    } catch (_) {}
    // Drop session-scoped nav state so the next login starts fresh
    // (Customers → All Customers, no stale DCU/tab selection).
    _clearNavSession();
    setUser(null);
  }, []);

  useEffect(() => {
    refresh();
    const onExpired = () => { setUser(null); };
    window.addEventListener('auth-expired', onExpired);
    return () => window.removeEventListener('auth-expired', onExpired);
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh }}>
      {loading ? (
        <div style={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'var(--bg-base, #0a0e17)', color: 'var(--accent, #00e5ff)', fontSize: '16px',
        }}>
          Authenticating…
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}

// Login screen — full-viewport gradient background with a centered card.
// Renders only when AuthContext.user is null. On successful login the
// AuthContext flips and the dashboard takes over automatically.
function LoginScreen() {
  const { login } = useAuth();
  const t = useT();
  const c = useThemeColors();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await login(username.trim(), password);
    } catch (ex) {
      setErr(ex.message || t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: `radial-gradient(circle at 30% 20%, ${c.bgElevated} 0%, ${c.bgBase} 60%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <style>{styles}</style>

      {/* Language + theme switch — top-right corner of the login screen
          so users can pick language/theme before signing in. */}
      <div style={{ position: 'fixed', top: '16px', right: '16px', display: 'flex', gap: '8px' }}>
        <LanguageSwitch />
        <ThemeSwitch />
      </div>

      <form onSubmit={handleSubmit} style={{
        // 380px flat overflowed every phone narrower than that (iPhone SE
        // is 375px), pushing the card off-centre with the password field
        // partly off-screen. Cap to the viewport and keep the generous
        // padding only where there is room for it.
        width: 'min(380px, 92vw)',
        padding: 'clamp(22px, 5vw, 36px) clamp(18px, 5vw, 32px)',
        maxHeight: '92vh',
        overflowY: 'auto',
        background: c.bgCard,
        border: `1px solid ${c.border}`,
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '4px',
          background: 'linear-gradient(135deg, #00e5ff, #7c3aed)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          {t('login.title')}
        </div>
        <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '24px' }}>
          {t('login.subtitle')}
        </div>

        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('login.username')}
          <input
            type="text"
            className="input-field"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            disabled={busy}
          />
        </label>

        <label className="input-label" style={{ display: 'block', marginBottom: '20px' }}>
          {t('login.password')}
          <input
            type="password"
            className="input-field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={busy}
          />
        </label>

        {err && (
          <div style={{
            padding: '10px 12px',
            marginBottom: '16px',
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.4)',
            borderRadius: '6px',
            color: '#fca5a5',
            fontSize: '13px',
          }}>
            {err}
          </div>
        )}

        <button
          type="submit"
          className="button button-primary"
          style={{ width: '100%' }}
          disabled={busy || !username || !password}
        >
          {busy ? <Spinner /> : t('login.submit')}
        </button>

      </form>
    </div>
  );
}

// Change-password modal — opens from the user menu in the sidebar.
function ChangePasswordModal({ onClose }) {
  const toast = useToast();
  const t = useT();
  const c = useThemeColors();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.push(t('toast.pw_no_match'), 'error');
      return;
    }
    if (next.length < 8) {
      toast.push(t('toast.pw_too_short'), 'error');
      return;
    }
    setBusy(true);
    try {
      await api('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      toast.push(t('toast.password_updated'), 'success');
      onClose();
    } catch (err) {
      toast.push(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()} style={{
        background: c.bgCard,
        padding: '28px',
        borderRadius: '10px',
        border: `1px solid ${c.border}`,
        // Every other modal in the app caps its width against the viewport;
        // this one did not, so on a phone narrower than 360px the dialog
        // ran off the right edge taking the Save button with it.
        width: 'min(360px, 92vw)',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <div className="section-title" style={{ marginBottom: '16px' }}>
          🔐 {t('modal.change_password')}
        </div>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.current_password')}
          <input type="password" className="input-field" autoComplete="current-password" value={current} required disabled={busy} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '12px' }}>
          {t('form.new_password')}
          <input type="password" className="input-field" autoComplete="new-password" value={next} required disabled={busy} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="input-label" style={{ display: 'block', marginBottom: '20px' }}>
          {t('form.confirm_password')}
          <input type="password" className="input-field" autoComplete="new-password" value={confirm} required disabled={busy} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="submit" className="button button-primary" disabled={busy} style={{ flex: 1 }}>
            {busy ? <Spinner /> : t('btn.update')}
          </button>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t('btn.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Idle Timer — auto-logout after N minutes of inactivity.
//
// Tracks mouse/keyboard/touch/scroll events to detect activity.
// Uses a ref for the last-activity timestamp (no React re-render
// on each event). A 1-second setInterval checks if the elapsed
// idle time exceeds the configured threshold. If yes, shows a
// 60-second warning countdown; if the countdown completes without
// interaction, calls logout().
//
// Respects document.visibilitychange — pauses the check when the
// tab is hidden so switching to another app doesn't trigger a
// false timeout. The timeout setting is stored in localStorage
// under 'apm_idle_timeout' (minutes, 0 = disabled). The default
// is 15 minutes.
// ─────────────────────────────────────────────────────────────
const IDLE_TIMEOUT_KEY = 'apm_idle_timeout';
const IDLE_DEFAULT_MINUTES = 15;
const IDLE_WARNING_SECONDS = 60;

function IdleTimer() {
  const { logout } = useAuth();
  const t = useT();
  const c = useThemeColors();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(IDLE_WARNING_SECONDS);
  const lastActivityRef = useRef(Date.now());
  const pausedAtRef = useRef(null);

  // Read the configured timeout (minutes) from localStorage.
  const getTimeoutMs = () => {
    try {
      const v = parseInt(localStorage.getItem(IDLE_TIMEOUT_KEY), 10);
      if (v === 0) return 0; // disabled
      if (v > 0 && v <= 120) return v * 60 * 1000;
    } catch (_) {}
    return IDLE_DEFAULT_MINUTES * 60 * 1000;
  };

  useEffect(() => {
    // Activity handler — just stamps the ref, no state update.
    const onActivity = () => { lastActivityRef.current = Date.now(); };

    // Attach passive listeners for common user activity signals.
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    // Visibility change — pause/resume when tab is hidden/visible.
    const onVisibility = () => {
      if (document.hidden) {
        pausedAtRef.current = Date.now();
      } else {
        // When tab becomes visible again, credit the hidden time as
        // activity so we don't immediately timeout after switching
        // back from another app.
        if (pausedAtRef.current) {
          lastActivityRef.current = Date.now();
          pausedAtRef.current = null;
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Tick every second to check idle time.
    const interval = setInterval(() => {
      const timeoutMs = getTimeoutMs();
      if (timeoutMs === 0 || document.hidden) return; // disabled or tab hidden

      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= timeoutMs) {
        setShowWarning(true);
      }
    }, 1000);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, []);

  // Warning countdown — runs when the warning modal is visible.
  useEffect(() => {
    if (!showWarning) { setCountdown(IDLE_WARNING_SECONDS); return; }
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showWarning, logout]);

  const stay = () => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  };

  if (!showWarning) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
    onClick={stay}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: c.bgCard,
          border: `1px solid ${c.border}`,
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '400px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏰</div>
        <div style={{ fontSize: '18px', fontWeight: 600, color: c.textPrimary, marginBottom: '8px' }}>
          {t('idle.title')}
        </div>
        <div style={{ fontSize: '13px', color: c.textSecondary, marginBottom: '20px', lineHeight: 1.5 }}>
          {t('idle.warning', { n: countdown })}
        </div>
        <div style={{ fontSize: '32px', fontWeight: 700, color: countdown <= 10 ? c.danger : c.warning, marginBottom: '20px' }}>
          {countdown}
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button className="button button-primary" onClick={stay}>
            {t('idle.stay')}
          </button>
          <button className="button button-danger" onClick={logout}>
            {t('idle.logout_now')}
          </button>
        </div>
      </div>
    </div>
  );
}


// External Servers — admin-only top-level tab. Lists every customer
// install that's checked in to this central, with revoke/restore +
// view-last-summary controls. Hidden on customer installs (the table
// is empty there) by gating tab visibility on row count > 0.
function ExternalServersTab() {
  const c = useThemeColors();
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [showRegister, setShowRegister] = React.useState(false);
  const [registered, setRegistered] = React.useState(null);  // {install_id, checkin_secret}
  const [expanded, setExpanded] = React.useState({});         // install_id -> bool
  const [revokeRow, setRevokeRow] = React.useState(null);     // row pending revoke + reason
  const [revokeReason, setRevokeReason] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    api('/external-servers')
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(err => toast.push(err.message, 'error'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);

  const onRegister = async (customer_name, fqdn) => {
    try {
      const r = await api('/external-servers', {
        method: 'POST',
        body: JSON.stringify({
          customer_name,
          fqdn: fqdn || null,
        }),
      });
      if (!r?.checkin_secret_b64) {
        toast.push('Registered but secret missing in response', 'error');
        return;
      }
      setRegistered({
        install_id: r.install_id,
        checkin_secret: r.checkin_secret_b64,
        customer_name: r.customer_name,
      });
      setShowRegister(false);
      toast.push(t('extsrv.toast_registered'), 'success');
      refresh();
    } catch (err) {
      toast.push(`Register failed: ${err.message}`, 'error');
    }
  };

  const onRevoke = async () => {
    if (!revokeRow) return;
    try {
      await api(`/external-servers/${revokeRow.install_id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason: revokeReason || null }),
      });
      toast.push(t('extsrv.toast_revoked'), 'success');
      setRevokeRow(null);
      setRevokeReason('');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  const onRestore = async (row) => {
    if (!(await confirm(t('extsrv.confirm_restore_q'), t('extsrv.confirm_restore_b')))) return;
    try {
      await api(`/external-servers/${row.install_id}/restore`, { method: 'POST' });
      toast.push(t('extsrv.toast_restored'), 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  const onDelete = async (row) => {
    if (!(await confirm(t('extsrv.confirm_delete_q'), t('extsrv.confirm_delete_b')))) return;
    try {
      await api(`/external-servers/${row.install_id}`, { method: 'DELETE' });
      toast.push(t('extsrv.toast_deleted'), 'success');
      refresh();
    } catch (err) { toast.push(err.message, 'error'); }
  };

  const fmtRelative = (iso) => {
    if (!iso) return t('extsrv.cell_never');
    try {
      const ts = new Date(iso).getTime();
      const dSec = Math.max(0, (Date.now() - ts) / 1000);
      if (dSec < 60)    return `${Math.round(dSec)}s ago`;
      if (dSec < 3600)  return `${Math.round(dSec / 60)}m ago`;
      if (dSec < 86400) return `${Math.round(dSec / 3600)}h ago`;
      return `${Math.round(dSec / 86400)}d ago`;
    } catch (_) { return iso; }
  };

  const statusPill = (row) => {
    if (!row.active) {
      return <span style={{ color: '#e57373', fontWeight: 600 }}>{t('extsrv.status_revoked')}</span>;
    }
    if (row.stale) {
      return <span style={{ color: '#f0b400', fontWeight: 600 }}>{t('extsrv.status_stale')}</span>;
    }
    return <span style={{ color: '#4caf50', fontWeight: 600 }}>{t('extsrv.status_active')}</span>;
  };

  const cellStyle = { padding: '10px 8px', borderBottom: `1px solid ${c.border}`, fontSize: '13px' };

  return (
    <div className="tab-content">
      <div className="section-title">{t('extsrv.title')}</div>
      <div style={{ fontSize: '12px', color: c.textMuted, marginBottom: '14px', maxWidth: '900px' }}>
        {t('extsrv.intro')}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <button className="button button-primary" onClick={() => { setShowRegister(true); setRegistered(null); }}>
          {t('extsrv.btn_register')}
        </button>
        <button className="button button-secondary" onClick={refresh}>{t('extsrv.btn_refresh')}</button>
      </div>

      {registered && (
        <div style={{
          background: c.bgElevated, border: `1px solid ${c.accent}`, padding: '14px',
          borderRadius: '6px', marginBottom: '14px',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: c.accent }}>
            {t('extsrv.flash_secret')}
          </div>
          <div style={{ fontSize: '12px', marginBottom: '4px' }}>
            <strong>{t('extsrv.label_install_id')}</strong>{' '}
            <code style={{ background: c.bg, padding: '2px 6px', borderRadius: '3px', userSelect: 'all' }}>
              {registered.install_id}
            </code>{' '}
            <button
              className="button button-secondary"
              style={{ padding: '2px 8px', fontSize: '11px' }}
              onClick={() => {
                navigator.clipboard?.writeText(registered.install_id);
                toast.push(t('apim.toast_copied') || 'Copied', 'success');
              }}>{t('extsrv.btn_copy')}</button>
          </div>
          <div style={{ fontSize: '12px', marginBottom: '8px' }}>
            <strong>{t('extsrv.label_secret')}</strong>{' '}
            <code style={{
              background: c.bg, padding: '2px 6px', borderRadius: '3px',
              userSelect: 'all', wordBreak: 'break-all',
            }}>{registered.checkin_secret}</code>{' '}
            <button
              className="button button-secondary"
              style={{ padding: '2px 8px', fontSize: '11px' }}
              onClick={() => {
                navigator.clipboard?.writeText(registered.checkin_secret);
                toast.push(t('apim.toast_copied') || 'Copied', 'success');
              }}>{t('extsrv.btn_copy')}</button>
          </div>
          <button
            className="button button-secondary"
            style={{ padding: '4px 12px', fontSize: '12px' }}
            onClick={() => setRegistered(null)}>{t('extsrv.btn_dismiss')}</button>
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div style={{ color: c.textMuted, fontSize: '13px', padding: '14px 0' }}>
          {t('extsrv.empty')}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${c.border}`, textAlign: 'left' }}>
                <th style={cellStyle}>{t('extsrv.col_customer')}</th>
                <th style={cellStyle}>{t('extsrv.col_install')}</th>
                <th style={cellStyle}>{t('extsrv.col_fqdn')}</th>
                <th style={cellStyle}>{t('extsrv.col_last_checkin')}</th>
                <th style={{ ...cellStyle, textAlign: 'right' }}>{t('extsrv.col_meters')}</th>
                <th style={cellStyle}>{t('extsrv.col_status')}</th>
                <th style={cellStyle}>{t('extsrv.col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const isOpen = !!expanded[row.install_id];
                return (
                  <React.Fragment key={row.install_id}>
                    <tr>
                      <td style={cellStyle}>{row.customer_name}</td>
                      <td style={{ ...cellStyle, fontFamily: 'monospace', fontSize: '11px' }}
                          title={row.install_id}>
                        {row.install_id.slice(0, 8)}…
                      </td>
                      <td style={cellStyle}>{row.fqdn || '—'}</td>
                      <td style={cellStyle} title={row.last_checkin_at || ''}>
                        {fmtRelative(row.last_checkin_at)}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>
                        {row.last_meter_count ?? '—'}
                      </td>
                      <td style={cellStyle}>{statusPill(row)}</td>
                      <td style={cellStyle}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button
                            className="button button-secondary"
                            style={{ padding: '4px 8px', fontSize: '11px' }}
                            onClick={() => setExpanded(p => ({ ...p, [row.install_id]: !isOpen }))}>
                            {isOpen ? t('extsrv.btn_hide_summary') : t('extsrv.btn_view_summary')}
                          </button>
                          {row.active ? (
                            <button
                              className="button button-danger"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => { setRevokeRow(row); setRevokeReason(''); }}>
                              {t('extsrv.btn_revoke')}
                            </button>
                          ) : (
                            <button
                              className="button button-primary"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => onRestore(row)}>
                              {t('extsrv.btn_restore')}
                            </button>
                          )}
                          <button
                            className="button button-secondary"
                            style={{ padding: '4px 8px', fontSize: '11px' }}
                            onClick={() => onDelete(row)}>
                            {t('extsrv.btn_delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ padding: '12px', background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: c.textSecondary }}>
                            {t('extsrv.summary_title')}
                          </div>
                          <pre style={{
                            background: c.bgElevated, padding: '10px', borderRadius: '4px',
                            fontSize: '11px', maxHeight: '320px', overflow: 'auto', margin: 0,
                          }}>
                            {row.last_summary
                              ? JSON.stringify(row.last_summary, null, 2)
                              : '(no summary yet)'}
                          </pre>
                          {!row.active && row.revoked_reason && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#e57373' }}>
                              <strong>Revoked:</strong> {row.revoked_reason}
                              {row.revoked_at && (
                                <span style={{ color: c.textMuted }}> ({fmtRelative(row.revoked_at)})</span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showRegister && (
        <ExternalServerRegisterModal
          onCancel={() => setShowRegister(false)}
          onSubmit={onRegister}
        />
      )}

      {revokeRow && (
        <RevokeWithReasonDialog
          row={revokeRow}
          reason={revokeReason}
          setReason={setRevokeReason}
          onCancel={() => { setRevokeRow(null); setRevokeReason(''); }}
          onConfirm={onRevoke}
        />
      )}
    </div>
  );
}

function ExternalServerRegisterModal({ onCancel, onSubmit }) {
  const c = useThemeColors();
  const t = useT();
  const [customerName, setCustomerName] = React.useState('');
  const [fqdn, setFqdn] = React.useState('');
  const submit = (e) => {
    e?.preventDefault?.();
    if (!customerName.trim()) return;
    onSubmit(customerName.trim(), fqdn.trim() || null);
  };
  const labelStyle = { display: 'block', fontSize: '12px', color: c.textSecondary, marginBottom: '4px', fontWeight: 600 };
  const inputStyle = { width: '100%', padding: '6px 8px', fontSize: '13px', border: `1px solid ${c.border}`, borderRadius: '4px', background: c.bg, color: c.text };
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        <div className="modal-header">{t('extsrv.modal_title')}</div>
        <form onSubmit={submit}>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{t('extsrv.modal_customer')}</label>
            <input
              type="text"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
              placeholder={t('extsrv.modal_customer_ph')}
              autoFocus
              maxLength={120}
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>{t('extsrv.modal_fqdn')}</label>
            <input
              type="text"
              value={fqdn}
              onChange={e => setFqdn(e.target.value)}
              placeholder={t('extsrv.modal_fqdn_ph')}
              maxLength={255}
              style={inputStyle}
            />
          </div>
          <div style={{ fontSize: '11px', color: c.textMuted, marginBottom: '14px' }}>
            {t('extsrv.modal_hint')}
          </div>
          <div className="modal-footer">
            <button type="button" className="button button-secondary" onClick={onCancel}>
              {t('apim.modal_cancel') || 'Cancel'}
            </button>
            <button type="submit" className="button button-primary" disabled={!customerName.trim()}>
              {t('extsrv.modal_submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RevokeWithReasonDialog({ row, reason, setReason, onCancel, onConfirm }) {
  const c = useThemeColors();
  const t = useT();
  const labelStyle = { display: 'block', fontSize: '12px', color: c.textSecondary, marginBottom: '4px', fontWeight: 600 };
  const inputStyle = { width: '100%', padding: '6px 8px', fontSize: '13px', border: `1px solid ${c.border}`, borderRadius: '4px', background: c.bg, color: c.text };
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">{t('extsrv.confirm_revoke_q')}</div>
        <div className="modal-body">
          <div style={{ marginBottom: '10px' }}>{t('extsrv.confirm_revoke_b')}</div>
          <div style={{ fontSize: '12px', marginBottom: '12px' }}>
            <strong>{row.customer_name}</strong>{' '}
            <span style={{ color: c.textMuted }}>({row.install_id.slice(0, 8)}…)</span>
          </div>
          <label style={labelStyle}>{t('extsrv.reason_label')}</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('extsrv.reason_ph')}
            maxLength={255}
            autoFocus
            style={inputStyle}
          />
        </div>
        <div className="modal-footer">
          <button type="button" className="button button-secondary" onClick={onCancel}>
            {t('apim.modal_cancel') || 'Cancel'}
          </button>
          <button type="button" className="button button-danger" onClick={onConfirm}>
            {t('extsrv.btn_revoke')}
          </button>
        </div>
      </div>
    </div>
  );
}


// Main Authenticated App — only mounted once a user is logged in.
// This used to be the unconditional entry point; AuthGate now wraps it
// so the meter / WebSocket bootstrapping doesn't run until we have a
// valid session.
function AuthenticatedApp() {
  const { user, logout } = useAuth();
  const t = useT();
  const c = useThemeColors();
  // Initial tab from URL hash (#dashboard → '📊 Dashboard', etc.) so a
  // reload / bookmark / deep link lands where the operator left off.
  // Defaults to Device Management for admins, Customers for others.
  const _TAB_TO_SLUG = {
    '📊 Dashboard': 'dashboard',
    '👥 Customers': 'customers',
    '🔌 Device Management': 'meters',
    '🎛 Relay Control': 'relay',
    '📑 Reports': 'reports',
    '🛠 Admin Tools': 'admin',
    '📘 API Management': 'api',
    '📡 External Servers': 'extservers',
    '⚙ System': 'system',
  };
  const _SLUG_TO_TAB = Object.fromEntries(
    Object.entries(_TAB_TO_SLUG).map(([k, v]) => [v, k])
  );
  const _initialTabFromHash = () => {
    try {
      const slug = (window.location.hash || '').replace(/^#/, '').toLowerCase();
      if (_SLUG_TO_TAB[slug]) return _SLUG_TO_TAB[slug];
    } catch (_) {}
    return '🔌 Device Management';
  };
  const [activeTab, _setActiveTabRaw] = useState(_initialTabFromHash);
  // Wrap setActiveTab so every tab change also updates the URL hash +
  // pushes a new history entry. That turns the browser back button
  // into "previous tab" instead of "leave the site".
  const setActiveTab = useCallback((tabName) => {
    _setActiveTabRaw(tabName);
    try {
      const slug = _TAB_TO_SLUG[tabName];
      if (slug) {
        const nextHash = `#${slug}`;
        if (window.location.hash !== nextHash) {
          window.history.pushState({ tab: tabName }, '', nextHash);
        }
      }
    } catch (_) {}
  }, []);
  // popstate listener — back/forward buttons should switch tabs.
  useEffect(() => {
    const onPop = () => {
      try {
        const slug = (window.location.hash || '').replace(/^#/, '').toLowerCase();
        const tab = _SLUG_TO_TAB[slug];
        if (tab) _setActiveTabRaw(tab);
      } catch (_) {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [toasts, setToasts] = useState([]);
  const [showPwModal, setShowPwModal] = useState(false);
  // Sidebar starts CLOSED on a phone and open on the desktop. It is a
  // 240px fixed-position panel: on a 390px screen an open sidebar plus the
  // matching content offset left roughly 150px of usable width, so every
  // mobile visit began by dismissing it.
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return !window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches; }
    catch (_) { return true; }
  });
  // Crossing the breakpoint (rotate, resize, desktop window drag) resets to
  // that side's sensible default instead of stranding the previous state —
  // an open overlay sidebar on a phone, or a hidden one on a wide screen.
  const _wasMobile = React.useRef(isMobile);
  React.useEffect(() => {
    if (_wasMobile.current !== isMobile) {
      _wasMobile.current = isMobile;
      setSidebarOpen(!isMobile);
    }
  }, [isMobile]);

  // Fetch CT/VT OBIS classification sets from the server once on
  // mount so primaryFactor() stays in sync with the backend's
  // _primary_factor without duplicating the OBIS tables in two
  // places. Silent on failure — hard-coded defaults still correct
  // for every CAS meter deployed today.
  useEffect(() => {
    _refreshPrimaryScalingFromServer();
  }, []);
  // Side-channel state for cross-tab navigation. DashboardTab KPI
  // cards set this before calling setActiveTab('🔌 Meters') so the
  // destination tab can pick up the preset filter.
  const [meterFilter, setMeterFilter] = useState('all');
  // dcuFocus: a side-channel hint — set the DCU serial (+ section) before
  // setActiveTab('🔌 Device Management') to deep-link into a specific DCU's
  // sub-tab (e.g. the dashboard 'Abnormal PLC' box → that DCU's Black List).
  const [dcuFocus, setDcuFocus] = useState(null);

  // Centralized meter list — fetched here once and shared via context.
  const [meters, setMeters] = useState([]);
  // `explicit` distinguishes a meter the USER picked from the one the fleet
  // loader auto-pins below (`data[0].serial`) purely so the Meters tab has
  // something to show. Cross-tab navigation must only honour the former:
  // HierarchyTab remounts on every tab switch, so an auto-pin was overriding
  // the operator's restored customer selection every single time they opened
  // Customers — the "refresh goes to a random meter" report.
  const [selectedSerial, _setSelectedSerialRaw] = useState(null);
  const [selectedIsExplicit, setSelectedIsExplicit] = useState(false);
  const setSelectedSerial = React.useCallback((v) => {
    setSelectedIsExplicit(true);
    _setSelectedSerialRaw(v);
  }, []);

  // Session-wide assignment cache. Consumers share this array across tab
  // mounts and explicitly force-refresh it after hierarchy mutations.
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const assignmentsRef = useRef([]);
  const assignmentsFetchedAt = useRef(0);
  const assignmentsRequest = useRef(null);
  const refreshAssignments = useCallback(async ({ force = false } = {}) => {
    const freshForMs = Date.now() - assignmentsFetchedAt.current;
    if (!force && assignmentsFetchedAt.current && freshForMs < 30_000) {
      return assignmentsRef.current;
    }
    if (assignmentsRequest.current) return assignmentsRequest.current;
    setAssignmentsLoading(true);
    const request = api('/assignments')
      .then((data) => {
        const next = Array.isArray(data?.assignments) ? data.assignments : null;
        if (next === null) throw new Error('Unexpected /assignments response');
        assignmentsRef.current = next;
        assignmentsFetchedAt.current = Date.now();
        setAssignments(next);
        return next;
      })
      .finally(() => {
        assignmentsRequest.current = null;
        setAssignmentsLoading(false);
      });
    assignmentsRequest.current = request;
    return request;
  }, []);
  useEffect(() => {
    refreshAssignments().catch((e) => console.warn('Initial assignment load failed', e?.message));
  }, [refreshAssignments]);

  // Centralized meter list — designed to hold steady at 3000-5000 meters.
  //
  // History: this effect depended on [meterEvents] and refetched the ENTIRE
  // ~1 MB /api/meters on EVERY /ws/meters message. useWebSocket returns a new
  // `messages` array per message, so as the fleet grew every open dashboard was
  // pulling the whole fleet ~once a second — flooding the backend (hundreds of
  // worker threads) and lagging navigation. At 5000 meters that payload is
  // ~5 MB, so a per-event (or even per-10s) full refetch is untenable.
  //
  // Now: the websocket carries per-meter deltas, so we APPLY them in place and
  // almost never refetch the fleet:
  //   • connect / disconnect of a KNOWN meter → flip its online flags locally,
  //     no network at all;
  //   • a never-seen serial (new meter) → ONE coalesced refetch (rare);
  //   • deltas are batched (one state update per ~500 ms burst, not per event)
  //     so 5000 meters churning can't cause a re-render storm;
  //   • a slow reconciliation poll backstops missed events — infrequent (60 s)
  //     while the socket is live, faster (15 s) as a fallback when it's down.
  const fetchMetersRef = useRef(null);
  const metersEtagRef = useRef(null);
  const pendingDeltas = useRef([]);
  const flushTimer = useRef(null);
  const newMeterFetchTimer = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchMeters = async ({ conditional = true } = {}) => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (conditional && metersEtagRef.current) {
          headers['If-None-Match'] = metersEtagRef.current;
        }
        const res = await fetch('/api/meters', { headers, credentials: 'include' });
        if (res.status === 304) return;
        if (res.status === 401) {
          try { window.dispatchEvent(new Event('auth-expired')); } catch (_) {}
        }
        if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
        const data = await res.json();
        if (!mounted) return;
        metersEtagRef.current = res.headers.get('etag') || metersEtagRef.current;
        setMeters(data);
        // raw setter: this is a default, not a choice — see selectedIsExplicit
        _setSelectedSerialRaw(prev => prev || (data.length > 0 ? data[0].serial : prev));
      } catch (_) {}
    };
    fetchMetersRef.current = fetchMeters;
    fetchMeters({ conditional: false });  // initial full load
    return () => {
      mounted = false;
      fetchMetersRef.current = null;
      if (flushTimer.current) { clearTimeout(flushTimer.current); flushTimer.current = null; }
      if (newMeterFetchTimer.current) { clearTimeout(newMeterFetchTimer.current); newMeterFetchTimer.current = null; }
    };
  }, []);

  // Apply a batch of websocket deltas in ONE state update.
  const flushMeterDeltas = useCallback(() => {
    flushTimer.current = null;
    const batch = pendingDeltas.current;
    pendingDeltas.current = [];
    if (batch.length === 0) return;
    let needFetch = false;
    setMeters(prev => {
      const bySerial = new Map(prev.map(m => [m.serial_number || m.serial, m]));
      let changed = false;
      for (const ev of batch) {  // batch is oldest→newest, so newest status wins
        const s = ev.serial;
        if (ev.type === 'meter_disconnected') {
          if (s && bySerial.has(s)) {
            bySerial.set(s, {
              ...bySerial.get(s),
              session_open: false,
              connected: false,
              online: false,
              status: 'offline',
              _offline: true,
              last_seen: ev.timestamp || bySerial.get(s).last_seen,
            });
            changed = true;
          }
        } else if (ev.type === 'meter_connected') {
          const info = ev.info && typeof ev.info === 'object' ? ev.info : null;
          if (s && bySerial.has(s)) {
            const current = bySerial.get(s);
            // The connect callback's info is authoritative. In particular,
            // identified/online does NOT imply that HLS/LLS session_open=true;
            // hardcoding it enabled read/relay buttons prematurely.
            bySerial.set(s, {
              ...current,
              ...(info || {}),
              serial: info?.serial || current.serial || s,
              serial_number: info?.serial_number || current.serial_number || s,
              connected: info?.connected ?? true,
              online: info?.online ?? true,
              _offline: false,
            });
            changed = true;
          } else if (s && info) {
            bySerial.set(s, {
              ...info,
              serial: info.serial || s,
              serial_number: info.serial_number || s,
              connected: info.connected ?? true,
              online: info.online ?? true,
              _offline: false,
            });
            changed = true; // full event means no fleet refetch for new serial
          } else if (s) needFetch = true;
        } else if (ev.type === 'dcu_meter_discovered' && ev.meter) {
          const meter = ev.meter;
          const childSerial = meter.serial_number || meter.serial || meter.meter_serial;
          if (childSerial) {
            const current = bySerial.get(childSerial) || {};
            bySerial.set(childSerial, {
              ...current,
              ...meter,
              serial: meter.serial || childSerial,
              serial_number: meter.serial_number || childSerial,
              dcu_serial: ev.dcu_serial || meter.dcu_serial || current.dcu_serial,
              _via_dcu: true,
              _offline: false,
              online: meter.online ?? true,
            });
            changed = true;
          } else {
            needFetch = true;
          }
        } else {
          needFetch = true;  // unknown event shape → reconcile defensively
        }
      }
      return changed ? Array.from(bySerial.values()) : prev;
    });
    if (needFetch && !newMeterFetchTimer.current) {
      newMeterFetchTimer.current = setTimeout(() => {
        newMeterFetchTimer.current = null;
        if (fetchMetersRef.current) fetchMetersRef.current();
      }, 3000);  // coalesce a burst of new-meter events into one refetch
    }
  }, []);

  // Callback-mode websocket: frames land straight in the pending buffer with
  // ZERO React re-renders — only the coalesced 500 ms flush touches state.
  const onMeterWsMessage = useCallback((msg) => {
    pendingDeltas.current.push(msg);
    if (!flushTimer.current) flushTimer.current = setTimeout(flushMeterDeltas, 500);
  }, [flushMeterDeltas]);
  const { connected: metersWsConnected } = useWebSocket('/ws/meters', { onMessage: onMeterWsMessage });

  // Reconciliation poll — rare when the socket is delivering deltas, faster when
  // it's down (no deltas arriving). Re-armed when the connection flips.
  useEffect(() => {
    const period = metersWsConnected ? 60000 : 15000;
    const id = setInterval(() => { if (fetchMetersRef.current) fetchMetersRef.current(); }, period);
    return () => clearInterval(id);
  }, [metersWsConnected]);

  const pushToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => {
      // Keep max 3 visible at a time to prevent stacking
      const next = [...prev, { id, message, type }];
      return next.length > 3 ? next.slice(-3) : next;
    });
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Tab registry. Some tabs are admin-only and get filtered out below
  // for non-admin users so the sidebar reflects role.
  //
  // v3 UI refresh consolidated multiple per-meter tabs (Read Meter,
  // Write Meter, Load Curve, Events, Reports) into a single 📟 Meter
  // tab with internal sub-tabs (Phase 3.2). The old component
  // definitions remain in the file until Phase 4.1 deletes them,
  // since Phases 3.3–3.8 port their logic into MeterDetailView.
  //
  // Keys / Users / COM Log used to be their own top-level tabs too;
  // they now live under ⚙ System (Phase 3.0).
  const allTabs = {
    '📊 Dashboard': <DashboardTab />,
    '👥 Customers': <HierarchyTab />,
    '🔌 Device Management': <MetersTab />,
    '🎛 Relay Control': <RelayControlTab />,
    '📑 Reports': <ReportsTab />,
    // Live Billing removed (Change 4) — BillingTab kept for Admin Tools fallback
    '🛠 Admin Tools': <AdminToolsTab />,
    '📘 API Management': <ApiManagementTab />,
    '📡 External Servers': <ExternalServersTab />,
    '⚙ System': <SystemSettingsTab />,
    '🪟 Windows App Management': <WindowsAppManagementTab />,
  };
  // i18n labels for the sidebar — keys above stay as English literals
  // because they're also state values, but the rendered label below
  // pulls from the dictionary so EN/RU switching just updates the
  // visible text without breaking active-tab matching.
  const TAB_LABEL_KEYS = {
    '📊 Dashboard':    'tab.dashboard',
    '👥 Customers':    'tab.customers',
    '🔌 Device Management': 'tab.device_mgmt',
    '🎛 Relay Control': 'tab.relay_control',
    '📑 Reports':      'tab.reports',
    // '💰 Live Billing' removed (Change 4)
    '🛠 Admin Tools':  'tab.admin_tools',
    '📘 API Management': 'tab.api_management',
    '📡 External Servers': 'tab.ext_servers',
    '⚙ System':        'tab.system',
    '🪟 Windows App Management': 'tab.windows_app_mgmt',
  };

  // Non-admin sidebar is an explicit ALLOWLIST (default-deny): a tab not
  // listed for a role is never shown. This is deliberately not a denylist —
  // a denylist is how API Management ended up visible to viewers (a tab
  // nobody remembered to hide). Roles:
  //   viewer    → Dashboard, Customers only
  //   operator  → + Relay Control (backend scopes it to their granted meters)
  //   developer → + API Management (own API keys + API reference)
  //   admin     → everything (handled in the admin branch below)
  // Each sidebar tab needs a permission (any of the listed ones). A role is
  // just a default set of permissions, so this list replaced the old
  // admin / per-role tab sets.
  const TAB_PERMS = {
    '📊 Dashboard': ['view_data'],
    '👥 Customers': ['view_data'],
    '🔌 Device Management': ['dcu_diagnostics'],
    '🎛 Relay Control': ['relay_control'],
    '📑 Reports': ['dcu_diagnostics'],
    '🛠 Admin Tools': ADMIN_TOOLS_PERMS,
    '📘 API Management': ['api_keys'],
    '📡 External Servers': ['system_settings'],
    '⚙ System': ['system_settings', 'manage_users'],
    '🪟 Windows App Management': ['system_settings'],
  };


  // Gate the External Servers tab on ≥1 registered install. On a
  // customer install the table is always empty, so the tab stays
  // hidden — same single codebase serves both central and customer.
  const [extServerCount, setExtServerCount] = useState(null);
  useEffect(() => {
    if (!userCan(user, 'system_settings')) { setExtServerCount(0); return; }
    let alive = true;
    api('/external-servers')
      .then(d => { if (alive) setExtServerCount(Array.isArray(d) ? d.length : 0); })
      .catch(() => { if (alive) setExtServerCount(0); });
    return () => { alive = false; };
  }, [user?.permissions]);

  const tabs = Object.fromEntries(
    Object.entries(allTabs).filter(([k]) => {
      // External Servers only once at least one install is registered —
      // hides it on customer installs and on a fresh central.
      if (k === '📡 External Servers' && !(extServerCount && extServerCount > 0)) return false;
      return (TAB_PERMS[k] || []).some(p => userCan(user, p));
    })
  );

  // Snap activeTab back to a visible one if the user's role doesn't
  // include the currently-selected tab. Without this, a viewer who
  // had Keys open would see a blank main area. extServerCount is a
  // dep so the External Servers tab being filtered in/out also
  // triggers a snap-back when needed.
  useEffect(() => {
    if (!tabs[activeTab]) {
      setActiveTab(Object.keys(tabs)[0] || '📊 Dashboard');
    }
  }, [user?.permissions, extServerCount]);

  // Memoize context values: inline object literals here made every consumer of
  // useNav()/useMeters()/useToast() re-render on EVERY AuthenticatedApp render
  // (each websocket frame, toast, sidebar toggle) — a fleet-size render storm
  // at 3000-5000 meters. Now consumers only re-render when their data changes.
  const collapseSidebar = useCallback(() => setSidebarOpen(false), []);
  const navValue = React.useMemo(
    () => ({ setActiveTab, meterFilter, setMeterFilter, dcuFocus, setDcuFocus, collapseSidebar }),
    [meterFilter, dcuFocus, collapseSidebar]
  );
  const meterSelectionValue = React.useMemo(
    () => ({ selected: selectedSerial, setSelected: setSelectedSerial,
             explicit: selectedIsExplicit }),
    [selectedSerial, selectedIsExplicit, setSelectedSerial]
  );
  const meterListValue = React.useMemo(() => ({ meters }), [meters]);
  const assignmentsValue = React.useMemo(
    () => ({ assignments, assignmentsLoading, refreshAssignments }),
    [assignments, assignmentsLoading, refreshAssignments],
  );
  const toastValue = React.useMemo(() => ({ push: pushToast }), [pushToast]);

  return (
    <ToastContext.Provider value={toastValue}>
    <ConfirmDialogProvider>
      <NavContext.Provider value={navValue}>
      <MeterSelectionContext.Provider value={meterSelectionValue}>
      <MeterListContext.Provider value={meterListValue}>
      <AssignmentsContext.Provider value={assignmentsValue}>
        <div className="app-container">
          <style>{styles}</style>

          {/* Sidebar toggle tab — visible when sidebar is collapsed */}
          {!sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(true)}
              style={{
                position: 'fixed',
                // Desktop keeps the vertical edge tab. On a phone that strip
                // sits exactly where iOS Safari reads a back-swipe, so the
                // gesture meant to open the menu is the one that leaves the
                // page. Mobile gets a normal horizontal button tucked into
                // the top-left instead, clear of the edge-gesture zone.
                // MOBILE: a full-width opaque BAR, not a floating pill.
                // A fixed pill can only be cleared by padding while the page
                // sits at scroll-top; the moment the operator scrolls, page
                // content slides underneath it and collides with the label —
                // which is exactly what "MENU over Customers" was, and no
                // amount of padding can fix it. A bar spanning the width
                // with a solid background means scrolled content passes
                // BEHIND it and reads as intentional chrome.
                left: 0,
                right: isMobile ? 0 : undefined,
                top: isMobile ? 0 : '50%',
                transform: isMobile ? 'none' : 'translateY(-50%)',
                zIndex: 101,
                cursor: 'pointer',
                writingMode: isMobile ? 'horizontal-tb' : 'vertical-rl',
                textOrientation: 'mixed',
                background: isMobile ? c.bgCard : c.accent,
                color: isMobile ? c.accent : c.bgBase,
                borderBottom: isMobile ? `1px solid ${c.border}` : undefined,
                padding: isMobile ? '0 14px' : '24px 12px',
                borderRadius: isMobile ? 0 : '0 10px 10px 0',
                fontSize: isMobile ? '15px' : '18px',
                minHeight: isMobile ? '48px' : undefined,
                display: isMobile ? 'flex' : undefined,
                alignItems: isMobile ? 'center' : undefined,
                fontWeight: 800,
                letterSpacing: isMobile ? '1px' : '2px',
                boxShadow: isMobile
                  ? '0 2px 10px rgba(0,0,0,0.35)'
                  : '3px 0 14px rgba(0,0,0,0.45)',
                border: isMobile ? 'none' : `1px solid ${c.accent}`,
                userSelect: 'none',
                textTransform: 'uppercase',
              }}
              // Hover shadows are a desktop affordance; on a touch device
              // they stick after a tap because there is no mouseleave.
              onMouseEnter={isMobile ? undefined : (e) => { e.currentTarget.style.boxShadow = '4px 0 18px rgba(0,0,0,0.6)'; }}
              onMouseLeave={isMobile ? undefined : (e) => { e.currentTarget.style.boxShadow = '3px 0 14px rgba(0,0,0,0.45)'; }}
              title="Open main menu"
            >
              ☰ MENU
            </div>
          )}

          {/* Backdrop — mobile only. An overlay sidebar needs a
              tap-outside-to-close affordance; without one the only way out
              is the small ✕, which is a poor target on a phone. Rendered as
              a <button> so it is reachable by keyboard and screen readers
              rather than being an invisible click-trap div. */}
          {isMobile && sidebarOpen && (
            <button
              type="button"
              className="sidebar-backdrop"
              aria-label="Close menu"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          <div className={`sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div
                className="logo"
                style={{ marginBottom: 0, cursor: 'pointer' }}
                onClick={() => {
                  // Click logo → jump to default landing tab (role-aware).
                  // Admins land on Dashboard, everyone else on Customers.
                  const home = userCan(user, 'dcu_diagnostics') ? '📊 Dashboard' : '👥 Customers';
                  setActiveTab(home);
                }}
                title="Home"
              >
                <span className="logo-text">eMeter</span>
                <div style={{ fontSize: '10px', color: c.textMuted, marginTop: '2px' }}>Automated Metering Intelligence</div>
                <div className="version-badge">v3.0.0</div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: c.textMuted,
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1,
                }}
                title="Collapse menu"
              >
                ✕
              </button>
            </div>

            <ul className="nav-list">
              {Object.keys(tabs).map(tabName => (
                <li
                  key={tabName}
                  className={`nav-item ${activeTab === tabName ? 'active' : ''}`}
                  onClick={() => { setActiveTab(tabName); setSidebarOpen(false); }}
                >
                  <span className="nav-icon">{tabName.split(' ')[0]}</span>
                  <span>{t(TAB_LABEL_KEYS[tabName] || '')}</span>
                </li>
              ))}
            </ul>

            <div className="sidebar-bottom">
              <div className="status-indicator">
                <div className="status-dot connected" />
                <span>{t('msg.meters_connected', { n: meters.length })}</span>
              </div>
              {user && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: c.bgBase,
                  border: `1px solid ${c.border}`,
                  borderRadius: '8px',
                }}>
                  <div style={{
                    fontSize: '13px',
                    color: c.textPrimary,
                    fontWeight: 600,
                    marginBottom: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {user.full_name || user.username}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: c.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                  }}>
                    {user.role}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="button button-secondary"
                      style={{ flex: 1, padding: '6px 8px', fontSize: '11px' }}
                      onClick={() => setShowPwModal(true)}
                      title={t('btn.change_pw')}
                    >
                      🔐
                    </button>
                    <button
                      className="button button-secondary"
                      style={{ flex: 1, padding: '6px 8px', fontSize: '11px' }}
                      onClick={logout}
                      title={t('btn.logout')}
                    >
                      ⏻ {t('btn.logout')}
                    </button>
                  </div>
                  <div style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${c.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{ fontSize: '10px', color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {t('common.language')}
                    </span>
                    <LanguageSwitch compact />
                    <ThemeSwitch compact />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="main-content" style={{
            // When the sidebar is collapsed, leave room for the
            // fixed-position MENU re-open tab (≈48px wide, vertical
            // text) so table content doesn't slide under it. 64px
            // matches the tab width + ~16px breathing space.
            marginLeft: sidebarOpen ? '240px' : '64px',
            transition: 'margin-left 0.25s ease',
          }}>
            {/* AlertBanner lives HERE, not as a child of .app-container.
                .app-container is display:flex (a ROW), and position:sticky
                still occupies normal flow — so as a direct child the banner
                became a flex ITEM: a column wedged between the sidebar and
                the content, shoving the whole UI sideways. Reported with a
                screenshot 2026-09-11 showing two DCU-offline alerts pushing
                the Customers panel right.
                Inside .main-content it is a block in a normal column, spans
                the content width, and its `position: sticky; top: 0` keeps
                it pinned while the page scrolls — which was the intent. */}
            <AlertBanner />
            {tabs[activeTab]}
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {toasts.map(toast => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                onClose={() => dismissToast(toast.id)}
              />
            ))}
          </div>

          {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
          <IdleTimer />
        </div>
      </AssignmentsContext.Provider>
      </MeterListContext.Provider>
      </MeterSelectionContext.Provider>
      </NavContext.Provider>
    </ConfirmDialogProvider>
    </ToastContext.Provider>
  );
}

// AuthGate — the actual root component. Reads the auth context and
// either shows the LoginScreen or the AuthenticatedApp. The Toast
// context is intentionally available inside the LoginScreen too so the
// password-change modal can use it (it's instantiated inside
// AuthenticatedApp anyway).
function AuthGate() {
  const { user } = useAuth();
  if (!user) {
    // Still need a Toast provider for any flash messages from the
    // login flow itself, even though LoginScreen doesn't currently
    // surface any beyond the inline error box.
    return (
      <ToastContext.Provider value={{ push: () => {} }}>
        <LoginScreen />
      </ToastContext.Provider>
    );
  }
  return <AuthenticatedApp />;
}

// ── Windows App Management (admin) ─────────────────────────────────────
// Published thin-client release + which install runs which version, and a
// one-click "publish latest signed CI build" (server pulls it from GitHub
// via EMETER_GH_TOKEN). Backed by /api/admin/thin-client/{overview,publish}.
function WindowsAppManagementTab() {
  const { user } = useAuth();
  const toast = useToast();
  const t = useT();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [lastChecked, setLastChecked] = React.useState(null);

  // Keep a stable ref to toast so `load` never changes identity. Context
  // values here are fresh objects each render (ToastContext/AuthContext),
  // so a [toast]-deps callback in a [load]-deps effect would re-fire every
  // render = a refresh loop. This tab must refresh on mount + manual click ONLY.
  const toastRef = React.useRef(toast);
  toastRef.current = toast;

  const load = React.useCallback(() => {
    setLoading(true);
    api('/admin/thin-client/overview')
      .then((d) => { setData(d); setLastChecked(new Date()); })
      .catch((err) => toastRef.current.push(err.message, 'error'))
      .finally(() => setLoading(false));
  }, []);

  // One-shot initial load, guarded so context identity churn can't re-trigger it.
  const didLoad = React.useRef(false);
  React.useEffect(() => {
    if (userCan(user, 'system_settings') && !didLoad.current) {
      didLoad.current = true;
      load();
    }
  }, [user, load]);

  const publish = async (force) => {
    setPublishing(true);
    try {
      const r = await api('/admin/thin-client/publish', {
        method: 'POST',
        body: JSON.stringify({ force: !!force }),
      });
      toastRef.current.push(
        r.published ? `Published ${r.version}` : (r.message || 'Already up to date'),
        r.published ? 'success' : 'info',
      );
      load();
    } catch (err) {
      toastRef.current.push(err.message, 'error');
    } finally {
      setPublishing(false);
    }
  };

  if (!userCan(user, 'system_settings')) {
    return (
      <div className="tab-content">
        <div className="section-title">🪟 {t('tab.windows_app_mgmt')}</div>
        <div style={{ opacity: 0.65 }}>{t('msg.admin_required')}</div>
      </div>
    );
  }

  const pub = data && data.published;
  const ci = data && data.latest_ci_version;
  const updateAvail = !!(ci && (!pub || ci !== pub.version));
  const installs = (data && data.installs) || [];
  const card = { border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 };
  const muted = { opacity: 0.65 };
  const th = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)', fontWeight: 600 };
  const td = { padding: '6px 8px', borderBottom: '1px solid var(--border)' };

  return (
    <div className="tab-content">
      <div className="section-title">🪟 {t('tab.windows_app_mgmt')}</div>
      {loading && !data ? <Spinner /> : (
        <>
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Published release</div>
            {pub ? (
              <>
                <div>
                  Version <b>{pub.version}</b>
                  {pub.size_bytes ? ` · ${(pub.size_bytes / 1048576).toFixed(1)} MB` : ''}
                  {pub.pub_date ? ` · ${fmtDT(pub.pub_date, { utc: true, seconds: true })}` : ''}
                </div>
                {pub.notes ? (
                  <pre style={{ whiteSpace: 'pre-wrap', background: 'rgba(127,127,127,0.08)', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginTop: 6, fontFamily: 'inherit' }}>{pub.notes}</pre>
                ) : null}
              </>
            ) : <div style={muted}>No release published yet.</div>}
            <div style={{ marginTop: 10 }}>
              {data && data.token_configured ? (
                <>
                  {ci ? (
                    <span style={{ marginRight: 12, color: updateAvail ? 'var(--warning)' : undefined, ...(updateAvail ? {} : muted) }}>
                      Latest signed build: <b>{ci}</b>{updateAvail ? ' — new!' : ' (current)'}
                    </span>
                  ) : null}
                  <button className="btn btn-primary" disabled={publishing} onClick={() => publish(false)}>
                    {publishing ? 'Publishing…' : 'Publish latest build'}
                  </button>
                  <button className="btn btn-secondary" disabled={publishing} onClick={() => publish(true)} style={{ marginLeft: 8 }}>
                    Force republish
                  </button>
                </>
              ) : (
                <div style={muted}>
                  Set <code>EMETER_GH_TOKEN</code> (read-only, <code>actions:read</code>) in
                  {' '}<code>/etc/emeter/db.env</code> to enable one-click publish from CI.
                </div>
              )}
            </div>
          </div>

          <div style={{ fontWeight: 600, margin: '4px 0 6px' }}>
            Installs ({installs.length}) <span style={muted}>— version reported by 0.6.24+ clients</span>
          </div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Device (key)</th>
                <th style={th}>User</th>
                <th style={th}>Version</th>
                <th style={th}>Last seen</th>
                <th style={th}>IP</th>
              </tr>
            </thead>
            <tbody>
              {installs.map((k, i) => (
                <tr key={i}>
                  <td style={td}>{k.label || k.prefix || '—'}</td>
                  <td style={td}>{k.user || '—'}</td>
                  <td style={td}>{k.version || <span style={muted}>unknown</span>}</td>
                  <td style={td}>{k.last_seen ? fmtDT(k.last_seen, { utc: true, seconds: true }) : '—'}</td>
                  <td style={{ ...td, ...muted }}>{k.ip || '—'}</td>
                </tr>
              ))}
              {installs.length === 0 ? (
                <tr><td style={{ ...td, ...muted }} colSpan={5}>No installs have reported a version yet.</td></tr>
              ) : null}
            </tbody>
          </table>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn btn-secondary" onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : '↻ Refresh'}
            </button>
            <span style={muted}>
              Last checked: {lastChecked ? lastChecked.toLocaleString() : '—'}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── ErrorBoundary ───────────────────────────────────────────────────
// Without this, ANY uncaught render/effect error makes React 18 unmount
// the whole tree. #root goes empty, the `#root:empty::after` rule in
// index.html re-displays "Loading eMeter...", and the app is
// indistinguishable from a hung server — operators reported exactly that
// on 2026-07-26 for a one-line ReferenceError in
// CustomerCollectionStatsCard. Rendering *something* here is the point:
// it keeps #root non-empty and puts the real error on screen.
//
// Deliberately styled with literal inline CSS and no hooks/context — the
// thing that just crashed may BE the theme or auth provider, so this
// component must not depend on anything above it.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    try {
      console.error('[eMeter] uncaught render error:', error, info);
    } catch (_) { /* console may be unavailable */ }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const pre = {
      background: '#0a0e17', color: '#f87171', padding: '12px',
      borderRadius: '6px', border: '1px solid #7f1d1d', overflow: 'auto',
      fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      maxHeight: '38vh', fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
    };
    return (
      <div style={{
        minHeight: '100vh', background: '#0f172a', color: '#e2e8f0',
        padding: '24px', fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'auto',
      }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '18px', marginBottom: '6px', color: '#f87171' }}>
            eMeter hit a client-side error
          </h1>
          <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '16px' }}>
            The dashboard failed to render. The server is unaffected — this is a
            bug in the page. Details below; send them to whoever maintains eMeter.
          </p>
          <div style={pre}>
            {String(this.state.error && (this.state.error.stack || this.state.error.message || this.state.error))}
            {this.state.info && this.state.info.componentStack
              ? `\n\nComponent stack:${this.state.info.componentStack}`
              : ''}
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => { try { window.location.reload(); } catch (_) {} }}
              style={{
                background: '#0ea5e9', color: '#04121b', border: 'none',
                borderRadius: '6px', padding: '9px 16px', fontSize: '13px',
                fontWeight: 600, cursor: 'pointer',
              }}>
              Reload
            </button>
            <button
              onClick={() => this.setState({ error: null, info: null })}
              style={{
                background: 'transparent', color: '#e2e8f0',
                border: '1px solid #334155', borderRadius: '6px',
                padding: '9px 16px', fontSize: '13px', cursor: 'pointer',
              }}>
              Try to continue
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ReportLangProvider>
          <ThemeProvider>
            <AuthProvider>
              <AuthGate />
              <BgJobsWidget />
            </AuthProvider>
          </ThemeProvider>
        </ReportLangProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

// Mount React App
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(React.createElement(App));
